// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { euModuleTips } from './eu-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
export const eu = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Hasi saioa zure kontuan',
        email: 'Helbide elektronikoa',
        password: 'Pasahitza',
        sign_in: 'Hasi saioa',
        signing_in: 'Saioa hasten...',
        mfa_code: 'MFA kodea',
        mfa_placeholder: 'Sartu 6 digituko kodea',
        mfa_instruction: 'Sartu zure autentifikazio-aplikazioko 6 digituko kodea.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Egiaztatu',
        verifying: 'Egiaztatzen...',
        forgot_password: 'Pasahitza ahaztu duzu?',
        account_locked: 'Kontua blokeatuta dago. Saiatu berriro geroago.',
        account_deactivated: 'Kontua desaktibatuta dago. Jarri harremanetan zure administratzailearekin.',
        invalid_mfa: 'MFA kode baliogabea. Saiatu berriro.',
        invalid_credentials: 'Helbide elektroniko edo pasahitz baliogabea.',
        login_failed: 'Saioa hasteak huts egin du',
        session_limit_reached: 'Zure kontuak saio aktiboen muga lortu du. Amaitu saioa beste gailu batean edo eskatu administratzaileari lehendik dagoen saio bat baliogabetzeko, eta saiatu berriro.',
        session_expired: 'Zure saioa iraungi da. Mesedez, kargatu orrialdea berriro eta saiatu berriro.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Pasahitza ahaztu duzu',
            subtitle: 'Sartu zure helbide elektronikoa eta pasahitza berrezartzeko esteka bidaliko dizugu.',
            send: 'Bidali berrezartze-esteka',
            sending: 'Bidaltzen...',
            success: 'Helbide elektroniko hori existitzen bada, pasahitza berrezartzeko esteka bidali da.',
            back_to_login: 'Itzuli saio-hasierara',
        },
        reset_password: {
            title: 'Berrezarri pasahitza',
            subtitle: 'Sartu zure pasahitz berria behean.',
            new_password: 'Pasahitz berria',
            confirm_password: 'Berretsi pasahitza',
            reset: 'Berrezarri pasahitza',
            resetting: 'Berrezartzen...',
            success: 'Zure pasahitza behar bezala berrezarri da.',
            go_to_login: 'Joan saio-hasierara',
            password_mismatch: 'Pasahitzak ez datoz bat.',
            invalid_token: 'Berrezartze-token baliogabea edo falta dena. Mesedez, eskatu pasahitza berrezartzeko esteka berri bat.',
            requirements_title: 'Pasahitz-eskakizunak:',
            password_requirements: 'Pasahitzak honako eskakizunak bete behar ditu:',
            rules: {
                min_length: 'Gutxienez 12 karaktere',
                uppercase: 'Gutxienez letra larri bat',
                lowercase: 'Gutxienez letra xehe bat',
                digit: 'Gutxienez zenbaki bat',
                special: 'Gutxienez karaktere berezi bat',
            },
        },
        verify_email: {
            title: 'Helbide elektronikoaren egiaztapena',
            verifying: 'Zure helbide elektronikoa egiaztatzen...',
            success: 'Zure helbide elektronikoa behar bezala egiaztatu da.',
            invalid_token: 'Egiaztapen-token baliogabea edo falta dena.',
            failed: 'Helbide elektronikoaren egiaztapenak huts egin du. Tokena baliogabea edo iraungitakoa izan daiteke.',
            go_to_login: 'Joan saio-hasierara',
        },
        offline: {
            title: 'Konexiorik gabe zaude',
            message: 'Badirudi interneteko konexioa galdu duzula. Mesedez, egiaztatu zure sarea eta saiatu berriro.',
        },
    },
    intake: {
        title: 'Datuen Eskubide-eskaera',
        subtitle: 'Bidali datuen eskubide-eskaera bat gure erakundeari',
        fields: {
            full_name: 'Izen-abizenak',
            email: 'Helbide elektronikoa',
            phone: 'Telefono-zenbakia (aukerakoa)',
            request_type: 'Eskaera mota',
            description: 'Deskribatu zure eskaera',
            preferred_language: 'Hizkuntza hobetsia',
            consent: 'Eskaera hau tratatzeko baimena ematen dut',
        },
        submit: 'Bidali eskaera',
        validation: {
            name_required: 'Izen-abizenak derrigorrezkoa da',
            email_required: 'Helbide elektroniko baliogarria beharrezkoa da',
            type_required: 'Mesedez, hautatu eskaera mota bat',
            description_required: 'Mesedez, deskribatu zure eskaera',
            consent_required: 'Tratamendurako baimena eman behar duzu jarraitzeko',
        },
        error_title: 'Errorea',
        config_load_error: 'Ezin izan da konfigurazioa kargatu. Mesedez, egiaztatu URLa.',
        organization_label: 'Erakundea',
        customer_reference: 'Bezeroaren erreferentzia-zenbakia',
        country: 'Herrialdea / Jurisdikzioa',
        preferred_channel: 'Erantzun-kanal hobetsia',
        channels: {
            email: 'Posta elektronikoa',
            postal: 'Posta',
            phone: 'Telefonoa',
            portal: 'Ataria',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Eskaera jasota',
            message: 'Zure eskaera behar bezala erregistratu da. Zure erreferentzia-zenbakia hau da:',
            next_steps: 'Zure eskaera ahalik eta azkarren prozesatuko dugu indarrean dagoen legediari jarraikiz. Aurreikusitako prozesatze-epea 30 lanegunekoa da.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Sarbidea (DSAR)',
        deletion: 'Ezabatzea',
        correction: 'Zuzenketa / Eguneratzea',
        objection: 'Aurka egitea',
        portability: 'Datuen eramangarritasuna',
        restriction: 'Tratamenduaren murrizketa',
        consent_withdrawal: 'Baimena atzera botatzea',
        protective_measures: 'Datu pertsonalak babesteko neurriak eta irtenbideak eskatzea',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Berria',
        in_review: 'Berrikuspenean',
        waiting_on_requester: 'Eskatzailearen zain',
        waiting_on_internal_data: 'Barneko datuen zain',
        approved: 'Onartua',
        closed: 'Itxita',
        rejected: 'Baztertua',
    },
    admin: {
        nav: {
            rights_requests: 'Eskubide-eskaerak',
            templates: 'Txantiloiak',
            response_templates: 'Erantzun Txantiloiak',
            data_mapping: 'Tratamendu-jardueren erregistroa',
            governance: 'Gobernantza',
            consent: 'Adostasuna',
            assessments: 'Ebaluazioak',
            classification: 'Sailkapena',
            monitoring: 'Jarraipena',
            incidents: 'Gertakariak',
            onboarding: 'Hasierako konfigurazioa',
            connectors: 'Konektoreak',
            vendor_risk: 'Hornitzaile-arriskua',
            deletion_orchestration: 'Ezabatze-orkestrazioa',
            discovery: 'Datuen aurkikuntza',
            incident_ops: 'Gertakarien kudeaketa',
            program_governance: 'Programaren gobernantza',
            access_accountability: 'Sarbidearen ardura',
            consent_properties: 'Adostasun-propietateak',
            localization: 'Lokalizazioa',
            regulatory: 'Araudia',
            partner_workspace: 'Bazkideen lan-eremua',
            audit_trail: 'Ikuskaritza-pista',
            forseti: 'Forseti',
            settings: 'Ezarpenak',
            help_center: 'Laguntza-zentroa',
            dashboard: 'Kontrol-panela',
            platform: 'Plataforma',
            integrations: 'Integrazioak',
            cmp_dsar_sync: 'CMP DSAR sinkronizazioa',
            cmp_integration: 'CMP integrazioa',
            purpose_mapping: 'Xede-esleipena',
            webhook_config: 'Webhook konfigurazioa',
            bulk_sync: 'Sinkronizazio masiboa',
            privacy_scanner: 'Pribatutasun-eskanerra',
            privacy_monitoring: 'Pribatutasun-jarraipena',
            real_time_scanner: 'Denbora errealeko eskanerra',
            compliance_forms: 'Betetze-formularioak',
            forms_dashboard: 'Formularioen kontrol-panela',
            form_templates: 'Txantiloiak',
            form_catalog: 'Formulario-katalogoa',
            pack_catalog: 'Pakete-katalogoa',
            org_groups: 'Erakunde-taldeak',
            consolidation: 'Finkatzea',
            organizations: 'Erakundeak',
            org_list: 'Erakunde guztiak',
            intake_configs: 'Sarrera-konfigurazioak',
            task_management: 'Zereginen kudeaketa',
            unified_audit: 'Ikuskaritza Bateratua',
            evidence_packs: 'Ebidentzia-paketeak',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Ikuskaritza-aldiak',
            regulatory_updates: 'Araudi Eguneratzeak',
            departments: 'Sailak',
        },
        sidebar_category_data_privacy_rights: 'Datuen Pribatutasuna & Eskubideak',
        sidebar_category_vendor_third_party: 'Hornitzaile & Hirugarrenen Kudeaketa',
        sidebar_category_risk_assessment: 'Arriskua & Ebaluazioa',
        sidebar_category_operations_automation: 'Eragiketak & Automatizazioa',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Zereginen & Lan-fluxuaren Kudeaketa',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP eta Webhook-ak',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'RoPA Erregistroa',
            data_mapping_classification: 'Sailkapena',
            data_mapping_governance: 'Gobernantza',
            access_live_monitoring: 'Zuzeneko Jarraipena',
            access_reviews_investigations: 'Iritziak eta ikerketak',
            access_evidence_policies: 'Froga eta politikak',
            classification_taxonomies: 'Taxonomiak eta Etiketak',
            classification_workflow: 'Lan-fluxua',
            classification_insights: 'Ikuspegiak',
            connectors_runtime: 'Exekuzioa',
            connectors_due_diligence: 'Duela Diligentzia',
            connectors_coverage: 'Estaldura',
            cp_definitions: 'Definizioak',
            cp_lifecycle: 'Bizi-zikloa',
            cp_quality: 'Kalitatea eta Froga',
            deletion_execution: 'Exekuzioa',
            deletion_vendors: 'Saltzaileak',
            deletion_evidence: 'Froga eta Integrazioa',
            discovery_rules: 'Arauak eta txantiloiak',
            discovery_scanning: 'Eskaneatzea',
            discovery_review: 'Berrikusi lan-fluxua',
            discovery_insights: 'Ikuspegiak',
            governance_oversight: 'Gainbegiratzea',
            governance_reporting: 'Txostenak ematea',
            governance_vendors: 'Saltzaileak eta Mugaz Gaindikoak',
            governance_lifecycle: 'Bizi-zikloa',
            incident_ops_dashboards: 'Aginte-panelak',
            incident_ops_sla: 'SLA Konfig',
            incident_ops_playbooks: 'Jolas liburuak',
            incident_ops_vendor_regulator: 'Saltzailea eta erregulatzailea',
            incident_ops_evidence: 'Froga eta berrikuspena',
            incidents_workload: 'Lan karga',
            incidents_regulatory: 'Araudia',
            incidents_reporting: 'Txostenak ematea',
            localization_content: 'Edukia',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Behagarritasuna',
            monitoring_metrics: 'Metrikak',
            monitoring_programs: 'Programak',
            monitoring_tasks: 'Zereginak',
            monitoring_controls: 'Kontrolak',
            org_entities: 'Entitateak',
            org_config: 'Konfigurazioa',
            org_frameworks: 'Esparruak eta bilketa',
            partner_overview: 'Ikuspegi orokorra',
            partner_partners: 'Bazkideak',
            partner_delivery: 'Entrega',
            partner_support: 'Laguntza',
            platform_identity: 'Identitatea eta rolak',
            platform_access_control: 'Sarbide Kontrola',
            platform_auth_session: 'Autentifikazioa eta saioa',
            platform_access_reviews: 'Sartu Iritziak',
            platform_files: 'Fitxategiak',
            platform_notifications: 'Jakinarazpenak',
            platform_module_config: 'Moduluaren konfigurazioa',
            platform_ops: 'Operazioak eta erregistroak',
            pg_health: 'Programa Osasuna',
            pg_maturity: 'Heldutasuna',
            pg_audit: 'Ikuskaritza eta Froga',
            pg_training: 'Prestakuntza',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Betebeharrak',
            pg_documents: 'Dokumentuak',
            vr_overview: 'Ikuspegi orokorra',
            vr_onboarding: 'Sartzea',
            vr_assessment: 'Ebaluazioa',
            vr_oversight: 'Gainbegiratzea',
        },
        tickets: {
            title: 'Eskubideak Eskaerak',
            create: 'Sortu Eskaera',
            filters: 'Iragazi',
            search_placeholder: 'Search tickets...',
            export_csv: 'Esportatu CSV',
            exporting: 'Esportatzen...',
            bulk_export_failed: 'Ezin izan da esportazio masiboa',
            bulk_export: {
                label: 'Esportatu',
                format: {
                    pdf: 'PDF Laburpena',
                    zip: 'ZIP (kasu bakoitzeko froga paketeak)',
                    csv: 'CSV',
                },
                warning_over_50: 'Honek gutxi gorabehera {count} kasu pakete sortuko ditu - minutu batzuk behar izan ditzake. Jarraitu nahi duzu?',
                blocked_over_500: 'Mugitu iragazkia 500 kasu edo gutxiagora ZIP esportatu aurretik (unekoa: {count}).',
                success: 'Esportazio masiboa Fitxategien arakatzailean gorde da ({count} kasu)',
                failed: 'Ezin izan da esportazio masiboa',
                continue: 'Jarraitu',
                cancel: 'Utzi',
            },
            no_results: 'Ez da requests aurkitu',
            ticket_id: 'Tiketa ID',
            request_type: 'Eskaera Mota',
            requester: 'Eskatzailea',
            source: 'Iturria',
            all_sources: 'Iturri guztiak',
            assigned_to: 'Esleituta To',
            created: 'Sortuta',
            load_error: 'Huts egin du: load cases. Saiatu berriro.',
            case_creation_failed: 'Kasu-sortzeak huts egin du',
            business_unit: 'Negozio-unitatea',
            reference: 'Erreferentzia',
            sla: {
                title: 'SLA Egoera',
                on_track: '{days} egun geratzen dira',
                approaching: 'Epemuga hurbiltzen',
                overdue: '{days} egun atzeratuta',
                ack_overdue: '{hours}o harrera-agiri epemugaz gain',
                ack_warning: 'Harrera-agiria laster iraungiko da',
                closed: 'Itxita',
                completed_in: '{days} egunetan osatua',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Erantzun Txantiloiak',
            new: 'Berria Txantiloi',
            edit: 'Editatu Txantiloi',
            all_types: 'Mota guztiak',
            all_locales: 'Lokal guztiak',
            type_access: 'Sarbidea/DSAR',
            type_deletion: 'Ezabatzea',
            type_correction: 'Zuzenketa',
            type_objection: 'Aurka egitea',
            type_portability: 'Eramangarritasuna',
            type_other: 'Beste',
            type_restriction: 'Murrizketa',
            type_all: 'All',
            locale_en: 'Ingelesa',
            locale_vi: 'Vietnamera',
            locale_all: 'Biak',
            name: 'Izena',
            request_type: 'Eskaera Mota',
            locale: 'Tokikoa',
            subject: 'Subjektua',
            body: 'Gorputza',
            name_placeholder: 'Txantiloi izena...',
            subject_placeholder: 'Posta elektronikoa/gutunaren gaia...',
            body_placeholder: 'Txantiloiaren gorputza. Erabili {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} leku-marka gisa.',
            available_placeholders: 'Eskuragarri placeholders:',
            create_template: 'Sortu Txantiloi',
            preview: 'Aurrebista',
            close_preview: 'Itxi Preview',
            default_badge: 'Lehenetsia',
            no_templates: 'Ez da txantiloiak aurkitu',
            confirm_delete: 'Ezabatu this template?',
            load_error: 'Huts egin du: load txantiloiak. Saiatu berriro.',
            create_error: 'Huts egin du: create template. Saiatu berriro.',
            update_error: 'Huts egin du: update template. Saiatu berriro.',
            delete_error: 'Huts egin du: delete template. Saiatu berriro.',
            preview_error: 'Huts egin du: preview template. Saiatu berriro.',
            response_templates_badge: 'Erantzun Txantiloiak',
            browse_form_templates: 'Betetze Inprimaki Txantiloiak Arakatu',
            templates_loaded: 'Txantiloiak kargatuta',
            sample_organization: 'Erakunde-lagina',
        },
        detail: {
            requester_info: 'Eskatzailearen informazioa',
            status: 'Egoera',
            assignment: 'Esleipena',
            notes: 'Oharrak',
            audit_trail: 'Ikuskaritza Trail',
            add_note: 'Gehitu oharra',
            case_not_found: 'Kasu not found',
            load_error: 'Huts egin du: load case. Saiatu berriro.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Huts egin du: add note. Saiatu berriro.',
            submitted_from: 'Bidalita hemendik',
            via_cmp: 'AesirX CMP bidez',
            cmp_ticket: 'CMP txartela:',
            select_template: 'Hautatu txantiloi bat',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Xehetasunak',
                evidence: 'Ebidentzia',
                communications: 'Komunikazio',
                escalations: 'Eskalazio',
                audit_trail: 'Ikuskaritza Trail',
                fulfillment: 'Betetzea',
                response: 'Erantzuna',
                extension: 'Luzapena',
            },
            requester: {
                name: 'Izena',
                email: 'Eposta',
                phone: 'Telefonoa',
                language: 'Hizkuntza',
                source: 'Iturria',
                customer_ref: 'Bezeroaren erref',
                country: 'Herrialdea',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Kanal hobetsia',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Bidean',
                due_soon: 'Laster epe-muga',
                overdue: 'Atzeratuta',
                days_remaining: 'd geratzen',
                due_date: 'Due Data',
                breached: 'Urratuta',
                paused: 'Pausatuta',
                total_paused_days: 'Paused egun guztira',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'egun Elapsed',
                progress: 'Aurrerapena',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Nortasuna egiaztatzea',
                verified: 'Egiaztatuta',
                not_verified: 'Egiaztatu gabe',
                method: 'Metodoa',
                notes: 'Oharrak',
                verified_by: 'Egiaztatuta By',
                verified_at: 'Egiaztatuta At',
                warning: 'Identitatea oraindik egiaztatu gabe.',
                verify_button: 'Egiaztatu Identity',
                select_method: '-- Hautatu --',
                methods: {
                    email_confirmation: 'Eposta Confirmation',
                    id_document: 'ID Dokumentu',
                    phone_call: 'Telefonoa Call',
                    in_person: 'Aurrez aurre',
                    other: 'Beste',
                },
            },
            closure: {
                reason: 'Itxiera Arrazoia',
                summary: 'Closure Laburpena',
                summary_placeholder: 'Laburpena of how the request was resolved...',
                reason_required: 'Itxiera-laburpena eta arrazoia beharrezkoak dira',
                response_not_sent: 'Erantzun formal bat bidali behar zaio datu-subjektuari kasu hau itxi aurretik',
                reasons: {
                    fulfilled: 'Beteta',
                    partially_fulfilled: 'Partzialki beteta',
                    unfounded: 'Oinarririk gabea',
                    excessive: 'Gehiegizkoa',
                    withdrawn: 'Erretiratua',
                    duplicate: 'Bikoiztu',
                    identity_unverified: 'Identitate egiaztatu gabea',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Esportatu',
            export_pdf: 'Esportatu PDF',
            export_zip: 'Esportatu ZIP',
            case_file_pdf: 'Esportatu PDF (kasuaren fitxategia)',
            evidence_pack_zip: 'Esportatu ZIP (Froga paketea)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Markatu haustura-eragile gisa',
                modal: {
                    title: 'Markatu kasua haustura eragiten duen moduan',
                    type_label: 'Urrats mota',
                    type: {
                        misdirected_response: 'Okerreko erantzuna',
                        unauthorised_disclosure: 'Baimenik gabeko dibulgazioa',
                        identity_mismatch: 'Identitate-desegokia',
                        other: 'Bestela',
                    },
                    description: 'Deskribapena (aukerakoa)',
                    description_placeholder: 'Zer gertatu da? (gehienez 1000 karaktere)',
                    confirm: 'Markatu eta hasi jakinarazpen-erlojua',
                    cancel: 'Utzi',
                    flagging: 'Markatzen...',
                },
                banner: {
                    title: 'Urraketa markatu da: {type}',
                    detected_at: '{timestamp}-n detektatu da',
                    filing_deadline: 'Jakinarazpen-epea: {hours} ordutan',
                    overdue: 'Jakinarazpen-epea: EPEZ KANPO',
                },
                toasts: {
                    flagged: 'Kasua hausteak eragiten dituen moduan markatuta - jakinarazpen-erlojua hasi da',
                    flag_failed: 'Ezin izan da markatu kasuak urratzea eragiten duela',
                },
            },
            export: {
                generating: 'Sortzen... itxaron mesedez',
                success: 'Esportatu fitxategiak arakatzailean gordeta',
                failed: 'Huts egin du esportatu - egiaztatu auditoria-erregistroa',
            },
            no_notes: 'Oraindik ez dago oharrik',
            assign_placeholder: 'Erabiltzaile ID to assign...',
            reassign: 'Berriro esleitu',
            evidence: {
                checklist_title: 'Ebidentzia Egiaztapen-zerrenda',
                complete_pct: '% osatuta',
                no_checklist: 'Egiaztapen-zerrendako elementurik ez',
                attachments: 'Eranskinak',
                upload_attachment: 'Erantsi fitxategia',
                upload_button: 'Igo',
                uploading: 'Igotzen...',
                attachment_uploaded: 'Fitxategia igo da',
                upload_error: 'Ezin izan da fitxategia igo',
                category: 'Kategoria',
                categories: {
                    identity_proof: 'Nortasun froga',
                    correspondence: 'Korrespondentzia',
                    internal_data: 'Barne datuak',
                    legal: 'Juridikoa',
                    other: 'Beste',
                },
                no_attachments: 'Eranskinik ez',
                references_title: 'Ebidentzia Erreferentziak',
                no_references: 'Erreferentziarik ez',
                add_reference: 'Gehitu erreferentzia',
                ref_type: 'Mota',
                ref_value: 'Balioa',
                ref_desc: 'Deskribapena',
                ref_value_placeholder: 'URLa, eposta IDa edo erreferentzia...',
                ref_desc_placeholder: 'Deskribapena...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Eposta ID',
                    crm_record: 'CRM Erregistro',
                    ticket_ref: 'Tiketa Erreferentzia',
                    other: 'Beste',
                },
            },
            communications: {
                title: 'Komunikazio',
                add: 'Gehitu message',
                no_records: 'Ez dago komunikaziorik erregistratu',
                direction: 'Norabidea',
                channel: 'Kanala',
                visibility: 'Ikusgarritasuna',
                subject: 'Subjektua',
                body: 'Gorputza',
                send: 'Bidali',
                inbound: 'Sarrerako',
                outbound: 'Irteerako',
                internal: 'Barnekoa',
                external: 'Kanpokoa',
                tab_all: 'All',
                tab_internal: 'Barnekoa',
                tab_external: 'Kanpokoa',
                subject_placeholder: 'Subjektua (hautazkoa)',
                body_placeholder: 'Mezuaren gorputza...',
                channels: {
                    email: 'Eposta',
                    portal: 'Ataria',
                    phone: 'Telefonoa',
                    postal: 'Postala',
                    in_person: 'Aurrez aurre',
                },
                apply_template: 'Txantiloia Aplikatu',
                select_template: 'Hautatu txantiloia...',
                no_templates: 'Ez dago txantiloirik eskuragarri',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Txantiloia erantzunari aplikatuta.',
                template_apply_error: 'Ezin izan da txantiloia aplikatu.',
            },
            escalations: {
                title: 'Eskalazio Historia',
                no_events: 'Ez dago igoera-gertaerarik',
                tier: 'Maila',
                threshold: 'Atalase',
                action: 'Ekintza',
                target: 'Helburu',
                triggered_at: 'Abiarazita',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP sinkronizazioa',
                sync_status: 'Sync Egoera',
                last_synced: 'Azkenekoz sinkronizatua',
                not_synced: 'Sinkronizatu gabe',
                resync: 'Berriro sinkronizatu orain',
                syncing: 'Sinkronizatzen...',
                sync_history: 'Sync Historia (Last 10)',
                direction: 'Norabidea',
                time: 'Ordua',
                retries: 'Saiakera berriak',
                error_col: 'Errorea',
            },
            audit: {
                filter_event_placeholder: 'Iragazi by event type...',
                filter_actor_placeholder: 'Iragazi by actor...',
            },
            fulfillment: {
                title: 'Sail-betetzea',
                assign_departments: 'Sailak esleitu',
                department: 'Saila',
                assignee: 'Esleitua',
                deadline: 'Epemuga',
                evidence_count: 'Ebidentzia',
                signoff: 'Onespena',
                signed_off: 'Onetsita',
                pending_signoff: 'Zain',
                start_button: 'Hasi',
                complete_button: 'Osatuta markatu',
                signoff_button: 'Saioa amaitu',
                no_tasks: 'Oraindik ez da betetze-zereginik esleitu.',
                tasks_table: 'Betetze-zereginak',
                progress_label: 'Betetze-aurrerapena',
                overdue: 'atzeratuta',
                assigned_success: 'Sail-betetze zeregina arrakastaz esleitu da.',
                assign_error: 'Ezin izan da betetze-zeregina esleitu.',
                signoff_success: 'Sail-onespena erregistratu da.',
                signoff_error: 'Ezin izan da onespena erregistratu.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Ebidentzia gehitu',
                evidence_uploaded: 'Ebidentzia igo eta lotu da',
                evidence_upload_error: 'Ezin izan da ebidentzia igo',
                suggest_data_stores: 'Datu-biltegiak iradoki',
                suggest_error: 'Ezin izan dira datu-biltegi iradokizunak kargatu.',
                suggest_stores_title: 'Iradokitako datu biltegiak',
                suggest_stores_none: 'Ez dago kasu honekin bat datorren datu biltegirik. Konfiguratu sistemak Data Mapping-en iradokizunak hobetzeko.',
                suggest_stores_add: 'Gehitu kasuari',
                suggest_stores_added: 'Datu biltegia gehitu da kasuari',
                no_departments_title: 'Ez dago sailik konfiguratuta',
                no_departments_body: 'Sailek DSR betetzeko zereginak funtzio batera bideratzen uzten dizute (Pribatutasuna, Legea, HR, IT Ops). Instalatu lagin-departamenduak edo eskatu administratzaileari sailak konfiguratzeko Erakundearen ezarpenetan.',
                no_departments_cta: 'Instalatu lagin-datuak',
                department_placeholder: 'Sailaren izena...',
                assignee_placeholder: 'Erabiltzaile-IDa esleitzeko...',
                assign_button: 'Esleitu',
                status_pending: 'Zain',
                status_in_progress: 'Abian',
                status_completed: 'Osatua',
                status_verified: 'Egiaztatua',
                confirm_approve: 'Ziur zaude luzapen hau onartu nahi duzula?',
                confirm_deny: 'Ziur zaude luzapen hau ukatu nahi duzula?',
            },
            response: {
                title: 'Erantzun-sorkuntza',
                select_template: 'Erantzun-txantiloia',
                choose_template: 'Hautatu txantiloi bat...',
                generate: 'Erantzuna sortu',
                preview: 'Erantzunaren aurrebista',
                send: 'Erantzuna bidali',
                delivery_method: {
                    label: 'Bidalketa-metodoa',
                    email: 'Posta elektronikoa',
                    portal: 'Ataria',
                    postal: 'Posta posta',
                    in_person: 'Pertsonalki',
                },
                recipient: {
                    email: 'Hartzailearen posta elektronikoa',
                    portal: 'Atariko erabiltzailearen IDa',
                    address: 'Posta helbidea',
                },
                auto_generate_btn: 'Sortu eta bidali erantzuna',
                auto_generate_success: 'Erantzuna sortu eta eman',
                auto_generate_missing_recipient: 'Mesedez, eman hartzaile bat bidali aurretik.',
                auto_fields: 'Automatikoki betetako eremuak',
                case_id: 'Kasu-IDa',
                requester_name: 'Eskatzailearen izena',
                rights_type: 'Eskubide-mota',
                history: 'Bidalitako erantzunak',
                generated_at: 'Sortua',
                confirmation: 'Berrespena',
                sent_label: 'Bidalita',
                draft_label: 'Zirriborroa',
                generated_success: 'Erantzuna arrakastaz sortu da.',
                generate_error: 'Ezin izan da erantzuna sortu.',
                sent_success: 'Erantzuna arrakastaz bidali da.',
                send_error: 'Ezin izan da erantzuna bidali.',
                load_error: 'Ezin izan dira erantzun-txantiloiak kargatu.',
                pdf_title: 'Erantzun-gutuna datu-subjektuari',
                download_pdf: 'PDF deskargatu',
                download_all: 'Dena deskargatu',
                download_error: 'Ezin izan da erantzuna deskargatu',
            },
            extension: {
                title: 'Epemuga-luzapenak',
                acknowledgment_deadline: 'Aitorpen-epemuga',
                fulfillment_deadline: 'Betetze-epemuga',
                request_extension: 'Luzapena eskatu',
                reason: 'Arrazoia',
                reason_placeholder: 'Denbora gehigarria eskatzeko arrazoia...',
                additional_days: 'Egun gehigarriak',
                submit_request: 'Luzapen-eskaera bidali',
                history: 'Luzapen-historia',
                requested_at: 'Eskatua',
                approve: 'Onartu',
                deny: 'Ukatu',
                request_success: 'Luzapen-eskaera bidali da.',
                request_error: 'Ezin izan da luzapen-eskaera bidali.',
                approved_success: 'Luzapena onartua.',
                denied_success: 'Luzapena ukatua.',
                decision_error: 'Ezin izan da luzapen-erabakia prozesatu.',
                status_pending: 'Zain',
                status_approved: 'Onartua',
                status_denied: 'Ukatua',
                confirm_approve: 'Ziur zaude luzapen-eskaera hau onartu nahi duzula?',
                confirm_deny: 'Ziur zaude luzapen-eskaera hau ukatu nahi duzula?',
                decided_by: 'Erabaki duena',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Esportatu CSV',
            exporting: 'Esportatzen...',
            search_placeholder: 'Bilatu audit details...',
            filter_by_actor: 'Iragazi by actor...',
            all_event_types: 'Gertaera mota guztiak',
            seq: '#',
            timestamp: 'Denbora-zigilua',
            event: 'Gertaera',
            case_col: 'Kasu',
            actor: 'Aktorea',
            summary: 'Laburpena',
            no_events: 'Ez da audit events aurkitu',
            export_success: 'Esportatu da ikuskaritza-pista.',
            export_error: 'Huts egin du: export audit CSV. Saiatu berriro.',
            load_error: 'Huts egin du: load audit trail. Saiatu berriro.',
            announce_loaded: 'Ikuskaritza-pista kargatuta',
            start_date: 'Hasiera-data',
            end_date: 'Amaiera-data',
            per_page: '{count} / orrialde',
            unknown: 'Ezezaguna',
            source_filter: {
                label: 'Iturria',
                all: 'Denak',
                module: 'Modulua',
                forseti: 'Forseti',
                aria_label: 'Iragazi auditoria-gertaerak iturriaren arabera',
            },
            event_types: {
                case_created: 'Kasu Created',
                status_changed: 'Egoera Changed',
                case_assigned: 'Kasu Esleituta',
                note_added: 'Ohar Added',
                attachment_added: 'Eranskin Added',
                attachment_deleted: 'Eranskin Deleted',
                reference_added: 'Erreferentzia Added',
                checklist_updated: 'Egiaztapen-zerrenda Updated',
                identity_verified: 'Identity Egiaztatuta',
                closure_summary_added: 'Closure Laburpena',
                approval_granted: 'Onartzea emanda',
                template_applied: 'Txantiloi Applied',
                export_generated: 'Esportatu Generated',
                sla_warning: 'SLA Abisua',
                sla_overdue: 'SLA Atzeratuta',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Ikuskaritza-pista Bateratua',
                module_filter: 'Modulua',
                framework_filter: 'Esparrua',
                select_framework: 'Hautatu esparru bat...',
                entity_type_filter: 'Entitate-mota',
                action_filter: 'Ekintza',
                actor_filter: 'Eragilea',
                select_actor: 'Hautatu eragile bat...',
                search_filter: 'Bilatu',
                search_placeholder: 'Bilatu auditoretza-gertaerak...',
                filters_heading: 'Iragazkiak',
                date_range: 'Data-tartea',
                no_events_found: 'Ez da ikuskaritza-gertaerarik aurkitu.',
                event_count: 'Gertaera-kopurua',
                action: 'Ekintza',
                actor: 'Eragilea',
                date_range_start: 'Data-tartea hasiera',
                date_range_end: 'Data-tartea amaiera',
                filtering_events: 'Auditoretza-gertaerak iragazten',
                framework_codes: 'Esparru-kodeak',
                entity_type_col: 'Entitate mota',
                aria_table: 'Auditoretza-gertaera bateratuak',
                page_of: '{page}. orrialdea {total}(e)tik',
                entity_types: {
                    user: 'Erabiltzailea',
                    case: 'Kasua',
                    incident: 'Gorabehera',
                    assessment: 'Ebaluazioa',
                    vendor: 'Saltzailea',
                    consent: 'Adostasuna',
                    policy: 'Politika',
                    data_map: 'Datuen mapa',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Sortu',
                    update: 'Eguneratu',
                    delete: 'Ezabatu',
                    view: 'Ikusi',
                    export: 'Esportatu',
                    status_change: 'Egoera-aldaketa',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Ikuskaritza Ebidentzia-paketeak',
                assemble_pack: 'Ebidentzia-paketea Muntatu',
                framework: 'Esparrua',
                select_framework: 'Hautatu esparru bat...',
                framework_code_label: 'Esparruko kodea',
                entity_types: 'Entitate-motak',
                select_entity_types: 'Hautatu entitate-motak...',
                period: 'Aldia',
                period_start: 'Aldiaren hasiera',
                period_end: 'Aldiaren amaiera',
                completeness: 'Osotasuna',
                no_packs_found: 'Ez da ebidentzia-paketerik aurkitu.',
                verify: 'Egiaztatu',
                export_pack: 'Esportatu',
                exporting: 'Esportatzen...',
                pack_assembled: 'Ebidentzia-paketea osatua',
                pack_exported: 'Ebidentzia-paketea esportatua',
                aria_table: 'Auditoretza-ebidentzia paketeak',
                aria_export_pack: 'Esportatu paketea',
                export_format: 'Esportazio formatua',
                export_in_progress: 'Esportazioa abian...',
                status_draft: 'Zirriborroa',
                status_assembled: 'Muntatuta',
                status_verified: 'Egiaztatuta',
                status_submitted: 'Bidalita',
                entity_type_labels: {
                    user: 'Erabiltzailea',
                    case: 'Kasua',
                    incident: 'Gorabehera',
                    assessment: 'Ebaluazioa',
                    vendor: 'Saltzailea',
                    consent: 'Adostasuna',
                    policy: 'Politika',
                    data_map: 'Datuen mapa',
                },
                template_id: 'Txantiloi-IDa',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Modulua',
                    action: 'Ekintza',
                    actor_id: 'Aktorea',
                    actor_role: 'Actor Role',
                    entity_type: 'Entitate Mota',
                    entity_id: 'Entitatearen IDa',
                    summary: 'Laburpena',
                    timestamp: 'Denbora-zigilua',
                    record_type: 'Record Type',
                    framework_code: 'Esparrua',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Ebidentzia-paketea — Esparrua: {framework}',
                export_header_period: 'Aldia: {start}(e)tik {end}(e)ra',
                export_header_completeness: 'Osotasuna: {score}%',
                export_header_status: 'Egoera: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Ikuskaritza-aldiak',
                create_period: 'Ikuskaritza-aldia Sortu',
                framework_code_label: 'Esparruko kodea',
                select_framework: 'Hautatu esparru bat...',
                period_label: 'Aldi-etiketa',
                period_start: 'Aldiaren hasiera',
                period_end: 'Aldiaren amaiera',
                start: 'Hasi',
                end: 'Amaiera',
                framework: 'Esparrua',
                retention_days: 'Atxikipen-egunak',
                section_period_dates: 'Aldiaren datak',
                no_periods_found: 'Ez da ikuskaritza-aldirik aurkitu.',
                period_created: 'Auditoretza-aldia sortua',
                aria_table: 'Auditoretza-aldiak',
                suggested_boundaries: 'Muga iradokiak',
                suggested_boundaries_info: 'Aldi-muga iradokiak esparru bakoitzaren araudi-txosten-ziklotik eratortzen dira. Doitu zure erakundearen beharretara.',
                reporting_cycle: 'Txosten-zikloa',
                suggested_start: 'Hasiera iradokia',
                suggested_end: 'Amaiera iradokia',
                suggested_next: 'Iradokitako Hurrengo Epea',
                cycle_monthly: 'Hilerokoa',
                cycle_quarterly: 'Hiruhilabetekaria',
                cycle_semi_annual: 'Seiurtekoa',
                cycle_annual: 'Urtekoa',
                cycle_custom: 'Pertsonalizatua',
                period_deleted: 'Ikuskaritza-aldia ezabatu da',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Customer Erreferentzia',
            country: 'Herrialdea / Jurisdiction',
            preferred_channel: 'Kanal hobetsia',
            channels: {
                email: 'Eposta',
                postal: 'Postala',
                phone: 'Telefonoa',
                portal: 'Ataria',
            },
            source: 'Iturria',
            sources: {
                admin_manual: 'Admin eskuliburua',
                public_form: 'Forma publikoa',
                cmp_gateway: 'CMP Pasabidea',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefonoa jaso da',
                postal_received: 'Posta jasota',
                email_received: 'Posta elektronikoa jaso da',
                in_person: 'Pertsonalki',
                legacy_import: 'Inportazio ondarea',
            },
            required_fields: 'Eskaera type, izena, and email are required',
            create_error: 'Huts egin du: create case. Saiatu berriro.',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Ezarpenak',
            request_types: 'Eskaera motak',
            request_types_desc: 'Konfiguratu zein eskaera mota gaituta dauden harrera-formulario publikorako.',
            notifications: 'Jakinarazpenak',
            ticket_numbering: 'Txartel-zenbakitzea',
            ticket_format: 'Uneko formatua',
            ticket_prefix: 'Aurrizkia',
            ticket_prefix_note: 'ez da konfiguragarria',
            sla_deadlines: 'SLA epemugak',
            sla_desc: 'Ezarri gehieneko erantzun-denbora (egun naturaletan) eskaera mota bakoitzerako. Atalarira hurbiltzen diren kasuek abisuak erakutsiko dituzte.',
            days: 'egun',
            warning_threshold: 'Abisu-ataria',
            escalation_label: 'Gaitu eskalazio automatikoa SLA haustearen kasuan',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Gorde SLA konfigurazioa',
            saving: 'Gordetzen...',
            saved: 'Behar bezala gordeta',
            announce_loaded: 'Ezarpenak kargatuta',
            email_on_creation: 'Mezu elektroniko bidezko jakinarazpena txartela sortzean',
            send_confirmation: 'Bidali berrespen-mezua eskatzaileari',
            users: 'Erabiltzaileak',
            roles: 'Rolak',
            api_keys: 'API Teklak',
            mfa: 'MFA',
            sessions: 'Saioak',
            webhooks: 'Webhook-ak',
            modules: 'Moduluak',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Sarbidea (DSAR)',
                deletion: 'Ezabatzea',
                correction: 'Zuzenketa',
                portability: 'Eramangarritasuna',
                objection: 'Aurka egitea',
                restriction: 'Murrizketa',
                consent_withdrawal: 'Baimena atzera botatzea',
                protective_measures: 'Babes-neurriak',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Atal esleipenak',
            assign_section: 'Atala esleitu',
            section_ref: 'Atalaren erreferentzia',
            section_label: 'Atalaren etiketa',
            department_id: 'Saila',
            assignee_id: 'Esleitutakoa',
            mandatory: 'Nahitaezkoa',
            assign: 'Esleitu',
            no_assignments: 'Ez dago atal esleipenik',
            status: 'Egoera',
            completion: 'Osatzea',
            mark_complete: 'Osatu',
        },
        consolidation: {
            title: 'Finkatze-aurrerapena',
            add_child: 'Azpi-ataza gehitu',
            child_task_id: 'Azpi-atazaren IDa',
            department_id: 'Saila',
            link: 'Esteka',
            overall_completion: 'Osatze orokorra',
            departments_complete: 'Sailak osatuta',
            overdue: 'Epez kanpo',
            ready: 'Prest',
            not_ready: 'Ez prest',
            master_status: 'Egoera nagusia',
            department_breakdown: 'Sail-xehetasuna',
            department: 'Saila',
            status: 'Egoera',
            completion: 'Osatzea',
            due_date: 'Epemuga',
            overdue_flag: 'Epez kanpo',
            child_tasks: 'Azpi-atazak',
            task: 'Ataza',
        },
        conflict_detection: {
            title: 'Gatazken detekzioa',
            unresolved: 'konpondu gabe',
            no_conflicts: 'Ez da gatazkak aurkitu',
            section: 'Atala',
            field: 'Eremua',
            dept_a: 'Saila A',
            value_a: 'Balioa A',
            dept_b: 'Saila B',
            value_b: 'Balioa B',
            severity: 'Larritasuna',
            status: 'Egoera',
            resolved: 'Konponduta',
            open: 'Irekia',
            resolve: 'Konpondu',
        },
        org_maturity: {
            title: 'Erakundearen Heldutasuna',
            departments: 'Sailak',
            department_hierarchy: 'Sail Hierarkia',
            create_department: 'Saila Sortu',
            department_name: 'Sail Izena',
            department_code: 'Kodea',
            level: 'Maila',
            division: 'Dibisioa',
            department: 'Saila',
            team: 'Taldea',
            parent_department: 'Sail Nagusia',
            head_user: 'Sail Burua',
            no_departments: 'Ez dago sailik konfiguratuta.',
            user_mappings: 'Erabiltzaile Esleipenak',
            map_user: 'Erabiltzailea Esleitu',
            user_id: 'Erabiltzaile IDa',
            role: 'Rola',
            no_users: 'Ez dago erabiltzailerik sail honetan esleituta.',
            remove: 'Kendu',
            framework_assignments: 'Esparru Esleipenak',
            assign_framework: 'Esparrua Esleitu',
            entity_id: 'Entitate IDa',
            entity_type: 'Entitate Mota',
            framework_id: 'Esparru IDa',
            framework_code: 'Esparru Kodea',
            no_assignments: 'Ez dago esparru esleipenrik.',
            compliance_rollup: 'Betetze Laburpena',
            entity_name: 'Entitatea',
            total_assessments: 'Ebaluazio Guztira',
            completed: 'Osatua',
            completion_pct: 'Osaketa %',
            open_gaps: 'Hutsune Irekiak',
            critical_gaps: 'Kritikoak',
            compliance_score: 'Puntuazioa',
            no_rollup: 'Ez dago betetze laburpen daturik eskuragarri.',
            entity_summary: 'Entitatearen Betetze Laburpena',
            frameworks_assigned: 'Esparruak',
            overall_score: 'Puntuazio Orokorra',
            load_departments_error: 'Sailak kargatzeak huts egin du',
            load_users_error: 'Erabiltzaileak kargatzeak huts egin du',
            department_created: 'Departamentua sortua',
            department_created_announce: 'Departamentua sortua',
            create_department_error: 'Saila sortzeak huts egin du',
            user_mapped: 'Erabiltzailea esleitua',
            user_mapped_announce: 'Erabiltzailea departamentura esleitua',
            map_user_error: 'Erabiltzailea esleitzeak huts egin du',
            user_removed: 'Erabiltzailea kendua',
            load_rollup_error: 'Laburpena kargatzeak huts egin du',
            load_assignments_error: 'Esleipenak kargatzeak huts egin du',
            framework_assigned: 'Esparrua esleitua',
            framework_assigned_announce: 'Esparrua entitateari esleitua',
            assign_framework_error: 'Esparrua esleitzeak huts egin du',
            assignment_removed: 'Esleipena kendua',
            enter_entity_id_placeholder: 'Sartu entitatearen IDa esleipenak ikusteko',
            entity_type_organization: 'Antolaketa',
            entity_type_department: 'Departamentua',
        },
        onboarding_maturity: {
            title: 'Onboarding Heldutasuna',
            quick_starts: 'Hasiera azkarrak',
            progress: 'Onboarding Aurrerapena',
            personas: 'Pertsona Bideak',
            persona_onboarding: 'Pertsonaren barneratzea',
            quick_start_scenarios: 'Hasierako eszenatokiak',
            start: 'Hasi',
            restart: 'Berrabiarazi',
            estimated_minutes: 'Gutxi gorabeherako minutuak',
            steps: 'Urratsak',
            scenario: 'Eszenarioa',
            no_scenarios: 'Ez dago azkar hasteko agertokirik.',
            persona_id: 'Pertsonaren IDa',
            persona_label: 'Persona',
            role_description: 'Rolaren deskribapena',
            primary_modules: 'Lehen Hezkuntzako Moduluak',
            recommended_tours: 'Gomendatutako Tours',
            sample_data_modules: 'Lagin Datu Moduluak',
            quick_start_scenario: 'Abisu azkarreko eszenatokia',
            no_personas: 'Ez dago pertsona biderik eskuragarri.',
            framework_setup: 'Arau-esparruak',
            framework_code: 'Esparru Kodea',
            framework_name: 'Esparruaren izena',
            related_modules: 'Erlazionatutako Moduluak',
            persona_relevance: 'Pertsona garrantzitsuak',
            no_frameworks: 'Ez dago markoa konfiguratzeko aukerarik.',
            tier_config: 'Maila-moduluaren konfigurazioa',
            tier: 'Maila',
            available_modules: 'Eskuragarri dauden moduluak',
            module_count: 'Moduluen zenbaketa',
            overall_completion: 'Osaketa Orokorra',
            wizard_completed: 'Morroia amaitu da',
            tours_completed: 'Ibilaldiak amaituta',
            quick_starts_completed: 'Hasiera azkarrak amaitu dira',
            selected_frameworks: 'Hautatutako Esparruak',
            no_progress: 'Ez dago sartze-aurrerapen daturik eskuragarri.',
            detected_persona: 'Pertsona detektatua',
            current_tier: 'Oraingo maila',
            completion_pct: 'Osaketa',
            progress_page: {
                loaded: 'Aurrerapena kargatuta',
                no_progress: 'Ez dago inkorporazio-aurrerapenaren daturik eskuragarri.',
                overall: 'Aurrerapena orokorra',
                detected_persona: 'Detektatutako pertsonaia',
                tier: 'Maila',
                modules: 'moduluak',
                wizard: 'Laguntzailea',
                completed: 'Osatuta',
                pending: 'Zain',
                tours: 'Bisitak',
                quick_starts: 'Hasiera azkarrak',
                frameworks: 'Hautatutako esparruak',
                no_frameworks: 'Ez da esparrurik hautatu.',
                go_to_wizard: 'Konfigurazio-laguntzailea osatu',
                framework_selection: 'Esparru-hautapena',
                framework_toggle: 'Gaitu edo desgaitu esparruak zure betetze-programarako.',
                framework_updated: 'Esparru-hautapena eguneratua.',
                view_tours: 'Bisitak ikusi',
                view_quick_starts: 'Hasiera azkarrak ikusi',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Hautatu pertsonaia bat gomendatutako moduluak, bisitak eta hasiera azkarreko egoerak ikusteko.',
                no_personas: 'Ez dago pertsonaia-biderik eskuragarri.',
                modules: 'Moduluak',
                tours: 'Bisitak',
                quick_start: 'Hasiera azkarra',
                details: 'Xehetasunak',
                primary_modules: 'Modulu nagusiak',
                recommended_tours: 'Gomendatutako bisitak',
                framework_options: 'Arau-esparruak',
                no_frameworks: 'Ez dago esparru-konfigurazio aukerarik eskuragarri.',
                no_frameworks_explain: 'Oraindik ez dago arau-esparrurik instalatuta zure erakundearentzat, beraz hemen ez dago ezer konfiguratzeko.',
                no_frameworks_action: 'Instalatu merkatu-pakete bat zuri dagozkizun esparruak gehitzeko',
                start_tour: 'Hasi',
                no_tours: 'Ez dago bisitarik eskuragarri.',
                help_center: 'Laguntza-gunea',
                no_articles: 'Ez da artikulurik aurkitu.',
                primary: 'Nagusia',
                framework_code: 'Esparru-kodea',
                framework_label: 'Etiketa',
                region: 'Eskualdea',
                category: 'Kategoria',
                enabled: 'Gaituta',
                disabled: 'Desgaituta',
                tier_modules: 'Maila-modulu konfigurazioa',
                tiers_loaded: 'Maila-konfigurazioak kargatuta',
                no_tier_config: 'Ez dago maila-konfiguraziorik eskuragarri.',
                view_tours: 'Modulu-bisitak ikusi',
                view_quick_start: 'Hasiera azkarra hasi',
                toggle_framework: 'Esparrua aldatu',
                framework_updated: 'Esparru-hautapena eguneratua.',
                tier_desc_light: 'Pribatutasun oinarrizkoak — PDPL betetze-oinarriak',
                tier_desc_standard: 'Betetze-suite osoa — modulu guztiak lan-fluxuekin',
                tier_desc_enterprise: 'Gobernantza aurreratua eta automatizazioa — entitate anitz, talde-txostenak',
                persona_highlight: 'Zure moduluak',
                aria_go_to_module: '{name} — joan modulura',
                aria_start_tour: 'Hasi {title}',
                aria_quick_start: 'Hasiera azkarra: {title}',
                aria_help_article: 'Laguntza-artikulua: {title}',
                aria_view_framework: '{label}{primary} — ikusi markoaren xehetasunak',
                aria_primary_for_persona: '{name} (pertsona honentzako nagusia)',
                primary_for_persona_suffix: '(pertsona honetarako nagusia)',
                aria_view_in_persona_paths: '{name} — ikusi Persona Bideak atalean',
                progress_of_total: '{completed}/{total} osatu da',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Praktikatu ohiko lan-fluxuak gidatutako hasiera azkarreko egoerekin.',
                no_scenarios: 'Ez dago hasiera azkarreko egoerarik eskuragarri.',
                started: 'Hasiera azkarra hasita',
                status_completed: 'Osatuta',
                status_in_progress: 'Abian',
                status_not_started: 'Hasi gabe',
                minutes: 'min',
                steps: 'urratsak',
                step: 'Urratsa',
                restart: 'Berrabiarazi',
                start: 'Hasi',
                go_to_module: 'Modulura joan',
                next_step: 'Hurrengo urratsa',
                complete: 'Osatu',
                reset: 'Berrezarri',
                step_of: '/',
                action_navigate: 'Nabigatu',
                action_form_fill: 'Inprimakia bete',
                action_review: 'Berrikusi',
                action_confirm: 'Berretsi',
                step_changed: '{current}. urratsa {total}(e)tik: {title}',
                completed_message: 'Hasiera azkarra arrakastaz osatuta!',
                reset_message: 'Hasiera azkarra berrezarri da.',
            },
            persona_p_vn_01_label: 'Enpresa-mailako datu-babeserako ordezkari',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Saileko arduradun',
            persona_p_vn_02_description: 'Saileko buruzagia, atalez ataleko betetze-txostenen ardura duena',
            persona_p_vn_03_label: 'Merkataritza elektronikoaren betetze-arduradun',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Datu-gobernantzaren arduradun',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Zibersegurtasun- eta lege-eragiketen arduradun',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AAren gobernantzaren arduradun',
            persona_p_vn_07_description: 'AA sistemen gobernantza, arrisku-ebaluazioak eta sailkapena gainbegiratzen dituen arduraduna',
            persona_p_vn_08_label: 'Mugaz gaindiko transferentzien eta hornitzaileen gobernantza',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Urraketei erantzuteko arduradun',
            persona_p_vn_09_description: 'Urraketen jakinarazpena eta gertaeren erantzuna koordinatzen dituen arduraduna',
            persona_p_vn_10_label: 'Barne-auditoretzaren arduradun',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'GG & langile-datuen arduraduna',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Filial multinazionaleko betetze-arduraduna',
            persona_p_vn_12_description: 'DPOa atzerriko enpresaren Vietnameko filialean tokiko betetze-geruza behar duena',
            persona_p_vn_13_label: 'Hezkuntza betetze-arduraduna',
            persona_p_vn_13_description: 'Atzerriko eskolako betetze-arduraduna ikasleen eta gurasoen datuen babesa kudeatzen',
            qs_hr_consent_title: 'Langileen baimenak erregistratu',
            qs_hr_consent_description: 'Langileen baimena erregistratzeko eta estaldura-txostena sortzeko ibilbidea.',
            qs_subsidiary_dpia_title: 'Filialeko DBEE ebaluazioa sortu',
            qs_subsidiary_dpia_description: 'Filialeko datu-tratamendu jarduera baterako DBEE sortzeko ibilbidea.',
            qs_student_consent_title: 'Ikasleen baimen-bilketa konfiguratu',
            qs_student_consent_description: 'Ikasleen datuetarako guraso-baimen politikak sortzeko ibilbidea.',
            persona_p_vn_14_label: 'Kanpoko aholkulari juridiko',
            persona_p_vn_14_description: 'Lege-bulegoko bazkide nagusia, hainbat bezero-erakunderi PDPL betetzeari buruz aholkua ematen',
            persona_p_vn_15_label: 'Lege-bulegoko eragiketen kudeatzaile',
            persona_p_vn_15_description: 'Bezeroekiko harremanen koordinatzailea, entregagaien jarraipena eta epemugen koordinazioa kudeatzen',
            persona_p_vn_16_label: 'Negozioaren jabea / Zuzendari nagusi',
            persona_p_vn_16_description: 'Zuzendari nagusia edo gerentea, PDPLren arabera legez datu-arduraduna dena, betetze-inbertsioaren erantzulea',
            qs_dpia_filing_title: 'Osatu zure lehen DBEE aurkezpena',
            qs_dept_compliance_title: 'Aurkeztu zure lehen saileko betetze-atala',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Ezarri telekomunikazioetako harpidedunen datuen kudeaketa',
            qs_data_flow_title: 'Mapatu zure lehen datu-fluxua',
            qs_incident_response_title: 'Konfiguratu gertaerei erantzuteko lan-fluxua',
            qs_ai_risk_title: 'Egin AA sistema baten arrisku-ebaluazioa',
            qs_cross_border_title: 'Ebaluatu mugaz gaindiko datu-transferentzia bat',
            qs_breach_notification_title: 'Simulatu urraketa-jakinarazpenaren lan-fluxua',
            qs_audit_trail_title: 'Berrikusi betetze-auditoretzaren arrastoa',
            qs_legal_advisor_portfolio_title: 'Berrikusi bezero anitzen betetze-zorroa',
            qs_legal_advisor_portfolio_description: 'Berrikusi betetze-egoera hainbat bezero-erakunderen artean.',
            qs_law_firm_ops_title: 'Jarraitu bezeroaren entregagaien egoerari',
            qs_law_firm_ops_description: 'Gainbegiratu aholkularitza-entregagaien aurrerapena eta bezeroekiko harremana.',
            qs_ceo_dashboard_title: 'Ikusi zuzendaritza-mailako betetze-aginte-panela',
            qs_ceo_dashboard_description: 'Berrikusi maila altuko betetze-egoera eta inbertsio-metrikak.',
        },
        rights_requests: {
            response_templates: {
                title: 'Erantzun txantiloiak',
                subtitle: 'Kudeatu ResponseGenerator-ek interesdunei erantzunak idazteko erabiltzen dituen DSR erantzun-gutun txantiloiak.',
                create_btn: 'Txantiloi berria',
                edit_btn: 'Editatu txantiloia',
                filter_right_type: 'Iragazi eskuineko motaren arabera',
                filter_locale: 'Iragazi lokalaren arabera',
                filter_status: 'Iragazi egoeraren arabera',
                all_right_types: 'Mota egokiak',
                any_right_type: 'Edozein (mota guztietan aplikatzen da)',
                all_locales: 'Toki guztiak',
                all_statuses: 'Denak',
                status_active: 'Aktiboa',
                status_inactive: 'Inaktibo',
                empty_state: 'Ez dago txantiloirik uneko iragazkiekin.',
                columns: {
                    template_id: 'Txantiloiaren IDa',
                    outcome: 'Emaitza',
                    locale: 'Tokikoa',
                    right_type: 'Eskuineko Mota',
                    is_active: 'Egoera',
                    last_updated: 'Azken eguneratua',
                    actions: 'Ekintzak',
                },
                actions: {
                    edit: 'Editatu',
                    clone: 'Klonatu',
                    deactivate: 'Desaktibatu',
                    reactivate: 'Berriz aktibatu',
                    preview: 'Aurrebista',
                },
                form: {
                    template_id: 'Txantiloiaren IDa',
                    template_id_help: 'Minuskularen identifikatzailea, adibidez. dsr_access_fulfilled_vi',
                    outcome: 'Emaitza',
                    locale: 'Tokikoa',
                    right_type: 'Eskuineko Mota',
                    subject: 'Gaia',
                    body: 'Gorputz txantiloia',
                    body_help: 'Erabili {case_reference}, {data_subject_name}, {fulfilled_at} bezalako leku-markak. Gutxienez 15 lerro atal anitzeko letren kasuan.',
                    is_active: 'Aktiboa',
                    official_form_id: 'Inprimakiaren ID ofiziala (aukerakoa)',
                },
                outcomes: {
                    fulfilled: 'Bete',
                    partially_fulfilled: 'Partzialki beteta',
                    denied: 'Ukatua',
                },
                clone_modal_title: 'Klonatu txantiloia',
                clone_target_locale: 'Helburu lokala',
                clone_help: 'Txantiloi berri bat sortuko da helburuko lokalean txantiloi hau abiapuntu gisa erabiliz.',
                preview_modal_title: 'Txantiloiaren aurrebista',
                preview_note: 'Aurrebistak DSR-DEMO-0020 demo kasu-markak erabiltzen ditu. Benetako entregak ordezkatzen ditu zuzeneko kasuen datuak.',
                preview_unavailable: 'Aurrebistak lehendik dagoen kasu bat behar du. Erantsi txantiloi hau kasu bati eta erabili Erantzun Zirriborroa ekintza kasuaren xehetasunen orrian.',
                deactivate_confirm: 'Txantiloi hau desaktibatu nahi duzu? Lehendik dauden kasuetan aplikatzen jarraituko da.',
                toasts: {
                    created: 'Sortu da txantiloia',
                    updated: 'Txantiloia eguneratu da',
                    deactivated: 'Txantiloia desaktibatu da',
                    reactivated: 'Txantiloia berriro aktibatu da',
                    cloned: 'Txantiloia klonatu da',
                    load_error: 'Ezin izan dira txantiloiak kargatu',
                    save_error: 'Ezin izan da gorde txantiloia',
                    clone_error: 'Ezin izan da txantiloia klonatu',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Sailak',
                subtitle: 'Kudeatu DSR kasuak, DPIA zereginak eta betetze lan-fluxuak bideratzen dituzten sailak.',
                create_btn: 'Sail berria',
                edit_btn: 'Editatu Saila',
                columns: {
                    name: 'Izena',
                    description: 'Deskribapena',
                    active_cases: 'Kasu aktibo',
                    members: 'Kideak',
                    status: 'Egoera',
                    actions: 'Ekintzak',
                },
                status_active: 'Aktiboa',
                status_inactive: 'Inaktibo',
                form: {
                    name: 'Izena',
                    description: 'Deskribapena',
                },
                actions: {
                    edit: 'Editatu',
                    deactivate: 'Desaktibatu',
                    reactivate: 'Berriz aktibatu',
                    delete: 'Ezabatu',
                    manage_members: 'Kideak kudeatu',
                },
                delete_modal_title: 'Ezabatu Saila',
                delete_warning: 'Sail honek oraindik kasu aktibo ditu. Aukeratu ezabatu aurretik berriro esleitzeko sail bat.',
                delete_no_cases: 'Sail honek ez du kasu aktiborik eta segurtasunez ezaba daiteke.',
                reassign_prompt: 'Kasu aktiboak berriro esleitu honetara:',
                reassign_none_available: 'Ez dago beste sail aktiborik eskuragarri. Sortu beste sail bat lehenik.',
                member_section_title: 'Kideak',
                add_member: 'Gehitu kidea',
                remove_member: 'Kendu',
                member_role: 'Rola',
                no_members: 'Ez dago kiderik sail honetan.',
                toasts: {
                    created: 'Saila sortu',
                    updated: 'Saila eguneratua',
                    deactivated: 'Saila desaktibatu da',
                    reactivated: 'Saila berriro aktibatu da',
                    deleted: 'Saila ezabatu da',
                    reassigned_count: '{count} kasu berriro esleitu dira',
                    member_added: 'Kideak gehitu du',
                    member_removed: 'Kendu da kidea',
                    load_error: 'Ezin izan dira sailak kargatu',
                    save_error: 'Ezin izan da saila gorde',
                    delete_error: 'Ezin izan da saila ezabatu',
                },
            },
            retention_policies: {
                title: 'Atxikipen-politikak',
                subtitle: 'Konfiguratu helburu-motako atxikipen-leihoak. Nortasuna egiaztatzeko dokumentuek hau erabiltzen dute retention_expires_at zigilatzeko etorkizuneko kargaketan.',
                create_btn: 'Politika berria',
                edit_btn: 'Editatu politika',
                columns: {
                    target_type: 'Helburu mota',
                    retention_days: 'Atxikipena (egunak)',
                    owner: 'Politikaren jabea',
                    effective_from: 'Eraginkorra Notik',
                    last_reviewed: 'Azken berrikuspena',
                    status: 'Egoera',
                    actions: 'Ekintzak',
                },
                status_active: 'Aktiboa',
                status_inactive: 'Inaktibo',
                form: {
                    target_type: 'Helburu mota',
                    target_type_help: 'Atxikipen-leihoa zer datu-kategoriari aplikatzen zaio. Gaur egun, DSR identitatea egiaztatzeko dokumentuak soilik onartzen dira.',
                    retention_days: 'Atxikipen-leihoa (egunak)',
                    retention_days_help: '1 eta 3650 egun artean (10 urte gehienez).',
                    owner: 'Politikaren jabea',
                    owner_help: 'Normalean DPO. DG-2 pean atxikitzeko erabakiaren erantzule.',
                    effective_from: 'Eraginkorra Notik',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR identitatea egiaztatzeko agiriak',
                },
                deactivate_notice: 'Desaktibatzeak ez die eraginik lehendik zigilatutako eranskinei; etorkizuneko kargatzeek soilik ez dute jasoko atxikipen-leihorik.',
                deactivate_confirm: 'Atxikipen-gidalerro hau desaktibatu nahi duzu?',
                toasts: {
                    created: 'Atxikipen-politika sortu da',
                    updated: 'Atxikipen-politika eguneratu da',
                    deactivated: 'Atxikipen-politika desaktibatu da',
                    load_error: 'Ezin izan dira kargatu atxikipen-gidalerroak',
                    save_error: 'Ezin izan da gorde atxikipen-politika gorde',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Sistemak',
            categories: 'Datu Kategoriak',
            registry: 'Datuen Erregistroa',
            purposes: 'Prozesatzeko helburuak',
            retention: 'Kontserbazioa',
            flows: 'Datu-fluxuak',
            activities: 'Tratamendu-jarduerak',
            evidence: 'Frogak',
            exports: 'Esportazioak',
            attestation: 'Aitorpen Zikloak',
            governance: 'Gobernantza',
            review: 'Berrikuspen-zereginak',
            inventory: 'Inbentarioa',
            exceptions: 'Salbuespenak',
            history: 'Historia',
            ropa_completeness: 'ROPA Osotasuna',
        },
        ropa_completeness_autofix: {
            ask_button: 'Galdetu Forseti',
            drafting: 'Zirriborroa egiten…',
            accept: 'Onartu',
            reject: 'Baztertu',
            accepted: 'Onartua — aplikatu jarduera-editorearen bidez.',
            rejected: 'Baztertua.',
            needs_human_input: 'Forsetik jarduera-testuinguru gehiago behar du (adibidez, helburu dokumentatua) eremu hau idatzi aurretik.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA Osotasun Txostena',
            subtitle: 'Prozesatzeko Jardueren Erregistroen eremu bakoitzeko higiene-auditoria, arau-esparruaren arabera.',
            framework_label: 'Esparrua',
            kpi_total: 'Puntuatutako jarduerak',
            kpi_critical: 'Kritikoa',
            kpi_high: 'Alta',
            kpi_conditional: 'Baldintzazkoa',
            field_chart_heading: 'Eremu faltak maiztasunaren arabera',
            drilldown_heading: 'Hutsuneak dituzten jarduerak',
            col_activity: 'Jarduera',
            col_field: 'Eremua',
            col_severity: 'Larritasuna',
            col_condition: 'Baldintza',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritikoa',
            severity_high: 'Alta',
            severity_conditional: 'Baldintzazkoa',
            severity_informational: 'Informazioa',
            include_informational: 'Sartu informazioa',
            export_csv: 'Deskargatu CSV',
            exporting: 'Esportatzen...',
            loading: 'Kargatzen…',
            empty_state: 'Jarduera aktibo guztiek ROPA erregistro osoa dute esparru honetarako.',
            error_fetch: 'Ezin izan da kargatu osotasun-txostena.',
            error_unsupported_framework: 'Esparru hau ez da onartzen.',
            error_framework_not_installed: 'Esparru hau ez dago zure erakunderako instalatuta.',
            no_misses_for_field: 'Eremu honi falta zaion jarduerarik.',
            framework_PDPL: 'PDPL (91/2025 Legea + 356 Dekretua)',
            framework_GDPR: 'GDPR 30. artikulua',
            framework_DECREE_165: '165 Dekretua (Vietnam Datuen Legea)',
            framework_ISO_27701: 'ISO 27701 (informatiboa)',
        },
        registry: {
            // Sprint 64.72.0 — datu-erregistroaren esportazioaren zutabe-goiburuak.
            export_col: {
                registry_item_id: 'Erregistro-elementuaren IDa',
                name: 'Izena',
                description: 'Deskribapena',
                field_identifiers: 'Eremu-identifikatzaileak',
                category_id: 'Kategoriaren IDa',
                category_name: 'Kategoriaren izena',
                data_type: 'Datu mota',
                effective_sensitivity: 'Sentiberatasun eragingarria',
                sensitivity_override: 'Sentiberatasun ordezkatzailea',
                data_subjects: 'Datuen titularrak',
                volume_band: 'Bolumen-tartea',
                source_system_ids: 'Jatorrizko sistemen IDak',
                processing_activity_ids: 'Tratamendu-jardueren IDak',
                retention_policy_id: 'Kontserbazio-politikaren IDa',
                effective_retention_policy_id: 'Kontserbazio-politika eragingarriaren IDa',
                purposes: 'Helburuak',
                collection_method: 'Bilketa-metodoa',
                legal_basis: 'Oinarri juridikoa',
                data_tier: 'Datu-maila',
                data_tier_source: 'Datu-mailaren jatorria',
                storage_locations: 'Biltegiratze-kokalekuak',
                cross_border: 'Mugaz gaindikoa',
                status: 'Egoera',
                source_form_submission_id: 'Jatorrizko inprimakiaren bidalketa-IDa',
                source_form_template_id: 'Jatorrizko inprimakiaren txantiloi-IDa',
                last_reviewed_at: 'Azken berrikuspena',
                next_review_at: 'Hurrengo berrikuspena',
                created_at: 'Sortze-data',
                updated_at: 'Eguneratze-data',
                data_element: 'Datu-elementua',
                data_category: 'Datu-kategoria',
                systems: 'Sistemak',
                retention_policy: 'Kontserbazio-politika',
                name_of_processing: 'Tratamenduaren izena',
                categories_of_personal_data: 'Datu pertsonalen kategoriak',
                categories_of_data_subjects: 'Datuen titularren kategoriak',
                recipients: 'Hartzaileak',
                third_country_transfers: 'Hirugarren herrialdeetarako transferentziak',
                retention_period: 'Kontserbazio-epea',
                doc_title: 'Datu-erregistroa',
                doc_generated: 'Esportazioa sortua {timestamp}',
                doc_item_count: '{count} erregistro-elementu',
                doc_template_full: 'Osoa',
            },
            export_vocab: {
                data_type: {
                    text: 'Testua',
                    number: 'Zenbakia',
                    date: 'Data',
                    datetime: 'Data eta ordua',
                    boolean: 'Bai/ez',
                    binary: 'Bitarra',
                    structured: 'Egituratua',
                    file: 'Fitxategia',
                    identifier: 'Identifikatzailea',
                    geolocation: 'Geokokapena',
                },
                collection_method: {
                    direct_from_subject: 'Zuzenean datuen titularrarengandik',
                    third_party: 'Hirugarren batengandik',
                    derived: 'Beste datu batzuetatik eratorria',
                    observed: 'Behatua',
                    inferred: 'Ondorioztatua',
                    public_source: 'Iturri publikoa',
                },
                data_subject_type: {
                    customer: 'Bezeroa',
                    employee: 'Langilea',
                    contractor: 'Kontratista',
                    prospect: 'Balizko bezeroa',
                    minor: 'Adingabea',
                    patient: 'Gaixoa',
                    student: 'Ikaslea',
                    beneficiary: 'Onuraduna',
                    vendor_contact: 'Hornitzailearen kontaktua',
                    website_visitor: 'Webgunearen bisitaria',
                    other: 'Bestelakoa',
                },
                volume_band: {
                    lt_1k: '1.000 erregistro baino gutxiago',
                    '1k_to_10k': '1.000 eta 10.000 erregistro artean',
                    '10k_to_100k': '10.000 eta 100.000 erregistro artean',
                    '100k_to_1m': '100.000 eta milioi bat erregistro artean',
                    gt_1m: 'Milioi bat erregistro baino gehiago',
                    unknown: 'Ezezaguna',
                },
                tier_source: {
                    assessed_and_approved: 'Ebaluatua eta onartua',
                    manual_legacy: 'Aurreko balio batetik hartua',
                    legacy_platform_field: 'Plataformatik jasotako balioa',
                    scheme_scoped_only: 'Eskema bakoitzaren mailen arabera ebaluatua — kode bakar batek ere ez ditu lege-mailen eskala anitz biltzen',
                },
            },
            page: {
                title: 'Datuen Erregistroa',
                subtitle: 'Zure erakundeak dauzkan datu pertsonalen elementuak, Datu Kategorietan antolatuta.',
                empty_state: 'Oraindik ez dago erregistroko elementurik. Gehitu elementu bat eskuz edo inportatu erregistro erregulatzaile batetik.',
                empty_filter_state: 'Ez dago zure iragazkiekin bat datorren elementurik.',
            },
            filter: {
                category: 'Kategoria',
                sensitivity: 'Sentikortasuna',
                data_subject: 'Datu-gaia',
                status: 'Egoera',
                data_tier: 'Datu maila',
                cross_border: 'Mugaz gaindi',
                has_owner: 'Jabea du',
                has_form_source: 'Inprimakitik inportatua',
                search_placeholder: 'Bilatu izena, deskribapena edo eremu-identifikatzailea...',
                clear: 'Garbitu iragazkiak',
                option_all: 'Denak',
            },
            col: {
                name: 'Izena',
                category: 'Kategoria',
                sensitivity: 'Sentikortasuna',
                data_subjects: 'Datuen subjektuak',
                systems: 'Sistemak',
                activities: 'Jarduerak',
                owner: 'Jabea',
                retention: 'Atxikipena',
                data_tier: 'Datu maila',
                status: 'Egoera',
                last_reviewed: 'Azken berrikuspena',
                actions: 'Ekintzak',
            },
            action: {
                add_manual: 'Gehitu elementua',
                add_from_form: 'Gehitu erregistro erregulatzailetik',
                import_history: 'Inportatu iraganeko bidalketak',
                export: 'Esportatu',
                archive: 'Artxiboa',
                restore: 'Berreskuratu',
                edit: 'Editatu',
                assign_owner: 'Esleitu jabea',
                attach_evidence: 'Erantsi frogak',
                open_lineage: 'Leinu irekia',
            },
            modal_editor: {
                section_identity: 'Identitatea',
                section_classification: 'Sailkapena',
                section_subjects: 'Gaiak eta bolumena',
                section_location: 'Kokapena eta transferentzia',
                section_systems: 'Sistemak eta Jarduerak',
                section_retention: 'Atxikipena eta legezkoa',
                section_governance: 'Gobernantza',
                section_notes: 'Oharrak',
                hint_inherited: '(kategoriatik heredatua)',
                hint_overridden: '(gaineztatu)',
                placeholder_field_identifiers: 'adibidez, bezeroak.primary_email, hris.employee_id',
                validation_name_required: 'Izena behar da',
                validation_category_required: 'Kategoria beharrezkoa da',
                validation_data_subjects_required: 'Datu-gai mota bat gutxienez behar da',
                label_data_type: 'Datu mota',
                label_field_identifiers: 'Eremu-identifikatzaileak (komaz bereizita)',
                label_data_subject_types: 'Datuen subjektu motak',
                label_volume_band: 'Bolumen banda',
                label_storage_locations: 'Biltegiratze kokapenak (komaz bereizita)',
                placeholder_storage_locations: 'ISO-3166 kodeak edo testu librea (adibidez, VN, Hetzner EU)',
                label_cross_border: 'Mugaz gaindiko transferentzia',
                label_source_systems: 'Iturburu-sistemak',
                label_processing_activities: 'Prozesatzeko jarduerak',
                label_collection_method: 'Bilketa metodoa',
                label_purposes: 'Helburuak',
                hint_multi_select: 'Mantendu Cmd/Ctrl sakatuta anitz hautatzeko.',
                option_inherit_category: '— (kategoriatik heredatu)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Inportatu aurrebista',
                label_to_create: 'Sortzeko',
                label_to_update: 'Eguneratzeko',
                label_skipped_archived: 'Saltatu (artxibatuta)',
                label_conflicts: 'Gatazkak',
                commit: 'Inportatu',
                cancel: 'Utzi',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Kategoria',
                section_systems: 'Iturburu-sistemak',
                section_activities: 'Prozesatzeko jarduerak',
                section_flows: 'Datu-fluxuak',
                section_retention: 'Atxikipen eraginkorra',
                section_governance: 'Gobernantza',
                section_evidence: 'Frogak',
                section_audit: 'Azken ikuskaritza-pista',
                section_source_form: 'Iturria inprimakia bidaltzea',
                badge_inherited: 'Heredatua',
                badge_overridden: 'Gaindituta',
            },
            toast: {
                created: 'Erregistroko elementua sortu da.',
                updated: 'Erregistroko elementua eguneratu da.',
                archived: 'Erregistroko elementua artxibatu da.',
                imported: '{count} elementu inportatu dira.',
                conflict_warning: '{count} eremuek zure aldaketak gorde dituzte — ikusi gatazka-zerrenda.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Sistemen inbentarioa',
            new: 'Sistema berria',
            edit: 'Editatu sistema',
            name: 'Izena',
            owner: 'Jabea',
            criticality: 'Kritikotasuna',
            dpa_status: 'DPA egoera',
            location: 'Kokapena',
            system_type: 'Sistema mota',
            metadata: 'Metadatuak (JSON)',
            department: 'Saila',
            hosting_location: 'Ostalaritza-kokapena',
            hosting_provider: 'Ostalaritza-hornitzailea',
            data_residency_country: 'Datuen egoitza-herrialdea',
            hosting: 'Ostalaritza',
            no_systems: 'Ez da sistemarik aurkitu',
            confirm_archive: 'Artxibatu sistema "{name}"?',
            count: 'sistemak',
            count_one: 'sistema',
            type_internal: 'Barnekoa',
            type_external: 'Kanpokoa',
            type_cloud_service: 'Hodei-zerbitzua',
            type_on_premise: 'Tokikoa',
            type_hybrid: 'Hibridoa',
            status_active: 'Aktiboa',
            status_decommissioning: 'Desaktibatzen',
            status_decommissioned: 'Desaktibatua',
            status_planned: 'Planifikatua',
            dpa_not_required: 'Ez beharrezkoa',
            dpa_required_missing: 'Beharrezkoa falta',
            dpa_in_progress: 'Abian',
            dpa_signed: 'Sinatua',
            all_criticalities: 'Kritikotasun guztiak',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Datu-kategoriak',
            new: 'Kategoria berria',
            edit: 'Editatu kategoria',
            name: 'Izena',
            sensitivity: 'Sentikortasuna',
            tags: 'Etiketak',
            used_in: 'Erabiltzen da hemen',
            examples: 'Adibideak',
            examples_hint: 'Adibideak (komaz bereiziak)',
            default: 'Lehenetsia',
            all_sensitivities: 'Sentikortasun guztiak',
            all_statuses: 'Egoera guztiak',
            no_categories: 'Ez da kategoriarik aurkitu',
            confirm_delete: 'Ezabatu kategoria "{name}"?',
            count: 'kategoriak',
            count_one: 'kategoria',
            status_draft: 'Zirriborroa',
            status_proposed: 'Proposatua',
            status_under_review: 'Berrikuspenean',
            status_approved: 'Onartua',
            status_deprecated: 'Zaharkitua',
            status_updated: 'Kategoriaren egoera eguneratua',
            propose: 'Proposatu',
            approve: 'Onartu',
            deprecate: 'Zaharkitu',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Tratamendu-xedeak',
            new: 'Xede berria',
            edit: 'Editatu xedea',
            name: 'Izena',
            legal_basis: 'Oinarri juridikoa',
            retention_period: 'Kontserbazio-epea (egunak)',
            justification: 'Interes legitimoaren justifikazioa',
            legal_basis_detail: 'Oinarri juridikoaren xehetasuna',
            all_legal_bases: 'Oinarri juridiko guztiak',
            no_purposes: 'Ez da xederik aurkitu',
            confirm_delete: 'Ezabatu "{name}"?',
            default: 'Lehenetsia',
            count: '{count} xede',
            count_one: '{count} xede',
        },
        retention: {
            title: 'Kontserbazio-politikak',
            new: 'Politika berria',
            edit: 'Editatu politika',
            name: 'Izena',
            retention_days: 'Kontserbazio-egunak',
            disposal_method: 'Xedatze-metodoa',
            data_categories: 'Datu-kategoriak',
            activities: 'Jarduerak',
            review_frequency: 'Berrikustapen-maiztasuna',
            review_frequency_days: 'Berrikustapen-maiztasuna (egunak)',
            no_policies: 'Ez da kontserbazio-politikarik aurkitu',
            confirm_delete: 'Ezabatu "{name}"?',
            count: '{count} politika',
            count_one: '{count} politika',
            days: 'egun',
            disposal_deletion: 'Ezabatzea',
            disposal_anonymization: 'Anonimizazioa',
            disposal_archival: 'Artxibatzea',
            review_overdue: 'Berrikusketa atzeratua',
            review_status: 'Berrikusketa egoera',
            review_ok: 'Egokia',
            department: 'Saila',
            all_departments: 'Sail guztiak',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Fluxuen ikuspegia',
            view_list: 'Zerrenda',
            view_map: 'Mapa',
            title: 'Datu-fluxuak',
            new: 'Fluxu berria',
            edit: 'Editatu fluxua',
            name: 'Izena',
            from_system: 'Jatorrizko sistema',
            to_system: 'Helburuko sistema',
            flow_type: 'Fluxu mota',
            cross_border: 'Mugaz gaindikoa',
            risk_level: 'Arrisku-maila',
            mechanism: 'Mekanismoa',
            transfer_mechanism: 'Transferentzia-mekanismoa',
            frequency: 'Maiztasuna',
            source_country: 'Jatorrizko herrialdea',
            destination_country: 'Helburuko herrialdea',
            transfer_safeguard: 'Transferentzia-bermea',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Arrisku-maila guztiak',
            no_flows: 'Ez da datu-fluxurik aurkitu',
            confirm_archive: 'Artxibatu "{name}"?',
            count: '{count} fluxu',
            count_one: '{count} fluxu',
            status_active: 'Aktiboa',
            status_inactive: 'Ez-aktiboa',
            status_under_review: 'Berrikustapen',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Fitxategi-transferentzia',
            mechanism_database_replication: 'Datu-base erreplikazioa',
            mechanism_manual: 'Eskuzkoa',
            mechanism_email: 'Posta elektronikoa',
            mechanism_other: 'Beste bat',
            frequency_real_time: 'Denbora errealean',
            frequency_hourly: 'Orduro',
            frequency_daily: 'Egunero',
            frequency_weekly: 'Astero',
            frequency_monthly: 'Hilero',
            frequency_on_demand: 'Eskariz',
            frequency_one_time: 'Behin bakarrik',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdikzioa',
        },
        activities: {
            title: 'Tratamendu-jarduerak',
            new: 'Jarduera berria',
            edit: 'Editatu jarduera',
            activity_title: 'Izenburua',
            category: 'Kategoria',
            legal_basis: 'Oinarri juridikoa',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA sortua',
            version: 'Bertsioa',
            generate_ropa: 'Sortu RoPA',
            controller: 'Tratamenduaren arduraduna',
            controller_name: 'Tratamenduaren arduradunaren izena',
            controller_contact: 'Tratamenduaren arduradunaren kontaktua',
            legal_basis_detail: 'Oinarri juridikoaren xehetasuna',
            organization_info: 'Organization Information',
            org_address: 'Erakundearen helbidea',
            tax_code: 'Zerga-kodea',
            contact_person: 'Kontaktu-pertsona',
            contact_email: 'Kontaktu-posta',
            contact_phone: 'Kontaktu-telefonoa',
            select_entity: 'Hautatu…',
            no_entities: 'Ez da elementurik aurkitu',
            dpo_name: 'DBOaren izena',
            dpo_contact: 'DBOaren kontaktua',
            processor_name: 'Tratamendu-arduradunaren izena',
            processor_contact: 'Tratamendu-arduradunaren kontaktua',
            data_subjects: 'Interesdunak',
            data_subjects_hint: 'Interesdunak (komaz bereiziak)',
            recipients: 'Hartzaileak',
            recipients_hint: 'Hartzaileak (komaz bereiziak)',
            security_measures: 'Segurtasun-neurriak',
            dpia_reference: 'DBEE erreferentzia',
            no_activities: 'Ez da tratamendu-jarduerarik aurkitu',
            confirm_archive: 'Artxibatu "{name}"?',
            count: '{count} jarduera',
            count_one: '{count} jarduera',
            ropa_success: 'RoPA erregistroa sortu da. Joan Esportazioetara deskargatzeko.',
            ropa_error: 'RoPA sortzeak huts egin du',
            required_label: 'Beharrezkoa',
            status_draft: 'Zirriborroa',
            status_active: 'Aktiboa',
            status_under_review: 'Berrikustapen',
            status_archived: 'Artxibatua',
            decree_165_heading: '165 Dekretuaren datuen maila (Vietnam Datuen Legea)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Datu maila',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Datu maila',
            data_tier_regular: 'Erregularra',
            data_tier_important: 'Garrantzitsua',
            data_tier_core: 'Nukleoa',
            transfer_mechanism_label: 'Mugaz gaindiko transferentzia-mekanismoa',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Segurtasunaren ebaluazioaren erreferentzia',
            security_assessment_reference_placeholder: 'adib. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Froga-biltegi',
            new: 'Froga berria',
            edit: 'Editatu froga',
            name: 'Izena',
            evidence_type: 'Froga mota',
            linked_entities: 'Lotutako entitateak',
            uploaded: 'Igota',
            expiry: 'Iraungitzea',
            file_url: 'Fitxategiaren URLa',
            source_mode_label: 'Ebidentziaren iturria',
            source_mode_file: 'Igo fitxategia',
            source_mode_url: 'Lotu kanpo-URLa',
            attachment_label: 'Eranskina',
            link_to: 'Lotu froga honi',
            entity_type: 'Entitate mota',
            entity_id: 'Entitate IDa',
            no_evidence: 'Ez da frogarik aurkitu',
            manage_links: 'Kudeatu estekak',
            unlink: 'Kendu',
            confirm_delete: 'Ezabatu froga "{name}"?',
            count: '{count} elementu',
            count_one: '{count} elementu',
            type_document: 'Dokumentua',
            type_screenshot: 'Pantaila-argazkia',
            type_policy: 'Politika',
            type_contract: 'Kontratua',
            type_assessment: 'Ebaluazioa',
            type_correspondence: 'Korrespondentzia',
            type_other: 'Beste bat',
            linked_type_system: 'Sistema',
            linked_type_data_flow: 'Datu-fluxua',
            linked_type_processing_activity: 'Prozesatzeko Jarduera',
            linked_type_data_category: 'Datuen Kategoria',
            linked_type_purpose: 'Helburua',
            linked_type_retention_policy: 'Atxikipen politika',
            link_entity: 'Esteka Entitatera',
            link_success: 'Ondo lotu da entitatea',
            confirm_unlink: 'Ziur entitate hau deskonektatu nahi duzula?',
            unlink_success: 'Entitatea behar bezala kendu da',
            no_linked_entities: 'Lotutako entitaterik ez',
            select_entity: 'Hautatu…',
            no_entities: 'Ez dago elementurik',
        },
        exports: {
            title: 'Datu-esportazioak',
            recent: 'Azken esportazioak',
            export_type: 'Esportazio mota',
            format: 'Formatua',
            requested_by: 'Eskatzailea',
            requested_at: 'Eskatze-data',
            download: 'Deskargatu',
            csv_export: 'CSV esportazioa',
            csv_description: 'Deskargatu datuak CSV formatuan kalkulu-orrien azterketarako.',
            pdf_reports: 'PDF txostenak',
            pdf_description: 'Sortu formatu-txostenak betetze-dokumentaziorako.',
            csv_systems: 'Sistemen inbentarioa',
            csv_categories: 'Datu-kategoriak',
            csv_flows: 'Datu-fluxuak',
            csv_activities: 'Tratamendu-jarduerak',
            csv_cross_border: 'Mugaz gaindiko fluxuak',
            csv_retention_policies: 'Atxikipen-politikak',
            csv_evidence: 'Froga-elementuak',
            csv_governance_assignments: 'Gobernantza Esleipenak',
            pdf_ropa: 'RoPA erregistroa',
            pdf_systems: 'Sistemen txostena',
            pdf_cross_border: 'Mugaz gaindiko txostena',
            pdf_flow_map: 'Datu-fluxuen mapa',
            export_failed: 'Esportazioak huts egin du',
            pdf_generated: 'PDF arrakastaz sortu da.',
            ropa: 'RoPA Erregistroa',
            retention_policies: 'Atxikipen-politikak',
            governance_assignments: 'Gobernantza Esleipenak',
            col_entity: 'Esportatu Entitatea',
            col_description: 'Deskribapena',
            desc_ropa: 'Lege-oinarria, DPIA egoera eta kontrolak dituzten prozesatzeko jarduera guztien ROPA erregistro osoa.',
            desc_systems: 'Sistemen inbentarioa jabearekin, datu-kategorien etiketarekin eta betetze-egoerarekin.',
            desc_activities: 'Arduraduna, legezko oinarria eta DPIA sailkapena duten banakako prozesatzeko jarduerak.',
            desc_categories: 'Sailkapen eta auditoretzetan erabiltzen diren datu-kategoriak eta sentikortasun-mailak.',
            desc_purposes: 'Tratamendu-helburuak, bakoitzerako erregistratutako lege-oinarriarekin.',
            desc_flows: 'Sistemen arteko datu-fluxuak, barne eta mugaz gaindiko ibilbideak barne.',
            desc_cross_border: 'Mugaz gaindiko fluxuak soilik, transferentzia-mekanismoarekin eta arrisku-ebaluazioarekin.',
            desc_retention_policies: 'Atxikipen-politikak, ezabatzeko metodoak eta berrikusteko kadentzia.',
            desc_evidence: 'Prozesatzeko jarduerei erantsitako frogak: ziurtagiriak, DPA kontratuak, DPIA txostenak.',
            desc_governance_assignments: 'Gobernantza-esleipenak: jabeak, onartzaileak eta ISO 27001 erreferentziak.',
            unified_heading: 'Esportazio Entitateak',
            unified_description: 'Esportatu datuak mapatzeko edozein entitate CSV, XLSX, PDF edo DOCX formatuan. Esportazio guztiak fitxategi-arakatzailean gordetzen dira.',
            export_button_label: 'Esportatu',
            csv_download_success: 'CSV arrakastaz deskargatu da.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Atestatze Zikloak',
            create: 'Ziklo Berria',
            name: 'Zikloaren Izena',
            year: 'Urtea',
            deadline: 'Epemuga',
            scope: 'Irismena',
            status: 'Egoera',
            tasks: 'Sail-atazak',
            assign_tasks: 'Atazak esleitu',
            department: 'Saila',
            assignee: 'Esleitua',
            progress: 'Aurrerapena',
            stale_entries: 'Zaharkitutako sarrerak',
            detect_stale: 'Zaharkitutakoak antzeman',
            export_bundle: 'Paketea esportatu',
            no_cycles: 'Ez dago atestatze ziklorik oraindik.',
            create_success: 'Atestatze zikloa sortua.',
            assign_success: 'Atazak ondo esleituta.',
            complete_success: 'Ataza osatua.',
            stale_detected: 'Zaharkitutako sarrerak antzemanda.',
            stale_systems: 'Zaharkitutako sistemak',
            stale_flows: 'Zaharkitutako fluxuak',
            no_stale: 'Ez da zaharkitutako sarrerarik antzeman.',
            stale_name: 'Izena',
            stale_announcement: '{0} sistema zaharkitu eta {1} fluxu zaharkitu aurkitu dira',
            last_updated: 'Azken eguneraketa',
            status_draft: 'Zirriborroa',
            status_active: 'Aktiboa',
            status_completed: 'Osatua',
            task_status_pending: 'Zain',
            task_status_in_progress: 'Abian',
            task_status_completed: 'Osatua',
            scope_systems: 'Sistemak',
            scope_flows: 'Datu-fluxuak',
            scope_activities: 'Jarduerak',
            scope_categories: 'Kategoriak',
            scope_purposes: 'Helburuak',
            scope_retention: 'Atxikipena',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Gobernantza Esleipenak',
            assign: 'Jabetza esleitu',
            entity_type: 'Entitate Mota',
            entity: 'Entitatea',
            role: 'Rola',
            assigned_by: 'Esleitzailea',
            assigned_at: 'Esleitze data',
            remove: 'Kendu',
            no_assignments: 'Ez dago gobernantza esleipenik.',
            assign_success: 'Jabetza esleitua.',
            remove_success: 'Esleipena kendua.',
            assignee: 'Esleitua',
            department: 'Saila',
            all_departments: 'Sail guztiak',
            roles: {
                data_owner: 'Datu Jabea',
                data_steward: 'Datu Kudeatzailea',
                data_custodian: 'Datu Zaintzailea',
                informed: 'Informatua',
            },
            entity_types: {
                system: 'Sistema',
                flow: 'Datu-fluxua',
                activity: 'Jarduera',
                category: 'Kategoria',
                purpose: 'Helburua',
                retention_policy: 'Atxikipen-politika',
            },
        },
        risk_assessment: {
            title: 'Arrisku Ebaluazioa',
            create: 'Ebaluazio Berria',
            safeguard_type: 'Babes Mota',
            adequacy_status: 'Egokitasun Egoera',
            risk_factors: 'Arrisku Faktoreak',
            mitigations: 'Arintzeko Neurriak',
            reviewer: 'Berrikuskatzailea',
            decision: 'Erabakia',
            approve: 'Onartu',
            reject: 'Baztertu',
            no_assessments: 'Ez dago arrisku ebaluaziorik.',
            create_success: 'Arrisku ebaluazioa sortua.',
            approve_success: 'Ebaluazioa onartua.',
            reject_success: 'Ebaluazioa baztertua.',
            one_per_line: 'Bat lerro bakoitzeko',
            adequacy_adequate: 'Egokia',
            adequacy_inadequate: 'Desegokia',
            adequacy_under_review: 'Berrikuspenean',
            adequacy_not_assessed: 'Ebaluatu gabe',
            decision_approved: 'Onartua',
            decision_rejected: 'Baztertua',
        },
        review: {
            title: 'Berrikuspen-zereginak',
            description: 'Berrikuspena-zereginak automatikoki sortzen dira datu-kategoria bat proposatzen denean. Zeregin berri bat sortzeko, joan Datu-kategorietara eta egin klik Proposatu zirriborro-kategoria batean.',
            count: 'berrikuspen-zereginak',
            summary: 'Berrikuspenaren laburpena',
            pending: 'Zain',
            resolved_today: 'Gaur ebatziak',
            escalated: 'Eskalatua',
            overdue: 'Atzeratua (>5 egun)',
            all_statuses: 'Egoera guztiak',
            status_pending: 'Zain',
            status_resolved: 'Ebatzia',
            status_escalated: 'Eskalatua',
            category_name: 'Kategoria',
            system_name: 'Sistema',
            owner: 'Jabea',
            resolve: 'Ebatzi',
            resolve_task: 'Berrikuspen-zeregina ebatzi',
            resolution: 'Ebazpena',
            resolution_approve: 'Onartu',
            resolution_modify: 'Aldatu eta onartu',
            resolution_reject: 'Baztertu',
            resolution_escalate: 'Eskalatu',
            notes: 'Ebazpen-oharrak',
            notes_required: 'Oharrak beharrezkoak (gutxienez 10 karaktere) Aldatu, Baztertu edo Eskalatu egiteko',
            resolved_success: 'Berrikuspen-zeregina ebatzia',
            bulk_approve: 'Onarpen masiboa',
            bulk_reject: 'Baztertze masiboa',
            bulk_success: '{count} zeregin prozesatu',
            no_tasks: 'Ez da berrikuspen-zereginik aurkitu',
        },
        inventory: {
            title: 'Sailkapen-inbentarioa',
            count: 'sistemak',
            summary: 'Inbentarioaren laburpena',
            total_systems: 'Sistema guztira',
            total_labels: 'Etiketak guztira',
            system_name: 'Sistema',
            label_count: 'Etiketak',
            approved_count: 'Onartuak',
            pending_count: 'Zain',
            no_items: 'Ez da inbentario-elementurik aurkitu',
        },
        exceptions: {
            title: 'Sailkapen-salbuespenak',
            count: 'salbuespenak',
            all_severities: 'Larritasun guztiak',
            all_statuses: 'Egoera guztiak',
            system_name: 'Sistema',
            severity_label: 'Larritasuna',
            status_open: 'Irekia',
            status_resolved: 'Ebatzia',
            status_accepted_risk: 'Onartutako arriskua',
            resolve: 'Ebatzi',
            resolve_exception: 'Salbuespena ebatzi',
            resolution_type: 'Ebazpen mota',
            notes: 'Oharrak',
            justification_required: 'Justifikazioa beharrezkoa (gutxienez 10 karaktere) onartutako arriskurako',
            resolved_success: 'Salbuespena ebatzia',
            no_exceptions: 'Ez da salbuespenrik aurkitu',
        },
        history: {
            title: 'Sailkapen-historia',
            count: 'gertaerak',
            actor: 'Aktorea',
            action: 'Ekintza',
            entity_type: 'Entitate mota',
            changes: 'Aldaketak',
            view_lineage: 'Jatorria ikusi',
            lineage_title: 'Sailkapen-jatorria',
            no_events: 'Ez da historia-gertaerarik aurkitu',
            no_lineage: 'Ez dago jatorri-daturik eskuragarri',
            modified: 'Aldatua',
            created: 'Sortua',
            removed: 'Kendua',
            action_created: 'Sortu',
            action_updated: 'Eguneratua',
            action_archived: 'Artxibatua',
            action_approved: 'Onartua',
            action_deprecated: 'Zaharkitua',
            entity_label: 'Etiketa',
            entity_taxonomy: 'Taxonomia',
            entity_review_task: 'Berrikuspen-zeregina',
            entity_exception: 'Salbuespena',
            before: 'Aurretik',
            after: 'Ondoren',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Gobernantza-auditoretza-arrastoa',
        audit_trail_subtitle: 'Entitate arteko auditoretza-jarioa gobernantza-ekintzetarako: onespenak, playbook-exekuzioak, ebidentzia-loturak eta gehiago.',
        audit_trail_item_count: 'auditoretza-gertaerak',
        audit_trail_fetch_error: 'Ezin izan dira auditoretza-gertaerak eskuratu.',
        audit_trail_no_events: 'Ez da auditoretza-gertaerarik aurkitu uneko iragazkiekin.',
        audit_trail_entity_type: 'Entitate mota',
        audit_trail_entity_id: 'Entitate ID',
        audit_trail_action: 'Ekintza',
        audit_trail_actor: 'Eragilea',
        audit_trail_timestamp: 'Denbora-marka',
        audit_trail_date_from: 'Hasiera',
        audit_trail_date_to: 'Amaiera',
        audit_trail_no_rows_to_export: 'Ez dago errenkadarik esportatzeko.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Hornitzaileak',
            subprocessors: 'Azpi-tratatzaileak',
            cross_border: 'Mugaz gaindikoa',
            drift: 'Desbideratze-monitorea',
            scores: 'Betetze-puntuazioa',
            playbooks: 'Jokabide-liburuak',
            approvals: 'Onarpenak',
            audit_trail: 'Auditoretza-arrastoa',
            board_report: 'Kontseiluko Txostena',
            evidence: 'Frogak',
            onboarding: 'Sarrera-kanalizazioa',
            dpa_reviews: 'DPA berrikuspenak',
            periodic_reviews: 'Berrikuspen periodikoak',
            offboarding: 'Desegitea',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Hornitzaile-erregistroa',
            new: 'Hornitzaile berria',
            edit: 'Editatu hornitzailea',
            name: 'Izena',
            jurisdiction: 'Jurisdikzioa',
            dpa_status: 'DPA egoera',
            risk_level: 'Arrisku-maila',
            onboarding_status: 'Harrera-egoera',
            contact_email: 'Kontaktu-helbidea',
            contact_name: 'Kontaktu-izena',
            contact_phone: 'Kontaktu-telefonoa',
            website: 'Webgunea',
            services_csv: 'Zerbitzuak (komaz bereiziak)',
            country: 'Herrialdea',
            country_placeholder: 'adib. ES',
            last_reviewed: 'Azken berrikuspena',
            offboard: 'Baja eman',
            offboard_vendor: 'Hornitzaileari baja eman',
            offboard_reason: 'Baja-arrazoia',
            no_vendors: 'Ez da hornitzailerik aurkitu',
            vendor_count: '{count} hornitzaile',
            all_dpa_status: 'DPA egoera guztiak',
            all_risk_levels: 'Arrisku-maila guztiak',
            all_onboarding: 'Harrera guztiak',
            fetch_error: 'Huts egin du. Mesedez, saiatu berriro.',
            save_error: 'Hornitzailea gordetzeak huts egin du',
            offboard_error: 'Bajak huts egin du',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Tratamendu-arduraduna',
            sub_processor: 'Azpi-arduraduna',
            controller: 'Tratamenduaren Arduraduna',
            joint_controller: 'Tratamenduaren Erantzunkidea',
        },
        onboarding_statuses: {
            pending_review: 'Berrikuspena Zain',
            approved: 'Onartua',
            active: 'Aktiboa',
            suspended: 'Etena',
            offboarded: 'Baja Emanda',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Ez Beharrezkoa',
            required_missing: 'Beharrezkoa Falta',
            draft: 'Zirriborroa',
            under_review: 'Berrikustapen',
            signed: 'Sinatua',
            expired: 'Iraungitua',
        },
        risk_levels: {
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
        },
        approval_statuses: {
            pending: 'Zain',
            approved: 'Onartua',
            rejected: 'Baztertua',
            expired: 'Iraungitua',
        },
        approval_action_types: {
            vendor_onboarding: 'Hornitzailearen Alta',
            vendor_offboarding: 'Hornitzailearen Baja',
            cross_border_acceptance: 'Mugaz Gaindiko Onarpena',
            playbook_activation: 'Playbook-aren Aktibatzea',
            drift_accept_risk: 'Desbideraketa Arrisku Onartua',
            activity_activation: 'Jarduera Aktibatzea',
        },
        drift_check_types: {
            dpa_expiry: 'DPA Iraungitzea',
            dpa_missing: 'DPA Falta',
            review_overdue: 'Berrikuspena Atzeratua',
            cross_border_gap: 'Mugaz Gaindiko Hutsunea',
            sub_processor_chain: 'Azpi-arduradun Katea',
            risk_assessment_overdue: 'Arrisku Ebaluazioa Atzeratua',
            retention_policy_missing: 'Atxikipen Politika Falta',
            evidence_expiring: 'Frogak Iraungitzen',
        },
        drift_statuses: {
            open: 'Irekia',
            acknowledged: 'Aitortua',
            in_progress: 'Abian',
            resolved: 'Ebatzita',
            accepted_risk: 'Arrisku Onartua',
        },
        drift_entity_types: {
            vendor: 'Hornitzailea',
            system: 'Sistema',
            data_flow: 'Datu-fluxua',
            processing_activity: 'Tratamendu Jarduera',
            evidence_item: 'Froga Elementua',
        },
        playbook_statuses: {
            draft: 'Zirriborroa',
            active: 'Aktiboa',
            archived: 'Artxibatua',
        },
        playbook_action_types: {
            delete_data: 'Datuak Ezabatu',
            verify_deletion: 'Ezabaketa Egiaztatu',
            notify_vendor: 'Hornitzaileari Jakinarazi',
            confirm_receipt: 'Jasotze Berretsi',
            document_evidence: 'Dokumentatu',
            other: 'Beste bat',
        },
        playbook_target_types: {
            system: 'Sistema',
            vendor: 'Hornitzailea',
        },
        dpa_chain_statuses: {
            complete: 'Osoa',
            incomplete: 'Osatu gabea',
            unknown: 'Ezezaguna',
        },
        sub_processor_statuses: {
            active: 'Aktiboa',
            terminated: 'Amaiturik',
        },
        evidence_types: {
            dpa_agreement: 'DPA Akordioa',
            risk_assessment: 'Arrisku Ebaluazioa',
            audit_report: 'Auditoretza Txostena',
            certification: 'Ziurtagiria',
            deletion_confirmation: 'Ezabatze Berrespena',
            training_record: 'Prestakuntza Erregistroa',
            incident_report: 'Gertaera Txostena',
            document: 'Dokumentua',
            screenshot: 'Pantaila-argazkia',
            policy: 'Politika',
            contract: 'Kontratua',
            assessment: 'Ebaluazioa',
            correspondence: 'Korrespondentzia',
            other: 'Beste bat',
        },
        linkable_entity_types: {
            vendor: 'Hornitzailea',
            transfer_mechanism: 'Transferentzia Mekanismoa',
            playbook: 'Jokabide-gida',
            execution: 'Exekuzioa',
            system: 'Sistema',
            activity: 'Jarduera',
        },
        subprocessors: {
            title: 'Azpi-tratatzaileen harremanak',
            new: 'Lotura berria',
            vendor: 'Hornitzailea',
            subprocessor: 'Azpi-tratatzailea',
            relationship_type: 'Harreman mota',
            countries: 'Herrialdeak',
            countries_csv: 'Herrialdeak (komaz bereiziak)',
            dpa_chain: 'DPA katea',
            dpa_chain_status: 'DPA katearen egoera',
            no_processors: 'Ez da tratamendu-arduraduna aurkitu.',
            no_subprocessors: 'Ez da azpi-arduraduna aurkitu.',
            processor_vendor: 'Tratamendu-arduradun hornitzailea',
            select_processor: 'Hautatu tratamendu-arduraduna',
            select_vendor: 'Hautatu hornitzailea',
            services: 'Zerbitzuak',
            sub_processor_vendor_id: 'Azpi-arduradun hornitzaile IDa',
            terminate: 'Amaitu',
            confirm_terminate: 'Ziur zaude harreman hau amaitu nahi duzula?',
            create_error: 'Lotura sortzeak huts egin du.',
            fetch_error: 'Azpi-arduradunei buruzko datuak lortzeak huts egin du.',
            processor: 'Tratamendu-arduraduna',
            services_csv: 'Zerbitzuak (komaz bereiziak)',
            terminate_error: 'Amaitze-prozesuak huts egin du.',
            version_history_heading: 'Azpiprozesadorea aldatzeko bertsioa',
            change_tracking_note: 'Azpiprozesadorearen erlazio-aldaketa guztiak bertsionatu egiten dira. Aldaketa bakoitzak aurreko/ondoko egoera erregistratzen du auditoretza-pistarako.',
            history_retention_note: 'Aldaketen historia datuak gordetzeko aldi osoan gordetzen da. Berrikusi aldaketa-erregistroak saltzaileen xehetasunen ikuspegiaren bidez.',
            vendor_risk_cross_view_heading: 'Azpiprozesadoreen erregistroa (Vendor Risk)',
            vendor_risk_cross_view_source: 'Iturria: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Ez dago sarrerarik Vendor Risk erregistroan.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Mugaz gaindiko gobernantza-txostena',
            flows: 'Mugaz gaindiko fluxuak',
            transfers: 'Mugaz gaindiko transferentziak',
            detected_gaps: 'Detektatutako hutsuneak',
            safeguards: 'Bermeak',
            cross_border_vendors: 'Mugaz gaindiko hornitzaileak',
            destination: 'Helburua',
            no_cross_border_vendors: 'Ez da mugaz gaindiko hornitzailerik aurkitu.',
            no_safeguard: 'Bermerik gabe',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Tratamendu-kokapenak',
            export_error: 'Txostena esportatzeak huts egin du.',
            export_pdf: 'PDF esportatu',
            export_success: 'Txostena arrakastaz esportatu da.',
            exporting: 'Esportatzen...',
            fetch_error: 'Mugaz gaindiko datuak lortzeak huts egin du.',
            source: 'Jatorria',
            pdpl_p2_heading: 'PDPL-P2 Mugaz gaindiko transferentzia betetzea',
            dossier_prep_note: 'Dosierra prestatzea: inpaktuaren ebaluazioa, oinarri juridikoa eta babes-dokumentazioa bildu aurretik.',
            filing_tracking_note: 'Artxiboaren jarraipena: bidalketen egoera kontrolatu Agintaritzari (PDPC) hasierako eskaeratik onartu arte.',
            supplement_loop_note: 'Begizta osagarria: Agintaritzaren informazio eskaerei erantzun eta aldatutako espedienteak berriro bidali behar direnean.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Hornitzaile-bilaketa',
            vendor_search_placeholder: 'Bilatu hornitzaile-izenaren arabera…',
            jurisdiction_filter_label: 'Jurisdikzioa',
            filings_section_heading: 'Mugaz Gaindiko Fitxategiak',
            filings_empty: 'Oraindik ez dago fitxategirik. Sortu bat espediente bat hasteko.',
            filings_create_button: 'Fitxategi berria',
            filings_table_id: 'Aurkezpenaren IDa',
            filings_table_jurisdiction: 'Jurisdikzioa',
            filings_table_type: 'Inprimaki mota',
            filings_table_status: 'Egoera',
            filings_table_updated: 'Eguneratua',
            filings_table_open: 'Ireki',
            filings_detail_title: 'Mugaz Gaindiko Fitxategia',
            filings_back_to_list: 'Itzuli Mugaz Gaindikora',
            filings_status_heading: 'Bizi-zikloa',
            filings_evidence_heading: 'Frogak',
            filings_history_heading: 'Ikuskaritza-pista',
            filings_field_filing_id: 'Aurkezpenaren IDa',
            filings_field_jurisdiction: 'Jurisdikzioa',
            filings_field_type: 'Inprimaki mota',
            filings_field_status: 'Egoera',
            filings_field_reference: 'Agintaritzaren erreferentzia',
            filings_field_mps_receipt: 'MPS ordainagiria',
            filings_field_submitted_at: 'Heldu den',
            filings_field_acknowledged_at: 'Onartua',
            filings_field_archived_at: 'Helbide honetan artxibatuta',
            filings_field_version: 'Bertsioa',
            filings_field_notes: 'Oharrak',
            filings_action_advance: 'Aurretik egoera',
            filings_action_reject: 'Baztertu',
            filings_action_archive: 'Artxiboa',
            filings_action_submit: 'Mark bidali du',
            filings_action_acknowledge: 'Markek aitortu zuen',
            filings_action_resume_draft: 'Hasi zirriborro gisa',
            filings_status_draft: 'Zirriborroa',
            filings_status_assessed: 'Ebaluatua',
            filings_status_pending_review: 'Berrikuspenaren zain',
            filings_status_ready_to_file: 'Fitxatzeko prest',
            filings_status_submitted: 'Aurkeztua',
            filings_status_acknowledged: 'Aitortua',
            filings_status_rejected: 'Baztertua',
            filings_status_archived: 'Artxibatuta',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Mugaz gaindiko espediente berria',
            filings_new_form_jurisdiction: 'Jurisdikzioa',
            filings_new_form_type: 'Inprimaki mota',
            filings_new_form_notes: 'Oharrak (aukerakoa)',
            filings_new_submit: 'Sortu',
            filings_new_cancel: 'Utzi',
            filings_reason_label: 'Arrazoia',
            filings_reference_label: 'Agintaritzaren erreferentzia-zenbakia',
            filings_mps_receipt_label: 'MPS ordainagiriaren erreferentzia',
            filings_create_error: 'Ezin izan da fitxategia sortu.',
            filings_create_success: 'Fitxategia sortu.',
            filings_update_error: 'Ezin izan da eguneratu fitxategia.',
            filings_update_success: 'Fitxategia eguneratu da.',
            filings_fetch_error: 'Ezin izan dira fitxategiak eskuratu.',
            filings_archive_confirm: 'Artxibo hau artxibatu nahi duzu? Hau ezin da desegin.',
            filings_status_rejected_detour: 'Artxibatzea baztertu egin da berrikusi ondoren. Berekin zirriborro gisa fluxua berrabiarazteko.',
            recent_exports_heading: 'Azken esportazioak',
            recent_exports_empty: 'Oraindik ez dago esportaziorik. Egin klik "Esportatu PDF" bat sortzeko.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Konfigurazio-desbideratzearen monitorea',
            drift_type: 'Desbideratze mota',
            severity: 'Larritasuna',
            detected_at: 'Detektatze-data',
            resolve: 'Ebatzi',
            all_check_types: 'Egiaztapen mota guztiak',
            all_entity_types: 'Entitate mota guztiak',
            event_count: 'Gertaera-kopurua',
            accept_risk: 'Arriskua onartu',
            acknowledge: 'Berretsi',
            assign_error: 'Esleitzeak huts egin du.',
            assign_event: 'Esleitu',
            assigned: 'Esleitua',
            enter_resolution_notes: 'Sartu ebazpen-oharrak...',
            enter_user_id: 'Sartu erabiltzaile IDa...',
            entity: 'Entitatea',
            fetch_error: 'Desbideratze-gertaerak lortzeak huts egin du.',
            no_events: 'Ez da desbideratze-gertaerarik aurkitu.',
            resolution_notes: 'Ebazpen-oharrak',
            update_status_error: 'Egoera eguneratzeak huts egin du.',
        },
        scores: {
            title: 'Betetze-puntuazioaren kontrol-panela',
            overall: 'Betetze-puntuazio orokorra',
            breakdown: 'Puntuazioen banaketa moduluka',
            trend: 'Joera historikoa',
            module: 'Modulua',
            score: 'Puntuazioa',
            grade: 'Kalifikazioa',
            calculated_at: 'Kalkulatze-data',
            generate_board_report: 'Zuzendaritza-txostena sortu',
            lang_english: 'Ingelesa',
            lang_vietnamese: 'Vietnamera',
            fetch_error: 'Betetze-puntuazioak lortzeak huts egin du.',
            generate_report: 'Txostena sortu',
            generating: 'Sortzen...',
            recommendations: 'Gomendioak',
            report_error: 'Txostena sortzeak huts egin du.',
            factor_owner_assigned: 'Jabea esleitu da',
            factor_data_categories_linked: 'Datu-kategoriak lotuta',
            factor_purposes_linked: 'Xedeak lotuta',
            factor_data_flows_documented: 'Datu-fluxuak dokumentatuta',
            factor_processing_activity_linked: 'Tratamendu-jarduera lotuta',
            factor_dpa_status: 'DPA egoera',
            factor_retention_policy_linked: 'Atxikipen-politika lotuta',
            factor_last_reviewed: 'Azken berrikuspena atalasearen barruan',
            factor_risk_assessment_current: 'Arrisku-ebaluazioa eguneratuta',
            factor_sub_processors_documented: 'Azpiprozesatzaileak dokumentatuta',
            factor_cross_border_safeguards: 'Mugaz gaindiko babes-neurriak',
            factor_contact_info_complete: 'Harremanetarako informazioa osatua',
            factor_onboarding_approved: 'Harrera onartuta',
            factor_avg_system_score: 'Batez besteko sistema-puntuazioa',
            factor_avg_vendor_score: 'Batez besteko hornitzaile-puntuazioa',
            factor_processing_activity_coverage: 'Tratamendu-jardueren estaldura',
            factor_ropa_completeness: 'RoPA osotasuna',
            factor_open_drift_events: 'Irekitako desbideratze-gertaerak',
        },
        playbooks: {
            title: 'Ezabatze-jokabide-liburuak',
            new: 'Jokabide-liburu berria',
            edit: 'Editatu jokabide-liburua',
            steps: 'Urratsak',
            add_step: 'Gehitu urratsa',
            remove_step: 'Kendu urratsa',
            data_categories_csv: 'Datu-kategoriak (komaz bereiziak)',
            estimated_duration: 'Aurreikusitako iraupena',
            estimated_duration_label: 'Aurreikusitako iraupena',
            evidence_description: 'Frogaren deskribapena',
            no_playbooks: 'Ez da jokabide-libururik aurkitu.',
            playbook_count: 'Jokabide-liburu kopurua',
            requires_evidence: 'Froga behar du',
            step_label: 'Urratsaren etiketa',
            target_name: 'Helburuaren izena',
            target_type: 'Helburu mota',
            action_type: 'Ekintza mota',
            archive_error: 'Artxibatzeak huts egin du.',
            confirm_archive: 'Ziur zaude jokabide-liburu hau artxibatu nahi duzula?',
            data_categories: 'Datu-kategoriak',
            days: 'egun',
            fetch_error: 'Jokabide-liburuak lortzeak huts egin du.',
            sla_days: 'SLA (egun)',
            target_id: 'Helburu IDa',
            template_class_heading: 'Txantiloien Sailkapena',
            official: 'Ofiziala',
            internal: 'Barnekoa',
            classification: 'Sailkapena',
            official_note: 'Ofizialak: araudiak agindutako txantiloiak PDPL, GDPR edo sektoreko eskakizun espezifikoekin bat datozenak.',
            internal_note: 'Barnekoak: arauzko txantiloietatik eratorri ez diren erakundearen berariazko prozedurak.',
            active_executions_heading: 'Exekuzio aktiboak',
            no_active_executions: 'Ez dago exekuzio aktiborik.',
            playbook_label: 'Playbook-a',
            ticket_id_label: 'Txartela',
            progress_label: 'Aurrerapena',
            actions_label: 'Ekintzak',
            view_steps_label: 'Ikusi urratsak',
            execution_detail_title: 'Exekuzio xehetasunak',
            playbook_missing: 'Playbook-a ez da aurkitu.',
            evidence_required_label: 'Ebidentzia beharrezkoa',
            upload_evidence_label: 'Kargatu ebidentzia',
            mark_complete_label: 'Markatu osatuta',
            evidence_required_toast: 'Ebidentzia erantsi behar da osatu aurretik.',
            evidence_blocking_message: 'Erantsi ebidentzia-fitxategi bat jarraitzeko.',
            step_completed_toast: 'Urratsa osatuta.',
            executions_fetch_error: 'Ezin izan dira exekuzioak kargatu.',
            step_evidence_default_name: 'Exekuzio-urratsaren ebidentzia',
            attached_evidence_label: 'Erantsitako ebidentziak',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Onarpen-lan-fluxuak',
            new: 'Onarpen berria',
            edit: 'Editatu onarpena',
            request_type: 'Eskaera mota',
            requester: 'Eskatzailea',
            approve: 'Onartu',
            reject: 'Baztertu',
            justification: 'Justifikazioa',
            all_action_types: 'Ekintza mota guztiak',
            decided_by: 'Erabakia hartua',
            no_approvals: 'Ez da onarpenrik aurkitu.',
            approval_count: 'Onarpen-kopurua',
            approval_notes_optional: 'Oharrak (aukerakoa)',
            approve_error: 'Onartzeak huts egin du.',
            approve_request: 'Eskaera onartu',
            decision: 'Erabakia',
            entity: 'Entitatea',
            fetch_error: 'Onarpenak lortzeak huts egin du.',
            reject_error: 'Baztertzeak huts egin du.',
            reject_request: 'Eskaera baztertu',
            rejection_notes_required: 'Baztertze-oharrak beharrezkoak',
            rejection_notes_required_msg: 'Mesedez, adierazi baztertzearen arrazoia.',
            requested_at: 'Eskatze-data',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Laguntzarako ebidentzia (hautazkoa)',
            decision_evidence_name: 'Erabaki-ebidentzia',
        },
        evidence: {
            title: 'Gobernantza-frogak',
            new: 'Froga berria',
            link_to: 'Lotu honi',
            entity_id_placeholder: 'Entitate IDa',
            file_path_placeholder: '/bidea/fitxategira',
            evidence_label: 'Frogaren etiketa',
            file_path: 'Fitxategi-bidea',
            source_mode_label: 'Ebidentziaren iturria',
            source_mode_file: 'Igo fitxategia',
            source_mode_url: 'Lotu kanpo-URLa',
            attachment_label: 'Eranskina',
            item_count: 'Elementu-kopurua',
            link_button: 'Froga lotu',
            confirm_delete: 'Ziur zaude froga hau ezabatu nahi duzula?',
            enter_entity_id: 'Sartu entitate IDa...',
            entity_id: 'Entitate IDa',
            entity_type: 'Entitate mota',
            expires: 'Iraungitze-data',
            expires_at: 'Iraungitze-data',
            fetch_error: 'Frogak lortzeak huts egin du.',
            link_error: 'Froga lotzeak huts egin du.',
            no_evidence: 'Ez da frogarik aurkitu.',
            tags_csv: 'Etiketak (komaz bereiziak)',
            uploaded: 'Igota',
            url: 'URLa',
            framework_tag_heading: 'Esparru-etiketatutako frogak',
            framework_tag_note: 'Froga-erregistro bakoitzak esparru-kodea, aktorea eta denbora-zigilua jasotzen ditu. Iragazi markoaren arabera auditoretzarako garrantzitsuak diren erregistroak isolatzeko.',
            framework_code: 'Esparru Kodea',
            filter_by_framework: 'Iragazi marko-kodearen arabera...',
            actor: 'Aktorea',
        },
        certification: {
            not_applicable: 'Ez aplikagarria',
            not_certified: 'Ziurtatu gabea',
            pending: 'Zain',
            certified: 'Ziurtatua',
            revoked: 'Baliogabetua',
        },
        onboarding_pipeline: {
            title: 'Sarrera-kanalizazioa',
            new_workflow: 'Sarrera-lan-fluxu berria',
            vendor_id: 'Hornitzaile-IDa',
            stages: 'Etapak',
            current_stage: 'Uneko etapa',
            advance: 'Etapa aurreratu',
            advance_success: 'Etapa arrakastaz aurreratua',
            workflow_status: 'Lan-fluxuaren egoera',
            initiated: 'Hasita',
            in_progress: 'Abian',
            completed: 'Osatuta',
            cancelled: 'Bertan behera',
            no_workflows: 'Ez da sarrera-lan-fluxurik aurkitu',
            stages_placeholder: 'Etapa 1, Etapa 2, Etapa 3',
            assigned_to: 'Esleituta',
            notes: 'Oharrak',
            pdpl_lifecycle_heading: 'PDPL Saltzaileen bizi-zikloa — Onartzea',
            due_diligence_note: 'Duela diligentzia fasea derrigorrezkoa da PDPL pean edozein prozesadore txertatu aurretik. Datuen tratamenduaren esparrua, legezko oinarria eta segurtasun neurriak egiaztatzea.',
            contract_review_note: 'Kontratuaren berrikuspenak DPA klausulak, datuak gordetzeko mugak eta prozesadoreen murrizketak berretsi behar ditu.',
            create_dpa_review_cta: 'Sortu DPA berrikuspena',
            dpa_review_linked: 'DPA berrikuspena lotuta',
        },
        dpa_review: {
            title: 'DPA berrikuspenak',
            new_review: 'DPA berrikuspena berria',
            review_type: 'Berrikuspena mota',
            findings: 'Aurkikuntzak',
            outcome: 'Emaitza',
            complete_review: 'Berrikuspena osatu',
            complete_success: 'Berrikuspena arrakastaz osatua',
            link_assessment: 'Segurtasun-ebaluazioa lotu',
            link_success: 'Ebaluazioa arrakastaz lotua',
            assessment_id: 'Ebaluazio-IDa',
            combined_title: 'Ebaluazio konbinatua',
            no_reviews: 'Ez da DPA berrikuspen aurkitu',
            telecom_disclosure_heading: 'Telekomunikazioen Dibulgazioa Gobernantza',
            disclosure_trigger_note: 'Dibulgazioek abiarazitako berrikuspenek telekomunikazioen harpidedunen datuak partekatzeko gertaerak jasotzen dituzte VN Telecom Legearen arabera.',
            telecom_t2_note: 'TELECOM-T2 mailaren onespena behar da harpidedunen datuak agintariei jakinarazi aurretik.',
            quarterly_log_note: 'Erabili hiruhileko erregistroa dibulgazio-gertaera guztiak dokumentatzeko eta etengabeko auditoretza-pista mantentzeko.',
        },
        periodic_review: {
            title: 'Berrikuspen periodikoak',
            new_review: 'Berrikuspen periodiko berria',
            review_type: 'Berrikuspena mota',
            due_date: 'Epemuga',
            next_review: 'Hurrengo berrikuspena data',
            trigger_reassessment: 'Berrebaluazioa abiarazi',
            reassessment_reason: 'Berrebaluazioaren arrazoia',
            reassessment_success: 'Berrebaluazioa arrakastaz abiarazita',
            complete_success: 'Berrikuspena arrakastaz osatua',
            scheduled: 'Programatuta',
            in_progress: 'Abian',
            completed: 'Osatuta',
            overdue: 'Atzeratuta',
            material_change: 'Aldaketa garrantzitsua',
            no_reviews: 'Ez da berrikuspen periodikorik aurkitu',
            mark_complete: 'Osatu gisa markatu',
            pdpl_periodic_heading: 'PDPL Saltzaileen bizi-zikloa — Aldizkako berrikuspenak',
            scheduled_review_note: 'Aldizkako azterketak gutxienez urtero programatu behar dira prozesadore aktibo guztientzat. Berandutako berrikuspenek betetze-alertak eragiten dituzte.',
            material_change_note: 'Datuak prozesatzeko aldaketa garrantzitsuak berehalako berrazterketa eskatzen du ordutegia edozein dela ere.',
            overdue_badge: 'Atzeratua',
            overdue_aria: 'Berrikuspen atzeratua',
            only_overdue_filter: 'Atzeratuak soilik',
        },
        offboarding: {
            title: 'Hornitzailearen desegitea',
            initiate: 'Desegitea hasi',
            initiate_success: 'Desegitea arrakastaz hasita',
            reason: 'Desegitearen arrazoia',
            data_return_required: 'Datuen itzulketa beharrezkoa',
            verify_data: 'Datuen itzulketa egiaztatu',
            verify_success: 'Datuen itzulketa arrakastaz egiaztatua',
            sign_off: 'Sinatu',
            sign_off_success: 'Desegitea arrakastaz sinatua',
            status_initiated: 'Hasita',
            status_data_pending: 'Datuen itzulketa zain',
            status_data_verified: 'Datuen itzulketa egiaztatua',
            status_completed: 'Osatuta',
            status_cancelled: 'Bertan behera',
            task_id: 'Zeregin IDa',
            no_tasks: 'Ez da desegitze-zereginik aurkitu',
            evidence_id: 'Ebidentzia-IDa',
            pdpl_offboarding_heading: 'PDPL Saltzaileen bizi-zikloa — Offboarding eta ezabatzea',
            deletion_capture_note: 'Offboarding-ek saltzaileak datuak ezabatzearen berrespena jaso behar du. Egiaztatu datuen itzulera saioa amaitu aurretik.',
            evidence_attachment_note: 'Erantsi ezabatzearen froga (ziurtagiriak, pantaila-argazkiak, saltzaileen berrespenak) offboarding erregistroa osatzeko.',
            verify_mode_label: 'Egiaztatze metodoa',
            verify_mode_upload: 'Igo fitxategi berria',
            verify_mode_link: 'Lotu ebidentzia existentea',
            verify_mode_none: 'Ez dago itzultzeko daturik',
            upload_data_return_evidence: 'Igo datu-itzulketa ebidentzia',
            no_data_justification_label: 'Justifikazioa',
            no_data_justification_placeholder: 'Azaldu zergatik ez dagoen daturik itzultzeko',
            no_data_justification_hint: 'Gutxienez 20 karaktere',
            upload_required_toast: 'Erantsi fitxategi bat jarraitzeko',
            evidence_link_required_toast: 'Hautatu ebidentzia bat jarraitzeko',
            no_data_justification_required_toast: 'Eman gutxienez 20 karaktereko justifikazioa',
            data_return_evidence_name: 'Datu-itzulketa ebidentzia',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Hasierako',
            annual: 'Urteko',
            renewal: 'Berrikuntza',
            incident_triggered: 'Gertaerak eraginda',
        },
        periodic_review_types: {
            initial: 'Hasierako',
            annual: 'Urteko',
            renewal: 'Berritze',
            incident_triggered: 'Gertaerak eraginda',
            material_change: 'Aldaketa materiala',
            disclosure_triggered: 'Zabalkunde-eraginda',
            quarterly_log: 'Hiruhileko erregistroa',
        },
        dpa_review_statuses: {
            pending: 'Zain',
            in_progress: 'Abian',
            completed: 'Osatua',
            cancelled: 'Bertan behera',
        },
        onboarding_stage_names: {
            due_diligence: 'Zuzentasun-azterketa',
            contract_review: 'Kontratu-berrikuspena',
            security_assessment: 'Segurtasun-ebaluazioa',
            data_mapping: 'Datuen mapaketa',
            approval: 'Onespena',
            integration: 'Integrazioa',
        },
        access_denied_title: 'Sarbidea ukatua',
        access_denied: 'Ez duzu orrialde hau ikusteko baimenik. Jarri harremanetan zure administratzailearekin.',
    },
    consent: {
        nav: {
            policies: 'Politikak',
            purposes: 'Xede-katalogoa',
            workflows: 'Lan-fluxuak',
            snapshots: 'CMP argazkiak',
            change_awareness: 'Aldaketen jakinaraztea',
            audit: 'Adostasun-ikuskaritza',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Hutsuneen Analisia',
            page_title: 'Adostasunaren hutsunearen analisia',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Esparruak',
            org_flags_label: 'Antolakuntzaren testuingurua',
            flag_withdrawal: 'Erretiratzeko mekanismo globala dokumentatuta dauka',
            flag_parental: 'Gurasoen baimenaren fluxua du 16 urtetik beherako subjektuentzat',
            flag_cross_border: 'Erregistroan mugaz gaindiko fitxategiak ditu',
            run_label: 'Exekutatu hutsuneen azterketa',
            running_label: 'Korrika…',
            requires_advisor_label: 'ai_advisor:txat baimena behar du.',
            results_heading: 'Hutsuneak azaleratu ziren ({count})',
            tokens_label: '≈ {count} token',
            empty_label: 'Ez da hutsunerik hauteman hautatutako esparruetarako.',
            remediation_label: 'Iradokitako konponketa:',
            dismiss_label: 'Baztertu aplikagarria ez denez',
            resolve_label: 'Ebatzi',
            dismissed_label: 'Baztertua ez dagokio',
            resolved_label: 'Ebatzita',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Adostasun-lan-fluxuak inportatu',
            nav_title: 'Inportazio masiboa',
            description: 'Igo CSV fitxategi bat adostasun-lan-fluxuak masiboki inportatzeko.',
            select_file: 'Aukeratu CSV fitxategia',
            file_help: 'CSV lan-fluxu eremuei dagozkien goiburuak dituena.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Nahitaezko zutabeak: Lan-fluxu mota, Datuen subjektua, Kanala.',
            file_too_large: 'Fitxategia handiegia da. Gehienezko tamaina 2MB da.',
            preview: 'Aurrebista',
            columns_detected: 'Detektatutako zutabeak: {count}',
            column_mapping: 'Zutabe-esleipena',
            import_button: 'Inportatu',
            import_complete: 'Inportazioa osatuta',
            import_error: 'Inportazioak huts egin du',
            result: 'Inportazio-emaitza',
            total: 'Guztira',
            imported: 'Inportatuta',
            skipped: 'Saltatuta',
            failed: 'Huts eginda',
            errors: 'Erroreak',
            error_row: 'Errenkada',
            error_field: 'Eremua',
            error_message: 'Mezua',
            history: 'Inportazio-historia',
            no_history: 'Ez dago inportazio-historiarik.',
            history_file: 'Fitxategi-izena',
            history_date: 'Data',
            create_profiles_label: 'Sortu Datuen Subjektuen profilak subjektu berrietarako',
            create_profiles_help: 'Profil bat duten inportazioak automatikoki sortuko dute bat. Desmarkatu lan-fluxuak soilik inportatzeko.',
            profiles_created: 'Sortutako profilak',
            profiles_matched: 'Parekatutako profilak',
            subject_kind_help: 'Aukerako zutabeak: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Baliogabeko posta elektronikoa subject_email zutabean.',
            error_subject_unknown_kind: 'subject_kind balio ezezaguna. Espero direnak: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key nahitaezkoa da subject_kind=external_ref denean',
        },
        policies: {
            title: 'Adostasun-politikak',
            new: 'Politika berria',
            edit: 'Editatu politika',
            name: 'Izena',
            version: 'Bertsioa',
            policy_text: 'Politikaren testua',
            publish: 'Argitaratu',
            versions: 'Bertsioak',
            hide_versions: 'Ezkutatu Bertsioak',
            submit_for_review: 'Bidali for Berrikusketa',
            suspend: 'Eten',
            retire: 'Erretiratu',
            jurisdictions: 'Jurisdikzioak',
            jurisdictions_csv: 'Jurisdikzioak (komaz bananduta)',
            approval_mode: 'Onartze-modua',
            reviewer_ids: 'Berrikuskatzaile IDs (comma-separated)',
            tags_csv: 'Etiketak (komaz bananduta)',
            effective_date: 'Effective Data',
            review_date: 'Berrikusketa Data',
            consent_model_statement: 'Adostasun Model Statement',
            consent_model_placeholder: 'Hautazkoa consent model statement...',
            default_settings: 'Lehenetsia Ezarpenak (JSON)',
            changes: 'Aldaketak',
            fetch_error: 'Huts egin du: fetch consent policies. Saiatu berriro.',
            save_error: 'Huts egin du: save consent policy. Saiatu berriro.',
            submit_review_error: 'Huts egin du: submit policy for review. Saiatu berriro.',
            activate_error: 'Huts egin du: activate policy. Saiatu berriro.',
            suspend_error: 'Huts egin du: suspend policy. Saiatu berriro.',
            retire_error: 'Huts egin du: retire policy. Saiatu berriro.',
            submit_review_success: 'Politika berrikusteko bidali da.',
            activate_success: 'Politika aktibatu da.',
            suspend_success: 'Politika etenda.',
            retire_success: 'Poliza erretiratua.',
            versions_error: 'Huts egin du: fetch policy versions. Saiatu berriro.',
            invalid_json: 'Baliogabea JSON in Lehenetsia Ezarpenak field. Please try again.',
            page_title: 'Adostasun-politikak',
            version_updated: 'Eguneratua',
            version_initial: 'Hasierako bertsioa',
            json_format: 'JSON formatua',
            sensitive: 'Sentikorra',
            flag_sensitive: 'Bandera sentikorra',
            flag_sensitive_title: 'Markatu politika sentikor gisa',
            require_enhanced_review: 'Berrikuspen hobetua eskatu',
            enhanced_review_success: 'Berrikuspen-eskakizun hobetua gaituta dago.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Hautatu PDPL 2. artikuluko datu sentikorren kategoria aplikagarriak:',
            no_policies: 'Ez da baimen-politikarik aurkitu. Sortu zure lehen politika hasteko.',
            approval_success: 'Politika onartu da.',
            reviewers_label: 'Berrikuskatzaileak',
            enhanced_review_title: 'Berrikusketa hobetua beharrezkoa da',
            enhanced_review_badge: 'Berrikusketa hobetua beharrezkoa da',
            enhanced_review_status_pending: 'Berrikusketa zain',
            enhanced_review_status_approved: 'Berrikusketa onartua',
            enhanced_review_status_rejected: 'Berrikusketa baztertua',
            enhanced_review_reviewed_by: 'Berrikusi duena',
            enhanced_review_notes: 'Berrikusketa-oharrak',
            enhanced_review_approve: 'Berrikusketa onartu',
            enhanced_review_reject: 'Berrikusketa baztertu',
            enhanced_review_notes_required: 'Berrikusketa-oharrak beharrezkoak dira',
            enhanced_review_approved: 'Berrikusketa hobetua onartua',
            enhanced_review_rejected: 'Berrikusketa hobetua baztertua — politika zirriborrora berrezarri da',
            enhanced_review_reset_to_draft: 'Berrikusketa hobetua baztertua — politika zirriborrora berrezarri da',
            enhanced_review_reject_warning: 'Baztertzeak politika hau Zirriborro egoerara berrezarriko du. DPO-k berriro bidali behar du berrikusketa egiteko arazoak konpondu ondoren.',
            submitted_modal_title: 'Berrikusketa hobeturako bidalia',
            submitted_modal_body: 'Politika hau bidali da. Legelari/Pribatutasun berrikuskatzaile batek onartu behar du aktibatu aurretik.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Osasun-datuak',
            biometric: 'Datu biometrikoak',
            political: 'Iritzi politikoak',
            ethnic: 'Jatorri etnikoa',
            genetic: 'Datu genetikoak',
            criminal: 'Aurrekari penalak',
        },
        purposes: {
            title: 'Xede-katalogoa',
            new: 'Xede berria',
            edit: 'Editatu xedea',
            name: 'Izena',
            legal_basis: 'Oinarri juridikoa',
            retention_days: 'Kontserbazio-epea (egunak)',
            is_active: 'Aktibo',
            filter_by_category: 'Iragazi by category',
            policy_ids: 'Politika IDs (comma-separated)',
            ropa_activity_ids: 'ROPA Jarduera IDs (comma-separated)',
            policies: 'Politikak',
            deprecate: 'Zaharkitu',
            fetch_error: 'Huts egin du: fetch consent purposes. Saiatu berriro.',
            save_error: 'Huts egin du: save consent purpose. Saiatu berriro.',
            deprecate_error: 'Huts egin du: deprecate consent purpose. Saiatu berriro.',
            page_title: 'Helburuen Katalogoa',
            no_purposes: 'Ez da baimen-xederik aurkitu. Sortu zure lehen xedea hasteko.',
            category_personal_data: 'Datu pertsonalak',
            category_sensitive_data: 'Datu sentikorrak',
            category_health_data: 'Osasun Datuak',
            category_financial_data: 'Finantza Datuak',
            category_biometric_data: 'Datu biometrikoak',
            category_children_data: 'Haurrentzako Datuak',
            category_location_data: 'Kokapen Datuak',
            category_communications_data: 'Komunikazio Datuak',
            category_other: 'Bestela',
        },
        workflows: {
            title: 'Adostasun lan-fluxuak',
            new: 'Lan-fluxu berria',
            edit: 'Editatu lan-fluxua',
            workflow_type: 'Lan-fluxu mota',
            user_id: 'Erabiltzaile IDa',
            purpose_id: 'Xede IDa',
            purpose_ids_csv: 'Xede ID (comma-separated)',
            reason: 'Arrazoia',
            process: 'Prozesatu',
            data_subject_ref: 'Datu Subject Erreferentzia',
            data_subject: 'Datu-subjektua',
            channel: 'Kanala',
            policy_id: 'Politika ID',
            scope: 'Esparrua',
            legal_grounds: 'Lege-oinarriak',
            assessment: 'Ebaluazio',
            select_outcome: 'Hautatu outcome',
            partially_approved: 'Partially Onartuta',
            denied: 'Ukatua',
            outcome_upheld: 'Berretsia',
            outcome_pending: 'Zain',
            outcome_denied: 'Ukatua',
            status_count: 'Egoera-kopurua',
            channel_filter: 'Kanalaren arabera iragazi',
            add_action: '+ Ekintza',
            by_type: 'By Mota',
            by_status: 'By Egoera',
            downstream_actions: 'Downstream Ekintzak',
            downstream_actions_for: 'Downstream Ekintzak for {subject} ({type})',
            add_downstream_action: 'Gehitu ondorengo ekintza',
            assigned_to: 'Esleituta To',
            fetch_error: 'Huts egin du: fetch consent workflows. Saiatu berriro.',
            dashboard_error: 'Huts egin du: fetch workflow dashboard. Saiatu berriro.',
            save_error: 'Huts egin du: save consent workflow. Saiatu berriro.',
            status_update_error: 'Huts egin du: update workflow egoera. Saiatu berriro.',
            assessment_error: 'Huts egin du: update workflow assessment. Saiatu berriro.',
            add_action_error: 'Huts egin du: add workflow action. Saiatu berriro.',
            update_action_error: 'Huts egin du: update workflow action. Saiatu berriro.',
            type_refusal: 'Uko egitea',
            type_withdrawal: 'Erretiratzea',
            type_restriction: 'Murrizketa',
            type_objection: 'Aurka egitea',
            status_received: 'Jasota',
            status_processing: 'Prozesatzen',
            status_under_assessment: 'Ebaluazioan',
            status_implemented: 'Inplementatua',
            evidence_button: 'Frogak',
            evidence_modal_title: 'Lan-fluxuaren froga',
            no_evidence: 'Lan-fluxu honi ez zaio frogarik erantsi.',
            upload_evidence_label: 'Kargatu lan-fluxuaren froga',
            completed_at: 'Orduz beteta',
            effective_date: 'Indarreko data',
            page_title: 'Baimenaren lan-fluxuak',
            no_workflows: 'Ez da baimen-lan-fluxurik aurkitu. Lan-fluxuak sortzen dira datuen titularrek beren eskubideak erabiltzen dituztenean.',
            type_grant: 'Baimena',
            propagation_none: 'Bat ere ez',
            propagation_pending: 'Zain',
            propagation_pending_warning: 'Ezin da osatu — erretiratzeko hedapena oraindik zain dago',
            propagation_completed: 'Osatua',
            propagation_partial: 'Partzialki',
            propagation_incomplete_error: 'Ezin da lan-fluxua osatu erretiratzea oraindik zain dagoen bitartean',
            propagation_status: 'Hedapen egoera',
        },
        snapshots: {
            title: 'CMP argazkiak',
            new: 'Argazki berria',
            label: 'Etiketa',
            snapshot_date: 'Argazkiaren data',
            purposes_count: 'Xede-kopurua',
            compare: 'Konparatu',
            snapshot_data: 'Argazkiaren datuak (JSON)',
            select_left: 'Hautatu left argazki',
            select_right: 'Hautatu eskubide argazki',
            comparison_results: 'Konparaketa-emaitzak',
            field: 'Eremua',
            left: 'Ezkerra',
            right: 'Eskubide',
            close_comparison: 'Itxi Comparison',
            trigger: 'Abiarazi',
            trigger_manual: 'Eskuzkoa',
            trigger_policy_approval: 'Politika Onartze',
            cmp_version: 'CMP Bertsio',
            policy_version_id: 'Politika Bertsio ID',
            banner_config: 'Banner konfigurazioa (JSON)',
            active_purposes: 'Xede aktiboak (JSON)',
            active_legal_bases: 'Oinarri legal aktiboak (JSON)',
            domain_scope: 'Domeinu Esparrua',
            domain_scope_csv: 'Domeinu Esparrua (comma-separated)',
            raw_config: 'Konfigurazio gordina (JSON)',
            checksum: 'Egiaztapen-batura',
            created: 'Sortuta',
            fetch_error: 'Huts egin du: fetch CMP snapshots. Saiatu berriro.',
            save_error: 'Huts egin du: save CMP snapshot. Saiatu berriro.',
            compare_error: 'Huts egin du: compare snapshots. Saiatu berriro.',
            page_title: 'Baimenaren argazkiak',
            json_format: 'JSON formatua',
            json_array_format: 'JSON array formatua',
            manual_snapshot_note: 'Administratzaile/garatzaileentzat soilik. Funtzionamendu normalean, argazkiak automatikoki sortzen dira CMP webhook bidez edo politikak onartzerakoan. Itsatsi JSON zure CMP hornitzailearengandik esportatua (adib. OneTrust, Cookiebot).',
            banner_config_hint: 'Itsatsi banner konfigurazio objektua zure CMP esportaziotik.',
            active_purposes_hint: 'JSON array zure CMP esportazioko helburu aktiboen objektuekin.',
            active_legal_bases_hint: 'JSON array zure CMP esportazioko oinarri legal aktiboen objektuekin.',
            raw_config_hint: 'CMP-ko konfigurazio JSON osoa. Beste JSON eremuak hemendik ateratzen dira. SHA-256 egiaztapen-gakoa automatikoki kalkulatzen da.',
            changed_field: 'Eremu aldatua:',
            no_snapshots: 'Ez da CMP argazkirik aurkitu. Argazkiak sortzen dira politikak onartzen direnean edo eskuz abiarazten direnean.',
            detail_title: 'Argazkiaren xehetasuna',
            view_snapshot_detail: 'Ikusi argazkiaren xehetasuna',
            policy_version: 'Politikaren bertsioa',
            removed: 'Aurretik',
            added: 'Ondoren',
        },
        change_awareness: {
            title: 'Adostasun-aldaketen jakinaraztea',
            change_type: 'Aldaketa mota',
            entity_type: 'Entitate mota',
            entity_id: 'Entitate IDa',
            detected_at: 'Detektatze-data',
            acknowledge: 'Berretsi',
            new_alert: 'Berria Alerta',
            new_alert_form: 'Berria Change Awareness Alerta',
            total_open: 'Ireki guztira',
            by_severity: 'By Larritasun',
            snapshot_id: 'Argazki ID',
            update_status: 'Eguneratu egoera',
            resolution_notes: 'Resolution Oharrak',
            resolution_required: 'Resolution Oharrak (required)',
            resolution_required_msg: 'Ebazpen-oharrak beharrezkoak dira elementu bat ebaztean.',
            status_open: 'Ireki',
            status_under_review: 'Under Berrikusketa',
            status_resolved: 'Ebatzita',
            status_accepted: 'Onartuta',
            severity_informational: 'Informatiboa',
            severity_requires_review: 'Requires Berrikusketa',
            severity_policy_violation: 'Politika Violation',
            create_reconciliation: 'Sortu Adiskidetze Ataza',
            reconciliation_created: 'Arrakastaz sortu da bateratze-ataza.',
            reconciliation_error: 'Ezin izan da sortu bateratze-zeregin. Mesedez, saiatu berriro.',
            fetch_error: 'Huts egin du: fetch change awareness items. Saiatu berriro.',
            dashboard_error: 'Huts egin du: fetch change awareness dashboard. Saiatu berriro.',
            create_error: 'Huts egin du: create change awareness item. Saiatu berriro.',
            status_update_error: 'Huts egin du: update change awareness egoera. Saiatu berriro.',
            page_title: 'Aldatu Kontzientzia',
            no_items: 'Ez da aldaketa-kontzientzia elementurik aurkitu. Elementuak sortzen dira baimen-konfigurazio aldaketak hautematen direnean.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Adostasunaren ikuskaritza-pista',
            event_type: 'Gertaera mota',
            user_id: 'Erabiltzaile IDa',
            timestamp: 'Denbora-marka',
            details: 'Xehetasunak',
            actor: 'Aktorea',
            filter_by_entity_type: 'Iragazi by entity type',
            filter_by_action: 'Iragazi by action',
            fetch_error: 'Huts egin du: fetch consent audit trail. Saiatu berriro.',
            page_title: 'Adostasun Ikuskaritza',
            export_csv: 'Esportatu CSV',
            exporting: 'Esportatzen…',
            export_success: 'Esportatu da ikuskaritza-pista.',
            export_error: 'Ezin izan da esportatu baimenaren auditoretza-pista. Mesedez, saiatu berriro.',
            no_entries: 'Ez da auditoria-sarrerarik aurkitu. Auditoria-gertaerak automatikoki erregistratzen dira baimen-datuak aldatzen direnean.',
            entity_policy: 'Politika',
            entity_purpose: 'Xedea',
            entity_workflow: 'Lan-fluxua',
            entity_consent_record: 'Baimen-erregistroa',
            entity_change_awareness: 'Aldaketa-kontzientzia',
            entity_cmp_snapshot: 'CMP argazkia',
            action_created: 'Sortua',
            action_updated: 'Eguneratua',
            action_status_changed: 'Egoera aldatua',
            action_drift_detected: 'Desbideratzea detektatua',
            action_snapshot_created: 'Argazkia sortua',
            action_approval_submitted: 'Onarpena bidalita',
            action_archived: 'Artxibatua',
            action_purpose_auto_mapped: 'Xedea automatikoki esleituak',
            action_purpose_mapping_updated: 'Xede-esleipena eguneratua',
            action_webhook_configured: 'Webhook konfiguratua',
            entity_propagation: 'Hedapena',
            entity_reconciliation: 'Adiskidetzea',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Hedapena baieztatu da',
            action_propagation_failed: 'Hedapena huts egin du',
            action_consent_captured: 'Adostasuna harrapatu',
            action_reconciliation_created: 'Adiskidetzea Sortu',
            action_reconciliation_resolved: 'Adiskidetzea Ebatzita',
            action_submitted_for_review: 'Berrikusketarako bidalita',
            action_activated: 'Aktibatuta',
            action_suspended: 'Etenda',
            action_retired: 'Erretiratu da',
            action_enhanced_review_required: 'Berrikuspen hobetua beharrezkoa',
            action_enhanced_review_approved: 'Berrikusketa hobetua onartua',
            action_enhanced_review_rejected: 'Berrikusketa hobetua baztertua — politika zirriborrora berrezarri da',
            action_consented: 'Adostua',
            action_consented_customize: 'Adostasuna pertsonalizatua',
            action_revoked: 'Ezeztatua',
            action_rejected: 'Baztertua',
        },
        web_records: {
            title: 'Web Adostasun Erregistroak',
            page_title: 'Web Adostasun Erregistroak',
            domain: 'Domeinua',
            visitor_id: 'Bisitariaren IDa',
            consent_action: 'Adostasun Ekintza',
            received_at: 'Ordun jasoa',
            admin_status: 'Administratzailearen egoera',
            revoke: 'Errebokatu',
            reject: 'Baztertu',
            reason: 'Arrazoia',
            reason_placeholder: 'Ekintza honen arrazoia (aukerakoa)',
            revoke_confirm: 'Baimen-erregistro hau baliogabetu nahi duzu?',
            reject_confirm: 'Baimen-erregistro hau baztertu nahi duzu?',
            override_success: 'Erregistroaren egoera behar bezala eguneratu da.',
            override_error: 'Ezin izan da eguneratu erregistroaren egoera.',
            no_records: 'Ez da sareko baimen-erregistrorik aurkitu.',
            fetch_error: 'Ezin izan dira kargatu web-baimenaren erregistroak.',
            filter_by_domain: 'Iragazi domeinuaren arabera',
            filter_by_action: 'Iragazi ekintzaren arabera',
            active: 'Aktiboa',
        },
        legal_bases: {
            all: 'Oinarri legalak guztiak',
        },
        common_statuses: {
            suspended: 'Etenda',
            retired: 'Erretiratua',
            deprecated: 'Zaharkituta',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Integrazio',
            back_to_consent: 'Atzera to Adostasun',
            back_to_integration: 'Atzera to Integrazio',
            webhook_status: 'Webhook Egoera',
            events_received: 'Gertaerak Received',
            web_consent_records: 'Web Adostasun Erregistroak',
            unmapped_purposes: 'Unmapped Xedeak',
            integration_management: 'Integrazio Management',
            webhook_configuration: 'Webhook Konfigurazio',
            webhook_config_desc: 'Konfiguratu the CMP webhook endpoint and manage the HMAC secret.',
            purpose_mapping: 'Xede Mapping',
            purpose_mapping_desc: 'Mapatu CMP xede-identifikatzaileak GRC adostasun-katalogo xedeetara.',
            bulk_sync: 'Sinkronizazio masiboa',
            bulk_sync_desc: 'Berriz aplikatu xede-esleipenak esleipenaren zain dauden baimen-erregistro guztietan.',
            consent_analytics: 'Web Adostasun Analytics (Last 30 egun)',
            consent_grant_rate: 'Adostasun Grant Tasa',
            top_domain: 'Top Domeinu',
            records: 'erregistroak',
            banner_interactions: 'Banner-interakzioak',
            consent_actions: 'Baimen-ekintzak',
            opt_in_trend: 'Opt-in/Opt-out Joera',
            records_by_domain: 'Domeinuaren araberako erregistroak',
            date: 'Data',
            grants: 'Diru-laguntzak',
            denials: 'Ukapena',
            not_configured: 'Konfiguratu gabe',
            load_error: 'Huts egin du: load integration datu. Saiatu berriro.',
            webhook_not_configured: 'Webhook-a konfiguratu gabe.',
            webhook_not_configured_msg: 'Mesedez, {link} lehenik.',
            configure_webhook_link: 'konfiguratu webhook-a',
            setup_wizard: 'Konfigurazio-morroia',
            step_configure_webhook: 'Konfiguratu Webhook',
            step_map_purposes: 'Map Xedeak',
            step_run_sync: 'Exekutatu Initial Bulk Sync',
            step_verify_data: 'Egiaztatu Datu in Aginte-panel',
            configure: 'Konfiguratu',
            view_dashboard: 'Ikusi aginte-panel',
            syncing: 'Sinkronizatzen...',
            sync_now: 'Sinkronizatu orain',
            sync_completed: 'Sinkronizazioa osatuta: {imported} inportatuta, {skipped} saltatuta, {failed} huts egin du.',
            sync_failed: 'Sinkronizazioak huts egin du',
            last_sync_result: 'Azken sinkronizazio-emaitza',
            imported: 'Inportatua',
            skipped: 'Saltatuta',
            errors: 'Errorea',
            index: 'Indizea',
            error: 'Errorea',
            sync_history: 'Sync Historia',
            no_sync_jobs: 'Ez da sinkronizazio-lanik erregistratu oraindik.',
            load_sync_error: 'Huts egin du: load sync datu. Saiatu berriro.',
            purpose_mapping_title: 'CMP Xede Mapping',
            auto_mapping: 'Auto-mapatzen...',
            auto_map_purposes: 'Auto-Map Xedeak',
            unmapped_warning: '{count} mapatu gabeko helburua(k) -- CMP helburu hauek GRC helburuekin mapatu behar dira.',
            mapping_saved: 'Mapaketa gordeta.',
            mapping_deleted: 'Mapaketa ezabatuta.',
            auto_map_complete: 'Auto-mapa osatuta: {mapped} mapatuta, {unmatched} bat etorri gabe.',
            auto_map_failed: 'Auto-mapak huts egin du',
            save_mapping_error: 'Huts egin du: save mapping. Saiatu berriro.',
            delete_mapping_error: 'Huts egin du: delete mapping. Saiatu berriro.',
            load_data_error: 'Huts egin du: load datu. Saiatu berriro.',
            cmp_purpose: 'CMP Xede',
            grc_purpose: 'GRC Xede',
            select_grc_purpose: '-- Hautatu GRC xedea --',
            auto_mapped: 'Auto-mapatuta',
            mapped: 'Mapatuta',
            unmapped: 'Mapatu gabea',
            no_mappings: 'Ez da xede-esleipenik aurkitu. Esleipenak automatikoki sortzen dira baimen-gertaerak jasotzen direnean.',
            total_mappings: 'Guztira: {count} mapping(s)',
            webhook_config_title: 'Webhook Konfigurazio',
            current_configuration: 'Unekoa Konfigurazio',
            setup_webhook: 'Konfiguratu Webhook-a',
            setup_webhook_desc: 'Konfiguratu the AesirX CMP webhook to receive consent events.',
            allowed_domains: 'Baimendutako domeinuak (bat lerroko)',
            webhook_secret_warning: 'Webhook sekretua (gorde orain -- ez da berriro erakutsiko):',
            last_event: 'Last Gertaera',
            none_received: 'Bat ere ez',
            update_domains: 'Eguneratu Domeinuak',
            pause_webhook: 'Pausatu Webhook',
            resume_webhook: 'Jarraitu Webhook',
            regenerate_secret: 'Birsortu sekretua',
            configuring: 'Konfiguratzen...',
            create_webhook_config: 'Sortu Webhook Konfigurazio',
            webhook_endpoint: 'Webhook amaiera-puntua',
            webhook_endpoint_desc: 'Konfiguratu your AesirX CMP to send consent events to:',
            webhook_signature_note: 'Sartu HMAC-SHA256 sinadura {header} goiburuan.',
            configured_success: 'Webhook-a arrakastaz konfiguratu da.',
            config_updated: 'Konfigurazio updated.',
            config_error: 'Huts egin du: configure webhook. Saiatu berriro.',
            update_error: 'Huts egin du: update configuration. Saiatu berriro.',
            toggle_error: 'Huts egin du: toggle webhook. Saiatu berriro.',
            deactivated: 'Webhook desaktibatuta dago.',
            deactivate_error: 'Huts egin du: deactivate webhook. Saiatu berriro.',
            page_title: 'CMP Integrazioa',
            webhook_config_page_title: 'CMP Webhook konfigurazioa',
            bulk_sync_page_title: 'Erregistroak berriro mapatzea',
            purpose_mapping_page_title: 'Helburuen kartografia',
            data_loaded: 'CMP integrazio-datuak kargatu dira',
            sync_all: 'Dena sinkronizatu',
            sync_selected: 'Hautatuak sinkronizatu',
            sync_status: 'Sinkronizazio-egoera',
            last_sync: 'Azken sinkronizazioa',
            no_records: 'Ez da erregistrorik aurkitu.',
            sync_in_progress: 'Sinkronizazioa abian...',
            sync_complete: 'Sinkronizazioa osatuta.',
            sync_error: 'Sinkronizazioak huts egin du. Mesedez, saiatu berriro.',
            map_purpose: 'Xedea esleitu',
            unmap: 'Esleipena kendu',
            mapping_status: 'Esleipena-egoera',
            save_mapping: 'Esleipena gorde',
            remap_unmapped_records: 'Mapeatu berriro mapatu gabeko erregistroak',
            remap_records: 'Erregistroak berriro mapatzea',
            add_mappings_first: 'Gehitu helburuen mapak lehenik (2. urratsa)',
            remap_result: 'Berriro mapatzearen emaitza',
            remap_result_desc: 'Adostasun-erregistro guztiak mapatu gabeko edo partzialki mapatutako helburuekin prozesatu ditu.',
            total_processed: 'Prozesatu osoa',
            newly_mapped: 'Mapeatu berria',
            still_unmapped_label: 'Oraindik mapatu gabe',
            still_unmapped_msg: '{count} erregistroek oraindik mapatu gabeko helburuak dituzte. {link} eta gero sakatu Berriro mapatu.',
            add_more_mappings: 'Gehitu mapa gehiago',
            remap_complete: 'Berriro mapatzea amaitu da: {remapped} mapatu berria, {still_unmapped} oraindik mapatu gabe.',
            add_mapping: 'Gehitu mapak',
            cmp_purpose_id_label: 'CMP Helburuaren ID *',
            cmp_purpose_id_required: 'CMP Helburuaren IDa beharrezkoa da',
            cmp_purpose_name_label: 'CMP Helburuaren izena',
            grc_purpose_label: 'GRC Helburua',
        },
        propagation: {
            title: 'Erretiratzea hedapena',
            systems: 'Lotutako sistemak',
            status: 'Egoera',
            confirmed: 'Baieztatuta',
            pending: 'Zain',
            failed: 'Huts eginda',
            confirm: 'Erretiratzea baieztatu',
            fail: 'Huts egin bezala markatu',
            failure_reason: 'Hutsegitearen arrazoia',
            can_close: 'Sistema guztiak baieztatuta — lan-fluxua itxi daiteke',
            cannot_close: 'Sistemen baieztatzearen zain',
            no_tasks: 'Ez dago hedapen-zereginik.',
            confirm_success: 'Sistemaren erretiratzea baieztatuta.',
            fail_success: 'Sistema huts egin bezala markatuta.',
            propagate_success: 'Erretiratzea lotutako sistemetara hedatuta.',
            propagate: 'Hedatu sistemetara',
            add_system: 'Gehitu sistema',
            remove_system: 'Kendu',
            system_id: 'Sistemaren IDa',
            system_name: 'Sistemaren izena',
            owner_id: 'Jabearen IDa',
            propagate_error: 'Ezin izan da erretiratzea zabaldu. Mesedez, saiatu berriro.',
            no_systems: 'Gehitu gutxienez sistema bat.',
            page_title: 'Erretiratzearen Hedapena',
            workflow_id: 'Hautatu Erretiratzeko lan-fluxua',
            system_id_hint: 'adib. crm-001, erp-prod',
            owner_id_hint: 'Sistemaren jabea',
            elapsed: 'Igaro da',
        },
        sensitive: {
            title: 'Datu sentikorren politikak',
            flag: 'Sentikor bezala markatu',
            categories: 'Kategoria sentikorrak',
            enhanced_review: 'Berrikusketa hobetua eskatu',
            enhanced_review_success: 'Berrikusketa hobetua eskatuta.',
            flag_success: 'Politika sentikor bezala markatuta.',
            no_sensitive: 'Ez dago datu sentikorren politikarik.',
        },
        reconciliation: {
            title: 'Desadostasun-adiskidetzea',
            create: 'Adiskidetze-zeregin berria',
            system: 'Sistema',
            owner: 'Jabea',
            description: 'Deskribapena',
            resolution: 'Ebazpena',
            resolve: 'Ebatzi',
            update_grc: 'GRC eguneratu',
            update_cmp: 'CMP eguneratu',
            accept_difference: 'Aldea onartu',
            escalate: 'Eskalatu',
            resolution_notes: 'Ebazpen-oharrak',
            no_tasks: 'Ez dago adiskidetze-zereginik.',
            resolve_success: 'Adiskidetze-zeregina ebatzita.',
            create_success: 'Adiskidetze-zeregina sortuta.',
            open: 'Irekita',
            resolved: 'Ebatzita',
            escalated: 'Eskalatuta',
            dashboard_open: 'Zeregin irekiak',
            dashboard_resolved: 'Zeregin ebatziak',
            dashboard_escalated: 'Zeregin eskalatutoak',
            page_title: 'Bat ez datozen adiskidetzea',
            my_tasks: 'Nire Zereginak',
            all_tasks: 'Zeregin guztiak',
            severity: 'Larritasuna',
        },
        sync: {
            title: 'CMP sinkronizazioa',
            direction: 'Norabidea',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Gatazka',
            resolve_conflict: 'Gatazka ebatzi',
            no_events: 'Ez dago sinkronizazio-gertaerarik.',
            resolve_success: 'Sinkronizazio-gatazka ebatzita.',
            create_success: 'Sinkronizazio-gertaera erregistratuta.',
            page_title: 'CMP Sinkronizatu',
            detect_drift: 'Gatazkak detektatu',
            detecting: 'Detektatzen...',
            drift_result: '{detected} gatazka(k) detektatu dira alderatuta {total} helburuetatik.',
            no_drift: 'Ez da gatazka berririk detektatu.',
            drift_error: 'Ezin izan da exekutatu gatazkak hautematea.',
            resolve_modal_title: 'Gatazka konpontzea',
            resolve_field: 'Eremua',
            resolve_grc_value: 'GRC balioa',
            resolve_cmp_value: 'CMP balioa',
            accept_cmp: 'Onartu CMP balioa',
            accept_cmp_desc: 'Eguneratu GRC CMParekin bat etor dadin — CMPko balioa GRCko balio berria bihurtzen da.',
            keep_grc: 'Mantendu GRC balioa',
            keep_grc_desc: 'Itxi gatazka hau GRC aldatu gabe — jakinarazi CMP jabeari lerrokatzeko.',
            resolve_applied: 'Gatazka konponduta. GRC helburua eguneratu da CMP bat etortzeko.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Prestutasun-txosten hau aholkularitza hutsa da. Ez du dosierra onartzen, muntatzen, gauzatzen, aurkezten edo bidaltzen.',
            run_label: 'Exekutatu hegaldiaren aurretik',
            running: 'Hegaldiaren aurretik martxan...',
            assemble_label: 'Auto-muntatu paketea',
            assembling: 'Muntatzen…',
            verdict_label: 'Hegaldiaren aurreko epaia',
            tokens_label: '≈ {count} token',
            remediation_label: 'Erremediazioa',
            no_gaps: 'Ez da hegaldiaren aurreko hutsunerik hauteman. Fitxategia muntatzeko prest dago.',
            override_label: 'Gainditu blokearen epaia - batzarra auditoria-zigilua izango da.',
            accept_label: 'Onartu txostena',
            reject_label: 'Errefusa txostena',
            assembled_label: 'Bidalketa paketea muntatuta',
            package_id_label: 'Paketearen IDa',
            package_filename_label: 'Fitxategiaren izena',
            package_hash_label: 'Pakete hash',
            evidence_count_label: '{count} froga elementu bilduta',
            error_generic: 'Ezin izan da hegaldiaren aurretik exekutatu edo paketea muntatu. Saiatu berriro edo egiaztatu fitxategiaren egoera.',
        },
        errors: {
            fetch: 'Huts egin du: load assessments. Saiatu berriro.',
            save: 'Huts egin du: save assessment. Saiatu berriro.',
            fetch_gap_report_failed: 'Huts egin du: fetch gap report. Saiatu berriro.',
            fetch_transfers_failed: 'Huts egin du: fetch transfers. Saiatu berriro.',
            save_transfer_failed: 'Huts egin du: save transfer. Saiatu berriro.',
            submit_review_failed: 'Huts egin du: submit for review. Saiatu berriro.',
            process_decision_failed: 'Huts egin du: process decision. Saiatu berriro.',
            deprecate_failed: 'Huts egin du: deprecate. Saiatu berriro.',
            evaluate_risk_failed: 'Huts egin du: evaluate arrisku. Saiatu berriro.',
            fetch_safeguards_failed: 'Huts egin du: fetch safeguards. Saiatu berriro.',
            save_safeguard_failed: 'Huts egin du: save safeguard. Saiatu berriro.',
            update_safeguard_status_failed: 'Huts egin du: babes-neurriaren egoera eguneratzea. Saiatu berriro.',
        },
        nav: {
            dpias: 'DBEEak',
            transfers: 'Transferentzia-ebaluazioak',
            dashboard: 'Kontrol-panela',
            gap_report: 'Hutsune-txostena',
        },
        risk_level_critical: 'Arrisku kritikoa',
        risk_level_high: 'Arrisku altua',
        risk_level_medium: 'Arrisku ertaina',
        risk_level_low: 'Arrisku baxua',
        severity_critical: 'Larritasun kritikoa',
        severity_high: 'Larritasun altua',
        severity_medium: 'Larritasun ertaina',
        severity_low: 'Larritasun baxua',
        overdue: 'Epez kanpo',
        framework_filter_label: 'Esparrua',
        aria_filter_by_framework: 'Iragazi markoaren arabera',
        dashboard: {
            title: 'Ebaluazio kontrol-panela',
            page_title: 'Ebaluazioen panela',
            announce_loaded: 'Ebaluazioen panela kargatuta',
            announce_chart_view: 'Grafiko-ikuspegia erakusten',
            announce_table_view: 'Datu-taula ikuspegia erakusten',
            aria_metrics_table: 'Ebaluazioen paneleko neurketak',
            metric_col: 'Neurketa',
            value_col: 'Balioa',
            dpia_prefix: 'DBEA',
            transfer_prefix: 'Transferentzia',
            risk_prefix: 'Arriskua',
            aria_dpias_by_status: 'DBEAk egoeraren arabera',
            aria_transfers_by_status: 'Transferentziak egoeraren arabera',
            aria_top_risks: 'Arrisku nagusiak',
            aria_top_risks_table: 'Arrisku nagusiak DBEAetan',
            overdue_dpias_attention: '{count} DBEA ebaluazio epez kanpo arreta behar du',
            no_overdue_dpias: 'Ez dago DBEA ebaluazio epez kanpo',
            overdue_transfers_attention: '{count} transferentzia ebaluazio epez kanpo arreta behar du',
            no_overdue_transfers: 'Ez dago transferentzia ebaluazio epez kanpo',
            dpias_overview: 'DBEEen ikuspegi orokorra',
            transfers_overview: 'Transferentzia-ebaluazioen ikuspegi orokorra',
            overdue_items: 'Epez kanpoko elementuak',
            overdue_dpias: 'Epez kanpoko DBEEak',
            overdue_transfers: 'Epez kanpoko transferentzia-ebaluazioak',
            count: 'Kopuru',
            no_data: 'Ez da datu. aurkitu',
            no_risks: 'Ez da arrisku found. aurkitu',
            no_dashboard_data: 'Ez da dashboard datu. aurkitu',
            fetch_error: 'Huts egin du: fetch dashboard datu. Saiatu berriro.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DBEAk',
            announce_submitted_for_review: 'DBEA berrikuspenerako bidalita',
            announce_deprecated: 'DBEA zaharkituta',
            announce_risks_loaded: '{count} arrisku kargatuta',
            announce_mitigations_loaded: '{count} aringarri kargatuta',
            announce_risk_deleted: 'Arriskua ezabatuta',
            announce_mitigation_status_updated: 'Aringarriaren egoera eguneratuta',
            aria_filter: 'Iragazi DBEAk',
            aria_filter_by_status: 'Iragazi egoeraren arabera',
            aria_filter_by_lead: 'Iragazi ebaluazio-buruaren arabera',
            aria_table: 'Datuen babesaren eragin-ebaluazioak',
            announce_found: '{count} DBEA aurkituta',
            announce_decision: 'DBEA {decision}',
            mitigation_start: 'Hasi',
            mitigation_mark_implemented: 'Inplementatu gisa markatu',
            mitigation_verify: 'Egiaztatu',
            title: 'Datuen Babeserako Eragin-ebaluazioak',
            new: 'DBEE berria',
            edit: 'Editatu DBEE',
            dpia_title: 'Izenburua',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Jarduera',
            risk_score: 'Arrisku-puntuazioa',
            risks: 'Arriskuak',
            mitigations: 'Arintzeak',
            add_risk: 'Gehitu arriskua',
            add_mitigation: 'Gehitu arintzea',
            necessity: 'Beharrezkotasuna / Proportzionaltasuna',
            likelihood: 'Probabilitatea',
            impact: 'Eragina',
            severity_label: 'Larritasuna',
            purpose: 'Xede',
            consultation_notes: 'Consultation Oharrak',
            dpo_opinion: 'DPO-aren iritzia',
            processing_activity_ids: 'Prozesatzen Jarduera IDs (comma-separated)',
            system_ids: 'Sistema IDs (comma-separated)',
            vendor_ids: 'Hornitzaile IDak (komaz bananduta)',
            data_types: 'Datu Motak (comma-separated)',
            data_types_short: 'Datu Motak',
            data_subjects: 'Datu Subjects (comma-separated)',
            assessment_lead_id: 'Ebaluazio Lead ID',
            assessment_lead: 'Ebaluazio Lead',
            dpo_id: 'DPO IDa',
            reviewer_ids: 'Berrikuskatzaile IDs (comma-separated)',
            approval_mode: 'Onartze-modua',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-aren oniritzia behar du',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Berrikusketa Data',
            submit_for_review: 'Bidali for Berrikusketa',
            approve_reject: 'Onartu/Baztertu',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Zaharkitu',
            deprecated: 'Zaharkituta',
            hide_risks: 'Ezkutatu Arriskuak',
            hide_mitigations: 'Ezkutatu Aringarriak',
            comments: 'Iruzkinak',
            submit_decision: 'Bidali Erabaki',
            category_label: 'Kategoria',
            category_data_breach: 'Datu Urraketa',
            category_unauthorized_access: 'Unauthorized Sarbide',
            category_function_creep: 'Funtzio-hedapena',
            category_re_identification: 'Berr-identifikazioa',
            category_discrimination: 'Diskriminazioa',
            category_surveillance: 'Zaintza',
            category_other: 'Beste',
            affected_rights: 'Eragindako eskubideak (komaz bananduta)',
            no_risks: 'Ez da arrisku found. aurkitu',
            no_mitigations: 'Ez da mitigations found. aurkitu',
            no_dpias: 'Ez da dpias found. aurkitu',
            mitigation_type: 'Mota',
            mitigation_technical: 'Teknikoa',
            mitigation_organizational: 'Erakundekoa',
            mitigation_contractual: 'Kontratukoa',
            risk_ids: 'Arrisku IDs (comma-separated)',
            responsible_party: 'Alderdi arduraduna',
            responsible_party_placeholder: 'Edo izena eskuz sartu',
            affected_rights_placeholder: 'Hautatutako eskubideak hemen agertzen dira',
            affected_rights_freetext_placeholder: 'Sartu kaltetutako eskubideak (komaz bereizita)',
            responsible: 'Arduraduna',
            target_date: 'Helburu Data',
            created: 'Sortuta',
            fetch_error: 'Huts egin du: fetch DPIAs. Saiatu berriro.',
            save_error: 'Huts egin du: save DPIA. Saiatu berriro.',
            submit_review_error: 'Huts egin du: submit DPIA for review. Saiatu berriro.',
            decision_error: 'Huts egin du: process decision. Saiatu berriro.',
            deprecate_error: 'Huts egin du: deprecate DPIA. Saiatu berriro.',
            fetch_risks_error: 'Huts egin du: fetch arrisku. Saiatu berriro.',
            save_risk_error: 'Huts egin du: save arrisku. Saiatu berriro.',
            delete_risk_error: 'Huts egin du: delete arrisku. Saiatu berriro.',
            fetch_mitigations_error: 'Huts egin du: fetch mitigations. Saiatu berriro.',
            save_mitigation_error: 'Huts egin du: save mitigation. Saiatu berriro.',
            update_mitigation_status_error: 'Huts egin du: arinketaren egoera eguneratzea. Saiatu berriro.',
            link_form_template: 'Lotura inprimaki erregulatzaileen txantiloia',
            link_form_template_placeholder: 'Hautatu DPIA inprimaki-txantiloia (aukerakoa)',
            link_form_template_none: 'Bat ere ez',
            linked_form: 'Lotutako formularioa',
            linked_form_creating: 'Lotutako formularioa sortzen...',
            linked_form_error: 'Ezin izan da estekatutako inprimaki-instantzia sortu. DPIA estekatutako inprimakirik gabe sortu da.',
            view_linked_form: 'Ikusi formularioa',
            announce_created_with_form: 'DPIA estekatutako inprimaki-instantziarekin sortu da',
            creation_mode: 'DPIA Sortze modua',
            mode_standard: 'DPIA estandarra',
            mode_template: 'Erabili inprimaki erregulatzaileen txantiloia',
            select_form_template: 'Hautatu Inprimaki-txantiloia',
            create_and_fill_form: 'Sortu eta bete formularioa',
            template_sections_title: 'Txantiloi Atalak',
            field_singular: 'eremua',
            field_plural: 'eremuak',
            no_sections: 'Ez da atalik aurkitu txantiloi honetan.',
            template_mode_hint: 'Sortu ondoren, arauzko formulario osoa betetzera birbideratuko zara.',
            linked_dossier_filings: 'Lotutako dossier-aurkezpenak',
            no_linked_dossier_filings: 'Ez dago dossier-aurkezpenik DBEA honekin lotuta.',
            view_dossier_filing: 'Ikusi aurkezpena',
            hide_dossier_filings: 'Ezkutatu dossier-aurkezpenak',
            supporting_evidence: 'Euskarrizko Frogak',
            hide_supporting_evidence: 'Ezkutatu frogak',
            no_supporting_evidence: 'Ez da frogarik erantsi.',
            upload_supporting_evidence: 'Kargatu frogak',
            view_form_responses: 'Ikusi formularioaren erantzunak',
            regulatory_framework: 'Araudia-esparrua',
            all_frameworks: 'Esparru guztiak',
            no_templates_for_market: 'Ez dago txantiloirik hautatutako merkaturako. Mesedez, hautatu beste merkatu bat edo sortu txantiloi bat.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Datu-transferentziak',
            announce_found: '{count} transferentzia-ebaluazio aurkituta',
            announce_submitted_for_review: 'Transferentzia berrikuspenerako bidalita',
            announce_deprecated: 'Transferentzia zaharkituta',
            announce_decision: 'Transferentzia {decision}',
            announce_risk_eval_completed: 'Arrisku-ebaluazioa osatuta',
            announce_safeguard_status_updated: 'Babes-neurriaren egoera eguneratuta',
            announce_safeguards_loaded: '{count} babes-neurri kargatuta',
            aria_filter: 'Iragazi transferentzia-ebaluazioak',
            aria_filter_by_status: 'Iragazi egoeraren arabera',
            aria_filter_by_mechanism: 'Iragazi transferentzia-mekanismoaren arabera',
            aria_table: 'Datu-transferentzia ebaluazioak',
            title: 'Mugaz gaindiko transferentzia-ebaluazioak',
            new: 'Transferentzia berria',
            edit: 'Editatu transferentzia',
            name: 'Izena',
            from_country: 'Jatorrizko herrialdea',
            to_country: 'Helburuko herrialdea',
            safeguards: 'Bermeak',
            risk_level: 'Arrisku-maila',
            assessment_notes: 'Ebaluazio-oharrak',
            mechanism: 'Mekanismo',
            transfer_mechanism: 'Transferentzia-mekanismoa',
            transfer_mechanism_detail: 'Transferitu Mekanismo Xehetasuna',
            all_mechanisms: 'Mekanismo guztiak',
            mechanism_other: 'Beste',
            data_types: 'Datu Motak (comma-separated)',
            data_subjects: 'Datu Subjects (comma-separated)',
            purpose: 'Xede',
            recipient_name: 'Recipient Izena',
            create_success: 'Transferentzia-ebaluazioa sortu da.',
            update_success: 'Transferentzia-ebaluazioa eguneratu da.',
            recipient_details: 'Hartzailearen xehetasunak',
            recipient_details_placeholder: 'Hartzailearen rola, kokapena eta sistemak, eta aurrerako transferentziaren xehetasunak',
            vendor_id: 'Hornitzaile IDa',
            processing_activity_ids: 'Prozesatzen Jarduera IDs (comma-separated)',
            reviewer_ids: 'Berrikuskatzaile IDs (comma-separated)',
            approval_mode: 'Onartze-modua',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-aren oniritzia behar du',
            review_date: 'Berrikusketa Data',
            created: 'Sortuta',
            deprecated: 'Zaharkituta',
            submit_for_review: 'Bidali for Berrikusketa',
            approve_reject: 'Onartu/Baztertu',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Zaharkitu',
            risk_eval: 'Arrisku Eval',
            hide_safeguards: 'Ezkutatu Babes-neurriak',
            mechanism_proof: 'Mekanismoaren Froga',
            hide_mechanism_proof: 'Ezkutatu Mekanismoaren Froga',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Ez dago mekanismo frogarik erantsita.',
            upload_mechanism_proof: 'Kargatu mekanismoaren froga',
            comments: 'Iruzkinak',
            submit_decision: 'Bidali Erabaki',
            data_sensitivity: 'Datu Sensitivity (1-5)',
            country_risk: 'Herrialdea Arrisku (1-5)',
            safeguard_adequacy: 'Babes-neurri Adequacy (1-5)',
            volume_factor: 'Bolumen-faktorea (1-5)',
            evaluate: 'Ebaluatu',
            add_safeguard: 'Gehitu babes-neurria',
            responsible_party: 'Alderdi arduraduna',
            responsible_party_placeholder: 'Edo izena eskuz sartu',
            responsible: 'Arduraduna',
            implementation_date: 'Implementation Data',
            implementation: 'Inplementazioa',
            expiry_date: 'Expiry Data',
            expiry: 'Iraungitzea',
            expire: 'Iraungi',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Ez da safeguards found. aurkitu',
            no_transfers: 'Ez da transfer assessments found. aurkitu',
            fetch_error: 'Huts egin du: fetch transfer assessments. Saiatu berriro.',
            save_error: 'Huts egin du: save transfer assessment. Saiatu berriro.',
            submit_review_error: 'Huts egin du: submit for review. Saiatu berriro.',
            decision_error: 'Huts egin du: process decision. Saiatu berriro.',
            deprecate_error: 'Huts egin du: deprecate transfer. Saiatu berriro.',
            risk_eval_error: 'Huts egin du: evaluate arrisku. Saiatu berriro.',
            fetch_safeguards_error: 'Huts egin du: fetch safeguards. Saiatu berriro.',
            save_safeguard_error: 'Huts egin du: save safeguard. Saiatu berriro.',
            update_safeguard_status_error: 'Huts egin du: babes-neurriaren egoera eguneratzea. Saiatu berriro.',
            safeguard_type_encryption_in_transit: 'Enkriptazioa garraioan',
            safeguard_type_encryption_at_rest: 'Enkriptazioa atsedenean',
            safeguard_type_access_controls: 'Sarbide-kontrolak',
            safeguard_type_contractual_clauses: 'Kontratu-klausulak',
            safeguard_type_data_minimization: 'Datu Minimization',
            safeguard_type_pseudonymization: 'Pseudonimizazioa',
            safeguard_type_audit_rights: 'Ikuskaritza Eskubideak',
            safeguard_type_breach_notification: 'Urraketa Jakinarazpen',
            safeguard_type_data_localization: 'Datu Localization',
            safeguard_type_other: 'Beste',
            activate: 'Aktibatu',
            all_statuses: 'Egoera guztiak',
            any: 'Any',
            mechanism_detail: 'Mekanismo-xehetasuna',
            safeguard_types: {
                encryption_in_transit: 'Enkriptazioa trantsizioan',
                encryption_at_rest: 'Enkriptazioa atsedenean',
                access_controls: 'Sarbide-kontrolak',
                contractual_clauses: 'Kontratu-klausulak',
                data_minimization: 'Datu-minimizazioa',
                pseudonymization: 'Pseudonimizazioa',
                audit_rights: 'Auditoretza-eskubideak',
                breach_notification: 'Urraketa-jakinarazpena',
                data_localization: 'Datuen lokalizazioa',
                other: 'Beste',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Ebaluazioen hutsune-txostena',
            announce_loaded: 'Hutsune-txostena kargatuta. {count} hutsune identifikatuta',
            aria_activities_without_dpia: 'DBEA ebaluaziorik gabeko jarduerak',
            aria_activities_unclassified: 'DBEA eskakizuna sailkatu gabeko jarduerak',
            activities_unclassified_section_title: 'DBEA eskakizuna sailkatu gabeko jarduerak',
            activities_unclassified_description: 'Jarduera hauek ez dira berrikusi DBEA lege aldetik beharrezkoa den zehazteko. Sailkatu bakoitza seinalea kentzeko.',
            activities_unclassified_empty: 'Jarduera aktibo guztiak sailkatu dira.',
            aria_unmitigated_risks: 'Arindu gabeko arrisku altu eta kritikoak',
            aria_transfers_without_safeguards: 'Babes-neurri egokirik gabeko transferentziak',
            aria_expired_assessments: 'Iraungitako ebaluazioak berritze behar dutenak',
            title: 'Ebaluazio hutsune-txostena',
            no_dpia: 'DBEErik gabeko jarduerak',
            no_assessment: 'Ebaluaziorik gabeko transferentziak',
            unmitigated: 'Arindu gabeko arriskuak dituzten DBEEak',
            activity_id: 'Jarduera IDa',
            dpia_id: 'DPIA IDa',
            assessment_id: 'Ebaluazio ID',
            expired_assessments: 'Iraungita Ebaluazioak',
            no_gaps_activities: 'Ez da gaps found. all activities have dpias. aurkitu',
            no_unmitigated_risks: 'Ez da unmitigated high/critical arrisku found. aurkitu',
            no_gaps_safeguards: 'Ez da gaps found. all transfers have adequate safeguards. aurkitu',
            no_expired: 'Ez da expired assessments found. aurkitu',
            no_report_data: 'Ez da gap report datu. aurkitu',
            fetch_error: 'Huts egin du: fetch gap report. Saiatu berriro.',
            no_expired_assessments: 'Ez dago iraungitako ebaluaziorik.',
            no_gaps_dpia: 'Ez da dpia gaps found. aurkitu',
            no_data: 'Ez da datu. aurkitu',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dosier-aurkezpenaren xehetasuna',
            page_title_list: 'Dosier-aurkezpen kasuak',
            announce_filings_found: '{count} aurkezpen aurkituta',
            announce_results: '{count} emaitza',
            aria_filter_by_status: 'Iragazi egoeraren arabera',
            title: 'Espedienteak Artxibatzea',
            nav_label: 'Espedienteen Aurkezpena',
            create: 'Sortu Espedientea Artxibatzea',
            form_type: 'Inprimaki mota',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Erreferentzia',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Aurkeztutako data',
            outcome: 'Emaitza',
            parent_filing: 'Gurasoa',
            no_filings: 'Ez da espedienterik aurkitu.',
            fetch_error: 'Ezin izan dira espedienteen fitxategiak eskuratu. Mesedez, saiatu berriro.',
            save_error: 'Ezin izan da sortu espedientea artxibatzea. Mesedez, saiatu berriro.',
            created: 'Espedientea sortu da.',
            loaded: 'Espedientea kargatuta dago.',
            not_found: 'Ez da aurkitu espedientea artxibatzea.',
            back_to_list: 'Itzuli zerrendara',
            section_assignments: 'Atala Zereginak',
            assign_sections: 'Esleitu Atalak',
            section_id: 'atala',
            department: 'Saila',
            assignee: 'Esleipenduna',
            deadline: 'Epemuga',
            no_assignments: 'Oraindik ez dago atal lanik.',
            sections_assigned_msg: 'Esleitutako atalak.',
            assign_error: 'Ezin izan dira atalak esleitu. Mesedez, saiatu berriro.',
            lifecycle: 'Artxiboaren Bizi-zikloa',
            submission: 'Bidalketa xehetasunak',
            submission_channel: 'Bidalketa kanala',
            confirmation_number: 'Berrespen-zenbakia',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Berrikusketara bidali',
            submitted_for_review: 'Barne berrikuspenerako aurkeztu da.',
            submit_review_error: 'Ezin izan da bidali berrikusteko. Mesedez, saiatu berriro.',
            legal_signoff: 'Lege-zenbakia',
            legal_signoff_done: 'Legezko sinadura amaitu da.',
            legal_signoff_error: 'Ezin izan da lege-zenbaketa osatu. Mesedez, saiatu berriro.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Ezin izan da onartu fitxategia. Mesedez, saiatu berriro.',
            materialisation_status_column: 'Bidalketa artefaktua',
            materialisation_na: 'Ez dago aplikagarri',
            materialisation_pending: 'Zain',
            materialisation_success: 'Materializatuta',
            materialisation_failed: 'Huts egin du',
            materialisation_banner_title: 'Ezin izan da bidalketa artefaktua sortu',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Saiatu berriro',
            materialisation_retry_count: 'Aurreko berriro saiakerak:',
            materialisation_retry_success: 'Bidalketa artefaktua behar bezala gauzatu da.',
            materialisation_retry_announce: 'Berriro saiatu da',
            filter_materialisation_failed: 'Erakutsi materializazio akatsak soilik',
            column_title: 'Izenburua',
            column_form_type: 'Forma',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Osagarri eskaera',
            supplement_requests: 'Osagarrien eskaerak',
            supplement_response: 'Erantzun osagarria',
            record_supplement: 'Erregistro osagarriaren eskaera',
            supplement_recorded: 'Osagarri eskaera erregistratu da.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Ezin izan da gehigarri eskaera erregistratu. Mesedez, saiatu berriro.',
            submit_supplement: 'Bidali Erantzun Gehigarria',
            supplement_submitted_msg: 'Erantzun gehigarria aurkeztu da.',
            supplement_submit_error: 'Ezin izan da erantzun osagarria bidali. Mesedez, saiatu berriro.',
            response_status: 'Erantzunaren egoera',
            response_notes: 'Erantzun Oharrak',
            resubmit: 'Bidali berriro',
            resubmitted_msg: 'Dosierra berriro bidali da.',
            resubmit_error: 'Ezin izan da berriro bidali espedientea. Mesedez, saiatu berriro.',
            outcome_error: 'Ezin izan da emaitza grabatu. Mesedez, saiatu berriro.',
            accepted: 'Onartua',
            rejected: 'Baztertua',
            close: 'Itxi',
            closed_msg: 'Artxiboa itxita.',
            close_error: 'Ezin izan da artxiboa itxi. Mesedez, saiatu berriro.',
            change_filing: 'Sortu aldaketa-fitxategia',
            change_filing_created: 'Aldaketa fitxategia sortu da.',
            change_filing_error: 'Ezin izan da sortu aldaketa-fitxategia. Mesedez, saiatu berriro.',
            change_reason: 'Aldaketaren arrazoia',
            filing_lineage: 'Filing Lineage',
            timers: 'Tenporizadore aktiboak',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Gehigarriaren Epea',
            timer_resubmission_review: 'Berriro aurkezteko epea',
            timer_department_input: 'Sailaren Epea',
            days_remaining: 'egun geratzen dira',
            days_overdue: 'egun atzeratuta',
            cover_letter: 'Aurkeztutako gutuna',
            checklist: 'Kontrol-zerrenda',
            evidence_index: 'Froga Indizea',
            status_draft: 'Zirriborroa',
            status_internal_review: 'Barneko berrikusketa',
            status_legal_signoff: 'Lege-zenbakia',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Osagarria eskatua',
            status_supplement_submitted: 'Aurkeztutako gehigarria',
            status_resubmitted: 'Berriz bidalita',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Itxita',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Zirriborroa',
            step_internal_review: 'Barneko berrikusketa',
            step_legal_signoff: 'Lege-zenbakia',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Osagarria eskatua',
            step_supplement_submitted: 'Aurkeztutako gehigarria',
            step_resubmitted: 'Berriz bidalita',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Itxita',
            linked_dpia: 'Lotutako DBEA',
            linked_dpia_none: 'Ez dago DBEA-rik aurkezpen honekin lotuta.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Ikusi DBEA',
            linked_dpia_label: 'Dossier-aurkezpen hau hurrengo DBEA-rekin dago lotuta:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Bidalketa artefaktuaren osasuna',
            page_title: 'Bidalketa Artifact Health',
            page_description: 'Espedientea materializatzeko arazoak detektatu eta konpondu. Zerrendak zure erakundeari dagozkio.',
            failed_title: 'Huts egindako materializazioak',
            failed_empty: 'Hutsik gabeko materializaziorik ez - ona.',
            pending_title: 'Onartua baina ez da gauzatu',
            pending_empty: 'Onartutako espediente guztiek atze-espediente bat dute.',
            orphan_title: 'Espedienteak umezurtzak',
            orphan_description: 'Materializazioaren ondorioz sortutako espedienteak, jada inongo artxibatzek erreferentzia egiten ez dutena, normalean aldibereko lasterketa baten galtzailea. Ikerketarako zerrendatua; garbiketa eskuzkoa da.',
            orphan_empty: 'Ez da espediente umezurtz detektatu.',
            materialise_button: 'Materializatu orain',
            column_failure_reason: 'Porrotaren arrazoia',
            column_failed_at: 'Huts egin du',
            column_retries: 'Berriro saiatu',
            column_approved_at: 'Onartua',
            column_dossier_id: 'Espedientea ID',
            column_source_filing_id: 'Iturria artxibatzea',
            column_dossier_type: 'Mota',
            column_created_at: 'urtean sortua',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Sarbidea ukatuta',
        access_denied: 'Ez duzu Sailkapen modulura sartzeko beharrezko baimenik. Jarri harremanetan zure administratzailearekin.',
        nav: {
            taxonomies: 'Taxonomiak',
            labeling: 'Etiketatzea',
            lifecycle: 'Bizi-zikloa',
            gaps: 'Hutsune-txostena',
            exports: 'Esportazioak',
        },
        exports: {
            title: 'Sailkapen esportazioak',
            csv_section: 'CSV esportazioak',
            pdf_section: 'PDF txostenak',
            inventory: 'Inbentarioa',
            classification_register: 'Sailkapen erregistroa',
            raci: 'RACI matrizea',
            exceptions: 'Salbuespenak',
            evidence_pack: 'Ebidentzia-paketea',
            delta_report: 'Delta txostena',
            executive_summary: 'Zuzendaritza-laburpena',
            exporting: 'Esportatzen…',
            export_success: 'Esportazioa arrakastaz osatu da.',
            export_error: 'Esportazioak huts egin du. Saiatu berriro.',
            csv_download: 'CSV deskarga prest.',
            pdf_download: 'PDF sortzea hasita.',
            no_data: 'Ez dago daturik esportatzeko.',
        },
        delta: {
            title: 'Aldaketa-xehetasunak',
            added: 'Gehitutakoak',
            removed: 'Kendutakoak',
            changed: 'Aldatutakoak',
            added_count: 'Gehitutakoak ({count})',
            removed_count: 'Kendutakoak ({count})',
            changed_count: 'Aldatutakoak ({count})',
            object_id: 'Objektu IDa',
            object_type: 'Objektu mota',
            taxonomy: 'Taxonomia',
            level: 'Maila',
            previous: 'Aurrekoa',
            current: 'Unekoa',
            no_records: 'Ez dago erregistrorik atal honetan.',
            empty_section: 'Atal hau hutsik dago.',
        },
        schedule: {
            title: 'Aurkikuntza-zikloa programatu',
            enabled: 'Gaituta',
            interval_days: 'Tartea (egunak)',
            interval_label: 'N egunero exekutatu',
            manual_only: 'Eskuzkoa soilik',
            every_n_days: '{n} egunero',
            save: 'Programazioa gorde',
            cancel: 'Utzi',
            schedule_updated: 'Programazioa eguneratuta.',
        },
        taxonomies: {
            title: 'Sailkapen-taxonomiak',
            new: 'Taxonomia berria',
            edit: 'Editatu taxonomia',
            name: 'Izena',
            levels: 'Sailkapen-mailak',
            seed_defaults: 'Hasieratu Lehenetsia',
            scope: 'Esparrua',
            scope_organization: 'Erakunde',
            scope_module_specific: 'Modulu Specific',
            deprecate: 'Zaharkitu',
            hide_levels: 'Ezkutatu Mailak',
            versions: 'Bertsioak',
            hide_versions: 'Ezkutatu Bertsioak',
            add_level: 'Gehitu maila',
            new_level: 'Berria Maila',
            edit_level: 'Editatu Maila',
            code: 'Kodea',
            label: 'Etiketa',
            color: 'Kolorea',
            ordinal: 'Ordinala',
            handling_requirements: 'Handling Eskakizunak',
            version_history: 'Bertsio-historia',
            changes: 'Aldaketak',
            failed_fetch: 'Huts egin du: fetch taxonomies. Saiatu berriro.',
            failed_save: 'Huts egin du: save taxonomy. Saiatu berriro.',
            failed_activate: 'Huts egin du: activate taxonomy. Saiatu berriro.',
            failed_deprecate: 'Huts egin du: deprecate taxonomy. Saiatu berriro.',
            failed_seed: 'Huts egin du: seed default taxonomies. Saiatu berriro.',
            failed_fetch_levels: 'Huts egin du: fetch taxonomy levels. Saiatu berriro.',
            failed_save_level: 'Huts egin du: save taxonomy level. Saiatu berriro.',
            failed_delete_level: 'Huts egin du: delete taxonomy level. Saiatu berriro.',
            failed_fetch_versions: 'Huts egin du: fetch taxonomy versions. Saiatu berriro.',
            loaded_count: '{count} taxonomia kargatuta',
            defaults_seeded: 'Lehenetsitako taxonomiak hasieratuta.',
            no_levels: 'Ez dago sailkapen-mailarik definituta — gehitu mailak zure sailkapen-hierarkia definitzeko.',
            no_versions: 'Oraindik ez dago bertsio-historiarik — bertsioak sortzen dira taxonomia aktibo bat eguneratzen denean.',
        },
        labeling: {
            title: 'Datuen etiketatzea',
            new: 'Etiketa berria',
            edit: 'Editatu etiketa',
            entity_type: 'Entitate mota',
            entity_id: 'Entitate IDa',
            taxonomy: 'Taxonomia',
            classification_level: 'Sailkapen-maila',
            applied_by: 'Aplikatzailea',
            applied_at: 'Aplikatze-data',
            bulk_apply: 'Aplikazio masiboa',
            bulk_remove: 'Kentzea masiboa',
            all_taxonomies: 'Taxonomia guztiak',
            system: 'Sistema',
            dataset: 'Datu-multzoa',
            level_code_placeholder: 'Maila kodea...',
            select_taxonomy: 'Hautatu taxonomy...',
            select_level: 'Hautatu level...',
            rationale: 'Arrazoibidea',
            review_date: 'Berrikusketa Data',
            review: 'Berrikusketa',
            selected: 'hautatuta',
            select_all_labels: 'Hautatu etiketa guztiak',
            loaded_count: '{count} etiketa kargatuta',
            hide_stale_labels: 'Ezkutatu Stale Etiketak',
            show_conflicts: 'Erakutsi Conflicts',
            hide_conflicts: 'Ezkutatu Conflicts',
            label_conflicts: 'Etiketa-gatazkak',
            conflict_reason: 'Gatazka-arrazoia',
            labels: 'Etiketak',
            failed_fetch: 'Huts egin du: fetch labels. Saiatu berriro.',
            failed_fetch_taxonomies: 'Huts egin du: fetch taxonomies. Saiatu berriro.',
            failed_fetch_levels: 'Huts egin du: fetch taxonomy levels. Saiatu berriro.',
            failed_save: 'Huts egin du: save label. Saiatu berriro.',
            failed_review: 'Huts egin du: review label. Saiatu berriro.',
            failed_bulk: 'Huts egin du: bulk label. Saiatu berriro.',
            failed_stale: 'Huts egin du: fetch stale labels. Saiatu berriro.',
            failed_conflicts: 'Huts egin du: fetch label conflicts. Saiatu berriro.',
            review_permission_denied: 'Ez duzu baimenik etiketa hau berrikusteko.',
            review_not_found: 'Etiketa ez da aurkitu — agian ezabatu egin da.',
            approve_selected: 'Hautatuak onartu',
            reject_selected: 'Hautatuak baztertu',
            approved: 'Onartua',
            rejected: 'Baztertua',
            labels_count: 'etiketak',
            failed_count: 'huts egin du',
            confirm_approve: 'Onarpena berretsi',
            confirm_reject: 'Baztertzea berretsi',
            approve_n_labels: '{count} etiketa onartu?',
            reject_n_labels: '{count} etiketa baztertu?',
            submit_for_review: 'Berrikuspenerako bidali',
            approve: 'Onartu',
            reject: 'Baztertu',
            parent_system: 'Guraso sistema',
            parent_system_placeholder: 'Hautatu guraso sistema...',
            parent_system_help: 'Gatazka detekziorako erabiltzen da.',
            dataset_level: 'Datu-multzo maila',
            parent_level: 'Guraso maila',
            bulk_created: '{created} etiketa sortu',
            bulk_skipped: '{skipped} saltatu',
            bulk_all_skipped: '{skipped} guztiak saltatu',
            ids_hint: 'Sartu ID bat edo gehiago komaz bereizita',
            delete_confirm: 'Ezabatu "{id}" etiketa?',
        },
        lifecycle: {
            title: 'Datuen bizi-zikloaren jarraipena',
            new: 'Sarrera berria',
            current_stage: 'Uneko fasea',
            last_transition: 'Azken trantsizioa',
            transition: 'Trantsizioa',
            view_history: 'Ikusi historia',
            stages: {
                creation: 'Sorkuntza',
                collection: 'Bilketa',
                processing: 'Tratamendua',
                storage: 'Biltegiratzea',
                retention: 'Kontserbazioa',
                disposal: 'Xedatzea',
                archived: 'Artxibatua',
                active: 'Aktibo',
                pending_deletion: 'Zain Deletion',
                deleted: 'Ezabatu da',
                on_hold: 'Atxikita',
            },
            all_stages: 'Fase guztiak',
            object_id: 'Objektu IDa',
            object_type: 'Object Mota',
            policy_reference: 'Politika Erreferentzia',
            retention_period_days: 'Atxikipen Aldi (egun)',
            retention_basis: 'Atxikipen Basis',
            deletion_method: 'Ezabatze-metodoa',
            deletion_secure_erase: 'Ezabatze segurua',
            deletion_anonymization: 'Anonimizazioa',
            deletion_physical_destruction: 'Suntsiketa fisikoa',
            deletion_other: 'Beste',
            responsible_party: 'Alderdi arduraduna',
            responsible_user_id: 'Responsible Erabiltzaile ID',
            retention_expiry: 'Atxikipen Expiry',
            hold_reason: 'Atxikitze-arrazoia',
            hold: 'Atxiki',
            release: 'Askatu',
            hide_events: 'Ezkutatu Gertaerak',
            events_timeline: 'Gertaerak Timeline',
            add_event: 'Gehitu gertaera',
            event_type: 'Gertaera mota',
            create_event: 'Sortu Gertaera',
            from_stage: 'From Fase',
            to_stage: 'To Fase',
            actor: 'Aktorea',
            reason_placeholder: 'Arrazoia...',
            go: 'Go',
            show_expired_retentions: 'Erakutsi Iraungita Atxikipen',
            hide_expired_retentions: 'Ezkutatu Iraungita Atxikipen',
            expired_retentions_alert: 'Iraungita Atxikipen Alerta',
            failed_fetch: 'Huts egin du: fetch lifecycle erregistroak. Saiatu berriro.',
            failed_save: 'Huts egin du: save lifecycle erregistro. Saiatu berriro.',
            failed_transition: 'Huts egin du: transition lifecycle stage. Saiatu berriro.',
            failed_hold: 'Huts egin du: place hold. Saiatu berriro.',
            failed_release: 'Huts egin du: release hold. Saiatu berriro.',
            failed_fetch_events: 'Huts egin du: fetch lifecycle events. Saiatu berriro.',
            failed_create_event: 'Huts egin du: create lifecycle event. Saiatu berriro.',
            failed_expired: 'Huts egin du: fetch expired retentions. Saiatu berriro.',
            hold_reason_prompt: 'Sartu hold reason:',
            loaded_count: '{count} bizi-ziklo erregistro kargatuta',
            stage_transition_for: 'Fase-trantsizioa {id}-rako',
            event_created: 'Sortua',
            event_updated: 'Eguneratua',
            event_approved: 'Onartua',
            event_deprecated: 'Zaharkitua',
            event_proposed: 'Proposatua',
            event_reviewed: 'Berrikusia',
            event_archived: 'Artxibatua',
            event_restored: 'Leheneratua',
        },
        gap_report: {
            title: 'Sailkapen hutsune-txostena',
            unlabeled: 'Etiketatu gabeko datuak',
            missing_lifecycle: 'Bizi-ziklorik gabea',
            stale_labels: 'Zaharkitutako etiketak',
            run_detection: 'Exekutatu Hutsune Detection',
            detecting: 'Detektatzen...',
            detection_complete: 'Hutsune detection complete. {count} gap(s) detected.',
            detection_failed: 'Hutsune detection failed.',
            by_severity: 'By Larritasun',
            by_gap_type: 'By Hutsune Mota',
            all_gap_types: 'Hutsune mota guztiak',
            conflicting_labels: 'Conflicting Etiketak',
            expired_retention: 'Iraungita Atxikipen',
            missing_handling: 'Falta-kudeaketa',
            info: 'Info',
            gap_type: 'Hutsune mota',
            object_id: 'Objektu IDa',
            object_type: 'Object Mota',
            detected_at: 'Detektatua',
            open: 'Ireki',
            acknowledged: 'Aitortua',
            remediated: 'Konponduta',
            accepted: 'Onartuta',
            acknowledge: 'Aitortu',
            remediate: 'Konpondu',
            accept_risk: 'Onartu arriskua',
            accept_risk_prompt: 'Sartu arrisku acceptance justifikazio:',
            failed_fetch: 'Huts egin du: fetch gaps. Saiatu berriro.',
            failed_acknowledge: 'Huts egin du: acknowledge gap. Saiatu berriro.',
            failed_remediate: 'Huts egin du: remediate gap. Saiatu berriro.',
            failed_accept_risk: 'Huts egin du: accept gap arrisku. Saiatu berriro.',
            failed_detect: 'Huts egin du: run gap detection. Saiatu berriro.',
            loaded_count: '{count} sailkapen-hutsune kargatuta',
            related_object_id: 'Lotutako objektuaren IDa',
            related_object_type: 'Lotutako objektu-mota',
            gap_types: {
                unlabeled_asset: 'Etiketatu gabeko aktiboa',
                missing_lifecycle: 'Bizi-ziklo falta',
                expired_retention: 'Atxikipen iraungia',
                extended_pending_deletion: 'Ezabatze zain luzatua',
            },
        },
        label_workflow: {
            title: 'Etiketa-lan-fluxua',
            proposed: 'Proposatua',
            under_review: 'Berrikuspenean',
            approved: 'Onartua',
            deprecated: 'Zaharkitua',
            submit_review: 'Bidali berrikuspenera',
            approve: 'Onartu etiketa',
            deprecate: 'Zaharkitu',
            bulk_approve: 'Onarpen masiboa',
            bulk_approve_success: 'Etiketak arrakastaz onartu dira.',
            submit_success: 'Etiketa berrikuspenera bidalita.',
            approve_success: 'Etiketa onartua.',
            deprecate_success: 'Etiketa zaharkitua.',
        },
        review_tasks: {
            title: 'Berrikusketa-atazak',
            create: 'Berrikusketa-ataza berria',
            system: 'Sistema',
            owner: 'Jabea',
            description: 'Deskribapena',
            resolution: 'Ebazpena',
            resolve: 'Ebatzi',
            approve: 'Onartu',
            modify: 'Aldatu',
            reject: 'Ukatu',
            escalate: 'Eskalatu',
            escalated: 'Eskalatuak',
            no_tasks: 'Ez dago berrikusketa-atazik.',
            resolve_success: 'Berrikusketa-ataza ebatzia.',
            status_pending: 'Zain',
            status_resolved: 'Ebatzia',
            status_escalated: 'Eskalatua',
            notes: 'Oharrak',
            create_success: 'Berrikusketa-zeregina sortuta.',
            no_tasks_detail: 'Oraindik ez dago berrikusketa-zereginik — sortu zeregin bat etiketa-berrikusketak sistemen jabeei esleitzeko.',
            label_id: 'Etiketa-IDa',
            system_id: 'Sistema-IDa',
            system_name: 'Sistemaren izena',
            owner_id: 'Jabearen IDa',
            resolution_col: 'Ebazpena',
            notes_col: 'Oharrak',
        },
        exceptions: {
            title: 'Salbuespen-ilara',
            create: 'Salbuespen berria',
            severity: 'Larritasuna',
            system: 'Sistema',
            owner: 'Jabea',
            resolve: 'Ebatzi',
            no_exceptions: 'Ez da salbuespinik aurkitu.',
            resolve_success: 'Salbuespena ebatzia.',
            dashboard_open: 'Salbuespen irekiak',
            dashboard_resolved: 'Ebatzitako salbuespenak',
            dashboard_total: 'Guztira',
            dashboard_by_severity: 'Larritasunaren arabera',
            status_open: 'Irekia',
            status_resolved: 'Ebatzia',
            status_accepted_risk: 'Onartutako arriskua',
            create_success: 'Salbuespena sortuta.',
            no_exceptions_detail: 'Oraindik ez dago salbuespenak — salbuespenak larritasun handiko hutsunetatik sortzen dira, tratamendu berezia behar dutenak.',
            gap_id: 'Hutsune-IDa',
            system_id: 'Sistema-IDa',
            system_name: 'Sistemaren izena',
            owner_id: 'Jabearen IDa',
            resolution_notes: 'Ebazpen oharrak',
            resolution_notes_placeholder: 'Deskribatu nola konpondu zen salbuespen hau...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Enpresa datu-mapa',
            inventory: 'Datu-inbentarioa',
            total_systems: 'Sistema guztira',
            total_labels: 'Etiketa guztira',
            approved_count: 'Onartutako etiketak',
            proposed_count: 'Proposatutako etiketak',
            no_data: 'Ez dago sailkapen-daturik.',
            no_approved_labels: 'Oraindik ez dago etiketatutako objekturik — objektu bat datuen mapan agertzen da sailkapen-etiketa bat izan bezain laster, proposatua, berrikusten ari dena edo onartua izan.',
            loaded_count: 'Kargatu da enpresaren datuen mapa',
        },
        discovery: {
            title: 'Aurkikuntza-zikloak',
            create: 'Ziklo berria',
            cycle_name: 'Ziklo-izena',
            run_delta: 'Exekutatu delta detekzioa',
            delta_success: 'Delta detekzioa osatua.',
            create_success: 'Aurkikuntza-zikloa sortua.',
            no_cycles: 'Ez dago aurkikuntza-zikloik.',
            status_created: 'Sortu',
            status_scanning: 'Eskaneatzea',
            status_review: 'Iritzia',
            status_approved: 'Onartua',
            status_published: 'Argitaratu',
            status_draft: 'Zirriborroa',
            running: 'Exekutatzen',
            completed: 'Osatua',
            cancelled: 'Bertan behera utzita',
            delta_result_summary: '{added} gehitu, {removed} kendu, {changed} aldatutako etiketa aurkitu dira.',
            delta_results: 'Delta-emaitzak',
            view_results: 'Emaitzak ikusi',
            scope_type: 'Esparru mota',
            scope_type_all: 'Denak',
            scope_type_systems: 'Sistemak',
            scope_type_datasets: 'Datu multzoak',
            scope_ids: 'Esparru IDak',
            scope_ids_placeholder: 'Sartu IDak komaz bereizita',
            cycle_explanation: 'Aurkikuntza-ziklo batek zure datu-azpiegitura aztertzen du datu-aktiboak identifikatzeko, sailkatzeko eta jarraitzeko. Konfiguratu esparrua sistema edo datu-multzo espezifikoetara bideratzeko.',
            empty_state_title: 'Aurkikuntza-ziklorik ez oraindik',
            empty_state_description: 'Sortu zure lehen aurkikuntza-zikloa zure datu-aktiboak eskaneatzen eta sailkatzen hasteko.',
            empty_state_cta: 'Sortu Lehen Zikloa',
            validation_name_required: 'Zikloaren izena beharrezkoa da',
            validation_scope_type_required: 'Esparru mota behar da',
            validation_scope_ids_required: 'Gutxienez esparru-ID bat behar da',
        },
        regulatory: {
            sensitivity_importance_info: 'Datuen Legeak (60/2024/QH15 Legeak, 18e4bfd1 eskakizuna) dimentsio bereizi gisa sailkatzea eskatzen du bai sentikortasunaren eta bai garrantziaren arabera. Sortu taxonomia bat dimentsio bakoitzerako, baldintza hori betetzeko.',
            handling_rules_info: 'Sailkapen-maila bakoitzak kudeatzeko arauak zehaztu behar ditu eskakizun bakoitzeko. b89981ac ("Definitu kudeaketa-arauak sailkapen-mailaren arabera").',
            template_internal_ids_info: 'Sailkapen-txantiloi guztiek barne IDak erabiltzen dituzte (adibidez, vn_data_law_data_classification_record). Ez da txantiloirik aurkezten ministerioaren inprimaki ofizial gisa.',
            ai_risk_tiers_info: 'AA Legeak (134/2025/QH15 Legea, 3a87ad63 eskakizuna) AA sistemen lau arrisku-maila definitzen ditu: minimoa, mugatua, altua eta onartezina. Sortu AA arriskuen taxonomia bat maila hauekin.',
            ai_risk_tier_minimal: 'Minimoa',
            ai_risk_tier_limited: 'Mugatua',
            ai_risk_tier_high: 'Altua',
            ai_risk_tier_unacceptable: 'Onartezinak',
            ai_register_info: 'AI sailkapen-erregistroek AI Sistemaren Erregistroko sarrera batera estekatu beharko lukete (0f4475d3 eskakizuna) atzemateko: sistemaren izena, jabea, helburua, hedapen-testuingurua, arrisku-maila, saltzaileen parte-hartzea.',
            ai_template_internal_info: 'AA sailkapen-txantiloiek barne IDak erabiltzen dituzte (adibidez, AI-RISK-01). Ez da AA leku-marken ID ofizialik erabiltzen (AI-HC-01etik AI-HC-08ra).',
            ai_audit_trail_info: 'AI sailkapen-aldaketek UC-VN-06 bakoitzeko auditoretza-pista bertsionatua sortzen dute: aktorea, denbora-zigilua, aurreko balioa, balio berria eta arrazoibidea.',
            lifecycle_data_d1_info: 'Bizi-zikloaren trantsizioak Datuen Legearen prozeduraren DATA-D1 pausoetara bideratzen dira: katalogatu, sailkatu, kudeatzeko arauak definitu, frogak jaso, berrikuspenak programatu.',
            lifecycle_deletion_link_info: 'Bizitzaren amaierara iristen den sailkatutako datu-aktibo batek ezabatze-programazioaren erregistroa abiarazi beharko luke Ezabatzeko Orkestrazio moduluaren bidez.',
            lifecycle_evidence_info: 'Bizi-zikloaren egoera-aldaketa bakoitzak (zirriborroa, aktiboa, berrikuspen gutxikoa, artxibatua, ezabatua) aktorea, denbora-zigilua eta justifikazioa ikuskaritza-bideko sarrera gisa jasotzen ditu.',
            escalation_timers_info: 'UC-VN-10 eskalatze-epeak: 5 laneguneko jabeak berrikusteko, 48 ordu salbuespenen eskalatzeko, 72 ordu arrisku handiko edo sentikorrak diren elementuetarako.',
            escalation_owner_review: '5 lan-egun',
            escalation_exception: '48 ordu',
            escalation_high_risk: '72 ordu',
            gap_risk_areas_info: 'Datuen Legearen eskaria. f6c3060a ("Datuen arriskuen ebaluazioa") hutsuneak aztertzea eskatzen du 4 arrisku-eremutan: pribatutasuna, zibersegurtasuna, sarbide-kontrola eta arrisku operatiboa.',
            gap_risk_area_privacy: 'Pribatutasuna',
            gap_risk_area_cybersecurity: 'Zibersegurtasuna',
            gap_risk_area_access: 'Sarbide-kontrola',
            gap_risk_area_operational: 'Operazionala',
            gap_framework_filter_info: 'Iragazi hutsuneak arau-esparruaren arabera (VN_DATA_LAW edo VN_AI_LAW_2026) arau-esparru bakoitzeko hutsune garrantzitsuak ikusteko.',
            framework_vn_data_law: 'VN Datu Legea',
            framework_vn_ai_law: 'VN AA Legea 2026',
            enterprise_map_approved_info: 'Datu-mapa honek UC-VN-10 lan-fluxuaren 10. urratseko onartutako sailkapen-erregistroak soilik erakusten ditu ("Onartutako etiketak egungo enpresaren egia bihurtzen dira"). Zirriborroak eta proposatutako erregistroak kanpoan geratzen dira.',
            exports_bundle_info: 'Esportazio-sortak honako hauek dira: sailkapen-erregistroa, maneiatzeko arauak, jabetza/RACI eta froga-paketea — UC-VN-10 esportazio-eskakizunekin eta DATA-EVD-01 froga-orri formatuan bat datozenak.',
        },
    },
    monitoring: {
        no_permission_title: 'Baimen nahikorik ez',
        no_permission_message: 'Ez duzu baimenik monitorizazio modulu honetara sartzeko.',
        errors: {
            fetch: 'Huts egin du: load. Saiatu berriro.',
            save: 'Huts egin du: save. Saiatu berriro.',
            fetch_programs_failed: 'Huts egin du: fetch programs. Saiatu berriro.',
            fetch_kpi_failed: 'Huts egin du: fetch KPI datu. Saiatu berriro.',
            create_snapshot_failed: 'Huts egin du: create snapshot. Saiatu berriro.',
            fetch_periods_failed: 'Huts egin du: fetch periods. Saiatu berriro.',
            create_period_failed: 'Huts egin du: create period. Saiatu berriro.',
            auto_generate_failed: 'Huts egin du: auto-generate periods. Saiatu berriro.',
            start_period_failed: 'Huts egin du: start period. Saiatu berriro.',
            close_period_failed: 'Huts egin du: close period. Saiatu berriro.',
            save_program_failed: 'Huts egin du: save program. Saiatu berriro.',
            activate_program_failed: 'Huts egin du: activate program. Saiatu berriro.',
            pause_program_failed: 'Huts egin du: pause program. Saiatu berriro.',
            complete_program_failed: 'Huts egin du: complete program. Saiatu berriro.',
            archive_program_failed: 'Huts egin du: archive program. Saiatu berriro.',
            fetch_ric_templates_failed: 'Huts egin du: fetch RIC txantiloiak. Saiatu berriro.',
            fetch_ric_cases_failed: 'Huts egin du: fetch RIC cases. Saiatu berriro.',
            create_ric_case_failed: 'Huts egin du: create RIC case. Saiatu berriro.',
            transition_ric_failed: 'Huts egin du: transition RIC case. Saiatu berriro.',
            add_follow_up_failed: 'Huts egin du: add follow-up. Saiatu berriro.',
            respond_follow_up_failed: 'Huts egin du: respond to follow-up. Saiatu berriro.',
            link_evidence_failed: 'Huts egin du: link ebidentzia. Saiatu berriro.',
            fetch_task_templates_failed: 'Huts egin du: fetch task txantiloiak. Saiatu berriro.',
            save_task_template_failed: 'Huts egin du: save task template. Saiatu berriro.',
            delete_task_template_failed: 'Huts egin du: delete task template. Saiatu berriro.',
            delete_framework_link_failed: 'Huts egin du markoaren esteka ezabatzean. Saiatu berriro.',
            seed_defaults_failed: 'Huts egin du: seed defaults. Saiatu berriro.',
            fetch_tasks_failed: 'Huts egin du: fetch tasks. Saiatu berriro.',
            create_task_failed: 'Huts egin du: create task. Saiatu berriro.',
            instantiate_tasks_failed: 'Huts egin du: instantiate tasks. Saiatu berriro.',
            start_task_failed: 'Huts egin du: start task. Saiatu berriro.',
            complete_task_failed: 'Huts egin du: complete task. Saiatu berriro.',
            defer_task_failed: 'Huts egin du: defer task. Saiatu berriro.',
            block_task_failed: 'Huts egin du: block task. Saiatu berriro.',
            override_complete_failed: 'Huts egin du: override complete. Saiatu berriro.',
            assign_task_failed: 'Huts egin du: assign task. Saiatu berriro.',
            update_checklist_failed: 'Huts egin du: update checklist. Saiatu berriro.',
        },
        nav: {
            programs: 'Programak',
            periods: 'Aldiak',
            task_templates: 'Zereginen txantiloiak',
            tasks: 'Zereginak',
            kpi: 'KPI kontrol-panela',
            ric: 'Arautzaile-kontsultak',
            framework_link: 'Esparru-jarraipen',
            authority_readiness: 'Agintaritza-prestutasun',
            change_review: 'Aldaketa-berrikusketa',
            raci: 'RACI Matrizea',
        },
        programs: {
            page_title: 'Jarraipen-programak',
            title: 'Jarraipen-programak',
            new: 'Programa berria',
            edit: 'Editatu programa',
            name: 'Izena',
            scope: 'Irismena',
            frequency: 'Maiztasuna',
            owner: 'Jabea',
            activate: 'Aktibatu',
            pause: 'Pausatu',
            resume: 'Berrekin',
            complete: 'Osatu',
            archive: 'Artxibatu',
            custom_frequency_days: 'Pertsonalizatua Frequency (egun)',
            select_user: 'Hautatu erabiltzailea…',
            assign_training: 'Prestakuntza esleitu',
            linked_forms: 'Lotutako inprimakiak',
            linked_forms_none: 'Oraindik ez dago lotutako inprimakirik',
            loaded_count: '{0} programa kargatu',
            scope_labels: {
                full: 'Osoa',
                partial: 'Partziala',
                targeted: 'Helburu',
                consent: 'Adostasuna',
                dpia: 'DPIA',
                vendors: 'Hornitzaileak',
                classification: 'Sailkapena',
                rights_requests: 'Eskubide-eskaerak',
                data_mapping: 'Datuen mapaketa',
                transfers: 'Transferentziak',
                security_monitoring: 'Segurtasun Jarraipena',
                authority_response: 'Agintaritzaren Erantzuna',
                evidence_preservation: 'Frogak zaintzea',
                localization_review: 'Lokalizazioaren berrikuspena',
                ai_system_registration: 'AI Sistemaren erregistroa',
                ai_pre_deployment: 'AI Hedapenaren aurreko ebaluazioa',
                ai_transparency: 'AI Gardentasun-egiaztapena',
                ai_incident_review: 'AI Gorabeheraren berrikuspena',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Jarraipen Epeak',
            title: 'Berrikusketa-aldiak',
            new: 'Aldi berria',
            label: 'Aldiaren etiketa',
            start_date: 'Hasiera-data',
            end_date: 'Amaiera-data',
            reviewers: 'Berrikuskatzaileak',
            auto_generate: 'Automatikoki sortu aldiak',
            start: 'Hasi',
            close: 'Itxi',
            change_digest: 'Aldaketen laburpena',
            no_digest: 'Ez da digest. aurkitu',
            reviewers_count: 'Berrikuskatzaile Kopuru',
            select_program: 'Hautatu programa',
            select_program_prompt: 'Hautatu a program to view periods.',
            hide_digest: 'Ezkutatu Digest',
            loaded_count: '{0} epe kargatu dira',
            periods_auto_generated: 'Automatikoki sortutako aldiak',
            label_placeholder: 'adib. 2026-Q1',
        },
        task_templates: {
            page_title: 'Zereginen txantiloiak',
            title: 'Zereginen txantiloiak',
            new: 'Txantiloi berria',
            edit: 'Editatu txantiloia',
            category: 'Kategoria',
            priority: 'Lehentasuna',
            checklist_items: 'Kontrol-zerrendako elementuak',
            effort: 'Aurreikusitako ahalegina (orduak)',
            pre_seeded: 'Aurrez hasitakoa',
            seed_defaults: 'Hasieratu lehenetsiak',
            add_checklist: 'Gehitu kontrol-zerrendako elementua',
            guidance: 'Orientazio-oharrak',
            all_categories: 'Kategoria guztiak',
            loaded_count: '{0} ataza-txantiloi kargatu dira',
            defaults_seeded: 'Lehenetsitako zereginen txantiloiak hazi dira',
            checklist_item_label: '{0} egiaztapen-zerrendako elementua',
            remove_checklist_item: 'Kendu {0} kontrol-zerrendako elementua',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Berrikusketa-zereginak',
            title: 'Berrikusketa-zereginak',
            new: 'Zeregin berria',
            assigned_to: 'Esleitua',
            due_date: 'Epemuga',
            checklist: 'Kontrol-zerrenda',
            start: 'Hasi',
            complete: 'Osatu',
            defer: 'Atzeratu',
            block: 'Blokeatu',
            override_complete: 'Gainditu eta osatu',
            link_evidence: 'Lotu froga',
            direct_upload_label: 'Kargatu froga fitxategia',
            instantiate: 'Instantziatu txantiloietatik',
            defer_reason: 'Atzeratzeko arrazoia',
            override_justification: 'Gainditzeko justifikazioa',
            filter_assignee: 'Iragazi by assignee',
            select_period: 'Hautatu aldi',
            select_program: 'Hautatu programa',
            select_program_period_prompt: 'Hautatu a program and period to view tasks.',
            assign_placeholder: 'Esleitu to...',
            loaded_count: '{0} zeregina kargatu',
            tasks_instantiated: 'Zereginak txantiloietatik sortuta',
            overdue: 'Iraungita',
            checklist_for: 'kontrol-zerrenda honentzat',
            manage_checklist: 'Kudeatu kontrol-zerrenda',
            add_checklist_item: 'Gehitu elementua',
            checklist_item_label: 'Elementua',
            checklist_item_description: 'Deskribapena',
            remove_checklist_item: 'Kendu elementua',
            save_checklist: 'Gorde kontrol-zerrenda',
            no_checklist_items: 'Oraindik ez dago elementurik.',
            confirm_delete_attachment: 'Ziur zaude froga-fitxategi hau ezabatu nahi duzula? Ekintza hau ezin da desegin.',
        },
        kpi: {
            page_title: 'KPI panela',
            title: 'KPI kontrol-panela',
            kpi_name: 'KPI izena',
            current_value: 'Uneko balioa',
            target: 'Helburua',
            trends: 'Joerak',
            capture_snapshot: 'Capture Argazki',
            select_program: 'Hautatu programa',
            select_program_prompt: 'Hautatu a program to view kpis.',
            snapshot: 'Argazki',
            kpi_labels: {
                on_track: 'Bidean',
                at_risk: 'At Arrisku',
                behind: 'Atzean',
                rights_request_sla_rate: 'Eskubideen eskaera SLA Tarifa',
                vendor_review_currency: 'Saltzaileen berrikuspenaren moneta',
                dpia_completion_rate: 'DPIA Osatze-tasa',
                classification_coverage: 'Sailkapen-estaldura',
                consent_policy_currency: 'Adostasun-politika Moneta',
                security_event_detection_rate: 'Segurtasun-gertaerak hautemateko tasa',
                evidence_preservation_compliance_rate: 'Frogak zaintzeko betetze-tasa',
                authority_response_timeliness: 'Agintaritzaren Erantzuna Egokitasuna',
                localization_readiness_score: 'Lokalizazio-prestatasunaren puntuazioa',
                ai_register_completeness: 'AI Sistemaren erregistroa osotasuna',
                pre_deployment_gate_pass_rate: 'Inplementazioaren aurreko atearen pasabide-tasa',
                transparency_disclosure_coverage: 'Gardentasuna Dibulgazio Estaldura',
                ai_incident_readiness_score: 'AI Gorabeheren prestatasunaren puntuazioa',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'Zibersegurtasun Legearen KPIak: Segurtasun Gertaerak detektatzeko tasa, Frogak zaintzeko betetze tasa, Agintaritzaren Erantzunaren Epea, Lokalizatzeko Prestakuntzaren Puntuazioa.',
            ai_law_kpi_info: 'AA Legearen KPIak: AA sistemen erregistroaren osotasuna, inplementazioaren aurretiko atearen gainditze-tasa, gardentasun-dibulgazioaren estaldura, AA gorabeheretarako prestutasunaren puntuazioa.',
            no_data: 'Ez da datu. aurkitu',
            snapshot_date: 'Argazki Data',
            loaded_count: '{0} KPI argazki kargatu',
            snapshot_captured: 'KPI argazkia hartuta',
            showing_chart: 'Grafikoa erakusten',
            showing_table: 'Datu-taula erakusten',
            aria_current_values: 'KPI uneko balioak',
            kpi_label: 'KPI',
            actual: 'Erreala',
            trend: 'Joera',
            notes: 'Oharrak',
            trend_up: 'Gorantz',
            trend_down: 'Beherantz',
            trend_stable: 'Egonkorra',
        },
        ric: {
            page_title: 'RIC kasuak',
            title: 'Arautzaile-kontsulta kasuak',
            new: 'RIC kasu berria',
            reference: 'Erreferentzia',
            regulator: 'Arautzailea',
            inquiry_type: 'Kontsulta mota',
            deadline: 'Erantzuteko epemuga',
            lead: 'Arduraduna',
            follow_ups: 'Jarraipen-ekintzak',
            add_follow_up: 'Gehitu jarraipena',
            respond: 'Erantzun',
            question: 'Galdera',
            response: 'Erantzuna',
            asked_at: 'Galdetua',
            no_follow_ups: 'Ez dago jarraipen-ekintzarik oraindik.',
            received_date: 'Received Data',
            responded_at: 'Erantzuna',
            hide_follow_ups: 'Ezkutatu Follow-ups',
            inquiry_types: {
                general: 'Orokorra',
                investigation: 'Ikerketa',
                audit: 'Ikuskaritza',
                complaint: 'Kexa',
                general_audit: 'Ikuskaritza orokorra',
                rights_requests_review: 'Eskubide-eskaeren berrikusketa',
                cross_border_transfer_review: 'Mugaz gaindiko transferentzien berrikusketa',
                vendor_governance_review: 'Hornitzaileen gobernantza berrikusketa',
                consent_management_review: 'Adostasun-kudeaketaren berrikusketa',
                data_mapping_ropa_review: 'Datuen mapaketa / ROPA berrikusketa',
                incident_readiness_review: 'Gertaera-prestatasunaren berrikusketa',
                security_access_control_review: 'Segurtasun eta sarbide-kontrolaren berrikusketa',
                targeted_processing_review: 'Zuzendutako prozesaketaren berrikusketa',
                bank_readiness_pack: 'Banku-prestatasun paketea',
            },
            link_evidence: 'Link Ebidentzia',
            loaded_count: '{0} RIC kasuak kargatu dira',
            to_follow_up: 'jarraipen galdera egiteko',
            overdue: 'Atzeratuta',
            overdue_indicator: 'Erantzuteko epea gainditu da',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Jasota',
                in_triage: 'Triajean',
                evidence_gathering: 'Froga-bilketa',
                response_drafting: 'Erantzun-zirriborroa',
                internal_review: 'Barne-berrikusketa',
                submitted: 'Bidalita',
                follow_up: 'Jarraipena',
                closed: 'Itxita',
            },
        },
        framework_link: {
            title: 'Esparrura lotutako jarraipena',
            link_program: 'Programa esparrura lotu',
            framework_code: 'Esparru-kodea',
            select_framework: 'Aukeratu esparru bat...',
            select_program: 'Aukeratu programa bat...',
            readiness_score: 'Prestutasun-puntuazioa',
            no_links: 'Ez da esparrura lotutako programarik aurkitu',
            seed_defaults: 'Txantiloi lehenetsiak sortu',
            program_id: 'Programaren IDa',
            program: 'Programa',
        },
        authority_readiness: {
            title: 'Agintaritza-eskaera prestutasuna',
            create_entry: 'Prestutasun-sarrera berria',
            select_framework: 'Aukeratu esparru bat...',
            framework_code: 'Esparrua',
            readiness_status: 'Prestutasun-egoera',
            readiness_package: 'Prestutasun-paketea',
            run_drill: 'Prestutasun-simulakroa exekutatu',
            drill_success: 'Prestutasun-simulakroa erregistratu da',
            last_drill: 'Azken simulakroa',
            never_drilled: 'Inoiz ere ez',
            no_readiness: 'Ez da agintaritza-prestutasun daturik aurkitu',
            entry_created: 'Prestutasun-sarrera sortua',
            ready: 'Prest',
            partial: 'Partziala',
            not_ready: 'Ez dago prest',
            items: 'items',
        },
        change_review: {
            title: 'Araudiaren aldaketa-berrikusketa',
            new_review: 'Aldaketa-berrikusketa berria',
            change_id: 'Aldaketa-ID',
            select_change: 'Aukeratu aldaketa bat...',
            select_framework: 'Aukeratu esparru bat...',
            impact_assessment: 'Inpaktu-ebaluazioa',
            decision: 'Erabakia',
            no_action: 'Ekintzarik ez',
            internal_update: 'Barne eguneraketa',
            authority_filing: 'Agintaritza-aurkezpena',
            no_reviews: 'Ez da aldaketa-berrikusketarik aurkitu',
            auto_cycle: 'Berrikusketa-ziklo automatikoa',
            cadence: 'Berrikusketa-maiztasuna',
            framework: 'Esparrua',
        },
        export_csv: 'Esportatu CSV',
        exporting: 'Esportatzen…',
        export_success: 'CSV ongi esportatu da.',
        export_failed: 'CSV esportazioa huts egin du.',
        export_attestation_pack: 'Esportatu aitorpen paketea',
        staleness: {
            badge_label: '{0} elementu zaharkituak',
            days_since: 'Azken eguneratzetik {0} egun',
            detail_title: 'Elementu zaharkituak',
            accept_justification: 'Onartu Justifikazioarekin',
            justification_label: 'Justifikazioa',
            no_stale: 'Ez da aurkitu zaharkitutako elementurik.',
            threshold_label: 'Gelditasun atalasea (egunak)',
            threshold_min: 'Gutxienez 30 egun',
            threshold_max: 'Gehienez 730 egun',
            threshold_saved: 'Zaharkitasun-atalasa eguneratu da.',
        },
        csv_columns: {
            name: 'Izena',
            status: 'Egoera',
            owner: 'Jabea',
            created_at: 'urtean sortua',
            updated_at: 'Eguneratu orduko',
            scope: 'Eremua',
            frameworks: 'Esparruak',
            period_label: 'Aldiaren Etiketa',
            start_date: 'Hasiera data',
            end_date: 'Amaiera-data',
            task_count: 'Zereginen zenbaketa',
            completion_rate: 'Osatze-tasa',
            title: 'Izenburua',
            assigned_to: 'Nori esleitua',
            due_date: 'Epemuga',
            priority: 'Lehentasuna',
            completed_at: 'Orduz beteta',
            kpi_code: 'KPI kodea',
            kpi_label: 'KPI Etiketa',
            target: 'Helburua',
            actual: 'Benetakoa',
            trend: 'Joera',
            reference: 'Erreferentzia',
            regulator: 'Erregulatzailea',
            inquiry_type: 'Kontsulta mota',
            lead: 'Beruna',
            received_date: 'Jasotako data',
            deadline: 'Epea',
            framework_code: 'Esparru Kodea',
            readiness_status: 'Prest egoera',
            package_count: 'Pakete kopurua',
            change_id: 'Aldatu IDa',
            impact_assessment: 'Eraginaren Ebaluazioa',
            decision: 'Erabakia',
            program: 'Egitaraua',
            readiness_score: 'Presttasun puntuazioa',
            category: 'Kategoria',
            checklist_count: 'Kontrol-zerrenda zenbaketa',
            effort_hours: 'Esfortzua (orduak)',
            version: 'Bertsioa',
            department: 'Saila',
            function_area: 'Funtzio Arloa',
            role: 'Rola',
            assignee: 'Esleipenduna',
            frequency: 'Maiztasuna',
        },
        raci: {
            page_title: 'Gobernantza RACI Matrizea',
            title: 'Gobernantza RACI Matrizea',
            department: 'Saila',
            function_area: 'Funtzio Arloa',
            responsible: 'Arduraduna',
            accountable: 'Erantzule',
            consulted: 'Kontsultatua',
            informed: 'Informatuta',
            assign_role: 'Esleitu RACI rola',
            select_program: 'Hautatu Programa',
            select_program_prompt: 'Hautatu programa bat RACI matrizea ikusteko.',
            no_assignments: 'Ez da RACI lanik aurkitu.',
            role_updated: 'RACI esleipena eguneratu da.',
            role_labels: {
                R: 'Arduraduna',
                A: 'Erantzule',
                C: 'Kontsultatua',
                I: 'Informatuta',
            },
            loaded_count: '{0} RACI zereginak kargatu dira',
            single_accountability_warning: 'Erantzule (A) rol bakarra onartzen da funtzio-eremu bakoitzeko. Erantzule rol berri bat esleitzeak lehendik dagoena ordezkatuko du.',
            cybersec_function_areas: 'Zibersegurtasun Legearen funtzio-eremuak: Gorabeheren Erantzuna, Ebidentzien Preservazioa, Autoritateen Lankidetza, Lokalizazioaren Berrikuspena.',
            ai_law_function_areas: 'AI Legearen funtzio-eremuak: Sistemaren Erregistroa, Inplementazioaren aurretiko Ebaluazioa, Gardentasuna, Gorabeheren kudeaketa.',
            suggested_function_areas: 'Iradokitako funtzio-eremuak',
        },
        prior_cycle: {
            link_label: 'Aurreko Ziklorako esteka',
            select_prior: 'Hautatu aurreko zikloa…',
            linked: 'Urtez Urte',
            no_prior: 'Ez dago aurreko ziklorik erabilgarri.',
            linked_success: 'Aurreko zikloa lotuta.',
        },
        digest_table: {
            module: 'Modulua',
            entity_type: 'Entitate Mota',
            change_type: 'Aldatu mota',
            description: 'Deskribapena',
            changed_at: 'Aldatua',
        },
        view_related_ric: 'Ikusi erlazionatutako RIC kasuak',
        linked_program: 'Lotutako Programa',
        nav_raci: 'RACI Matrizea',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Erantzun zirriborroa',
            drafting: 'Zirriborroa egiten…',
            results_heading: 'Erantzun zirriborroak ({count})',
            tokens_label: '≈ {count} token',
            counsel_label: 'Beharrezkoa da aholkulariaren berrikuspena:',
            counsel_default: 'Gutxienez erantzun batek aholkularien berrikuspena behar du bidali aurretik.',
            requires_counsel_label: 'Abokatuaren berrikuspena eskatzen du.',
            accept_label: 'Onartu zirriborroa',
            reject_label: 'Zirriborroa baztertu',
            error_generic: 'Ezin izan da erantzuna idatzi. Saiatu berriro edo hobetu kontsultaren testua.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Kalkulatu arau-hausteen jakinarazpen-kronologia',
            computing: 'Informatika…',
            results_heading: 'Urraketak jakinarazteko epeak ({count})',
            tokens_label: '≈ {count} token',
            handoff_label: 'Forsetik iradokitzen du:',
            empty_label: 'Instalatutako esparruek ez dute jakinarazpen-betebeharrik adierazten gertakari honetarako.',
            mandatory_label: 'Derrigorrezko eremuak:',
            draft_label: 'Jakinarazpenaren zirriborroa',
            remaining_label: '{count}h falta dira',
            overdue_label: 'BERAZKOA {count}h',
            acknowledge_label: 'Aitortu kronologia',
            acknowledging: 'Aitortzen…',
            error_generic: 'Ezin izan da arau-haustearen denbora-lerroa kalkulatu. Saiatu berriro.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmed Urraketa',
            no: 'Not a Urraketa',
            uncertain: 'Zain',
        },
        errors: {
            fetch: 'Huts egin du: load incidents. Saiatu berriro.',
            save: 'Huts egin du: save incident. Saiatu berriro.',
            fetch_failed: 'Huts egin du: fetch incidents. Saiatu berriro.',
            save_failed: 'Huts egin du: save incident. Saiatu berriro.',
            transition_failed: 'Huts egin du: transition incident. Saiatu berriro.',
            close_failed: 'Huts egin du: close incident. Saiatu berriro.',
            fetch_timeline_failed: 'Huts egin du: fetch timeline. Saiatu berriro.',
            fetch_decisions_failed: 'Huts egin du: fetch decisions. Saiatu berriro.',
            triage_failed: 'Huts egin du: update triage. Saiatu berriro.',
            impact_update_failed: 'Huts egin du: update impact assessment. Saiatu berriro.',
            breach_update_failed: 'Huts egin du: update breach assessment. Saiatu berriro.',
            create_timeline_failed: 'Huts egin du: create timeline sarrera. Saiatu berriro.',
            create_decision_failed: 'Huts egin du: create decision. Saiatu berriro.',
            notification_update_failed: 'Huts egin du: update notification. Saiatu berriro.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Huts egin du: fetch tasks. Saiatu berriro.',
            create_task_failed: 'Huts egin du: create task. Saiatu berriro.',
            update_task_status_failed: 'Huts egin du: zereginaren egoera eguneratzea. Saiatu berriro.',
            update_checklist_failed: 'Huts egin du: update checklist. Saiatu berriro.',
            assign_task_failed: 'Huts egin du: assign task. Saiatu berriro.',
            escalate_task_failed: 'Huts egin du: escalate task. Saiatu berriro.',
            fetch_templates_failed: 'Huts egin du: fetch txantiloiak. Saiatu berriro.',
            permission_denied: 'Ez duzu atal honetara sartzeko baimenik. Jarri harremanetan zure administratzailearekin.',
        },
        harm_likelihood: {
            unlikely: 'Ez da litekeena',
            possible: 'Posiblea',
            likely: 'Litekeena',
            very_likely: 'Oso litekeena',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'Eposta',
            phone: 'Telefonoa',
            internal_system: 'Barnekoa Sistema',
            third_party: 'Hirugarrena',
            automated_detection: 'Automatizatua Detection',
        },
        task_transition_labels: {
            start: 'Hasi',
            complete: 'Osatu',
            block: 'Blokeatu',
            defer: 'Atzeratu',
            cancel: 'Utzi',
            unblock: 'Desblokeatu',
        },
        transition_labels: {
            start: 'Hasi',
            complete: 'Osatu',
            block: 'Blokeatu',
            defer: 'Atzeratu',
        },
        nav: {
            all: 'Gertakari guztiak',
            tasks: 'Erantzun-zereginak',
            reports: 'Txostenak',
            breach_notifications: 'Urraketa-jakinarazpenak',
            supplements: 'Osagarriak',
            remediation: 'Konponketa',
            authority_requests: 'Agintaritza-eskaerak',
            disclosure_register: 'Dibulgazio-erregistroa',
        },
        list: {
            page_title: 'Gorabeherak',
            title: 'Gertakariak',
            new: 'Gertakari berria',
            edit: 'Editatu gertakaria',
            incident_title: 'Izenburua',
            severity: 'Larritasuna',
            reported_at: 'Jakinarazpen-data',
            reported_by: 'Jakinarazlea',
            assigned_to: 'Esleitua',
            view_detail: 'Ikusi xehetasunak',
            reference: 'Erreferentzia',
            lead: 'Arduraduna',
            reported_via: 'Bidez jakinarazia',
            discovery_date: 'Discovery Data',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Ezkutatu Xehetasunak',
            close_incident: 'Itxi Gertaera',
            close_justification_prompt: 'Please provide a justifikazio for closing this incident.',
            justification: 'Justifikazio',
            lead_id: 'Arduradun IDa',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Kontaktua',
            category: 'Kategoria',
            not_assessed: 'Ebaluatu gabe',
        },
        detail: {
            page_title: 'Gorabeheraren xehetasuna',
            title: 'Gertakariaren xehetasuna',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Ikuspegi orokorra',
                triage: 'Triazioa',
                impact: 'Eraginaren ebaluazioa',
                breach: 'Urraketaren ebaluazioa',
                timeline: 'Kronologia',
                decisions: 'Erabakiak',
                evidence: 'Frogak',
                audit_trail: 'Ikuskaritza Pista',
            },
            evidence_upload_label: 'Erantsi froga fitxategia',
            evidence_uploaded: 'Froga igota',
            evidence_deleted: 'Froga ezabatuta',
            evidence_delete_error: 'Ezin izan da froga ezabatu',
            evidence_uploaded_at: 'Igota',
            evidence_empty: 'Gertakari honi ez zaio froga fitxategirik erantsi.',
            evidence_size: 'Tamaina',
            triage_notes: 'Triazio-oharrak',
            initial_classification: 'Hasierako sailkapena',
            individuals_affected: 'Kaltetutako pertsona-kopurua',
            data_categories: 'Datu-kategoria inplikatuak',
            systems_compromised: 'Konprometitutako sistemak',
            geographic_scope: 'Eremu geografikoa',
            breach_determination: 'Urraketaren determinazioa',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Jakinarazpena beharrezkoa',
            notification_deadline: 'Jakinarazpen-epemuga',
            pdpc_reportable: 'Kontrol-agintaritzari jakinaraztekoa',
            add_timeline: 'Gehitu kronologia-sarrera',
            add_decision: 'Gehitu erabakia',
            not_found: 'Gertaera not found.',
            team_members: 'Taldekideak',
            checklist: 'Egiaztapen-zerrenda',
            completed: 'Osatuta',
            impact_severity_score: 'Calculated Eragin Larritasun Puntuazio',
            records_affected: 'Erregistroak Affected',
            potential_consequences: 'Ondorio posibleak',
            harm_likelihood: 'Harm Probabilitate',
            legal_basis: 'Oinarri legala',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdikzioak',
            jurisdictions_comma: 'Jurisdikzioak (komaz bananduta)',
            jurisdiction: 'Jurisdikzioa',
            authority_status: 'Authority Egoera',
            subject_status: 'Subject Egoera',
            authority_deadline: 'Agintaritza-epemuga',
            subject_deadline: 'Subjektuaren epemuga',
            mark_authority_done: 'Markatu agintaritza eginda',
            mark_subject_done: 'Markatu subjektua eginda',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Ebaluatu gabe',
            event_type: 'Gertaera mota',
            timestamp: 'Denbora-zigilua',
            actor: 'Aktorea',
            no_timeline_events: 'Ez dago denbora-lerroko gertaerarik oraindik.',
            decision_type: 'Erabaki Mota',
            decision: 'Erabaki',
            rationale: 'Arrazoibidea',
            decision_made: 'Erabaki Made',
            no_decisions: 'Ez da erabakirik erregistratu oraindik.',
            no_audit_events: 'Oraindik ez da erregistratu auditoria-gertaerarik.',
            export_pdf: 'Esportatu PDF',
            pdf_title: 'Gertaera-laburpen Txostena',
            description: 'Deskribapena',
            linked_records: 'Lotutako Erregistroak',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Gorabeheren Erantzuteko Zereginak',
            title: 'Gertakariei erantzuteko zereginak',
            new: 'Zeregin berria',
            edit: 'Editatu zeregina',
            task_type: 'Zeregin mota',
            assignee: 'Esleitua',
            due_date: 'Epemuga',
            dependencies: 'Mendekotasunak',
            mark_complete: 'Markatu osatuta',
            templates: 'Zereginen txantiloiak',
            select_incident: 'Hautatu gertakaria',
            all_statuses: 'Egoera guztiak',
            all_categories: 'Kategoria guztiak',
            new_response_task: 'Erantzun-zeregin berria',
            category: 'Kategoria',
            priority: 'Lehentasuna',
            depends_on: 'Honen mende (komaz bereizitako zeregin IDak)',
            checklist_items: 'Kontrol-zerrendako elementuak',
            remove: 'Kendu',
            add: 'Gehitu',
            new_checklist_item: 'Kontrol-zerrendako elementu berria',
            no_templates: 'Ez dago txantiloirik erabilgarri.',
            assign_task: 'Esleitu zeregina',
            assign_to: 'Esleitu honi',
            assign: 'Esleitu',
            select_incident_prompt: 'Mesedez, hautatu gertakari bat erantzun-zereginak ikusteko.',
            checklist: 'Kontrol-zerrenda',
            hide_checklist: 'Ezkutatu kontrol-zerrenda',
            escalate: 'Eskalatu',
            escalations: 'Eskalazio-ekintzak',
            no_checklist_items: 'Ez dago kontrol-zerrendako elementurik.',
        },
        reports: {
            page_title: 'Gorabeheren Txostenak',
            title: 'Gertakarien txostenak',
            summary: 'Laburpen-estatistikak',
            by_severity: 'Larritasunaren arabera',
            by_status: 'Egoeraren arabera',
            recent: 'Azken gertakariak',
            total_incidents: 'Gertakari guztira',
            open: 'Irekita',
            closed: 'Itxita',
            export_csv: 'Esportatu CSV',
            export_pdf: 'Esportatu PDF',
            export_success: 'Txostena behar bezala esportatu da.',
            export_error: 'Txostena esportatzeak huts egin du.',
            reference: 'Erreferentzia',
            view_report: 'Ikusi txostena',
        },
        statuses: {
            reported: 'Jakinarazia',
            triage: 'Triazioa',
            contained: 'Mugatua',
            investigation: 'Ikerketa',
            remediation: 'Konponketa',
            closed: 'Itxita',
            false_positive: 'Positibo faltsua',
        },
        task_statuses: {
            pending: 'Zain',
            in_progress: 'Abian',
            completed: 'Osatua',
            blocked: 'Blokeatua',
            cancelled: 'Bertan behera utzita',
        },
        severity: {
            low: 'Baxua',
            medium: 'Ertaina',
            high: 'Altua',
            critical: 'Kritikoa',
        },
        task_types: {
            containment: 'Mugatzea',
            investigation: 'Ikerketa',
            remediation: 'Konponketa',
            communication: 'Komunikazioa',
            documentation: 'Dokumentazioa',
            notification: 'Jakinarazpena',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Datu Urraketa',
            unauthorized_access: 'Unauthorized Sarbide',
            data_loss: 'Datu Loss',
            system_compromise: 'Sistema Compromise',
            malware: 'Malwarea',
            phishing: 'Phishing-a',
            insider_threat: 'Barne-mehatxua',
            other: 'Beste',
        },
        reported_via_options: {
            email: 'Eposta',
            phone: 'Telefonoa',
            internal_system: 'Barnekoa Sistema',
            third_party: 'Hirugarrena',
            automated_detection: 'Automatizatua Detection',
        },
        harm_likelihood_options: {
            unlikely: 'Ez da litekeena',
            possible: 'Posiblea',
            likely: 'Litekeena',
            very_likely: 'Oso litekeena',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmed Urraketa',
            suspected_breach: 'Suspected Urraketa',
            not_a_breach: 'Not a Urraketa',
            pending: 'Zain',
        },
        timeline_event_types: {
            detection: 'Detekzioa',
            escalation: 'Eskalazio',
            containment: 'Kontentzioa',
            investigation_start: 'Ikerketa Hasi',
            evidence_collected: 'Ebidentzia Collected',
            notification_sent: 'Jakinarazpen Sent',
            remediation_action: 'Konponketa Ekintza',
            status_change: 'Egoera Change',
            communication: 'Komunikazio',
            other: 'Beste',
        },
        decision_types: {
            containment: 'Kontentzioa',
            notification: 'Jakinarazpen',
            remediation: 'Konponketa',
            escalation: 'Eskalazio',
            closure: 'Itxiera',
            other: 'Beste',
        },
        task_transitions: {
            start: 'Hasi',
            complete: 'Osatu',
            block: 'Blokeatu',
            cancel: 'Utzi',
            unblock: 'Desblokeatu',
        },
        announce: {
            loaded: '{count} gorabehera kargatu dira',
            reports_loaded: '{count} gertakarien txosten kargatu dira',
            tasks_loaded: '{count} zeregin kargatu dira',
            created: 'Gertaera arrakastaz sortu da',
            updated: 'Gertaera behar bezala eguneratu da',
            closed: 'Gertaera behar bezala itxi da',
            status_changed: 'Gorabeheraren egoera aldatu da',
            triage_completed: 'Triage arrakastaz osatu da',
            impact_updated: 'Eraginaren ebaluazioa eguneratua',
            breach_updated: 'Arau-hausteen ebaluazioa eguneratu da',
            timeline_added: 'Gehitu da denbora-lerroko gertaera',
            decision_recorded: 'Erabakia erregistratuta',
            notification_updated: 'Jakinarazpenaren egoera eguneratu da',
            task_created: 'Zeregin behar bezala sortu da',
            task_status_changed: 'Zereginaren egoera aldatu da',
            checklist_completed: 'Kontrol zerrendako elementua osatu da',
            checklist_unchecked: 'Kontrol-zerrendako elementua desmarkatuta dago',
            task_assigned: 'Esleitu da zeregina',
            task_escalated: 'Ataza areagotu egin da',
            export_initiated: 'Esportazioa hasi da',
        },
        breach_notifications: {
            page_title: 'Urratze-jakinarazpenak',
            title: 'Urratze-jakinarazpenak',
            new_notification: 'Jakinarazpen berria',
            no_notifications: 'Ez da aurkitu urratze-jakinarazpenik',
            status: 'Egoera',
            submit_to_mps: 'Bidali MPS helbidera',
            decision: 'Erabakia',
            decision_maker: 'Erabakitzailea',
            notify: 'Jakinarazi',
            do_not_notify: 'Ez jakinarazi',
            rationale: 'Arrazoia',
            filing_deadline: 'Aurkezteko epea',
            overdue: 'Epez kanpo',
            due_soon: 'Laster iraungiko',
            on_track: 'Epe barruan',
            legal_review: 'Lege Berrikuspena',
            incident_id: 'Gorabeheraren IDa',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Ebaluazioa',
                decision_to_notify: 'Jakinarazteko erabakia',
                notification_drafted: 'Jakinarazpena idatzita',
                legal_review: 'Lege Berrikuspena',
                submitted_to_authority: 'Agintaritzari aurkeztua',
                supplement_requested: 'Eskatutako gehigarria',
                supplement_submitted: 'Aurkeztutako gehigarria',
                acknowledged: 'Aitortua',
                closed: 'Itxita',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Osagarrien eskaerak',
            title: 'Osagarrien eskaerak',
            new_supplement: 'Gehigarri berria',
            no_supplements: 'Ez da osagarri eskaerarik aurkitu',
            request_details: 'Eskatu xehetasunak',
            response_package: 'Erantzun Paketea',
            submit_response: 'Bidali Erantzuna',
            deadline: 'Epea',
            notification_id: 'Jakinarazpenaren IDa',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Idatzi jakinarazpen ID bat osagarriak ikusteko.',
            supplement_statuses: {
                requested: 'Eskatuta',
                drafting: 'Erredakzioa',
                submitted: 'Aurkeztua',
                accepted: 'Onartua',
                rejected: 'Baztertua',
            },
        },
        remediation: {
            page_title: 'Konponketa Ekintzak',
            title: 'Konponketa Ekintzak',
            new_action: 'Ekintza Berria',
            no_actions: 'Ez da aurkitu zuzentze-ekintzarik',
            description: 'Deskribapena',
            owner: 'Jabea',
            department: 'Departamentua',
            deadline: 'Epea',
            statuses: {
                planned: 'Planifikatua',
                in_progress: 'Eraikitzen',
                completed: 'Osatua',
                verified: 'Egiaztatuta',
            },
            verify: 'Egiaztatu',
            incident_id: 'Gorabeheraren IDa',
            enter_incident_id: 'Idatzi gorabeheraren ID bat konpontzeko ekintzak ikusteko.',
            start: 'Hasi',
            complete_action: 'Osatu',
        },
        authority_requests: {
            page_title: 'Agintaritzaren eskaerak',
            title: 'Agintaritzaren eskaerak',
            new_request: 'Eskaera berria',
            no_requests: 'Ez da aurkitu autoritate eskaerarik',
            authority_name: 'Agintaritzaren izena',
            request_details: 'Eskatu xehetasunak',
            response_package: 'Erantzun Paketea',
            submission_channel: 'Bidalketa kanala',
            submit_response: 'Bidali Erantzuna',
            deadline: 'Epea',
            confirmation: 'Berrespena',
            incident_id_optional: 'Gorabeheraren IDa (aukerakoa)',
            response_context_label: 'Erantzun-testuingurua (bidalketa-dokumentua materializatzeko)',
            response_context_none: '(materializazioa saltatu)',
            response_context_supplement: 'Osagarri-erantzuna',
            response_context_information: 'Informazio-eskariari erantzuna',
            response_context_inspection: 'Ikuskapenari erantzuna',
            materialisation_status_column: 'Bidalketa-dokumentua',
            materialisation_pending: 'Zain',
            materialisation_success: 'Materializatuta',
            materialisation_failed: 'Huts eginda',
            materialisation_retry_button: 'Saiatu berriro',
            ar_statuses: {
                received: 'Jasotakoa',
                verified: 'Egiaztatuta',
                assembling: 'Muntatzea',
                submitted: 'Bidalita',
                closed: 'Itxita',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Lege Berrikuspena',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Kontrol-zerrenda',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Dibulgazio Erregistroa',
            title: 'Dibulgazio Erregistroa',
            new_entry: 'Sarrera Berria',
            no_entries: 'Ez da dibulgazio-erregistroko sarrerarik aurkitu',
            disclosure_type: 'Dibulgazio mota',
            disclosure_date: 'Dibulgazio-data',
            recipient: 'Hartzailea',
            authority_name: 'Agintaritzaren izena',
            channel: 'Kanala',
            content_summary: 'Edukien laburpena',
            incident_id_optional: 'Gorabeheraren IDa (aukerakoa)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Konfigurazio-laguntzailea',
            tours: 'Moduluen bisitak',
            sample_data: 'Adibide-datuak',
        },
        wizard: {
            title: 'Konfigurazio-laguntzailea',
            welcome: 'Ongi etorri AesirX ComplianceOnera',
            welcome_message: 'Konfigura dezagun zure erakundea. Laguntzaile honek hasierako konfigurazioan gidatuko zaitu.',
            setup_complete: 'Konfigurazioa osatuta',
            skipped_message: 'Konfigurazio-laguntzailea saltatu da. Edozein unetan exekutatu dezakezu zure erakundea konfiguratzeko.',
            start_setup: 'Hasi konfigurazioa',
            skip_wizard: 'Saltatu laguntzailea',
            go_to_admin: 'Joan Administratzailera',
            skip_for_now: 'Saltatu oraingoz',
            next_step: 'Hurrengo urratsa',
            previous_step: 'Aurreko urratsa',
            finish_setup: 'Amaitu konfigurazioa',
            rerun: 'Berriz exekutatu konfigurazio-laguntzailea',
            step_label: 'Urratsa {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Erakundearen izena derrigorrezkoa da.',
            validation_admin_email_required: 'Administratzailearen helbide elektronikoa derrigorrezkoa da.',
            validation_admin_email_invalid: 'Mesedez, sartu baliozko helbide elektroniko bat.',
            validation_cmp_endpoint_required: 'CMP amaiera-puntua derrigorrezkoa da CMP gaituta dagoenean.',
            steps: {
                license_activation: 'Lizentzia Aktibatzea',
                org_profile: 'Erakundearen profila',
                module_selection: 'Moduluen hautapena',
                cmp_integration: 'CMP integrazioa',
                notification_preferences: 'Jakinarazpenak',
                review: 'Berrikusi eta berretsi',
            },
            step_descriptions: {
                license_activation: 'Sartu zure lizentzia-gakoa zure paketea aktibatzeko eta zein modulu dauden eskuragarri zehazteko.',
                org_profile: 'Konfiguratu erakundearen izena, jurisdikzioa, sektorea, tamaina eta hizkuntza hobetsia.',
                module_selection: 'Aukeratu zein betetze-modulu aktibatu zure erakundearentzat.',
                cmp_integration: 'Konfiguratu Consent Management Platform integrazioa (aukerakoa).',
                notification_preferences: 'Konfiguratu nola jasotzen dituzun jakinarazpenak betetze-gertaerei buruz.',
                review: 'Berrikusi zure konfigurazioa eta amaitu instalazioa.',
            },
            license_step: {
                validate_button: 'Lizentzia balioztatu',
                skip_warning: 'Modulurako sarbidea paketeen maila lehenetsira mugatuko da. Lizentzia bat geroago aktibatu dezakezu Plataforma > Lizentzia ezarpenak atalean.',
            },
            module_descriptions: {
                rights_requests: 'Kudeatu datu pertsonalen sarbide eta ezabatze eskaerak.',
                data_mapping: 'Mapatu sistemak, datu-fluxuak eta tratamendu-jarduerak.',
                governance: 'Gainbegiratu hornitzaileak, onarpenak, desbideratzeak eta frogen erregistroak.',
                consent: 'Kudeatu adostasun-politikak eta helburu-katalogoak.',
                consent_properties: 'Kudeatu adostasuna jabetza digital anitzetan.',
                assessments: 'Egin inpaktu-ebaluazioak eta mugaz gaindiko datu-transferentziak.',
                classification: 'Sailkatu eta etiketatu datu-aktiboak bizi-zikloaren jarraipenarekin.',
                monitoring: 'Programatutako betetze-berrikuspenak eta KPI jarraipena.',
                incidents: 'Jakinarazi eta kudeatu segurtasun eta pribatutasun gorabeherak.',
                connectors: 'Konektatu kanpoko sistemetara datu-truke automatizaturako.',
                vendor_risk: 'Puntuatu hornitzaileak, jarraitu kontratuak eta kudeatu behar bezalako ardura.',
                deletion_orchestration: 'Koordinatu datuen ezabatzea sistemen eta hornitzaileen artean.',
                discovery: 'Aurkitu eta sailkatu datuak zure azpiegitura osoan.',
                incident_ops: 'Gertaeren playbook aurreratuak, SLA jarraipena eta eskalazio.',
                program_governance: 'Heldutasun-ebaluazioak, zuzendaritza-txostenak eta ikuskaritza-prest.',
                access_accountability: 'Monitorizatu erabiltzaile-sarbidea, saioak eta justifikazio-lan-fluxuak.',
                localization: 'Merkatuko betetze-paketeak eta erregulatze-esparruak.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Bazkide eta birsaltzaileen lan-eremua.',
                privacy_scanner: 'Pribatutasun-monitorizazioa denbora errealean jabetza digitaletarako.',
                onboarding: 'Konfigurazio-laguntzailea, bisita gidatuak eta lagin-datuen kudeaketa.',
                compliance_forms: 'Betetze-txantiloi interaktiboak eta dokumentu-sortzea.',
                audit_trail: 'Ikuskaritza-erregistro aldaezin zabala eta gordetzea.',
                organizations: 'Erakunde anitz eta talde-kudeaketa.',
                task_management: 'Zeregin-planifikazio bateratua, lan-fluxu motorra eta egutegi-ikuspegiak.',
            },
            module_categories: {
                data_privacy_rights: 'Datuen pribatutasuna eta eskubideak',
                vendor_third_party: 'Saltzaileen eta hirugarrenen kudeaketa',
                risk_assessment: 'Arriskua eta Ebaluazioa',
                operations_automation: 'Eragiketak eta Automatizazioa',
                platform_admin: 'Plataforma eta Administrazioa',
            },
            package_tier_label: 'Zure Paketea',
            package_module_unavailable: 'Ez dago zure paketean sartuta',
            select_all: 'Hautatu Guztiak',
            deselect_all: 'Deshautatu Guztiak',
            load_sample_data: 'Kargatu lagin-datuak konfiguratu ondoren',
            load_sample_data_description: 'Instalatu demo-datu errealistak plataforma esploratzeko. Geroago ezaba daiteke Hasiera > Lagin-datuak atalean.',
            language_label: 'Hizkuntza Hobetsia',
            language_description: 'Aukeratu zure erakundearen hizkuntza lehenetsia.',
            admin_account_title: 'Administratzaile Kontua',
            admin_account_created: 'Administratzaile kontua sortua',
            admin_full_name: 'Izen osoa',
            admin_email: 'Helbide elektronikoa',
            admin_password: 'Pasahitza',
            admin_confirm_password: 'Berretsi pasahitza',
            admin_show_password: 'Erakutsi pasahitza',
            admin_hide_password: 'Ezkutatu pasahitza',
            admin_name_placeholder: 'adib. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Gutxienez 8 karaktere',
            admin_password_confirm_placeholder: 'Sartu pasahitza berriro',
            validation_admin_name_required: 'Administratzailearen izena beharrezkoa da',
            validation_admin_password_required: 'Pasahitza beharrezkoa da',
            validation_admin_password_min_length: 'Pasahitzak gutxienez 8 karaktere izan behar ditu',
            validation_passwords_mismatch: 'Pasahitzak ez datoz bat',
            notification_email_enabled: 'Gaitu Posta Elektronikoko Jakinarazpenak',
            notification_email_description: 'Jaso posta elektronikoko jakinarazpenak zereginen esleipenei, SLA-haustei eta betetze-gertaerei buruz.',
            notification_digest_frequency: 'Laburpen-maiztasuna',
            notification_digest_daily: 'Egunero',
            notification_digest_weekly: 'Astero',
            notification_digest_none: 'Laburpenik gabe',
            notification_in_app_enabled: 'Gaitu Plataforma-jakinarazpenak',
            notification_in_app_description: 'Erakutsi jakinarazpen-bereizgarria eta -tiradera denbora errealeko alertetarako.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EB/EEE',
                global: 'Globala',
            },
            languages: {
                en: 'ingelesa',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Frantsesa',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'portugesa',
            },
            industries: {
                financial_services: 'Finantza-zerbitzuak',
                healthcare: 'Osasungintza',
                technology: 'Teknologia',
                telecommunications: 'Telekomunikazioak',
                government: 'Gobernua',
                education: 'Hezkuntza',
                manufacturing: 'Manufaktura',
                retail: 'Txikizkako merkataritza',
                other: 'Beste bat',
            },
            placeholders: {
                org_name: 'adibidez, ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'erabiltzailea1@enpresa.vn, erabiltzailea2@enpresa.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Erakundearen izena',
                jurisdiction: 'Jurisdikzioa',
                industry: 'Industria',
                org_size: 'Erakundearen tamaina',
                language: 'Hizkuntza Hobetsia',
                admin_email: 'Administratzailearen helbide elektronikoa',
                team_emails: 'Taldekideen helbide elektronikoak (komaz bananduta)',
                enable_cmp: 'Gaitu CMP integrazioa',
                cmp_endpoint: 'CMP API amaiera-puntua',
                email_notifications: 'Posta Elektronikoko Jakinarazpenak',
                in_app_notifications: 'Plataforma-jakinarazpenak',
                digest_frequency: 'Laburpen-maiztasuna',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Moduluen harrera-bisitak',
            start: 'Hasi bisita',
            continue_tour: 'Jarraitu bisita',
            replay: 'Erreproduzitu bisita',
            exit: 'Irten bisitatik',
            skip: 'Saltatu bisita',
            next: 'Hurrengoa',
            previous: 'Aurrekoa',
            finish: 'Amaitu bisita',
            step_of: '{current}. urratsa {total}(e)tik',
            info: 'Egin modulu bakoitzaren bisita gidatuak funtsezko ezaugarri eta lan-fluxuak ezagutzeko. Bisitak edozein unetan erreproduzitu daitezke.',
            autoplay: 'Erreprodukzio automatikoa',
            interactive: 'Interaktiboa',
            play_all: 'Guztiak jolastu',
            search_tours: 'Bilatu bisitak...',
            duration: 'Iraupena',
            steps_label: '{count} urrats',
            mode: 'Modua',
            pause: 'Pausa',
            resume: 'Curriculuma',
            speed: 'Abiadura',
            restart: 'Berrabiarazi',
            reset: 'Berrezarri',
            reset_success: 'Biraren aurrerapena berrezarri da.',
            category_core_privacy: 'Oinarrizko pribatutasuna',
            category_governance: 'Gobernantza',
            category_operations: 'Eragiketak',
            category_platform: 'Plataforma',
            category_advanced: 'Aurreratua',
            category_data_privacy_rights: 'Datuen pribatutasuna eta eskubideak',
            category_vendor_third_party: 'Saltzaileen eta hirugarrenen kudeaketa',
            category_risk_assessment: 'Arriskua eta Ebaluazioa',
            category_operations_automation: 'Eragiketak eta Automatizazioa',
            category_platform_admin: 'Plataforma eta Administrazioa',
            steps_count: '{count} urrats',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Adibide-datuen kudeaketa',
            select_modules: 'Hautatu moduluak',
            current_status: 'Uneko egoera',
            operation_history: 'Eragiketa-historia',
            load: 'Kargatu adibide-datuak',
            clear: 'Garbitu adibide-datuak',
            processing: 'Prozesatzen...',
            loaded: 'Kargatua',
            not_loaded: 'Kargatu gabe',
            records: 'Adibide-erregistroak',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Ziur zaude hautatutako moduluetako adibide-datu guztiak garbitu nahi dituzula?',
            confirm_clear_title: 'Adibide-datuak ezabatu',
            confirm_load_title: 'Kargatu lagin-datuak',
            confirm_load: 'Ziur zaude hautatutako moduluetarako lagin-datuak kargatu nahi dituzula?',
            action_load: 'Kargatu',
            action_clear: 'Ezabatu',
            info: 'Kargatu datu errealisten demo-datuak prestakuntza eta erakustaldietarako. Adibide-datuak etiketatuta daude eta modu seguruan kendu daitezke benetako datuei eragin gabe.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Laguntza-zentroa',
            search: 'Bilatu',
            search_placeholder: 'Bilatu laguntza-artikuluak...',
            clear: 'Garbitu',
            all_categories: 'Kategoria guztiak',
            all_modules: 'Modulu Guztiak',
            back: 'Itzuli laguntza-zentrora',
            callout_important: 'Garrantzitsua',
            callout_tip: 'Aholkua',
            no_results: 'Ez da artikulurik aurkitu zure bilaketarekin bat datorrenik.',
            no_articles: 'Ez dago laguntza-artikulurik erabilgarri oraindik. Artikuluak hemen agertuko dira edukia gehitzen den heinean.',
            results_for: '{count} emaitza "{query}" bilaketarako',
            quick_links: {
                getting_started: 'Hasteko',
                getting_started_desc: 'Instalazio, konfigurazio eta hasiera azkarreko gida.',
                user_guide: 'Erabiltzaile-gida',
                user_guide_desc: 'Modulu bakoitzaren erabilera-dokumentazioa.',
                admin_guide: 'Administratzaile-gida',
                admin_guide_desc: 'Konfigurazioa, mantentze-lanak eta arazoen konponketa.',
                faq: 'Ohiko galderak',
                faq_desc: 'Maiz egindako galderak eta erantzunak.',
            },
            search_articles_aria: 'Bilatu laguntza-artikuluak',
            article_metadata_aria: 'Artikuluaren metadatuak',
            faq_editor_title: 'Laguntza-zentroko ohiko galderen editorea',
            faq_editor_description: 'Gehitu, editatu eta kendu galdera-erantzun bikoteak laguntza-zentroko edozein artikulutan. Editorea ez dago erabilgarri oraindik; helbide hau harentzat gordeta dago.',
            faq_editor_empty: 'Ohiko galderen editorea ez dago erabilgarri oraindik. Bitartean, eskatu zure administratzaileari ohiko galderen atalak gehitzeko laguntza-zentroko artikulu batean.',
            articles_list_aria: 'Laguntza-artikuluak',
            category_nav_aria: 'Kategoria-nabigazioa',
            quick_links_aria: 'Laguntza-kategorietarako esteka azkarrak',
            returned_to_list: 'Artikulu-zerrendara itzulia',
            opening_article: 'Artikulua irekitzen: {title}',
            read_article_aria: 'Irakurri artikulua: {title}',
            pagination_nav_aria: 'Artikuluen paginazioa',
            error_unavailable: 'Laguntza-zentroa ez dago erabilgarri une honetan. Saiatu berriro geroago.',
            retry: 'Saiatu berriro',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'adib. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Gorde',
        cancel: 'Utzi',
        system_actor: 'Sistema',
        delete: 'Ezabatu',
        edit: 'Editatu',
        create: 'Sortu',
        update: 'Eguneratu',
        loading: 'Kargatzen...',
        error: 'Errore bat gertatu da',
        success: 'Arrakasta',
        logout: 'Saioa itxi',
        actions: 'Ekintzak',
        retrying: 'Berriro saiatzen…',
        permission_denied: 'Ez duzu orri honetarako sarbiderik.',
        status: 'Egoera',
        description: 'Deskribapena',
        name: 'Izena',
        created_at: 'Sortze-data',
        updated_at: 'Eguneratze-data',
        yes: 'Bai',
        no: 'Ez',
        confirm: 'Berretsi',
        confirm_dialog_title: 'Confirm action',
        back: 'Atzera',
        next: 'Hurrengoa',
        previous: 'Aurrekoa',
        search: 'Bilatu',
        filter: 'Iragazi',
        page_x_of_y: '{page}/{total} orrialdea',
        pagination_label: 'Paginazioa',
        view_mode_label: 'Ikuste modua',
        export: 'Esportatu',
        exporting: 'Esportatzen…',
        sidebar_navigation: 'Administratzailearen alboko barraren nabigazioa',
        items_count: '{count} elementu',
        items_count_one: '{count} elementu',
        export_success: 'Dokumentua behar bezala sortu da.',
        generate_pdf: 'Sortu PDF',
        import: 'Inportatu',
        download: 'Deskargatu',
        upload: 'Igo',
        view: 'Ikusi',
        close: 'Itxi',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Bidali',
        saved: 'Gorde da',
        saving: 'Gordetzen...',
        submitting: 'Bidaltzen...',
        created: 'Sortua',
        all: 'Guztiak',
        none: 'Bat ere ez',
        select: 'Hautatu',
        select_user: 'Hautatu erabiltzailea...',
        custom_input: 'Eskuz idatzi',
        required: 'Derrigorrezkoa',
        optional: 'Aukerakoa',
        notes: 'Oharrak',
        tags: 'Etiketak',
        archive: 'Artxibatu',
        clone: 'Klonatu',
        refresh: 'Freskatu',
        retry: 'Saiatu berriro',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Berrezarri',
        add: 'Gehitu',
        remove: 'Kendu',
        removing: 'Kentzen…',
        attachment: 'Eranskina',
        upload_file: 'Kargatu fitxategia',
        upload_file_required: 'Kargatu fitxategia (beharrezkoa)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Arrastatu eta jaregin edo egin klik arakatzeko',
        upload_max_size: 'Gehienezko tamaina: {size} MB',
        upload_accepted_types: 'Onartuak: {types}',
        upload_in_progress: '{name} igotzen...',
        upload_size_exceeded: 'Fitxategiaren tamainak gainditzen du onartutako gehienezko tamaina: {size} MB.',
        upload_type_not_accepted: 'Fitxategi mota ez da onartzen. Onartutako motak: {types}',
        upload_succeeded: '"{name}" fitxategia ondo igo da.',
        upload_failed: 'Ezin izan da fitxategia igo.',
        select_entity_type_first: 'Hautatu entitate mota lehenik',
        failed_to_remove_file: 'Fitxategia ezin izan da kendu.',
        entity_type_label: 'Entitate mota',
        entity_label: 'Entitatea',
        entity_reference: 'Entitate erreferentzia',
        failed_to_load_entities: 'Entitateek ezin dira kargatu',
        enable: 'Gaitu',
        disable: 'Desgaitu',
        no_data: 'Ez da daturik aurkitu',
        confirm_delete: 'Ziur zaude elementu hau ezabatu nahi duzula?',
        confirm_action: 'Ziur zaude jarraitu nahi duzula?',
        save_success: 'Behar bezala gordeta',
        save_error: 'Ezin izan da gorde',
        delete_success: 'Behar bezala ezabatuta',
        delete_error: 'Ezin izan da ezabatu',
        hour: 'ordu',
        hours: 'orduak',
        load_error: 'Ezin izan dira datuak kargatu',
        operation_error: 'Eragiketak huts egin du',
        something_went_wrong: 'Zerbait gaizki joan da',
        go_home: 'Joan kontrol-panelera',
        access_denied: 'Sarbidea ukatua',
        no_permission: 'Ez duzu orri honetara sartzeko baimenik.',
        no_permission_title: 'Baimen nahikoa ez',
        no_permission_message: 'Ez duzu baimenik orri honetara sartzeko.',
        unknown: 'Ezezaguna',
        back_to_dashboard: 'Itzuli kontrol-panelera',
        id: 'IDa',
        type: 'Mota',
        date: 'Data',
        created_by: 'Sortzailea',
        updated_by: 'Eguneratzailea',
        page_of: '{page}. orria {total}(e)tik',
        showing_items: '{count} elementu erakusten',
        items_per_page: 'Elementuak orrialdeko',
        no_results: 'Ez dago erregistrorik zure iragazkiekin bat datorrik',
        no_items: 'Ez da elementurik aurkitu',
        sort_by: 'Ordenatu honen arabera',
        ascending: 'Goranzkoa',
        descending: 'Beheranzkoa',
        all_statuses: 'Egoera guztiak',
        all_types: 'Mota guztiak',
        all_categories: 'Kategoria guztiak',
        all_severities: 'Larritasun guztiak',
        date_range: 'Data-tartea',
        from_date: 'Hasiera',
        to_date: 'Amaiera',
        clear_filters: 'Garbitu iragazkiak',
        apply_filters: 'Aplikatu iragazkiak',
        save_changes: 'Gorde aldaketak',
        discard_changes: 'Baztertu aldaketak',
        confirm_discard: 'Gorde gabeko aldaketak dituzu. Baztertu nahi dituzu?',
        copy: 'Kopiatu',
        duplicate: 'Bikoiztu',
        move: 'Mugitu',
        assign: 'Esleitu',
        unassign: 'Esleipena kendu',
        approve: 'Onartu',
        reject: 'Baztertu',
        deleting: 'Ezabatzen...',
        processing: 'Prozesatzen...',
        loading_page: 'Orria kargatzen...',
        operation_success: 'Eragiketa behar bezala osatu da',
        operation_failed: 'Eragiketa huts egin du. Mesedez, saiatu berriro.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Sare-errorea. Egiaztatu zure konexioa.',
        session_expired: 'Saioa iraungi da. Mesedez, hasi saioa berriro.',
        test: 'Probatu',
        pause: 'Pausatu',
        resume: 'Berrekin',
        activate: 'Aktibatu',
        last_sync: 'Azken sinkronizazioa',
        health: 'Osasuna',
        email: 'Helbide elektronikoa',
        role: 'Rola',
        title: 'Izenburua',
        owner: 'Jabea',
        priority: 'Lehentasuna',
        category: 'Kategoria',
        details: 'Xehetasunak',
        overview: 'Ikuspegi orokorra',
        configuration: 'Konfigurazioa',
        history: 'Historia',
        new_item: 'Berria',
        edit_item: 'Editatu',
        select_option: 'Hautatu...',
        total: 'Guztira',
        active: 'Aktibo',
        inactive: 'Inaktibo',
        enabled: 'Gaitua',
        disabled: 'Desgaitua',
        last_login: 'Azken saio-hasiera',
        never: 'Inoiz ez',
        password_changed: 'Pasahitza aldatu da. Saioa itxiko da.',
        password_change_failed: 'Ezin izan da pasahitza aldatu',
        passwords_mismatch: 'Pasahitzak ez datoz bat',
        install: 'Instalatu',
        install_named: 'Instalatu {name}',
        uninstall: 'Desinstalatu',
        installed: 'Instalatua',
        available: 'Erabilgarri',
        catalog: 'Katalogoa',
        published: 'Argitaratua',
        version: 'Bertsioa',
        market: 'Merkatua',
        language: 'Hizkuntza',
        framework: 'Esparrua',
        template: 'Txantiloia',
        connector_type: 'Konektore mota',
        polling_interval: 'Kontsulta-tartea (minutuak)',
        new_instance: 'Instantzia berria',
        change_password: 'Aldatu pasahitza',
        current_password: 'Uneko pasahitza',
        new_password: 'Pasahitz berria',
        confirm_password: 'Berretsi pasahitza',
        comments: 'Iruzkinak',
        completed: 'Osatuta',
        contact: 'Kontaktua',
        hide: 'Ezkutatu',
        hide_details: 'Ezkutatu Xehetasunak',
        label: 'Etiketa',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Erakusten',
        page: 'Orrialdea',
        preview: 'Aurrebista',
        print: 'Inprimatu',
        show: 'Erakutsi',
        status_active: 'Aktibo',
        status_completed: 'Osatuta',
        status_draft: 'Zirriborro',
        status_in_progress: 'Martxan',
        status_pending: 'Zain',
        view_details: 'Ikusi xehetasunak',
        all_priorities: 'Lehentasun guztiak',
        pipeline: 'Kanalizazio',
        all_requests: 'Eskaera guztiak',
        schedule: 'Ordutegi',
        deploy: 'Zabaldu',
        rollback: 'Itzultzea',
        advance_stage: 'Aurreratu fasea',
        submit_for_approval: 'Bidali for Onartze',
        approval_log: 'Onartze-erregistroa',
        approver: 'Onesleak',
        decision: 'Erabaki',
        decided_at: 'Erabakitua',
        rollout_stages: 'Rollout Faseak',
        approval_comment: 'Onartze-iruzkina',
        optional_comment: 'Hautazkoa comment...',
        impact_summary: 'Eragin Laburpena',
        target_property_ids: 'Helburu Jabetza IDs (comma-separated)',
        policy_id: 'Politika ID',
        target_version: 'Helburu Bertsio',
        target_version_placeholder: 'adib. 2.0.0',
        scheduled_label: 'Programatua',
        scheduled_start: 'Programatua Hasi',
        scheduled_end: 'Programatua End',
        properties_count: '{count} jabetza',
        results: 'Emaitzak',
        comparison_results: 'Konparaketa-emaitzak',
        past_comparison_reports: 'Past Comparison Txostenak',
        scanning: 'Eskaneatzen...',
        run_inconsistency_scan: 'Exekutatu Inconsistency Eskaneatu',
        property_ids_label: 'Jabetza IDs (comma-separated, min 2)',
        comparison_type: 'Comparison Mota',
        golden_template_id: 'Golden Txantiloi ID (optional)',
        leave_blank_compare: 'Utzi hutsik jabetzak elkarren artean konparatzeko',
        add_notes_placeholder: 'Gehitu zure analisi-oharrak hemen...',
        covered_of_active: '{covered} / {active} jabetza aktibo estalita',
        fetch_error: 'Huts egin du: fetch datu. Saiatu berriro.',
        run_comparison_error: 'Huts egin du: run comparison. Saiatu berriro.',
        save_notes_error: 'Huts egin du: save notes. Saiatu berriro.',
        scan_complete: 'Inkoherentzia-eskaneatuak arrakastaz osatu du.',
        scan_error: 'Huts egin du: run inconsistency scan. Saiatu berriro.',
        min_two_properties: 'Mesedez, eman gutxienez 2 jabetza ID konparaketarako.',
        comma_separated: 'komaz bereiziak',
        language_english: 'Ingelesa',
        language_vietnamese: 'Vietnamera',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Egilea',
        dependencies: 'Mendekotasunak',
        checksum: 'Egiaztapen-batura',
        changelog: 'Aldaketa-erregistroa',
        tier: 'Maila',
        phone: 'Telefonoa',
        launch: 'Abiarazi',
        complete: 'Osatu',
        back_to_list: 'Atzera to List',
        clear_selection: 'Garbitu hautapena',
        deactivate: 'Desaktibatu',
        scope: 'Esparrua',
        deadline: 'Epemuga',
        progress: 'Aurrerapena',
        frequency: 'Maiztasuna',
        reviewer: 'Berrikuskatzaile',
        user: 'Erabiltzaile',
        campaign: 'Kanpaina',
        items: 'Elementuak',
        next_run: 'Hurrengoa Exekutatu',
        last_run: 'Last Exekutatu',
        overdue: 'OVERDUE',
        creating: 'Sortzen...',
        launching: 'Abiarazten...',
        loaded: 'loaded',
        page_size: 'Orrialdearen tamaina',
        per_page: '/ orrialdea',
        urgent: 'Premiazkoa',
        pagination: 'Paginazioa',
        metric: 'Metrikoa',
        value: 'Balioa',
        country: 'Herrialdea',
        showing_chart_view: 'Grafiko-ikuspegia erakusten',
        showing_table_view: 'Datu-taularen ikuspegia erakusten',
        policies: 'Politikak',
        compliant: 'Konforme',
        non_compliant_status: 'Ez-betetzea',
        all_data: 'Datu guztiak',
        sr_urgent: 'Premiazkoa',
        n_per_page: '{n} / orrialdea',
        select_user_placeholder: '-- Hautatu erabiltzailea --',
        field_required: 'Eremu hau nahitaezkoa da',
        results_found: 'emaitza aurkitu',
        select_placeholder: '-- Hautatu --',
        unknown_user: 'Erabiltzaile ezezaguna',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'CSV esportatu',
        not_applicable: 'E/A',
        aria_overdue_since: '{date} geroztik atzeratua',
        placeholders: {
            enter_justification: 'Sartu justifikazio',
            justification: 'Justifikazio',
            campaign_name_example: 'Q1 2026 Sarbide Berrikusketa',
            optional_description: 'Hautazkoa description',
            enter_user_id: 'Sartu the erabiltzaile id of the reviewer',
            enter_user_id_short: 'Sartu erabiltzaile id',
            schedule_name_example: 'Hiruhilero Sarbide Attestation',
            enter_property_id: 'Sartu property id',
            enter_policy_id: 'Sartu policy id',
            property_or_group_id: 'Jabetza or group ID',
            describe_template_purpose: 'Deskribatu txantiloiaren xedea...',
            step_title: 'Urrats title...',
            detailed_instructions: 'Argibide zehatzak...',
            filter_by_taxonomy_id: 'Iragazi by Taxonomia ID...',
            describe_classification_rule: 'Deskribatu sailkapen-araua...',
            describe_dedup_rule: 'Deskribatu desduplikazio-araua...',
            describe_import_template: 'Deskribatu inportazio-txantiloia...',
            reason_for_rejection: 'Baztertzeko arrazoia...',
            jurisdiction_codes_example: 'VN, SG, EB',
            filter_by_locale: 'Iragazi by locale...',
            filter_by_event_type: 'Iragazi by event type',
            filter_by_actor_id: 'Iragazi by actor ID',
            filter_by_market_code: 'Iragazi by market kodea...',
            filter_by_framework_id: 'Iragazi by framework ID...',
            add_comment: 'Gehitu iruzkina...',
            provide_rejection_reason: 'Eman arrazoi bat bidalketa hau baztertzeko...',
            filter_by_jurisdiction: 'Iragazi by jurisdiction kodea...',
            enter_report_title: 'Sartu report title',
            enter_name: 'Sartu izena',
            enter_description: 'Sartu description',
            enter_org_id: 'Sartu org id to add',
            describe_evidence_gap: 'Describe the ebidentzia gap...',
            expected_evidence: 'Zein ebidentzia espero den',
            entity_identifier: 'Entitate-identifikatzailea',
            remediation_steps: 'Urratsak to remediate this gap',
            user_id_or_name: 'Erabiltzaile IDa edo izena',
            reporting_period_id: 'Txostengintza period ID',
            maturity_model_id: 'Heldutasun model ID',
            describe_maturity_model: 'Deskribatu heldutasun-eredu hau...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Eman arrazoi bat eskaera hau ukatzeko',
            describe_emergency: 'Describe the emergency situation requiring elevated sarbide (minimum 20 characters)',
            enter_delegate_id: 'Sartu erabiltzaile id of the delegate',
            provide_delegation_reason: 'Eman arrazoi bat delegazio honetarako',
            client_identifier: 'Bezero-identifikatzailea',
            client_secret: 'Bezero-sekretua',
            time_format: 'OO:MM',
            describe_sod_rule: 'Deskribatu SoD arau honen xedea...',
            enter_role_id: 'Sartu role id',
            explain_exception: 'Azaldu zergatik behar den salbuespen hau...',
            describe_compensating_controls: 'Deskribatu indarrean dauden konpentsazio-kontrolak...',
            full_name: 'Osoa izena',
            min_chars: 'Gutxienez 12 karaktere',
            enter_override_justification: 'Sartu justifikazio for overriding this gate evaluation',
            enter_evidence_id: 'Sartu frogaren IDa estekatzeko:',
            date_format: 'EE/HH/UUUU',
            describe_pack_contents: 'Deskribatu paketearen edukia eta helburua',
            select_reviewers: 'Berrikuskatzaileak hautatu...',
        },
        aria_previous_page: 'Aurreko orrialdea',
        aria_next_page: 'Hurrengo orrialdea',
        aria_results_per_page: 'Emaitzak orrialde bakoitzeko',
        per_page_25: '25 / orrialdea',
        per_page_50: '50 / orrialdea',
        per_page_100: '100 / orrialdea',
        select_language: 'Hizkuntza hautatu',
        today: 'Gaur',
        tomorrow: 'Bihar',
        dashboard_load_error: 'Ezin izan da aginte-panela kargatu. Saiatu berriro.',
        enum_grade: {
            red: 'Gorria',
            amber: 'Anbarra',
            yellow: 'Horia',
            green: 'Berdea',
        },
        enum_severity: {
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
        },
        enum_priority: {
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
        },
        enum_status: {
            pending: 'Zain',
            in_progress: 'Abian',
            completed: 'Osatua',
            blocked: 'Blokeatuta',
            escalated: 'Eskalatuta',
            cancelled: 'Bertan behera',
            overdue: 'Atzeratuta',
            not_started: 'Hasi gabe',
            new: 'Berria',
            open: 'Ireki',
            closed: 'Itxita',
            acknowledged: 'Aitortua',
            resolved: 'Ebatzita',
        },
        enum_department: {
            unassigned: 'Esleitu gabe',
        },
        enum_form_category: {
            assessment: 'Ebaluazioa',
            assessment_report: 'Ebaluazio-txostena',
            dpia: 'DPIA',
            authority_submission: 'Agintaritzaren aurkezpena',
            cross_border_transfer: 'Mugaz gaindiko transferentzia',
            vendor: 'Saltzailea',
            breach: 'Haustura',
            consent: 'Adostasuna',
            governance: 'Gobernantza',
            register: 'Izena eman',
            monitoring: 'Jarraipena',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Pribatutasuna',
            incident: 'Gorabehera',
            org: 'Antolaketa',
            sector: 'Sektorea',
            classification: 'Sailkapena',
            discovery: 'Aurkikuntza',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Eskubideen eskaerak',
            assessments: 'Ebaluazioak',
            incidents: 'Gorabeherak',
            incident_ops: 'Gorabeheren Ops',
            monitoring: 'Jarraipena',
            vendor_risk: 'Saltzaileen Arriskua',
            governance: 'Gobernantza',
            deletion_orchestration: 'Ezabatzea',
            access_review: 'Sarbideen Berrikuspena',
            consent: 'Adostasuna',
            consent_properties: 'Adostasun Propietateak',
            forms: 'Formak',
            localization: 'Lokalizazioa',
            program_governance: 'Programaren Gobernantza',
            privacy_scanner: 'Pribatutasun eskanerra',
            classification: 'Sailkapena',
            discovery: 'Aurkikuntza',
            manual: 'Eskuliburua',
            data_mapping: 'Datuen mapaketa',
        },
        enum_risk_level: {
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
        },
        enum_request_type: {
            all: 'All',
            access: 'Sarbidea (DSAR)',
            deletion: 'Ezabatzea',
            correction: 'Zuzenketa',
            objection: 'Eragozpena',
            portability: 'Eramangarritasuna',
            restriction: 'Murrizketa',
            consent_withdrawal: 'Adostasuna kentzea',
            protective_measures: 'Babes-neurriak',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'ingelesa',
            vi: 'Vietnamera',
            all: 'Biak',
        },
        enum_event_type: {
            case_created: 'Kasua Sortu',
            status_changed: 'Egoera aldatu da',
            case_assigned: 'Esleitutako kasua',
            note_added: 'Oharra gehitu da',
            attachment_added: 'Eranskina gehitu da',
            attachment_deleted: 'Eranskina ezabatu da',
            reference_added: 'Erreferentzia Gehitua',
            checklist_updated: 'Kontrol-zerrenda eguneratua',
            identity_verified: 'Identitatea egiaztatuta',
            closure_summary_added: 'Itxieraren Laburpena',
            approval_granted: 'Emandako onespena',
            template_applied: 'Txantiloia Aplikatua',
            export_generated: 'Sortutako esportazioa',
            sla_warning: 'SLA Abisua',
            sla_overdue: 'SLA Atzeratuta',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Esportazioak huts egin du',
        you: 'You',
        export_failed: 'Esportazioak huts egin du. Saiatu berriro.',
        filters: 'Iragazi',
        failed: 'Huts eginda',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Kendua',
        draft: 'Zirriborroa',
        active: 'Aktibo',
        inactive: 'Inaktibo',
        pending: 'Zain',
        in_progress: 'Abian',
        completed: 'Osatua',
        archived: 'Artxibatua',
        published: 'Argitaratua',
        approved: 'Onartua',
        rejected: 'Baztertua',
        cancelled: 'Bertan behera utzita',
        blocked: 'Blokeatua',
        deferred: 'Atzeratua',
        not_started: 'Hasi gabe',
        dismissed: 'Baztertua',
        failed: 'Huts egindakoa',
        in_review: 'In Berrikusketa',
        deprecated: 'Zaharkituta',
        scheduled: 'Programatua',
        deploying: 'Zabaltzen',
        deployed: 'Zabalduta',
        paused: 'Pausatuta',
        rolled_back: 'Rolled Atzera',
        received: 'Jasota',
        processing: 'Prozesatzen',
        under_assessment: 'Ebaluazioan',
        implemented: 'Inplementatua',
        pending_approval: 'Zain Onartze',
        pending_deletion: 'Zain Deletion',
        on_hold: 'Atxikita',
        deleted: 'Ezabatu da',
        expired: 'Iraungita',
        outdated: 'Zaharkitua',
        drifted: 'Desbideratua',
        decommissioned: 'Desegitaratua',
        match: 'Bat-etortzea',
        different: 'Desberdina',
        missing: 'Falta',
        confirmed: 'Berretsia',
        revoked: 'Errebokatuta',
        escalated: 'Eskalatuta',
        system: 'Sistema',
        custom: 'Pertsonalizatua',
        warning: 'Abisua',
        blocking: 'Blokeatzen',
        analyst: 'Analista',
        viewer: 'Ikusle',
        daily: 'Egunero',
        weekly: 'Astero',
        policies: 'Politikak',
        denied: 'Ukatua',
        verified: 'Egiaztatua',
        detected: 'Detektatua',
        ignored: 'Ez ikusi egin',
        open: 'Ireki',
        resolved: 'Ebatzita',
        suspended: 'Etenda',
        retired: 'Erretiratua',
        review: 'Berrikuspena',
        generated: 'Sortutakoa',
        exported: 'Esportatua',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Baxua',
        medium: 'Ertaina',
        high: 'Altua',
        critical: 'Kritikoa',
    },
    sensitivity: {
        standard: 'Estandarra',
        sensitive: 'Sentikorra',
        special_category: 'Kategoria berezia',
        badge_sensitive: 'Sentikorra',
    },
    change_types: {
        policy_update: 'Politika Eguneratu',
        new_binding: 'Berria Binding',
        remove_binding: 'Kendu Binding',
        configuration_change: 'Konfigurazio Change',
        banner_update: 'Banner Eguneratu',
    },
    comparison_types: {
        full: 'Osoa',
        banner_only: 'Bannerra soilik',
        purposes_only: 'Xedeak Only',
        legal_bases_only: 'Legal Oinarriak Only',
        consent_settings: 'Adostasun Ezarpenak',
    },
    lifecycle_stages: {
        active: 'Aktibo',
        archived: 'Artxibatuta',
        pending_deletion: 'Zain Deletion',
        deleted: 'Ezabatu da',
        on_hold: 'Atxikita',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personal Datu',
        consent_record: 'Adostasun Erregistro',
        user_account: 'Erabiltzaile-kontua',
        audit_log: 'Ikuskaritza Erregistro',
        document: 'Dokumentu',
        backup: 'Babeskopia',
    },
    risk_categories: {
        data_breach: 'Datu Urraketa',
        unauthorized_access: 'Unauthorized Sarbide',
        data_loss: 'Datu Loss',
        regulatory_non_compliance: 'Araudi-ez-betetze',
        third_party_risk: 'Third-Party Arrisku',
        operational: 'Operatiboa',
        technical: 'Teknikoa',
        legal: 'Legala',
    },
    mitigation_types: {
        technical: 'Teknikoa',
        organizational: 'Erakundekoa',
        contractual: 'Kontratukoa',
        procedural: 'Prozedurazkoa',
    },
    safeguard_types: {
        encryption_in_transit: 'Enkriptatzea Garraioan',
        encryption_at_rest: 'Enkriptatzea Atsedenean',
        access_controls: 'Sarbide Kontrolak',
        contractual_clauses: 'Kontratu-klausulak',
        data_minimization: 'Datuen minimizazioa',
        pseudonymization: 'Pseudonimizazioa',
        audit_rights: 'Ikuskaritza Eskubideak',
        breach_notification: 'Urratze jakinarazpena',
        data_localization: 'Datuen lokalizazioa',
        other: 'Bestela',
    },
    scoring_methods: {
        weighted_average: 'Batez besteko haztatua',
        simple_average: 'Simple Batez bestekoa',
        maximum: 'Gehienez',
        manual: 'Eskuzkoa',
    },
    dpa_status: {
        not_required: 'Ez da beharrezkoa',
        required: 'Beharrezkoa',
        in_progress: 'Abian',
        completed: 'Osatua',
        signed: 'Sinatua',
    },
    disposal_methods: {
        anonymize: 'Anonimizatu',
        pseudonymize: 'Pseudonimizatu',
        aggregate: 'Agregatu',
        delete: 'Ezabatu',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Kontrol-panela',
        no_data: 'Ez dago kontrol-paneleko daturik erabilgarri.',
        no_data_heading: 'Ez dago datu errealrik oraindik',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Baimen estaldura',
        ctia_filings: 'CTIA aurkezpenak',
        next_dpia_review: 'Hurrengo DPIA berrikuspena',
        next_review_none: 'Antolatu gabe',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Gertakarien MPS aurkezpenak',
        compliance_score: 'Betetze-puntuazioa',
        grade: 'Kalifikazioa',
        no_score: 'Ez da betetze-puntuaziorik kalkulatu oraindik.',
        rights_requests: 'Eskubide-eskaerak',
        open: 'Irekita',
        sla_breached: 'SLA hautsita',
        sla_approaching: 'SLA epemuga hurbil',
        closed_month: 'Itxita (hilabetea)',
        incidents: 'Gertakariak eta urraketa',
        active: 'Aktibo',
        breaches: 'Urraketak',
        overdue_tasks: 'Epez kanpoko zereginak',
        breach_72h_notice: 'Arau-hauste aktiboei 72 orduko jakinarazpen-epea aplikatzen zaie',
        recommendations: 'Gomendioak',
        take_action: 'Ekintza hartu',
        drift_events: 'Desbideratzeak eta onarpenak',
        open_events: 'Irekitako gertaerak',
        pending_approvals: 'Onarpen zain',
        monitoring: 'Jarraipen-zereginak',
        programs: 'Programak',
        overdue: 'Epez kanpo',
        pending: 'Zain',
        ric_cases: 'RIC kasuak',
        coverage: 'Datuen estaldura',
        systems: 'Sistemak',
        activities: 'Jarduerak',
        vendors: 'Hornitzaileak',
        dpias: 'DBEEak',
        open_gaps: 'hutsune irekiak',
        view_as_table: 'Taula gisa ikusi',
        view_as_chart: 'Grafiko gisa ikusi',
        table_metric_header: 'Metrika',
        table_value_header: 'Balioa',
        announce_chart_view: 'Grafiko-ikuspegia erakusten',
        announce_table_view: 'Taula-ikuspegia erakusten',
        section_core_metrics: 'Oinarrizko metrikak',
        section_task_management: 'Zereginen kudeaketa',
        section_compliance_insights: 'Betetze-ikuspegiak',
        configure_dashboard: 'Aginte-panela konfiguratu',
        config_col_active: 'Aktibo',
        config_col_widget: 'Widgeta',
        collapse_section: 'Tolestu',
        expand_section: 'Zabaldu',
        reset_to_defaults: 'Berrezarri lehenetsiak',
        preferences_saved: 'Aginte-panelaren hobespenak gordeta',
        preferences_reset: 'Aginte-panela rol-lehenetsietara berrezarrita',
        preferences_load_error: 'Ezin izan dira aginte-panelaren hobespenak kargatu',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Sistemaren batez besteko puntuazioa',
        factor_avg_vendor_score: 'Saltzaileen batez besteko puntuazioa',
        factor_processing_coverage: 'Prozesatzeko jardueraren estaldura',
        factor_ropa_completeness: 'RoPA osotasuna',
        factor_drift_penalty: 'Drift gertaeren zigorra',
        rec_improve_system_gov: 'Sistemaren gobernantzaren dokumentazioa hobetzea',
        rec_improve_vendor_gov: 'Hobetu hornitzaileen gobernuaren dokumentazioa',
        rec_document_processing: 'Dokumentuak prozesatzeko jarduerak estali gabeko sistemetarako',
        rec_generate_ropa: 'Sortu RoPA erregistroak prozesatzeko jarduera aktiboetarako',
        rec_resolve_drift: 'Ebatzi larritasun handiko eta larritasun handiko gertakari irekiak',
        score_good: 'Ona',
        score_fair: 'Onargarria',
        score_poor: 'Txarra',
        score_critical: 'Kritikoa',
        drag_to_reorder: 'Arrastatu berrordenatzeko',
        widget_moved: 'Widgeta mugituta',
        press_space_to_grab: 'Sakatu zuriune-barra heltzeko, gezi-teklak mugitzeko, zuriune-barra askatzeko',
        reorder_instructions: 'Erabili arrastatu eta jaregin edo laster-teklak widgetak berrordenatzeko',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Zereginen ikuspegi orokorra',
            pending: 'Zain',
            in_progress: 'Eraikitzen',
            overdue: 'Atzeratuta',
            done_7d: 'Eginda (7d)',
            by_priority: 'Lehentasunaren arabera',
            view_all_tasks: 'Ikusi Zeregin guztiak',
            upcoming_tasks: 'Datozen zereginak (hurrengo 7 egunak)',
            no_upcoming: 'Ez dago zereginik datozen 7 egunetan.',
            view_calendar: 'Ikusi Egutegia',
            overdue_alerts: 'Atzeratuta',
            no_overdue: 'Ez dago atzeratutako zereginik. Dena bidean dago.',
            assigned_to: 'Honi esleitua',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Ikusi guztiak atzeratuta',
            notifications: 'Azken jakinarazpenak',
            unread: 'unread',
            no_notifications: 'Ez dago jakinarazpenik. Guztiak harrapatuta zaude!',
            view_all_notifications: 'Ikusi jakinarazpen guztiak',
            load_error: 'Ezin izan dira datuak kargatu.',
            day_late: 'egun berandu',
            days_late_plural: 'egun berandu',
            due_today: 'Gaur egitekoa',
            just_now: 'oraintxe bertan',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Erakundeen arteko estatistikak',
            by_organization: 'Erakundearen arabera',
            overdue_across_orgs: 'erakundeen arteko atzeratutako zereginak',
            overdue_count_label: '{count} overdue tasks',
            total: 'Guztira',
            tasks: 'tasks',
            organization: 'Antolaketa',
            current_org: 'Egungo Antolaketa',
            all_orgs: 'Erakunde guztiak',
            all_organizations: 'Erakunde guztiak',
            filter_by_org: 'Iragazi erakundearen arabera',
            cross_org: 'Gurutze-organoak',
            empty_calendar: 'Ez dago zereginik aldi honetarako. Sortu zure lehen zeregina edo egiaztatu zure iragazkiak.',
            empty_my_tasks: 'Ez duzu zereginik esleituta. Lan bikaina!',
            empty_overdue: 'Ez dago atzeratutako zereginik. Dena bidean dago.',
            empty_notifications: 'Ez dago jakinarazpenik. Guztiak harrapatuta zaude!',
            empty_workflows: 'Ez dago lan-fluxurik konfiguratuta. Arakatu txantiloiak hasteko.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Nire Atazak',
            no_tasks: 'Ez dago atazerik esleituta zuretzat.',
            load_error: 'Ezin izan dira atazak kargatu.',
            overdue: 'atzeratua',
            remaining: 'geratzen',
            view_all: 'Ataza Guztiak Ikusi',
        },
        my_section_status_widget: {
            title: 'Nire Atal Egoera',
            overall_completion: 'Orokorra',
            sections_count: 'Atalak',
            no_sections: 'Ez dago atalik esleituta zure departamentuari.',
            load_error: 'Ezin izan da atal egoera kargatu.',
            department_tasks_title: 'Sailaren zereginak',
            no_department_tasks: 'Zure sailak ez du zereginik.',
            department_tasks_error: 'Ezin izan dira sailaren zereginak kargatu.',
            no_department: 'Ez zaude sail bati esleituta.',
        },
        revision_requests_widget: {
            title: 'Berrikuste Eskaerak',
            no_revisions: 'Ez dago berrikuste eskaererik. Atal guztiak bidean daude.',
            load_error: 'Ezin izan dira berrikuste eskaerak kargatu.',
        },
        consent_summary: {
            title: 'Adostasuna',
            active: 'Aktiboa',
            withdrawn_30d: 'Erretiratua (30d)',
            expiring_7d: 'Iraungitzen (7d)',
        },
        transfer_assessments: {
            title: 'Transferentzien Ebaluazioak',
            open: 'Ireki',
            approved: 'Onartua',
            gaps_flagged: 'Hutsuneak markatuta',
        },
        breach_notifications: {
            title: 'Urratze-jakinarazpenak',
            deadline_72h: '72 orduko epea',
            submitted: 'Aurkeztua',
            pending: 'Zain',
        },
        authority_requests: {
            title: 'Agintaritzaren eskaerak',
            pending: 'Zain',
            overdue: 'Atzeratuta',
            responded_30d: 'Erantzuna (30 d)',
        },
        data_subjects_widget: {
            title: 'Datuen Subjektuak',
            total: 'Guztira',
            high_risk: 'Arrisku handia',
            active_30d: 'Aktiboa (30d)',
        },
        audit_activity_summary: {
            title: 'Ikuskaritza Jarduera',
            events_today: 'Gaurko ekitaldiak',
            events_7d: 'Gertaerak (7d)',
            unique_actors_30d: 'Aktore bereziak (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding Aurrerapena',
            completion_pct: 'Osatu',
            status_label: 'Egoera',
            current_step: 'Oraingo urratsa',
            status: {
                in_progress: 'Abian',
                completed: 'Osatua',
                skipped: 'Saltatu',
                not_started: 'Ez da hasi',
            },
        },
        sector_overlay_reports_due: {
            title: 'Epemugak dituzten sektore-gainjarpenen txostenak',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Iragazi esparruaren arabera',
            all_frameworks: 'Esparru guztiak',
            filing_lifecycle: 'Aurkezpenen bizi-zikloa',
            filing_pipeline: 'Aurkezpen-kanala',
            draft: 'Zirriborroa',
            internal_review: 'Barne Berrikuspena',
            approved: 'Onartua',
            submitted: 'Aurkeztua',
            supplement_requested: 'Eskatutako gehigarria',
            resubmitted: 'Berriro bidali da',
            accepted: 'Onartua',
            rejected: 'Baztertua',
            closed: 'Itxita',
            deadline: 'Epea',
            days_remaining: 'egun falta dira',
            days_overdue: 'egunak atzeratuta',
            no_filings: 'Ez dago aurkezpen aktiborik.',
            department_consolidation: 'Sailen kontsolidazioa',
            departments_completed: 'Sailak amaituta',
            departments_pending: 'Sailak zain',
            departments_overdue: 'Sailak atzeratuta',
            no_consolidation_cases: 'Ez dago sail anitzeko kasurik abian.',
            vendor_risk: 'Hornitzaile- eta hirugarren-arriskua',
            pending_assessment: 'Ebaluazioa zain',
            overdue_questionnaires: 'Galdetegi atzeratuak',
            dpa_review_queue: 'DPA berrikusketa-ilara',
            risk_distribution: 'Arriskuen Banaketa',
            total_vendors: 'Hornitzaile guztira',
            by_risk_level: 'Arrisku-mailaren arabera',
            no_vendor_data: 'Ez dago hornitzaileen arrisku-daturik.',
            discovery_summary: 'Datuen Aurkikuntza',
            discovery_classification: 'Datuen aurkikuntza eta sailkapena',
            active_scans: 'Eskaneatu aktiboak',
            labels_pending: 'Etiketak berrikusteko zain',
            exceptions_unresolved: 'Salbuespenak Ebatzi gabe',
            pending_review: 'Berrikusketa zain',
            exceptions: 'Salbuespenak',
            approved_labels: 'Etiketak onartuta',
            total_classified: 'Guztira sailkatuta',
            no_discovery_data: 'Ez dago aurkikuntza-daturik.',
            ai_governance: 'AA sistema-gobernantza',
            systems_inventory: 'Sistemak Inbentarioan',
            systems_in_inventory: 'Sistemak inbentarioan',
            classification_breakdown: 'Sailkapenaren banaketa',
            dossier_completion: 'Dosier-osotasuna',
            by_classification: 'Sailkapenaren arabera',
            labeling_status: 'Etiketatze-egoera',
            not_started: 'Hasi gabe',
            no_ai_data: 'Ez dago AA gobernantza-daturik.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Urraketa- eta gertaera-erantzuna',
            active_breaches: 'Urraketa aktiboak',
            notification_status: 'Jakinarazpen-egoera',
            remediation_progress: 'Konponketa-aurrerapena',
            supplement_loops: 'Osagarri-begiztak',
            no_breach_data: 'Ez dago urraketa-kasu aktiborik.',
            audit_readiness: 'Ikuskaritza eta ebaluazio-prestutasuna',
            assessments: 'Ebaluazioak',
            gap_findings: 'Hutsuneen aurkikuntzak',
            evidence_completion: 'Ebidentzia-osotasuna',
            export_ready: 'Esportatzeko prest',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Ez dago prest',
            no_audit_data: 'Ez dago ikuskaritza-prestutasun daturik.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Zibersegurtasuna 2018',
            framework_label_vn_ecommerce: 'VN Merkataritza elektronikoa 2023',
            framework_label_vn_telecom: 'VN Telekomunikazioak 2023',
            framework_label_vn_ai: 'VN AA Legea 2025',
            framework_label_gdpr: 'DBEO',
            framework_label_unspecified: 'Zehaztu gabe',
            my_department: 'Nire saila',
            my_tasks: 'Nire zereginak',
            section_status: 'Atalaren egoera',
            revision_requests: 'Berrikusketa-eskaerak',
            completion: 'Osotasuna',
            reason: 'Arrazoia',
            requested_at: 'Eskatua',
            no_tasks_assigned: 'Ez dago zeregin esleituta.',
            no_sections: 'Ez dago atalik zure sailari esleituta.',
            no_revisions: 'Ez dago berrikusketa-eskaerarik.',
            load_error: 'Ezin izan dira datuak kargatu.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instantziak',
            credentials: 'Kredentzial-biltegi',
            event_bus: 'Gertaera-busa',
            definitions: 'Definizioak',
            brds: 'BRD bilketa',
            security_reviews: 'Segurtasun-berrikuspenak',
            poc_scorecards: 'POC puntuazio-txartelak',
            automation_planning: 'Automatizazio-plangintza',
            system_inventory: 'Sistema-inbentarioa',
            traceability_matrix: 'Trazabilitate-matrizea',
            gap_register: 'Hutsune-erregistroa',
        },
        instances: {
            title: 'Konektore-instantziak',
            page_title: 'Konektoreak',
            add: 'Gehitu instantzia',
            test: 'Probatu konexioa',
            sync_now: 'Sinkronizatu orain',
            new_instance: 'Berria Konektore Instance',
            no_instances: 'Ez da connector instances aurkitu',
            instance_count: '{count} instantzia',
            confirm_delete: 'Ezabatu connector instance "{name}"?',
            all_connector_types: 'Konektore mota guztiak',
            select_connector: 'Hautatu a connector...',
            fetch_error: 'Huts egin du: fetch connector instances. Saiatu berriro.',
            fetch_definitions_error: 'Huts egin du: fetch connector definitions. Saiatu berriro.',
            create_error: 'Huts egin du: create connector instance. Saiatu berriro.',
            test_error: 'Huts egin du: test connector. Saiatu berriro.',
            status_error: 'Huts egin du: konektorearen egoera eguneratzea. Saiatu berriro.',
            delete_error: 'Huts egin du: delete connector instance. Saiatu berriro.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Egoera eguneratu da',
            reactivate: 'Berriz aktibatu',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Kredentzial-biltegia',
            page_title: 'Konektorearen kredentzialak',
            add: 'Gehitu kredentziala',
            rotate: 'Biratu',
            expired: 'Iraungita',
            expiring: 'Iraungitzear',
            orphaned: 'Umezurtza',
            new_credential: 'Berria Kredentzial',
            no_credentials: 'Ez da credentials aurkitu',
            no_expiry: 'Iraungitzerik ez',
            linked_connectors: 'Linked Konektoreak',
            expires_at: 'Iraungitze-data',
            value_label: 'Balioa * (idazketa soilik)',
            confirm_delete_title: 'Berretsi ezabatzea',
            confirm_delete_message: 'Kredentzial hau ezabatu? Loturiko konektoreak sarbidea galduko dute.',
            rotate_title: 'Kredentzial biratu',
            new_credential_value: 'Kredentzial balio berria',
            fetch_error: 'Huts egin du: load credentials. Saiatu berriro.',
            health_error: 'Huts egin du: load health. Saiatu berriro.',
            create_error: 'Huts egin du: create credential. Saiatu berriro.',
            rotate_error: 'Huts egin du: rotate credential. Saiatu berriro.',
            delete_error: 'Huts egin du: delete credential. Saiatu berriro.',
        },
        event_bus: {
            title: 'Gertaera-busa',
            page_title: 'Gertaera-busa',
            events: 'Gertaerak',
            subscriptions: 'Harpidetzak',
            dlq: 'Mezu hilakotz-ilara',
            publish: 'Argitaratu gertaera',
            retry: 'Saiatu berriro',
            dismiss: 'Baztertu',
            search_events: 'Bilatu events...',
            filter_event_type: 'Iragazi by event type...',
            event_type: 'Gertaera mota',
            source_module: 'Iturria Modulu',
            timestamp: 'Denbora-zigilua',
            delivery_status: 'Delivery Egoera',
            correlation_id: 'Korrelazio IDa',
            tracking_id: 'Jarraipen IDa',
            delivery_status_hint: 'Bidalketa egoera automatikoki eguneratzen da gertaerak prozesatzen diren heinean.',
            no_events: 'Ez da events aurkitu',
            total_with_count: '{count} guztira',
            prev: 'Aurrekoa',
            new_subscription: '+ Harpidetza berria',
            subscriber_type: 'Subscriber Mota',
            subscriber_id: 'Harpidedun IDa',
            event_type_pattern: 'Gertaera Mota Pattern',
            event_type_pattern_placeholder: 'adib. incident.*',
            filter_expression: 'Iragazi Expression',
            filter_expression_placeholder: 'Hautazkoa filter',
            subscriber_type_hint: 'Aukeratu gertaerak nola bidaliko diren: webhook-ek HTTP eskaerak bidaltzen ditu, emailak jakinarazpenak bidaltzen ditu, ilarako mezu-ilara batera bideratzen du.',
            subscriber_id_hint: 'Sartu gertaeren helmuga. Webhook-etarako, erabili URL bat. Emailerako, erabili helbide bat. Ilaretarako, erabili ilararen izena.',
            event_type_pattern_hint: 'Gertaerak motaren arabera lotu komodinak erabiliz. Erabili * edozein segmentu lotzeko (adib. incident.* incident.created, incident.resolved-ekin bat dator).',
            filter_expression_hint: 'Aukerakoa. Gertaerak edukiaren arabera gehiago iragazi (adib. severity=high).',
            subscriber_id_placeholder_webhook: 'adib. https://zure-app.com/webhook',
            subscriber_id_placeholder_email: 'adib. alertak@zure-enpresa.com',
            subscriber_id_placeholder_queue: 'adib. gertaera-ilara',
            event_pattern: 'Gertaera Pattern',
            filter: 'Iragazi',
            enabled: 'Gaituta',
            delivery_stats: 'Entrega-estatistikak',
            no_subscriptions: 'Ez da subscriptions aurkitu',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} entregatuta / {failed} huts egin',
            confirm_delete_subscription: 'Ezabatu this subscription?',
            unresolved: 'Ebatzi gabe',
            resolved: 'Ebatzita',
            event_id: 'Gertaera ID',
            subscription_id: 'Harpidetza ID',
            error_code: 'Errorea Kodea',
            error_message: 'Errorea Message',
            attempts: 'Saiakerak',
            last_failed: 'Last Huts egin',
            no_dlq_entries: 'Ez da dead letter entries aurkitu',
            dlq_count: '{count} sarrera guztira',
            fetch_events_error: 'Huts egin du: fetch events. Saiatu berriro.',
            fetch_subscriptions_error: 'Huts egin du: fetch subscriptions. Saiatu berriro.',
            fetch_dlq_error: 'Huts egin du: fetch DLQ entries. Saiatu berriro.',
            create_subscription_error: 'Huts egin du: create subscription. Saiatu berriro.',
            toggle_subscription_error: 'Huts egin du: toggle subscription. Saiatu berriro.',
            delete_subscription_error: 'Huts egin du: delete subscription. Saiatu berriro.',
            retry_error: 'Berriro saiatzeari huts egin dio',
            dismiss_error: 'Baztertzeak huts egin du',
            event_published: 'Gertaera argitaratua',
            publish_event_error: 'Ezin izan da gertaera argitaratu. Saiatu berriro.',
            priority_label: 'Lehentasuna',
            priority_low: 'Baxua',
            priority_normal: 'Normala',
            priority_high: 'Altua',
            payload_helper_text: 'Sartu gertaeraren datuak JSON formatuan. Adibidea: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Harpidetza sortua',
            subscription_toggled: 'Harpidetza txandakatua',
            subscription_deleted: 'Harpidetza ezabatua',
            dlq_retried: 'DLQ mezuari berriro saiatu zaio',
            dlq_dismissed: 'DLQ mezua baztertua',
        },
        definitions: {
            title: 'Konektoreen definizioak',
            page_title: 'Konektore-definizioak',
            add: 'Gehitu definizioa',
            new_definition: 'Konektoreen definizio berria',
            edit_definition: 'Editatu konektorearen definizioa',
            no_definitions: 'Ez da aurkitu konektoreen definiziorik',
            definition_count: '{count} definizio(k)',
            all_categories: 'Kategoria guztiak',
            select_category: 'Hautatu kategoria bat...',
            capabilities: 'Gaitasunak',
            capabilities_hint: 'Gaitasunak (komaz bereizita)',
            capabilities_placeholder: 'adib. irakurri, idatzi, sinkronizatu',
            created: 'Konektorearen definizioa sortu da',
            updated: 'Konektorearen definizioa eguneratu da',
            fetch_error: 'Ezin izan dira eskuratu konektoreen definizioak. Mesedez, saiatu berriro.',
            create_error: 'Ezin izan da sortu konektorearen definizioa',
            update_error: 'Konektorearen definizioa eguneratzeak huts egin du',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD bilketa',
            page_title: 'BRD harrapaketa',
            create_brd: 'BRD sortu',
            brd_title: 'BRD izenburua',
            description: 'Deskribapena',
            requirements: 'Eskakizunak',
            status: 'Egoera',
            stakeholders: 'Interesdunak',
            connector_label: 'Konektorea',
            select_connector: 'Hautatu konektore bat...',
            approval: 'Onespena',
            no_brds_found: 'Ez da BRDrik aurkitu',
            decision_records: 'Erabaki-erregistroak',
            create_decision: 'Erabakia sortu',
            alternatives_considered: 'Kontuan hartutako alternatibak',
            alternative_name: 'Alternatibaren izena',
            reason_rejected: 'Baztertze-arrazoia',
            add_alternative: 'Alternatiba gehitu',
            remove_alternative: 'Alternatiba {name} kendu',
            alternatives: 'Alternatibak',
            rationale: 'Arrazoibidea',
            selected_connector: 'Hautatutako konektorea',
            comma_separated_names: 'Komaz bereizitako izenak',
            brd_created: 'BRD sortua',
            decision_record_created: 'Erabaki-erregistroa sortua',
            no_decision_records_found: 'Ez da erabaki-erregistrorik aurkitu',
            aria_brds_table: 'BRDak',
            aria_decision_records_table: 'Erabaki-erregistroak',
            aria_approve: 'Onartu {name}',
            aria_archive: 'Artxibatu {name}',
            brd_title_help: 'BRD honetarako gizakiek irakur daitekeen izen labur bat - adibidez. "Jira ticketing integrazioa", "Okta SSO inplementazioa".',
            brd_title_placeholder: 'adib. Jira ticketing integrazioa',
            description_help: 'Konektore honek konpontzen duen negozio-arazoa. Zein emaitza espero duzu zuzenekoa denean?',
            description_placeholder: 'adib. Automatizatu gertakarien tiketak sortzea monitorizazio abisuetatik, deialdietan 60 segundoko epean orria izan dadin.',
            connector_help: 'Aukerakoa. Aukeratu BRD honentzat dagoen konektore bat — utzi hutsik konektorea oraindik aukeratu ez bada.',
            requirements_help: 'Komaz bereizitako ezinbesteko gaitasunen zerrenda - adibidez. "OAuth2 saio-hasiera, webhook entrega, auditoria esportazioa".',
            requirements_placeholder: 'OAuth2 saio-hasiera, webhook-en entrega, auditoria-esportazioa',
            stakeholders_help: 'Saioa amaitu behar duten pertsonen komaz bereizitako izenak edo helbide elektronikoak.',
            selected_connector_help: 'Aukeratu erabaki honi dagokion konektorea.',
            rationale_help: 'Zergatik aukeratu zen konektore hau. Estali erabakigarriak diren faktoreak etorkizuneko irakurle batek deialdia uler dezan.',
            rationale_placeholder: 'adib. Jira aukeratu dugu dagoeneko gure sarreren historia gordetzen duelako eta taldea horretan entrenatu delako.',
            alternatives_considered_help: 'Beste konektore batzuk ebaluatu eta zergatik ez ziren hautatu. Ikuskaritzarako babesgarritasuna gehitzen du.',
            alternative_name_placeholder: 'adib. Zerbitzua Orain',
            reason_rejected_placeholder: 'adib. Kostua 3× handiagoa, gure erabilera kasurako abantailarik ez',
        },
        security_reviews: {
            title: 'Segurtasun-berrikuspenak',
            page_title: 'Segurtasun-berrikuspenak',
            create_review: 'Berrikuspena sortu',
            connector_name: 'Konektorearen izena',
            connector_label: 'Konektorea',
            select_connector: 'Hautatu konektore bat...',
            reviewer: 'Berrikuskatzailea',
            select_reviewer: 'Hautatu berrikuskatzaile bat...',
            findings: 'Aurkikuntzak (bat lerro bakoitzeko)',
            risk_level: 'Arrisku-maila',
            risk_level_placeholder: 'adib. altua, ertaina, baxua',
            notes: 'Oharrak',
            review_status: 'Berrikuspenaren egoera',
            checklist: 'Egiaztapen-zerrenda',
            privacy_impact: 'Pribatutasun-eragina',
            approval_gate: 'Onespen-atea',
            no_reviews_found: 'Ez da berrikuspenrik aurkitu',
            review_created: 'Segurtasun-berrikuspena sortua',
            create_control_mapping: 'Sortu kontrol-mapaketa',
            control_mapping_created: 'Kontrol-mapaketa sortua',
            connector_id_label: 'Konektorearen IDa',
            control_id_label: 'Kontrolaren IDa',
            control_name_label: 'Kontrolaren izena',
            aria_approve: 'Onartu {name}',
            aria_reject: 'Baztertu {name}',
            scan_imported: 'Eskaneatzea ongi inportatu da',
            import_scan: 'Inportatu eskaneatzea',
            scan_format: 'Eskaneatze formatua',
            scan_data: 'Eskaneatze datuak',
            import_results: 'Inportazio emaitzak',
            recommendations: 'Gomendioak',
            auto_populated_from: '{tool}-tik automatikoki bete da',
            manual_entry: 'Eskuzko sarrera',
            automation_type: 'Automatizazio Mota',
            select_automation_type: 'Hautatu automatizazio mota...',
            automation_full: 'Osoa — konektoreak erabat automatizatzen du kontrol hau',
            automation_partial: 'Partziala — konektoreak frogak laguntzen ditu; gizakiak oraindik jokatzen du',
            automation_monitoring: 'Monitorizazioa soilik — konektoreak behatzen du, ez du betetzen',
            control_id_help: 'Erabili zure betetze-eskemaren kodea (adibidez, ISO-27001-A.12.1, SOC2-CC6.1 edo zure barne-kontroleko ID).',
            control_name_help: 'Gizakiek irakur daitekeen izen laburra (adibidez, "Sarerako sarbide-murrizketa").',
            description_label: 'Deskribapena',
            load_sarif_example: 'Kargatu SARIF adibidea',
            load_json_example: 'Kargatu JSON adibide pertsonalizatua',
            scan_format_help: 'SARIF eskaner gehienek (Snyk, CodeQL, Semgrep) igortzen duten OASIS estandarra da. Erabili JSON pertsonalizatua SARIF igortzen ez duten tresnetarako.',
            scan_data_help: 'Itsatsi eskaneatutako irteera osoa, kargatu .sarif/.json fitxategi bat edo egin klik Kargatu adibidea botoian forma ikusteko.',
            upload_file_label: 'Kargatu .sarif edo .json fitxategia',
            invalid_scan_json: 'Ezin izan dira eskaneatu datuak JSON gisa analizatu. Egiaztatu nabarmendutako karakterearen ondoan sintaxi-errorerik dagoen.',
            save_as_review: 'Gorde Segurtasun Berrikuspen gisa',
            discard_preview: 'Baztertu aurrebista',
            preview_hint: 'Hau aurrebista da; oraindik ez da ezer gorde. Sakatu "Gorde segurtasun-berrikuspen gisa" balio hauekin aurrez betetako zain dagoen berrikuspena sortzeko.',
            reviewer_required_for_save: 'Aukeratu berrikusle bat eskaneatu hau segurtasun-berrikuspen gisa gordetzeko.',
        },
        poc_scorecards: {
            title: 'POC puntuazio-txartelak',
            page_title: 'POC puntuazio-txartelak',
            create_scorecard: 'Puntuazio-txartela sortu',
            connector_label: 'Konektorea',
            select_connector: 'Hautatu konektore bat...',
            vendor_name: 'Hornitzailearen izena',
            recommendation: 'Gomendioa',
            recommendation_placeholder: 'adib. Aurrera jarraitu, Ez jarraitu, Ebaluazio gehiago behar da',
            criteria: 'Ebaluazio-irizpideak',
            criterion_name: 'Irizpidearen izena',
            score: 'Puntuazioa',
            max_score: 'Gehieneko puntuazioa',
            notes: 'Oharrak',
            add_criterion: 'Irizpidea gehitu',
            remove_criterion: 'Irizpidea {name} kendu',
            total_score: 'Guztizko puntuazioa',
            api_quality: 'API kalitatea',
            reliability: 'Fidagarritasuna',
            support_quality: 'Laguntza-kalitatea',
            cost_score: 'Kostu-puntuazioa',
            overall_score: 'Puntuazio orokorra',
            evidence: 'Ebidentzia',
            no_scorecards_found: 'Ez da puntuazio-txartelik aurkitu',
            scorecard_created: 'POC puntuazio-txartela sortua',
            connector_help: 'Aukeratu POC ebaluazio hau zer konektorea den.',
            vendor_name_help: 'Ebaluatzen ari den saltzailea edo produktua - adibidez. "Okta", "Datadog", "Elur maluta".',
            vendor_name_placeholder: 'adib. Okta, Datadog, Snowflake',
            recommendation_help: 'Zure azken deia ebaluazioaren ondoren. Jarrai ezazu labur eta erabakigarria.',
            criteria_help: 'Puntuatu saltzailea ebaluazio-irizpide bakoitzean. Gehitu errenkada bat irizpide bakoitzeko (API kalitatea, fidagarritasuna, laguntza, kostua, etab.).',
            criterion_name_help: 'Zer puntuatzen ari zaren - adibidez. "API Kalitatea", "Fidagarritasuna", "Laguntza", "Kostua".',
            criterion_name_placeholder: 'adib. API Kalitatea',
            score_help: 'Irizpide honengatik emandako puntuazioa. Ez da gehienezko puntuazioa gainditu behar.',
            max_score_help: 'Irizpide honetarako eskuragarri dagoen puntuaziorik onena. Erabili eskala bera errenkadetan zehar, guztirakoak konparagarriak izan daitezen.',
            notes_help: 'Aukerakoa. Gehitu frogak, oharrak edo estekak, puntuazioa azaltzen duen edozer.',
        },
        common_labels: {
            aria_filter_instances: 'Iragazi konektore-instantziak',
            aria_filter_definitions: 'Iragazi konektore-definizioak',
            aria_filter_credentials: 'Iragazi kredentzialak',
            aria_filter_events: 'Iragazi gertaerak',
            aria_filter_dlq: 'Iragazi hil-gutun ilara',
            aria_instance_summary: 'Konektore-instantziaren laburpena',
            aria_active_connectors: 'Konektore aktiboak',
            aria_error_connectors: 'Erroredun konektoreak',
            aria_credential_health: 'Kredentzial-osasunaren laburpena',
            aria_items_per_page: 'Elementuak orriko',
            show_password: 'Erakutsi pasahitza',
            hide_password: 'Ezkutatu pasahitza',
            payload_json_label: 'Karga (JSON)',
            payload_invalid_json: 'Karga JSON baliogarria izan behar da',
            placeholder_event_type: 'adib. incident.created',
            placeholder_source_module: 'adib. monitoring',
            placeholder_version: 'adib. 1.0',
        },
        statuses: {
            configuring: 'Konfiguratzen',
            testing: 'Probak',
            active: 'Aktiboa',
            paused: 'Pausatuta',
            error: 'Errorea',
            disabled: 'Ezindua',
            draft: 'Zirriborroa',
            approved: 'Onartua',
            archived: 'Artxibatuta',
            pending: 'Zain',
            rejected: 'Baztertua',
        },
        system_types: {
            application: 'Aplikazioa',
            database: 'Datu-basea',
            service: 'Service',
            infrastructure: 'Azpiegitura',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Lokalean',
            cloud: 'Hodeia',
            hybrid: 'Hibridoa',
            managed: 'Kudeatua',
        },
        system_statuses: {
            active: 'Aktiboa',
            decommissioned: 'Desegindakoa',
            planned: 'Planifikatuta',
            under_review: 'Aztertzen',
        },
        automation_statuses: {
            planning: 'Planifikazioa',
            in_progress: 'Abian',
            completed: 'Osatuta',
            on_hold: 'Zain',
        },
        gap_types: {
            functional: 'Funtzionala',
            technical: 'Teknikoa',
            security: 'Segurtasuna',
            compliance: 'Betetzea',
            process: 'Prozesua',
        },
        severity_labels: {
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
        },
        gap_statuses: {
            open: 'Irekia',
            in_progress: 'Abian',
            mitigated: 'Arintuta',
            closed: 'Itxita',
            accepted: 'Onartuta',
        },
        verification_statuses: {
            not_started: 'Hasi gabe',
            in_progress: 'Abian',
            verified: 'Egiaztatuta',
            failed: 'Huts egina',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Barnekoa',
            email: 'Posta',
            queue: 'Ilara',
        },
        credential_types: {
            password: 'Pasahitza',
            api_key: 'API gakoa',
            oauth2_client: 'OAuth2 bezeroa',
            certificate: 'Ziurtagiria',
            token: 'Tokena',
            ssh_key: 'SSH gakoa',
            imap_credentials: 'IMAP kredentzialak',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Hodeia',
            security: 'Segurtasuna',
            itsm: 'ITSM',
            custom: 'Pertsonalizatua',
        },
        delivery_statuses: {
            pending: 'Zain',
            delivered: 'Entregatuta',
            partial: 'Partziala',
            failed: 'Huts egina',
        },
        automation_planning: {
            title: 'Automatizazio-plangintza',
            page_title: 'Automatizazio-plangintza',
            program_name: 'Programaren izena',
            program_status: 'Egoera',
            description: 'Deskribapena',
            linked_artifacts: 'Lotutako artefaktuak',
            create_program: 'Programa sortu',
            edit_program: 'Programa editatu',
            no_programs_found: 'Ez da programarik aurkitu',
            program_created: 'Programa sortua',
            program_updated: 'Programa eguneratua',
            target_date: 'Helburu-data',
            owner: 'Jabea',
            select_status: 'Egoera hautatu...',
            aria_programs_table: 'Automatizazio-programak',
            aria_edit: '{name} editatu',
            export_programs: 'Programak esportatu',
            title_label: 'Programaren Izenburua',
            title_help: 'Automatizazio-programaren izen labur eta irakurgarria da, adibidez. "Hirugarren hiruhilekoan gertatutakoaren inplementazioa", "PDPL sarbide-eskaeraren automatizazioa".',
            title_placeholder: 'adib. Hirugarren hiruhilekoan Jirako gertakarien hedapena',
            description_help: 'Programa honek jasotzen duena eta zuzenean dagoenean espero duzun emaitza. Aukerakoa baina gomendagarria.',
            description_placeholder: 'adib. Wire monitorearen alertak Jira-ra, beraz, deiak 60 seko epean orria izan dadin.',
            owner_label: 'Programaren jabea',
            owner_help: 'Programa honen arduraduna. Aurrerapenari eta ateei buruzko jakinarazpenak jasoko dituzte.',
            select_owner: 'Hautatu jabea...',
            frameworks_in_scope: 'Esparruak',
            frameworks_in_scope_help: 'Programa honek onartzen dituen betetze-esparruak. Ikuskaritza ibilbideak automatizazioa zein araudi arautzen duen erakusten laguntzen du.',
            select_frameworks: 'Aukeratu esparruak...',
            status_draft: 'Zirriborroa',
            status_active: 'Aktiboa',
            status_completed: 'Osatua',
            status_archived: 'Artxibatuta',
            change_status: 'Aldatu egoera',
            status_updated: 'Egoera eguneratu da',
            aria_change_status: 'Aldatu {name}-ren egoera',
        },
        system_inventory: {
            title: 'Sistema-inbentarioa',
            page_title: 'Sistema-inbentarioa',
            system_name: 'Sistemaren izena',
            system_type: 'Sistema-mota',
            hosting: 'Ostatatzea',
            status: 'Egoera',
            description: 'Deskribapena',
            create_system: 'Sistema sortu',
            edit_system: 'Sistema editatu',
            no_systems_found: 'Ez da sistemarik aurkitu',
            system_created: 'Sistema sortua',
            system_updated: 'Sistema eguneratua',
            select_type: 'Mota hautatu...',
            select_hosting: 'Ostatatzea hautatu...',
            select_status: 'Egoera hautatu...',
            owner: 'Jabea',
            department: 'Saila',
            data_categories: 'Datuen kategoriak',
            data_categories_placeholder: 'Komaz bereizita, adibidez: PII, finantzarioa, osasuna',
            jurisdiction: 'Jurisdikzioa',
            jurisdiction_placeholder: 'Adibidez: EB, AEB, globala',
            interfaces: 'Interfazeak',
            interfaces_placeholder: 'Komaz bereizita, adibidez: REST API, SFTP, JDBC',
            owner_placeholder: 'Hautatu jabe arduratsu bat...',
            department_placeholder: 'Hautatu erakunde-unitate arduraduna...',
            system_name_hint: 'Erabili negozioak onartutako sistemaren edo aplikazioaren izena.',
            system_type_hint: 'Sailkatu sistema, txostenek antzeko aktiboak taldekatu ahal izateko.',
            hosting_hint: 'Identifikatu sistema non exekutatzen den egoitza, segurtasuna eta hornitzaileen berrikuspenarako.',
            status_hint: 'Ezarri berrikuspenetan, esportazioetan eta auditoretza-ebidentzietan erabiltzen den bizi-zikloaren egoera.',
            owner_hint: 'Aukeratu sistemaren gobernantzaz eta jarraipen-atazez arduratuko den erabiltzaile aktiboa.',
            department_hint: 'Erabili sistema hau ustiatzeaz edo gobernatzeaz arduratzen den erakunde-unitatea.',
            data_categories_hint: 'Zerrendatu sistemak prozesatutako datu-motak, komaz bereizita.',
            jurisdiction_hint: 'Grabatu jurisdikzio legal edo ostatatze nagusia betetze-mapaketa egiteko.',
            interfaces_hint: 'Zerrendatu konektoreak edo integrazio-bideak datuak trukatzeko.',
            description_hint: 'Laburbildu zer egiten duen sistemak eta zergatik den garrantzitsua datu-maparako.',
            owner_department_required: 'Jabea eta Saila beharrezkoak dira.',
            aria_systems_table: 'Sistema-inbentarioa',
            aria_edit: '{name} editatu',
            export_systems: 'Sistemak esportatu',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Trazabilitate-matrizea',
            page_title: 'Trazabilitate-matrizea',
            brd_selector: 'BRD hautatu',
            requirement: 'Eskakizuna',
            linked_artifact: 'Lotutako artefaktua',
            verification_status: 'Egiaztapen-egoera',
            verified_by: 'Egiaztatua',
            create_entry: 'Sarrera sortu',
            edit_entry: 'Sarrera editatu',
            no_entries_found: 'Ez da sarrerarik aurkitu',
            entry_created: 'Sarrera sortua',
            entry_updated: 'Sarrera eguneratua',
            select_brd: 'BRD bat hautatu...',
            select_status: 'Egoera hautatu...',
            aria_matrix_table: 'Trazabilitate-matrizea',
            aria_edit: '{name} sarrera editatu',
            export_matrix: 'Matrizea esportatu',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Eskakizunaren IDa',
        },
        gap_register: {
            title: 'Hutsune-erregistroa',
            page_title: 'Hutsune-erregistroa',
            gap_title: 'Hutsune-izenburua',
            gap_type: 'Hutsune-mota',
            severity: 'Larritasuna',
            status: 'Egoera',
            description: 'Deskribapena',
            remediation_plan: 'Konponketa-plana',
            assigned_to: 'Esleitu',
            due_date: 'Epemuga',
            create_gap: 'Hutsunea sortu',
            edit_gap: 'Hutsunea editatu',
            no_gaps_found: 'Ez da hutsunerik aurkitu',
            gap_created: 'Hutsunea sortua',
            gap_updated: 'Hutsunea eguneratua',
            select_type: 'Mota hautatu...',
            select_severity: 'Larritasuna hautatu...',
            select_status: 'Egoera hautatu...',
            aria_gaps_table: 'Hutsune-erregistroa',
            aria_edit: '{name} editatu',
            export_gaps: 'Hutsuneak esportatu',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Esparru-lotura',
            framework_linkage_placeholder: 'adib. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Esparru-irismena',
            framework_scope_placeholder: 'adib. GDPR, PCI-DSS',
            procedure_linkage: 'Prozedura-lotura',
            procedure_linkage_placeholder: 'adib. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Arautzaileen iturria',
            regulatory_source_placeholder: 'adib. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrol-mapaketa',
            control_mappings_section: 'Kontrol-mapeak',
            no_control_mappings: 'Oraindik ez dago kontrol-maparik. Egin klik "Sortu kontrol mapak" bat gehitzeko.',
            automation_type_col: 'Automatizazioa',
        },
        gap_register_extra: {
            framework_filter: 'Esparrua',
            framework_id: 'Esparru-IDa',
            framework_id_placeholder: 'adib. GDPR, ISO 27001',
            all_frameworks: 'Esparru guztiak',
        },
        poc_scorecards_extra: {
            criteria_count: 'Irizpideak',
        },
        export_labels: {
            export_brds: 'BRDak esportatu',
            export_decision_records: 'Erabaki-erregistroak esportatu',
            export_security_reviews: 'Segurtasun-berrikuspenak esportatu',
            export_poc_scorecards: 'POC puntuazio-txartelak esportatu',
            export_instances: 'Esportatu Instantziak',
            export_definitions: 'Esportatu definizioak',
            export_credentials: 'Esportatu kredentzialak',
            export_events: 'Esportatu Gertaerak',
            exporting: 'Esportatzen...',
            export_success: 'Esportazioa burutua',
            export_error: 'Esportazioak huts egin du',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Obligazioak ateratzea',
            extracting: 'Erauzten…',
            results_heading: 'Erauzitako betebeharrak ({count})',
            tokens_label: '≈ {count} token',
            no_obligations: 'Kontratu-testu honetan ez da betebehar eredurik hauteman.',
            col_class: 'Klasea',
            col_summary: 'Laburpena (editagarria)',
            col_excerpt: 'Klausula zatia',
            col_due_date: 'Epemuga (editagarria)',
            col_priority: 'Lehentasuna',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Jakinarazteko epea',
            class_audit: 'Ikuskaritza eskubidea',
            class_insurance: 'Aseguruak',
            class_sla: 'SLA erantzuna',
            class_breach: 'Urratze-jakinarazpena',
            class_renewal: 'Berritze leihoa',
            citations_label: 'Aipamenak',
            accept_hint: 'Onartzeak zeregin bat sortuko du betebehar bakoitzeko (8. kap.) lehentasuna + mugaeguna erakutsita.',
            accept_label: 'Betebeharrak onartu',
            reject_label: 'Errefusa txostena',
            resolve_summary: 'Ebazpena: {status} · {tasks} zereginak sortu dira',
            error_generic: 'Ezin izan dira betebeharrak atera. Saiatu berriro edo egiaztatu kontratuaren testua.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Arrazoiketa zirriborroa',
            drafting: 'Arrazoiketaren zirriborroa...',
            results_heading: 'AI-ren zirriborroa ({risk_level})',
            tokens_label: '≈ {count} token',
            narrative_label: 'Justifikazio kontakizuna',
            controls_heading: 'Kontrol konpentsatzaileak',
            no_controls: 'Ez da iradokitzen konpentsazio-kontrolik.',
            citations_label: 'Aipamenak',
            accept_label: 'Arrazoiketak onartu',
            reject_label: 'Zirriborroa baztertu',
            accept_hint: 'Onartu erregistroak IAT leinurako mantentzen den zirriborroari buruzko arrazoibidea. Narrazio + kontrolak ez dute automatikoki onarpen-erregistroa eguneratzen; operadorea itsatsi behar da.',
            resolve_summary: 'Ebazpena: {status}',
            error_generic: 'Ezin izan da arrazoiketa idatzi. Saiatu berriro edo egiaztatu arriskua onartzeko egoera.',
        },
        redline_assistant: {
            run_label: 'Lerro gorria liburutegiarekin',
            running: 'Marra gorria martxan...',
            results_heading: 'Lerro gorriko iradokizunak ({count})',
            tokens_label: '≈ {count} token',
            no_suggestions: 'Ez da klausula estandarraren hutsunerik hauteman.',
            jurisdiction_label: 'Jurisdikzioa',
            col_kind: 'Jatorra',
            col_severity: 'Larritasuna',
            col_title: 'Iradokizuna',
            col_excerpt: 'Lotutako zatia',
            col_apply: 'Aplikatu',
            kind_missing: 'Estandarra falta da',
            kind_weaker: 'Estandarra baino ahulagoa',
            kind_jurisdiction: 'Jurisdikzioaren gainjartzea',
            proposed_label: 'Proposatutako testua',
            citations_label: 'Aipamenak',
            accept_label: 'Onartu hautatutakoa',
            reject_label: 'Errefusa txostena',
            partial_label: 'Aplikatu hautaketa',
            accept_hint: 'Aplikatu hautapen-erregistroak aukeratutako iradokizunak IAT leinurako zirriborroan. Ez zaio aldaketa automatikorik aplikatzen kontratuaren testuari — operadoreak marra gorria gidatzen du.',
            resolve_summary: 'Ebazpena: {status} · {count} iradokizun(k) aplikatu dira',
            error_generic: 'Ezin izan da marra gorririk sortu. Saiatu berriro edo egiaztatu kontratuaren egoera.',
            clause_library_link: 'Kudeatu klausulen liburutegia',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Klausula Liburutegia',
            add_label: 'Gehitu klausula',
            edit_label: 'Editatu',
            delete_label: 'Ezabatu',
            slug_label: 'Bareak',
            title_label: 'Izenburua',
            body_label: 'Gorputza',
            jurisdiction_label: 'Jurisdikzioa',
            tags_label: 'Etiketak (komaz bereizita)',
            must_contain_label: 'Beharrezkoak diren markatzaileak (komaz bereizita)',
            severity_label: 'Larritasuna falta denean',
            no_clauses: 'Klausularik ez oraindik. Gehitu bat marra gorriko laguntzailea hazteko.',
            save_label: 'Gorde',
            cancel_label: 'Utzi',
            load_error: 'Ezin izan da klausulen liburutegia kargatu.',
            save_error: 'Ezin izan da klausula gorde.',
            delete_error: 'Ezin izan da klausula ezabatu.',
            delete_confirm: '"{slug}" klausula ezabatu? Hau ezin da desegin.',
        },
        subprocessor_impact: {
            assess_label: 'Eragina ebaluatu',
            assessing: 'Eragina ebaluatzen...',
            results_heading: 'Azpiprozesadorearen eragina ({severity})',
            tokens_label: '≈ {count} token',
            risk_delta_label: 'Arrisku delta',
            notification_heading: 'Bezeroaren jakinarazpena',
            subject_label: 'Gaia',
            deadline_label: 'Jakinarazpen-epea',
            body_label: 'Jakinarazpen-organoa',
            required_fields_label: 'Jakinarazpenaren eremuak (28. art.(2))',
            mitigation_heading: 'Arintzeko urratsak',
            citations_label: 'Aipamenak',
            accept_label: 'Ebaluazioa onartu',
            reject_label: 'Ebaluazioa baztertu',
            accept_hint: 'Onartzeak bezeroei jakinarazteko zeregin bat sortuko du SLA mugaeguna delarik.',
            resolve_summary: 'Ebazpena: {status}{task}',
            error_generic: 'Ezin izan da azpiprozesadorearen aldaketa hau ebaluatu. Saiatu berriro edo egiaztatu aldaketa-erregistroa.',
        },
        onboarding_evidence: {
            collect_label: 'Exekutatu AI froga-bilketa',
            collecting: 'Frogak biltzen…',
            results_heading: 'Froga bilketa ({tier})',
            tokens_label: '≈ {count} token',
            findings_heading: 'Dauden frogak',
            missing_heading: 'Falta diren artefaktuak',
            no_findings: 'Ez da lehendik dagoen frogarik detektatu.',
            no_missing: 'Arrisku maila honetarako ez da beharrezko artefakturik falta.',
            next_step_label: 'Hurrengo urratsa',
            class_dpa: 'Datuak Tratatzeko Akordioa',
            class_soc2: 'SOC 2 txostena',
            class_iso27001: 'ISO 27001 ziurtagiria',
            class_insurance: 'Ziberaseguru poliza',
            class_incident_history: 'Aurretik gorabehera',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Onartu txostena',
            reject_label: 'Errefusa txostena',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Ezin izan da AI froga-bilketa exekutatu. Saiatu berriro edo egiaztatu barneratzeko kasuaren egoera.',
        },
        no_permission_title: 'Baimen nahikoa ez',
        no_permission_message: 'Ez duzu saltzaileen arriskuen kudeaketan sartzeko baimenik.',
        unknown_vendor: 'Saltzaile ezezaguna',
        nav: {
            dashboard: 'Arrisku aginte-mahaia',
            scoring: 'Arrisku-puntuazioa',
            questionnaires: 'Galdetegiak',
            contracts: 'Kontratuak',
            gates: 'Onespen-ateak',
            reviews: 'Hornitzaile-ebaluazioak',
            signals: 'Arrisku-seinaleak',
            reassessments: 'Berrebaluazioak',
            portfolio: 'Arrisku-zorroa',
            acceptances: 'Arrisku-onarpena',
            review_routing: 'Berrikuste-bideratzea',
            onboarding_cases: 'Onboarding-kasuak',
            sub_processor_register: 'Azpiprozesatzaileen erregistroa',
        },
        dashboard: {
            title: 'Hornitzaile-arrisku aginte-mahaia',
            page_title: 'Saltzaileen arriskuen panela',
            loading: 'dashboard datu kargatzen...',
            risk_tier_header: 'Arrisku Maila',
            count_header: 'zenbaketa',
            percentage_header: 'Ehunekoa',
            sections_nav: 'Saltzaileen arrisku-atalak',
            urgent_expiry: 'Premiazkoa: 14 eguneko epean iraungiko da',
            no_previous_tier: 'Ez dago aurreko mailarik',
            view_as_chart: 'Ikusi grafiko gisa',
            view_as_table: 'Ikusi taula gisa',
            showing_chart: 'Grafiko-ikuspegia erakusten',
            showing_table: 'Datu-taula ikuspegia',
            load_error: 'Huts egin du: load hornitzaile arrisku dashboard. Saiatu berriro.',
            risk_distribution: 'Arrisku Distribution Ikuspegi orokorra',
            recent_tier_changes: 'Recent Maila Changes',
            no_tier_changes: 'Ez dago azken maila-aldaketarik.',
            review_kpis: 'Berrikusketa KPIs',
            total_vendors_reviewed: 'Berrikusitako hornitzaileak guztira',
            reviews_by_outcome: 'Berrikusketak by Outcome',
            contract_coverage: 'Kontratu Estaldura',
            total_vendors: 'Hornitzaile guztira',
            with_active_dpa: 'DPA aktiboarekin',
            without_dpa: 'DPA gabe',
            dpa_coverage: 'DPA Estaldura',
            active_contracts_by_type: 'Kontratu aktiboak motaren arabera',
            expiring_contracts: 'Expiring Kontratuak (Hurrengoa 90 egun)',
            no_expiring_contracts: 'No contracts expiring within the next 90 egun.',
            vendor: 'Hornitzaile',
            previous_tier: 'Aurrekoa Maila',
            current_tier: 'Unekoa Maila',
            changed_at: 'Aldatua',
            expires_at: 'Iraungitze-data',
            days_remaining: 'egun geratzen',
            days: 'egun',
            vendors_label: 'hornitzaileak',
            no_data: 'No datu',
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
            expiring_acceptances_heading: 'Epea gainditzear dauden arrisku-onespenak',
            expiring_30d_label: '30 egun barru',
            expiring_60d_label: '60 egun barru',
            expiring_90d_label: '90 egun barru',
        },
        scoring: {
            title: 'Arrisku-puntuazioa',
            page_title: 'Saltzaileen Arriskuaren Puntuazioa',
            filter_scores: 'Iragazi arriskuen puntuazioak',
            filter_by_tier: 'Iragazi arrisku-mailaren arabera',
            scores_pagination: 'Arrisku puntuazioen orria',
            config_dimensions: 'Puntuazio-dimentsioen konfigurazioa',
            scores_loaded: '{count} arrisku puntuazio kargatu dira',
            weight_must_equal_100: 'Pisu guztira 100 izan behar du (unean: {current})',
            out_of_10: '10etik {tier} baloratua',
            dimension_name: '{index} dimentsioaren izena',
            dimension_weight: '{name} pisua (ehunekoa)',
            must_equal_100: '100 izan behar du',
            config_title: 'Scoring Konfigurazio',
            scores_title: 'Hornitzaileen arrisku-puntuazioak',
            new_score_title: 'Puntuazio a Hornitzaile',
            new: 'Berria Puntuazio',
            vendor_id: 'Hornitzaile IDa',
            risk_tier: 'Arrisku Maila',
            score: 'Puntuazio',
            scored_at: 'Puntuatua',
            no_scores: 'Ez da arrisku scores found. aurkitu',
            all_tiers: 'Maila guztiak',
            load_config_error: 'Huts egin du: load arrisku scoring configuration. Saiatu berriro.',
            update_config: 'Puntuazio-konfigurazioa eguneratu',
            update_config_error: 'Huts egin du: update arrisku scoring configuration. Saiatu berriro.',
            load_scores_error: 'Huts egin du: load hornitzaile arrisku scores. Saiatu berriro.',
            calculate_error: 'Huts egin du: calculate hornitzaile arrisku score. Saiatu berriro.',
            weight: 'weight',
            score_range: 'Puntuazio (1-5)',
            method: 'Metodoa',
        },
        questionnaires: {
            title: 'Galdetegiak',
            page_title: 'Hornitzaileen galdetegiak',
            filter_questionnaires: 'Galdetegi-iragazkiak',
            questionnaire_views: 'Galdetegi-ikuspegiak',
            templates_list: 'Galdetegi-txantiloien zerrenda',
            instances_list: 'Galdetegi-instantzien zerrenda',
            filter_by_status: 'Iragazi galdetegiaren egoeraren arabera',
            templates_per_page: 'Txantiloiak orrialdeko',
            instances_per_page: 'Instantziak orrialdeko',
            select_template: 'Hautatu galdetegi-txantiloia',
            template_category: 'Txantiloi-kategoria',
            templates_tab: 'Txantiloiak',
            instances_tab: 'Instantziak',
            new: 'Galdetegi berria',
            new_template: 'Txantiloi berria',
            new_instance: 'Instantzia berria',
            vendor_id: 'Hornitzailearen IDa',
            template_id: 'Txantiloiaren IDa',
            assigned_to: 'Esleituta',
            due_date: 'Epemuga',
            responses: 'Erantzunak',
            risk_score: 'Arrisku-puntuazioa',
            no_questionnaires: 'Ez da galdetegirik aurkitu.',
            load_templates_error: 'Ezin izan dira galdetegi-txantiloiak kargatu.',
            load_instances_error: 'Ezin izan dira galdetegiak kargatu.',
            create_template_error: 'Ezin izan da galdetegi-txantiloia sortu.',
            initiate_error: 'Ezin izan da galdetegia abiatu.',
            submit_error: 'Ezin izan da galdetegia bidali.',
            review_error: 'Ezin izan da galdetegia berrikusi.',
            resubmit: 'Berriz bidali',
            approve_error: 'Ezin izan da galdetegia onartu.',
            reject_error: 'Ezin izan da galdetegia ukatu.',
            resubmit_error: 'Ezin izan da galdetegia berriz bidali.',
            templates_loaded: '{count} galdetegi-txantiloi kargatuta',
            instances_loaded: '{count} galdetegi-instantzia kargatuta',
            fill_responses_cta: 'Bete erantzunak',
            fill_responses_title: 'Bete galdetegiaren erantzunak',
            template_missing: 'Txantiloia ez da aurkitu.',
            responses_saved: 'Erantzunak gorde dira.',
            load_template_error: 'Ezin izan da txantiloia kargatu.',
            version_history: 'Bertsio-historia',
            download_previous_version: 'Deskargatu aurreko bertsioa',
            trigger: 'Abiarazlea',
            template_label: 'Txantiloia',
            reviewer: 'Berrikuslea',
            reviewer_required: '{trigger} galdetegietarako berrikusle bat esleitu behar da (eginkizunen bereizketa).',
            select_option: 'Hautatu aukera bat',
            multi_choice_hint: 'Hautatu dagozkion guztiak',
            category_initial_onboarding: 'Hasierako sarrera',
            category_annual_review: 'Urteko berrikuspena',
            category_incident_triggered: 'Gertakariak eraginda',
            category_contract_renewal: 'Kontratu-berritzea',
            template_editor: {
                page_title: 'Editatu galdetegi-txantiloia',
                preview_page_title: 'Aurreikusi galdetegi-txantiloia',
                edit_structure: 'Editatu egitura',
                preview: 'Aurrebista',
                actions: 'Ekintzak',
                back_to_list: 'Itzuli txantiloien zerrendara',
                load_error: 'Ezin izan da txantiloia kargatu.',
                save_error: 'Ezin izan da txantiloiaren egitura gorde.',
                saved: 'Txantiloiaren egitura gorde da.',
                sections_heading: 'Atalak',
                questions_heading: 'Galderak',
                add_section: 'Gehitu atala',
                edit_section: 'Editatu atala',
                delete_section: 'Ezabatu atala',
                move_section_up: 'Mugitu atala gora',
                move_section_down: 'Mugitu atala behera',
                confirm_delete_section: 'Ezabatu "{title}" atala? Bere galdera guztiak kenduko dira.',
                section_title: 'Atalaren izenburua',
                section_description: 'Atalaren deskribapena',
                section_id: 'Atalaren IDa',
                section_id_hint: 'Automatikoki sortua, esplizituki ezarri ezean.',
                no_sections: 'Oraindik ez dago atalik. Gehitu atal bat hasteko.',
                no_questions: 'Atal honek oraindik ez du galderarik.',
                add_question: 'Gehitu galdera',
                edit_question: 'Editatu galdera',
                delete_question: 'Ezabatu galdera',
                move_question_up: 'Mugitu galdera gora',
                move_question_down: 'Mugitu galdera behera',
                confirm_delete_question: 'Ezabatu "{text}" galdera?',
                question_text: 'Galderaren testua',
                question_type: 'Galdera-mota',
                question_id: 'Galderaren IDa',
                evidence_required: 'Froga beharrezkoa',
                risk_weight: 'Arrisku-pisua',
                scoring_map: 'Puntuazio-mapa',
                scoring_map_json_hint: 'JSON objektua, erantzun-balioak puntuazio numerikoekin lotzen dituena.',
                scoring_map_invalid_json: 'Puntuazio-mapak baliozko JSON izan behar du.',
                options_heading: 'Erantzun-aukerak',
                add_option: 'Gehitu aukera',
                remove_option: 'Kendu aukera',
                option_label: 'Etiketa',
                option_value: 'Balioa',
                option_risk_weight: 'Arrisku-pisua',
                min_options_error: 'Aukera anitzeko galderek gutxienez 2 aukera izan behar dituzte.',
                option_label_required: 'Aukera bakoitzak etiketa bat behar du.',
                option_value_required: 'Aukera bakoitzak balio bat behar du.',
                option_value_unique: 'Aukeren balioek bakarrak izan behar dute.',
                branching_rules_heading: 'Adarkatze-arauak',
                branching_rules_hint: 'Erakutsi edo ezkutatu galdera hau aurreko galdera baten erantzunaren arabera.',
                add_branching_rule: 'Gehitu adarkatze-araua',
                remove_branching_rule: 'Kendu adarkatze-araua',
                branching_condition_question: 'Galdera bada',
                branching_condition_value: 'eta balioa',
                branching_action: 'orduan',
                branching_action_show: 'erakutsi galdera hau',
                branching_action_hide: 'ezkutatu galdera hau',
                branching_select_condition_question: 'Hautatu aurreko galdera',
                branching_select_condition_value: 'Hautatu balioa',
                save: 'Gorde egitura',
                saving: 'Gordetzen...',
                cancel: 'Utzi',
                close: 'Itxi',
                question_text_required: 'Galderaren testua beharrezkoa da.',
                section_title_required: 'Atalaren izenburua beharrezkoa da.',
                type_single_choice: 'Aukera bakarra',
                type_multi_choice: 'Aukera anitzak',
                type_free_text: 'Testu librea',
                type_numeric: 'Zenbakizkoa',
                type_date: 'Data',
                type_file_upload: 'Fitxategi-igoera',
                type_yes_no: 'Bai / Ez',
                preview_heading: 'Txantiloiaren aurrebista',
                preview_subtitle: 'Galdetegia hornitzaileari nola agertzen zaion irakurtzeko soilik den aurrebista.',
                preview_no_content: 'Txantiloi honek oraindik ez du atalik edo galderarik.',
                preview_branching_notice: 'Adarkatze-arauak betetze-unean ebaluatzen dira; aurrebistak galdera guztiak erakusten ditu.',
            },
            review_modal: {
                title: 'Berrikusi galdetegiaren erantzunak',
                open_cta: 'Berrikusi',
                loading: 'Galdetegia kargatzen...',
                template_missing: 'Txantiloia ez da aurkitu.',
                load_error: 'Ezin izan da galdetegia kargatu berrikusketarako.',
                annotation_heading: 'Oharrak',
                annotation_placeholder: 'Gehitu berrikuslearen oharra galdera honetarako...',
                annotation_status_label: 'Oharraren egoera',
                annotation_status_acceptable: 'Onargarria',
                annotation_status_concern: 'Kezka',
                annotation_status_requires_action: 'Ekintza behar du',
                review_comments_label: 'Berrikusketaren iruzkin orokorrak',
                review_comments_placeholder: 'Laburtu zure berrikusketaren aurkikuntzak...',
                justification_label: 'Erabakiaren justifikazioa',
                justification_placeholder: 'Azaldu zure erabakiaren arrazoiketa...',
                reject_requires_comments: 'Galdetegi bat ukatzeko berrikusketa-iruzkinak beharrezkoak dira.',
                decision_heading: 'Erabakia',
                decision: {
                    approved: 'Onartu',
                    rejected: 'Ukatu',
                    additional_info_requested: 'Eskatu informazio osagarria',
                },
                submit: 'Bidali berrikusketa',
                submitting: 'Berrikusketa bidaltzen...',
                cancel: 'Utzi',
                submit_success: 'Berrikusketa bidali da.',
                submit_error: 'Ezin izan da berrikusketa bidali.',
                separation_of_duties_error: 'Ezin duzu berrikusi zuri betetzeko esleitu zaizun galdetegirik. Eginkizunen bereizketak berrikusle desberdina eskatzen du.',
                decision_required: 'Mesedez, hautatu berrikusketa-erabaki bat.',
                announce_opened: 'Berrikusketa-leihoa irekita.',
                announce_submitted: 'Berrikusketa bidali da.',
            },
            instance_detail: {
                submitted_at: 'Bidalita',
                reviewed_at: 'Berrikusita',
                reviewer: 'Berrikuslea',
                review_decision: 'Erabakia',
                review_comments: 'Berrikusketa-iruzkinak',
                decision_approved: 'Onartua',
                decision_rejected: 'Ukatua',
                decision_additional_info_requested: 'Informazio gehiago eskatuta',
                decision_pending: 'Berrikusketa zain',
                expand_comments: 'Erakutsi iruzkin osoak',
                collapse_comments: 'Ezkutatu iruzkin osoak',
                no_reviewer: 'Ez da berrikuslerik esleitu',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Zirriborroa',
                in_progress: 'Abian',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Onartua',
                rejected: 'Baztertua',
                expired: 'Iraungita',
            },
            export: {
                portfolio_label: 'Esportatu zorroa',
                instance_label: 'Esportatu',
                portfolio_success: 'Zorroaren esportazioa prest dago.',
                instance_success: 'Galdetegiaren esportazioa prest dago.',
                portfolio_error: 'Ezin izan da galdetegi-zorroa esportatu.',
                instance_error: 'Ezin izan da galdetegia esportatu.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Galdera batzuk adarkatze-arauek ezkutatu dituzte.',
                cycle_warning: 'Adarkatze-arauetan ziklo bat antzeman da. Eragindako galderak ikusgai geratuko dira lehenespenez.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Kontratuak',
            page_title: 'Saltzaileen Kontratuak',
            new: 'Berria Kontratu',
            vendor_id: 'Hornitzaile IDa',
            filter_contracts: 'Iragazi kontratuak',
            filter_by_document_type: 'Iragazi dokumentu motaren arabera',
            filter_by_status: 'Iragazi kontratuaren egoeraren arabera',
            per_page: 'Orrialde bakoitzeko kontratuak',
            expired_notice: 'Kontratu hau iraungi da',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Dokumentu mota',
            expiry_date: 'Expiry Data',
            no_contracts: 'Ez da contracts found. aurkitu',
            all_document_types: 'Dokumentu mota guztiak',
            load_error: 'Huts egin du: load contracts. Saiatu berriro.',
            create_error: 'Huts egin du: create contract. Saiatu berriro.',
            delete_error: 'Huts egin du: delete contract. Saiatu berriro.',
            update_error: 'Huts egin du: update egoera. Saiatu berriro.',
            confirm_delete: 'Ezabatu contract "{title}"?',
            renewal_date: 'Renewal Data',
            effective_date: 'Effective Data',
            parties: 'Alderdiak',
            summary: 'Laburpena',
            n_contracts: '{count} kontratu',
            n_contract: '{count} kontratu',
            contracts_loaded: '{count} kontratu kargatuta',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Segurtasun eranskina',
            doc_type_scc_annex: 'SCC eranskina',
            doc_type_sub_processor_list: 'Azpiprozesatzaileen zerrenda',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Bestelakoa',
            status_draft: 'Zirriborroa',
            status_under_negotiation: 'Negoziazioan',
            status_pending_signature: 'Sinadura zain',
            status_active: 'Aktiboa',
            status_expired: 'Iraungitakoa',
            status_terminated: 'Amaitu da',
            status_superseded: 'Ordezkatua',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Onespen-ateak',
            page_title: 'Ateen Ebaluazioak',
            new: 'Berria Gate Evaluation',
            vendor_id: 'Hornitzaile IDa',
            gate_name: 'Gate Izena',
            outcome: 'Emaitza',
            evaluator: 'Ebaluatzailea',
            no_gates: 'Ez da gate evaluations found. aurkitu',
            load_error: 'Huts egin du: load gate evaluations. Saiatu berriro.',
            evaluate_error: 'Huts egin du: evaluate hornitzaile gate. Saiatu berriro.',
            detail_error: 'Huts egin du: fetch evaluation detail. Saiatu berriro.',
            override_error: 'Huts egin du: override gate evaluation. Saiatu berriro.',
            criteria_results: 'Irizpide-emaitzak',
            evaluate_form: 'Ebaluatu saltzaileen atea',
            per_page: 'Orrialde bakoitzeko ebaluazioak',
            passed: 'Gainditua',
            failed: 'Huts egin',
            override: 'Gainidatzi',
            override_justification: 'Gainidatzi Justifikazio',
            override_placeholder: 'Sartu justifikazio for overriding this gate evaluation',
            approval_id: 'Onartze IDa',
            overall_passed: 'Gainditua',
            overall_failed: 'Huts egina',
            overall_overridden: 'Gainidatzia',
            evaluations_loaded: '{count} ate-ebaluazio kargatuta',
            create_approval_cta: 'Sortu onespena',
        },
        reviews: {
            title: 'Hornitzaile-ebaluazioak',
            page_title: 'Saltzaileen Iritziak',
            new: 'Berria Berrikusketa',
            vendor_id: 'Hornitzaile IDa',
            filter_by_outcome: 'Iragazi berrikuspenaren emaitzaren arabera',
            per_page: 'Orrialde bakoitzeko iritziak',
            kpi_summary: 'Berrikusi KPI laburpena',
            review_outcome_label: 'Berrikusi emaitza',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Editatu berrikuspenaren emaitza',
            reviews_loaded: '{count} saltzaileen iritziak kargatu dira',
            reviewer: 'Berrikuskatzaile',
            review_type: 'Berrikusketa Mota',
            outcome: 'Emaitza',
            findings: 'Aurkikuntzak',
            no_reviews: 'Ez da hornitzaile reviews found. aurkitu',
            load_kpis_error: 'Huts egin du: fetch hornitzaile review KPIs. Saiatu berriro.',
            load_error: 'Huts egin du: fetch hornitzaile reviews. Saiatu berriro.',
            update_error: 'Ezin izan da hornitzailearen berrikuspena eguneratu',
            create_error: 'Huts egin du: create hornitzaile review. Saiatu berriro.',
            total_reviewed: 'Berrikusitako hornitzaileak guztira',
            total_reviews: 'Berrikusketa guztira',
            satisfactory: 'Nahikoa',
            concerns_identified: 'Identifikatutako kezkak',
            remediation_required: 'Konponketa Beharrezkoa',
            escalated: 'Eskalatuta',
            program_id: 'Programa ID',
            period_id: 'Aldi ID',
            task_id: 'Zeregin ID',
            recommendations: 'Gomendioak',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Arrisku-seinaleak',
            page_title: 'Arrisku Seinaleak',
            new_signal: 'Arrisku-seinale berria',
            signal_type: 'Seinale mota',
            severity: 'Larritasuna',
            description: 'Deskribapena',
            source: 'Iturria',
            vendor_id: 'Saltzailearen IDa',
            certification_expiry: 'Ziurtapen-iraungitzea',
            breach_notification: 'Urraketa-jakinarazpena',
            financial_instability: 'Finantza-ezegonkortasuna',
            compliance_change: 'Betetze-aldaketa',
            custom: 'Pertsonalizatua',
            active: 'Aktiboa',
            acknowledged: 'Onartua',
            resolved: 'Ebatzita',
            no_signals: 'Ez da arrisku-seinalerik aurkitu',
            source_external_feed: 'Kanpo-iturria',
            source_manual: 'Eskuzkoa',
            source_integration: 'Integrazioa',
            source_regulatory: 'Araudia',
            source_media: 'Hedabideak',
            trigger_reassessment_prompt: 'Seinale honek larritasun handia du. Orain hornitzailearen berrazterketa hasi?',
            reassessment_triggered: 'Berrazterketa abian.',
            reassessment_trigger_error: 'Ezin izan da berrazterketa abiatu.',
        },
        reassessments: {
            title: 'Berrebaluazio-zereginak',
            page_title: 'Berriz ebaluatzeko atazak',
            trigger: 'Berrebaluazioa abiarazi',
            trigger_success: 'Berrebaluazioa arrakastaz abiarazi da',
            reason: 'Arrazoia',
            deadline: 'Epemuga',
            assigned_to: 'Esleitua',
            vendor_id: 'Saltzailearen IDa',
            outcome: 'Emaitza',
            new_risk_level: 'Arrisku-maila berria',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Berrebaluazioa osatu',
            complete_success: 'Berrebaluazioa arrakastaz osatu da',
            pending: 'Zain',
            in_progress: 'Abian',
            completed: 'Osatua',
            cancelled: 'Bertan behera utzita',
            no_tasks: 'Ez da berrebaluazio-zereginerik aurkitu',
            risk_trend: 'Arrisku-joera',
            notes: 'Oharrak',
        },
        portfolio: {
            title: 'Arrisku-zorroa',
            page_title: 'Arrisku-zorroa',
            dashboard: 'Zorroaren aginte-mahaia',
            vendor_id: 'Saltzailearen IDa',
            total_vendors: 'Hornitzaile guztira',
            average_score: 'Batez besteko arrisku-puntuazioa',
            high_risk_count: 'Arrisku handiko hornitzaileak',
            trend_direction: 'Joera-norabidea',
            improved: 'Hobetua',
            stable: 'Egonkorra',
            degraded: 'Okertua',
            vendor_trend: 'Hornitzaile-joera',
            outstanding_questionnaires: 'Erantzun gabeko galdetegiak',
            overdue_assessments: 'Atzeratutako ebaluazioak',
            no_outstanding: 'Ez dago erantzun gabeko galdegirik',
            no_overdue: 'Ez dago atzeratutako ebaluaziorik',
            due_date: 'Epemuga',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Arrisku-onarpen Erregistroa',
            page_title: 'Arriskuen Onarpen Erregistroa',
            new_acceptance: 'Arrisku-onarpen berria',
            vendor_id: 'Saltzailearen IDa',
            risk_level: 'Arrisku-maila',
            conditions: 'Baldintzak',
            justification: 'Justifikazioa',
            expiry_date: 'Iraungitze-data',
            create_success: 'Arrisku-onarpena arrakastaz sortu da',
            review_condition: 'Baldintza berrikusi',
            condition_met: 'Baldintza beteta',
            condition_not_met: 'Baldintza bete gabe',
            review_success: 'Baldintza arrakastaz berrikusi da',
            export_register: 'Erregistroa esportatu',
            active: 'Aktiboa',
            expired: 'Iraungita',
            revoked: 'Errebokatua',
            no_acceptances: 'Ez da arrisku-onarpenik aurkitu',
            met_count: '{met}/{total} elkartu dira',
        },
        review_routing: {
            title: 'Berrikuste-bideratzea',
            page_title: 'Berrikusi bideratzea',
            route_to_reviewers: 'Berrikusleei bidali',
            route_success: 'Berrikusleei arrakastaz bidali da',
            vendor_id: 'Saltzailearen IDa',
            reviewer_id: 'Berrikuslearen IDa',
            reviewer_role: 'Berrikuslearen rola',
            questionnaire_id: 'Galdegiaren IDa',
            review_status: 'Berrikuste-egoera',
            reviewers_label: 'Ebaluatzaileak (user_id, lerro bakoitzeko eginkizuna)',
            pending: 'Zain',
            in_review: 'Berrikusten',
            completed: 'Osatua',
            no_routing: 'Ez da berrikuste-bideratzerik aurkitu',
            primary_reviewer: 'Berrikuskle nagusia',
            secondary_reviewer: 'Bigarren berrikusklea',
            approver: 'Onartzailea',
        },
        onboarding: {
            title: 'Hornitzaileen Onboarding-kasuak',
            page_title: 'Hornitzaileen Onboarding-kasuak',
            new_case: 'Onboarding-kasu berria',
            case_detail: 'Onboarding-kasua',
            open_mps_dossier_filing: 'Ireki MPS-aurkezpena',
            dossier_filing_description: 'Sortu MPS-aurkezpen berri bat onboarding kasu honetatik aurre-betea. Aurkezpena kasuari lotuko zaio.',
            dossier_filing_dpia_id: 'DPIA ID',
            dossier_filing_dpia_id_placeholder: 'adibidez dpia_01TEST000000000000000001',
            dossier_filing_title: 'Aurkezpenaren izenburua',
            dossier_filing_form_type: 'Inprimaki mota',
            dossier_filing_submit: 'Sortu aurkezpena',
            dossier_filing_validation_error: 'DPIA ID eta izenburua beharrezkoak dira.',
            dossier_filing_created: 'MPS-aurkezpena sortu da',
            dossier_filing_error: 'Ezin izan da MPS-aurkezpena sortu.',
            linked_questionnaire_due_date: 'Azken data',
            linked_questionnaire_assignee: 'Esleituta',
            linked_questionnaire_submitted_at: 'Aurkezpena',
            score_last_scored: 'Azken puntuazioa',
            score_dimensions: 'Dimentsioak',
            linked_contract_document_type: 'Dokumentu mota',
            linked_contract_effective_date: 'Indarrean jartzeko data',
            linked_contract_expiry_date: 'Iraungitze-data',
            linked_sub_processor_countries: 'Prozesatze-herrialdeak',
            linked_sub_processor_last_assessment: 'Azken ebaluazioa',
            linked_sub_processor_next_assessment: 'Hurrengo ebaluazioa',
            case_id: 'Kasu-IDa',
            vendor: 'Hornitzailea',
            case_type: 'Kasu mota',
            service_description: 'Zerbitzu-deskribapena',
            data_categories: 'Datu-kategoriak',
            systems_accessed: 'Sartutako sistemak',
            jurisdiction: 'Jurisdikzioa',
            hosting_model: 'Ostatze-eredua',
            controller_role: 'Arduradun/Tratatzaile-rola',
            status_pipeline: 'Egoera-kanalizazioa',
            tabs_label: 'Onboarding-kasu atalak',
            per_page: 'Kasuak orriko',
            filter_cases: 'Onboarding-kasuak iragazi',
            cases_count: 'kasu',
            cases_loaded: 'onboarding-kasu kargatuta',
            no_cases: 'Ez da onboarding-kasurik aurkitu.',
            vendor_lookup_placeholder: 'Sartu hornitzaile-IDa',
            data_categories_placeholder: 'adib., datu_pertsonalak, finantzak, osasuna',
            systems_placeholder: 'adib., CRM, ERP, GR Sistema',
            status_open: 'Irekia',
            status_in_progress: 'Abian',
            status_awaiting_approval: 'Onarpenaren zain',
            status_approved: 'Onartua',
            status_remediation_required: 'Konponketa behar du',
            status_closed: 'Itxia',
            type_new_vendor: 'Hornitzaile berria',
            type_service_change: 'Zerbitzu-aldaketa',
            type_renewal: 'Berrikuntza',
            type_reassessment: 'Berrebaluazioa',
            tab_overview: 'Ikuspegi orokorra',
            tab_due_diligence: 'Arretazko azterketa',
            tab_security_assessment: 'Segurtasun-ebaluazioa',
            tab_contractual_controls: 'Kontratu-kontrolak',
            tab_sub_processors: 'Azpiprozesatzaileak',
            tab_approval_gates: 'Onarpen-ateak',
            tab_evidence: 'Ebidentzia',
            tab_placeholder: 'Atal honen edukia onboarding-prozesuan beteko da.',
            completion_not_started: 'Hasi gabe',
            completion_in_progress: 'Abian',
            completion_completed: 'Osatua',
            completion_requiring_attention: 'Arreta behar du',
            gate_name: 'Ate-izena',
            approver: 'Onartzailea',
            decision: 'Erabakia',
            comments: 'Iruzkinak',
            decide: 'Erabakia hartu',
            decision_approved: 'Onartua',
            decision_rejected: 'Baztertua',
            decision_deferred: 'Atzeratua',
            decision_pending: 'Pending',
            no_approvals: 'Ez dago onarpen-aterik kasu honetarako konfiguratuta.',
            approval_success: 'Onarpen-erabakia behar bezala erregistratu da',
            approval_error: 'Ezin izan da onarpen-erabakia erregistratu',
            transition_status: 'Egoera-trantsizioa',
            confirm_transition: 'Kasua "{from}"-tik "{to}"-ra aldatu?',
            transition_success: 'Kasu-egoera behar bezala aldatu da',
            transition_error: 'Ezin izan da kasu-egoera aldatu',
            create_success: 'Onboarding-kasua behar bezala sortu da',
            create_error: 'Ezin izan da onboarding-kasua sortu',
            load_error: 'Ezin izan dira onboarding-kasuak kargatu',
            detail_error: 'Ezin izan dira kasu-xehetasunak kargatu',
            export: 'Esportatu',
            export_error: 'Ezin izan da onboarding-kasua esportatu',
            evidence_tab_description: 'Igo eta esportatu ebidentziak harrera-kasu honetarako.',
            upload_evidence_label: 'Igo ebidentzia',
            attached_evidence_heading: 'Erantsitako ebidentziak',
            no_evidence_attached: 'Oraindik ez dago ebidentziarik erantsita.',
            export_bundle_button: 'Esportatu ebidentzia-sorta',
            load_attachments_error: 'Ezin izan dira ebidentziak kargatu.',
            linked_questionnaires_heading: 'Estekatutako galdetegiak',
            no_linked_questionnaires: 'Ez dago estekatutako galdetegirik.',
            linked_scores_heading: 'Estekatutako puntuazioak',
            no_linked_scores: 'Ez dago estekatutako puntuaziorik.',
            latest_score_label: 'Azken puntuazioa',
            linked_contracts_heading: 'Estekatutako kontratuak',
            no_linked_contracts: 'Ez dago estekatutako kontraturik.',
            linked_sub_processors_heading: 'Estekatutako azpiprozesadoreak',
            no_linked_sub_processors: 'Ez dago estekatutako azpiprozesadorerik.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Osatuta markatu',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Azpiprozesatzaileen Erregistroa',
            page_title: 'Azpiprozesatzaileen Erregistroa',
            new_record: 'Azpiprozesatzaile berria',
            edit_record: 'Azpiprozesatzailea editatu',
            onboarding_case: 'Onboarding-kasua',
            services: 'Emandako zerbitzuak',
            data_categories: 'Datu-kategoriak',
            processing_countries: 'Tratamendu-herriak',
            dpa_chain_status: 'DPA kate-egoera',
            certifications: 'Ziurtagiriak',
            last_assessment: 'Azken ebaluazioa',
            next_assessment: 'Hurrengo ebaluazioa',
            change_history: 'Aldaketa-historia',
            history: 'Historia',
            changed_by: 'Aldatua',
            comma_separated: 'Komaz bereizitako balioak',
            filter_records: 'Erregistroak iragazi',
            filter_by_dpa: 'DPA kate-egoeraren arabera iragazi',
            all_dpa_statuses: 'DPA egoera guztiak',
            per_page: 'Erregistroak orriko',
            records_count: 'azpiprozesatzaile',
            records_loaded: 'erregistro kargatuta',
            no_records: 'Ez da azpiprozesatzailerik aurkitu.',
            no_history: 'Ez dago aldaketa-historiarik.',
            dpa_active: 'Aktiboa',
            dpa_pending: 'Zain',
            dpa_expired: 'Iraungita',
            dpa_not_required: 'Ez da beharrezkoa',
            create_success: 'Azpiprozesatzailea behar bezala sortu da',
            create_error: 'Ezin izan da azpiprozesatzailea sortu',
            update_success: 'Azpiprozesatzailea behar bezala eguneratu da',
            update_error: 'Ezin izan da azpiprozesatzailea eguneratu',
            load_error: 'Ezin izan da erregistroa kargatu',
            history_error: 'Ezin izan da aldaketa-historia kargatu',
            export_csv: 'CSV esportatu',
            export_json: 'JSON esportatu',
            export_error: 'Ezin izan dira datuak esportatu',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orkestrazio aginte-mahaia',
            runs: 'Orkestrazio-exekuzioak',
            tasks: 'Exekuzio-atazak',
            templates: 'Egiaztapen-zerrendako txantiloiak',
            vendor_requests: 'Hornitzaile-eskaerak',
            proof_chain: 'Froga-katea',
            dsr_integration: 'DSR integrazioa',
            vendor_verification: 'Hornitzaile-egiaztapena',
            completeness: 'Osotasun-panela',
        },
        dashboard: {
            title: 'Orkestrazio aginte-mahaia',
            loading: 'dashboard datu kargatzen...',
            active_runs: 'Exekuzio aktiboak',
            completion_rate: 'Completion Tasa',
            avg_completion_time: 'Avg Completion Ordua',
            overdue_tasks: 'Atzeratuta Zereginak',
            vendor_compliance: 'Hornitzaile-betetze',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Recent Exekutatu',
            no_recent_runs: 'Ez dago azken orkestraziorik.',
            run_id: 'Exekutatu ID',
            ticket_id: 'Tiketa ID',
            progress: 'Aurrerapena',
            systems: 'Sistemak',
            vendors: 'Hornitzaileak',
            load_error: 'Ezin izan da kargatu orkestrazioaren panela. Mesedez, saiatu berriro.',
            aria_table: 'Azken orkestrazio-lanak',
        },
        runs: {
            title: 'Orkestrazio-exekuzioak',
            new: 'Berria Exekutatu',
            run_id: 'Exekutatu ID',
            ticket_id: 'Tiketa ID',
            progress: 'Aurrerapena',
            systems: 'Sistemak',
            vendors: 'Hornitzaileak',
            data_subject_ref: 'Datu Subject Ref',
            data_categories: 'Datu Kategoriak',
            enable_proof_chain: 'Gaitu Froga Katea',
            start: 'Hasi',
            started: 'Korrika hasi zen',
            mark_ready: 'Mark Prest',
            marked_ready: 'Prest gisa markatu da korrika',
            complete: 'Osatu',
            complete_exceptions: 'Osatu salbuespenekin',
            pause: 'Pausa',
            resume: 'Curriculuma',
            cancel: 'Utzi',
            no_runs: 'Ez da orchestration runs found. aurkitu',
            load_error: 'Ezin izan dira kargatu orkestrazioaren exekuzioak. Mesedez, saiatu berriro.',
            create_error: 'Ezin izan da sortu orkestrazioaren exekuzioa',
            aria_filter_status: 'Iragazi exekuzioaren egoeraren arabera',
            aria_table: 'Orkestrazioa martxan',
            placeholder_ticket_id: 'adibidez, TKT-000123',
            placeholder_data_subject_ref: 'adibidez, DS-REF-001',
            placeholder_data_categories: 'adibidez, personal_data, finantza, osasuna',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Exekuzio-atazak',
            task_id: 'Zeregin ID',
            run_id: 'Exekutatu ID',
            system_name: 'Sistema Izena',
            assigned_to: 'Esleituta To',
            sla_deadline: 'SLA epemuga',
            progress: 'Aurrerapena',
            enter_run_id: 'Sartu exekutatu id to filter...',
            select_run: 'Hautatu korrika bat zereginak ikusteko',
            unassigned: 'Esleitu gabe',
            overdue: 'Atzeratuta',
            assign_task: 'Esleitu Zeregin',
            assignee_id: 'Esleitua ID',
            update_status: 'Eguneratu egoera',
            new_status: 'Berria Egoera',
            no_transitions: 'Ataza hau azken egoeran dago eta ezin da aldatu.',
            select_status_placeholder: 'Hautatu egoera berri bat',
            skip_justification: 'Saltatzeko arrazoia',
            failure_reason: 'Hutsegitearen arrazoia',
            no_tasks: 'Ez da execution tasks found. aurkitu',
            new_task: 'Zeregin berria',
            system_id: 'Sistemaren IDa',
            data_classification: 'Datuen Sailkapena',
            load_error: 'execution tasks kargatzeak huts egin du. Saiatu berriro.',
            assign_error: 'assign task huts egin du',
            status_error: 'Zereginaren egoera eguneratzeak huts egin du',
            no_tasks_for_run: 'Ezabatze-exekuzio honek ez du oraindik exekuzio-atazarik.',
            aria_filter_run_id: 'Iragazi korrika IDaren arabera',
            aria_filter_status: 'Iragazi zereginaren egoeraren arabera',
            aria_table: 'Exekuzio-zereginak',
            view_steps: 'Ikusi urratsak',
            steps_modal_title: 'Exekuzio urratsak',
            step_title: 'Urratsa',
            step_status: 'Urratsaren egoera',
            step_notes: 'Oharrak',
            step_evidence: 'Froga fitxategiak',
            step_evidence_required: 'Urrats honetarako froga behar da',
            step_evidence_missing: 'Oraindik ez da frogarik igo',
            step_save: 'Gorde urratsa',
            step_saved: 'Urratsa eguneratuta',
            step_save_error: 'Ezin izan da urratsa eguneratu',
            create_success: 'Exekuzio-ataza sortuta',
            create_error: 'Ezin izan da exekuzio-ataza sortu',
            step_upload_label: 'Erantsi froga',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Egiaztapen-zerrendako txantiloiak',
            new: 'Berria Txantiloi',
            template_id: 'Txantiloi ID',
            system_type: 'Sistema mota',
            est_duration: 'Est. Iraupena',
            steps: 'Urratsak',
            action_type: 'Ekintza mota',
            instructions: 'Argibideak',
            mandatory: 'Nahitaezkoa',
            add_step: 'Gehitu urratsa',
            step_n: 'Urrats',
            no_templates: 'Ez da checklist txantiloiak found. aurkitu',
            all_system_types: 'Sistema mota guztiak',
            load_error: 'Ezin izan dira kargatu kontrol-zerrenden txantiloiak. Mesedez, saiatu berriro.',
            create_error: 'Ezin izan da sortu egiaztapen-zerrenda txantiloia',
            aria_filter_system_type: 'Iragazi sistema motaren arabera',
            aria_filter_status: 'Iragazi txantiloiaren egoeraren arabera',
            aria_table: 'Kontrol-zerrenda txantiloiak',
            placeholder_name: 'adibidez, Datu-basea ezabatzeko zerrenda',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Txantiloia ondo eguneratu da',
            create_success_msg: 'Txantiloia ondo sortu da',
            update_error: 'Ezin izan da txantiloia eguneratu',
            activate_success: 'Txantiloia ondo aktibatu da',
            activate_error: 'Ezin izan da txantiloia aktibatu',
            archive_success: 'Txantiloia ondo artxibatu da',
            archive_error: 'Ezin izan da txantiloia artxibatu',
            clone_success: 'Txantiloia ondo klonatu da',
            clone_error: 'Ezin izan da txantiloia klonatu',
        },
        vendor_requests: {
            title: 'Hornitzaile Deletion Eskaerak',
            new: 'Berria Eskaera',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Eskaera ID',
            vendor_name: 'Hornitzailearen izena',
            vendor_id: 'Hornitzaile IDa',
            data_subject_ref: 'Datu Subject Ref',
            data_categories: 'Datu Kategoriak',
            legal_basis: 'Oinarri legala',
            sla_days: 'SLA egun',
            requested_completion: 'Eskatutako osaketa',
            communications: 'Komunikazio',
            total_requests: 'Eskaerak guztira',
            no_requests: 'Ez da hornitzaile deletion requests found. aurkitu',
            filter_vendor_id: 'Iragazi by Hornitzaile ID...',
            change_status: 'Aldatu Egoera',
            new_status: 'Egoera Berria',
            actions: 'Ekintzak',
            load_error: 'Ezin izan dira kargatu saltzaileak ezabatzeko eskaerak. Mesedez, saiatu berriro.',
            create_error: 'Ezin izan da sortu hornitzaileak ezabatzeko eskaera',
            dashboard_error: 'Ezin izan da kargatu saltzaileak ezabatzeko panela. Mesedez, saiatu berriro.',
            aria_filter_status: 'Iragazi eskaeraren egoeraren arabera',
            aria_filter_vendor_id: 'Iragazi saltzaile IDaren arabera',
            aria_table: 'Saltzaileak ezabatzeko eskaerak',
            placeholder_vendor_id: 'adibidez, VND-001',
            placeholder_vendor_name: 'adibidez, Acme Analytics',
            placeholder_data_subject_ref: 'adibidez, DS-REF-001',
            placeholder_data_categories: 'adibidez, personal_data, analisiak',
            placeholder_legal_basis: 'adibidez, PDPL 19. artikulua',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Froga Katea Ikusle',
            enter_run_id: 'Sartu exekutatu id...',
            load_chain: 'Kargatu Katea',
            verify_chain: 'Egiaztatu katea',
            verifying: 'Egiaztatzen...',
            chain_valid: 'Katea is valid',
            chain_invalid: 'Katea is broken',
            chain_length: 'Katea Length',
            broken_at_index: 'Hautsita indizean',
            no_proof_records: 'Ez da froga erregistroak found for this run. aurkitu',
            proof_id: 'Froga ID',
            entity_type: 'Entity Mota',
            entity_id: 'Entitate IDa',
            event_hash: 'Gertaera Hash',
            previous_hash: 'Aurrekoa Hash',
            attestation: 'Aitortza',
            attestation_actor: 'Egilea',
            attestation_signed_at: 'Sinatze-data',
            attestation_signature: 'Sinadura',
            attestation_evidence_refs: 'Froga-erreferentziak',
            run_field_label: 'Ezabatze-exekuzioa',
            select_run: 'Hautatu ezabatze-exekuzio bat…',
            aria_select_run: 'Ezabatze-exekuzioa froga-katearen bilaketarako',
            chain_broken_here: 'Katea broken here',
            load_error: 'Ezin izan da kargatu froga-katea. Mesedez, saiatu berriro.',
            verify_error: 'Ezin izan da egiaztatu froga-katea. Mesedez, saiatu berriro.',
            aria_run_id: 'Exekutatu ID froga-katearen bilaketarako',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Aldatu egoera',
            request_type: 'Eskaera mota',
            data_subject_hint: 'Datuak ezabatuko zaizkion datu-subjektuaren identifikatzailea (adib., posta edo bezero-IDa). Normalean hautatutako DSR kasuko eskatzailearekin bat dator.',
            title: 'DSR ezabaketa-integrazioa',
            create_run: 'Sortu DSR ezabaketa-exekuzioa',
            dsr_case_id: 'DSR kasu-IDa',
            data_subject_id: 'Datu-subjektuaren IDa',
            systems: 'Helburu-sistemak',
            route_to_department: 'Sailera bideratu',
            confirm_deletion: 'Ezabaketa berretsi',
            no_runs: 'Ez da DSR ezabaketa-exekuziorik aurkitu',
            status: 'Ezabaketa-egoera',
            deadline: 'Epea',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Hornitzaile-ezabaketa egiaztapena',
            create_followup: 'Jarraipena sortu',
            deadline: 'Berrespen-epemuga',
            escalation_contact: 'Eskalazio-kontaktua',
            escalate: 'Eskalatu',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Ez da hornitzaile-jarraipenik aurkitu',
            vendor_request: 'Saltzaileen eskaera',
            vendor: 'Hornitzailea',
            unknown_vendor: 'Hornitzaile ezezaguna',
            overdue_badge: 'Atzeratua',
            mark_confirmed: 'Markatu berretsi gisa',
            mark_confirmed_for: 'Markatu ezabaketa berretsi gisa {vendor} hornitzailearentzat',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Hornitzaile-ezabaketa berretsi da',
            confirmation_failed: 'Ezin izan da hornitzaile-ezabaketa berretsi',
            select_request: 'Hautatu saltzaileen eskaera bat…',
            escalation_contact_placeholder: 'adib. dpo@vendor.example',
            create_success: 'Hornitzailearen jarraipena ondo sortu da',
            create_failed: 'Ezin izan da hornitzailearen jarraipena sortu',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Hornitzaile-ezabaketa ondo eskalatu da',
            escalation_failed: 'Hornitzaile-ezabaketa eskalatzeak huts egin du',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Ezabaketa-osotasuna',
            check_conflicts: 'Atxikipen-gatazkak egiaztatu',
            no_conflicts: 'Ez da atxikipen-gatazkak aurkitu',
            conflict_resolution: 'Gatazka-ebazpena',
            extend_retention: 'Atxikipena luzatu',
            proceed_deletion: 'Ezabaketarekin jarraitu',
            escalate_conflict: 'Eskalatu',
            resolve: 'Gatazka ebatzi',
            data_subject_id: 'Datu-subjektuaren IDa',
            placeholder_data_subject_id: 'Sartu datu-gaiaren IDa',
            data_subject_hint: 'Ezabaketa egiaztatzen ari zaren pertsonaren identifikatzailea (adib., posta, bezero-IDa edo DSR erreferentzia). Aukeratu ezagutzen den subjektu bat edo idatzi bat.',
            guidance: 'Sartu edo hautatu datu-subjektuaren ID bat, aukeran hautatu sistemak, eta exekutatu egiaztapena ezabaketaren osotasuna eta atxikitze-gatazkak ikusteko.',
            completion: 'Ezabaketaren osatzea',
            pending_items: 'Zain dauden elementuak',
            system: 'Sistema',
            policy: 'Politika',
            retention_end: 'Atxikipen amaiera',
            completed_items: 'Osatutako Elementuak',
            last_review_label: 'Azken berrikuspena',
            days_ago: 'duela egun',
            review_overdue: 'Berrikuspen-zikloa gainditu da. Berrikusi behar da.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Zain',
            planning: 'Planifikazioa',
            ready: 'Prest',
            in_progress: 'Abian',
            paused: 'Pausatua',
            completed: 'Osatua',
            completed_with_exceptions: 'Salbuespenarekin Osatua',
            cancelled: 'Bertan behera',
        },
        task_status_labels: {
            pending: 'Zain',
            assigned: 'Esleitua',
            in_progress: 'Abian',
            awaiting_verification: 'Egiaztapena Zain',
            verified: 'Egiaztatua',
            failed: 'Huts egina',
            skipped: 'Saltatua',
        },
        system_type_labels: {
            database: 'Datu-basea',
            saas_application: 'SaaS Aplikazioa',
            file_storage: 'Fitxategi-biltegiratzea',
            email_system: 'Posta Sistema',
            analytics_platform: 'Analitika Plataforma',
            backup_system: 'Babesk. Sistema',
            vendor_managed: 'Hornitz. Kudeatua',
            custom: 'Pertsonalizatua',
        },
        template_status_labels: {
            draft: 'Zirriborroa',
            active: 'Aktiboa',
        },
        action_type_labels: {
            manual_check: 'Eskuzko Egiaztapena',
            api_call: 'API Deia',
            sql_query: 'SQL Kontsulta',
            script_execution: 'Script Exekuzioa',
            file_deletion: 'Fitxategi Ezabatzea',
            verification: 'Egiaztapena',
            notification: 'Jakinarazpena',
            approval: 'Onarpena',
        },
        request_status_labels: {
            draft: 'Zirriborroa',
            sent: 'Bidalita',
            acknowledged: 'Aitortua',
            in_progress: 'Abian',
            completed: 'Osatua',
            overdue: 'Epez kanpo',
            escalated: 'Eskalatua',
            disputed: 'Eztabaidatua',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Hasiera',
            data_deletion: 'Datu Ezabatzea',
            completion: 'Amaiera',
            verification: 'Egiaztapena',
            rollback: 'Atzera-itzultzea',
        },
        count_labels: {
            one_template: '1 txantiloi',
            n_templates: '{n} txantiloi',
            one_run: '1 exekuzio',
            n_runs: '{n} exekuzio',
            one_task: '1 ataza',
            n_tasks: '{n} ataza',
            one_request: '1 eskaera',
            n_requests: '{n} eskaera',
        },
        aria: {
            completion_rate: 'Amaiera-tasa: {pct}%',
            vendor_compliance: 'Hornitz. betetze: {pct}%',
            run_progress: 'Exekuzio aurrerapena: {pct}%',
            run_progress_systems: 'Aurrerapena: {completed}/{total} sistema prozesatua',
            chain_broken: 'Katea hautsita erregistro honetan',
            chain_valid: 'Katea baliozkoa erregistro honetan',
            chain_unknown: 'Katearen egoera ezezaguna',
            escalate_vendor: 'Eskalatu {id}',
            resolve_conflict: 'Gatazka konpondu {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Ezabatzearen osotasuna gutxienez 365 egunetik behin berrikusi behar da. Atalase hori gainditzen duten erregistroak derrigorrezko berrikuspenerako markatzen dira, araudia betetzen dutela mantentzeko.',
            runs_data_d1_lifecycle_info: 'Orkestrazioaren exekuzio-egoerak DATA-D1 bizi-zikloarekin mapatzen dira: plangintza = katalogoa, prest = sailkatu, in_progress = arauak aplikatu, amaituta = frogak sortu dira, completed_with_exceptions = berrikusi behar da. Trantsizio bakoitza froga-katean erregistratzen da.',
            vendor_record_linkage_info: 'Saltzaile ezabatzeko eskaera bakoitza bere saltzaileen erregistroarekin lotzen da vendor_id bidez. Osatzea berresteko, froga-katearen froga kriptografikoak behar dira eskaera beteta bezala markatu aurretik.',
            templates_internal_classification_info: 'Ezabatzeko kontrol-zerrenda txantiloiak barne-antolakuntzako dokumentu gisa sailkatzen dira. Ez dute arauzko inprimaki-identifikazio ofizialik eramaten eta ez dira arauzko espediente gisa aurkeztu behar.',
            proof_chain_evidence_info: 'Froga-erregistro bakoitzak datuen gaia/kategoria, denbora-zigilua, aktorea, oinarri juridikoa eta SHA-256 hash bat jasotzen ditu. Eremu hauek batera ezabatzeko froga kriptografikoak sortzen dituzte, arauzko auditoretza-eskakizunak betetzen dituztenak.',
            retention_data_law_info: 'Aginte-panel honetako atxikipen-programazioak datu-kategoria bakoitzari esleitutako Datuen Legearen sailkapenetik eratorritakoak dira. Sailkapen bat aldatzen denean, estekatutako atxikipen-arauak automatikoki eguneratzen dira, araudiaren lerrokatzea bermatzeko.',
            dsr_erasure_route_info: 'DSR ezabatze-exekuzioak jatorriko Eskubideen eskaera kasuarekin lotuta daude. Arauzko epea (normalean GDPR 17. art.aren arabera 30 egunekoa) automatikoki jarraitzen da. Atzeratutako kasuak areagotzeko markatzen dira.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Aurkikuntza aginte-mahaia',
            scans: 'Aurkikuntza-eskaneatuak',
            staging_area: 'Prestaketa-eremua',
            dedup_rules: 'Deduplikazio-arauak',
            classification_rules: 'Sailkapen-arauak',
            suggestions: 'Iradokizun-ilara',
            analytics: 'Iradokizun-analitika',
            import_templates: 'Inportazio-txantiloiak',
            connectors: 'Konektore-kudeaketa',
            discovery_cycles: 'Aurkikuntza-zikloak',
            owner_review: 'Jabearen berrikusketa',
            classification_lineage: 'Sailkapen-historia',
        },
        dashboard: {
            title: 'Aurkikuntza aginte-mahaia',
            loading: 'dashboard datu kargatzen...',
            total_scans: 'Eskaneatze guztira',
            active_scans: 'Eskaneatze aktiboak',
            completed_30d: 'Osatuta (30d)',
            failed_30d: 'Huts egin (30d)',
            systems_discovered: 'Sistemak Discovered',
            pending_staged_records: 'Zain Staged Erregistroak',
            recent_scans: 'Recent Eskaneatuak',
            no_recent_scans: 'Ez dago azken eskaneatzerik.',
            scan_id: 'Eskaneatu ID',
            discovered: 'Aurkitua',
            view_all_scans: 'Ikusi eskaneatze guztiak',
            staging_area: 'Prestaketa-eremua',
            suggestion_queue: 'Iradokizun Queue',
            records_reviewed: 'Erregistro berrikusiak',
            records_approved: 'Erregistro onartutakoak',
            exceptions_pending: 'Salbuespen zain',
            view_as_chart: 'Grafiko gisa ikusi',
            view_as_table: 'Taula gisa ikusi',
        },
        scans: {
            title: 'Aurkikuntza-eskaneatuak',
            new: 'Berria Eskaneatu',
            scan_id: 'Eskaneatu ID',
            scan_type: 'Eskaneatze mota',
            progress: 'Aurrerapena',
            discovered: 'Aurkitua',
            no_scans: 'Ez da discovery scans found. aurkitu',
            all_scan_types: 'Eskaneatze mota guztiak',
            source_config: 'Iturria Config (JSON)',
            invalid_json: 'Baliogabea JSON in source config',
            load_error: 'Huts egin du: load discovery scans. Saiatu berriro.',
            create_error: 'Huts egin du: create discovery scan. Saiatu berriro.',
            change_status: 'Aldatu egoera',
            new_status: 'Egoera Berria',
            actions: 'Ekintzak',
            type_endpoint: 'Amaiera-puntua',
            type_spreadsheet_import: 'Kalkulu-orri inportazioa',
            type_cmdb_sync: 'CMDB sinkronizazioa',
            type_cloud_sync: 'Hodei sinkronizazioa',
            status_pending: 'Zain',
            status_running: 'Exekutatzen',
            status_completed: 'Osatua',
            status_failed: 'Huts egin du',
            status_cancelled: 'Bertan behera utzia',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'Eskaneatze mota hau ez dago erabilgarri oraindik. Amaiera-puntuen eskaneatzeak soilik sor daitezke.',
            adapter_not_implemented_warning: 'Oraindik ez dago erabilgarri. Mota honetako eskaneatzeak ezin dira sortu haien euskarria argitaratu arte.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Prestaketa-eremua',
            record_id: 'Erregistro IDa',
            source_scan: 'Iturria Eskaneatu',
            entity_type: 'Entity Mota',
            suggested_name: 'Suggested Izena',
            confidence: 'Konfiantza',
            no_records: 'Ez da staged erregistroak found. aurkitu',
            promote: 'Sustatu',
            discard: 'Baztertu',
            review: 'Berrikusketa',
            merge: 'Bateratu',
            merge_title: 'Batu erregistroa',
            merge_target_id: 'Bateratu Helburu ID',
            reject_reason_placeholder: 'Baztertzeko arrazoia...',
            promotion_status: 'Promotion Egoera',
            dedup_status: 'Dedup Egoera',
            all_source_types: 'Iturri mota guztiak',
            all_promotion_statuses: 'Sustapen egoera guztiak',
            all_dedup_statuses: 'Desduplikazio egoera guztiak',
            load_error: 'Huts egin du: load staged erregistroak. Saiatu berriro.',
            promote_error: 'Huts egin du: promote erregistro. Saiatu berriro.',
            reject_error: 'Huts egin du: reject erregistro. Saiatu berriro.',
            merge_error: 'Huts egin du: merge erregistro. Saiatu berriro.',
            review_error: 'Huts egin du: mark erregistro for review. Saiatu berriro.',
            bulk_promote_error: 'Huts egin du: bulk promote erregistroak. Saiatu berriro.',
            bulk_reject_error: 'Huts egin du: bulk reject erregistroak. Saiatu berriro.',
            service_type_web_server: 'Web zerbitzaria',
            service_type_database: 'Datu-basea',
            service_type_api_gateway: 'API atebidea',
            service_type_storage: 'Biltegiratzea',
            service_type_authentication: 'Autentifikazioa',
            service_type_analytics: 'Analitika',
            service_type_unknown: 'Ezezaguna',
            source_type_endpoint_scan: 'Amaiera-puntuko eskaneoa',
            source_type_spreadsheet: 'Kalkulu-orria',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Hodei inbentarioa',
            promotion_status_new: 'Berria',
            promotion_status_under_review: 'Berrikuspenean',
            promotion_status_promoted: 'Sustatua',
            promotion_status_rejected: 'Baztertua',
            promotion_status_merged: 'Bateratua',
            dedup_status_unmatched: 'Bat etortze gabe',
            dedup_status_potential_match: 'Bat etortze posiblea',
            dedup_status_exact_match: 'Bat etortze zehatza',
            dedup_status_resolved: 'Ebatzita',
            confirm_bulk_reject: 'Ziur zaude hautatutako erregistroak baztertu nahi dituzula?',
            scan_batch_id: 'Lote IDa',
            promote_to_registry_title: 'Sustatu Datuen Erregistrora',
            promote_modal_instructions: 'Datuen Erregistroko elementu bat sortzen du collection_method: aurkikuntza-erregistro honekin behatutako eta jatorriaren lotura. Elementua zirriborro-egoeran hasten da berrikusteko.',
            promote_label_category: 'Kategoria',
            promote_label_name_override: 'Izena (aukerakoa gainidaztea)',
            placeholder_name_override: 'Aurkikuntza-erregistroaren izena/ostalari-izena da lehenetsia',
            promote_label_sensitivity: 'Proposatutako sentsibilitatea',
            inherit_from_category: 'Kategoriatik heredatu',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Deduplikazio-arauak',
            new: 'Berria Arau',
            rule_name: 'Arau Izena',
            match_fields: 'Bat-etortze eremuak',
            threshold: 'Atalase',
            no_rules: 'Ez da dedup rules found. aurkitu',
            seed_defaults: 'Hasieratu Lehenetsia',
            match_type: 'Match Mota',
            similarity_threshold: 'Similarity Atalase',
            field_name: 'Field Izena',
            load_error: 'Huts egin du: load dedup rules. Saiatu berriro.',
            create_error: 'Huts egin du: create dedup rule. Saiatu berriro.',
            toggle_error: 'Huts egin du: toggle dedup rule. Saiatu berriro.',
            seed_error: 'Huts egin du: seed defaults. Saiatu berriro.',
            priority: 'Lehentasuna',
            enabled_status: 'Gaituta',
            disabled_status: 'Ezindua',
            default_badge: 'Lehenetsia',
            enable: 'Gaitu',
            disable: 'Desgaitu',
            match_type_exact: 'Zehatza',
            match_type_fuzzy: 'Lausoa',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP tartea',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Sailkapen-arauak',
            new: 'Berria Arau',
            rule_name: 'Arau Izena',
            pattern: 'Eredua',
            classification: 'Sailkapena',
            no_rules: 'Ez da classification rules found. aurkitu',
            seed_defaults: 'Hasieratu Lehenetsia',
            run_rules_now: 'Exekutatu Arauak orain',
            run_rules_success: '{count} sailkapen-iradokizun berri sortu dira',
            run_rules_no_results: 'Ez da iradokizun berririk sortu — bat datozen arau guztiek iradokizun zain daude dagoeneko',
            run_rules_error: 'Ezin izan dira sailkapen-arauak exekutatu. Mesedez, saiatu berriro.',
            taxonomy_id: 'Taxonomia ID',
            level_id: 'Maila ID',
            condition_logic: 'Baldintza-logika',
            weight: 'Pisua',
            conditions: 'Baldintzak',
            condition_field: 'Eremua',
            condition_operator: 'Operadorea',
            condition_value: 'Balioa',
            effectiveness: 'Eraginkortasuna',
            match_count: 'Bat-etortzeak',
            approval_count: 'Onartzeak',
            rejection_count: 'Baztertzeak',
            filter_taxonomy: 'Iragazi by Taxonomia ID...',
            load_error: 'Huts egin du: load classification rules. Saiatu berriro.',
            create_error: 'Huts egin du: create classification rule. Saiatu berriro.',
            seed_error: 'Huts egin du: seed defaults. Saiatu berriro.',
            toggle_error: 'Huts egin du: toggle rule. Saiatu berriro.',
            priority: 'Lehentasuna',
            enabled_status: 'Gaituta',
            disabled_status: 'Ezindua',
            enable: 'Gaitu',
            disable: 'Desgaitu',
            logic_and: 'ETA',
            logic_or: 'EDO',
            operator_equals: 'Berdina da',
            operator_contains: 'Dauka',
            operator_matches_regex: 'Regex-arekin bat dator',
            operator_in_list: 'Zerrendan',
            operator_not_equals: 'Ez da berdina',
            operator_not_contains: 'Ez dauka',
            enabled_label: 'Gaituta',
            disabled_label: 'Desgaituta',
            taxonomy_label: 'Taxonomia',
            operator_label: 'Eragilea',
            value_label: 'Balioa',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Iradokizun-ilara',
            entity_type: 'Entity Mota',
            suggested_name: 'Suggested Izena',
            source: 'Iturria',
            confidence: 'Konfiantza',
            accept: 'Onartu',
            reject: 'Baztertu',
            no_suggestions: 'Ez dago iradokizunik ilaran.',
            modify_approve: 'Modify & Onartu',
            delegate: 'Delegatu',
            delegate_user_id: 'Delegatu Erabiltzaile ID',
            new_level_id: 'Berria Maila ID',
            reject_reason_placeholder: 'Baztertzeko arrazoia...',
            current_level: 'Unekoa Maila',
            suggested_level: 'Suggested Maila',
            filter_taxonomy: 'Iragazi by Taxonomia ID...',
            all_confidence: 'Konfiantza-banda guztiak',
            load_error: 'Huts egin du: load suggestions. Saiatu berriro.',
            approve_error: 'Huts egin du: approve suggestion. Saiatu berriro.',
            reject_error: 'Huts egin du: reject suggestion. Saiatu berriro.',
            modify_error: 'Huts egin du: modify & approve suggestion. Saiatu berriro.',
            delegate_error: 'Huts egin du: delegate suggestion. Saiatu berriro.',
            bulk_approve_error: 'Huts egin du: bulk approve suggestions. Saiatu berriro.',
            bulk_reject_error: 'Huts egin du: bulk reject suggestions. Saiatu berriro.',
            suggestion_type_new_label: 'Etiketa berria',
            suggestion_type_validation: 'Baliozkotzea',
            suggestion_type_reclassification: 'Birsailkapena',
            status_pending: 'Zain',
            status_approved: 'Onartua',
            status_rejected: 'Baztertua',
            status_superseded: 'Ordezkatua',
            confidence_high: 'Altua',
            confidence_medium: 'Ertaina',
            confidence_low: 'Baxua',
            confirm_bulk_reject: 'Ziur zaude hautatutako iradokizunak baztertu nahi dituzula?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Taxonomia guztiak',
        },
        analytics: {
            title: 'Iradokizun-analitika',
            total_suggestions: 'Iradokizun guztira',
            accepted: 'Onartuta',
            rejected: 'Baztertuta',
            pending: 'Zain',
            acceptance_rate: 'Onartze-tasa',
            by_entity_type: 'By Entity Mota',
            by_source: 'By Iturria',
            avg_decision_time: 'Avg Erabaki Ordua',
            rule_effectiveness: 'Arau Effectiveness',
            by_status: 'By Egoera',
            by_confidence: 'Konfiantza-bandaren arabera',
            load_error: 'Huts egin du: load suggestion analytics. Saiatu berriro.',
            confidence_high: 'Altua',
            confidence_medium: 'Ertaina',
            confidence_low: 'Baxua',
            by_confidence_band: 'Konfiantza-bandaren arabera',
            approval_rate: 'Onarpen-tasa',
            by_label_state: 'Etiketa-egoeraren arabera',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Inportazio-txantiloiak',
            new: 'Berria Txantiloi',
            template_name: 'Txantiloi Izena',
            file_format: 'Fitxategia Format',
            column_mappings: 'Zutabe-mapaketak',
            no_templates: 'Ez da import txantiloiak found. aurkitu',
            source_column: 'Iturria Column',
            target_field: 'Helburu Field',
            transform: 'Eraldatu',
            source_column_label: 'Iturburu-zutabea',
            target_field_label: 'Helburu-eremua',
            transform_label: 'Eraldatu',
            confirm_delete: 'Ziur zaude ezabatu nahi duzula this import template?',
            load_error: 'Huts egin du: load import txantiloiak. Saiatu berriro.',
            save_error: 'Huts egin du: save import template. Saiatu berriro.',
            delete_error: 'Huts egin du: delete import template. Saiatu berriro.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Konektore-kudeaketa',
            new: 'Berria Konektore',
            connector_name: 'Konektore Izena',
            connector_type: 'Mota',
            last_sync: 'Azken sinkronizazioa',
            no_connectors: 'Ez da connectors found. aurkitu',
            cmdb_syncs: 'CMDB sinkronizazioak',
            cloud_syncs: 'Hodei-sinkronizazioak',
            new_sync: 'Berria Sync',
            no_syncs: 'Ez da syncs found. aurkitu',
            cmdb_error: 'Huts egin du: load CMDB scans. Saiatu berriro.',
            cloud_error: 'Huts egin du: load cloud scans. Saiatu berriro.',
            trigger_error: 'Huts egin du: trigger sync. Saiatu berriro.',
            trigger_sync_error: 'trigger {type} huts egin du',
            cmdb_section_title: 'CMDB sinkronizazioak',
            cloud_section_title: 'Hodei-sinkronizazioak',
            column_connector: 'Konektorea',
            action_start: 'Hasi',
            confirm_cancel_sync: 'Sinkronizazio hau bertan behera utzi?',
            start_error: 'Ezin izan da sinkronizazioa hasi',
            cancel_error: 'Ezin izan da sinkronizazioa bertan behera utzi',
            type_endpoint: 'Endpoint eskaneatzea',
            type_spreadsheet_import: 'Kalkulu-orria inportatzea',
            type_cmdb_sync: 'CMDB sinkronizazioa',
            type_cloud_sync: 'Hodei-sinkronizazioa',
        },
        discovery_cycles: {
            title: 'Aurkikuntza-zikloak',
            new_cycle: 'Aurkikuntza-ziklo berria',
            cycle_name: 'Zikloaren izena',
            scope_type: 'Irismen-mota',
            scope_ids: 'Irismen-IDak',
            cadence_days: 'Maiztasuna (egunak)',
            status: 'Egoera',
            no_cycles: 'Ez da aurkikuntza-ziklorik aurkitu',
            compare: 'Zikloak alderatu',
            delta_report: 'Aldaketen txostena',
            new_records: 'Erregistro berriak',
            changed_records: 'Aldatutako erregistroak',
            removed_records: 'Kendutako erregistroak',
            schedule: 'Eskaneoa programatu',
            scope_organization: 'Antolaketa',
            scope_department: 'Saila',
            scope_system: 'Sistema',
            scope_systems: 'Sistemak',
            bulk_actions: 'Ekintza masiboak',
            selected: '{count} hautatuta',
            bulk_create: 'Sortu masiboki',
            bulk_assign_scope: 'Esleitu irismena masiboki',
            bulk_advance: 'Aurreratu egoera masiboki',
            confirm_bulk_action: 'Berretsi ekintza masiboak',
            bulk_confirm_message: 'Ziur zaude ekintza hau egin nahi duzula?',
            cycles_affected: '{count} ziklo eragingo ditu',
            select_all: 'Hautatu denak',
            select_cycle: 'Hautatu {name} zikloa',
            from_template: 'txantiloitik',
            bulk_results: 'Emaitza masiboak',
            failed_label: 'huts egin du',
            template_id: 'Txantiloi IDa',
            count: 'Kopurua',
            advance_to: 'Helburu-egoera',
            bulk_success: 'Eragiketa masiboa amaituta',
        },
        owner_review: {
            title: 'Jabearen berrikusketa-zereginak',
            assign_owner: 'Jabeari esleitu',
            owner_id: 'Jabea',
            decision: 'Erabakia',
            approve: 'Onartu',
            modify: 'Aldatu',
            reject: 'Baztertu',
            escalate: 'Eskalatu',
            no_tasks: 'Ez da berrikusketa-zereginik aurkitu',
            bulk_resolve: 'Masiboki ebatzi',
            exceptions: 'Salbuespen-elementuak',
            no_exceptions: 'Ez da salbuespen-elementurik aurkitu',
            record_id_label: 'Erregistroaren IDa',
            selected: 'hautatua',
            notes: 'Oharrak',
            select_all: 'Dena Hautatu',
            select: 'Hautatu',
            confirm_resolve: 'Berretsi',
        },
        classification_lineage: {
            title: 'Sailkapen-historia',
            link_suggestion: 'Proposatutako etiketarekin lotu',
            suggestion_id: 'Iradokizuna',
            proposed_label_id: 'Proposatutako etiketa',
            no_lineage: 'Ez da historia-daturik aurkitu',
            full_lineage: 'Historia osoa',
            record_id_label: 'Erregistroaren IDa',
            staged_record_label: 'Erregistro eszenikoa',
            enter_staged_record_id: 'Idatzi etapako erregistro IDa',
            scan_id_label: 'Eskaneatu IDa',
            select_record: 'Erregistro bat hautatu',
            select_suggestion: 'Iradokizun bat hautatu',
            select_label: 'Etiketa bat hautatu',
        },
        common_labels: {
            n_rules: '{n} arauak',
            one_rule: '1 arau',
            n_scans: '{n} eskaneatzen',
            one_scan: '1 eskaneatu',
            n_syncs: '{n} sinkronizatzen da',
            one_sync: '1 sinkronizazioa',
            n_records: '{n} erregistro',
            one_record: '1 erregistro',
            n_templates: '{n} txantiloiak',
            one_template: '1 txantiloia',
            n_suggestions: '{n} iradokizun',
            one_suggestion: '1 iradokizun',
            n_selected: '{n} hautatuta',
            dashboard_loaded: 'Aurkikuntza-panela kargatu da',
            n_scans_loaded: '{n} eskaneatu kargatu dira',
            n_staged_records_loaded: '{n} etapako disko kargatu dira',
            confirm_delete_import_template: 'Ziur inportazio-txantiloi hau ezabatu nahi duzula?',
            invalid_json_source_config: 'JSON baliogabea source_config-n',
            scope_organization: 'Antolaketa',
            scope_department: 'Saila',
            scope_system: 'Sistema',
            aria_filter_scans: 'Iragazkiak eskaneatzea',
            aria_filter_staged_records: 'Iragazi etapakako erregistroak',
            aria_filter_suggestions: 'Iragazkiak',
            aria_filter_by_taxonomy_id: 'Iragazi taxonomiaren IDaren arabera',
            aria_filter_by_enabled_status: 'Iragazi gaituta dagoen egoeraren arabera',
            aria_filter_by_source_type: 'Iragazi iturri motaren arabera',
            aria_filter_by_promotion_status: 'Iragazi sustapen egoeraren arabera',
            aria_filter_by_dedup_status: 'Iragazi deskontu egoeraren arabera',
            aria_filter_by_scan_type: 'Iragazi eskaneatu motaren arabera',
            aria_filter_by_status: 'Iragazi egoeraren arabera',
            aria_filter_by_confidence: 'Iragazi konfiantza-bandaren arabera',
            aria_select_all_records: 'Hautatu erregistro guztiak',
            aria_select_record: 'Hautatu erregistroa {id}',
            aria_select_all_suggestions: 'Hautatu iradokizun guztiak',
            aria_select_suggestion: 'Hautatu iradokizuna {id}',
            aria_promote_record: 'Sustatu {id} erregistroa',
            aria_discard_record: 'Baztertu {id} erregistroa',
            aria_merge_record: 'Batu erregistroa {id}',
            aria_review_record: 'Berrikusi erregistroa {id}',
            aria_accept_suggestion: 'Onartu iradokizuna {id}',
            aria_reject_record: 'Errefusatu erregistroa',
            aria_merge_record_dialog: 'Batu erregistroa',
            aria_create_new_scan: 'Sortu eskaneatu berria',
            aria_sync_progress: 'Sinkronizazioaren aurrerapena: {processed}/{total} elementu prozesatu dira',
            aria_scan_progress: 'Eskanearen aurrerapena: {processed}/{total} elementu prozesatu dira',
            aria_confidence: 'Konfiantza: {pct}%',
            aria_effectiveness: 'Eraginkortasuna: {pct}',
            aria_promote_n_records: 'Sustatu hautatutako {n} erregistro',
            aria_discard_n_records: 'Baztertu hautatutako {n} erregistro',
            placeholder_pii_detection_rule: 'adibidez, PII detektatzeko araua',
            placeholder_tax_id: 'adibidez, TAX-001',
            placeholder_lvl_id: 'adibidez, LVL-001',
            placeholder_service_type: 'adibidez, service_type',
            placeholder_database: 'adib., datu-basea',
            placeholder_hostname_match: 'adibidez, ostalari-izena zehatz-mehatz',
            placeholder_hostname: 'adibidez, ostalari-izena',
            placeholder_hr_systems_csv: 'adibidez, CSV HR Systems inportatu',
            placeholder_server_name: 'adibidez, zerbitzariaren izena',
            placeholder_hostname_field: 'adibidez, ostalari-izena',
            placeholder_lowercase: 'adibidez, minuskula',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'adibidez, STG-000123',
            placeholder_lvl_002: 'adibidez, LVL-002',
            placeholder_usr_id: 'adibidez, USR-000123',
            decision_for: '{id}-rako erabakia',
        },
        exports: {
            title: 'Esportatu',
            inventory: 'Datu-inbentarioa',
            classification_register: 'Sailkapen-erregistroa',
            raci: 'RACI matrizea',
            exceptions: 'Salbuespen-txostena',
            evidence_pack: 'Ebidentzia-paketea',
            delta_report: 'Aldaketen txostena',
            executive_summary: 'Zuzendari-laburpena',
            export_button: 'Esportatu',
            downloading: 'Deskargatzen...',
            download_complete: 'Deskarga osatua.',
            download_error: 'Ezin izan da esportazioa deskargatu. Saiatu berriro.',
        },
        flags: {
            title: 'Ondorengo markatzaileak',
            pending: 'Zain dauden markatzaileak',
            cross_border_review_needed: 'Mugaz gaindiko berrikuspena beharrezkoa',
            sharing_disclosure_needed: 'Partekatze-jakinarazpena beharrezkoa',
            dpia_review_needed: 'DBEE berrikuspena beharrezkoa',
            acknowledge: 'Aitortu',
            resolve: 'Ebatzi',
            no_flags: 'Ez dago ondorengo markatzailerik.',
        },
        delta: {
            title: 'Aldaketen laburpena',
            new_count: '{n} berri',
            removed_count: '{n} kendu',
            changed_count: '{n} aldatu',
            re_review_triggered: 'Berrikuspena aktibatua',
            create_re_review: 'Berrikuspena sortu',
            recent_alerts: 'Azken aldaketa-alertak',
            no_alerts: 'Ez dago azken aldaketa-alertarik.',
            threshold_exceeded: 'Aldaketa-atalasea gainditua (>%10)',
        },
        regulatory: {
            dashboard_governance_info: 'Aginte-panel honek ziklo-mailako gobernantzaren egoera erakusten du: eskaneatutako sistemak, erregistroak eszenatuta, berrikusitako erregistroak, onartutako erregistroak eta salbuespenak zain dauden. Honek bat egiten du gobernantza nagusien arrakasta-irizpideekin: "inbentarioa, sailkapena eta partekatzeko erregistroak eguneratu dituenaren zuzeneko panela" (P-VN-05).',
            export_artifacts_info: 'Aurkikuntza-esportazioak UC-VN-10 eta DATA-EVD-01 bakoitzeko beharrezkoak diren artefaktu mota guztiak barne hartzen ditu: onartutako datu-inbentarioa, sailkapen-erregistroa, jabetza/RACI mapaketa, salbuespen-ilara, laburpen exekutiboa, froga-paketea eta delta txostena.',
            scan_inventory_pipeline_info: 'Amaitutako eskaneatu bakoitzak vn_data_law_data_inventory_register txantiloiarekin bateragarriak diren erregistroak sortzen ditu (88dd9877 eskakizuna). Aurkitutako datu-aktiboek automatikoki betetzen dute datu-inbentarioaren erregistroa, Datuen Legeak datu-inbentario digitala mantentzeko betebeharra betetzeko.',
            connector_linkage_info: 'Konektore-konfigurazio bakoitzak honako hauek izan behar ditu: sistema mota (adibidez, CMDB, hodeiko plataforma), datu-biltegiaren erreferentzia (datu-basea, fitxategi-biltegia, SaaS) eta negozio-prozesuen lotura (UC-VN-10 behar diren euskarri-erregistroak). Eremu horiek bermatzen dute aurkitutako aktiboak beren iturburu-sisteman eta negozio-testuinguruan traza daitezkeela.',
            owner_routing_info: 'Berrikuspen-zereginak sail edo aplikazioaren jabe egokiari bideratzen zaizkio sistemaren/datuen jabetzaren arabera (UC-VN-10 5. urratsa). Bideratzeak sistemaren jabetza-erregistroa erabiltzen du, ez ad-hoc esleipena.',
            owner_actions_audit_info: 'Jabeek sailkapen bakoitza onartu, aldatu, baztertu edo handitu dezakete (UC-VN-10 6. urratsa). Ekintza bakoitzak aktorea, denbora-zigilua eta arrazoia jasotzen ditu, f12fc090 (Urteko gobernantzaren egiaztagiria) eskakizunaren arabera gobernantzaren auditoretzaren froga-gertaerak sortuz. Ikuskaritza-ibilbideak nork berrikusi duen, zer aldatu den eta noiz erakusten du.',
            escalation_timers_info: 'Eskalatzeko tenporizadoreak: jabeak berrikusteko epea 5 lanegunekoa da; salbuespenaren igoera 48 ordukoa da (UC-VN-10 tenporizadoreak). Ebatzi gabeko elementuak automatikoki igotzen dira epea bukatzen denean. Tenporizadoreak antolakuntza-politikaren arabera konfigura daitezke.',
            original_suggestion_preserved_info: 'Jatorrizko makinaren/sistemaren sailkapen-iradokizuna jabearen gainidatzekin batera gordetzen da (UC-VN-10 froga-eskakizuna). Jatorrizko iradokizuna ez da inoiz gainidazten eta ikuskapen-helburuetarako ikusgai izaten jarraitzen du.',
            dedup_lineage_info: 'Desduplicazio-arauek inbentario-sarrera bikoiztuak saihesten dituzte datu-aktibo bera eskaneatu edo konektore anitzetan aurkitzen denean. Eskaneatutako metadatu berrienak erregistro nagusi gisa gordetzen dira, aurreko aurkikuntza guztien leinu-erreferentziak mantenduz, auditoretzaren trazabilitatea lortzeko.',
            label_states_info: 'Analitikek 4 etiketa-egoera bereizten dituzte gobernantza berrikusteko: makinek iradokitakoa (zain), jabeak onartua, jabeak aldatua eta baztertua. Egoera horiei esker, auditoreek automatizazioaren zehaztasuna eta jabearen konpromisoa ebaluatzeko aukera dute.',
            import_pipeline_info: 'Inportazio txantiloiak aurkikuntza kanalizazioarekin bateragarriak diren erregistroak sortu behar ditu. Inportatutako erregistroek eskaneatutako erregistroen metadatuen eskakizun berberekin sartzen dira eszenaratzea: iturburu-sistemaren identifikazioa, lotearen IDa eta denbora-zigilua. Zutabeen mapak vn_data_law_data_inventory_register atalean definitutako eremuetara zuzendu behar dira.',
            lineage_full_path_info: 'Sailkapen leinuak bide osoa jarraitzen du: eskaneatu/inportatu -> eszenaratzea -> jabearen berrikuspena -> onartutako etiketa -> erregistratu sarrera (UC-VN-10 1-10 urratsak). Etapa bakoitzak erregistroaren IDa, aktorea, denbora-zigilua eta egoera-trantsizioa erakusten ditu auditoretzaren trazabilitatea osoa izateko.',
            staging_workflow_info: 'Mailakako erregistroak bisualki bereizten dira inbentario-sarreretatik onartutako sustapen-egoeraren bereizgarriaren arabera (UC-VN-10 3. urratsa). Erregistro bakoitzak jatorriaren metadatuak gordetzen ditu: iturburu-sistema, eskaneatu lotearen IDa eta aurkikuntzaren denbora-zigilua auditoretzaren trazabilitatea osoa izateko.',
            suggestion_data_d1_info: 'Iradokizunak Datuen Legearen sailkapen prozedurarekin bat datoz DATA-D1: (1) datuen aktiboak katalogatu, (2) sentsibilitate mailaren arabera sailkatu, (3) kudeatzeko arauak definitu. Iradokizun bakoitzak sentsibilitatea eta garrantzia 18e4bfd1 eskakizun bakoitzeko dimentsioak ditu.',
            downstream_flags_info: 'Partekatzeko, mugaz gaindiko transferentziarako edo arrisku handiko tratamendurako markatutako elementuek beheranzko lan-fluxuak abiarazten dituzte: mugaz gaindiko transferentziaren ebaluazioa, partekatzeko arriskuen ebaluazioa eta DPIA berrikuspena (UC-VN-10 8. urratsa).',
            delta_detection_info: 'Aurkikuntza-zikloek delta detekzioa onartzen dute UC-VN-10 11. urratseko: ziklo bakoitzak erakusten du zein erregistro diren berriak, aldatu edo kendu diren azken onartutako oinarri-lerrotik. Material-aldaketek (>% 10 delta) automatikoki berriro berrikusteko lan-fluxuak abiarazten dituzte.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Ezin izan da kargatu.',
            save: 'Ezin izan da gorde.',
            fetch_escalation_log_failed: 'Ezin izan da eskalatze-erregistroa eskuratu.',
            fetch_activations_failed: 'Ezin izan dira aktibazioak eskuratu.',
            deactivate_failed: 'Ezin izan da desaktibatu.',
            complete_activation_failed: 'Ezin izan da aktibazioa osatu.',
            fetch_playbook_templates_failed: 'Ezin izan dira jokabide-gida txantiloiak eskuratu.',
            create_playbook_template_failed: 'Ezin izan da jokabide-gida txantiloia sortu.',
            clone_template_failed: 'Ezin izan da txantiloia klonatu.',
            update_status_failed: 'Ezin izan da egoera eguneratu.',
            deprecate_template_failed: 'Ezin izan da txantiloia zaharkitu.',
            seed_defaults_failed: 'Ezin izan dira lehenespenak hasieratu.',
            fetch_regulator_exports_failed: 'Ezin izan dira erregulatzaileen esportazioak eskuratu.',
            create_regulator_export_failed: 'Ezin izan da erregulatzailearen esportazioa sortu.',
            update_submission_status_failed: 'Ezin izan da aurkezpen-egoera eguneratu.',
            fetch_response_metrics_failed: 'Ezin izan dira erantzun-metrikak eskuratu.',
            fetch_sla_dashboard_failed: 'Ezin izan da SLA aginte-mahaia eskuratu.',
            fetch_sla_definitions_failed: 'Ezin izan dira SLA definizioak eskuratu.',
            create_sla_definition_failed: 'Ezin izan da SLA definizioa sortu.',
            seed_sla_defaults_failed: 'Ezin izan dira SLA lehenespenak hasieratu.',
            fetch_vendor_coordination_failed: 'Ezin izan da hornitzailearen koordinazioa eskuratu.',
            fetch_vendor_detail_failed: 'Ezin izan da hornitzailearen xehetasuna eskuratu.',
            create_vendor_coordination_failed: 'Ezin izan da hornitzailearen koordinazioa sortu.',
            add_communication_failed: 'Ezin izan da komunikazioa gehitu.',
            add_info_request_failed: 'Ezin izan da informazio-eskaera gehitu.',
            add_action_request_failed: 'Ezin izan da ekintza-eskaera gehitu.',
        },
        nav: {
            playbooks: 'Jokabide-giden txantiloiak',
            sla_definitions: 'SLA definizioak',
            sla_dashboard: 'SLA aginte-mahaia',
            escalation_log: 'Eskalatze-erregistroa',
            regulator_exports: 'Erregulatzaile-esportazioak',
            deadlines: 'Jakinarazpen-epemugak',
            vendor_coordination: 'Hornitzaile-koordinazioa',
            metrics: 'Erantzun-metrikak',
            playbook_links: 'Jokabide-giden loturak',
            regulatory_sla: 'Araudiaren SLA',
            coordination_dashboard: 'Koordinazioa',
            evidence_chain: 'Froga-katea',
            post_review: 'Gertakari osteko berrikusketa',
        },
        playbooks: {
            page_title: 'Jokabide-giden txantiloiak',
            activations_page_title: 'Jokabide-giden aktibazioak',
            title: 'Jokabide-giden txantiloiak',
            new: 'Txantiloi berria',
            seed_defaults: 'Hasieratu lehenespenak',
            scenario_type: 'Eszenategi-mota',
            severity_applicability: 'Larritasunari aplikagarritasuna',
            all_scenario_types: 'Eszenategi-mota guztiak',
            new_playbook: 'Jokabide-gida txantiloi berria',
            clone: 'Klonatu',
            clone_template: 'Klonatu txantiloia',
            template_name: 'Txantiloiaren izena',
            deprecate: 'Zaharkitu',
            confirm_deprecate: 'Zaharkitu "{name}" txantiloia?',
            create_template: 'Sortu txantiloia',
            update_status: 'Eguneratu egoera',
            no_templates: 'Ez da jokabide-gida txantiloirik aurkitu',
            deactivation_reason_placeholder: 'Desaktibatzeko arrazoia...',
            description_placeholder: 'Jokabide-gidaren deskribapena...',
            name_placeholder: 'Jokabide-gidaren izena...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA definizioak',
            title: 'SLA definizioak',
            new: 'SLA definizio berria',
            sla_name: 'SLAren izena',
            target_hours: 'Helburu-orduak',
            escalation_after: 'Eskalatzea ondoren',
            applies_to: 'Honi aplikatzen zaio',
            no_definitions: 'Ez da SLA definiziorik aurkitu.',
            target_value_placeholder: 'Helburu-balioa',
            name_placeholder: 'SLAren izena...',
            threshold_hours_label: 'Atalase-orduak',
            severity_label: 'Larritasuna',
            all_types: 'Mota guztiak',
            all_severities: 'Larritasun guztiak',
            default_label: 'Lehenetsia',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Konforme',
            page_title: 'SLA aginte-mahaia',
            title: 'SLA aginte-mahaia',
            loading: 'SLA datuak kargatzen...',
            compliance_rate: 'Betetze-tasa',
            breached: 'Urratuta',
            at_risk: 'Arriskuan',
            on_track: 'Bidean',
            by_severity: 'Larritasunaren arabera',
            total_records: 'Erregistroak guztira',
            met: 'Beteta',
            sla_records: 'SLA erregistroak',
            sla_reference: 'SLA erreferentzia',
            record_id: 'Erregistroaren IDa',
            metric_type: 'Metrika-mota',
            severity: 'Larritasuna',
            elapsed: 'Igarota',
            clock_start: 'Erlojuaren hasiera',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskalatze-erregistroa',
            title: 'Eskalatze-erregistroa',
            incident_id: 'Gertakariaren IDa',
            escalated_by: 'Eskalatzailea',
            escalated_to: 'Hartzailea',
            reason: 'Arrazoia',
            escalated_at: 'Eskalatze-data',
            no_escalations: 'Ez da eskalatzerik aurkitu.',
            filter_incident_id: 'Iragazi gertakariaren IDaren arabera',
            escalation_id: 'Eskalatzearen IDa',
            level: 'Maila',
            level_range: 'Maila (L1–L4)',
            create_escalation: 'Sortu eskalatzea',
            incident_id_placeholder: 'adib., INC-001',
            target_role_placeholder: 'adib., dpo, incident_manager',
            select_placeholder: '— hautatu —',
        },
        regulator_exports: {
            page_title: 'Erregulatzaile-esportazioak',
            title: 'Erregulatzaile-esportazioak',
            new: 'Esportazio berria',
            incident_id: 'Gertakariaren IDa',
            regulator: 'Erregulatzailea',
            export_format: 'Formatua',
            exported_at: 'Esportazio-data',
            no_exports: 'Ez da erregulatzaile-esportaziorik aurkitu.',
            enter_incident_id: 'Sartu gertakariaren IDa',
            export_id: 'Esportazioaren IDa',
            watermark: 'Ur-marka',
            submission_status: 'Aurkezpen-egoera',
            all_jurisdictions: 'Jurisdikzio guztiak',
            no_transitions: 'Ez dago trantsizio gehiagorik hemendik:',
            select_new_status: '— hautatu egoera berria —',
            sections_included: 'Sartutako atalak',
            form_classification: 'Sailkapena',
            submission_channel: 'Kanala',
        },
        deadlines: {
            title: 'Jakinarazpen-epemugak',
            incident_id: 'Gertakariaren IDa',
            deadline: 'Epemuga',
            regulator: 'Erregulatzailea',
            remaining: 'Geratzen dena',
            no_deadlines: 'Ez da jakinarazpen-epemugarik aurkitu.',
        },
        vendor_coordination: {
            page_title: 'Hornitzaile-koordinazioa',
            title: 'Hornitzaile-koordinazioa',
            new: 'Koordinazio berria',
            incident_id: 'Gertakariaren IDa',
            vendor_name: 'Hornitzailearen izena',
            contact: 'Kontaktua',
            coordination_status: 'Koordinazio-egoera',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Ez da hornitzaile-koordinazioko elementurik aurkitu.',
            assessment_placeholder: 'Hornitzailearen hasierako balorazioa...',
            coordination_id: 'Koordinazioaren IDa',
            communications: 'Komunikazioak',
            info_requests: 'Informazio-eskaerak',
            action_requests: 'Ekintza-eskaerak',
            direction: 'Norabidea',
            channel_label: 'Kanala',
            no_transitions: 'Ez dago trantsizio gehiagorik.',
            vendor_ref_number: 'Erreferentzia-zenbakia',
            report_date: 'Txosten-data',
            contact_email: 'Posta elektronikoa',
            contact_phone: 'Telefonoa',
            channel_email: 'Posta elektronikoa',
            channel_meeting: 'Bilera',
            channel_phone: 'Telefonoa',
            channel_portal: 'Ataria',
            incident_id_placeholder: 'Iragazi gertakariaren IDaren arabera...',
            priority_critical: 'Kritikoa',
            priority_high: 'Altua',
            priority_low: 'Baxua',
            priority_medium: 'Ertaina',
            vendor_id_placeholder: 'Iragazi hornitzailearen IDaren arabera...',
            description_placeholder: 'Deskribapena...',
            filter_vendor_id: 'Iragazi hornitzailearen IDaren arabera',
            inbound: 'Sarrerakoa',
            outbound: 'Irteerakoa',
            summary_placeholder: 'Laburpena...',
            priority_label: 'Lehentasuna',
            due_date_label: 'Epemuga',
            info_request_desc: 'Informazio-eskaeraren deskribapena',
            action_request_desc: 'Ekintza-eskaeraren deskribapena',
            content_label: 'Edukia',
            select_incident: 'Hautatu gertakaria',
            select_vendor: 'Hautatu hornitzailea',
            discovery_date: 'Discovery Data',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Aktorea',
        },
        metrics: {
            page_title: 'Erantzun-metrikak',
            title: 'Erantzun-metrikak',
            loading: 'Metrika-datuak kargatzen...',
            avg_response_time: 'Batez besteko erantzun-denbora',
            avg_resolution_time: 'Batez besteko ebazpen-denbora',
            total_incidents: 'Gertakariak guztira',
            by_severity: 'Larritasunaren arabera',
            by_status: 'Egoeraren arabera',
            trend: 'Joera',
            start_sla_clock: 'Hasi SLA erlojua',
            sla_records: 'SLA erregistroak',
            no_sla_records: 'Oraindik ez dago SLA erregistrorik. Sakatu "Hasi SLA erlojua" bat sortzeko.',
            record_id: 'Erregistroaren IDa',
            severity: 'Larritasuna',
            progress: 'Aurrerapena',
            incident_id_required: 'Gertakariaren IDa beharrezkoa da',
            sla_definition_required: 'SLA definizioa beharrezkoa da',
            metric_type_required: 'Metrika-mota beharrezkoa da',
            threshold_required: 'Atalase-orduak beharrezkoak dira',
            sla_clock_started: 'SLA erlojua hasi da',
            sla_definition: 'SLA definizioa',
            no_definitions: 'Ez da SLA definiziorik aurkitu. Sortu bat aurrena SLA definizioetan.',
            select_definition: 'Hautatu SLA definizioa…',
            select_metric_type: 'Hautatu metrika-mota…',
            select_severity: 'Hautatu larritasuna…',
            threshold_hours: 'Atalasea (orduak)',
            warning_threshold_pct: 'Ohartarazpen-atalasea (%)',
            start_clock: 'Hasi erlojua',
            starting: 'Hasten…',
            metric_label: 'Metrika',
            value_label: 'Balioa',
            incident_id: 'Gertakariaren IDa',
            threshold_placeholder: 'adib., 24',
            metric_types: {
                triage_time: 'Triaje-denbora',
                containment_time: 'Edukitze-denbora',
                notification_time: 'Jakinarazpen-denbora',
                resolution_time: 'Ebazpen-denbora',
            },
            severities: {
                critical: 'Kritikoa',
                high: 'Altua',
                medium: 'Ertaina',
                low: 'Baxua',
            },
            sla_reference: 'SLA erreferentzia',
        },
        playbook_links: {
            page_title: 'Jokabide-giden loturak',
            title: 'Jokabide-giden jakinarazpen-loturak',
            new_link: 'Lotu jokabide-gida jakinarazpenari',
            activation_id: 'Aktibazioaren IDa',
            notification_id: 'Jakinarazpenaren IDa',
            select_activation: 'Hautatu aktibazio bat...',
            select_notification: 'Hautatu jakinarazpen bat...',
            no_links: 'Ez da jokabide-gidaren loturarik aurkitu',
            seed_pdpl: 'Gehitu txantiloi-playbook estekak',
            created: 'Jokabide-gidaren lotura sortu da',
            seeded: 'Txantiloi-playbook estekak gehitu dira',
            create_failed: 'Ezin izan da jokabide-gidaren lotura sortu',
            seed_failed: 'Ezin izan dira txantiloi-playbook estekak gehitu',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Instalatutako esparruek ez dute gorabeheren erantzuna arautzen, beraz ez dago gehitzeko eperik.',
                declared_but_none_active: 'Gorabeheren erantzuna arautzen duen esparru bat instalatuta dago, baina ez dago indarrean.',
                no_declared_deadlines: 'Instalatutako gorabehera-erantzun esparruek ez dute jakinarazpen-eperik ezartzen.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Epemuga',
            deadline_units: {
                hours: '{count} ordu',
                working_days: '{count} lanegun',
                calendar_days: '{count} egutegiko egun',
                days: '{count} egun',
                weeks: '{count} aste',
                months: '{count} hilabete',
                years: '{count} urte',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Araudiaren SLA esleipenak',
            title: 'Araudiaren SLA esleipenak',
            new_mapping: 'SLA esleipen berria',
            sla_definition: 'SLA definizioa',
            select_sla_definition: 'Hautatu SLA definizio bat...',
            framework_code: 'Esparru-kodea',
            deadline_type: 'Epemuga-mota',
            deadline_hours: 'Epemuga-orduak',
            seed_pdpl: 'Gehitu esleipenak instalatutako epeetatik',
            no_mappings: 'Ez da araudiaren SLA esleipenik aurkitu',
            dashboard: 'Araudiaren SLA aginte-mahaia',
            deadline_types: {
                initial_notification: 'Hasierako jakinarazpena',
                supplement_response: 'Erantzun osagarria',
                authority_response: 'Agintaritzaren erantzuna',
                remediation_report: 'Konponketa-txostena',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Lege-erreferentzia',
        },
        coordination_dashboard: {
            page_title: 'Koordinazio-aginte-mahaia',
            title: 'Koordinazio-aginte-mahaia',
            incident_id: 'Gertakariaren IDa',
            select_incident: 'Hautatu gertakari bat...',
            team_role: 'Talde-rola',
            members: 'Kideak',
            tasks: 'Zereginak',
            sla_status: 'SLA egoera',
            no_teams: 'Ez da koordinazio-talderik aurkitu',
            new_team: 'Koordinazio-talde berria',
            enter_incident_id: 'Sartu gertakariaren IDa koordinazio-taldeak ikusteko.',
            member_ids_placeholder: 'erabiltzailea1, erabiltzailea2',
            edit_team: 'Editatu taldea',
            task_count: 'Zereginen zenbaketa',
            tasks_completed: 'Osatua',
            team_roles: {
                security: 'Segurtasuna',
                legal: 'Lege-arloa',
                it: 'IT',
                management: 'Zuzendaritza',
                department_owner: 'Sail-arduraduna',
                external: 'Kanpokoa',
            },
        },
        evidence_chain: {
            page_title: 'Froga-katea',
            title: 'Frogen zaintza-katea',
            new_entry: 'Froga-sarrera berria',
            select_incident: 'Hautatu gertakari bat...',
            actor: 'Eragilea',
            select_actor: 'Hautatu eragile bat...',
            evidence_id: 'Frogaren IDa',
            action: 'Ekintza',
            recipient: 'Hartzailea',
            no_entries: 'Ez da froga-kateko sarrerarik aurkitu',
            enter_incident_id: 'Sartu gertakariaren IDa froga-katea ikusteko.',
            evidence_id_placeholder: 'adib. EVD-2026-0001',
            evidence_id_help: 'Frogaren elementuaren erreferentzia bakarra —erregistro-fitxategi bat, disko-irudi bat edo pantaila-argazki bat—. Iradokizun bat aurrez bete da; editatu zure froga-katalogoarekin bat etor dadin.',
            select_incident_first: 'Hautatu goiko gorabehera bat froga-sarrera bat gehitu aurretik.',
            for_incident: 'Gorabeherarako',
            actions: {
                collected: 'Bildua',
                shared: 'Partekatua',
                exported: 'Esportatua',
                archived: 'Artxibatua',
            },
        },
        post_review: {
            page_title: 'Gertakari osteko berrikusketa',
            title: 'Gertakari osteko berrikusketa',
            new_review: 'Berrikusketa berria',
            select_incident: 'Hautatu gertakari bat...',
            lessons_learned: 'Ikasitako ikasgaiak',
            recommendations: 'Gomendioak',
            improvement_actions: 'Hobekuntza-ekintzak',
            submit_for_review: 'Bidali berrikusketarako',
            aria_submit_review: 'Bidali {name} berrikusketa berrikusteko',
            aria_sign_off: 'Onartu {name} berrikusketa',
            sign_off: 'Onartu',
            no_reviews: 'Ez da gertakari osteko berrikusketarik aurkitu',
            enter_incident_id: 'Sartu gertakariaren IDa gertakari osteko berrikusketa ikusteko.',
            one_per_line: 'Bat lerroko',
            statuses: {
                draft: 'Zirriborroa',
                in_review: 'Berrikusten',
                signed_off: 'Onartuta',
                closed: 'Itxita',
            },
        },
        activations: {
            title: 'Jokabide-giden aktibazioak',
            activation_id: 'Aktibazioaren IDa',
            phases: 'Faseak',
            activated_at: 'Aktibazio-data',
            activated_by: 'Aktibatzailea',
            deactivate: 'Desaktibatu',
            complete: 'Osatu',
            deactivation_reason: 'Desaktibatzeko arrazoia',
            evidence_button: 'Frogak',
            evidence_modal_title: 'Aktibazioaren frogak',
            no_evidence: 'Aktibazio honi ez zaio frogarik erantsi.',
            upload_evidence_label: 'Igo aktibazioaren frogak',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Ibili nazazu hutsuneetatik',
            running: 'Hutsuneak oinez…',
            results_heading: 'Hobetzeko bide-orria ({count} hutsune{plural})',
            tokens_label: '≈ {count} token',
            no_gaps: 'Ez da hutsunerik hauteman: domeinu guztiak xedean edo goian.',
            col_priority: 'Lehentasuna',
            col_domain: 'Domeinua',
            col_levels: 'Uneko → Helburua',
            col_severity: 'Larritasuna',
            col_actions: 'Konponketa-ekintzak',
            citations_label: 'Aipamenak',
            accept_label: 'Onartu bide orria',
            reject_label: 'Zirriborroa baztertu',
            export_label: 'Esportatu DOCX',
            exported_label: 'Esportatu da bide-orria',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Ebazpena: {status}',
            resolve_status_accepted: 'Onartua',
            resolve_status_rejected: 'Baztertua',
            error_generic: 'Ezin izan da hutsunearen jarraipena egin. Saiatu berriro edo egiaztatu ebaluazioaren egoera.',
        },
        policy_gap: {
            run_label: 'Politika-hutsuneak hautematea',
            running: 'Hutsuneak hautematen…',
            results_heading: 'Politikaren hutsuneak ({count})',
            tokens_label: '≈ {count} token',
            no_gaps: 'Ez da estaldura-hutsunerik hauteman.',
            col_severity: 'Larritasuna',
            col_kind: 'Jatorra',
            col_clause: 'Klausula / betekizuna',
            kind_missing: 'Falta den klausula',
            kind_weak: 'Estaldura ahula',
            kind_stale: 'Zaharkituta (berrikuspena egin behar da)',
            remediation_label: 'Iradokitako konponketa',
            citations_label: 'Aipamenak',
            accept_label: 'Onartu hutsunearen txostena',
            reject_label: 'Errefusa txostena',
            export_label: 'Esportatu DOCX',
            accept_hint: 'Onartu mantentzen den zirriborroari buruzko hutsune-txostena erregistratzen du. Erabili Esportatu DOCX Politikaren Ikuskaritza Txostena sortzeko; operadoreak benetako politikaren aldaketa gidatzen du.',
            resolve_summary: 'Ebazpena: {status}',
            error_generic: 'Ezin izan dira politika-hutsuneak hauteman. Saiatu berriro edo egiaztatu dokumentuaren egoera.',
        },
        remediation_plan_generator: {
            run_label: 'Sortu plana hutsuneetatik',
            running: 'Plana sortzen…',
            results_heading: 'Konponketa plana ({phase_count} fasea{plural})',
            tokens_label: '≈ {count} token',
            phase_label: '{index} fasea',
            window_label: 'Leihoa',
            owner_label: 'Jabearen rola',
            evidence_label: 'Beharrezko froga motak',
            tasks_label: 'Zereginak',
            no_phases: 'Ez da faserik sortu; baliteke sarrerako hutsune-multzoa hutsik egotea.',
            citations_label: 'Aipamenak',
            accept_label: 'Plana onartu',
            reject_label: 'Plana baztertu',
            export_label: 'Esportatu DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Ebazpena: {status} · {tasks} zereginak sortu dira',
            error_generic: 'Ezin izan da konponketa-plana sortu. Saiatu berriro edo egiaztatu hutsune-multzoaren sarrera.',
            source_picker_label: 'Iturria',
            source_all: 'Denak',
            source_consent: 'Adostasunaren hutsunearen azterketa',
            source_policy: 'Politiken hutsuneak hautematea',
            source_vendor: 'Saltzaileak sartzea',
            load_upstream_label: 'Kargatu ibaian gora',
            loading_upstream: 'Kargatzen…',
            upstream_count: '{count} hutsune(k) kargatu dira',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Heldutasun-ereduak',
            maturity_assessments: 'Heldutasun-ebaluazioak',
            trend_dashboard: 'Joera aginte-mahaia',
            reporting_periods: 'Txosten-aldiak',
            executive_summaries: 'Zuzendaritza-laburpenak',
            audit_readiness: 'Auditoria-prestutasuna',
            evidence_gaps: 'Ebidentzia-hutsuneak',
            program_health: 'Programaren egoera',
            obligation_mappings: 'Betebehar-esleipenak',
            governance_documents: 'Gobernantzako dokumentuak',
            remediation_plans: 'Konponketa-planak',
            governance_model: 'Gobernantzako eredua',
            document_drafting: 'Dokumentuen zirriborroa',
            document_issuance: 'Dokumentuen jaulkipena',
            acknowledgment_tracking: 'Aitorpen-jarraipena',
            change_log: 'Aldaketa-erregistroa',
            review_cadence: 'Berrikusketa-kadentzia',
        },
        maturity_models: {
            title: 'Heldutasun-ereduak',
            new: 'Berria Model',
            seed_defaults: 'Hasieratu Lehenetsia',
            domains: 'Domeinuak',
            activate: 'Aktibatu',
            no_models: 'Ez da maturity models aurkitu',
            new_maturity_model: 'Berria Heldutasun Model',
            create_model: 'Sortu Model',
            load_error: 'Ezin izan dira heldutasun-ereduak kargatu. Saiatu berriro.',
            create_error: 'Ezin izan da heldutasun-eredua sortu',
            status_error: 'Ezin izan da ereduaren egoera eguneratu',
            seed_error: 'Ezin izan dira balio lehenetsiak sortu',
            aria_seed_defaults: 'Lehenetsitako heldutasun-ereduak sortu',
            aria_create_new: 'Heldutasun-eredu berria sortu',
            loading_sr: 'Heldutasun-ereduak kargatzen',
            model_name_placeholder: 'adib., Pribatutasun heldutasun-eredua',
            status_changed: 'Ereduaren egoera aldatu da {status} egoerara',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Heldutasun-ebaluazioak',
            new: 'Berria Ebaluazio',
            model: 'Eredua',
            assessor: 'Ebaluatzailea',
            overall_score: 'Puntuazio orokorra',
            overall_score_help: '0–5 arteko heldutasun-balorazio haztatua, ebaluazio bat amaitzean automatikoki kalkulatzen dena. Handitu ezazu ebaluazioan zehar domeinu bakoitzari puntuazio altuagoa emanez, eta ondoren ebaluazioa Berrikusketara bidali → Amaitu urratsetan zehar eramanez.',
            assessed_at: 'Ebaluatua',
            no_assessments: 'Ez da maturity assessments found. aurkitu',
            load_error: 'Ezin izan dira heldutasun-ebaluazioak kargatu. Saiatu berriro.',
            create_error: 'Ezin izan da ebaluazioa sortu',
            start_error: 'Ezin izan da ebaluazioa hasi',
            archive_error: 'Ezin izan da ebaluazioa artxibatu',
            eval_error: 'Ezin izan da ebaluazioa kargatu ebaluatzeko',
            eval_min_score: 'Puntuatu gutxienez domeinu bat',
            eval_save_error: 'Ezin izan dira domeinu-ebaluazioak gorde',
            review_error: 'Ezin izan da berrikusketara bidali',
            finalize_error: 'Ezin izan da ebaluazioa amaitu',
            start: 'Hasi',
            start_confirm_title: 'Ebaluazioa hasi',
            start_confirm_message: 'Ziur zaude ebaluazio hau hasi nahi duzula?',
            archive_confirm_title: 'Ebaluazioa artxibatu',
            archive_confirm_message: 'Ziur zaude ebaluazio hau artxibatu nahi duzula?',
            evaluate_domains: 'Ebaluatu',
            evaluate_domains_title: 'Domeinuak ebaluatu',
            submit_for_review: 'Berrikusketara bidali',
            finalize: 'Amaitu',
            archive: 'Artxibatu',
            all_reviewers: 'Berrikusle guztiek onartu behar dute',
            any_reviewer: 'Edozein berrikuslek onar dezake',
            select_level: 'Heldutasun-maila aukeratu...',
            findings_placeholder: 'Aurkikuntzak, oharrak, ebidentzia-erreferentziak...',
            no_domains: 'Ez da domeinurik aurkitu eredu honetarako.',
        },
        trend_dashboard: {
            title: 'Joera aginte-mahaia',
            loading: 'trend datu kargatzen...',
            score_trend: 'Puntuazio Joera',
            domain_comparison: 'Domeinu Comparison',
            period: 'Aldi',
            load_error: 'Ezin izan da joera-laburpena kargatu. Saiatu berriro.',
            metrics_load_error: 'Ezin izan dira joera-metrikak kargatu. Saiatu berriro.',
            module: 'Modulua',
            metric: 'Metrika',
            value: 'Balioa',
            trend: 'Joera',
            aria_filters: 'Joera-metriken iragazkiak',
            showing_chart: 'Grafiko-ikuspegia erakusten',
            showing_table: 'Datu-taula ikuspegia',
            create_metric: 'Sortu metrika',
            data_points: 'Datu-puntuak',
            trend_direction: 'Joeraren norabidea',
            unit: 'Unitatea',
        },
        reporting_periods: {
            title: 'Txosten-aldiak',
            new: 'Berria Aldi',
            period_name: 'Aldi Izena',
            start_date: 'Hasiera-data',
            end_date: 'Amaiera-data',
            no_periods: 'Ez da reporting periods found. aurkitu',
            load_error: 'Ezin izan dira txosten-aldiak kargatu. Saiatu berriro.',
            create_error: 'Ezin izan da txosten-aldia sortu',
            auto_generate_error: 'Ezin izan dira txosten-aldiak automatikoki sortu',
            status_error: 'Ezin izan da aldiaren egoera eguneratu',
            start_collecting: 'Bilketa hasi',
            mark_as_draft: 'Zirriborro gisa markatu',
            submit_for_review: 'Berrikusketara bidali',
            publish: 'Argitaratu',
            auto_generate: 'Txosten-aldiak automatikoki sortu',
            period_label_placeholder: 'adib., H1 2026',
            status_updated: 'Aldiaren egoera eguneratua',
        },
        executive_summaries: {
            title: 'Zuzendaritza-laburpenak',
            new: 'Berria Laburpena',
            period: 'Aldi',
            generated_at: 'Sortua',
            generated_by: 'Sortzailea',
            no_summaries: 'Ez da executive summaries found. aurkitu',
            generate: 'Sortu',
            load_error: 'Ezin izan dira zuzendaritza-laburpenak kargatu. Saiatu berriro.',
            generate_error: 'Ezin izan da zuzendaritza-laburpena sortu',
            save_error: 'Ezin izan da zuzendaritza-laburpena gorde',
        },
        audit_readiness: {
            title: 'Auditoria-prestutasuna',
            loading: 'audit readiness datu kargatzen...',
            overall_readiness: 'Prestutasun orokorra',
            by_domain: 'By Domeinu',
            evidence_coverage: 'Ebidentzia Estaldura',
            gaps: 'Hutsuneek',
            load_error: 'Ezin izan da azken prestutasuna kargatu. Saiatu berriro.',
            history_error: 'Ezin izan da prestutasun-historia kargatu. Saiatu berriro.',
            calculate_error: 'Ezin izan da auditoria-prestutasuna kalkulatu',
            aria_recalculate: 'Auditoria-prestutasun puntuazioa berkalkulatu',
            aria_current_readiness: 'Uneko auditoria-prestutasuna',
            aria_history: 'Prestutasun-puntuazio historia',
            recalculated: 'Auditoria-prestutasuna berkalkulatua',
            loading_sr: 'Auditoria-prestutasun datuak kargatzen',
        },
        evidence_gaps: {
            title: 'Ebidentzia-hutsuneak',
            domain: 'Domeinu',
            control: 'Kontrol',
            gap_type: 'Hutsune mota',
            severity: 'Larritasun',
            no_gaps: 'Ez da ebidentzia gaps found. aurkitu',
            load_error: 'Ezin izan dira ebidentzia-hutsuneak kargatu. Saiatu berriro.',
            stats_error: 'Ezin izan dira hutsune-estatistikak kargatu. Saiatu berriro.',
            create_error: 'Ezin izan da ebidentzia-hutsunea sortu',
            update_error: 'Ezin izan da ebidentzia-hutsunea eguneratu',
            status_error: 'Ezin izan da egoera eguneratu',
            aria_create_new: 'Ebidentzia-hutsune berria sortu',
            aria_statistics: 'Ebidentzia-hutsuneen estatistikak',
            loading_sr: 'Ebidentzia-hutsuneak kargatzen',
            status_updated: 'Hutsune-egoera eguneratua',
            entity_type_placeholder: 'adib., hornitzailea, sistema',
            all_modules: 'Domeinu guztiak',
            all_severities: 'Larritasun guztiak',
        },
        program_health: {
            title: 'Programaren egoera',
            loading: 'program health datu kargatzen...',
            overall_health: 'Osasun orokorra',
            by_module: 'By Modulu',
            risk_areas: 'Arrisku Areas',
            recommendations: 'Gomendioak',
            load_error: 'Ezin izan dira programaren osasun-datuak kargatu. Saiatu berriro.',
            aria_key_metrics: 'Programaren osasun-metrika nagusiak',
            aria_module_scorecard: 'Moduluaren puntuazio-txartelaren xehetasuna',
            loading_sr: 'Programaren osasun-datuak kargatzen',
            trending_up: 'Goranzko joera',
            trending_down: 'Beheranzko joera',
            trending_stable: 'Egonkorra',
            data_loaded: 'Programaren osasun-datuak kargatuta',
        },
        obligation_mappings: {
            title: 'Betebehar-esleipenak',
            create_mapping: 'Betebehar-esleipena sortu',
            framework: 'Esparru arautzailea',
            select_framework: 'Aukeratu esparru bat...',
            obligation: 'Betebeharra',
            obligation_ref: 'Betebehar-erreferentzia',
            obligation_description: 'Betebeharraren deskribapena',
            role: 'Rola',
            assigned_role: 'Esleitutako rola',
            select_role: 'Aukeratu rol bat...',
            assigned_department: 'Esleitutako saila',
            select_department: 'Aukeratu sail bat...',
            control_ids: 'Kontrol-IDak',
            control_ids_placeholder: 'adib. A.5.1, CC6.1',
            control_ids_hint: 'Komaz bereizitako kontrol-erreferentzia kodeak.',
            raci_assignments: 'RACI esleipenak',
            create_raci: 'RACI esleipena sortu',
            activity: 'Jarduera',
            activity_name: 'Jardueraren izena',
            activity_description: 'Jardueraren deskribapena',
            responsible: 'Arduraduna',
            select_users: 'Aukeratu erabiltzaileak...',
            accountable: 'Kontu-ematea',
            select_user: 'Aukeratu erabiltzaile bat...',
            consulted: 'Kontsultatua',
            informed: 'Informatua',
            review_frequency: 'Berrikusketa-maiztasuna',
            frequency_monthly: 'Hilero',
            frequency_quarterly: 'Hiruhilean',
            frequency_semi_annual: 'Seihilean',
            frequency_annual: 'Urtero',
            no_mappings_found: 'Ez da betebehar-esleipenik aurkitu.',
            no_raci_found: 'Ez da RACI esleipenrik aurkitu.',
            mapping_created: 'Betebehar-esleipena sortua',
            mapping_updated: 'Betebehar-esleipena eguneratua',
            mapping_deleted: 'Betebehar-esleipena ezabatua',
            raci_created: 'RACI esleipena sortua',
            raci_updated: 'RACI esleipena eguneratua',
            raci_deleted: 'RACI esleipena ezabatua',
            aria_edit_mapping: '{name} esleipena editatu',
            aria_delete_mapping: '{name} esleipena ezabatu',
            aria_edit_raci: 'RACI {name} editatu',
            aria_delete_raci: 'RACI {name} ezabatu',
            confirm_delete_mapping: 'Betebehar-esleipena hau ezabatu?',
            confirm_delete_raci: 'RACI esleipena hau ezabatu?',
            table_view: 'Taula ikuspegia',
            matrix_view: 'Matrize ikuspegia',
            print_export: 'Inprimatu / Esportatu',
            filter_framework: 'Iragazi esparruaren arabera',
            no_matrix_data: 'Ez dago RACI esleipen matrize ikuspegian erakusteko.',
            aria_print_export: 'Inprimatu edo esportatu RACI matrizea',
        },
        governance_documents: {
            title: 'Gobernantzako dokumentuak',
            create_document: 'Gobernantzako dokumentua sortu',
            document_title: 'Dokumentuaren izenburua',
            document_type: 'Dokumentu-mota',
            content_url: 'Eduki-URLa',
            content_url_placeholder: 'https://...',
            framework_codes: 'Esparru-kodeak',
            select_frameworks: 'Aukeratu esparruak...',
            effective_date: 'Indarraldi-data',
            review_date: 'Berrikusketa-data',
            status: 'Egoera',
            version: 'Bertsioa',
            acknowledgments: 'Aitorpenak',
            acknowledge: 'Aitortu',
            acknowledged_by: 'Aitortua honek',
            department: 'Saila',
            no_documents_found: 'Ez da gobernantzako dokumenturik aurkitu.',
            draft: 'Zirriborroa',
            review: 'Berrikuspena',
            approved: 'Onartua',
            issued: 'Emandakoa',
            superseded: 'Ordezkatua',
            document_created: 'Dokumentua sortua',
            document_acknowledged: 'Dokumentua aitortua',
            policy: 'Politika',
            procedure: 'Prozedura',
            standard: 'Estandarra',
            guideline: 'Gidalerroa',
        },
        governance_model: {
            title: 'Gobernantzako eredua',
            create_model: 'Gobernantzako eredua',
            edit_model: 'Gobernantzako eredua',
            model_name: 'Model',
            description: 'Deskribapena',
            governance_type: 'Type',
            type_centralized: 'Zentralizatua',
            type_federated: 'Federatua',
            type_hybrid: 'Hibridoa',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Hilerokoa',
            freq_quarterly: 'Hiruhilabetekaria',
            freq_semi_annual: 'Seiurtekoa',
            freq_annual: 'Urtekoa',
            committees: 'Batzordeak',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandatua',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Eremua',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Jarduera',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'regulatory_framework:irakurtzeko baimena behar duzu esparruak hautatzeko. Eskatu administratzaile bati ematea.',
            select_members: 'Hautatu batzordeko kideak',
            select_from_role: 'Hautatu delegatzeko rola',
            select_to_role: 'Hautatu hartzailearen rola',
            mandate_markdown_hint: 'Markdown onartzen da: **lodia**, zerrendak, estekak.',
            scope_tab_obligation: 'Betebeharra',
            scope_tab_framework: 'Esparrua',
            scope_tab_free_text: 'Testu librea',
            select_obligation: 'Hautatu betebehar bat',
            select_framework: 'Aukeratu marko bat',
            aria_remove_committee: 'Kendu {n} batzordea',
            aria_remove_delegation_rule: 'Kendu {n} eskuordetze-araua',
            no_frameworks_available: 'Ez dago markorik eskuragarri. Gehitu lehenik arau-esparruak.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dokumentuen zirriborroa',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Egoera',
            drafter: 'Idazlea',
            deadline: 'Epea',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Esleitu',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Berrikuslea',
            select_reviewer: '...',
            comments: 'Iruzkinak',
            review_action: 'Action',
            approve: 'Onartu',
            request_changes: 'Aldaketak eskatu',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Dokumentuen jaulkipena',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Egoera',
            version: 'Bertsioa',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Aitorpen-jarraipena',
            document_title: 'Dokumentua',
            acknowledged_count: 'Aitortua',
            pending_count: 'Zain',
            percentage: '%',
            details: 'Xehetasunak',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Erabiltzailea',
            department: 'Saila',
            acknowledged_at: 'Date',
            status: 'Egoera',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Aldaketa-erregistroa',
            date: 'Data',
            document: 'Dokumentua',
            change_type: 'Type',
            actor: 'Aktorea',
            summary: 'Laburpena',
            export_csv: 'Esportatu CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Berrikusketa-kadentzia',
            configure_cadence: 'Berrikusketa-kadentzia',
            edit_cadence: 'Berrikusketa-kadentzia',
            document: 'Dokumentua',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Egoera',
            overdue: 'Atzeratuta',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Konponketa-planak',
            create_plan: 'Konponketa-plana sortu',
            gap_id: 'Hutsune-erreferentzia',
            select_gap: 'Aukeratu hutsune bat...',
            plan_title: 'Izenburua',
            description: 'Deskribapena',
            priority: 'Lehentasuna',
            priority_critical: 'Kritikoa',
            priority_high: 'Altua',
            priority_medium: 'Ertaina',
            priority_low: 'Baxua',
            assigned_to: 'Esleitua',
            select_user: 'Aukeratu erabiltzaile bat...',
            target_date: 'Helburu-data',
            framework_code: 'Esparrua',
            select_framework: 'Aukeratu esparru bat...',
            gap_finding: 'Hutsune-aurkikuntza',
            action_items: 'Ekintza-elementuak',
            owner: 'Jabea',
            deadline: 'Epemuga',
            planned: 'Planifikatua',
            in_progress: 'Abian',
            completed: 'Osatua',
            verified: 'Egiaztatua',
            no_plans_found: 'Ez da konponketa-planik aurkitu.',
            evidence: 'Ebidentzia',
            plan_created: 'Konponketa-plana sortua',
            action_items_hint: 'Ekintza-elementuak (bat lerro bakoitzeko)',
        },
        statuses: {
            identified: 'Identifikatua',
            assigned: 'Esleitua',
            in_remediation: 'Erremediazioan',
            resolved: 'Ebatzita',
            accepted_risk: 'Onartutako Arriskua',
            planned: 'Planifikatua',
            in_progress: 'Eraikitzen',
            under_review: 'Berrikuspenean',
            finalized: 'Amaitua',
            archived: 'Artxibatuta',
            draft: 'Zirriborroa',
            active: 'Aktiboa',
            upcoming: 'Datorren',
            collecting: 'Biltzea',
            published: 'Argitaratu',
            review: 'Berrikuspena',
            approved: 'Onartua',
            issued: 'Igorria',
            superseded: 'Ordezkatuta',
            completed: 'Osatua',
            verified: 'Egiaztatuta',
        },
        severities: {
            critical: 'Kritikoa',
            high: 'Alta',
            medium: 'Ertaina',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritikoa',
            high: 'Alta',
            medium: 'Ertaina',
            low: 'Low',
        },
        gap_types: {
            missing: 'Faltan',
            expired: 'Iraungi egin da',
            insufficient: 'Nahikoa ez',
        },
        document_types: {
            policy: 'Politika',
            procedure: 'Prozedura',
            standard: 'Estandarra',
            guideline: 'Jarraibidea',
            template: 'Txantiloia (Barnekoa)',
        },
        maturity_levels: {
            level_1: '1. maila — Hasierakoa',
            level_2: '2. maila — Garatzen',
            level_3: '3. maila — Definitua',
            level_4: '4. maila — Kudeatua',
            level_5: '5. maila — Optimizazioa',
        },
        grades: {
            excellent: 'Bikaina',
            good: 'Ona',
            fair: 'Azoka',
            poor: 'Pobrea',
            critical: 'Kritikoa',
            needs_improvement: 'Hobetu behar da',
            not_assessed: 'Ez Ebaluatua',
        },
        modules: {
            data_mapping: 'Datuen mapaketa',
            consent: 'Adostasuna',
            governance: 'Gobernantza',
            incidents: 'Gorabeherak',
            monitoring: 'Jarraipena',
            classification: 'Sailkapena',
            vendor_risk: 'Hornitzaileen arriskua',
            discovery: 'Aurkikuntza',
            deletion: 'Ezabatzea',
        },
        cadences: {
            monthly: 'Hilerokoa',
            quarterly: 'Hiruhilabetekaria',
            semi_annual: 'Seiurtekoa',
            annual: 'Urtekoa',
        },
        section_labels: {
            opening: 'Irekiera',
            key_metrics: 'Funtsezko neurketak',
            highlights: 'Nabarmenak',
            concerns: 'Kezkak',
            recommendations: 'Gomendioak',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} hutsunea',
        count_gaps_plural: '{count} hutsune',
        count_summaries: '{count} laburpena',
        count_summaries_plural: '{count} laburpenak',
        count_assessments: '{count} ebaluazioa',
        count_assessments_plural: '{count} ebaluazioak',
        count_models: '{count} eredua',
        count_models_plural: '{count} ereduak',
        count_periods: '{count} puntua',
        count_periods_plural: '{count} aldi',
        count_metrics: '{count} metrika',
        count_metrics_plural: '{count} neurketak',
        unknown_user: 'Erabiltzaile ezezaguna',
        unknown_gap: 'Hutsune ezezaguna',
        no_permission_title: 'Baimen nahikoa ez',
        no_permission_message: 'Ez duzu orri honetara sartzeko baimenik.',
        ac_vn_frameworks_title: 'VN Esparruaren Estaldura',
        ac_vn_frameworks_body: 'Heldutasun-ebaluazioak Vietnamgo 6 arau-esparru guztiak hartzen ditu barne: PDPL, Datuen Legea, Telekomunikazioen Legea, Zibersegurtasun Legea, AI Legea eta Merkataritza Elektronikoaren Legea — bakoitza esparruko ebaluazio irizpide zehatzekin.',
        ac_controller_processor_title: 'Kontroladorea vs prozesadorea',
        ac_controller_processor_body: 'Betebeharren mapak datuen arduradunaren eta prozesadorearen erantzukizunak bereizten ditu. Esleitu rolak RACI matrizea erabiliz, betebehar bakoitzak jabe argia duela ziurtatzeko, erakundeak kontrolatzaile edo prozesadore gisa jokatzen duen.',
        ac_versioned_evidence_title: 'Versioned Evidence Trail',
        ac_versioned_evidence_body: 'Egoera-trantsizio bakoitzak (Zirriborroa, Berrikuspena, Onartua, Igorria) denbora-zigilua eta bertsiodun erregistro bat sortzen du, froga ikuskagarri gisa balio duena. Aldaketen erregistroak dokumentuaren bizi-zikloaren historia osoa jasotzen du.',
        ac_remediation_gap_link_title: 'Ebaluazio hutsuneekin lotuta',
        ac_remediation_gap_link_body: 'Zuzenketa-plan bakoitzak identifikatutako ebaluazio-hutsune batekin zuzenean lotzen du. Esleitu jabe bat eta ezarri helburu-epea hutsunetik itxiera arte konponbidearen aurrerapena jarraitzeko.',
        ac_annual_attestation_title: 'Urteko Aitorpen Zikloa',
        ac_annual_attestation_body: 'Txostenen epeak Vietnamgo Datuen Legearen urteko egiaztapen-eskakizunarekin bat datoz. Erabili "Urteko" kadentzia (365 eguneko zikloa) atestazioarekin lerrokaturiko aldiak automatikoki sortzeko.',
        ac_evidence_per_obligation_title: 'Esparru Betebeharraren Froga',
        ac_evidence_per_obligation_body: 'Froga hutsuneek esparru betebehar bakoitzerako froga falta, iraungi edo nahikoa ez dela identifikatzen dute. Iragazi domeinuaren arabera, egiaztagiririk gabeko zein arauzko eskakizun ez diren ikusteko.',
        ac_cross_framework_title: 'Zehar-esparruaren osotasuna',
        ac_cross_framework_body: 'Ikuskaritza-prestasunak markoen arteko osotasunaren ikuspegia eskaintzen du. Osagaien puntuazioek mapatutako esparru guztien estaldura islatzen dute, auditoria baten aurretik arreta behar duten arloak nabarmenduz.',
        ac_cadence_default_title: 'Berrikuspen lehenetsia Kadentzia',
        ac_cadence_default_body: 'Berrikusteko kadentzia lehenetsia 180 egunekoa da. Berandutako iritziak automatikoki markatzen dira. Doitu maiztasuna dokumentu bakoitzeko behar den moduan - aukerak 30 eta 365 egun bitartekoak dira.',
        ac_templates_internal_title: 'Txantiloien Sailkapena',
        ac_templates_internal_body: 'Txantiloiak barne dokumentu gisa sailkatzen dira. Erabili "Txantiloi" dokumentu mota berrerabil daitezkeen gobernantza-artefaktuak barneko banaketa-esparruarekin soilik markatzeko.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Idazketa zain daudenak',
        subtitle: 'Kanpoko Claude bezeroek idazketak proposatzen dituzte hemen. Onartu edo baztertu sarrera bakoitza — Forsetik administratzaileak baieztatu ondoren bakarrik exekutatzen du.',
        no_pending: 'Ez dago idazketarik zain.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Tresna',
        col_preview: 'Aurrebista',
        col_key: 'API tekla',
        col_requested: 'Eskatua',
        col_expires: 'Iraungitzen da',
        col_status: 'Egoera',
        status_pending: 'Zain',
        status_approved: 'Onartua',
        status_declined: 'Baztertua',
        status_expired: 'Iraungia',
        status_executed: 'Exekutatua',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Onartu',
        decline_label: 'Baztertu',
        refresh_label: 'Freskatu',
        error_generic: 'Ezin izan dira zain dauden idazketak kargatu.',
        confirm_approve: 'Onartzeak tresna zure erakundean exekutatuko du API key-aren jabeak dituen baimenekin. Jarraitu nahi duzu?',
        confirm_decline: 'Zain dagoen idazketa hau baztertu nahi duzu? Ezin da desegin.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Zain dauden idazketak sortu eta 24 ordura iraungitzen dira. Orduroko GC garbiketak iraungitako sarrerak automatikoki baztertzen ditu.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Ekintzak',
    },
    audit_walkthrough: {
        title: 'Forseti — Auditoria-ibilbidearen sortzailea',
        subtitle: 'Sortu auditoria baten hasierarako ibilbide egituratu bat: kontrolen hasierako galdera, espero den froga, jarraipeneko galderak, eta hutsune posibleak.',
        audit_picker_label: 'Auditoria',
        audit_picker_hint: 'Aukeratu planifikatutako auditoria bat ibilbidea sortzeko.',
        run_label: 'Ibilbidea sortu',
        running: 'Sortzen…',
        results_heading: 'Ibilbidea — {count} kontrol',
        tokens_label: '≈ {count} token',
        no_controls: 'Esparruan kontrolik ez. Gehitu kontrolak auditoria-erregistroan.',
        col_control: 'Kontrola',
        col_question: 'Hasierako galdera',
        col_evidence: 'Froga',
        col_gaps: 'Hutsune posibleak',
        expected_evidence_label: 'Espero den froga',
        followup_probes_label: 'Jarraipeneko galderak',
        accept_label: 'Ibilbidea onartu',
        reject_label: 'Baztertu',
        export_label: 'DOCX esportatu',
        resolve_summary: 'Ebazpena: {status}',
        resolve_status_accepted: 'Onartua',
        resolve_status_rejected: 'Baztertua',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Ezin izan da ibilbidea sortu. Saiatu berriro edo egiaztatu auditoriaren egoera.',
    },
    privilege_creep: {
        title: 'Forseti — Pribilegioen pilaketaren detekzioa',
        subtitle: 'Rol-esleipenen aldizkako eskaneaketa. Aurkikuntzak iradokizunak dira — Forsetik ez du inoiz automatikoki kentzen.',
        run_label: 'Eskaneatu orain',
        running: 'Eskaneatzen…',
        findings_heading: '{count} aurkikuntza',
        no_findings: 'Ez da pribilegio-pilaketarik markatu.',
        col_user: 'Erabiltzailea',
        col_job_function: 'Lan-funtzioa',
        col_severity: 'Larritasuna',
        col_unexpected: 'Ustekabeko baimenak',
        col_remediation: 'Iradokitako konponketa',
        col_status: 'Egoera',
        severity_critical: 'Kritikoa',
        severity_high: 'Altua',
        severity_medium: 'Ertaina',
        severity_low: 'Baxua',
        status_open: 'Irekia',
        status_dismissed: 'Baztertua',
        status_remediated: 'Konpondua',
        dismiss_label: 'Baztertu',
        remediate_label: 'Konpondutzat markatu',
        note_label: 'Oharra (aukerakoa)',
        error_generic: 'Ezin izan da pribilegio-pilaketa eskaneatu. Saiatu beranduago.',
        advisory_banner: 'Aurkikuntza hauek iradokizunak dira. Forsetik ez du inoiz baimenik automatikoki kentzen — berrikusi rol-jabearekin ekin aurretik.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Betetze-arauen mapa automatikoa',
            subtitle: 'Forsetik instalatutako erregulazio pakete bat erabiltzen du eta iturburu-artikuluetan oinarritutako betetze-arau berriak edo berrituak iradokitzen ditu.',
            pack_picker_label: 'Pakete erregulatzailea',
            pack_picker_hint: 'Hautatu instalatutako pakete bat mapan jartzeko.',
            run_label: 'Paketetik mapa',
            running: 'Kartografiatzen…',
            results_heading: '{count} iradokizun(k)',
            tokens_label: '≈ {count} token hileroko ontzitik',
            no_suggestions: 'Ez dago iradokizunik — baliteke paketearen edukia dagoeneko guztiz estalita egotea lehendik dauden arauek.',
            kind_new: 'Arau berria',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Ez da aldaketarik behar',
            col_kind: 'Jatorra',
            col_title: 'Proposatutako izenburua',
            col_existing: 'Dauden araua',
            col_source: 'Iturria',
            proposed_body_label: 'Proposatutako organoa',
            source_quote_label: 'Iturriaren aipua',
            accept_selected: 'Onartu hautatutakoa',
            reject_all: 'Guztiak baztertu',
            accept_all: 'Onartu guztiak',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Ezin izan da arauen mapaketa exekutatu. Saiatu berriro edo egiaztatu paketearen kodea.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Saio aginte-mahaia',
            access_log: 'Sarbide-erregistroa',
            justification_review: 'Justifikazio-berrikuspena',
            investigations: 'Ikerketak',
            pattern_analysis: 'Patroi-analisia',
            anchoring_status: 'Ainguratze-egoera',
            configuration: 'Konfigurazioa',
            access_policies: 'Sarbide-politikak',
            review_campaigns: 'Berrikusketa-kanpainak',
            evidence_packages: 'Ebidentzia-paketeak',
        },
        session_dashboard: {
            title: 'Saio aginte-mahaia',
            loading: 'Aginte-mahaiaren datuak kargatzen...',
            total_sessions: 'Saio guztira',
            active_sessions: 'Saio aktiboak',
            avg_duration: 'Batez besteko iraupena',
            peak_day_sessions: 'Egunik jendetsuena',
            auth_method_breakdown: 'Autentifikazio-metodoaren banaketa',
            geo_country_breakdown: 'Herrialdeen araberako banaketa',
            recent_session_events: 'Azken saio-gertaerak',
            user_id: 'Erabiltzaile IDa',
            session_id: 'Saioaren IDa',
            ip_address: 'IP helbidea',
            auth_method: 'Autentifikazio-metodoa',
            started_at: 'Hasita',
            event_type: 'Gertaera mota',
            all_event_types: 'Gertaera mota guztiak',
            filter_by_user: 'Iragazi erabiltzailearen arabera',
            no_active_sessions: 'Ez dago saio aktiborik',
            no_session_events: 'Ez da saio-gertaerarik aurkitu',
            revoke: 'Errebokatu',
            revoke_session: 'Errebokatu saioa',
            aria_revoke_session: 'Errebokatu {user} saioa',
            revoke_reason: 'Arrazoia',
            revoke_placeholder: 'Eman arrazoi bat saio hau errebokatzeko...',
            session_label: 'Saio:',
            load_failed: 'Huts egin du saioen aginte-mahaia kargatzeak. Saiatu berriro.',
            events_load_failed: 'Huts egin du saio-gertaerak kargatzeak. Saiatu berriro.',
            revoke_failed: 'Huts egin du saioa errebokatzeak. Saiatu berriro.',
            session_events_loaded: '{n} saio-gertaera kargatuta',
            event_types: {
                session_started: 'Saioa hasita',
                session_refreshed: 'Saioa freskatua',
                session_expired: 'Saioa iraungi da',
                session_revoked: 'Saioa baliogabetua',
                session_terminated: 'Saioa amaitu da',
            },
        },
        access_log: {
            title: 'Sarbide-erregistroa',
            user_id: 'Erabiltzaile IDa',
            resource: 'Baliabidea',
            action: 'Ekintza',
            ip_address: 'IP Helbidea',
            timestamp: 'Denbora-zigilua',
            no_logs: 'Ez da sarbide log entries found. aurkitu',
            filter_by_user: 'Iragazi by Erabiltzaile ID...',
            load_failed: 'Huts egin du: load sarbide log. Saiatu berriro.',
            export_success: 'Esportatu initiated successfully',
            export_failed: 'Huts egin du: export sarbide log. Saiatu berriro.',
            record_count: 'erregistro',
            record_count_plural: 'erregistroak',
            unclassified: 'Sailkatu gabea',
            classification_missing: 'Sailkapena falta da',
            classification_level: 'Sailkapen-maila',
            response_code: 'Erantzun kodea',
            access_surface: 'Sarbide-kanala',
            api_key: 'API gakoa',
            classification_levels: {
                public: 'Publikoa',
                internal: 'Barnekoa',
                confidential: 'Isilpekoa',
                restricted: 'Mugatuta',
                unclassified: 'Sailkatu gabea',
            },
        },
        justification_review: {
            title: 'Justifikazio-berrikuspena',
            user_id: 'Erabiltzaile IDa',
            resource: 'Baliabidea',
            justification: 'Justifikazio',
            submitted_at: 'Bidalita',
            reviewer: 'Berrikuskatzaile',
            no_reviews: 'Ez da justifikazio reviews found. aurkitu',
            load_failed: 'Huts egin du: load justifikazio. Saiatu berriro.',
            review_failed: 'Huts egin du: review justifikazio. Saiatu berriro.',
            decline_failed: 'Huts egin du: decline sarbide. Saiatu berriro.',
            justification_count: 'justifikazio',
            justification_count_plural: 'justifikazio',
            reviewed: 'Berrikusita',
            concerning: 'Kezkatzekoa',
            escalated: 'Eskalatuta',
            link_investigation_placeholder: 'Link to an ikerketa (optional)',
            review_queue: 'Berrikusketa Queue',
            access_id_label: 'Sarbide-IDa',
            access_id_placeholder: 'Sarbide-erregistroaren sarrera-IDa',
            resource_type_label: 'Baliabide-mota',
            resource_type_placeholder: 'adib. document, database',
            resource_id_label: 'Baliabidearen IDa',
            resource_id_placeholder: 'Baliabidearen identifikatzailea',
            reason_placeholder: 'Baliabide honetara sartzeko arrazoia...',
            create_failed: 'create justification huts egin du',
            unclassified: 'Sailkatu gabea',
            review_statuses: {
                pending: 'Zain',
                reviewed: 'Berrikusi',
                concerning: 'Buruz',
                escalated: 'Goratuta',
            },
            reason_codes: {
                business_need: 'Negozio beharra',
                legal_obligation: 'Lege-betebeharra',
                data_subject_request: 'Datuen Subjektuaren eskaera',
                audit_requirement: 'Ikuskaritza-eskakizuna',
                incident_response: 'Gorabeheren Erantzuna',
                research: 'Ikerketa',
                administrative: 'Administratiboa',
                other: 'Bestela',
            },
            classification_level: 'Sailkapen-maila',
            review_decision: 'Berrikusi Erabakia',
        },
        investigations: {
            title: 'Ikerketa Workspace',
            new: 'Berria Ikerketa',
            subject: 'Subjektua',
            investigator: 'Ikertzailea',
            opened_at: 'Irekia',
            findings: 'Aurkikuntzak',
            no_investigations: 'Ez da ikerketak found. aurkitu',
            load_failed: 'Huts egin du: load ikerketak. Saiatu berriro.',
            create_failed: 'Huts egin du: create ikerketa. Saiatu berriro.',
            timeline_add_failed: 'Huts egin du: add timeline sarrera. Saiatu berriro.',
            link_access_failed: 'Huts egin du: link sarbide IDs. Saiatu berriro.',
            link_justification_failed: 'Huts egin du: link justifikazio IDs. Saiatu berriro.',
            close_failed: 'Huts egin du: close ikerketa. Saiatu berriro.',
            export_success: 'Esportatu initiated successfully',
            export_failed: 'Huts egin du: export ikerketa. Saiatu berriro.',
            refresh_failed: 'Huts egin du: refresh ikerketa detail. Saiatu berriro.',
            update_failed: 'Ikerketa eguneratzeak huts egin du',
            investigation_count: 'ikerketa',
            investigation_count_plural: 'ikerketak',
            title_placeholder: 'Ikerketa title',
            description_placeholder: 'Detailed description of the ikerketa...',
            assignee_placeholder: 'Esleitutakoaren erabiltzaile IDa (hautazkoa)',
            timeline_action_placeholder: 'adib. Berrikusitako sarbide-erregistroak',
            timeline_note_placeholder: 'Hautazkoa note',
            access_ids_placeholder: 'sarbide-id-1, sarbide-id-2',
            justification_ids_placeholder: 'justifikazio-id-1, justifikazio-id-2',
            findings_placeholder: 'Summarize the ikerketa findings...',
            by_actor: 'by',
            close_investigation: 'Itxi Ikerketa',
            add_timeline_failed: 'Huts egin du: add timeline sarrera. Saiatu berriro.',
            fetch_failed: 'Huts egin du: fetch ikerketak. Saiatu berriro.',
            change_status: 'Aldatu ikerketa-egoera',
            details: 'Ikerketaren xehetasunak',
            link_evidence: 'Estekatu ebidentzia',
            link_justifications: 'Estekatu justifikazioak',
            access_ids_label: 'Sarbide-IDak',
            justification_ids_label: 'Justifikazio-IDak',
            access_ids_link_placeholder: 'sarbide-id-1, sarbide-id-2',
            justification_ids_link_placeholder: 'justifikazio-id-1, justifikazio-id-2',
            investigations_loaded: '{n} investigations kargatuta',
            status_changed_to: 'Egoera {status} aldatu da',
            add_timeline_entry: 'Gehitu denbora-lerroko sarrera',
            add_access_ids: 'Gehitu sarbide-IDak',
            add_justification_ids: 'Gehitu justifikazio-IDak',
            investigation_types: {
                anomaly_review: 'Anomalia Berrikuspena',
                complaint_driven: 'Kexa bultzatuta',
                scheduled_review: 'Programatutako Berrikuspena',
                whistleblower_report: 'Txistularien txostena',
            },
            severity_labels: {
                low: 'Baxua',
                medium: 'Ertaina',
                high: 'Alta',
                critical: 'Kritikoa',
            },
            status_labels: {
                open: 'Ireki',
                in_progress: 'Eraikitzen',
                pending_review: 'Berrikuspenaren zain',
                closed_substantiated: 'Itxita (frogatuta)',
                closed_unsubstantiated: 'Itxita (Frogarik gabekoa)',
                closed_inconclusive: 'Itxita (Ondorioz)',
            },
            disposition_labels: {
                substantiated: 'Oinarrituta',
                unsubstantiated: 'Funtsik gabekoa',
                inconclusive: 'Konklusiboa',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Patroi-analisia',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'pattern datu kargatzen...',
            anomalies_detected: 'Detektatutako anomaliak',
            patterns: 'Ereduak',
            by_user: 'By Erabiltzaile',
            by_resource: 'Baliabidearen arabera',
            time_range: 'Ordua Tartea',
            load_failed: 'Huts egin du: analyze patterns. Saiatu berriro.',
            top_resources: 'Lehen 10ak',
            access_by_hour: 'Sarbidea eguneko orduaren arabera',
            access_by_day: 'Sarbidea asteko egunaren arabera',
            fetch_failed: 'Huts egin du: fetch pattern analysis. Saiatu berriro.',
            analysis_loaded: 'Patroi-analisia {user} erabiltzailearentzat kargatuta, {n} saio aurkituta',
            date_format_placeholder: 'EE/HH/UUUU',
            access_by_hour_heading: 'Sarbidea eguneko orduaren arabera',
            access_by_day_heading: 'Sarbidea asteko egunaren arabera',
            by_classification: 'Sailkapen-mailaren arabera',
            top_accessed_resources: 'Gehien atzitutako baliabideak',
            user_access_summary: 'Erabiltzaile-sarbidearen laburpena',
            days: {
                sunday: 'Igandea',
                monday: 'Astelehena',
                tuesday: 'Asteartea',
                wednesday: 'Asteazkena',
                thursday: 'Osteguna',
                friday: 'Ostirala',
                saturday: 'Larunbata',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Ainguratze-egoera',
            loading: 'anchoring datu kargatzen...',
            anchored: 'Ainguratuta',
            pending: 'Zain',
            failed: 'Huts egin',
            last_anchor: 'Last Aingura',
            no_records: 'Ez da anchoring erregistroak found. aurkitu',
            status_load_failed: 'Huts egin du: load anchoring egoera. Saiatu berriro.',
            proofs_load_failed: 'Huts egin du: load sarbide frogak. Saiatu berriro.',
            verify_failed: 'Huts egin du: verify froga chain. Saiatu berriro.',
            anchor_failed: 'Huts egin du: anchor sarbide froga. Saiatu berriro.',
            batch_anchor_failed: 'Huts egin du: batch anchor sarbide frogak. Saiatu berriro.',
            proof_count: 'froga',
            proof_count_plural: 'frogak',
            anchor_single: 'Aingura bakarra',
            anchor_batch: 'Aingura-sorta',
            access_id_placeholder: 'Sartu sarbide id to anchor',
            batch_ids_placeholder: 'sarbide-id-1, sarbide-id-2, sarbide-id-3',
            verify_chain: 'Egiaztatu katea',
            chain_breaks: 'Katea Breaks',
            total_checked: 'Checked guztira',
            anchor_placeholder: 'Sartu sarbide id to anchor',
            fetch_failed: 'Huts egin du: fetch anchoring egoera. Saiatu berriro.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Huts egin du: fetch frogak. Saiatu berriro.',
            access_id: 'Sarbide-IDa',
            chain_sequence: 'Kate-sekuentzia',
            proof_hash_label: 'Froga-hash-a',
            batch_id: 'Lote-IDa',
            proofs_loaded: '{n} access proofs kargatuta',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Zain',
                confirmed: 'Baieztatuta',
                failed: 'Huts egin du',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Katea',
                blockchain_anchor: 'Blockchain aingura',
                zkp_stub: 'ZKP zirriborroa',
            },
        },
        configuration: {
            title: 'Konfigurazioa',
            session_settings: 'Saio Ezarpenak',
            log_retention: 'Erregistro Atxikipen (egun)',
            anomaly_threshold: 'Anomalia-atalasea',
            anchoring_enabled: 'Ainguraketa gaituta',
            save_config: 'Gorde Konfigurazio',
            load_failed: 'Huts egin du: load configuration. Saiatu berriro.',
            save_success: 'Konfigurazio saved successfully',
            save_failed: 'Huts egin du: save configuration. Saiatu berriro.',
            justification_rules: 'Justifikazio Arauak',
            reason_codes: 'Reason Kodea',
            anchoring_settings: 'Ainguraketa-ezarpenak',
            anchoring_method: 'Ainguraketa-metodoa',
            anchoring_frequency: 'Ainguraketa-maiztasuna',
            batch_interval: 'Sorta-tartea (minutu)',
            eligibility_criteria: 'Hautagarritasun-irizpideak',
            add_criterion_placeholder: 'Gehitu hautagarritasun-irizpidea...',
            retention_settings: 'Atxikipen Ezarpenak',
            standard_retention: 'Estandarra Atxikipen (egun)',
            sensitive_retention: 'Sensitive Atxikipen (egun)',
            working_hours: 'Lan-orduak',
            work_start: 'Hasi Ordua',
            work_end: 'End Ordua',
            timezone: 'Ordu-eremua',
            timezone_placeholder: 'adib. Asia/Ho_Chi_Minh',
            working_days: 'Lan-egunak',
            concurrent_sessions: 'Concurrent Saio Muga',
            concurrent_threshold: 'Concurrent Saio Atalase',
            classification_level: 'Sailkapena Maila',
            prompt_mode: 'Gonbidapen-modua',
            grace_period: 'Grace Aldi (s)',
            code_placeholder: 'kodea',
            is_custom: 'Pertsonalizatua',
            label_placeholder: 'Etiketa',
            description_placeholder: 'Deskribapena',
            custom: 'Pertsonalizatua',
            fetch_failed: 'Huts egin du: fetch configuration. Saiatu berriro.',
            classification_levels: {
                public: 'Publikoa',
                internal: 'Barnekoa',
                confidential: 'Isilpekoa',
                restricted: 'Mugatuta',
                unclassified: 'Sailkatu gabea',
            },
            prompt_modes: {
                reason_only: 'Arrazoia Bakarrik',
                reason_text: 'Arrazoia + Testua',
                reason_text_supervisor: 'Arrazoia + Testua + Begiralea',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Katea',
                blockchain_anchor: 'Blockchain aingura',
                zkp_stub: 'ZKP zirriborroa',
            },
            anchoring_frequencies: {
                realtime: 'Denbora errealean',
                batched: 'Loteka',
                on_demand: 'Eskaera',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Sarbide-politikak',
            create_policy: 'Sarbide-politika sortu',
            policy_name: 'Politikaren izena',
            framework_code: 'Esparrua',
            select_framework: 'Esparru bat hautatu...',
            review_frequency: 'Berrikusketa-maiztasuna',
            scope: 'Irismena',
            no_policies_found: 'Ez da sarbide-politikarik aurkitu.',
            policy_created: 'Sarbide-politika sortua',
            frequency_labels: {
                monthly: 'Hilabetekoa',
                quarterly: 'Hiruhilabetekoa',
                semi_annual: 'Sei-hilabetekoa',
                annual: 'Urtekoa',
            },
        },
        review_campaigns: {
            title: 'Sarbide-berrikusketa kanpainak',
            create_campaign: 'Berrikusketa-kanpaina sortu',
            reviewer: 'Berrikuskatzailea',
            due_date: 'Muga-data',
            certify: 'Ziurtatu',
            no_campaigns_found: 'Ez da sarbide-berrikusketa kanpainarik aurkitu.',
            status_open: 'Irekia',
            status_in_review: 'Berrikusketan',
            status_completed: 'Osatua',
            campaign_created: 'Berrikuspenen kanpaina sortua',
            review_certified: 'Sarbide-berrikuspena ziurtatua',
            certify_campaign: 'Ziurtatu {id} kanpaina',
            policy_id: 'Politikaren IDa',
            select_policy: 'Politika bat hautatu...',
            reviewer_ids_label: 'Berrikuskatzaileak',
            select_reviewers: 'Berrikuskatzaileak hautatu...',
            status_labels: {
                open: 'Irekia',
                in_review: 'Berrikuspenean',
                in_progress: 'Aurrera doa',
                cancelled: 'Bertan behera utzita',
                completed: 'Osatua',
            },
        },
        evidence_packages: {
            title: 'Ebidentzia-paketeak',
            generate_package: 'Ebidentzia-paketea sortu',
            export_type: 'Esportazio mota',
            package_type: 'Pakete mota',
            framework_scope: 'Esparru-irismena',
            period: 'Aldia',
            generated_by: 'Sortua nork',
            generated_at: 'Sortua noiz',
            no_packages_found: 'Ez da ebidentzia-paketerik aurkitu.',
            package_generated: 'Ebidentzia-paketea sortua',
            export_type_full: 'Esportazio osoa',
            export_type_summary: 'Laburpena',
            export_type_certification_only: 'Ziurtapena soilik',
            package_type_access_review: 'Sarbide-berrikuspena',
            package_type_investigation: 'Ikerketa',
            package_type_certification: 'Ziurtapena',
            package_type_authority_request: 'Agintaritza-eskaera',
            period_start: 'Aldiaren hasiera',
            period_end: 'Aldiaren amaiera',
            select_frameworks: 'Hautatu esparruak...',
            completeness_col: 'Osotasuna',
            status_col: 'Egoera',
            generated_by_col: 'Sortutako By',
        },
        evidence_package_detail: {
            title: 'Ebidentzia-paketearen xehetasuna',
            not_found: 'Ebidentzia-paketea ez da aurkitu.',
            package_metadata: 'Paketearen metadatuak',
            status: 'Egoera',
            item_count: 'Elementu kopurua',
            disposition: 'Xedapena',
            closed_at: 'Itxia',
            completeness_dashboard: 'Osotasun panela',
            no_completeness_data: 'Ez dago osotasun daturik.',
            controls_covered: 'kontrol estaliak',
            missing: 'Falta',
            sign_off_tracker: 'Onespen jarraipena',
            stage_framework_owner: 'Esparru jabea',
            stage_legal_compliance: 'Lege eta Betetze',
            stage_internal_audit: 'Barne auditoria',
            approved: 'Onartua',
            returned: 'Itzulia',
            pending: 'Zain',
            approve: 'Onartu',
            return_btn: 'Itzuli',
            sign_off_submitted: 'Onespena bidalita',
            artifacts: 'Artefaktuak',
            no_artifacts: 'Ez da artefakturik aurkitu.',
            template_id: 'Txantiloi IDa',
            description: 'Deskribapena',
            classification: 'Sailkapena',
            evidence_type: 'Ebidentzia mota',
            framework_section: 'Esparru atala',
            history: 'Onespen historia',
            expand: 'Zabaldu',
            collapse: 'Tolestu',
            action: 'Ekintza',
            actor: 'Aktorea',
            timestamp: 'Denbora-marka',
            comment: 'Iruzkina',
            close_case: 'Kasua itxi',
            confirm_close: 'Itxiera berretsi',
            closure_notes: 'Itxiera oharrak',
            disposition_compliant: 'Bateragarria',
            disposition_non_compliant: 'Bateraezina',
            disposition_partial: 'Partziala',
            case_closed: 'Ebidentzia-paketea itxia',
            export_package: 'Paketea esportatu',
            export_complete: 'Ebidentzia-paketea esportatua',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Aginte-mahaia',
            properties: 'Propietateak',
            groups: 'Taldeak',
            bindings: 'Loturak',
            change_requests: 'Aldaketa-eskaerak',
            comparison: 'Konparazioa',
            inconsistencies: 'Inkoherentziak',
            snapshot_schedules: 'Argazki-programazioak',
            golden_templates: 'Urrezko txantiloiak',
            evidence_packs: 'Ebidentzia-paketeak',
            audit: 'Auditoria',
        },
        select_property: 'Jabetza hautatu…',
        select_policy: 'Politika hautatu…',
        select_properties: 'Jabetzak hautatu…',
        select_target_properties: 'Helburu-jabetzak hautatu…',
        select_owner: 'Jabea hautatu…',
        select_technical_contact: 'Kontaktu teknikoa hautatu…',
        property_statuses: {
            active: 'Aktibo',
            inactive: 'Ez aktibo',
            pending: 'Zain',
            decommissioned: 'Baja emanda',
        },
        dashboard: {
            title: 'Propietate-arteko aginte-mahaia',
            loading: 'dashboard datu kargatzen...',
            total_properties: 'Jabetzak guztira',
            active_properties: 'Jabetza aktiboak',
            snapshot_coverage: 'Argazki-estaldura',
            properties_covered: 'jabetza estalita',
            total_bindings: 'Lotura guztira',
            deployed_bindings: 'Zabaldua',
            pending_bindings: 'Zabaltzeko zain',
            deployment_progress: 'Zabaltze-aurrerapena',
            on_target: 'Helburuan',
            below_target: 'Helburuaren azpitik',
            target_label: 'Helburua',
            total_groups: 'Taldeak guztira',
            pending_changes: 'Zain Changes',
            inconsistencies: 'Inkoherentziak',
            recent_activity: 'Recent Jarduera',
            no_recent_activity: 'Ez dago azken jarduerarik.',
            fetch_error: 'Huts egin du: load dashboard. Saiatu berriro.',
        },
        properties: {
            title: 'Propietate-erregistroa',
            new: 'Berria Jabetza',
            edit: 'Editatu Jabetza',
            property_name: 'Izena',
            property_type: 'Mota',
            identifier: 'Identifikatzailea',
            environment: 'Ingurunea',
            owner_id: 'Jabea ID',
            technical_contact_id: 'Kontaktu tekniko IDa',
            decommission: 'Desegitaratu',
            confirm_decommission: 'Ziur zaude decommission this property?',
            no_properties: 'Ez da properties found. aurkitu',
            all_environments: 'Ingurune guztiak',
            all_types: 'Mota guztiak',
            cmp_instance_id: 'CMP Instantzia ID',
            deployment_date: 'Inplementazio-data',
            identifier_placeholder: 'adibidez, www.example.com edo com.example.app',
            owner_name: 'Jabea',
            technical_contact_name: 'Kontaktu teknikoa',
            sensitive_data_categories: 'Datu sentikorren kategoriak',
            sensitive_data_categories_placeholder: 'Hautatu datu sentikorren kategoriak',
            requires_enhanced_review: 'Berrikuspen hobetua eskatzen du',
            sensitive_review_status: 'Datu sentikorren berrikuspena',
            sensitive_review_pending: 'Berrikuspenaren zain',
            sensitive_review_approved: 'Onartua',
            sensitive_review_rejected: 'Baztertua',
            sensitive_review_submit: 'Iritzia bidali',
            sensitive_review_approve: 'Onartu',
            sensitive_review_reject: 'Baztertu',
            sensitive_review_notes: 'Berrikusi Oharrak',
            sensitive_review_notes_placeholder: 'Sartu berrikuspen-oharrak...',
            record_withdrawal: 'Erregistroa ateratzea',
            withdrawal_subject_id: 'Gaiaren IDa',
            withdrawal_consent_id: 'Baimenaren IDa',
            withdrawal_reason: 'Arrazoia',
            withdrawal_downstream_systems: 'Downstream Sistemak',
            withdrawal_downstream_placeholder: 'sistema-1, sistema-2',
            withdrawal_success: 'Erretiratzea ondo hedatu da.',
            withdrawal_error: 'Ezin izan da erretiratzea zabaldu.',
            withdrawal_data_subject_id: 'Datu-subjektuaren IDa',
            withdrawal_data_subject_id_placeholder: 'adib. usr_abc123',
            withdrawal_consent_record_id: 'Baimenaren Erregistroaren IDa',
            withdrawal_consent_record_id_placeholder: 'adib. consent_xyz789',
            withdrawal_purposes: 'Erretiratutako Helburuak',
            withdrawal_purposes_placeholder: 'Hautatu baimena kentzeko helburuak...',
            withdrawal_withdrawn_at: 'Atzera egiteko data',
            withdrawal_tasks_created: 'Sortutako atazak',
            withdrawal_affected_bindings: 'Eragindako Loturak',
            withdrawal_affected_activities: 'Eragindako Prozesamendu-jarduerak',
            withdrawal_history: 'Erretiratzea Historia',
        },
        groups: {
            title: 'Propietate-taldeak',
            new: 'Berria Talde',
            group_name: 'Talde Izena',
            member_count: 'Kideak',
            no_groups: 'Ez da property groups found. aurkitu',
            policies: 'Politikak',
            confirm_delete: 'Ziur talde hau ezabatu nahi duzula?',
        },
        bindings: {
            title: 'Lotura-matrizea',
            property: 'Jabetza',
            purpose: 'Xede',
            legal_basis: 'Oinarri legala',
            bound: 'Lotua',
            no_bindings: 'Ez da bindings found. aurkitu',
            bind_policy: 'Lotu politika',
            bulk_bind: 'Bulk Bind',
            bind_policy_to_property: 'Lotu politika jabetzarekin',
            bulk_bind_policy: 'Lotura ugariko politika',
            unbound: 'Lotu gabe',
            policy_id: 'Politika IDa',
            version_placeholder: 'adibidez, 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Lotura',
            property_name: 'Propietatearen izena',
            policy_name: 'Politikaren izena',
            bindings_list: 'Loturen zerrenda',
            binding_actions: {
                start_deploy: 'Zabalkundea hasi',
                mark_deployed: 'Zabalduta markatu',
                rollback: 'Atzera egin',
            },
        },
        change_requests: {
            title: 'Aldaketa-eskaera prozesua',
            new: 'Berria Change Eskaera',
            requester: 'Eskatzailea',
            change_type: 'Change Mota',
            property: 'Jabetza',
            submitted_at: 'Bidalita',
            no_requests: 'Ez da change requests found. aurkitu',
            fetch_pipeline_error: 'Huts egin du: fetch pipeline. Saiatu berriro.',
            fetch_list_error: 'Huts egin du: fetch change requests. Saiatu berriro.',
            create_error: 'Huts egin du: create change request. Saiatu berriro.',
            action_error: 'Huts egin du: perform action. Saiatu berriro.',
            approver_name: 'Onartzailea',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Denak',
            view_my_properties: 'Nire Propietateak',
            stage_requests: '{stage} eskaerak',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Konparaketa-lan eremua',
            select_properties: 'Hautatu jabetzak to compare',
            compare: 'Konparatu',
            no_comparison: 'Hautatu at least two properties to compare.',
            fetch_error: 'Huts egin du: fetch comparison reports. Saiatu berriro.',
            run_error: 'Huts egin du: run comparison. Saiatu berriro.',
            save_notes_error: 'Huts egin du: save notes. Saiatu berriro.',
            scan_complete: 'Inkoherentzia-eskaneatuak arrakastaz osatu du.',
            scan_error: 'Huts egin du: run inconsistency scan. Saiatu berriro.',
            min_two_properties: 'Mesedez, eman gutxienez 2 jabetza ID konparaketarako.',
            view_inconsistencies: 'Ikusi inkoherentziak',
            click_to_select: 'Egin klik bi jabetza edo gehiago hautatzeko',
            no_properties_available: 'Ez dago jabetzarik eskuragarri',
            no_matching_properties: 'Ez dago bat datorren jabetzarik',
            select_at_least_two_props: 'Hautatu gutxienez bi jabetza goialdeko zerrendatik.',
            no_differences_found: 'Ez da desberdintasunik aurkitu hautatutako jabetzen artean.',
            found_inconsistencies: '{count} inkoherentzia aurkitu dira {properties} jabetza eta {bindings} lokarrien artean.',
        },
        inconsistencies: {
            title: 'Inkoherentzia-zerrenda',
            property: 'Jabetza',
            field: 'Eremua',
            expected: 'Espero dena',
            actual: 'Benetakoa',
            severity: 'Larritasun',
            no_inconsistencies: 'Ez da inconsistencies found. aurkitu',
            trend: 'Inkoherentziaren joera',
            trend_chart_label: 'Inkoherentziaren joera-diagrama denboran zehar zenbaketak erakusten dituena',
            acknowledge: 'Aitortu',
            resolve: 'Ebatzi',
            suppress: 'Ezabatu',
            cr_id_placeholder: 'CR IDa',
            view_all: 'Denak',
            view_my_properties: 'Nire Propietateak',
            view_dpo_review: 'DPO Berrikuspen-ilara',
            change_request_id_required: 'Aldaketa eskaeraren IDa beharrezkoa da inkoherentzia hau konpontzeko.',
        },
        snapshot_schedules: {
            title: 'Argazki-programazioak',
            new: 'Berria Ordutegi',
            schedule_name: 'Ordutegi Izena',
            frequency: 'Maiztasuna',
            next_run: 'Hurrengoa Exekutatu',
            last_run: 'Last Exekutatu',
            no_schedules: 'Ez da snapshot schedules found. aurkitu',
            target_type: 'Helburu mota',
            target_id: 'Helburuaren IDa',
            custom_frequency_days: 'Maiztasun pertsonalizatua (egunak)',
            time_of_day: 'Eguneko ordua',
            time: 'Denbora',
            retention_days: 'Atxikipena (egunak)',
            execute_now: 'Exekutatu orain',
            placeholder_days: 'adibidez, 10',
            target_name: 'Helburuaren izena',
        },
        golden_templates: {
            title: 'Urrezko txantiloiak',
            new: 'Berria Txantiloi',
            template_name: 'Txantiloi Izena',
            property_type: 'Jabetza Mota',
            no_templates: 'Ez da golden txantiloiak found. aurkitu',
            applicable_group_ids: 'Talde ID aplikagarriak (komaz bereizita)',
            applicable_groups: 'Talde aplikagarriak',
            purposes: 'Helburuak',
            legal_bases: 'Oinarri juridikoak',
            banner_config: 'Banner konfigurazioa',
            consent_settings: 'Baimen-ezarpenak',
            group_ids_placeholder: 'talde-id-1, talde-id-2',
            apply_to_property: 'Jabetzari Aplikatu',
            apply_to_group: 'Taldeari Aplikatu',
            apply_success: 'Txantiloia arrakastaz aplikatu da.',
            applications: 'Aplikazio-historia',
            no_applications: 'Ez dago txantiloi-aplikaziorik oraindik.',
            no_configuration_details: 'Txantiloiak ez du konfigurazio xehetasunik. Editatu txantiloia helburuak, oinarri juridikoak, banner ezarpenak edo baimen ezarpenak gehitzeko.',
        },
        rollouts: {
            title: 'Txantiloi-hedapenak',
            rollout_status: 'Hedapen-egoera',
            confirmed: 'Baieztatua',
            pending: 'Zain',
            failed: 'Huts egina',
            confirm: 'Jabetza Baieztatu',
            confirm_success: 'Jabetza hedapenean baieztatua.',
            can_close: 'Jabetza guztiak baieztatuta — hedapena osatua',
            cannot_close: 'Jabetza-baieztapenen zain',
            no_rollouts: 'Ez da hedapenik aurkitu.',
            in_progress: 'Abian',
            completed: 'Osatua',
            cancelled: 'Bertan behera utzita',
            reject: 'Jabetza Baztertu',
            reject_success: 'Jabetza hedapenetik baztertua.',
            confirm_all: 'Guztiak Baieztatu',
            reject_all: 'Guztiak Baztertu',
            confirm_label: 'Hedapena baieztatu honentzat',
            reject_label: 'Hedapena baztertu honentzat',
            confirm_all_label: 'Zain dauden Hedapen Guztiak Baieztatu',
            reject_all_label: 'Zain dauden Hedapen Guztiak Baztertu',
            no_properties_in_rollout: 'Hedapen honek ez du propietaterik.',
        },
        analytics: {
            title: 'Jabetza-analitika',
            opt_in_rate: 'Opt-in tasa',
            opt_out_rate: 'Opt-out tasa',
            withdrawal_rate: 'Erretiratzeko tasa',
            total_consents: 'Baimenak guztira',
            total_withdrawals: 'Erretiratzeak guztira',
            compliance_score: 'Betetze-puntuazioa',
            non_compliant: 'Betetzen ez duten jabetzak',
            no_non_compliant: 'Jabetza guztiak betetzen dute.',
            threshold: 'Betetze-atalasea',
            property_id: 'Jabetza ID',
            compliant: 'Konforme',
            non_compliant_status: 'Ez-betetzea',
            compliance_help_summary: 'Nola kalkulatzen da betetze-puntuazioa, eta nola konpondu dezaket?',
            compliance_help_intro: 'Jabetza bat ez-betetzen dela adierazten da bere puntuazioa % 80tik beherakoa denean. Puntuazioa honela kalkulatzen da:',
            compliance_help_with_template_label: 'Urrezko txantiloia aplikatzen bada:',
            compliance_help_with_template_text: 'Txantiloiarekin zehazki bat datozen konfigurazio-gakoen % (helburuak, legal_bases, banner_config, consent_settings).',
            compliance_help_no_template_label: 'Urrezko txantiloirik aplikatzen ez bada:',
            compliance_help_no_template_text: 'deployment_status = zabaldutako loturen % (jabetzari esleitutako adostasun motak). Loturarik gabeko propietate batek %100eko lehenespena du.',
            compliance_help_fix_intro: 'Propietate bat zerrendatik kentzeko, egin hauetako bat:',
            compliance_help_fix_deploy_label: 'Zabaldu bere loturak',
            compliance_help_fix_deploy_text: 'ireki Binding Matrix orria eta ezarri lotura bakoitzaren hedapen-egoera zabalduta.',
            compliance_help_fix_template_label: 'Aplikatu Urrezko Txantiloi bat',
            compliance_help_fix_template_text: 'erabili Aplikatu Taldeari txantiloian, eta, ondoren, berretsi propietate honen inplementazioa. Ziurtatu jabetzaren konfigurazioa txantiloiarekin bat datorrela.',
            compliance_help_fix_decommission_label: 'Jabetza desafektatzea',
            compliance_help_fix_decommission_text: 'erabili Deskargatzeko botoia jabetza-xehetasun modalitatean jada erabiltzen ez bada.',
            compliance_help_fix_no_bindings_label: 'Kendu lotura guztiak',
            compliance_help_fix_no_bindings_text: 'propietate batek txantiloirik eta loturarik ez badu, %100eko puntuazioa lortzen du lehenespenez.',
        },
        evidence_packs: {
            title: 'Ebidentzia-paketeak',
            new: 'Berria Pack',
            pack_name: 'Pack Izena',
            properties: 'Jabetzak',
            generated_at: 'Sortua',
            no_packs: 'Ez da ebidentzia packs found. aurkitu',
            generate: 'Sortu',
            content_type_consent_records: 'Adostasun Erregistroak',
            content_type_policy_versions: 'Politika Bertsioak',
            content_type_binding_history: 'Binding Historia',
            content_type_change_requests: 'Aldaketa-eskaerak',
            content_type_inconsistency_reports: 'Inconsistency Txostenak',
            content_type_snapshot_data: 'Argazki Datu',
            content_type_audit_trail: 'Ikuskaritza Trail',
        },
        audit: {
            title: 'Baimen-propietateen auditoria',
            event_type: 'Gertaera mota',
            entity_type: 'Entity Mota',
            entity_id: 'Entitate IDa',
            actor: 'Aktorea',
            timestamp: 'Denbora-zigilua',
            no_events: 'Ez da audit events found. aurkitu',
            entity_type_property: 'Propietatea',
            entity_type_group: 'Taldea',
            entity_type_binding: 'Lotura',
            entity_type_change_request: 'Aldaketa-eskaera',
            entity_type_golden_template: 'Urrezko txantiloia',
            entity_type_snapshot_schedule: 'Argazki-egutegia',
            entity_type_inconsistency: 'Inkoherentzia',
            entity_type_evidence_pack: 'Ebidentzia-paketea',
            action_created: 'Sortua',
            action_updated: 'Eguneratua',
            action_deleted: 'Ezabatua',
            action_decommissioned: 'Desegina',
            action_status_changed: 'Egoera aldatua',
            action_submitted: 'Bidalita',
            action_approved: 'Onartua',
            action_rejected: 'Baztertua',
            action_deployed: 'Hedatua',
            action_rolled_back: 'Itzulita',
            action_paused: 'Gelditua',
            action_resumed: 'Berrekina',
            action_cancelled: 'Bertan behera',
            action_acknowledged: 'Aitortua',
            action_resolved: 'Ebatzita',
            action_suppressed: 'Ezabatua',
            action_executed: 'Exekutatua',
        },
        property_types: {
            website: 'Webgunea',
            mobile_app: 'Mugikor-aplikazioa',
            ott: 'OTT',
            iot_device: 'IoT gailua',
            kiosk: 'Kioskoa',
            api_service: 'API zerbitzua',
            sdk: 'SDK',
        },
        environments: {
            production: 'Ekoizpena',
            staging: 'Eszenaratzea',
            development: 'Garapena',
            testing: 'Proba',
        },
        group_types: {
            brand: 'Marka',
            region: 'Eskualdea',
            product_line: 'Produktu-lerroa',
            business_unit: 'Negozio-unitatea',
            custom: 'Pertsonalizatua',
        },
        target_types: {
            property: 'Propietatea',
            group: 'Taldea',
            all: 'Denak',
        },
        frequencies: {
            hourly: 'Orduro',
            daily: 'Egunero',
            weekly: 'Astero',
            monthly: 'Hilero',
            custom: 'Pertsonalizatua',
        },
        binding_statuses: {
            current: 'Unekoa',
            outdated: 'Zaharkitua',
            pending: 'Zain',
            deploying: 'Hedatzen',
            rolled_back: 'Itzulita',
        },
        sensitive_categories: {
            health: 'Osasun Datuak',
            biometric: 'Datu biometrikoak',
            genetic: 'Datu Genetikoak',
            racial_ethnic: 'Arraza/Jatorri etnikoa',
            political_opinions: 'Iritzi Politikoak',
            religious_beliefs: 'Sinesmen erlijiosoak',
            trade_union: 'Sindikatu Afiliazioa',
            sexual_orientation: 'Orientazio sexuala',
            criminal_records: 'Zigor-aurrekariak',
            financial: 'Finantza Datuak',
            children: 'Haurrentzako Datuak',
            geolocation: 'Geokokapen zehatza',
        },
        export_csv: 'Esportatu CSV',
        exporting: 'Esportatzen...',
        page_x_of_y: '{page}. orrialdea {total}-tik',
        regulatory: {
            capture_method_guidance: 'PDPL-k baimenaren froga behar du bilketa-kanalarekin bat etortzeko. Jabetza motak baimena harrapatzeko metodoen mapa: webgunea/OTT = banneretan oinarritutako baimena, mugikorretarako aplikazioa/SDK = aplikazio barruko baimen-fluxua, kioskoa = atzemate fisikoa/pantailan, API zerbitzua = baimen programatikoa, IoT gailua = gailu-mailako baimen-mekanismoa.',
            dashboard_metrics_info: 'Arbel honek DPO-ri baimenaren jarreraren ikuspegi bakarra eskaintzen dio propietate digital guztietan. Funtsezko neurketak honako hauek dira: lotura-estaldura-ehunekoa (inplementatutako eta guztizko loturak), txantiloiaren atxikimendua inkoherentziak widgetaren bidez, zain dauden aldaketa-eskaerak berrikustea eskatzen duten eta jabetza-egoeraren matxura egoera aktibo, inaktibo edo deskargatu duten propietateak erakusten dituena. Datu sentikorren kategoriak dituzten propietateen jarraipena egiten da jabetza-erregistroaren bidez.',
            change_request_impact_info: 'Adostasun-propietateei eragiten dieten aldaketa-eskaerek araudiaren eragina dokumentatu behar dute: zein PDPL baldintza eragiten duten, aldaketak datu sentikorren tratamenduari eragiten dion ala ez (2.4. art.) eta aldaketak interesdunen berriro baimena behar duen ala ez. Aldaketa-eskaera bakoitzeko eraginaren laburpenak ikuskaritza-frogak egiteko PDPL arauzko erreferentziak zehazten ditu.',
            outdated_binding_warning: '"Zaharkituta" egoera duten loturak adierazten dute propietate batek egungoa ez den politika-bertsio bat exekutatzen duela. Baliteke propietate hauek azken PDPL eskakizunak edo erakundearen gidalerroen eguneraketak ez islatzea. Berrikusi eta eguneratu zaharkitutako loturak berehala PDPL baimen-propietate guztietan bete daitezen.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Aginte-mahaia',
            market_packs: 'Merkatu-paketeak',
            language_packs: 'Hizkuntza-paketeak',
            frameworks: 'Arautze-esparruak',
            mappings: 'Modulu-esleipenak',
            submissions: 'Pakete-bidalketak',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Itzulpenaren aurrebista',
            audit: 'Auditoria-aztarna',
        },
        dashboard: {
            title: 'Lokalizazio aginte-mahaia',
            loading: 'dashboard datu kargatzen...',
            active_market_packs: 'Merkatu-pakete aktiboak',
            published_packs_available: 'Argitaratuta market packs available',
            language_packs: 'Hizkuntza Packs',
            total_language_packs: 'Erregistratutako hizkuntza-paketeak guztira',
            active_regulatory_frameworks: 'Arau-esparru aktiboak',
            frameworks_active: 'Esparruak with active egoera',
            pending_pack_submissions: 'Zain Pack Submissions',
            submissions_awaiting_review: 'Berrikusketa zain dauden bidalketak',
            recent_audit_events: 'Recent Ikuskaritza Gertaerak',
            no_recent_audit: 'Ez dago azken ikuskaritza-gertaerarik.',
            event_type: 'Gertaera mota',
            entity_type: 'Entity Mota',
            entity_id: 'Entitate IDa',
            actor: 'Aktorea',
            timestamp: 'Denbora-zigilua',
        },
        market_packs: {
            title: 'Merkatu-pakete katalogoa',
            new: 'Berria Merkatu Pack',
            pack_name: 'Pack Izena',
            market: 'Merkatu',
            locale: 'Tokikoa',
            no_packs: 'Ez da market packs found. aurkitu',
        },
        language_packs: {
            title: 'Hizkuntza-paketeak',
            new: 'Berria Hizkuntza Pack',
            language: 'Hizkuntza',
            locale_code: 'Locale Kodea',
            completeness: 'Osotasuna',
            completeness_detail: 'Osotasun Xehetasuna',
            overall_completeness: 'Osotasun orokorra',
            total_keys: 'Gakoak guztira',
            translated_keys: 'Itzulitako giltzak',
            missing_keys: 'Gakoak falta dira',
            namespace_breakdown: 'Izen-espazioaren banaketa',
            namespace: 'Izen-espazioa',
            translated: 'Itzuliak',
            no_packs: 'Ez da language packs found. aurkitu',
            install_language_pack: 'Hizkuntza-paketea instalatu',
            available_packs: 'Hizkuntza-pakete erabilgarriak',
            install: 'Instalatu',
            all_installed: 'Hizkuntza-pakete guztiak instalatuta daude dagoeneko.',
            install_success: 'Hizkuntza-paketea ongi instalatuta.',
            market: 'Merkatua',
            featured: 'Nabarmendua',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Arautze-esparruak',
            new: 'Berria Esparru',
            framework_name: 'Esparru Izena',
            jurisdiction: 'Jurisdikzioa',
            effective_date: 'Effective Data',
            no_frameworks: 'Ez da regulatory frameworks found. aurkitu',
            view_requirements: 'Ikusi baldintzak',
            hide_requirements: 'Ezkutatu baldintzak',
            show_legacy: 'Erakutsi esparru zaharrak',
            effective_until: 'Indarrean arte',
            effective_on: 'Indarrean noiztik',
            replaced_by: 'Ordezkatua',
            replaces_label: 'Ordezkatzen du',
            status_active: 'Aktibo',
            status_draft: 'Zirriborroa',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Zaharra',
            status_upcoming: 'Laster',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Jarraipen zerrenda',
            watchlist_empty: 'Ez dago zirriborrorik edo datozen esparrurik',
            watchlist_empty_desc: '"Zirriborroa" edo "datorren" egoera duten markoak hemen agertuko dira jarraipena egiteko.',
            expected_effective: 'Eraginkorra espero da',
            effective_date_tbd: 'Indarrera data: zehaztu',
            activate: 'Aktibatu',
            ai_law_title: 'Vietnam AI legea',
            ai_risk_classification: 'AI Arriskuen Sailkapena',
            ai_system_dossier: 'AI Sistemaren dosierra',
            ai_labeling_checklist: 'AI Etiketatzea eta dibulgazioa',
            ai_incident_report: 'AI Gorabeheren txostena',
            ai_impact_assessment: 'AI Eraginaren Ebaluazioa',
            ai_sandbox_application: 'Sandbox aplikazioa',
            ai_sandbox_monitoring: 'Sandbox Monitorizazioa',
            ai_sandbox_completion: 'Sandbox osatzea',
            ai_submission_tracker: 'Bidalketa jarraitzailea',
            ai_evidence_capture: 'Froga Harrapatzea',
            risk_level_minimal: 'Arrisku minimoa',
            risk_level_limited: 'Arrisku mugatua',
            risk_level_high: 'Arrisku Handia',
            risk_level_unacceptable: 'Arrisku onartezina',
            submission_status_draft: 'Zirriborroa',
            submission_status_submitted: 'Aurkeztua',
            submission_status_returned: 'Zuzenketa egiteko itzuli da',
            submission_status_accepted: 'Onartua',
            submission_status_rejected: 'Baztertua',
            submission_status_closed: 'Itxita',
            ecommerce_law_title: 'Vietnameko merkataritza elektronikoaren legea',
            ecommerce_platform_classification: 'Plataforma Sailkapena',
            ecommerce_seller_register: 'Saltzaileen Erregistroa',
            ecommerce_complaint_register: 'Erreklamazio Erregistroa',
            ecommerce_takedown_register: 'Eraiketa-erregistroa',
            ecommerce_submission_tracking: 'Bidalketen jarraipena',
            ecommerce_dossier_checklist: 'Dosierren Kontrol-zerrenda',
            ecommerce_cross_border: 'Mugaz Gaindiko Betetzea',
            ecommerce_trustmark: 'Trustmark Programa',
            ecommerce_notification: 'Jakinarazpena',
            ecommerce_registration: 'Izen ematea',
            ecommerce_termination: 'Amaiera',
            ecommerce_reporting_readiness: 'Txostenaren Prestasuna',
            ecommerce_evidence_capture: 'Froga Harrapatzea',
            submission_status_approved: 'Onartua',
            cybersec_law_2025_title: 'Vietnamgo Zibersegurtasun Legea (2025)',
            cybersec_system_classification: 'Sistema Sailkapena',
            cybersec_risk_assessment: 'Arriskuen Ebaluazioa',
            cybersec_incident_report: 'Gorabeheren Txostena',
            cybersec_authority_cooperation: 'Agintaritzaren Lankidetza',
            cybersec_data_localization: 'Datuen lokalizazioa',
            cybersec_vendor_security: 'Saltzaileen Segurtasuna',
            cybersec_submission_tracker: 'Bidalketa jarraitzailea',
            cybersec_evidence_capture: 'Froga Harrapatzea',
            cybersec_dossier_checklist: 'Dosierren Kontrol-zerrenda',
            cybersec_classification_general: 'Orokorra',
            cybersec_classification_important: 'Garrantzitsua',
            cybersec_classification_critical: 'Kritikoa',
            data_law_title: 'Vietnameko Datuen Legea',
            data_inventory: 'Datuen Inbentarioa',
            data_classification: 'Datuen Sailkapena',
            data_sharing: 'Datuak partekatzea',
            data_governance: 'Datuen gobernantza',
            data_submission_tracker: 'Bidalketa jarraitzailea',
            data_evidence_capture: 'Froga Harrapatzea',
            data_dossier_checklist: 'Dosierren Kontrol-zerrenda',
            telecom_law_title: 'Vietnameko Telekomunikazio Legea',
            telecom_authority_request: 'Agintaritzaren eskaera',
            telecom_disclosure: 'Dibulgazioa',
            telecom_confidentiality: 'Konfidentzialtasuna',
            telecom_submission_tracker: 'Bidalketa jarraitzailea',
            telecom_evidence_capture: 'Froga Harrapatzea',
            telecom_dossier_checklist: 'Dosierren Kontrol-zerrenda',
            pdpl_law_title: 'Vietnameko Datu Pertsonalak Babesteko Legea',
            pdpl_breach_notification: 'Urratze-jakinarazpena',
            pdpl_dpo_notification: 'DPO Hitzorduaren jakinarazpena',
            pdpl_data_subject_notification: 'Datuei buruzko jakinarazpena',
            pdpl_submission_tracker: 'Bidalketa jarraitzailea',
            pdpl_evidence_capture: 'Froga Harrapatzea',
            pdpl_dossier_checklist: 'Dosierren Kontrol-zerrenda',
            pdpl_authority_filing: 'Agintaritzaren Artxiboa',
            pdpl_mops_filing: 'MoPS fitxategia',
        },
        mappings: {
            title: 'Modulu-esleipenak',
            new: 'Berria Mapping',
            module: 'Modulu',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Adostasuna',
            module_dpia: 'DPIA',
            module_incident: 'Gorabehera',
            module_rights_request: 'Eskubideen eskaera',
            module_data_mapping: 'Datuen mapaketa',
            module_classification: 'Sailkapena',
            module_monitoring: 'Jarraipena',
            module_governance: 'Gobernantza',
            module_vendor: 'Saltzailea',
            module_risk: 'Arriskua',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Merkatu-paketea',
            mapping_status: 'Mapping Egoera',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Ez da module mappings found. aurkitu',
            incomplete_entries_warning: '{count} mapaketa ezkutatuta, esparru-ID, eskakizun-ID edo modulu falta delako.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Pakete-bidalketak',
            new: 'Berria Submission',
            submitter: 'Bidaltzailea',
            pack: 'Paketea',
            submitted_at: 'Bidalita',
            no_submissions: 'Ez da pack submissions found. aurkitu',
            installed_sector_overlays_title: 'Sektore-gainjarpenak ({count})',
            installed_sector_overlays_aria: 'Instalatutako sektore-gainjarpenak',
            installed_col_name: 'Izena',
            installed_col_code: 'Kodea',
            installed_col_sector: 'Sektorea',
            installed_col_mode: 'Modua',
            installed_col_status: 'Egoera',
            installed_refresh: 'Berritu',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Itzulpenaren aurrebista',
            select_locale: 'Hautatu locale',
            select_module: 'Hautatu modulu',
            key: 'Key',
            value: 'Balioa',
            no_translations: 'Ez da translations found. aurkitu',
        },
        audit: {
            title: 'Localization Ikuskaritza Trail',
            event_type: 'Gertaera mota',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entity Mota',
            entity_id: 'Entitate IDa',
            entity_name: 'Entitatearen izena',
            actor: 'Aktorea',
            timestamp: 'Denbora-zigilua',
            payload: 'Karga',
            collapse_payload: 'Tolestu karga-xehetasunak',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Zabaldu karga-xehetasunak',
            collapse: 'Tolestu',
            expand: 'Zabaldu',
            all_entity_types: 'Entitate-mota guztiak',
            no_events: 'Ez da audit events found. aurkitu',
        },
        market_packs_detail: {
            installed_at: 'Noiz instalatua',
            pack_details_label: 'Paketearen xehetasunak',
            market_code: 'Merkatu-kodea',
            supported_locales: 'Onartutako lurraldeek',
            min_platform_version: 'Plataforma-bertsio minimoa',
            pack_information: 'Paketearen informazioa',
            pack_contents: 'Paketearen edukia',
            included_frameworks: 'Barne dauden esparruak',
            pack_artifacts: 'Paketearen artefaktuak',
            no_artifacts: 'Ez da artefakturik aurkitu pakete honetan',
            pack_not_found: 'Paketea ez da aurkitu',
        },
        mappings_form: {
            framework_id: 'Esparruaren IDa',
            requirement_id: 'Eskakizunaren IDa',
            module_label: 'Modulua',
            mapping_type: 'Mapaketa-mota',
            configuration_json: 'Konfigurazioa (JSON)',
            conflict_resolution: 'Gatazka-ebazpena',
            is_override: 'Gainidazketa da',
            all_modules: 'Modulu guztiak',
            all_mapping_types: 'Mapaketa-mota guztiak',
        },
        submissions_detail: {
            submission_id: 'Bidalketa-IDa',
            validation: 'Baliozkotzea',
            reviewers: 'Berrikusleak',
            start_review: 'Hasi berrikuspena',
            hide_comments: 'Ezkutatu iruzkinak',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Berrikuspenen iruzkinak',
            loading_comments: 'Iruzkinak kargatzen...',
            no_comments: 'Oraindik ez dago iruzkinik.',
            add_comment: 'Gehitu iruzkina',
            reject_submission: 'Baztertu bidalketa',
            reason_for_rejection: 'Baztertzeko arrazoia',
            reviewer_ids: 'Berrikusleen IDak (komaz bereizita)',
            submit_new_pack: 'Bidali pakete berria',
            pack_type: 'Pakete-mota',
            market_code: 'Merkatu-kodea',
            target_locales: 'Helburu-lurraldeek',
            pack_file: 'Paketearen fitxategia',
            pack_format_info: 'Pakete-formatua: Market-en paketeek tokiko berariazko JSON fitxategiak izan behar dituzte izen-espazio/gako itzulpenekin. Hizkuntza-paketeek itzulpenak eman behar dituzte zehaztutako xede-lokaletarako. Ikusi dokumentazioa eskema zehatza ikusteko.',
            accepted_formats: 'Onartutako formatuak: .json edo .zip. JSON fitxategiek itzulpen gako-balio bikoteen objektu laua edo izen-tartea eduki behar dute. ZIP fitxategiek JSON tokiko fitxategi bat edo gehiago izan behar dituzte.',
            comma_separated_locales: 'Komaz bereizitako lurralde-kodeak hizkuntza-pakete honetarako',
            confirm_approval: 'Berretsi onarpena',
            approve_message: 'Onartu pakete-bidalketa hau?',
            submission_label: 'Bidalketa',
            all_pack_types: 'Pakete-mota guztiak',
            passed: 'gainditua',
            failed_label: 'huts',
            market_code_required: 'Merkatu-kodea beharrezkoa da',
            version_required: 'Bertsioa beharrezkoa da',
            pack_type_regulatory: 'Erregulatzailea',
            framework_code: 'Esparru-kodea',
            jurisdiction_code: 'Jurisdikzio-kodea',
            pack_source: 'Iturria',
            pack_source_manual: 'Eskuzkoa',
            pack_version: 'Pakete-bertsioa',
            install_regulatory_pack: 'Instalatu araudia-paketea',
            regulatory_pack_desc: 'Kargatu araudia-esparru pakete bat (.zip)',
            framework_required: 'Esparru-araudiaren kodea beharrezkoa da',
            jurisdiction_required: 'Jurisdikzio-kodea beharrezkoa da',
        },
        catalog_detail: {
            packs_installed_of: '{total} pakete instalatuetatik',
            install_all: 'Instalatu denak',
            installing: 'Instalatzen...',
            removing: 'Kentzen...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Behar du',
            tab_catalog: 'Katalogoa',
            tab_installed: 'Instalatua',
            details: 'Xehetasunak',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Jatorri-lurraldea',
            target_locale: 'Helburu-lurraldea',
            locales_must_differ: 'Jatorri- eta helburu-lurraldeek desberdinak izan behar dute.',
            all_keys_translated: 'Gako guztiak itzulita',
            keys_missing: '{count} tekla(k) falta dira',
            show_missing_only: 'Erakutsi falta direnak soilik',
            namespace: 'Izen-espazioa',
            source: 'Iturria',
            target: 'Helburua',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Esparruak',
            tab_watchlist: 'Jarraipen-zerrenda',
            authority: 'Agintaritza',
            requirements_count: 'Eskakizunak',
            all_types: 'Mota guztiak',
            collapse_all: 'Tolestu denak',
            expand_all: 'Zabaldu denak',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Iragazita',
            clear_filter: 'Garbitu iragazkia',
            no_requirements: 'Ez da eskakizunik aurkitu esparru honetan.',
            requirements_label: 'Eskakizunak',
            status_updated: 'Egoera ondo eguneratua',
            overview_tab: 'Ikuspegi orokorra',
            forms_tab: 'Inprimakiak',
            procedures_tab: 'Prozedurak',
            fines_risks_tab: 'Isunak eta arriskuak',
            deadlines_tab: 'Epeak',
            guidance_tab: 'Orientazioa',
            guidance_empty: 'Ez dago marko honetarako orientabide-edukirik.',
            guidance_loading: 'Orientabidea kargatzen…',
            guidance_section: 'Orientazio atala',
            guidance_view: 'Ikusi orientabidea',
            guidance_map_to_workflow: 'Mapatu lan-fluxurako',
            guidance_link_evidence: 'Lotura froga',
            guidance_filter_type: 'Mota',
            guidance_filter_category: 'Kategoria',
            guidance_filter_module: 'Modulua',
            guidance_filter_status: 'Egoera',
            guidance_filter_search: 'Bilatu orientabidea',
            guidance_filter_all: 'Denak',
            guidance_table_reference: 'Erreferentzia',
            guidance_table_name: 'Gidari izena',
            guidance_table_type: 'Mota',
            guidance_table_category: 'Kategoria',
            guidance_table_effective_date: 'Indarreko data',
            guidance_table_modules: 'Erlazionatutako Moduluak',
            guidance_table_actions: 'Ekintzak',
            guidance_reference_only_banner: 'Erreferentzia soilik — {{sector}} sektoreari dagokio baina ez zure erakundeari.',
            guidance_open_official_source: 'Ireki iturri ofiziala',
            guidance_no_detail: 'Ez dago xehetasun gehigarririk erabilgarri gida honetarako.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Lotutako orientabidea',
            deadlines_empty: 'Ez da arauzko epe aurkitu esparru honetarako.',
            deadlines_sla_type: 'SLA mota',
            deadlines_value: 'Epea',
            deadlines_unit: 'Unitatea',
            deadlines_extension: 'Luzapena',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Erreferentzia juridikoa',
            framework_stack: 'Esparru-egitura',
            layer_parent_law: 'Lege nagusia',
            layer_implementing_decree: 'Gauzatze-dekretua',
            layer_enforcement_decree: 'Zehapen-dekretua',
            layer_admin_procedures: 'Administrazio-prozedurak',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Menpeko tresnak',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiboa',
            status_draft: 'Zirriborroa',
            draft_enforcement_badge: 'Zirriborroa — oraindik aldarrikatu gabe',
            effective_date_draft: 'Zirriborroa — oraindik ez dago indarrean',
            effective_date: 'Indarrean sartzeko data',
            forms_table_id: 'Inprimaki-IDa',
            forms_table_name: 'Izena',
            forms_table_source: 'Iturria',
            forms_table_category: 'Kategoria',
            forms_table_type: 'Mota',
            fill_form: 'Formularioa bete',
            view_reference: 'Ikusi Erreferentzia',
            forms_empty: 'Ez da inprimakirik aurkitu esparru honetarako.',
            forms_loading: 'Inprimakiak kargatzen...',
            filter_by_source_layer: 'Iragazi iturri-geruzaren arabera',
            all_layers: 'Geruza guztiak',
            procedures_id: 'Prozedura-IDa',
            procedures_name: 'Prozedura',
            procedures_trigger: 'Abiarazlea',
            procedures_steps: 'Urratsak',
            procedures_outputs: 'Emaitzak',
            procedures_related_forms: 'Lotutako inprimakiak',
            procedures_submission_channels: 'Bidalketa Kanalak',
            procedures_empty: 'Ez da prozedurarik aurkitu esparru honetarako.',
            fines_area: 'Arau-hauste eremua',
            fines_articles: 'Artikuluak',
            fines_individual: 'Isuna (banakakoa)',
            fines_organization: 'Isuna (erakundea)',
            fines_revenue: 'Diru-sarrera eskalatzea',
            fines_deadlines: 'Epeak',
            fines_remediation: 'Zuzenketa-neurriak',
            fines_related: 'Lotutako prozedurak',
            fines_empty: 'Ez dago zehapen-daturik esparru honetarako.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Arrisku mota',
            fines_risk_triggers: 'Zerk eragiten duen',
            fines_risk_only_note: 'Sarrera hauek betetze- eta jarduera-arriskua deskribatzen dute, ez isun ekonomikoak. Isunak aplikatzen direnean, marko nagusian zehazten dira.',
            fines_enforcement_pending: 'Zehapen-esparrua oraindik argitaratu gabe. Zehapen-datuak gehituko dira aplikazio-tresna aldarrikatzen denean.',
            view_details: 'Ikusi xehetasunak',
            back_to_list: 'Itzuli zerrendara',
            description: 'Deskribapena',
            jurisdiction: 'Jurisdikzioa',
            lineage: 'Jatorria',
            replaces: 'Ordezkatzen du',
            replaced_by: 'Ordezkatua',
            requirements: 'Eskakizunak',
            tab_navigation: 'Esparru-xehetasunen fitxak',
            forms_table_actions: 'Ekintzak',
            create_form: 'Formularioa sortu',
            mark_promulgated: 'Aldarrikatu gisa markatu',
            status_promulgated: 'Aldarrikatua',
            promulgated_success: 'Geruza-egoera aldarrikatu gisa eguneratu da',
            transition_review: 'Trantsizio-berrikuspena',
            transition_review_desc: 'Esparru honek trantsizio-berrikuspena du oinordeko legearen hartzapenerako.',
            transition_review_by: 'Berrikuspenen muga-data',
            transition_review_status: 'Berrikuspenen egoera',
            transition_review_notes: 'Oharrak',
            transition_review_pending: 'Zain',
            transition_review_in_progress: 'Abian',
            transition_review_completed: 'Osatuta',
            transition_review_successor: 'Oinordeko esparrua',
            transition_review_lead_time: 'Aurretiko denbora',
            transition_review_lead_time_days: '{days} egun indarrean sartzeko data baino lehen',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Inprimakien trantsizio-berrikuspenak',
            transition_review_forms_desc: 'Berrikusi txantiloi operatibo bakoitza oinordeko esparruaren azpian garrantzitsua den.',
            transition_review_outcome: 'Emaitza',
            transition_review_outcome_carry_forward: 'Mantendu',
            transition_review_outcome_carry_forward_revised: 'Mantendu (berrikusita)',
            transition_review_outcome_retire: 'Erretiratu',
            transition_review_outcome_split: 'Banatu',
            transition_review_outcome_awaiting_decree: 'Dekretuaren zain',
            transition_review_no_forms: 'Ez dago inprimakien trantsizio-berrikuspenik konfiguratuta.',
            transition_review_update_success: 'Trantsizio-berrikuspena eguneratuta',
            transition_review_operational_note: 'Hauek prozesuak laguntzeko txantiloi operatiboak dira, ez lege-inprimaki kopuruak. Lege-atribuzioa esparru zaharrean geratzen da oinordeko legea indarrean sartu arte.',
            rights_tab: 'Eskubideak',
            rights_empty: 'Ez dago datu-hartzaileen eskubiderik esparru honetarako definitu.',
            rights_code: 'Kodea',
            rights_name: 'Eskuin',
            rights_legal_basis: 'Oinarri juridikoa',
            rights_elaborating_articles: 'Artikuluak lantzea',
            rights_description: 'Deskribapena',
            requirements_reference: 'Erreferentzia',
            requirements_mandatory: 'Nahitaezkoa',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Iturria',
            category_legal_basis: 'Oinarri juridikoa',
            category_data_subject_rights: 'Datuen Subjektuaren Eskubideak',
            category_notification: 'Jakinarazpena',
            category_assessment: 'Ebaluazioa',
            category_governance: 'Gobernantza',
            category_record_keeping: 'Erregistroak gordetzea',
            category_cross_border: 'Mugaz gaindi',
            category_technical_measures: 'Neurri Teknikoak',
            category_incident_reporting: 'Gorabeheren berri ematea',
            category_risk_management: 'Arriskuen kudeaketa',
            category_supply_chain: 'Hornikuntza Katea',
            category_certification: 'Ziurtagiria',
            child_frameworks: '{count} haur-esparrua',
            child_frameworks_plural: '{count} haur-esparruak',
            replaces_prefix: 'Ordezkatzen du:',
            replaced_by_prefix: 'Honek ordeztua:',
            responsible_prefix: 'Arduraduna:',
            watchlist_empty_hint: '"Zirriborroa" edo "datorren" egoera duten markoak hemen agertuko dira jarraipena egiteko.',
            framework_count: '{count} markoa',
            framework_count_plural: '{count} esparruak',
            hiding_legacy: '({count} ondarea ezkutatzen)',
            label_VN_PDPD_DECREE_13: 'PDPD 13/2023 Dekretua',
            label_VN_PDPL_LAW_2025: 'PDPL 91/2025 Legea',
            label_VN_PDPL_DECREE_356_2025: '356/2025/ND-CP Dekretua',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Betearazteko Zirriborroa 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Datuen Legea',
            label_VN_DATA_LAW_DECREE_165_2025: 'Datuen Lege Dekretua 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Datu-trukeen zirriborroa 2026',
            label_VN_TELECOM_LAW_2023: '2023ko Telekomunikazioen Legea',
            label_VN_TELECOM_DECREE_163_2024: 'Telekomunikazioen 163/2024 Dekretua',
            label_VN_CYBERSEC_LAW: 'Zibersegurtasun Legea (2018/24)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec 53/2022 Dekretua',
            label_VN_CYBERSEC_LAW_2025: '116/2025 Zibersegurtasun Legea',
            label_VN_ECOMMERCE_LAW_2025: 'Merkataritza Elektronikoaren 122/2025 Legea',
            label_VN_AI_LAW_2026: 'AI 2026 Legea',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Enpresa',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Agintea',
            sla_type_rights_request_ack: 'Eskubideak eskatzeko aitorpena',
            'sla_type_Access control review': 'Sarbide-kontrolaren berrikuspena',
            'sla_type_Annual governance attestation': 'Urteko gobernuaren egiaztagiria',
            'sla_type_Audit review cycle': 'Ikuskaritza berrikusteko zikloa',
            'sla_type_Authority request response': 'Agintaritzaren eskaeraren erantzuna',
            'sla_type_Change notification processing': 'Aldaketa jakinarazpenaren tratamendua',
            'sla_type_Complaint acknowledgment': 'Kexaren aitorpena',
            'sla_type_Complaint resolution': 'Erreklamazioaren ebazpena',
            'sla_type_Confidentiality policy review': 'Konfidentzialtasun politikaren berrikuspena',
            'sla_type_Cybersecurity incident full report': 'Zibersegurtasuneko gertakarien txosten osoa',
            'sla_type_Cybersecurity incident initial report': 'Zibersegurtasuneko gertakarien hasierako txostena',
            'sla_type_Data inventory review': 'Datuen inbentarioaren berrikuspena',
            'sla_type_Disclosure log review cycle': 'Dibulgazio-erregistroa berrikusteko zikloa',
            'sla_type_Incident classification decision': 'Gertakariak sailkatzeko erabakia',
            'sla_type_Information system classification review': 'Informazio sistemaren sailkapenaren berrikuspena',
            'sla_type_Notification processing (sales website/app)': 'Jakinarazpenen tratamendua (salmenta webgunea/aplikazioa)',
            'sla_type_Pen test cycle': 'Boligrafoaren proba-zikloa',
            'sla_type_Registration processing (service platform)': 'Erregistroaren tramitazioa (zerbitzu-plataforma)',
            'sla_type_Risk assessment cycle': 'Arriskuen ebaluazio-zikloa',
            'sla_type_Takedown decision (after review)': 'Bajatzeko erabakia (berrikusi ondoren)',
            'sla_type_Termination notice (public)': 'Amaierako oharra (publikoa)',
            sla_type_ai_impact_assessment_completion: 'AI eraginaren ebaluazioa amaitu da',
            sla_type_ai_sandbox_application_review: 'AI sandbox aplikazioaren berrikuspena',
            sla_type_ai_serious_incident_report: 'AI gertakari larrien txostena',
            sla_type_ai_system_registration: 'AI sistemaren erregistroa',
            sla_type_authority_request_response: 'Agintaritzaren eskaeraren erantzuna',
            sla_type_certificate_issuance_review: 'Ziurtagiria jaulkitzearen berrikuspena',
            sla_type_complaint_handling_approach_notice: 'Kexak kudeatzeko hurbilketa-oharra',
            sla_type_complaint_incomplete_info_request: 'Kexa informazio eskaera osatu gabe',
            sla_type_data_governance_attestation: 'Datuen gobernantzaren egiaztagiria',
            sla_type_dsr_acknowledgment: 'DSR aitorpena',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Erretiratu / mugatu / objektua',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Hirugarrena',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Sartu / zuzendu / eman',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Hirugarrena',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Ezabatzea',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Ezabatzea, hirugarrena',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Babes-neurriak',
            sla_type_telecom_authority_request_response: 'Telekomunikazio-agintaritzak eskaeraren erantzuna',
            sla_type_rights_request_completion: 'Eskubideen eskaera betetzea',
            sla_type_incident_authority_notification: 'Gertaeren agintaritzaren jakinarazpena',
            sla_type_incident_subject_notification: 'Gorabeheraren gaiaren jakinarazpena',
            sla_type_dpia_completion: 'DPIA osatzea',
            sla_type_data_retention_review: 'Datuak gordetzeko berrikuspena',
            sla_type_authority_filing_review: 'Agintaritzaren espedienteen berrikuspena',
            sla_type_authority_supplement_response: 'Agintaritzaren osagarriaren erantzuna',
            sla_type_authority_resubmission: 'Agintaritzak berriro bidaltzea',
            sla_type_authority_filing_supplement_response: 'Agintaritzak aurkezteko gehigarriaren erantzuna',
            sla_type_authority_filing_resubmission: 'Agintaritzak berriro aurkeztea',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'lanegunak',
            unit_calendar_days: 'egun naturalak',
            unit_hours: 'orduak',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Adostasuna',
            form_category_privacy: 'Pribatutasuna',
            form_category_breach: 'Haustura',
            form_category_dpia: 'DPIA',
            form_category_register: 'Izena eman',
            form_category_monitoring: 'Jarraipena',
            form_category_vendor: 'Saltzailea',
            form_category_org: 'Antolakuntza',
            form_category_sector: 'Sektorea',
            form_category_data_mapping: 'Datuen mapaketa',
            form_category_governance: 'Gobernantza',
            form_category_incident: 'Gorabehera',
            form_category_authority_submission: 'Agintaritzaren aurkezpena',
            form_category_notice: 'Oharra',
            form_category_assessment_report: 'Ebaluazio-txostena',
            form_category_request: 'Eskaera',
            form_category_dossier: 'Dosierra',
            form_category_filing: 'Artxibatzea',
            module_consent: 'Adostasuna',
            module_rights_request: 'Eskubideen eskaera',
            module_classification: 'Sailkapena',
            module_dpia: 'DPIA',
            module_governance: 'Gobernantza',
            module_monitoring: 'Jarraipena',
            module_register: 'Izena eman',
            module_risk: 'Arriskua',
            module_data_mapping: 'Datuen mapaketa',
            module_incident: 'Gorabehera',
            module_assessment: 'Ebaluazioa',
            module_vendor: 'Saltzailea',
            module_policy: 'Politika',
            module_audit: 'Ikuskaritza',
            module_training: 'Prestakuntza',
            module_discovery: 'Aurkikuntza',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Kalkulu orientabidea',
            enum_guidance_type_implementation_guidance: 'Ezartzeko orientabidea',
            enum_guidance_type_interpretive_note: 'Interpretazio Oharra',
            enum_guidance_type_legal_guidance: 'Lege Orientabidea',
            enum_guidance_type_procedural_guidance: 'Prozedurarako Orientabidea',
            enum_guidance_type_sector_guidance: 'Sektoreko Orientabidea',
            enum_guidance_type_technical_methodology: 'Metodologia Teknikoa',
            enum_guidance_category_annual_reporting: 'Urteko txostena',
            enum_guidance_category_authority_cooperation: 'Agintaritzaren Lankidetza',
            enum_guidance_category_authority_coordination: 'Agintarien Koordinazioa',
            enum_guidance_category_business_continuity: 'Negozioaren Jarraipena',
            enum_guidance_category_capital_adequacy: 'Kapital Egokitasuna',
            enum_guidance_category_complaint_handling: 'Kexak kudeatzea',
            enum_guidance_category_consent: 'Adostasuna',
            enum_guidance_category_consent_management: 'Adostasunaren kudeaketa',
            enum_guidance_category_consumer_protection: 'Kontsumitzaileen babesa',
            enum_guidance_category_control_environment: 'Kontrol-ingurunea',
            enum_guidance_category_cross_border: 'Mugaz gaindi',
            enum_guidance_category_cross_border_transfers: 'Mugaz gaindiko transferentziak',
            enum_guidance_category_data_classification: 'Datuen Sailkapena',
            enum_guidance_category_data_localization: 'Datuen lokalizazioa',
            enum_guidance_category_data_protection_impact_assessment: 'Datuak Babesteko Eraginaren Ebaluazioa',
            enum_guidance_category_direct_marketing: 'Zuzeneko marketina',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Gobernantza',
            enum_guidance_category_ict_risk_management: 'IKT Arriskuen Kudeaketa',
            enum_guidance_category_incident_management: 'Gorabeheren kudeaketa',
            enum_guidance_category_internal_audit: 'Barne Ikuskaritza',
            enum_guidance_category_isms_documentation: 'ISMS Dokumentazioa',
            enum_guidance_category_lawful_basis: 'Legezko oinarria',
            enum_guidance_category_licensing: 'Lizentzia ematea',
            enum_guidance_category_local_presence: 'Tokiko Presentzia',
            enum_guidance_category_logging_and_retention: 'Erregistroa eta atxikipena',
            enum_guidance_category_logical_and_physical_access: 'Sarbide logikoa eta fisikoa',
            enum_guidance_category_operational_resilience_testing: 'Eragiketa-erresilientzia-probak',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Plataforman izena ematea',
            enum_guidance_category_quality_of_service: 'Zerbitzuaren kalitatea',
            enum_guidance_category_risk_classification: 'Arriskuen Sailkapena',
            enum_guidance_category_risk_management: 'Arriskuen kudeaketa',
            enum_guidance_category_scope_definition: 'Eremuaren definizioa',
            enum_guidance_category_sectoral_localisation: 'Lokalizazio Sektoriala',
            enum_guidance_category_subscriber_protection: 'Harpidedunen babesa',
            enum_guidance_category_supply_chain: 'Hornikuntza Katea',
            enum_guidance_category_system_classification: 'Sistema Sailkapena',
            enum_guidance_category_system_operations: 'Sistema Eragiketak',
            enum_guidance_category_telecommunications_data: 'Telekomunikazioen Datuak',
            enum_guidance_category_third_party_risk: 'Hirugarrenen Arriskua',
            enum_guidance_category_transitional_provisions: 'Xedapen Iragankorrak',
            enum_guidance_category_vulnerability_management: 'Ahultasunen kudeaketa',
            enum_guidance_status_active: 'Aktiboa',
            enum_guidance_status_draft: 'Zirriborroa',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Sarbideen Kudeaketa',
            enum_module_ai_governance: 'AI Gobernua',
            enum_module_asset_management: 'Aktiboen Kudeaketa',
            enum_module_audit_trail: 'Ikuskaritza Pista',
            enum_module_breach_notification: 'Urratze-jakinarazpena',
            enum_module_capital_adequacy: 'Kapital Egokitasuna',
            enum_module_classification: 'Sailkapena',
            enum_module_consent: 'Adostasuna',
            enum_module_consent_management: 'Adostasunaren kudeaketa',
            enum_module_cross_border_transfers: 'Mugaz gaindiko transferentziak',
            enum_module_data_localization: 'Datuen lokalizazioa',
            enum_module_data_mapping: 'Datuen mapaketa',
            enum_module_data_protection: 'Datuen Babesa',
            enum_module_data_subject_rights: 'Datuen Subjektuaren Eskubideak',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Enkriptatzea',
            enum_module_governance: 'Gobernantza',
            enum_module_incident: 'Gorabehera',
            enum_module_incident_management: 'Gorabeheren kudeaketa',
            enum_module_internal_audit: 'Barne Ikuskaritza',
            enum_module_isms_documentation: 'ISMS Dokumentazioa',
            enum_module_isms_governance: 'ISMS gobernantza',
            enum_module_logging_and_monitoring: 'Erregistroa eta jarraipena',
            enum_module_monitoring: 'Jarraipena',
            enum_module_policy_management: 'Politiken kudeaketa',
            enum_module_risk: 'Arriskua',
            enum_module_risk_management: 'Arriskuen kudeaketa',
            enum_module_training: 'Prestakuntza',
            enum_module_vendor: 'Saltzailea',
            enum_module_vendor_management: 'Saltzaileen kudeaketa',
            enum_module_vendor_risk: 'Saltzaileen Arriskua',
            enum_module_vulnerability_management: 'Ahultasunen kudeaketa',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Sarbidea',
            category_availability: 'Eskuragarritasuna',
            category_change_management: 'Aldaketen kudeaketa',
            category_classification: 'Sailkapena',
            category_communication_and_information: 'Komunikazioa eta Informazioa',
            category_compliance: 'Betetzea',
            category_confidentiality: 'Konfidentzialtasuna',
            category_control_activities: 'Kontrol-jarduerak',
            category_control_environment: 'Kontrol-ingurunea',
            category_incident: 'Gorabehera',
            category_internal_audit: 'Barne Ikuskaritza',
            category_internal_control: 'Barne Kontrola',
            category_logical_and_physical_access: 'Sarbide logikoa eta fisikoa',
            category_mis_data: 'Kudeaketa Informazio Sistemaren Datuak',
            category_monitoring: 'Jarraipena',
            category_monitoring_activities: 'Jarraipen-jarduerak',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Pribatutasuna',
            category_processing_integrity: 'Prozesatzeko osotasuna',
            category_reporting: 'Txostenak ematea',
            category_risk_assessment: 'Arriskuen Ebaluazioa',
            category_risk_mitigation: 'Arriskuak Arintzea',
            category_sector_applicability: 'Sektorearen aplikagarritasuna',
            category_system_operations: 'Sistema Eragiketak',
            category_training: 'Prestakuntza',
            category_vendor: 'Saltzailea',
            guidance_related_forms_advisory: 'Inprimaki batzuek erlazionatutako orientabidea dute: ikusi',
            guidance_related_procedures_advisory: 'Prozedura batzuek erlazionatutako jarraibideak dituzte — ikusi',
            guidance_related_requirements_advisory: 'Baldintza batzuek erlazionatutako orientabidea dute - ikusi',
        },
        channels: {
            title: 'Bidalketa-kanalak eta epemugak',
            channels: 'Bidalketa-kanalak',
            add_channel: 'Gehitu kanala',
            channel_type: 'Mota',
            name: 'Izena',
            instructions: 'Argibideak',
            active: 'Aktiboa',
            no_channels: 'Ez dago bidalketa-kanalik konfiguratuta',
            portal: 'Ataria',
            in_person: 'Aurrez aurre',
            postal: 'Posta',
            email: 'Posta elektronikoa',
            add: 'Gehitu',
            deadlines: 'Araudiaren epemugak',
            add_deadline: 'Gehitu epemuga',
            label: 'Etiketa',
            days: 'Egunak',
            day_type: 'Egun-mota',
            calendar_days: 'Egutegi-egunak',
            working_days: 'Lanegun',
            no_deadlines: 'Ez dago epemugarik konfiguratuta',
            description: 'Deskribapena',
            failed_load: 'load channel data huts egin du',
            channel_added: 'Kanala gehitua',
            channel_added_announce: 'Bidalketa-kanala gehitua',
            failed_add_channel: 'add channel huts egin du',
            deadline_added: 'Epemuga gehitua',
            deadline_added_announce: 'Araudiaren epemuga gehitua',
            failed_add_deadline: 'add deadline huts egin du',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Esparruen lotsa',
            modules: 'Lotutako moduluak',
            add_module: 'Gehitu modulua',
            module_code: 'Moduluaren kodea',
            required: 'Beharrezkoa',
            link: 'Estekatu',
            no_modules: 'Ez dago modulurik lotuta',
            notes: 'Oharrak',
            personas: 'Lotutako pertsonaiak',
            add_persona: 'Gehitu pertsonaia',
            persona_id: 'Pertsonaiaren IDa',
            role_description: 'Rolaren deskribapena',
            no_personas: 'Ez dago pertsonaiarik lotuta',
            failed_load: 'load framework links huts egin du',
            module_linked: 'Modulua lotua',
            module_linked_announce: 'Modulua ondo lotua',
            failed_link_module: 'link module huts egin du',
            persona_linked: 'Pertsonaia lotua',
            persona_linked_announce: 'Pertsonaia ondo lotua',
            failed_link_persona: 'link persona huts egin du',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Zain',
                assessed: 'Ebaluatuta',
                applied: 'Aplikatuta',
                dismissed: 'Baztertuta',
            },
            title: 'Araudiaren eguneraketak eta esparruen aginte-panela',
            page_description: 'Instalatutako arautegi-esparru bakoitzaren estaldura begiratu batez, eta produktu barneko araudi-eguneraketen aldaketa-egunkaria: zuzenketak, indarrean sartzeko datak eta eraginen ebaluazioak.',
            coverage_dashboard: 'Esparru-estalduraren aginte-panela',
            no_frameworks: 'Ez da esparrurik aurkitu',
            framework: 'Esparrua',
            modules: 'Moduluak',
            templates: 'Txantiloiak',
            forms: 'Inprimakiak',
            channels: 'Kanalak',
            deadlines: 'Epemugak',
            pending: 'Zain dauden eguneraketak',
            updates: 'Araudiaren eguneraketak',
            create_update: 'Sortu eguneratzea',
            framework_id: 'Esparruaren IDa',
            update_title: 'Izenburua',
            description: 'Deskribapena',
            effective_date: 'Indarrean sartzeko data',
            amendment_ref: 'Aldaketa Erreferentzia',
            create: 'Sortu',
            no_updates: 'Ez dago araudiaren eguneraketarik',
            status: 'Egoera',
            impact: 'Eragina',
            change_summary: 'Aldaketen laburpena',
            assess: 'Ebaluatu',
            failed_load: 'load data huts egin du',
            update_created: 'Eguneraketa sortua',
            update_created_announce: 'Araudiaren eguneraketa sortua',
            failed_create: 'create update huts egin du',
            update_assessed: 'Eguneraketa ebaluatua',
            failed_assess: 'assess update huts egin du',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Erlazionatutako esparruak',
            panel_intro: 'Esparru hau besteekin nola lotzen den. Gainjartze batek aldi berean hainbat goi-esparruren azpian aplika dezake; bakoitza behin bakarrik agertzen da hemen.',
            outgoing_heading: 'Esparru hau honi aplikatzen zaio',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Esparru honen azpian aplikatzen da',
            outgoing_caption: 'Esparru honek seinalatzen dituen esparruak',
            incoming_caption: 'Esparru honen azpian dauden esparruak',
            col_framework: 'Esparrua',
            col_relationship: 'Erlazioa',
            col_scope: 'Iturria',
            scope_global: 'Paketearen edukitik',
            scope_org: 'Erakundearentzat espezifikoa',
            load_failed: 'Ezin izan dira erlazionatutako esparruak kargatu',
            relationship_kinds: {
                parent: 'Goi-esparrua',
                applies_to: 'Honi aplikatzen zaio',
                cross_reference: 'Erreferentzia gurutzatua',
                supersedes: 'Ordezkatzen du',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banka',
                insurance: 'Aseguruak',
                healthcare: 'Osasun-arreta',
                education: 'Education',
                ict_critical: 'IKT kritikoa',
                telecom: 'Telekomunikazioak',
                energy: 'Energia',
                transport: 'Garraioa',
                digital_services: 'Zerbitzu digitalak',
                digital_technology_industry: 'Teknologia digitalaren industria',
                manufacturing: 'Manufacturing',
                public_administration: 'Administrazio publikoa',
                payments: 'Ordainketak',
                publishing: 'Argitalpena',
                crypto_assets: 'Kripto-aktiboak',
                other: 'Bestelakoa',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sektorea',
            direct_scope_label: 'Lege-eremu zuzena',
            chip_direct: 'Zuzenean aplikagarria',
            chip_reference: 'Erreferentzia soilik',
            banner_direct_copy: 'Zuzenean aplikagarria zure erakundeari. Sektoreko txostenak eta epemugak aktibo daude.',
            banner_reference_copy: 'Erreferentzia soilik — zure erakundea ez dago sektore honetan sailkatuta.',
            edit_mode_button: 'Editatu modua',
            edit_form_title: 'Aldatu instalazio-modua',
            mode_direct_label: 'Zuzena — legez aplikagarria',
            mode_direct_help: 'Sektoreko epemugak eta aginte-paneleko widgetak antolatzen ditu.',
            mode_reference_label: 'Erreferentzia soilik',
            mode_reference_help: 'Arakatu daiteke, baina ez du epemugarik edo widgetik.',
            reason_note_label: 'Arrazoia (aukerakoa)',
            reason_note_placeholder: 'adib. Banku-filiala eskuratu da',
            cancel: 'Utzi',
            confirm: 'Berretsi',
            confirming: 'Gordetzen…',
            save: 'Gorde',
            saving: 'Gordetzen…',
            toast_mode_changed: 'Sektore-gainjarpenaren modua eguneratu da.',
            toast_installed: 'Sektore-gainjarpena instalatu da.',
            toast_sectors_saved: 'Sektoreko autoidentifikazioa gorde da.',
            install_modal_title: 'Instalatu sektore-gainjarpena',
            install_modal_intro: 'Sektore-gainjarpenak industria bakar batera mugatutako aukerako arautegi-paketeak dira.',
            install_modal_footer: 'Geroago alda dezakezu instalazio-modua esparruaren xehetasun-orritik.',
            install_button: 'Instalatu',
            installed_label: 'Instalatua',
            uninstall_button: 'Desinstalatu',
            uninstall_confirm: 'Berretsi desinstalazioa',
            uninstall_cancel: 'Utzi',
            suggested_label: 'Iradokitua',
            catalog_page_title: 'Sektore-gainjarpenak',
            catalog_page_intro: 'Industria-sektore bakar batera mugatutako aukerako arautegi-paketeak.',
            catalog_empty_title: 'Ez dago sektore-gainjarpenik eskuragarri',
            catalog_empty_description: 'Zure plataforman argitaratutako sektore-gainjarpenen paketeak hemen agertuko dira.',
            authority_label: 'Agintaritza',
            org_sectors_title: 'Industria-sektoreak',
            org_sectors_intro: 'Hautatu zure erakundeak jarduten duen industria-sektoreak.',
            widget_title: 'Epemugak dituzten sektore-gainjarpenen txostenak',
            widget_aria_label: 'Epemugak dituzten sektore-gainjarpenen txostenak',
            widget_empty: 'Ez dago hurrengo sektore-gainjarpen-epemugarik.',
            audit_event_mode_changed: 'Sektore-gainjarpenaren instalazio-modua aldatu da',
            audit_event_installed: 'Sektore-gainjarpena instalatua',
            view_details_button: 'Ikusi xehetasunak →',
            banner_aria_label: 'Sektore-gainjarpenaren egoera',
            toast_load_failed: 'Sektore-gainjarpenak kargatzeak huts egin du',
            toast_install_failed: 'Sektore-gainjarpena instalatzeak huts egin du',
            toast_uninstall_confirm_required: 'Idatzi "{packCode}" desinstalazioa berresteko.',
            toast_uninstalled: '{packCode} desinstalatu da.',
            toast_uninstall_failed: 'Sektore-gainjarpena desinstalatzeak huts egin du',
            uninstall_warning_destructive_label: 'Ekintza suntsitzailea.',
            uninstall_warning_body: 'Desinstalatzeak esparruaren erregistroa eta {frameworkCode}-ri lotutako iraunarazitako arautegi-epemuga guztiak ezabatuko ditu erakunde honetatik.',
            uninstall_warning_type_to_confirm: 'Idatzi {packCode} berresteko.',
            uninstall_in_progress: 'Desinstalatzen…',
            widget_view_all: 'Ikusi sektore-gainjarpen guztiak →',
            widget_due_in_days: 'Epea: {days} egun',
            widget_overdue_days: 'Atzeratua {days} egunez',
            widget_due_today: 'Gaur amaitzen da',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Faseko betebeharrak',
            panel_aria_label: 'Faseko betebeharrak',
            panel_intro: 'Esparru honetako klausula batzuk esparrua bera baino geroago aktibatzen dira.',
            phased_effective_date_label: 'Faseko indarraldia:',
            status_pending: 'Zain',
            status_active: 'Aktibo',
            status_early: 'Aktibo (aurretik aplikatua)',
            notice_window_label: 'Agintaritzari jakinarazteko epea:',
            days_label: 'egun',
            recipient_label: 'Hartzailea:',
            early_implement_button: 'Markatu aurretik aplikatutzat',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Markatu faseko betebeharra aurretik aplikatutzat',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Indarrean noiztik',
            reason_label: 'Arrazoia (aukerakoa)',
            reason_placeholder: 'adib. Zuzendaritza-batzordeak 2026-09-01 aurreko hartzea onetsi du',
            confirm: 'Berretsi',
            toast_early_implemented: 'Aurretiko aplikazioa erregistratu da.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Faseko betebeharra aurretik aplikatutzat markatua',
            widget_rolling_window_label: 'Faseko aktibazioak hurrengo 90 egunetan',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Aginte-mahaia',
            partner_orgs: 'Bazkide-erakundeak',
            service_tiers: 'Zerbitzu-mailak',
            deliverable_templates: 'Entregagaien txantiloiak',
            deliverables: 'Entregagaiak',
            brand_profiles: 'Marka-profilak',
            support_tickets: 'Laguntza-txartelak',
            workflow_templates: 'Lan-fluxu txantiloiak',
            workflows: 'Lan-fluxuak',
            escalation: 'Eskalatzea',
            reports: 'Txostenak',
            audit: 'Auditoria-aztarna',
        },
        dashboard: {
            title: 'Bazkide aginte-mahaia',
            loading: 'dashboard datu kargatzen...',
            total_managed_tenants: 'Managed Tenants guztira',
            avg_health_score: 'Avg Health Puntuazio',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Ireki Tiketak',
            upcoming_deliverables: 'Datozen entregagaiak',
            escalated_tickets: 'Escalated Tiketak',
            managed_tenants: 'Kudeatutako maizterrak',
            partner_tier: 'Partner Maila',
            managed_tenants_count: 'Managed Tenants Kopuru',
            onboarding_date: 'Onboarding Data',
            view_details: 'Ikusi xehetasunak',
            no_partner_orgs: 'Ez da partner organizations found. aurkitu',
        },
        partner_orgs: {
            title: 'Bazkide-erakundeak',
            new: 'Berria Partner Org',
            contact_name: 'Contact Izena',
            contact_email: 'Harremanetarako eposta',
            partner_tier: 'Partner Maila',
            managed_orgs: 'Kudeatutako erakundeak',
            onboarding_date: 'Onboarding Data',
            no_orgs: 'Ez da partner organizations found. aurkitu',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contact Telefonoa',
            metadata_json: 'Metadatuak (JSON)',
            change_status: 'Change Egoera',
            new_status: 'Berria Egoera',
            invalid_json_metadata: 'Baliogabea JSON in metadata field',
            suspended: 'Etenda',
            offboarded: 'Kanporatua',
            all_tiers: 'Maila guztiak',
            silver: 'Zilarra',
            gold: 'Urrea',
            platinum: 'Platinoa',
            status_offboarded: 'Kanporatua',
            status_suspended: 'Etenda',
            tier_gold: 'Urrea',
            tier_platinum: 'Platinoa',
            tier_silver: 'Zilarra',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Zerbitzu-mailak',
            new: 'Berria Maila',
            tier_name: 'Maila Izena',
            monthly_fee: 'Hilero Fee',
            features: 'Ezaugarriak',
            no_tiers: 'Ez da service tiers found. aurkitu',
            tier_code: 'Maila Kodea',
            response_time_hrs: 'Erantzun Ordua (hrs)',
            resolution_time_hrs: 'Resolution Ordua (hrs)',
            review_frequency: 'Berrikusketa Frequency',
            sla_response_time: 'SLA Erantzun Ordua (hours)',
            sla_resolution_time: 'SLA Resolution Ordua (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Konparatzen...',
            compare: 'Konparatu',
            comparison: 'Zerbitzua Maila Comparison',
            attribute: 'Atributua',
            deprecate: 'Zaharkitu',
            deprecated: 'Zaharkituta',
            select_compare: 'Hautatu at least 2 tiers to compare',
            comparison_title: 'Zerbitzua Maila Comparison',
            status_deprecated: 'Zaharkituta',
            activate: 'Aktibatu',
            resolution_time: 'Resolution Ordua',
            response_time: 'Erantzun Ordua',
            select_at_least_two: 'Hautatu at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Entregagaien txantiloiak',
            new: 'Berria Txantiloi',
            template_name: 'Txantiloi Izena',
            deliverable_type: 'Entregagaia Mota',
            no_templates: 'Ez da deliverable txantiloiak found. aurkitu',
            frequency: 'Maiztasuna',
            est_effort_hrs: 'Zenb. ahalegin (ordu)',
            estimated_effort: 'Zenbatetsi ahalegin (ordu)',
            associated_module: 'Associated Modulu',
            linked_tiers_count: 'Linked Mailak Kopuru',
            evidence_requirements: 'Ebidentzia Eskakizunak (comma-separated)',
            tier_ids: 'Maila IDs (comma-separated)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'One Ordua',
            frequency_monthly: 'Hilero',
            frequency_quarterly: 'Hiruhilero',
            frequency_annually: 'Urtero',
            type_report: 'Txosten',
            type_review: 'Berrikusketa',
            type_assessment: 'Ebaluazio',
            type_meeting: 'Bilera',
            type_configuration: 'Konfigurazio',
            freq_one_time: 'One Ordua',
            freq_monthly: 'Hilero',
            freq_quarterly: 'Hiruhilero',
            freq_annually: 'Urtero',
            status_deprecated: 'Zaharkituta',
        },
        deliverables: {
            title: 'Entregagaiak',
            new: 'Berria Entregagaia',
            partner: 'Bazkidea',
            deliverable_type: 'Entregagaia Mota',
            due_date: 'Due Data',
            no_deliverables: 'Ez da deliverables found. aurkitu',
            assigned_to: 'Esleituta To',
            completion_date: 'Completion Data',
            update_status: 'Eguneratu egoera',
            hide_details: 'Ezkutatu Xehetasunak',
            view_details: 'Ikusi xehetasunak',
            evidence_ids: 'Ebidentzia IDs',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Onartze',
            bulk_update: 'Bulk Eguneratu',
            bulk_update_status: 'Bulk Eguneratu Egoera',
            generate_schedule: 'Sortu Ordutegi',
            generate_deliverable_schedule: 'Sortu Entregagaia Ordutegi',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Erakunde IDa',
            service_tier_id: 'Zerbitzua Maila ID',
            filter_assigned_to: 'Iragazi by Esleituta To',
            select_status: 'Hautatu egoera...',
            new_status: 'Berria Egoera',
            upcoming: 'Datorrena',
            overdue: 'Atzeratuta',
            status_deferred: 'Atzeratua',
            status_overdue: 'Atzeratuta',
            status_upcoming: 'Datorrena',
        },
        brand_profiles: {
            title: 'Marka-profilak',
            new: 'Berria Brand Profila',
            partner: 'Bazkidea',
            brand_name: 'Brand Izena',
            primary_color: 'Nagusia Color',
            logo_url: 'Logo URLa',
            no_profiles: 'Ez da brand profiles found. aurkitu',
            secondary_color: 'Bigarrena Color',
            header_text: 'Goiburu-testua',
            footer_text: 'Oin-testua',
            contact_email: 'Harremanetarako eposta',
            contact_phone: 'Contact Telefonoa',
            contact_website: 'Contact Webgunea',
            contact_address: 'Contact Helbidea',
            is_default: 'Is Lehenetsia',
            set_default: 'Set Lehenetsia',
            preview: 'Aurrebista',
            report_header_preview: 'Txosten Header Preview',
            report_footer_preview: 'Txosten Footer Preview',
            preview_description: 'This is a preview of the report header with the hautatuta brand colors.',
            contact_website_short: 'Webgunea',
            logo_preview: 'Logoaren aurrebista',
        },
        support_tickets: {
            title: 'Laguntza-txartelak',
            new: 'Berria Tiketa',
            partner: 'Bazkidea',
            subject: 'Subjektua',
            assigned_to: 'Esleituta To',
            no_tickets: 'Ez da support tickets found. aurkitu',
            category: 'Kategoria',
            priority: 'Lehentasun',
            escalation_tier: 'Eskalazio Maila',
            sla_status: 'SLA Egoera',
            update_status: 'Eguneratu egoera',
            escalate: 'Eskalatu',
            add_note: 'Gehitu oharra',
            escalate_ticket: 'Eskalatu Tiketa',
            escalate_to_tier: 'Eskalatu To Maila',
            notes_timeline: 'Oharrak Timeline',
            no_notes: 'Ez dago oharrik oraindik.',
            attachments: 'Eranskinak',
            no_attachments: 'Eranskinik ez.',
            client_org_id: 'Bezero-erakunde IDa',
            content: 'Edukia',
            note_type: 'Mota',
            view_detail: 'Ikusi xehetasuna',
            hide: 'Ezkutatu',
            new_status: 'Berria Egoera',
            all_categories: 'Kategoria guztiak',
            all_priorities: 'Lehentasun guztiak',
            all_tiers: 'Maila guztiak',
            ticket_not_found: 'Tiketa not found',
            ticket_id: 'Tiketa ID',
            client_org: 'Bezero-erakundea',
            deescalate: 'Des-eskalatu',
            deescalate_ticket: 'De-escalate Tiketa',
            deescalate_to_tier: 'De-escalate To Maila',
            add_attachment: 'Gehitu eranskina',
            file: 'Fitxategia',
            linked_entity: 'Estekatutako entitatea',
            sla_target: 'Helburu',
            breached: 'Urratuta',
            remaining_hours: 'geratzen',
            na: 'N/A',
            tickets_found: 'tiketa aurkitu dira',
            reason: 'Arrazoia',
            target: 'Helburu',
            upload: 'Igo',
        },
        workflow_templates: {
            title: 'Lan-fluxu txantiloiak',
            new: 'Berria Txantiloi',
            template_name: 'Txantiloi Izena',
            steps: 'Urratsak',
            no_templates: 'Ez da workflow txantiloiak found. aurkitu',
            category: 'Kategoria',
            view_steps: 'Ikusi urratsak',
            hide_steps: 'Ezkutatu Urratsak',
            step: 'Urrats',
            step_title: 'Izenburua',
            step_description: 'Deskribapena',
            assignee_role: 'Esleitua Rol',
            sla_hours: 'SLA orduak',
            evidence_required: 'Ebidentzia Beharrezkoa',
            guidance_notes: 'Guidance Oharrak',
            add_step: 'Gehitu urratsa',
            no_steps: 'Ez da urratsik definitu.',
            no_steps_defined: 'Ez da urratsik definitu.',
            templates_found: 'txantiloi aurkitu dira',
            all_categories: 'Kategoria guztiak',
        },
        workflows: {
            client_org: 'Bezero-erakundea',
            title: 'Lan-fluxu Instances',
            new: 'Berria Lan-fluxu',
            template: 'Txantiloi',
            partner: 'Bazkidea',
            current_step: 'Unekoa Urrats',
            progress: 'Aurrerapena',
            no_workflows: 'Ez da workflow instances found. aurkitu',
            instances_found: 'instantzia aurkitu dira',
        },
        escalation: {
            title: 'Eskalazio Aginte-panel',
            ticket: 'Tiketa',
            partner: 'Bazkidea',
            escalated_at: 'Eskalatua',
            reason: 'Arrazoia',
            no_escalations: 'Ez da escalations found. aurkitu',
            time_in_tier: 'Ordua in Maila',
            auto_evaluate: 'Autoebaluazioa',
            priority_critical: 'Kritikoa',
            priority_high: 'Altua',
            priority_medium: 'Ertaina',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Aginte-paneletik des-eskalatua',
            sla_breached: 'SLA urratuta',
            sla_remaining: 'SLA geratzen: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Bezero-erakundea',
            title: 'Txostenak',
            partner: 'Bazkidea',
            report_type: 'Txosten Mota',
            generated_at: 'Sortua',
            no_reports: 'Ez da reports found. aurkitu',
            generate: 'Sortu Txosten',
        },
        audit: {
            title: 'Partner Ikuskaritza Trail',
            event_type: 'Gertaera mota',
            entity_type: 'Entity Mota',
            entity: 'Entitatea',
            entity_id: 'Entitate IDa',
            actor: 'Aktorea',
            timestamp: 'Denbora-zigilua',
            no_events: 'Ez da audit events found. aurkitu',
            events_found: 'gertaera aurkitu dira',
            client_org: 'Bezero-erakundea',
            all_actors: 'Eragile guztiak',
            event_type_search: 'Bilatu gertaera mota',
            event_type_created: 'Sortu',
            event_type_updated: 'Eguneratua',
            event_type_deleted: 'Ezabatu da',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Bazkide Org',
            entity_type_service_tier: 'Zerbitzu Maila',
            entity_type_deliverable_template: 'Eman daitekeen txantiloia',
            entity_type_deliverable_instance: 'Entrega daitekeen Instantzia',
            entity_type_brand_profile: 'Markaren profila',
            entity_type_support_ticket: 'Laguntza-txartela',
            entity_type_workflow_template: 'Lan-fluxuaren txantiloia',
            entity_type_workflow_instance: 'Lan-fluxuaren Instantzia',
            entity_type_generated_report: 'Sortutako txostena',
            entity_type_audit_export: 'Auditoria-esportazioa',
        },
        no_permission_title: 'Baimen nahikoa ez',
        no_permission_message: 'Ez duzu bazkideen lan-eremuan sartzeko baimenik.',
        status_active: 'Aktiboa',
        status_suspended: 'Etenda',
        status_offboarded: 'Offboarded',
        status_completed: 'Osatua',
        status_cancelled: 'Bertan behera utzita',
        status_deprecated: 'Zaharkituta',
        status_new: 'Berria',
        status_triaged: 'Triatuak',
        status_in_progress: 'Eraikitzen',
        status_waiting_on_client: 'Bezeroaren zain',
        status_waiting_on_vendor: 'Saltzailearen zain',
        status_escalated: 'Goratuta',
        status_resolved: 'Ebatzita',
        status_closed: 'Itxita',
        status_pending: 'Zain',
        status_skipped: 'Saltatu',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Sartzea',
        category_incident_escalation: 'Gorabeheren igoera',
        category_config_change: 'Konfigurazio-aldaketa',
        category_periodic_review: 'Aldizkako Berrikuspena',
        category_offboarding: 'Offboarding',
        category_custom: 'Pertsonalizatua',
        category_configuration: 'Konfigurazioa',
        category_incident: 'Gorabehera',
        category_compliance_question: 'Betetze Galdera',
        category_feature_request: 'Ezaugarrien eskaera',
        category_bug_report: 'Akatsen txostena',
        priority_critical: 'Kritikoa',
        priority_high: 'Alta',
        priority_medium: 'Ertaina',
        priority_low: 'Baxua',
        tier_silver: 'Zilarrezkoa',
        tier_gold: 'Urrea',
        tier_platinum: 'Platinozkoa',
        report_type_compliance_summary: 'Betetze-laburpena',
        report_type_risk_overview: 'Arriskuen ikuspegi orokorra',
        report_type_incident_report: 'Gorabeheren Txostena',
        report_type_consent_status: 'Baimenaren egoera',
        report_type_program_maturity: 'Programaren Heldutasuna',
        report_type_executive_pack: 'Pack Exekutiboa',
        note_type_internal: 'Barnekoa',
        note_type_client_visible: 'Bezeroa ikusgai',
        status_updated: 'Egoera behar bezala eguneratu da',
        deliverable_status_updated: 'Entregagarriaren egoera behar bezala eguneratu da',
        ticket_status_updated: 'Txartelaren egoera behar bezala eguneratu da',
        ticket_escalated: 'Sarrera behar bezala igo da',
        note_added: 'Oharra behar bezala gehitu da',
        attachment_uploaded: 'Eranskina behar bezala kargatu da',
        ticket_deescalated: 'Sarrera behar bezala kendu da',
        auto_evaluation_completed: 'Ebaluazio automatikoa amaitu da',
        template_created: 'Lan-fluxuaren txantiloia behar bezala sortu da',
        template_updated: 'Lan-fluxuaren txantiloia behar bezala eguneratu da',
        brand_set_default: 'Markaren profila lehenetsi gisa ezarri da',
        brand_created: 'Markaren profila behar bezala sortu da',
        brand_updated: 'Markaren profila behar bezala eguneratu da',
        tier_created: 'Zerbitzu maila behar bezala sortu da',
        tier_updated: 'Zerbitzu maila behar bezala eguneratu da',
        tier_deprecated: 'Zerbitzu-maila behar bezala zaharkituta dago',
        tier_activated: 'Zerbitzu maila behar bezala aktibatu da',
        period: 'Aldia',
        format: 'Formatua',
        period_start: 'Aldiaren Hasiera',
        period_end: 'Aldiaren Amaiera',
        reports_count: '{count} txostenak',
        reports_count_one: '{count} txostena',
        metric: 'Metrikoa',
        value: 'Balioa',
        showing_chart_view: 'Diagrama-ikuspegia erakusten',
        showing_table_view: 'Datu-taularen ikuspegia erakusten',
        evidence_ids_placeholder: 'adib. ev-001, ev-002',
        confirm_cancel_workflow: 'Ziur lan-fluxu hau bertan behera utzi nahi duzula? Ekintza hau ezin da desegin.',
        export_csv: 'Esportatu CSV',
        compliance_qualifications_title: 'Betetze Zerbitzuaren Kualifikazioak',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Arauzko Irteeraren Lerrokadura',
        regulatory_output_desc: 'Txantiloi mota bakoitzak arauzko irteera-kategoria batera egokitzen du: Txostenek betetzen duten dokumentazioa sortzen dute, Berrikuspenek kontrolaren eraginkortasuna balioztatzen dute, Ebaluazioak arriskuen ebaluazioak estaltzen dituzte (DPIA, hutsuneen azterketa), Bileretako dokumentuen gobernantza saioak. Ebidentzia-baldintzak definitzen ditu entregatu bakoitzak ikuskapena prestatzeko erreferentzia egin behar dituen artefaktuak.',
        sla_compliance_title: 'SLA Betearen entregarako jarraipena',
        sla_compliance_desc: 'Erantzun eta ebazteko denbora SLAk kontratu bidez lotesleak dira betetze-zerbitzua emateko. Berrikuspen-maiztasunak bazkideen betekizunak betetzen diren maiztasunez ebaluatzen diren zehazten du. Betetze kritikoko txarteletako SLA urratzeek igoera automatikoa eragiten dute eta Bazkideen Ikuskaritza Bidean erregistratzen dira arauzko frogak lortzeko.',
        regulatory_engagement_title: 'Harremanetarako ereduak',
        regulatory_engagement_desc: 'Lan-fluxuen kategoriak arauzko konpromiso-motetara esleitzen dira: barneratzeak hasierako betetze-konfigurazioa eta hutsuneen azterketa estaltzen ditu, Aldizkako Berrikuspenak errepikatzen diren betetze-ebaluazioak kudeatzen ditu, Intzidentziaren igoerak arau-hausteen erantzuna kudeatzen du, Offboarding-ak datuak gordetzeko betebeharrak betetzen direla bermatzen du. Urrats bakoitzak froga-bilketa eska dezake auditoretza-bidearen osotasuna lortzeko.',
        compliance_escalation_title: 'Betetze-eskalatze-prozedura kritikoak',
        compliance_escalation_desc: 'L1-ek funtzionamendu-arazo estandarrak kudeatzen ditu. L2-k betetzea eragiten duten hutsegiteak biltzen ditu (erregulazio-epeak galduak, kontrol-hutsuneak). L3 berehalako arreta exekutiboa behar duten arau-hauste agertokietarako gordeta dago. L2/L3 txarteletan SLA urratzeak ikuskaritza-bideko sarrerak sortzen ditu automatikoki. Eskalatzeko eta deseskalatzeko ekintza guztiak behin betiko erregistratzen dira.',
        audit_regulatory_title: 'Arauzko Frogaren ibilbidea',
        audit_regulatory_desc: 'Ikuskaritza-bide honek manipulazio-bidezko erregistroak eskaintzen ditu araudia betetzeko. Bazkide-ekintza bakoitza (sortu, eguneratu, ezabatu, eskalatu, egoera-aldaketa) aktoreen identitatearekin, denbora-zigiluarekin eta karga-diferentzia osoarekin erregistratzen da. Erabili Entitate Mota iragazkiak betetze-domeinuaren arabera frogak isolatzeko. Esportatu iragazitako emaitzak ikuskapenetarako prest dagoen dokumentazioa ekoizteko, arauzko ikuskapenetarako.',
        evidence_trail_title: 'Modulu Zeharkako Froga Ibilbidea',
        evidence_trail_desc: 'Lan-fluxuaren urrats bakoitzak moduluetan entregagarriekin, auditoretzako gertaerekin eta betetze-artefaktuekin lotzen diren Froga IDak bil ditzake. Ebidentzia IDekin urrats bat aurreratzeak bazkideen lan-fluxuak arauzko dokumentazioarekin lotzen dituen kate egiaztagarria sortzen du. Osatutako lan-fluxuek froga-kate osoekin betetzen duten konpromisoaren froga gisa balio dute.',
        evidence_ids_label: 'Froga IDak',
    },
    org_groups: {
        title: 'Erakunde-taldeak',
        new: 'Sortu Talde',
        group_name: 'Talde Izena',
        member_count: 'Member Kopuru',
        root_org: 'Erro-erakundea',
        no_groups: 'Ez da org groups found. aurkitu',
        add_organization: 'Erakundea Gehitu',
        select_organization: 'Hautatu erakunde bat...',
        no_available_orgs: 'Ez dago erakunde erabilgarririk gehitzeko.',
        no_members: 'Ez dago kide-erakunderik talde honetan.',
        member_added: 'Erakundea taldean gehitu da.',
        member_removed: 'Erakundea taldetik kendu da.',
        already_in_group: 'Erakunde hau dagoeneko talde batean dago.',
        org_not_found: 'Erakundea ez da aurkitu.',
        add_member_error: 'Ezin izan da erakundea taldean gehitu.',
        remove_member_error: 'Ezin izan da erakundea taldetik kendu.',
        confirm_remove_member: 'Ziur zaude "{name}" talde honetatik kendu nahi duzula?',
        load_error: 'Ezin izan dira erakunde-taldearen xehetasunak kargatu.',
        page_title_list: 'Antolakuntza Taldeak',
        page_title_detail: 'Antolakuntza taldearen xehetasunak',
        page_title_create: 'Sortu Erakunde Taldea',
        page_title_settings: 'Antolakuntza-taldeen ezarpenak',
        page_title_users: 'Talde Erabiltzaileak',
        page_title_reports: 'Talde Txostenak',
        page_title_dashboard: 'Taldearen panela',
        page_title_audit: 'Talde Ikuskaritza',
        page_title_org_audit: 'Antolakuntza Taldearen Ikuskaritza',
        page_title_shared_vendors: 'Saltzaile partekatuak',
        page_title_shared_frameworks: 'Partekatutako Esparruak',
        page_title_shared_forms: 'Partekatutako inprimaki-txantiloiak',
        page_title_merged_vendors: 'Batatutako saltzaileak',
        page_title_settings_inheritance: 'Ezarpenak Herentzia',
        announce_loaded_groups: '{count} erakunde-talde kargatu dira',
        announce_loaded_group: '{name} taldea kargatu da',
        announce_loaded_users: 'Taldeko {count} erabiltzaile kargatu dira',
        announce_loaded_reports: '{count} txosten kargatu dira',
        announce_loaded_vendors: '{count} saltzaile partekatuak kargatu dira',
        announce_loaded_frameworks: '{count} partekatutako esparruak kargatu dira',
        announce_loaded_templates: '{count} partekatutako inprimaki-txantiloi kargatu dira',
        announce_loaded_audit: '{count} auditoretza-gertaera kargatu dira',
        announce_loaded_merged: '{shared} partekatutako eta tokiko {local} saltzaile kargatu dira',
        announce_dashboard_loaded: 'Kargatu da panela.',
        announce_dashboard_refreshed: 'Aginte-panela freskatu da.',
        announce_dashboard_refresh_failed: 'Ezin izan da freskatu panela.',
        announce_refreshing: 'Aginte-panela freskatzen...',
        announce_settings_loaded: 'Ezarpenen herentzia kargatu da.',
        announce_settings_saved: 'Ezarpenak ongi gorde dira.',
        announce_saving_settings: 'Ezarpenak gordetzen...',
        announce_error_saving: 'Errore bat gertatu da ezarpenak gordetzean.',
        announce_group_dissolved: 'Taldea desegin da.',
        announce_creating_group: 'Antolakuntza taldea sortzen...',
        announce_group_created: 'Antolaketa taldea behar bezala sortu da.',
        announce_error_creating: 'Errore bat gertatu da antolakuntza taldea sortzean.',
        announce_vendor_published: 'Saltzailea arrakastaz argitaratu da.',
        announce_vendor_retired: 'Saltzailea erretiratu egin da.',
        announce_framework_published: 'Markoa arrakastaz argitaratu da.',
        announce_framework_retired: 'Markoa erretiratu.',
        announce_template_published: 'Inprimaki txantiloia behar bezala argitaratu da.',
        announce_template_retired: 'Inprimaki txantiloia erretiratu da.',
        announce_role_assigned: 'Esleitu da rola.',
        announce_error_assign_role: 'Errorea rola esleitzean.',
        announce_role_revoked: 'Ezeztatu da rola.',
        announce_report_generating: 'Txostena sortzen...',
        announce_report_requested: 'Txostena sortzea eskatu da.',
        announce_error_report: 'Errore bat gertatu da txostena eskatzean.',
        announce_report_deleted: 'Txostena ezabatu da.',
        announce_search_applied: 'Bilaketa aplikatu da.',
        announce_search_cleared: 'Bilaketa garbitu da.',
        announce_tab_selected: '{tab} fitxa hautatuta',
        announce_error_add_member: 'Errore bat gertatu da kidea taldean gehitzean.',
        announce_category_reverted: '{category} gurasoengandik heredatutako izatera itzuli da.',
        announce_category_overridden: '{category} tokiko gainidazteko ezarri da.',
        announce_override_saved: '{category} baliogabetzea gorde da.',
        announce_export_started: 'Auditoria {format} gisa esportatzen...',
        announce_export_completed: 'Esportatu {format} gisa osatuta.',
        announce_export_failed: 'Ezin izan da esportatu.',
        announce_filter_org: '{name} erakundearen arabera iragazita',
        announce_filter_org_cleared: 'Garbitu da erakundearen iragazkia',
        announce_filter_event: '{type} gertaera motaren arabera iragazita',
        announce_filter_cleared: 'Garbitu da iragazkia',
        announce_download: '{name} {format} gisa deskargatzen',
        error_load_groups: 'Ezin izan dira kargatu erakunde taldeak. Mesedez, saiatu berriro.',
        error_load_users: 'Ezin izan dira taldeko erabiltzaileak kargatu.',
        error_load_reports: 'Ezin izan dira taldeen txostenak kargatu.',
        error_load_dashboard: 'Ezin izan da kargatu taldearen panela.',
        error_load_settings: 'Ezin izan dira kargatu taldearen ezarpenak.',
        error_load_vendors: 'Ezin izan dira bateratutako saltzaileak kargatu.',
        error_save_settings: 'Ezin izan dira gorde ezarpenak.',
        error_dissolve_group: 'Ezin izan da taldea desegin.',
        error_assign_role: 'Ezin izan da esleitu rola.',
        error_create_group: 'Ezin izan da sortu erakunde taldea. Mesedez, saiatu berriro.',
        error_request_report: 'Ezin izan da txostena eskatu.',
        error_group_name_required: 'Taldearen izena beharrezkoa da.',
        success_settings_saved: 'Ezarpenak ongi gorde dira.',
        confirm_retire_vendor: 'Ziur "{name}" saltzailea erretiratu nahi duzula?',
        confirm_retire_framework: 'Ziur "{name}" markoa erretiratu nahi duzula?',
        confirm_retire_template: 'Ziur "{name}" inprimaki-txantiloia erretiratu nahi duzula?',
        confirm_dissolve_group: 'Ziur "{name}" desegin nahi duzula? Ekintza hau ezin da desegin.',
        confirm_revoke_role: 'Ziur "{role}" eginkizuna "{name}"-tik kendu nahi duzula?',
        confirm_delete_report: 'Ziur "{name}" txostena ezabatu nahi duzula?',
        dissolve_group: 'Desegin taldea',
        dissolve_warning: 'Horrek erakunde-taldea behin betiko desegingo du. Kideen erakunde guztiak kenduko dira.',
        count_groups: '{count} taldea',
        count_groups_plural: '{count} talde',
        count_users: '{count} erabiltzailea',
        count_users_plural: 'Talde-eginkizunak dituzten {count} erabiltzaile',
        count_events: '{count} gertaera',
        count_events_plural: '{count} gertaera',
        count_reports: '{count} txostena',
        count_reports_plural: '{count} txostenak',
        count_vendors: '{count} saltzaileak taldean partekatu du',
        count_vendors_plural: '{count} saltzaileek taldean partekatu dute',
        count_frameworks: '{count} esparrua taldean partekatzen da',
        count_frameworks_plural: 'Taldean partekatutako {count} esparru',
        count_templates: '{count} txantiloia taldean partekatzen da',
        count_templates_plural: 'Taldean partekatutako {count} txantiloi',
        count_shared_vendors: '{count} saltzaile partekatuak',
        count_local_vendors: '{count} tokiko saltzaile',
        merged_combined_view: '{shared} partekatutako eta tokiko {local} saltzaileen ikuspegi konbinatua',
        dashboard: {
            title: 'Taldearen aginte-mahaia',
            loading: 'dashboard datu kargatzen...',
            total_groups: 'Taldeak guztira',
            total_members: 'Members guztira',
            active_groups: 'Talde aktiboak',
            overall_compliance_score: 'Betetze puntuazio orokorra',
            group_risk_level: 'Taldeko Arrisku Maila',
            sla_compliance: 'SLA Betetzea',
            compliance_posture: 'Betetze jarrera',
            risk_heatmap: 'Arriskuen bero-mapa',
            sla_performance: 'SLA Errendimendua',
            recent_incidents: 'Azken Gertaerak',
            recent_activity: 'Azken Jarduera',
            refresh_label: 'Freskatu paneleko datuak',
            score_good: 'Ona',
            score_needs_attention: 'Arreta behar du',
            score_critical: 'Kritikoa',
            trend_up: 'Gora joera',
            trend_down: 'Joera behera',
            trend_stable: 'Egonkorra',
            risk_low: 'Low',
            risk_medium: 'Ertaina',
            risk_high: 'Alta',
            risk_critical: 'Kritikoa',
            risk_unknown: 'Ezezaguna',
            col_organization: 'Antolaketa',
            col_score: 'Puntuazioa',
            col_trend: 'Joera',
            col_open_issues: 'Arazo irekiak',
            col_critical_issues: 'Gai Kritikoak',
            col_health: 'Osasuna',
            col_vendor_risk: 'Saltzaileen Arriskua',
            col_data_risk: 'Datuen Arriskua',
            col_compliance_risk: 'Betetze Arriskua',
            col_total_cases: 'Kasuak guztira',
            col_on_time: 'Garaiz',
            col_breached: 'Urratua',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Batez besteko egunak',
            sr_vendor_risk: 'Saltzaileen arriskuaren puntuazioa: {score}',
            sr_data_risk: 'Datuen arriskuaren puntuazioa: {score}',
            sr_compliance_risk: 'Betetze-arriskuaren puntuazioa: {score}',
            sr_sla_breaches: 'Abisua: {count} SLA urraketak',
            sr_severity: 'Larritasuna: {severity}',
        },
        detail: {
            title: 'Taldearen xehetasunak',
            members: 'Kideak',
            settings: 'Ezarpenak',
            audit: 'Ikuskaritza',
            sr_loading: 'Antolakuntza taldearen xehetasunak kargatzen',
            sr_loading_hierarchy: 'Kargatzeko hierarkia',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'kide den erakundea',
            root_org_label: 'erro antolaketa',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Taldearen erabiltzaileak',
            add_user: 'Gehitu erabiltzailea',
            no_users: 'No erabiltzaile in this group.',
            role_group_admin: 'Taldeko administratzailea',
            role_group_reader: 'Talde irakurlea',
            sr_loading: 'Taldeko erabiltzaileak kargatzen',
        },
        reports: {
            title: 'Taldearen txostenak',
            no_reports: 'Ez da reports found. aurkitu',
            generate_report: 'Sortu txosten berria',
            report_type_compliance: 'Betetze-laburpena',
            report_type_risk: 'Arriskuen ikuspegi orokorra',
            report_type_sla: 'SLA Errendimendua',
            report_type_incident: 'Gorabeheren Txostena',
            report_type_executive: 'Laburpen Exekutiboa',
            report_type_board: 'Kontseiluaren txostena',
            status_queued: 'Ilaran jarrita',
            status_generating: 'Sortzen',
            status_completed: 'Osatua',
            status_failed: 'Huts egin du',
            period_start: 'Aldiaren Hasiera',
            period_end: 'Aldiaren Amaiera',
            sr_loading: 'Talde-txostenak kargatzen',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Partekatutako hornitzaileak',
            no_vendors: 'Ez da shared hornitzaileak found. aurkitu',
            merged_vendors: 'Merged Hornitzaileak',
            publish_form_label: 'Argitaratu partekatutako saltzailea',
            sr_loading: 'Partekatutako saltzaileak kargatzen',
            sr_loading_merged: 'Batatutako saltzaileak kargatzen',
            local_vendors: 'Bertako Saltzaileak',
            source_shared: 'Taldetik partekatua',
            source_local: 'Bertako saltzailea',
            category_technology: 'Teknologia',
            category_consulting: 'Aholkularitza',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Hodeia',
            category_data_processing: 'Datuen tratamendua',
            category_other: 'Bestela',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Ertaina',
            risk_tier_high: 'Alta',
            risk_tier_critical: 'Kritikoa',
            compliance_compliant: 'Konforme',
            compliance_non_compliant: 'Ez-betetzea',
            compliance_pending: 'Zain',
            compliance_under_review: 'Berrikuspenean',
            status_active: 'Aktiboa',
            status_retired: 'Erretiratua',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Esparruko kodea',
            title: 'Partekatutako esparruak',
            no_frameworks: 'Ez da shared frameworks found. aurkitu',
            publish_form_label: 'Argitaratu partekatutako esparrua',
            sr_loading: 'Partekatutako esparruak kargatzen',
            status_active: 'Aktiboa',
            status_retired: 'Erretiratua',
        },
        shared_forms: {
            title: 'Partekatutako formulario-txantiloiak',
            no_forms: 'Ez da shared form txantiloiak found. aurkitu',
            publish_form_label: 'Argitaratu partekatutako inprimaki-txantiloia',
            sr_loading: 'Partekatutako inprimaki-txantiloiak kargatzen',
            status_active: 'Aktiboa',
            status_retired: 'Erretiratua',
        },
        audit: {
            title: 'Taldearen auditoria',
            consolidated_title: 'Consolidated Ikuskaritza Trail',
            consolidated_subtitle: 'Erakunde arteko ikuskaritza-gertaerak talde honetarako',
            audit_log_title: 'Talde Ikuskaritza Erregistro',
            event_type: 'Gertaera mota',
            actor: 'Aktorea',
            timestamp: 'Denbora-zigilua',
            source_org: 'Iturria Org',
            org: 'Org',
            summary: 'Laburpena',
            details: 'Xehetasunak',
            no_events: 'Ez da audit events found. aurkitu',
            all_events: 'Gertaera guztiak',
            all_orgs: 'Erakunde guztiak',
            organization: 'Erakunde',
            from: 'Nork',
            to: 'To',
            search_events: 'Bilatu events...',
            clear: 'Garbitu',
            events_24h: 'Gertaerak (24h)',
            events_7d: 'Gertaerak (7d)',
            active_orgs: 'Erakunde aktiboak',
            exporting: 'Esportatzen...',
            export_csv: 'Esportatu CSV',
            export_json: 'Esportatu JSON',
            back_to_group: 'Atzera to Talde',
            back_to_detail: 'Atzera to Xehetasuna',
            sr_loading: 'Ikuskaritza-gertaerak kargatzen',
            sr_loading_summary: 'Auditoretzaren laburpena kargatzen',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Talde Ezarpenak',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Ezarpenak Inheritance',
            inheritance_subtitle: 'Konfiguratu zein ezarpen-kategoriek taldetik heredatzen duten eta tokiko gainidatziak erabili',
            sla_settings: 'SLA Ezarpenak',
            calendar_settings: 'Calendar Ezarpenak',
            notification_settings: 'Jakinarazpen-ezarpenak',
            intake_settings: 'Intake Ezarpenak',
            cmp_integration: 'CMP Integrazio',
            overridden: 'Gainidatzita',
            inheriting: 'Heredatzen',
            hide_defaults: 'Ezkutatu Lehenetsia',
            show_group_defaults: 'Erakutsi Talde Lehenetsia',
            group_defaults: 'Talde Lehenetsia',
            inherit_from_group: 'Inherit from Talde',
            use_local_override: 'Erabili gainidazketa lokala',
            local_override_json: 'Lokala Gainidatzi (JSON)',
            save_override: 'Gorde Gainidatzi',
            saving: 'Gordetzen...',
            sr_loading: 'Ezarpenen herentzia kargatzen',
            sr_loading_settings: 'Taldearen ezarpenak kargatzen',
            sr_enabled: 'Gaituta',
            sr_disabled: 'Ezindua',
            sr_override_active: 'Tokiko baliogabetzea aktibo',
            sr_inherited: 'Gurasoengandik heredatua',
            danger_zone: 'Arrisku Gunea',
            sharing_options: 'Partekatzeko aukerak',
            advanced_options: 'Aukera aurreratuak',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiboa',
        status_dissolved: 'Disolbatua',
        status_pending: 'Zain',
        status_suspended: 'Etenda',
    },
    integrations: {
        nav: {
            webhooks: 'Webhook-ak',
            domains: 'Domeinuak',
            export_schedules: 'Esportazio-programazioak',
            email_log: 'Posta elektroniko-erregistroa',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Erabiltzaileak',
            roles: 'Rolak',
            profile: 'Nire profila',
        },
        security: {
            mfa: 'MFA ezarpenak',
            sso: 'SSO konfigurazioa',
            api_keys: 'API giltzak',
            service_accounts: 'Zerbitzu-kontuak',
            ip_allowlist: 'IP baimenduen zerrenda',
            delegations: 'Delegazioak',
            break_glass: 'Larrialdi-sarbidea',
            session_settings: 'Saio-ezarpenak',
            active_sessions: 'Saio aktiboak',
            access_reviews: 'Sarbide-berrikusketak',
            my_reviews: 'Nire berrikusketak',
            role_analytics: 'Rolen analitika',
            attestation_schedules: 'Atestigaritze-egutegiak',
            sod_rules: 'SoD arauak',
            sod_violations: 'SoD urratzeak',
        },
        module_config: {
            title: 'Moduluen konfigurazioa',
            subtitle: 'Gaitu edo desgaitu zure erakundearen funtzio-moduluak. Desgaitutako moduluak nabigaziotik ezkutatzen dira.',
            enabled: 'Gaituta',
            disabled: 'Desgaituta',
            save: 'Gorde konfigurazioa',
            saving: 'Gordetzen...',
            saved: 'Konfigurazioa behar bezala gorde da',
            nav_label: 'Moduluen konfigurazioa',
            settings: 'Ezarpenak',
            webhooks: 'Webhook-ak',
            domains: 'Domeinuak',
            export_schedules: 'Esportazio-egutegiak',
            email_log: 'Posta elektronikoaren erregistroa',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Fitxategiak',
            configuration: 'Konfigurazioa',
            browser: 'Fitxategi-arakatzailea',
            dashboard: 'Fitxategien aginte-panela',
        },
        immutable_audit_trails: {
            nav_group_label: 'Auditoretza eta froga',
            nav_settings: 'Ezarpenak',
            nav_status: 'Frogaren egoera',
            nav_integrity_center: 'Osotasun-zentroa',
            nav_usage: 'Erabilera eta kredituak',
            settings_title: 'Auditoretza-aztarna aldaezinak — Ezarpenak',
            settings_subtitle: 'Auditoretza-aztarna aldaezinen gehigarriaren erakundeko ezarpenak.',
            settings_banner: 'Auditoretza-aztarna aldaezinen gehigarria ez dago gaituta zure erakundearentzat. Gaitu hemen A maila eta B mailako auditoretza-gertaeren froga ainguratzen hasteko.',
            status_title: 'Auditoretza-aztarna aldaezinak — Frogaren egoera',
            status_subtitle: 'Gertaerako frogen erlazioak modulu guztietan.',
            status_banner: 'Auditoretza-aztarna aldaezinen gehigarria ez dago gaituta zure erakundearentzat. Joan Ezarpenetara gaitzeko.',
            integrity_center_title: 'Auditoretza-aztarna aldaezinak — Osotasun-zentroa',
            integrity_center_subtitle: 'Egiaztatu frogak eta esportatu Osotasun Paketeak.',
            integrity_center_banner: 'Auditoretza-aztarna aldaezinen gehigarria ez dago gaituta zure erakundearentzat. Joan Ezarpenetara hura gaitzeko egiaztapena exekutatu edo Osotasun Paketeak esportatu aurretik.',
            usage_title: 'Auditoretza-aztarna aldaezinak — Erabilera eta kredituak',
            usage_subtitle: 'Kreditu-saldoa, pakete-erosketak eta erabilera-historia.',
            usage_banner: 'Gaitu Auditoretza-aztarna aldaezinak Ezarpenetan froga-kredituak kontsumitzen hasteko.',
            module_tips_placeholder: 'Azal honi buruzko aholkuak eta gidak laguntza-zentroan eskuragarri daude.',
            column_label: 'Froga',
            no_proof_aria: 'Ez da frogarik erregistratu',
            proof_status_receipt_label: 'agiria {id}',
            in_progress_hint: 'Abian — automatikoki eguneratzen da',
            status_pills: {
                pending: 'Zain',
                submitted: 'Bidalita',
                anchored: 'Ainguratuta',
                confirmed: 'Berretsita',
                retrying: 'Berriz saiatzen',
                paused_cap: 'Gelditua (muga)',
                failed: 'Huts egin du',
                failed_auth: 'Autentifikazioak huts egin du',
            },
            status_tooltips: {
                pending: 'Zain: froga-zerbitzurako ilaran dago. Laster bidaliko da; ez da ekintzarik behar.',
                submitted: 'Bidali da: zure gertaera froga zerbitzura bidali da. Batch modurako, katean zigilatu eta ainguratzeko zain dago. Automatikoki eguneratzen da; ez da ekintzarik behar.',
                anchored: 'Ainguratuta: sorta katean idatzi da eta behin betiko berrespenaren zain dago. Automatikoki eguneratzen da; ez da ekintzarik behar.',
                confirmed: 'Berretsia - froga katean ainguratuta dago eta egiazta daiteke.',
                retrying: 'Berriro saiatzen — errore iragankorra gertatu da. Berradiskidetzailea automatikoki berriro saiatzen ari da; ez da ekintzarik behar.',
                paused_cap: 'Pausatuta: zure hileko froga-kreditu-muga iritsi da. Erabili kreditu gehiago edo itxaron hurrengo fakturazio hilabetera arte.',
                failed: 'Huts egin du — ainguratzea ez da lortu. Berradiskidetzailea automatikoki berriro saiatuko da; jar zaitez harremanetan laguntzarekin jarraitzen badu.',
                failed_auth: 'Autentifikazioak huts egin du — froga-zerbitzuak gure kredentzialak baztertu ditu. Egiaztatu Ikuskaritza-bide aldaezinak ezarpenen orria.',
            },
            mode_pills: {
                direct: 'Zuzena',
                batch: 'Sortakoa',
            },
            drawer: {
                title: 'Frogaren xehetasunak',
                close: 'Itxi',
                section_proof: 'Froga',
                section_batch: 'Sortako kidetasuna',
                status: 'Egoera',
                proof_reference: 'Frogaren erreferentzia',
                receipt_id: 'Agiriaren ID-a',
                taxonomy_class: 'Taxonomia-klasea',
                units_consumed: 'Kontsumitutako unitateak',
                created_at: 'Sortua',
                verification_ref: 'Katearen egiaztapen-erreferentzia',
                chain: 'Katea',
                tx_hash: 'Tx hash',
                block_ref: 'Bloke-erref.',
                batch_reference: 'Sortako erreferentzia',
                leaf_index: 'Hosto-indizea',
                local_event_hash: 'Tokiko gertaeraren hash',
                show_inclusion: 'Erakutsi inklusio-bidea',
                hide_inclusion: 'Ezkutatu inklusio-bidea',
                privacy_note: 'Karga hash bihurtzen da; ez da datu pertsonalik atera ComplianceOnetik gertaera honetan.',
                verify_now: 'Egiaztatu orain',
                verify_pending: 'Egiaztapena egiten…',
                verify_verified: 'Egiaztatuta — tokiko eta katearen aldeko egiaztapenak ondo igaro dira.',
                verify_issue_prefix: 'Egiaztapen-arazoa: ',
                verify_failed: 'Egiaztapen-deiak huts egin du.',
                verify_result_prefix: 'Egiaztapenaren epaia: ',
                verify_pending_note: 'Egiaztatzeko zain: froga hau oraindik katearen ainguraketaren zain dago. Saiatu berriro lotea zigilatu ondoren.',
                awaiting_anchor_note: 'Froga hau lote ireki baten parte da. Egiaztapena eskuragarri egongo da lotea zigilatu eta katean ainguratuta dagoenean, normalean erakundearen konfiguratutako lotearen leihoan. Egoeraren bereizgarria automatikoki eguneratuko da.',
                terminal_error_note: 'Froga honek huts egin zuen aingura. Froga-egoera bateratzailea automatikoki berriro saiatuko da; eskuzko egiaztapenak ez du ezer egiaztatzeko oraindik.',
            },
            integrity_center: {
                section_run: 'Exekutatu egiaztapena',
                section_progress: 'Exekuzioaren aurrerapena',
                section_issues: 'Arazoak',
                period_start: 'Aldiaren hasiera',
                period_end: 'Aldiaren amaiera',
                module_filter: 'Moduluak (komaz bereizita, aukerakoa)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Exekutatu egiaztapena',
                export_button: 'Esportatu Osotasun Paketea',
                export_success: 'Osotasun Paketea esportatu da.',
                export_failed: 'Esportazioak huts egin du.',
                empty_disabled: 'Gaitu Auditoretza-aztarna aldaezinak Ezarpenetan egiaztapen-pasaldiak exekutatzeko.',
                error_period_required: 'Aukeratu hasiera- eta amaiera-data.',
                error_start_run: 'Ezin izan da exekuzioa hasi.',
                processed: 'Prozesatua: {processed} / {total}',
                col_verdict: 'Epaia',
                col_count: 'Kopurua',
                col_proof_link: 'Frogaren esteka',
                col_module: 'Modulua',
                col_event_type: 'Gertaera mota',
                col_expected: 'Espero zen hash',
                col_recomputed: 'Berriz kalkulatutako hash',
                col_raised_at: 'Sortua',
                any_verdict: 'Edozein epai',
                no_issues: 'Ez da arazorik aurkitu.',
            },
            settings_page: {
                load_error: 'Ezin izan dira ezarpenak kargatu.',
                save: 'Gorde',
                save_success: 'Ezarpenak gorde dira.',
                save_error: 'Gordetzeak huts egin du.',
                discard: 'Baztertu aldaketak',
                read_only_banner: 'Irakurtzeko soilik duzu sarbidea. Jarri harremanetan plataformako administratzaile batekin aldaketak egiteko.',
                section_status: 'Egoera eta gaitzea',
                enabled_label: 'Gehigarria gaituta',
                enabled_help: 'Gaituta dagoenean, A maila eta B mailako auditoretza-gertaera oro hash bihurtzen eta ainguratzen da.',
                section_mode: 'Froga-modu lehenetsia',
                mode_direct: 'Zuzena (gertaera bakoitzeko aingura bat)',
                mode_batch: 'Sortakoa (Merkle-erro aingura taldeko)',
                mode_hybrid: 'Hibridoa (klaseko erregistroaren araberako lehenetsia)',
                batch_window_label: 'Sortako leihoa (minutuak, 1–60)',
                section_taxonomy: 'Babestutako gertaeren taxonomia',
                taxonomy_help: 'Gertaera-klase bakoitzeko gainidazketak. A maila → modu zuzena, B maila → sortako modua, Itzalita → ez da frogatzen.',
                taxonomy_search: 'Bilatu gainidazketak',
                taxonomy_reset: 'Berrezarri lehenetsietara',
                taxonomy_event_class: 'Gertaera-klasea',
                taxonomy_override: 'Gainidazketa',
                taxonomy_actions: 'Ekintzak',
                taxonomy_clear: 'Garbitu',
                taxonomy_no_overrides: 'Gainidazketarik ez — erregistroko lehenetsiak erabiltzen.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Gehitu gainidazketa',
                section_caps: 'Mugak eta limiteak',
                cap_label: 'Hileko muga (unitateak; hutsik = mugagabea)',
                warn_label: 'Ohartarazpen-atalasea (unitateak; mugatik beherakoa izan behar du)',
                auto_pause_label: 'Geldiarazi automatikoki froga berriak muga lortzean',
                section_credentials: 'Kredentzialak',
                credential_id: 'Kredentzialaren ID-a',
                credential_rotated_at: 'Azken txandakatzea',
                rotate_button: 'Txandakatu kredentziala',
                rotate_success: 'Kredentziala txandakatu da. Kredentzial zaharra {n} minutuz gehiago izango da baliozkoa.',
                rotate_failed: 'Kredentzial-txandakatzeak huts egin du.',
                rotate_modal_title: 'AesirX kredentziala txandakatu?',
                rotate_modal_body: 'Txandakatzeak kredentzial berri bat sortzen du. Kredentzial zaharra AesirX-ek 60 minutuz onartzen jarraitzen du, gauzatzen ari diren langileek beren uneko lanak amaitu ahal izan ditzaten. 60 minutu igaro ondoren, kredentzial zaharra baliogabetzen da.',
                rotate_modal_confirm: 'Txandakatu orain',
                tier_a_off_modal_title: 'Desgaitu gertaera-klase baten frogatzea?',
                tier_a_off_modal_body: 'Gertaera-klase bat «Itzalita» ezartzeak froga-iturria gertaera horiek ainguratzeari uzten dio. Arkitekturaren §6 atalak inplikazio arautzailea azaltzen du: A mailako gertaerak araugilearentzako garrantzitsuak dira; horiek itzaltzeak zure auditoretza-aztarnaren defentsa-ahalmena hauts dezake.',
                tier_a_off_modal_confirm: 'Ulertzen dut — aplikatu gainidazketa',
                error_warn_lt_cap: 'Ohartarazpen-atalasea hileko muga baino txikiagoa izan behar du.',
                error_window_range: 'Sortako leihoa 1 eta 60 minutu artean egon behar da.',
                error_run_in_progress: 'Egiaztapen-exekuzio bat abian da; ezin da gehigarria orain desgaitu.',
            },
            status_page: {
                filter_status: 'Egoera',
                filter_mode: 'Froga-modua',
                filter_module: 'Modulua',
                filter_source_collection: 'Iturburu-bilduma',
                filter_from_date: 'Hasiera',
                filter_to_date: 'Amaiera',
                filter_search: 'Bilatu',
                any_status: 'Edozein egoera',
                any_mode: 'Edozein modu',
                col_module: 'Modulua',
                col_event_type: 'Gertaera mota',
                col_proof_status: 'Egoera',
                col_proof_mode: 'Modua',
                col_created_at: 'Sortua',
                col_units: 'Unitateak',
                empty_disabled: 'Auditoretza-aztarna aldaezinen gehigarria ez dago gaituta zure erakundearentzat. Joan Ezarpenetara gaitzeko.',
                empty_enabled_no_rows: 'Ez dago uneko iragazkiekin bat datorren froga-estekarik.',
                export_csv: 'Esportatu CSV',
                export_xlsx: 'Esportatu XLSX',
                export_success: 'Esportazioa osatu da',
                export_failed: 'Esportazioak huts egin du',
            },
            usage_page: {
                section_alerts: 'Alertak',
                alert_dismiss: 'Baztertu',
                alert_dismiss_aria: 'Baztertu alerta {id}',
                alert_dismissed: 'Alerta baztertu da',
                alert_dismiss_failed: 'Ezin izan da baztertu alerta',
                section_balance: 'Kreditu-saldoa',
                balance_label: 'Erabilgarri dauden kredituak',
                balance_hint: 'Geratzen diren unitateak (1 unitate = babestutako gertaera baten froga).',
                section_current_month: 'Uneko hila',
                consumed_label: 'Kontsumituta',
                granted_label: 'Emandakoa',
                refunded_label: 'Itzulitakoa',
                net_label: 'Garbia',
                section_history: 'Erabilera-historia',
                history_kind: 'Mota',
                history_units: 'Unitateak',
                history_balance_after: 'Geroko saldoa',
                history_notes: 'Oharrak',
                history_at: 'Noiz',
                cap_warning_banner: 'Fakturazio-hilabete honetarako ohartarazpen-atalasea gainditu duzu. Hartu kontuan kreditu gehigarriak amortizatzea.',
                cap_paused_banner: 'Hileko muga lortuta. Froga-bidalketak geldituta daude hurrengo hilabetera arte edo muga handitu arte.',
                section_redeem: 'Amortizatu kreditu-paketea',
                redeem_purchase_id: 'Faktura-zenbakia',
                redeem_package: 'Paketea',
                redeem_button: 'Amortizatu',
                redeem_pending: 'Trukatzen…',
                redeem_success: 'Kredituak amortizatu dira',
                redeem_failed: 'Amortizatzeak huts egin du',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'unitateak',
                alert_dismissing: 'Baztertzen…',
                alert_kind: {
                    cap_reached: 'Hileko mugara iritsi da',
                    low_balance: 'Balantze baxua',
                    auth_failure: 'Autentifikazio huts egitea',
                    merkle_mismatch: 'Merkle desegokia',
                    batch_terminal_failure: 'Batch terminal porrota',
                    direct_terminal_failure: 'Bidalketa zuzeneko terminalaren hutsegitea',
                },
                usage_kind: {
                    consume: 'Kontsumitu',
                    refund: 'Itzulketa',
                    grant: 'Beka',
                    adjustment: 'Egokitzapena',
                    reservation: 'Erreserba',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Hasiberria',
                package_growth: 'Hazkundea',
                package_enterprise: 'Enpresa',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Erosi kreditu gehiago',
                    purchase_title: 'Erosi kreditu gehiago',
                    purchase_subtitle: 'Aukeratu pakete bat. Stripe-ren ordainketa seguruaren orrira eramango zaituzte, eta zure faktura posta elektronikoz jasoko duzu.',
                    checkout_button: 'Jarraitu ordainketara',
                    checkout_pending: 'Ordainketa irekitzen…',
                    cancel_button: 'Utzi',
                    purchase_error: 'Ezin izan da ordainketa hasi. Saiatu berriro geroxeago.',
                    redeem_subtitle: 'Ordaindu duzu jada? Aukeratu erositako paketea eta sartu zure posta elektronikoko faktura-IDa kredituak gehitzeko.',
                    error_not_found: 'Ez da zenbaki horrekin fakturarik aurkitu. Egiaztatu faktura-zenbakia eta saiatu berriro.',
                    error_not_paid: 'Faktura hau ez dago oraindik ordaindutzat markatuta.',
                    error_org_mismatch: 'Faktura hau ez zaio zure erakundeari jaulki.',
                    error_package_mismatch: 'Faktura hau ez dator bat aukeratutako paketearekin.',
                    error_billing_unavailable: 'Ezin izan da fakturazio-zerbitzura iritsi. Saiatu berriro geroxeago.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Fitxategien konfigurazioa',
            subtitle: 'Konfiguratu fitxategi-igoeren mugak, atxikitze-politikak eta jabe-motaren araberako gainidazketak plataformako eranskin-azalerarako.',
            section_global: 'Lehenetsi globalak',
            section_global_help: 'Muga hauek zure erakundeko erabiltzaileek igotako fitxategi orori aplikatzen zaizkio, beheko jabe-motaren araberako gainidazketa batek aldatu ezean.',
            section_overrides: 'Jabe-motaren araberako gainidazketak',
            section_overrides_help: 'Gainidatzi muga globalak zehazten den jabe-motarako. Jabe-motaren araberako balioek beti irabazten diote ezarpen globalari.',
            max_file_size_label: 'Fitxategiaren gehienezko tamaina',
            max_file_size_hint: 'Igoera bakoitzeko (MiB)',
            max_total_attachments_label: 'Eranskin guztien gehienezko kopurua',
            max_total_attachments_hint: 'Erakunde osoan zehar',
            max_total_size_label: 'Biltegiratze osoaren gehienezko tamaina',
            max_total_size_hint: 'Erakunde osoan zehar (MiB)',
            allowed_mime_types_label: 'Onartutako fitxategi motak',
            allowed_mime_types_placeholder: 'Hautatu MIME motak',
            retention_days_label: 'Atxikitze-aldia (egunak)',
            retention_days_hint: 'Fitxategiak ezabatzeko hautagai izan aurretik zenbat denbora gorde behar diren. Utzi hutsik atxikitze-politikarik ez izateko.',
            legal_hold_label: 'Atxikipen juridikoa',
            legal_hold_hint: 'Gaituta dagoenean, jabe-mota honetako fitxategi bat behin betiko ezabatzeak atxikipen juridikoa gainditzeko baimena eta operadorearen berariazko onarpena eskatzen ditu.',
            save: 'Gorde aldaketak',
            saving: 'Gordetzen…',
            reset: 'Berrezarri',
            save_success: 'Ezarpenak gorde dira',
            empty_state_title: 'Plataformako lehenetsiak erabiltzen',
            empty_state_body: 'Zure erakundea une honetan plataformako fitxategi-politika lehenetsian dabil. Editatu beheko edozein balio erakunde-mailako gainidazketa bat sortzeko.',
            read_only_banner: 'Fitxategien konfigurazioan irakurtzeko soilik duzu sarbidea. Jarri harremanetan plataformako administratzaile batekin aldaketak egiteko.',
            owner_type_default_badge: 'Lehenetsia',
            owner_type_overridden_badge: 'Gainidatzia',
            owner_type_label: 'Jabe mota',
            owner_type_collapse: 'Tolestu',
            owner_type_expand: 'Zabaldu',
            override_save: 'Gorde gainidazketa',
            override_remove: 'Kendu gainidazketa',
            mime_labels: {
                'application/pdf': 'PDF dokumentua',
                'image/png': 'PNG irudia',
                'image/jpeg': 'JPEG irudia',
                'image/jpg': 'JPG irudia',
                'application/msword': 'Word dokumentua (zaharra)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word dokumentua',
                'application/vnd.ms-excel': 'Excel kalkulu-orria (zaharra)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel kalkulu-orria',
                'text/csv': 'CSV fitxategia',
                'text/plain': 'Testu hutsa',
                'message/rfc822': 'Posta elektroniko-mezua',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Fitxategi-arakatzailea',
            subtitle: 'Bilatu, iragazi eta kudeatu erakunde osoan igotako eranskin oro.',
            filter_owner_type: 'Jabe mota',
            filter_uploader: 'Igo duena',
            filter_from: 'Igota ondoren',
            filter_to: 'Igota lehenago',
            filter_size_min: 'Gutxieneko tamaina (MiB)',
            filter_size_max: 'Gehienezko tamaina (MiB)',
            filter_mime: 'Fitxategi mota',
            filter_deleted_state: 'Ezabatze-egoera',
            filter_deleted_any: 'Fitxategi guztiak',
            filter_deleted_only: 'Ezabatuak soilik',
            filter_deleted_exclude: 'Baztertu ezabatuak',
            filter_orphaned: 'Umezurtzak soilik',
            filter_search_placeholder: 'Bilatu fitxategi-izena…',
            filter_apply: 'Aplikatu iragazkiak',
            filter_clear: 'Garbitu iragazkiak',
            col_filename: 'Fitxategi-izena',
            col_owner_type: 'Jabe mota',
            col_owner_entity: 'Jabea',
            col_uploader: 'Igo duena',
            col_uploaded_at: 'Igota',
            col_size: 'Tamaina',
            col_mime: 'Mota',
            col_state: 'Egoera',
            col_actions: 'Ekintzak',
            state_active: 'Aktibo',
            state_deleted: 'Ezabatuta',
            state_orphaned: 'Umezurtz',
            action_view_detail: 'Ikusi xehetasunak',
            action_download: 'Deskargatu',
            action_ai_edits: 'IA edizioak',
            action_soft_delete: 'Ezabaketa biguna',
            action_restore: 'Berreskuratu',
            action_hard_delete: 'Ezabaketa gogorra',
            bulk_soft_delete: 'Ezabatu hautatutakoak (biguna)',
            bulk_export_csv: 'Esportatu CSV',
            bulk_select_all: 'Hautatu denak',
            reason_label: 'Arrazoia',
            reason_min_10_chars: 'Arrazoia derrigorrezkoa da (gutxienez 10 karaktere)',
            reason_min_20_chars: 'Arrazoia derrigorrezkoa da (gutxienez 20 karaktere)',
            legal_hold_override_label: 'Gainditu atxikipen juridikoa',
            legal_hold_override_warning: 'Fitxategi hau atxikipen juridikoaren pean dago. Behin betiko ezabatzeak auditoretza-aztarnan erregistratuko du zure arrazoia eta gainidazketa-marka.',
            empty_state_title: 'Ez dago zure iragazkiekin bat datorren fitxategirik',
            empty_state_body: 'Doitu iragazkiak emaitzak ikusteko, edo garbitu fitxategi guztiak ikusteko.',
            pagination_prev: 'Aurrekoa',
            pagination_next: 'Hurrengoa',
            pagination_page: '{page} / {total} orria',
            pagination_size_label: 'Orriko',
            pagination_total: 'Guztira: {count}',
            toast_soft_deleted: 'Fitxategia ezabatuta (biguna)',
            toast_restored: 'Fitxategia berreskuratuta',
            toast_hard_deleted: 'Fitxategia behin betiko ezabatuta',
            toast_bulk_deleted: '{count} fitxategi ezabatuta',
            toast_error: 'Eragiketak huts egin du',
            confirm_soft_delete_title: 'Ezabatu fitxategia (biguna)',
            confirm_restore_title: 'Berreskuratu fitxategia',
            confirm_hard_delete_title: 'Ezabatu fitxategia behin betiko',
            confirm_bulk_soft_delete_title: 'Ezabatu hautatutako fitxategiak (biguna)',
            confirm_submit: 'Berretsi',
            confirm_cancel: 'Utzi',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Fitxategiaren xehetasunak',
            close: 'Itxi',
            meta_filename: 'Fitxategi-izena',
            meta_size: 'Tamaina',
            meta_mime: 'Mota',
            meta_owner_type: 'Jabe mota',
            meta_owner_entity: 'Jabe-entitatea',
            meta_uploader: 'Igo duena',
            meta_uploaded_at: 'Igota',
            meta_storage_path: 'Biltegiratze-bidea',
            meta_deleted_state: 'Ezabatze-egoera',
            audit_section_title: 'Auditoretza-aztarna',
            audit_load_more: 'Kargatu gertaera gehiago',
            audit_empty: 'Ez da auditoretza-gertaerarik erregistratu fitxategi honetarako.',
            audit_config_changed: 'Konfigurazioa aldatu da',
            audit_soft_deleted: 'Ezabatuta (biguna)',
            audit_restored: 'Berreskuratuta',
            audit_hard_deleted: 'Behin betiko ezabatuta',
            audit_downloaded: 'Deskargatuta',
            audit_quota_breached: 'Kuota gainditzen saiatu da',
            audit_owner_type_download_denied: 'Eskuratzea ukatuta',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{at}: {actor}(e)k {event}',
            action_download: 'Deskargatu',
            action_soft_delete: 'Ezabaketa biguna',
            action_restore: 'Berreskuratu',
            action_hard_delete: 'Ezabaketa gogorra',
            effective_settings_title: 'Politika eraginkorra',
            effective_max_file_size: 'Fitxategiaren gehienezko tamaina',
            effective_retention_days: 'Atxikitzea',
            effective_legal_hold: 'Atxikipen juridikoa',
            effective_legal_hold_yes: 'Aktibo',
            effective_legal_hold_no: 'Bat ere ez',
        },
        files_dashboard: {
            title: 'Fitxategien aginte-panela',
            subtitle: 'Biltegiratze-erabilera, hazkundea eta fitxategi umezurtzak erakunde osoan zehar.',
            kpi_total_files: 'Fitxategiak guztira',
            kpi_total_size: 'Biltegiratze osoa',
            kpi_near_quota: 'Kuotaren erabilera',
            kpi_orphaned: 'Fitxategi umezurtzak',
            chart_by_owner_type: 'Fitxategiak jabe motaren arabera',
            chart_growth_30d: '30 eguneko hazkundea',
            toggle_count: 'Kopuruka',
            toggle_size: 'Tamainaka',
            orphaned_panel_title: 'Fitxategi umezurtzak (lehen 20)',
            orphaned_view_all: 'Ikusi fitxategi umezurtz guztiak',
            orphaned_empty: 'Ez da fitxategi umezurtzik aurkitu.',
            growth_empty: 'Ez dago igoera-jarduerarik azken 30 egunetan.',
            retention_n_days: '{days} egun',
            retention_none: 'Politikarik ez',
        },
    },
    category_labels: {
        'user-guide': 'Erabiltzailearen Gida',
        'admin-guide': 'Admin Gida',
        'getting-started': 'Hasteko',
        faq: 'Ohiko galderak',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Teklak',
        webhook: 'Webhook',
        webhooks: 'Webhook-ak',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Helburuak',
        preferences: 'Hobespenak',
        onboarding: 'Sartzea',
        'first-steps': 'Lehen Urratsak',
        login: 'Saioa hasi',
        navigation: 'Nabigazioa',
        users: 'Erabiltzaileak',
        user: 'Erabiltzailea',
        roles: 'Rolak',
        role: 'Rola',
        'access-control': 'Sarbide Kontrola',
        'access-management': 'Sarbideen Kudeaketa',
        permissions: 'Baimenak',
        permission: 'Baimena',
        consent: 'Adostasuna',
        'consent-management': 'Adostasunaren kudeaketa',
        privacy: 'Pribatutasuna',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Ikuskaritza',
        auditing: 'Ikuskaritza',
        breach: 'Haustura',
        incident: 'Gorabehera',
        incidents: 'Gorabeherak',
        notification: 'Jakinarazpena',
        notifications: 'Jakinarazpenak',
        reporting: 'Txostenak ematea',
        risk: 'Arriskua',
        vendor: 'Saltzailea',
        vendors: 'Saltzaileak',
        'vendor-risk': 'Saltzaileen Arriskua',
        assessment: 'Ebaluazioa',
        assessments: 'Ebaluazioak',
        dpia: 'DPIA',
        'cross-border': 'Mugaz gaindi',
        transfer: 'Transferentzia',
        transfers: 'Transferentziak',
        deletion: 'Ezabatzea',
        retention: 'Atxikipena',
        'data-mapping': 'Datuen mapaketa',
        discovery: 'Aurkikuntza',
        classification: 'Sailkapena',
        workflow: 'Lan-fluxua',
        workflows: 'Lan-fluxuak',
        template: 'Txantiloia',
        templates: 'Txantiloiak',
        catalog: 'Katalogoa',
        monitoring: 'Jarraipena',
        tasks: 'Zereginak',
        task: 'Zeregin',
        'task-management': 'Zereginen kudeaketa',
        organizations: 'Erakundeak',
        org: 'Antolaketa',
        governance: 'Gobernantza',
        compliance: 'Betetzea',
        forms: 'Formak',
        form: 'Forma',
        connectors: 'Konektoreak',
        connector: 'Konektorea',
        integration: 'Integrazioa',
        integrations: 'Integrazioak',
        security: 'Segurtasuna',
        encryption: 'Enkriptatzea',
        backup: 'Babeskopia',
        recovery: 'Berreskuratzea',
        maintenance: 'Mantentzea',
        settings: 'Ezarpenak',
        configuration: 'Konfigurazioa',
        sla: 'SLA',
        escalation: 'Eskalada',
        remediation: 'Erremediazioa',
        evidence: 'Frogak',
        'case-management': 'Kasuen kudeaketa',
        profile: 'Profila',
        delegation: 'Ordezkaritza',
        'service-accounts': 'Zerbitzu-kontuak',
        license: 'Lizentzia',
        'license-management': 'Lizentzien kudeaketa',
        plan: 'Plana',
        plans: 'Planoak',
        dashboard: 'Aginte-panela',
        'web-consent': 'Web Adostasuna',
        'partner-marketplace': 'Bazkideen merkatua',
        partner: 'Bazkidea',
        partners: 'Bazkideak',
        deliverables: 'Entregagarriak',
        'service-tiers': 'Zerbitzu Mailak',
        'support-tickets': 'Laguntza Sarrerak',
        'audit-trail': 'Ikuskaritza Pista',
        'first-time-setup': 'Lehenengo konfigurazioa',
        setup: 'Konfigurazioa',
        quickstart: 'Hasiera azkarra',
        tour: 'Tourra',
        tours: 'Ibilbideak',
        'sample-data': 'Lagin Datuak',
        'data-subject': 'Datu-gaia',
        'data-subjects': 'Datuen Subjektuak',
        pseudonymous: 'Pseudonimoa',
        identified: 'Identifikatua',
        'help-center': 'Laguntza Zentroa',
        article: 'artikulua',
        articles: 'Artikuluak',
        search: 'Bilatu',
        'platform-settings': 'Plataformaren ezarpenak',
        'platform-config': 'Plataformaren konfigurazioa',
        'module-config': 'Moduluaren konfigurazioa',
        'module-configuration': 'Moduluaren konfigurazioa',
        'rights-requests': 'Eskubideen eskaerak',
        'access-accountability': 'Atzitu Kontu kontuak',
        'data-discovery': 'Datuen Aurkikuntza',
        'incident-ops': 'Gorabeheren Eragiketak',
        'deletion-orchestration': 'Ezabatzeko Orkestrazioa',
        'compliance-forms': 'Betetzeko formularioak',
        'program-governance': 'Programaren Gobernantza',
        localization: 'Lokalizazioa',
        'privacy-scanner': 'Pribatutasun eskanerra',
        'consent-properties': 'Adostasun Propietateak',
        acceptances: 'Onarpenak',
        access: 'Sarbidea',
        'access-review': 'Sarbideen Berrikuspena',
        'access-reviews': 'Sartu Iritziak',
        accountability: 'Erantzukizuna',
        acknowledgment: 'Aitorpena',
        'action-plans': 'Ekintza Planak',
        activation: 'Aktibazioa',
        activations: 'Aktibazioak',
        'active-response': 'Erantzun Aktiboa',
        activity: 'Jarduera',
        adequacy: 'Egokitasuna',
        'admin-guide': 'Admin Gida',
        aggregation: 'Agregazioa',
        agreements: 'Hitzarmenak',
        ai: 'AI',
        alerts: 'Alertak',
        analytics: 'Analitika',
        anchoring: 'Ainguraketa',
        approval: 'Onarpena',
        approvals: 'Onarpenak',
        apps: 'Aplikazioak',
        assets: 'Aktiboak',
        assignments: 'Zereginak',
        attestation: 'Egiaztagiria',
        'audit-log': 'Ikuskaritza Erregistroa',
        'audit-pack': 'Ikuskaritza paketea',
        authentication: 'Autentifikazioa',
        authority: 'Agintea',
        'auto-play': 'Erreprodukzio automatikoa',
        automated: 'Automatizatua',
        automation: 'Automatizazioa',
        baseline: 'Oinarrizko lerroa',
        batches: 'Loteak',
        bidirectional: 'Bi norabidekoa',
        bilingual: 'Elebiduna',
        bindings: 'Loturak',
        blockchain: 'Blockchain',
        board: 'Kontseilua',
        brand: 'Marka',
        brds: 'BRDak',
        'break-glass': 'Haustura-Beira',
        builder: 'Eraikitzailea',
        bulk: 'Solasean',
        'bulk-sync': 'Sinkronizazioa masiboa',
        bundles: 'Sorteak',
        calendar: 'Egutegia',
        campaigns: 'Kanpainak',
        cases: 'Kasuak',
        categories: 'Kategoriak',
        category: 'Kategoria',
        certification: 'Ziurtagiria',
        chain: 'Katea',
        change: 'Aldatu',
        'change-awareness': 'Aldatu Kontzientzia',
        'change-log': 'Aldaketa erregistroa',
        'change-management': 'Aldaketen kudeaketa',
        'change-requests': 'Aldaketa-eskaerak',
        'cmp-validation': 'CMP Balioztatzea',
        collection: 'Bilduma',
        comparison: 'Konparaketa',
        completeness: 'Osotasuna',
        completion: 'Osatzea',
        'compliance-posture': 'Betetze jarrera',
        'compliance-proof': 'Betetze Froga',
        'compliance-reviews': 'Betetze Berrikuspenak',
        config: 'Konfig',
        confirmation: 'Berrespena',
        conflicts: 'Gatazkak',
        consolidation: 'Finkatzea',
        continuous: 'Etengabea',
        contracts: 'Kontratuak',
        controls: 'Kontrolak',
        cookies: 'Cookieak',
        cooperation: 'Lankidetza',
        coordination: 'Koordinazioa',
        'corporate-governance': 'Gobernu Korporatiboa',
        'corrective-actions': 'Ekintza zuzentzaileak',
        coverage: 'Estaldura',
        'credential-vault': 'Kredentzialen ganga',
        credentials: 'Kredentzialak',
        cycles: 'Zikloak',
        dashboards: 'Aginte-panelak',
        'data-assets': 'Datu-aktiboak',
        'data-flows': 'Datu-fluxuak',
        'data-sensitivity': 'Datuen sentikortasuna',
        'data-steward': 'Datuen arduraduna',
        deadlines: 'Epeak',
        'decree-356': '356 Dekretua',
        dedup: 'Deskargatu',
        definitions: 'Definizioak',
        deliverable: 'Entregagarria',
        departments: 'Sailak',
        'dependency-order': 'Mendekotasun Agindua',
        deployment: 'Hedapena',
        detection: 'Detekzioa',
        diff: 'Desf',
        'digital-properties': 'Propietate digitalak',
        dimensions: 'Neurriak',
        disable: 'Desgaitu',
        'disaster-recovery': 'Hondamendien Berreskuratzea',
        disclosure: 'Dibulgazioa',
        documentation: 'Dokumentazioa',
        documents: 'Dokumentuak',
        domains: 'Domeinuak',
        dossier: 'Dosierra',
        dpa: 'DPA',
        drift: 'Deriba',
        'drift-detection': 'Deriba antzematea',
        'due-diligence': 'Duela Diligentzia',
        email: 'Email',
        emergency: 'Larrialdia',
        enable: 'Gaitu',
        'enterprise-map': 'Enpresaren mapa',
        erasure: 'Ezabatzea',
        evaluation: 'Ebaluazioa',
        'event-bus': 'Ekitaldiaren autobusa',
        exceptions: 'Salbuespenak',
        execution: 'Exekuzioa',
        executive: 'Exekutiboa',
        exercises: 'Ariketak',
        export: 'Esportatu',
        exports: 'Esportazioak',
        filing: 'Artxibatzea',
        filter: 'Iragazkia',
        filtering: 'Iragazkia',
        findings: 'Aurkikuntzak',
        flows: 'Fluxuak',
        'follow-up': 'Jarraipena',
        forensic: 'Foruzaingoa',
        forensics: 'Foruzaingoak',
        'form-filler': 'Inprimaki betegarria',
        framework: 'Esparrua',
        frameworks: 'Esparruak',
        'gap-analysis': 'Hutsuneen Analisia',
        'gap-report': 'Hutsuneen txostena',
        gaps: 'Hutsuneak',
        gates: 'Ateak',
        'getting-started': 'Hasteko',
        'golden-templates': 'Urrezko Txantiloiak',
        grc: 'GRC',
        'group-management': 'Talde Zuzendaritza',
        groups: 'Taldeak',
        'guided-tours': 'Bisita gidatuak',
        guides: 'Gidak',
        handling: 'Manipulazioa',
        'hands-on': 'Eskuak',
        health: 'Osasuna',
        help: 'Laguntza',
        hierarchy: 'Hierarkia',
        history: 'Historia',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Nortasun hornitzailea',
        implementation: 'Ezarpena',
        import: 'Inportatu',
        improvement: 'Hobekuntza',
        'incident-response': 'Gorabeheren Erantzuna',
        inconsistencies: 'Inkoherentziak',
        instances: 'Instantziak',
        intake: 'Hartzea',
        'intake-key': 'Hartzeko giltza',
        interactive: 'Interaktiboa',
        international: 'Nazioartekoa',
        inventory: 'Inbentarioa',
        investigation: 'Ikerketa',
        investigations: 'Ikerketak',
        'ip-allowlist': 'IP baimenduen zerrenda',
        issues: 'Gaiak',
        justification: 'Justifikazioa',
        justifications: 'Justifikazioak',
        kanban: 'Kanban',
        keyboard: 'Teklatua',
        keys: 'Giltzak',
        'knowledge-base': 'Ezagutza Oinarria',
        koncernstyring: 'Gobernu Korporatiboa',
        kpi: 'KPI',
        labeling: 'Etiketatzea',
        language: 'Hizkuntza',
        'language-packs': 'Hizkuntza paketeak',
        languages: 'Hizkuntzak',
        lawfulness: 'Legezkotasuna',
        layout: 'Diseinua',
        'legal-basis': 'Oinarri juridikoa',
        'lessons-learned': 'Ikasgaiak',
        levels: 'Mailak',
        library: 'Liburutegia',
        lifecycle: 'Bizi-zikloa',
        lineage: 'Leinua',
        links: 'Estekak',
        listings: 'Zerrendak',
        live: 'Zuzenean',
        locale: 'Tokikoa',
        log: 'Log',
        logging: 'Erregistratzea',
        'managed-service': 'Kudeatutako Zerbitzua',
        mapping: 'Kartografia',
        mappings: 'Mapeoak',
        'market-packs': 'Merkatu paketeak',
        marketplace: 'Merkatua',
        maturity: 'Heldutasuna',
        metrics: 'Metrikak',
        models: 'Ereduak',
        module: 'Modulua',
        modules: 'Moduluak',
        mps: 'MPS',
        'multi-factor': 'Faktore Anitzeko',
        'multi-market': 'Multimerkatua',
        'multi-org': 'Erakunde anitzeko',
        'multi-property': 'Jabetza Anitzekoa',
        'my-tasks': 'Nire Zereginak',
        obligations: 'Betebeharrak',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orkestrazioa',
        'org-groups': 'Taldeak',
        organization: 'Antolaketa',
        overdue: 'Atzeratuta',
        overview: 'Ikuspegi orokorra',
        'owner-review': 'Jabearen Iritzia',
        package: 'Paketea',
        packages: 'Paketeak',
        packs: 'Paketeak',
        pagination: 'Paginazioa',
        'parent-child': 'Guraso-Seme-alabak',
        patterns: 'Ereduak',
        performance: 'Errendimendua',
        periodic: 'Periodikoa',
        periods: 'Aldiak',
        personal: 'Pertsonalak',
        'personal-data': 'Datu pertsonalak',
        personas: 'Pertsonak',
        planning: 'Plangintza',
        platform: 'Plataforma',
        playbooks: 'Jolas liburuak',
        poc: 'PoC',
        'point-in-time': 'Denbora-puntua',
        policies: 'Politikak',
        portfolio: 'Portfolioa',
        'post-incident': 'Gertakariaren ostekoa',
        'power-user': 'Erabiltzaile boteretsua',
        practice: 'Praktikatu',
        pricing: 'Prezioak',
        'privacy-impact': 'Pribatutasun-eragina',
        procedures: 'Prozedurak',
        processes: 'Prozesuak',
        processing: 'Tramitazioa',
        'processing-activities': 'Prozesatzeko Jarduerak',
        procurement: 'Kontratazioa',
        productivity: 'Produktibitatea',
        profiles: 'Profilak',
        programs: 'Programak',
        progress: 'Aurrerapena',
        proof: 'Froga',
        propagation: 'Hedapena',
        properties: 'Propietateak',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Forma publikoa',
        quality: 'Kalitatea',
        questionnaires: 'Galdetegiak',
        'quick-links': 'Esteka azkarrak',
        'quick-start': 'Hasiera azkarra',
        'quick-starts': 'Hasiera azkarrak',
        raci: 'RACI',
        readiness: 'Presttasuna',
        'real-time': 'Denbora errealean',
        reassessments: 'Berriz ebaluaketak',
        reconciliation: 'Adiskidetzea',
        record: 'Grabatu',
        records: 'Erregistroak',
        recurring: 'Errepikatzen',
        register: 'Izena eman',
        registry: 'Erregistroa',
        regulations: 'Araudiak',
        regulator: 'Erregulatzailea',
        regulatory: 'Araudia',
        'regulatory-frameworks': 'Arau-esparruak',
        remap: 'Birmapatzea',
        reminders: 'Oroigarriak',
        reports: 'Txostenak',
        requests: 'Eskaerak',
        requirements: 'Baldintzak',
        response: 'Erantzuna',
        'response-plans': 'Erantzun Planak',
        'response-team': 'Erantzun Taldea',
        'response-templates': 'Erantzun txantiloiak',
        'response-time': 'Erantzun Denbora',
        responses: 'Erantzunak',
        responsibility: 'Erantzukizuna',
        results: 'Emaitzak',
        review: 'Berrikuspena',
        reviews: 'Iritziak',
        'right-to-delete': 'Ezabatzeko eskubidea',
        rights: 'Eskubideak',
        'risk-tiers': 'Arrisku Mailak',
        rollouts: 'Inplementazioak',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Bideratzea',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Arauak',
        runs: 'Korrikak',
        saml: 'SAML',
        scan: 'Eskaneatu',
        scanning: 'Eskaneatzea',
        scc: 'SCC',
        schedule: 'Ordutegia',
        scheduled: 'Programatua',
        schedules: 'Ordutegiak',
        scheduling: 'Programazioa',
        scores: 'Puntuazioa',
        scoring: 'Puntuazioa',
        scripts: 'Gidoiak',
        'security-reviews': 'Segurtasun Berrikuspenak',
        'sensitive-data': 'Datu sentikorrak',
        sensitivity: 'Sentikortasuna',
        'separation-of-duties': 'Eginkizunen Banaketa',
        service: 'Zerbitzua',
        sessions: 'Saioak',
        'setup-wizard': 'Konfigurazio morroia',
        severity: 'Larritasuna',
        'shared-governance': 'Gobernantza Partekatua',
        'shared-settings': 'Ezarpen partekatuak',
        shortcuts: 'Lasterbideak',
        sidebar: 'Alboko barra',
        signals: 'Seinaleak',
        'single-sign-on': 'Saio-hasiera bakarra',
        snapshots: 'Argazkiak',
        sod: 'SoD',
        sorting: 'Sailkatzea',
        sources: 'Iturriak',
        staging: 'Eszenaratzea',
        standards: 'Arauak',
        status: 'Egoera',
        structure: 'Egitura',
        'sub-processor': 'Azpiprozesadorea',
        'sub-processors': 'Azpiprozesadoreak',
        submissions: 'Bidalketak',
        subprocessors: 'Azpiprozesadoreak',
        subscription: 'Harpidetza',
        suggestions: 'Iradokizunak',
        summaries: 'Laburpenak',
        supplements: 'Osagarriak',
        support: 'Laguntza',
        sync: 'Sinkronizatu',
        system: 'Sistema',
        systems: 'Sistemak',
        tables: 'Taulak',
        tagging: 'Etiketatzea',
        'tamper-proof': 'Tamper-Froga',
        taxonomies: 'Taxonomiak',
        taxonomy: 'Taxonomia',
        tenants: 'Maizterrak',
        termination: 'Amaiera',
        'third-party': 'Hirugarrena',
        'ticket-sync': 'Ticket Sync',
        tickets: 'Sarrerak',
        tiers: 'Mailak',
        tokens: 'Tokenak',
        totp: 'TOTP',
        traceability: 'Trazabilitatea',
        tracking: 'Jarraipena',
        trail: 'Ibilbidea',
        training: 'Prestakuntza',
        translation: 'Itzulpena',
        translations: 'Itzulpenak',
        transparency: 'Gardentasuna',
        trends: 'Joerak',
        unified: 'Bateratua',
        updates: 'Eguneraketak',
        validation: 'Balioztatzea',
        vault: 'Ganga',
        'vendor-requests': 'Saltzaileen eskaerak',
        verification: 'Egiaztapena',
        versioning: 'Bertsioa',
        versions: 'Bertsioak',
        vietnam: 'Vietnam',
        violations: 'Urraketak',
        visualization: 'Bistaratzea',
        waivers: 'Salbuespenak',
        'web-records': 'Web Erregistroak',
        website: 'Webgunea',
        websites: 'Webguneak',
        weights: 'Pisuak',
        'white-label': 'Marka Zuria',
        withdrawal: 'Erretiratzea',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Eskubide-eskaerak',
        data_mapping: 'Tratamendu-jardueren erregistroa',
        governance: 'Gobernantza',
        consent: 'Adostasunaren gobernantza',
        consent_properties: 'Adostasunaren propietateak',
        assessments: 'DBEE eta ebaluazioak',
        classification: 'Datuen sailkapena',
        monitoring: 'Jarraipen-programak',
        program: 'Jarraipen-programa',
        incidents: 'Gertakariei erantzutea',
        connectors: 'Konektoreak',
        vendor_risk: 'Saltzaileen Arriskua',
        deletion_orchestration: 'Ezabaketa-orkestrazioa',
        discovery: 'Datuen aurkikuntza',
        incident_ops: 'Gertaeren eragiketak',
        program_governance: 'Programaren gobernantza',
        access_accountability: 'Sarbide-erantzukizuna',
        localization: 'Lokalizazioa',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Bazkideen lan-eremua',
        privacy_scanner: 'Pribatutasun-eskánerra',
        onboarding: 'Sartzea',
        compliance_forms: 'Betetze-inprimakiak',
        audit_trail: 'Ikuskaritza-aztarna',
        data_subjects: 'Datuen subjektuak',
        organizations: 'Erakundeak',
        task_management: 'Zereginen kudeaketa',
        deletion_orch: 'Ezabaketa-orkestrazioa',
        forms: 'Betetze-formularioak',
        partner_marketplace: 'Bazkideen merkatua',
        dashboard: 'Aginte-mahaia',
        platform_settings: 'Plataformaren ezarpenak',
        help_center: 'Laguntza-zentroa',
    },
    module_descriptions: {
        rights_requests: 'Kudeatu datuen titularren eskubide-eskaerak, sarbidea, ezabatzea eta eramangarritasuna barne. Jarraitu eskaeren bizi-zikloa, esleitu kudeatzaileak eta bete arauzko epeak.',
        data_mapping: 'Mapatu datu-fluxuak zure erakundean zehar. Jarraitu tratamendu-jarduerak, datu-kategoriak, helburuak, atxikipen-politikak eta sistema-integrazioak.',
        governance: 'Gainbegiratzea eta hori sostengatzen duten frogak. Kudeatu hornitzaileen erregistroa eta haren bizi-zikloa, onartu modulu honek kontrolatzen dituen erabakiak, zaindu betetze-desbideratzeak, eta mantendu konfiantzaren eta identitatearen, arau-artefaktuen, ordainketen eta eKYCren, eta plataformaren frogen erregistroak.',
        consent: 'Kudeatu adostasunaren bilketa, biltegiratzea eta erretiratzea. Jarraitu adostasun-erregistroak, helburuak eta adostasun-kudeaketa plataformekin integrazioa.',
        consent_properties: 'Konfiguratu adostasun-propietateak, taldeak eta urrezko txantiloiak. Definitu berrerabilgarriak diren adostasun-konfigurazioak hedapen koherentea bermatzeko.',
        assessments: 'Burutu Datuen Babesaren Eragin-Ebaluazioak (DBEE) eta pribatutasun-arriskuen ebaluazioak. Ebaluatu tratamendu-jarduerak betetze-arriskuetarako.',
        classification: 'Sailkatu datu-aktiboak sentikortasun-mailaren arabera. Aplikatu taxonomia-etiketak, jarraitu bizi-zikloko erregistroak eta identifikatu sailkapen-hutsuneak.',
        monitoring: 'Ezarri jarraipen-programak betetze-neurriak, arrisku-adierazle nagusiak eta kontrolen eraginkortasuna denborarekin jarraitzeko.',
        incidents: 'Eman abisuak eta kudeatu pribatutasun-gertakariak eta datu-urraketak. Jarraitu ikerketa, jakinarazpen eta konponketa lan-fluxuak.',
        connectors: 'Konfiguratu datu-iturrien konektoreak eta integrazioak. Konektatu kanpo-sistemak datu-aurkikuntza eta jarraipen automatikorako.',
        vendor_risk: 'Ebaluatu eta jarraitu hirugarrenen hornitzaileen arriskuak. Puntuatu hornitzaileak, jarraitu arriskuen arintzea eta kudeatu hornitzaileen arrisku-konfigurazioak.',
        deletion_orchestration: 'Orkestratu datuen ezabatzea sistemen artean. Jarraitu ezabatze-eskaerak, egiaztatu froga-kateak eta bermatu datuen erabateko ezabatzea.',
        discovery: 'Aurkitu datu pertsonalak konektatutako sistemetan. Eskaneatu datu-iturriak datu-inbentario integral bat eraikitzeko.',
        incident_ops: 'Gertakarien kudeaketa operatiboa jokabide-gidalerroekin, eskalazio lan-fluxuekin eta SLA jarraipenarekin gertakarien konponketarako.',
        program_governance: 'Jarraitu pribatutasun-programaren heldutasun orokorra, betetze-puntuazioak, ebidentzia-hutsuneak eta zuzendaritzarako txostenak zure erakunde osoan.',
        access_accountability: 'Ikertu eta auditatu erabiltzaileen sarbide-ereduak. Jarraitu sarbide-konfigurazioak, gainbegiratu jarduera susmagarria eta bermatu erantzukizuna.',
        localization: 'Instalatu eta kudeatu merkatu-lokalizazio paketeak. Gehitu arauzko esparruak, hizkuntza-paketeak eta eskualdeari lotutako betetze-edukia.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Lankidetzan aritu kanpoko bazkideekin. Partekatu betetze-datuak, jarraitu bazkideen betebeharrak eta kudeatu bazkide-harremanak.',
        privacy_scanner: 'Eskaneatu webguneak pribatutasun-betetzearen arazoetarako. Gainbegiratu cookie-erabilera, jarraitzaile-scriptak eta pribatutasun-politikaren presentzia denbora errealean.',
        onboarding: 'Gidatu erakunde berriak plataformaren konfigurazioan. Urratsez urratseko hasierako konfigurazio eta datu-inportaziorako laguntzailea.',
        compliance_forms: 'Sortu eta kudeatu betetze-inprimakiak txantiloi-katalogo batetik. Bete arauzko inprimakiak, jarraitu bidalketak eta sortu txostenak.',
        audit_trail: 'Ikusi sistemako ekintza guztien ikuskaritza-aztarna osoa. Jarraitu nork zer egin zuen, noiz eta non modulu guztietan.',
        organizations: 'Kudeatu erakundeak eta maizter anitzeko ezarpenak. Konfiguratu erakundeen profilak, hierarkiak eta erakunde arteko harremanak.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Azaldu anomaliak',
            explaining: 'Azaltzen…',
            results_heading: 'Anormaltasunaren azalpenak ({severity})',
            tokens_label: '≈ {count} token',
            items_label: '{count} elementu',
            remediation_label: 'Konponketa-urratsak',
            items_detail_label: 'Eragindako elementuak',
            accept_label: 'Onartu azalpena',
            reject_label: 'Azalpena baztertu',
            error_generic: 'Ezin izan dira anomaliak azaldu. Saiatu berriro edo egiaztatu eskaneatzea behar bezala burutu dela.',
        },
        cookie_insight: {
            insight_label: 'Cookie eta jarraipenaren AI ikuspegia',
            producing: 'Ikuspegia sortzen...',
            results_heading: 'Cookie eta jarraitzaileen ikuspegia ({severity})',
            tokens_label: '≈ {count} token',
            items_label: '{count} elementu',
            classification_label: 'Sailkapena',
            severity_label: 'Larritasuna',
            banner_copy_label: 'Iradokitako pankartaren kopia',
            retention_label: 'Atxikitzeko aholkuak',
            citations_label: 'Aipamenak',
            kind_cookie: 'Cookie',
            kind_tracker: 'Jarraitzailea',
            class_necessary: 'Beharrezkoa',
            class_analytics: 'Analitika',
            class_marketing: 'Marketina',
            class_social: 'Sozialak',
            accept_label: 'Onartu ikuspegia',
            reject_label: 'Baztertu ikuspegia',
            no_items: 'Ez da cookierik edo jarraitzailerik hauteman bilaketa honetan.',
            error_generic: 'Ezin izan da cookieen estatistikak sortu. Saiatu berriro edo egiaztatu eskaneatzea behar bezala burutu dela.',
        },
        monitoring: {
            title: 'Pribatutasun-jarraipena',
            new_scan: 'Eskaneo berria',
            scan_url: 'Eskaneatzeko URLa',
            scan_frequency: 'Eskaneo-maiztasuna',
            last_scan: 'Azken eskaneoa',
            score: 'Pribatutasun-puntuazioa',
            issues: 'Aurkitutako arazoak',
            history: 'Eskaneo-historia',
            settings: 'Jarraipen-ezarpenak',
        },
        real_time: {
            title: 'Denbora errealeko eskanerra',
            scan_now: 'Eskaneatu orain',
            scanning: 'Eskaneatzen...',
            results: 'Eskaneo-emaitzak',
            no_results: 'Ez dago eskaneo-emaitzarik oraindik.',
            cookies: 'Cookieak',
            trackers: 'Jarraitzaileak',
            consent_banners: 'Adostasun-pankartak',
            third_party: 'Hirugarrenen scriptak',
            archive: 'Emaitzen artxiboa',
            settings: 'Eskaneraren ezarpenak',
        },
        dashboard: {
            page_title: 'Pribatutasuna kontrolatzeko panela',
            privacy_monitoring: 'Pribatutasunaren jarraipena',
            plan_label: '{package} plana',
            expires_label: '{date} iraungitzen da',
            settings: 'Ezarpenak',
            refresh: 'Freskatu',
            refreshing_dashboard: 'Jarraipen-panela freskagarria',
            refresh_aria: 'Freskatu jarraipen-panela',
            not_configured_title: 'Pribatutasun-kontrola ez dago konfiguratuta',
            not_configured_desc: 'Konektatu zure AesirX Pribatutasun-zaintza-zerbitzua webguneak betetzen duen jarraipena egiten hasteko.',
            configure_monitoring: 'Konfiguratu Jarraipena',
            license_expired_title: 'Lizentzia iraungita',
            license_expired_desc: 'Zure AesirX Pribatutasuna kontrolatzeko lizentzia iraungi da. Mesedez, berritu zerbitzua etenik ez izateko.',
            compliance_score: 'Betetze puntuazioa',
            cookies_detected: 'Cookieak detektatu dira',
            trackers_label: 'Jarraitzaileak',
            open_issues: 'Arazo irekiak',
            score_good: 'Ona',
            score_needs_improvement: 'Hobekuntza behar du',
            score_critical: 'Kritikoa',
            action_required: 'Ekintza beharrezkoa',
            cookies_sr: 'cookieak detektatu dira',
            trackers_sr: 'jarraitzaileak aurkitu dira',
            open_issues_sr: 'gai irekiak',
            action_required_sr: 'beharrezko ekintza',
            metric_label: 'Metrikoa',
            value_label: 'Balioa',
            status_label: 'Egoera',
            aria_metrics: 'Pribatutasuna kontrolatzeko neurketak',
            plan_limit_warning: 'URL eskaneatzen: {current}/{max} — zure planaren mugara iritsi zara.',
            manage_domains: 'Kudeatu domeinuak edo berritu zure plana.',
            monitored_domains: 'Monitorizatutako domeinuak',
            domain_added: '{domain} domeinua gehitu da',
            domain_removed: '{domain} domeinua kendu da',
            failed_add_domain: 'Ezin izan da gehitu domeinua',
            failed_remove_domain: 'Ezin izan da kendu domeinua',
            failed_load: 'Ezin izan da kargatu monitorizazio-panela',
            new_domain_aria: 'URL domeinu berria',
            add_domain: 'Gehitu domeinua',
            adding_domain: 'Gehitzen…',
            no_domains: 'Ez dago oraindik kontrolatutako domeinurik konfiguratuta.',
            aria_domains_table: 'Monitorizatutako domeinuak',
            col_domain: 'Domeinua',
            col_compliance_score: 'Betetze puntuazioa',
            col_status: 'Egoera',
            col_cookies: 'Cookieak',
            col_trackers: 'Jarraitzaileak',
            col_issues: 'Gaiak',
            col_last_scan: 'Azken eskaneatzea',
            col_action: 'Ekintza',
            compliance_status_sr: 'betetze-egoera',
            showing_chart: 'Diagrama-ikuspegia erakusten',
            showing_table: 'Datu-taularen ikuspegia erakusten',
            loading_sr: 'Jarraipen-panela kargatzen',
            col_framework_tags: 'Marko Etiketak',
            col_last_monitored: 'Azken jarraipena',
            framework_tags_sr: 'aplikagarriak diren arau-esparruak',
            last_monitored_sr: 'etengabeko jarraipenaren denbora-zigilua',
            audit_trail_heading: 'Ikuskaritza Pista',
            audit_trail_info: 'Jarraipen-eskaneatze bakoitza denbora-zigilua eta erregistratuta dago. Domeinu-mailako betetze-aldaketek ikuskaritza-erregistro aldaezina sortzen dute, araudiaren berrikuspenerako esporta daitekeena.',
            cybersecurity_law_info_heading: 'Zibersegurtasun Legearen detekzioa',
            cybersecurity_law_info: 'Monitorizatutako domeinuak automatikoki egiaztatzen dira zibersegurtasun legearen eskakizunekin (adibidez, Vietnamgo Zibersegurtasun Legea, NIS2 Zuzentaraua). Esparru-etiketek zein araudi aplikatzen diren adierazten dute. Arazoak araudi-esparruaren arabera sailkatuta daude zuzendutako konponketarako.',
        },
        scan: {
            page_title: 'Denbora errealeko eskaneatzea',
            title: 'Denbora errealeko pribatutasun eskanerra',
            settings: 'Ezarpenak',
            results_archive: 'Emaitzen artxiboa',
            launch_scan: 'Abiarazi Scan',
            target_url: 'Helburua URL',
            protocol_aria: 'Protokoloa',
            url_required: 'URL beharrezkoa da',
            url_whitespace: 'Mesedez, kendu zuriuneak URL-tik',
            url_invalid: 'Mesedez, sartu baliozko domeinu bat (adibidez, example.com)',
            add_subpages: 'Gehitu azpiorriak',
            add_subpages_aria: 'Gehitu azpiorriak, {count} gehitu',
            start_scan: 'Hasi eskaneatzea',
            launching: 'Abiarazten…',
            scan_launched: 'Eskaneatzea arrakastaz abiarazi da',
            scan_failed: 'Ezin izan da eskaneatzea abiarazi',
            scan_progress: 'Eskaneatu Aurrerapena',
            scanning_in_progress: 'Eskaneatzen ari da…',
            scan_id: 'Eskaneatu IDa',
            status_label: 'Egoera',
            status_unknown: 'unknown',
            progress_label: 'Aurrerapena',
            pages_label: 'Orriak',
            refresh_status: 'Freskatu egoera',
            new_scan: 'Eskaneatu berria',
            scan_completed: 'Eskaneatzea amaitu da, emaitzak eskuragarri',
            failed_check_status: 'Ezin izan da egiaztatu eskaneatu egoera',
            scan_results: 'Eskaneatu emaitzak',
            main_page: 'Orri Nagusia',
            page_n: '{n} orrialdea',
            result_pages_nav: 'Emaitza orriak',
            page_url: 'URL orrialdea',
            risk_score: 'Arrisku puntuazioa',
            risk_score_sr: 'arrisku puntuazioa',
            cookies_label: 'Cookieak',
            cookies_sr: 'cookieak detektatu dira',
            scripts_label: 'Gidoiak',
            scripts_sr: 'gidoiak detektatu dira',
            issues_label: 'Gaiak',
            issues_sr: 'aurkitutako gaiak',
            issues_found: 'Aurkitutako arazoak',
            aria_issues_table: 'Eskaneatzean aurkitutako arazoak',
            col_severity: 'Larritasuna',
            col_category: 'Kategoria',
            col_title: 'Izenburua',
            col_remediation: 'Erremediazioa',
            severity_sr: 'severity',
            scan_result_metrics: 'Eskaneatu emaitzen neurketak',
            view_full_detail: 'Ikusi xehetasun osoa',
            col_consent_category: 'Adostasun Kategoria',
            consent_category_sr: 'adostasun kategorizazioa',
            compliance_evidence_heading: 'Betetze-frogak',
            compliance_evidence_info: 'Eskaneen aurkikuntzak betetze-kasuekin lotzen dira automatikoki. Arazo bakoitza adostasun-kategoria batera daiteke (funtsezkoa, funtzionala, analitikoa, marketina) eta ikuskaritzarako prest dauden frogak sortzen ditu araudia berrikusteko.',
            rule_config_heading: 'Arauen konfigurazioa',
            rule_config_info: 'Eskaneatzeko arauak arau-esparruaren arabera konfigura daitezke. Cookieen egiaztapenak, script-en detekzioa eta saltzaileen analisia gaitu edo desgaitu daitezke Ezarpenen bidez. Arau pertsonalizatuak defini daitezke zure erakundearen betetze-eskakizunekin bat egiteko.',
            case_linkage_heading: 'Betetze kasuen lotura',
            case_linkage_info: 'Denbora errealeko analisietan aurkitutako arazoak betetze-kasuekin lotu daitezke jarraipena egiteko eta konpontzeko. Nabigatu Eskanearen emaitzaren xehetasuna orrira kasuen elkarteak ikusteko.',
            subpages_modal_title: 'Gehitu azpiorriak',
            subpages_modal_desc: 'Gehitu 5 azpi-orrien URL gehienez domeinu nagusiarekin batera eskaneatzeko.',
            subpage_n: '{n} azpi-orria',
        },
        result_detail: {
            page_title: 'Eskanearen emaitzaren xehetasuna',
            title: 'Eskanearen emaitza',
            back_to_archive: 'Itzuli artxibora',
            failed_load: 'Ezin izan da eskanearen emaitza kargatu',
            loading_sr: 'Eskanearen emaitza kargatzen…',
            scan_id: 'Eskaneatu IDa',
            target_url: 'Helburua URL',
            status_label: 'Egoera',
            status_sr: 'status',
            started: 'Hasi da',
            completed: 'Osatua',
            pages_scanned: 'Eskaneatutako orriak',
            https_label: 'HTTPS',
            highest_risk_score: 'Arrisku-puntuazio altuena',
            most_cookies: 'Cookie gehienak',
            most_vendors: 'Hirugarrenen saltzaile gehienak',
            most_issues: 'Gai gehienak',
            across_pages: 'orrialdeetan zehar',
            highest_values_aria: 'Orrialde guztietan baliorik altuenak',
            scanned_pages_nav: 'Eskaneatutako orriak',
            main_page: 'Orri Nagusia',
            page_n: '{n} orrialdea',
            page_url: 'URL orrialdea',
            risk_score: 'Arrisku puntuazioa',
            overall_risk_sr: 'arriskuaren puntuazio orokorra',
            cookies_label: 'Cookieak',
            cookies_sr: 'cookieak detektatu dira',
            vendors_label: 'Hirugarrenen saltzaileak',
            vendors_sr: 'hirugarrenen saltzaileak detektatu dira',
            issues_label: 'Gaiak',
            issues_sr: 'aurkitutako gaiak',
            risk_breakdown_aria: 'Arrisku puntuazioaren matxura',
            issues_found: 'Aurkitutako arazoak',
            aria_issues_table: 'Eskaneatzean aurkitutako arazoak',
            col_severity: 'Larritasuna',
            col_category: 'Kategoria',
            col_title: 'Izenburua',
            col_remediation: 'Erremediazioa',
            severity_sr: 'severity',
            cookies_heading: 'Cookieak ({count})',
            aria_cookies_table: 'Cookieak detektatu dira',
            col_name: 'Izena',
            col_domain: 'Domeinua',
            col_path: 'Bidea',
            col_secure: 'Segurua',
            col_httponly: 'HttpBakarrik',
            col_samesite: 'Webgune Bera',
            vendors_heading: 'Hirugarrenen saltzaileak ({count})',
            aria_vendors_table: 'Hirugarrenen saltzaileak detektatu dira',
            col_hostname: 'Ostalari izena',
            scripts_heading: 'Gidoiak/Beacons ({count})',
            aria_scripts_table: 'Scriptak eta balizak detektatu dira',
            col_url: 'URL',
            col_type: 'Mota',
            links_heading: 'Estekak',
            first_party: 'Lehena',
            third_party: 'Hirugarrena',
            col_risk_category: 'Arrisku Kategoria',
            col_regulatory_framework: 'Arau-esparrua',
            col_actionable_detail: 'Ekingarria den xehetasuna',
            risk_category_sr: 'arauzko arrisku-kategoria',
            risk_category_mapping_heading: 'Arrisku-kategorien mapaketa',
            risk_category_mapping_info: 'Eskaneatutako aurkikuntza bakoitza arauzko arrisku-kategoria batean kokatzen da (datuen bilketa, mugaz gaindiko transferentzia, baimenaren kudeaketa, datuak gordetzea, segurtasun kontrolak). Mapa honek araudiaren eraginaren arabera lehenetsitako zuzenketa zuzendua ahalbidetzen du.',
            compliance_case_heading: 'Betetze Kasuen Elkartea',
            compliance_case_info: 'Eskaneatze-emaitzak betetze-kasuekin lotuta daude muturreko jarraipena egiteko. Arazoak eskaneatu emaitzetatik zuzenean eskaneatu daitezke betetze kasuen lan-fluxura.',
        },
        results_archive: {
            page_title: 'Eskaneatu emaitzen artxiboa',
            title: 'Eskaneatu emaitzen artxiboa',
            back_to_scanner: 'Itzuli eskanerra',
            failed_load: 'Ezin izan dira eskaneatu emaitzak kargatu',
            results_loaded: 'Eskanearen emaitzak kargatu dira, {count} erregistro',
            total_label: 'Guztira: {count}',
            aria_table: 'Eskaneatu emaitzen artxiboa',
            col_scan_date: 'Eskaneatu data',
            col_target_url: 'Helburua URL',
            col_duration: 'Iraupena',
            col_issues_found: 'Aurkitutako arazoak',
            col_risk_score: 'Arrisku puntuazioa',
            col_status: 'Egoera',
            col_actions: 'Ekintzak',
            scan_status_sr: 'eskaneatu egoera',
            view_result: 'Ikusi',
            no_results: 'Ez da bilaketa-emaitzarik aurkitu.',
            loading_sr: 'Eskaneatu emaitzak kargatzen',
            pagination_aria: 'Eskaneatu emaitzen orria',
            col_risk_category: 'Arrisku Kategoria',
            risk_category_sr: 'arauzko arrisku-kategoria',
            archive_retention_heading: 'Artxiboen atxikipena',
            archive_retention_info: 'Eskanearen emaitzak zure erakundearen datuak gordetzeko gidalerroan zehaztutako denboran gordetzen dira. Artxibatutako emaitzek azterketa-testuinguru osoa barne hartzen dute (esparruko etiketak, arrisku-kategoriak, konponketa-gidak) eta CSV gisa esporta daitezke arauzko frogak lortzeko.',
            export_context_info: 'Esportatutako CSV fitxategiek arau-testuingurua dute: arrisku-kategoria, aplikagarriak diren esparruak eta aurkikuntza bakoitzerako ekintzak konpontzeko xehetasunak.',
        },
        scanner_settings: {
            page_title: 'Eskanerraren ezarpenak',
            title: 'Eskanerraren ezarpenak',
            back_to_scanner: 'Itzuli eskanerra',
            failed_load: 'Ezin izan da eskanerraren konfigurazioa kargatu',
            endpoint_url: 'Amaiera-puntua URL',
            save_success: 'Eskanerraren konfigurazioa behar bezala gorde da.',
            save_failed: 'Ezin izan da gorde eskanerraren konfigurazioa',
            test_connection: 'Proba konexioa',
            testing: 'Proba egiten...',
            saving: 'Gordetzen...',
            loading_sr: 'Eskanerraren ezarpenak kargatzen',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Pribatutasuna kontrolatzeko ezarpenak',
            title: 'Jarraipen ezarpenak',
            back_to_dashboard: 'Itzuli Arbelera',
            failed_load: 'Ezin izan da kargatu jarraipenaren konfigurazioa',
            credentials_heading: 'AesirX Kredentzialak',
            connected_as: '{username} gisa konektatuta',
            credentials_desc: 'Sartu zure AesirX kontuaren kredentzialak Pribatutasunaren monitorizazioa konektatzeko. Bilatu zure Kidearen IDa zure AesirX kontuko profil orrian.',
            client_id: 'Bezeroaren IDa',
            client_secret: 'Bezeroaren sekretua',
            username: 'AesirX Erabiltzaile-izena (posta elektronikoa)',
            password: 'AesirX Pasahitza',
            placeholder_keep: '(utzi hutsik existitzen jarraitzeko)',
            credential_form_aria: 'AesirX kredentzial-inprimakia',
            test_connection: 'Proba konexioa',
            testing: 'Proba egiten…',
            test_aria: 'Probatu AesirX konexioa',
            testing_aria: 'Konexioa probatzen',
            save_configuration: 'Gorde konfigurazioa',
            saving: 'Gordetzen…',
            save_aria: 'Gorde kredentzialak',
            saving_aria: 'Kredentzialak gordetzen',
            credentials_saved: 'AesirX kredentzialak behar bezala gorde dira.',
            credentials_saving: 'AesirX kredentzialak gorde dira. Konfigurazioa berriro kargatzen…',
            credentials_save_failed: 'Ezin izan dira gorde kredentzialak',
            client_id_required: 'Bezeroaren IDa eta erabiltzaile-izena beharrezkoak dira.',
            all_fields_required: 'Kredentzial-eremu guztiak beharrezkoak dira.',
            email_heading: 'Posta elektroniko bidezko jakinarazpenak',
            receive_emails: 'Jaso eskaneatzeko txostenen mezu elektronikoak',
            email_toggle_aria: 'Aldatu eskaneatu txostenen mezu elektronikoak',
            only_on_change: 'Arrisku-egoera aldatzen denean soilik posta elektroniko bidez',
            only_on_change_aria: 'Aldatu posta elektronikoa arrisku-egoeraren aldaketan soilik',
            emails_enabled: 'Eskaneatu txostenen mezu elektronikoak gaituta',
            emails_disabled: 'Eskaneatu txostenen mezu elektronikoak desgaituta daude',
            only_change_on: 'Arrisku aldaketari buruzko posta elektronikoa soilik bidaltzea',
            only_change_off: 'Eskaneatu bakoitzean posta elektronikoa bidaltzea',
            email_failed: 'Ezin izan da eguneratu posta elektronikoaren ezarpena',
            email_settings_aria: 'Posta elektronikoaren jakinarazpenaren ezarpenak',
            loading_sr: 'Jarraipen ezarpenak kargatzen',
        },
        entity_types: {
            user: 'Erabiltzailea',
            case: 'Kasua',
            incident: 'Gorabehera',
            assessment: 'Ebaluazioa',
            vendor: 'Saltzailea',
            consent: 'Adostasuna',
            policy: 'Politika',
            data_map: 'Datuen mapa',
        },
        actions: {
            create: 'Sortu',
            update: 'Eguneratu',
            delete: 'Ezabatu',
            view: 'Ikusi',
            export: 'Esportatu',
            status_change: 'Egoera-aldaketa',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Formularioen kontrol-panela',
            total_templates: 'Txantiloi guztira',
            installed: 'Instalatua',
            available: 'Erabilgarri',
            by_market: 'Merkatuaren arabera',
            back_dashboard: 'Atzera to Aginte-panel',
            browse_templates: 'Browse Txantiloiak',
            catalog_coverage: 'Catalog Estaldura',
            quick_start: 'Quick Hasi',
            recent_instances: 'Azken instantziak',
            total_instances: 'Instances guztira',
            fetch_failed: 'Huts egin du: fetch dashboard datu. Saiatu berriro.',
            no_instances: 'Ez da instances found. aurkitu',
            form_metrics: 'Inprimaki-metriken ikuspegi orokorra',
            templates_total: 'txantiloi guztira',
            instances_total: 'instantzia guztira',
            installed_of_available: '{installed}/{available} instalatuta',
            templates_in_category: 'txantiloiak {category} kategorian',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Formulario-txantiloiak',
            new: 'Txantiloi berria',
            edit: 'Editatu txantiloia',
            name: 'Txantiloiaren izena',
            form_type: 'Formulario mota',
            market: 'Merkatua',
            language: 'Hizkuntza',
            schema: 'Formularioaren eskema',
            preview: 'Aurrebista',
            duplicate: 'Bikoiztu txantiloia',
            grid_view: 'Grid Ikusi',
            list_view: 'List Ikusi',
            fetch_failed: 'Huts egin du: fetch txantiloiak. Saiatu berriro.',
            filter_templates: 'Iragazi txantiloiak',
            version_history: 'Bertsio-historia',
            no_version_history: 'Bertsio-historiarik ez — hau hasierako bertsioa da.',
            form_id: 'Inprimakiaren IDa',
            source_instrument: 'Jatorri-tresna',
            issuer: 'Erakuslea',
            all_frameworks: 'Esparru guztiak',
            view_mode: 'Ikuspegi-modua',
            classification: 'Sailkapena',
        },
        catalog: {
            title: 'Formulario-katalogoa',
            install: 'Instalatu',
            uninstall: 'Desinstalatu',
            open_form: 'Open form',
            install_all: 'Instalatu guztiak',
            installed_tab: 'Instalatua',
            catalog_tab: 'Katalogoa',
            search_placeholder: 'Bilatu txantiloiak...',
            no_templates: 'Ez da txantiloirik aurkitu.',
            template_count: '{count} txantiloi',
            all_markets: 'Merkatu guztiak',
            sections: 'Atalak',
            all_categories: 'Kategoria guztiak',
            category: 'Kategoria',
            category_install_failed: 'Huts egin du: install category txantiloiak. Saiatu berriro.',
            category_install_success: 'Kategoria txantiloiak installed.',
            fetch_failed: 'Huts egin du: fetch txantiloiak. Saiatu berriro.',
            fetch_installed_failed: 'Huts egin du: fetch installed txantiloiak. Saiatu berriro.',
            fields: 'Eremuak',
            install_failed: 'Huts egin du: install template. Saiatu berriro.',
            install_success: 'Txantiloi installed successfully.',
            installing: 'Instalatzen...',
            removing: 'Kentzen...',
            uninstall_confirm: 'Ziur zaude uninstall this template?',
            uninstall_failed: 'Huts egin du: uninstall template. Saiatu berriro.',
            uninstall_success: 'Txantiloi uninstalled successfully.',
            installation_summary: 'Instalazioaren laburpena',
            filter_catalog: 'Iragazi katalogoa',
            all_frameworks: 'Esparru guztiak',
            templates_installed_count: '{count} txantiloi(k) behar bezala instalatu dira',
            all_already_installed: 'Kategoria honetako txantiloi guztiak instalatuta daude',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Inprimakiaren IDa',
            issuer: 'Erakuslea',
            catalog_tabs: 'Inprimaki-katalogoaren fitxak',
        },
        filler: {
            back_templates: 'Atzera to Txantiloiak',
            no_sections: 'Ez da sections found. aurkitu',
            draft_created: 'Zirriborro created.',
            draft_saved: 'Zirriborro saved.',
            fetch_template_failed: 'Huts egin du: fetch template. Saiatu berriro.',
            save_draft: 'Gorde Zirriborro',
            save_draft_failed: 'Huts egin du: save draft. Saiatu berriro.',
            saving: 'Gordetzen...',
            select_option: 'Hautatu an option',
            submit: 'Bidali',
            submit_failed: 'Huts egin du: submit. Saiatu berriro.',
            submitting: 'Bidaltzen...',
            title: 'Formulario-laguntzailea',
            form_id_label: 'Inprimakiaren IDa',
            schema_label: 'Eskema',
            form_number_label: 'Form',
            framework_label: 'Esparrua',
            issuer_label: 'Erakuslea',
            source_label: 'Iturria',
            market_label: 'Merkatua',
            section_of: '{current}. atala {total}(e)tik',
            form_navigation: 'Inprimaki-nabigazioa',
            form_progress: 'Inprimakiaren aurrerapena: {current}. atala {total}(e)tik',
            removed_item: '{index} elementua {field} eremutik kendua',
            added_item: 'Elementu berria gehitu da {field} eremuan',
            go_to_previous: 'Joan aurreko atalera, {current}. atala {total}(e)tik',
            go_to_next: 'Joan hurrengo atalera, {current}. atala {total}(e)tik',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Aurkeztze-egoera',
            advance: 'Aurkeztze-egoera aurreratu...',
            not_started: 'Hasi gabe',
            drafting: 'Zirriborroa',
            internal_review: 'Barne-berrikusketa',
            approved_for_submission: 'Aurkezteko onartua',
            submitted_to_authority: 'Agintaritzari aurkeztua',
            supplement_requested: 'Osagarria eskatua',
            supplement_in_progress: 'Osagarria abian',
            resubmitted: 'Berriro aurkeztua',
            accepted: 'Onartua',
            rejected: 'Baztertua',
            closed: 'Itxita',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Onartua honek',
            form_answers: 'Formulario-erantzunak',
            column_header: 'Form',
            no_answers: 'Ez da answers found. aurkitu',
            view_outputs: 'Ikusi irteerak',
            approve: 'Onartu',
            approve_failed: 'Huts egin du: approve. Saiatu berriro.',
            approving: 'Onartzen...',
            detail_title: 'Instance Xehetasuna',
            fetch_failed: 'Huts egin du: fetch instances. Saiatu berriro.',
            generate_failed: 'Huts egin du: generate output. Saiatu berriro.',
            generate_output: 'Sortu Irteera',
            generating: 'Sortzen...',
            submitted_at: 'Bidalita',
            instance_metadata: 'Inprimaki-instantziaren metadatuak',
            filing_status_steps: 'Artxibatze-egoeraren urratsak',
            current_status: 'egungo egoera',
            filing_status_update_failed: 'Aurkezpenaren egoera eguneratzeak huts egin du',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Atzera to Instance',
            no_outputs_msg: 'Ez da irteerarik sortu oraindik.',
            no_outputs_title: 'No Irteerak',
            output_name: 'Irteera Izena',
            download: 'Deskargatu',
            download_failed: 'Huts egin du: download. Saiatu berriro.',
            fetch_failed: 'Huts egin du: fetch outputs. Saiatu berriro.',
            format: 'Formatua',
            generated_at: 'Sortua',
            generated_by: 'Sortzailea',
            size: 'Tamaina',
            title: 'Form Irteerak',
            print: 'Inprimatu',
            print_outputs: 'Inprimatu inprimakiaren irteerak',
            printing: 'Inprimakiaren irteerak inprimatzen',
            pdf_preview: 'PDF aurrebista',
            downloading: '{name} deskargatzen',
            preview_in_new_window: '{name} ({format}) aurreikusi leiho berrian',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Irismena eta aplikagarritasuna',
            risk: 'Arrisku-ebaluazioa eta kontrol-mapaketa',
            policy: 'Erakundeko segurtasun-politikak',
            localization: 'Datuen lokalizazioa eta tokiko presentzia',
            vendor: 'Hirugarrenak eta hornidura-katea',
            authority: 'Agintaritza-eskaeren kudeaketa',
            incident: 'Gertakarien erantzuna eta ebidentziak',
            training: 'Prestakuntza eta prest egotea',
        },
        market_finalization_domains: {
            data_law: 'Datu Legea — Gobernantza eta Kontrol Operatiboak',
            pdpl_2025: 'PDPL 2025 — Pribatutasun Gobernantza eta Datuen Subjektuen Eskubideak',
            decree_356: '356/2025 Dekretua — Hornitzaileen eta Mugaz Gaindiko Kontrolak',
            telecom: 'Telekomunikazio Legea — Konfidentzialtasuna eta Agintaritza Eskaerak',
        },
        nav: {
            filing_case_links: 'Artxibatze-kasuaren estekak',
            section_routing: 'Atal-bideratzea',
            submission_packages: 'Aurkezpen-paketeak',
        },
        filing_case_links: {
            title: 'Artxibatze-kasuaren estekak',
            link_form: 'Formularioa artxibatze-kasuari lotu',
            filing_case_type: 'Artxibatze-kasu mota',
            form_instance: 'Formulario-instantzia',
            select_form_instance: 'Formulario-instantzia bat hautatu...',
            select_filing_case: 'Artxibatze-kasu bat hautatu...',
            no_links_found: 'Ez da artxibatze-kasuaren estekarik aurkitu.',
            linked: 'Lotuta',
            filing_case_id: 'Artxibatze-kasuaren IDa',
            link_created: 'Artxibatze-kasuaren esteka sortua',
            link_deleted: 'Artxibatze-kasuaren esteka ezabatua',
            aria_delete_link: 'Ezabatu esteka {name}',
            case_type_dpia_dossier: 'DBEI dosierra',
            case_type_cross_border_filing: 'Mugaz gaindiko artxibatzea',
            case_type_breach_notification: 'Urraketa-jakinarazpena',
            no_instances_guidance: 'Oraindik ez dago formulario-instantziarik. Sortu bat Formulario-txantiloietatik.',
            no_filings_guidance: 'Oraindik ez dago artxibatze-kasurik. Sortu bat Ebaluazioetatik.',
        },
        section_routing: {
            title: 'Atal-bideratzea',
            assign_section: 'Atala esleitu',
            department: 'Saila',
            select_department: 'Sail bat hautatu...',
            assignee: 'Esleitua',
            select_assignee: 'Esleitu bat hautatu...',
            section: 'Atala',
            select_section: 'Atal bat hautatu...',
            select_form_instance: 'Formulario-instantzia bat hautatu...',
            no_assignments_found: 'Ez da atal-esleipenik aurkitu.',
            select_instance_prompt: 'Formulario-instantzia bat hautatu atal-esleipenak ikusteko.',
            complete_section: 'Atala osatu',
            status_pending: 'Zain',
            status_in_progress: 'Abian',
            status_completed: 'Osatuta',
            status_reviewed: 'Berrikusia',
            form_instance_id: 'Inprimaki-instantziaren IDa',
            assignment_created: 'Atal-esleipena sortua',
            assignment_deleted: 'Atal-esleipena ezabatua',
            status_updated: 'Atal-egoera eguneratua',
            aria_delete_assignment: 'Ezabatu esleipena {name}',
            deadline: 'Epemuga',
            deadline_help: 'Aukerakoa. Data honen ondorengo esleipenak atzeratuak gisa agertzen dira.',
            overdue: 'Atzeratua',
            days_remaining: 'egun geratzen dira',
            approval_requested: 'Berrikusketarako bidalita',
            form_approved: 'Formularioa onartua',
            form_rejected: 'Formularioa baztertua',
            no_departments_guidance: 'Ez dago sailik konfiguratuta. Konfiguratu Erakunde-ezarpenetan.',
            no_sections_guidance: 'Ez dago atalik txantiloian definituta. Ziurtatu formulario-txantiloiak atalak dituela.',
            select_instance_first: 'Hautatu lehenik formulario-instantzia bat erabilgarri dauden atalak kargatzeko.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Aurkezpen-paketeak',
            assemble_package: 'Aurkezpen-paketea muntatu',
            dossier: 'Dosier',
            select_dossier: 'Dosier bat hautatu...',
            package_label: 'Paketearen etiketa',
            cover_letter: 'Azaleko gutuna',
            checklist: 'Egiaztapen-zerrenda',
            evidence_index: 'Froga-indizea',
            completeness: 'Osotasuna',
            incomplete_warning: 'Ezin da muntatu: formularioak atal osatugabeak ditu.',
            no_packages_found: 'Ez da aurkezpen-paketerik aurkitu.',
            version: 'Bertsioa',
            package_assembled: 'Bidalketa-paketea osatua',
            package_deleted: 'Aurkezpen-paketea ezabatua',
            aria_delete_package: 'Ezabatu paketea {name}',
            status_draft: 'Zirriborroa',
            status_complete: 'Osatua',
            status_submitted: 'Bidalita',
            status_accepted: 'Onartua',
            status_rejected: 'Baztertua',
            package_updated: 'Aurkezpen-paketea eguneratua',
            document_generated: 'Dokumentua sortua',
            documents: 'Dokumentuak',
            generate: 'Sortu',
            preview: 'Aurrebista',
            download: 'Deskargatu',
            download_all: 'Dena deskargatu',
            no_dossiers_guidance: 'Oraindik ez dago dossierrik. Sortu bat Ebaluazioetatik.',
            package_plan_help: 'Sartu pakete-planean (dokumentuen sorrera automatikoa erabilgarri):',
            cover_letter_help: 'Gutun ofiziala jasotzaile-agintaritzari zuzendua',
            checklist_help: 'Betetze-egiaztapen zerrenda aurkezpen-baldintza guztiak betetzen direla egiaztatzen duena',
            evidence_index_help: 'Aurkezpenean erreferentziatutako froga-dokumentuen aurkibidea',
            completeness_skipped: 'Osotasun-egiaztapena saltatu da — ez dago formulario-instantziarik dossier honekin lotuta.',
            completeness_unavailable: 'Osotasun-egiaztapena ez dago erabilgarri — paketearen muntaketarekin jarraitzen da.',
            duplicate_package_error: 'Aurkezpen-pakete bat jada existitzen da dossier honetarako.',
            doc_cover_letter: 'Aurkezpen-gutuna',
            doc_checklist: 'Egiaztapen-zerrenda',
            doc_evidence_index: 'Froga-aurkibidea',
            export_csv: 'CSV esportatu',
            export_success: 'Artxibatze-kasuen estekak arrakastaz esportatu dira',
            no_assignments_guidance: 'Oraindik ez dago sekzio-esleipenik. Esleitu sekzioak sail eta taldekideei bideratzea hasteko.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Txantiloi bat hautatu',
            hint: 'Aukeratu arau-formularioaren txantiloi bat edo hasi formulario huts batekin.',
            market_filter: 'Merkatuaren arabera iragazi',
            framework_filter: 'Esparruaren arabera iragazi',
            all_markets: 'Merkatu guztiak',
            all_frameworks: 'Esparru guztiak',
            search_placeholder: 'Txantiloiak bilatu...',
            blank_form: 'Formulario hutsa / generikoa',
            blank_form_desc: 'Sortu txantiloirik gabe — bete eremuak eskuz.',
            no_templates: 'Ez da txantiloirik aurkitu iragazki honetarako.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Hautatu',
            link_error: 'Txantiloiaren esteka huts egin du — txantiloia geroago lotu dezakezu Editatzeko ikuspegitik.',
            internal_badge: 'Barne-txantiloia',
            legacy_badge: 'Zaharkitua',
            draft_badge: 'Zirriborroa',
            upcoming_badge: 'Datorren',
            hidden_badge: 'Ezkutatua',
            show_hidden_toggle: 'Erakutsi ezkutuko / zaharkitutako txantiloiak',
            show_hidden_toggle_hint: 'Administratzaile-ikuspegia: internal_only, hidden, legacy edo draft markatutako txantiloiak biltzen ditu.',
        },
        cover_letter_modal: {
            title: 'Aurkezpen-gutuna: aurrebista eta edizioa',
            iframe_title: 'Aurkezpen-gutunaren aurrebista',
            stage_label: 'Faseak',
            stage_initial: 'Lehen bidalketa',
            stage_supplement: 'Osagarri-erantzuna',
            stage_resubmission: 'Berriz bidaltzea',
            subject_label: 'Gaia',
            body_label: 'Gorputz-paragrafoa',
            notes_label: 'Ohar gehigarriak',
            refresh_preview: 'Aurrebista berritu',
            confirm: 'Berretsi eta gorde',
            regenerate: 'Birsortu',
            snapshot_saved: 'Aurkezpen-gutuna gordeta',
        },
        related_panel: {
            title: 'Lotutako arau-formularioak',
            open_form: 'Formularioa bete',
            no_forms: 'Ez da lotutako formulariorik aurkitu.',
            hint_vn: 'VN-ko arau-formulario espezifikoak eskuragarri — hautatu formulario bat betetze-laguntzailea irekitzeko.',
            internal_badge: 'Barnekoa',
            official_badge: 'Ofiziala',
            loading: 'Lotutako formularioak kargatzen...',
            error: 'Ezin izan dira lotutako formularioak kargatu.',
            form_id: 'Formularioaren IDa',
            form_name: 'Formularioaren izena',
            framework: 'Erregulazio-esparrua',
        },
        view_form: 'Inprimakia ikusi',
        edit_form: 'Inprimakia editatu',
        save_draft: 'Zirriborroa gorde',
        module_settings: 'Moduluaren ezarpenak',
        validation_errors: 'Baliozkotze-erroreak zuzendu',
        custom_name_help: 'Aukerakoa — lehenespenez, txantiloiaren izena hutsik uzten bada',
        select_placeholder: 'Hautatu...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Aurrekoa',
        next_section: 'Hurrengoa',
        loading: 'Kargatzen...',
        array_add: 'Gehitu',
        array_remove: 'Kendu',
        array_item_label: '{label} elementua {index}',
        array_add_item: 'Gehitu elementua {label}(e)ra',
        array_remove_item: 'Kendu {index} elementua {label}(e)tik',
        legacy_banner: 'Erregistro hau formulario bateratuak baino lehen sortu zen. Datu batzuk aldatu egin daitezke entitatea eta inprimaki instantzia artean.',
        field_required: '{label} beharrezkoa da',
        section_announcement: '{total}tik {current}. atala: {title}',
        sections_nav_label: 'Inprimaki-atalak',
        no_sections: 'Ez dago txantiloi-atal erabilgarri.',
        section_label_tpl: '{index} atala: {title}',
        field_shown_announcement: '{field} orain agertzen da.',
        field_required_announcement: '{field} orain nahitaezkoa da.',
        submit_blocked_hint_one: 'Nahitaezko eremu 1 erantzun gabe dago oraindik.',
        submit_blocked_hint_many: 'Nahitaezko {count} eremu erantzun gabe daude oraindik.',
        module_settings_auto_applied: 'Moduluaren ezarpen espezifikoak automatikoki aplikatzen dira.',
        operation_failed: 'Eragiketak huts egin du',
        failed_to_load_form: 'Formularioa ezin izan da kargatu',
        failed_to_save_form: 'Formularioa ezin izan da gorde',
        failed_to_submit_form: 'Formularioa ezin izan da bidali',
        failed_to_load_template: 'Txantiloia ezin izan da kargatu',
        failed_to_save_draft: 'Zirriborroa ezin izan da gorde',
        failed_to_create: 'Ezin izan da sortu',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Pakete-katalogoa',
        install: 'Instalatu paketea',
        uninstall: 'Desinstalatu paketea',
        install_batch: 'Instalatu multzoa',
        installed_tab: 'Instalatua',
        catalog_tab: 'Katalogoa',
        pack_details: 'Paketearen xehetasunak',
        frameworks: 'Esparruak',
        requirements: 'Eskakizunak',
        artifacts: 'Artefaktuak',
        language_pack: 'Hizkuntza-paketea',
        dependencies: 'Mendekotasunak',
        no_packs: 'Ez da paketerik aurkitu.',
    },
    platform_pages: {
        users: {
            title: 'Erabiltzaileak',
            new: 'Erabiltzaile berria',
            invite: 'Gonbidatu erabiltzailea',
            edit: 'Editatu erabiltzailea',
            name: 'Izena',
            email: 'Helbide elektronikoa',
            role: 'Rola',
            status: 'Egoera',
            identity_type: 'Identitate mota',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Azken saio-hasiera',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Desaktibatu',
            reactivate: 'Berraktibatu',
            unlock: 'Desblokeatu',
            reset_password: 'Berrezarri pasahitza',
            remove: 'Kendu',
            remove_confirm: '"{name}" kendu? Ekintza hau behin betikoa da eta erabiltzaileak sarbide guztia galduko du. Erregistroa auditoretzarako gordetzen da.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Rolak',
            new: 'Rol berria',
            edit: 'Editatu rola',
            name: 'Rolaren izena',
            description: 'Deskribapena',
            permissions: 'Baimenak',
            users_count: 'Erabiltzaileak',
            detail: 'Rolaren xehetasuna',
            all_tiers: 'Maila guztiak',
            tier: 'Maila',
            type_system: 'Sistema',
            type_custom: 'Pertsonalizatua',
            role_name_placeholder: 'adib. eskualdeko betetze-arduradun',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Nire profila',
            name: 'Izena',
            display_name: 'Bistaratze-izena',
            email: 'Helbide elektronikoa',
            phone: 'Telefonoa',
            department: 'Saila',
            job_title: 'Lanpostu-izena',
            language: 'Hizkuntza',
            role: 'Rola',
            persona: 'Detektatutako pertsonaia',
            last_login: 'Azken saio-hasiera',
            save: 'Aldaketak gorde',
            save_success: 'Profila ondo eguneratu da',
            module_tips_section: 'Modulu-aholkuak',
            tips_toggle: 'Erakutsi araudi-aholkuak moduluetan',
            dismissed_tips: 'Baztertutako aholkuak',
            reset_dismissed: 'Berrezarri baztertutako aholku guztiak',
            notification_prefs: 'Jakinarazpen-hobespenak',
            notification_prefs_link: 'Kudeatu jakinarazpen-hobespenak →',
            change_password: 'Aldatu pasahitza',
            current_password: 'Uneko pasahitza',
            new_password: 'Pasahitz berria',
            confirm_password: 'Berretsi pasahitza',
            accessibility: 'Irisgarritasuna',
            high_contrast: 'Kontraste altuko modua',
            high_contrast_desc: 'Kontrastea handitu irakurgarritasun hoberako. WCAG 2.1 AAA betetzen du.',
            passwords_no_match: 'Pasahitzak ez datoz bat',
            password_changed_logout: 'Pasahitza aldatua. Saioa itxiko da.',
            failed_update: 'Profila eguneratzeak huts egin du',
            failed_change_password: 'Pasahitza aldatzeak huts egin du',
            none: 'Bat ere ez',
            never: 'Inoiz ez',
            password_requirements: 'Gutxienez 12 karaktere, maiuskula, minuskula, zifra, karaktere berezia',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA ezarpenak',
            mfa_enable: 'Gaitu MFA',
            mfa_disable: 'Desgaitu MFA',
            mfa_setup: 'Eskaneatu QR kode hau zure autentifikazio-aplikazioarekin',
            sso_title: 'SSO konfigurazioa',
            sso_provider: 'Hornitzailea',
            sso_client_id: 'Bezero IDa',
            sso_redirect: 'Birbideratze URIa',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API giltzak',
            api_keys_new: 'API giltza berria',
            api_keys_name: 'Giltzaren izena',
            api_keys_last_used: 'Azken erabilera',
            api_keys_expires: 'Iraungitze-data',
            api_keys_revoke: 'Errebokatu',
            service_accounts_title: 'Zerbitzu-kontuak',
            ip_allowlist_title: 'IP baimenduen zerrenda',
            ip_add: 'Gehitu IP helbidea',
            ip_address: 'IP helbidea',
            ip_description: 'Deskribapena',
            delegations_title: 'Delegazioak',
            delegations_new: 'Delegazio berria',
            delegations_from: 'Jatorrizko erabiltzailea',
            delegations_to: 'Helburuko erabiltzailea',
            delegations_expires: 'Iraungitze-data',
            break_glass_title: 'Larrialdi-sarbidea',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Eskatu sarbidea',
            break_glass_reason: 'Sarbidearen arrazoia',
            break_glass_duration: 'Iraupena',
            session_settings_title: 'Saio-ezarpenak',
            eviction_policy_section: 'Kanporatze-politika',
            session_timeout: 'Saioaren iraungitzea (minutuak)',
            session_max_concurrent: 'Gehienezko saio aldi berekoak',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Saio aktiboak',
            active_sessions_device: 'Gailua',
            active_sessions_ip: 'IP helbidea',
            active_sessions_started: 'Hasiera-data',
            active_sessions_revoke: 'Errebokatu saioa',
            active_sessions_confirm_terminate: 'Ziur saio hau amaitu nahi duzula?',
            active_sessions_confirm_terminate_current: 'Hau da zure oraingo saioa. Amaitzen baduzu, berehala amaituko zara. Jarraitu nahi duzu?',
            active_sessions_confirm_terminate_all: 'Ziur gainerako saio guztiak amaitu nahi dituzula? Uneko saioak bakarrik jarraituko du aktibo.',
            active_sessions_terminated: 'Saioa ondo amaitu da.',
            active_sessions_all_terminated: 'Gainerako saio guztiak amaitu dira.',
            active_sessions_terminate_all: 'Amaitu beste saio guztiak',
            active_sessions_last_activity: 'Azken Jarduera',
            active_sessions_current: 'Oraingo saioa',
            active_sessions_revoke_selected: 'Hautatutakoak baliogabetu',
            active_sessions_confirm_terminate_selected: 'Ziur zaude hautatutako saioak amaitu nahi dituzula?',
            active_sessions_selected_terminated: 'Hautatutako saioak ondo amaitu dira.',
            ip_allowlist_desc: 'Kudeatu API atzitzeko baimendutako IP helbideak.',
            ip_no_entries: 'Ez dago IP helbiderik konfiguratuta.',
            session_settings_desc: 'Konfiguratu saioaren denbora-muga eta saio aldibereko mugak.',
            session_settings_save_policy: 'Gorde saioaren politika',
            session_settings_saved: 'Saioaren ezarpenak behar bezala eguneratu dira.',
            session_settings_idle_timeout: 'Inaktiboen denbora-muga (minutuak)',
            session_settings_absolute_timeout: 'Denbora-muga absolutua (minutuak)',
            session_settings_help_idle: 'Jardunaldirik gabeko epe hori igaro ondoren amaituko da saioa.',
            session_settings_help_absolute: 'Saioa iraungiko da iraupen osoaren ondoren jarduera edozein dela ere.',
            session_settings_help_max: 'Erabiltzaile bakoitzeko aldibereko saioen gehienezko kopurua.',
            sso_note: 'Baliteke SSO konfigurazioan egindako aldaketek erabiltzaileek berriro autentifikatu behar izatea.',
            service_accounts_warning: 'API gako hau behin bakarrik erakutsiko da. Kopiatu orain eta gorde ezazu modu seguruan.',
            service_accounts_new: 'Zerbitzu-kontu berria',
            service_accounts_name: 'Kontuaren izena',
            service_accounts_role: 'Rola',
            service_accounts_created: 'Sortu',
            service_accounts_desc: 'Kudeatu makina-makina zerbitzu-kontuak API integrazioetarako.',
            break_glass_warning: 'Break-glass sarbidea ikuskatu eta erregistratzen da. Erabili benetako larrialdietarako soilik.',
            break_glass_min_chars: '{count} / {min} gutxieneko karaktere',
            break_glass_permission_count: '{count} baimen hautatuta',
            break_glass_submitted: 'Beira hausteko eskaera aurkeztu da. Onarpenaren zain.',
            break_glass_approved: 'Beira hausteko eskaera onartu da.',
            break_glass_denied: 'Beira hausteko eskaera ukatu egin da.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Berretsi aktibazioa',
            break_glass_pending: 'Zain dauden eskaerak',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Gelditzen den denbora',
            break_glass_expired: 'Iraungi egin da',
            break_glass_approved_denied_by: 'Onartua/Ukatua',
            delegations_desc: 'Kudeatu erabiltzaileen arteko aldi baterako baimen-eskubideak.',
            delegations_my: 'Nire Ordezkaritzak',
            delegations_to_me: 'Niri delegatua',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Baimen guztiak',
            delegations_scope_specific: 'Baimen espezifikoak',
            delegations_created: 'Ordezkaritza arrakastaz sortu da.',
            delegations_revoked: 'Ordezkaritza baliogabetua.',
            delegations_confirm_revoke: 'Ziur ordezkaritza hau baliogabetu nahi duzula?',
            delegations_confirm_revoke_title: 'Berretsi Errebokatu',
            delegations_permission_count: '{count} baimena(k)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Egoera',
            api_keys_prefix: 'Gako-aurrizkia',
            api_keys_permissions_count: 'Baimenak',
            api_keys_ip_count: 'IP Murrizketak',
            api_keys_created: 'Sortu',
            api_keys_confirm_revoke_title: 'Berretsi Errebokatu',
            api_keys_confirm_revoke_message: 'Ziur API "{name}" tekla baliogabetu nahi duzula? Ekintza hau ezin da desegin.',
            api_keys_confirm_rotate_title: 'Berretsi biraketa',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API gakoa behar bezala sortu da.',
            api_keys_revoked_success: 'API "{name}" tekla kendu da.',
            api_keys_rotated_success: 'API tekla "{name}" biratu da. Kopiatu beheko gako berria.',
            api_keys_load_error: 'Ezin izan dira API gakoak kargatu.',
            api_keys_create_error: 'Ezin izan da sortu API gakoa.',
            api_keys_revoke_error: 'Ezin izan da baliogabetu API gakoa.',
            api_keys_rotate_error: 'Ezin izan da biratu API tekla.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutu',
            break_glass_duration_30: '30 minutu',
            break_glass_duration_60: '60 minutu (1h)',
            break_glass_duration_120: '120 minutu (2h)',
            break_glass_duration_240: '240 minutu (4h)',
            sod_rules_title: 'Eginkizunen Banaketa Arauak',
            sod_violations_report: 'Arau-hausteen txostena',
            sod_create_rule: 'Sortu araua',
            sod_col_name: 'Izena',
            sod_col_description: 'Deskribapena',
            sod_col_conflict_count: 'Gatazka zenbaketa',
            sod_col_severity: 'Larritasuna',
            sod_col_status: 'Egoera',
            sod_col_actions: 'Ekintzak',
            sod_no_rules: 'Ez da aurkitu SoD araurik',
            sod_severity_warning: 'Abisua',
            sod_severity_blocking: 'Blokeatzea',
            sod_active: 'Aktiboa',
            sod_inactive: 'Inaktibo',
            sod_edit: 'Editatu',
            sod_exceptions: 'Salbuespenak',
            sod_deactivate: 'Desaktibatu',
            sod_confirm_deactivate_title: 'Berretsi desaktibazioa',
            sod_confirm_deactivate_message: '"{name}" araua desaktibatu nahi duzu? Jada ez ditu urraketak markatuko.',
            sod_deactivate_error: 'Ezin izan da desaktibatu araua',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Sarbide-berrikusketak',
            campaigns: 'Berrikusketa-kanpainak',
            new_campaign: 'Kanpaina berria',
            campaign_name: 'Kanpainaren izena',
            scope: 'Irismena',
            reviewers: 'Berrikuskatzaileak',
            due_date: 'Epemuga',
            progress: 'Aurrerapena',
            my_reviews: 'Nire berrikusketak',
            approve: 'Onartu',
            revoke: 'Errebokatu',
            analytics: 'Rolen analitika',
            analytics_title: 'Rolen analitika',
            permission_usage: 'Baimenen erabilera',
            role_coverage: 'Rolen estaldura',
            campaign_detail: 'Kanpainaren xehetasunak',
            campaign_description: 'Deskribapena',
            campaign_status: 'Egoera',
            campaign_deadline: 'Epea',
            campaign_scope_type: 'Esparru mota',
            campaign_completion: 'Osatzea',
            campaign_total_items: 'Elementuak guztira',
            campaign_pending_items: 'Zain',
            campaign_confirmed_items: 'Baieztatuta',
            campaign_revoked_items: 'Ezeztatua',
            campaign_escalated_items: 'Goratuta',
            campaign_launch: 'Abiarazi kanpaina',
            campaign_cancel: 'Utzi kanpaina',
            campaign_confirm_launch: 'Ziur kanpaina hau martxan jarri nahi duzula?',
            campaign_confirm_cancel: 'Ziur kanpaina hau bertan behera utzi nahi duzula?',
            campaign_launched: 'Kanpaina arrakastaz abiarazi da.',
            campaign_cancelled: 'Kanpaina bertan behera utzi da.',
            review_items: 'Berrikusi elementuak',
            review_item_user: 'Erabiltzailea',
            review_item_role: 'Rola',
            review_item_reviewer: 'Berrikuslea',
            review_item_decision: 'Erabakia',
            review_item_justify: 'Justifikazioa',
            review_item_confirm: 'Berretsi',
            review_item_revoke: 'Errebokatu',
            review_item_escalate: 'Igotu',
            all_decisions: 'Erabaki guztiak',
            bulk_action: 'Bulk Ekintza',
            bulk_confirm: 'Multzoan baieztatzea',
            bulk_revoke: 'Errebokatze masiboa',
            selected_count: '{count} hautatuta',
            justify_required: 'Justifikazioa beharrezkoa da',
            scope_all_users: 'Erabiltzaile guztiak',
            scope_role_owner: 'Rolaren jabea',
            scope_department: 'Saila',
            back_to_campaigns: 'Itzuli Kanpainetara',
            recommendations: 'Gomendioak',
            refreshing: 'Freskatzen...',
            decide: 'Erabaki',
            decision_by: 'Erabakia',
            loading_items: 'Elementuak kargatzen...',
            loading_review_items: 'Iritzi-elementuak kargatzen...',
            campaign_not_found: 'Ez da aurkitu kanpaina',
            loading_campaign: 'Kanpainaren xehetasunak kargatzen...',
            campaign_completed: 'Kanpaina behar bezala osatu da.',
            confirm_cancel_title: 'Berretsi Utzi',
            justify_bulk_confirm: 'Multzoan baieztatzearen justifikazioa:',
            justify_bulk_revoke: 'Errebokatze masiboaren justifikazioa:',
            items_selected: '{count} elementu hautatu dira',
            justify_bulk_required: 'Ekintza ugarietarako justifikazioa behar da',
            create_campaign_title: 'Sortu Sarbidea Berrikusteko Kanpaina',
            name_required: 'Izena behar da',
            scope_type_required: 'Esparru mota behar da',
            reviewer_strategy_required: 'Ebaluatzaileen estrategia beharrezkoa da',
            deadline_required: 'Epea beharrezkoa da',
            reviewer_id_required: 'Ebaluatzailearen IDa beharrezkoa da erabiltzailearen estrategia zehatzetarako',
            invalid_json: 'JSON baliogabea',
            scope_filter_label: 'Esparru-iragazkia (JSON)',
            reviewer_strategy_label: 'Berrikusleen estrategia',
            reviewer_id_label: 'Ebaluatzailearen IDa',
            deadline_label: 'Epea',
            create_campaign_btn: 'Sortu kanpaina',
            scope_type_label: 'Esparru mota',
            scope_by_role: 'Rolaren arabera',
            scope_by_department: 'Sailaren arabera',
            scope_custom: 'Pertsonalizatua',
            reviewer_manager: 'Kudeatzailea',
            reviewer_specific_user: 'Erabiltzaile espezifikoa',
            enter_json_filter: 'Sartu JSON iragazkia',
            hint_label: 'Aholkua:',
            role_usage: 'Rolen erabilera',
            over_privileged_users: 'Gehiegizko Erabiltzaileak',
            role_name_col: 'Rolaren izena',
            assigned_users: 'Esleitutako Erabiltzaileak',
            active_users_30d: 'Erabiltzaile aktiboak (30d)',
            total_perms: 'Perms guztira',
            used_perms: 'Erabilitako Perms',
            unused_perms: 'Erabili gabeko Perms',
            no_data_available: 'Ez dago daturik eskuragarri',
            roles_col: 'Rolak',
            total_col: 'Guztira',
            used_col: 'Erabiliak',
            unused_pct: 'Erabili gabeko %',
            current_count: 'Egungo zenbaketa',
            recommended_count: 'Gomendatutako zenbaketa',
            rationale: 'Arrazoia',
            loading_analytics: 'Analitikak kargatzen...',
            loading_role_analytics: 'Rolen analitikaren datuak kargatzen...',
            computed_at: 'Helbide honetan kalkulatua:',
            form_errors: 'Inprimakiak {count} errore ditu. Mesedez, zuzendu.',
            scope_filter_invalid_json: 'Esparru-iragazkiak JSON baliogabeak ditu',
            scope_hint_all_users: 'Ez da iragazkirik behar -- erabiltzaile guztiak sartuko dira.',
            review_type_label: 'Berrikuspenen mota',
            review_type_required: 'Berrikuspenen mota beharrezkoa da',
            reviewer_ids_label: 'Berrikuslearen IDak',
            reviewer_ids_hint: 'Sartu erabiltzaile IDak komaz bereizita',
            reviewer_ids_required: 'Gutxienez berrikusle ID bat behar da',
            start_date_label: 'Hasiera-data',
            start_date_required: 'Hasiera-data beharrezkoa da',
            end_date_label: 'Amaiera-data',
            end_date_required: 'Amaiera-data beharrezkoa da',
            review_type_user_access: 'Erabiltzailearen sarbidea',
            review_type_role_permissions: 'Rol-baimenak',
            review_type_service_accounts: 'Zerbitzu-kontuak',
            review_type_privileged_access: 'Sarbide pribilegiatua',
            review_type_application_access: 'Aplikazio-sarbidea',
            auto_revoke_label: 'Auto-baliogabetzea ukatzean',
            campaigns_title: 'Atzitu Berrikuspen Kanpainak',
            create_campaign_label: '+ Sortu kanpaina',
            no_campaigns_found: 'Ez da kanpainarik aurkitu',
            loading_campaigns: 'Kanpainak kargatzen...',
            no_reviews_assigned: 'Ez zaizu esleitu iritzirik',
            loading_reviews: 'Iritziak kargatzen...',
            review_decision_title: 'Berrikusi Erabakia',
            justification_label: 'Justifikazioa',
            announce_loaded_review_items: '{count} iritzi-elementu kargatu dira',
            announce_loaded_campaigns: '{count} kanpaina kargatu dira',
            announce_decision_submitted: '"{decision}" erabakia bidali da berrikusteko elementua',
            announce_decision_submitted_for: '"{decision}" erabakia {name}-rako bidali da',
            announce_bulk_decision: '{decision} multzoan aplikatu da {count} elementuei',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Atestigaritze-programazioak',
            new: 'Programazio berria',
            edit: 'Editatu programazioa',
            create_title: 'Atestigaritze-programazioa sortu',
            edit_title: 'Atestigaritze-programazioa editatu',
            schedule_history: 'Programazio-historia',
            next_run_date: 'Hurrengo exekuzio-data',
            schedule_name: 'Programazioaren izena',
            frequency: 'Maiztasuna',
            next_run: 'Hurrengo exekuzioa',
            last_run: 'Azken exekuzioa',
            history: 'Atestigaritze-historia',
            description: 'Deskribapena',
            scope: 'Eremua',
            status: 'Egoera',
            created_at: 'Sortu',
            updated_at: 'Eguneratua',
            trigger_now: 'Trigger Now',
            back_to_schedules: 'Itzuli ordutegietara',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Programazioa behar bezala abiarazi da.',
            deleted: 'Ezabatu da ordutegia.',
            saved: 'Egitaraua ongi gorde da.',
            campaigns_generated: 'Sortutako kanpainak',
            run_date: 'Exekutatu data',
            run_status: 'Exekutatu Egoera',
            create_schedule: 'Sortu ordutegia',
            view_history: 'Ikusi historia',
            deactivate: 'Desaktibatu',
            overdue: 'OVERDUE',
            overdue_sr: 'Egitaraua atzeratuta dago',
            confirm_trigger_title: 'Berretsi ekintza',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Berretsi desaktibazioa',
            confirm_deactivate_message: 'Ziur programazio hau desaktibatu nahi duzula?',
            triggered_success: 'Programazioa behar bezala abiarazi da.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Ordutegia desaktibatuta dago.',
            name_label: 'Izena*',
            frequency_label: 'Maiztasuna *',
            scope_type_label: 'Esparru mota *',
            scope_filter_label: 'Esparru-iragazkia (JSON)',
            reviewer_strategy_label: 'Berrikusleen estrategia *',
            reviewer_id_label: 'Ebaluatzailearen IDa *',
            frequency_quarterly: 'Hiruhilabetekaria',
            frequency_semi_annual: 'Seiurtekoa',
            frequency_annual: 'Urtekoa',
            scope_all_users: 'Erabiltzaile guztiak',
            scope_by_role: 'Rolaren arabera',
            scope_by_department: 'Sailaren arabera',
            scope_custom: 'Pertsonalizatua',
            reviewer_manager: 'Kudeatzailea',
            reviewer_role_owner: 'Rolaren jabea',
            reviewer_specific_user: 'Erabiltzaile espezifikoa',
            computed_next_run_date: 'Hurrengo exekuzio-data kalkulatua',
            current_scheduled_date: 'Egungo programatutako data',
            recomputed_frequency_changed: '(berriro kalkulatua -- maiztasuna aldatu da)',
            estimated_days_hint: 'Gaurtik aurrera {days} egunetan oinarrituta kalkulatua',
            validation_name_required: 'Izena behar da',
            validation_frequency_required: 'Maiztasuna beharrezkoa da',
            validation_scope_type_required: 'Esparru mota behar da',
            validation_reviewer_strategy_required: 'Ebaluatzaileen estrategia beharrezkoa da',
            validation_reviewer_id_required: 'Ebaluatzailearen IDa beharrezkoa da erabiltzailearen estrategia zehatzetarako',
            validation_invalid_json: 'JSON baliogabea',
            validation_scope_filter_invalid_json: 'Esparru-iragazkiak JSON baliogabeak ditu',
            form_errors: 'Inprimakiak {count} errore ditu. Mesedez, zuzendu.',
            create_schedule_btn: 'Sortu ordutegia',
            schedule_history_title: 'Ordutegien historia',
            campaign_name_col: 'Kanpainaren izena',
            items_col: 'Elementuak',
            no_campaigns_yet: 'Oraindik ez da sortu kanpainarik',
            loading_history: 'Historia kargatzen...',
            loading_schedule: 'Egitaraua kargatzen...',
            total_campaigns: 'Guztira kanpainak: {count}',
            results_per_page: 'Emaitzak orrialde bakoitzeko',
            page_size_option: '{size} / orrialdea',
        },
        sod: {
            title: 'Eginkizunen bereizketa-arauak',
            new: 'Arau berria',
            edit: 'Editatu araua',
            create_title: 'SoD araua sortu',
            edit_title: 'SoD araua editatu',
            rule_name: 'Arauaren izena',
            conflicting_permissions: 'Gatazkan dauden baimenak',
            permission_a: 'A baimena',
            permission_b: 'B baimena',
            violations: 'SoD urratzeak',
            violation_user: 'Erabiltzailea',
            violation_rule: 'Urratutako araua',
            violation_date: 'Detektatze-data',
            exceptions: 'SoD salbuespenak',
            exception_reason: 'Salbuespenen arrazoia',
            exception_approver: 'Onartzailea',
            exception_status: 'Salbuespen egoera',
            has_exception: 'Salbuespena du',
            no_exception: 'Salbuespenik ez',
            grant_exception: 'Diru-laguntzaren salbuespena',
            granting: 'Ematea...',
            grant_new_exception: 'Eman Salbuespen Berria',
            exceptions_for: 'Honen salbuespenak: {name}',
            back_to_rules: 'Itzuli Arauetara',
            no_violations: 'Ez da urraketarik aurkitu',
            no_exceptions_found: 'Ez da salbuespenik aurkitu arau honetarako',
            all_exception_statuses: 'Salbuespen egoera guztiak',
            user_id_label: 'Erabiltzaile IDa',
            justification_label: 'Justifikazioa',
            compensating_controls_label: 'Kontrol Konpentsatzaileak',
            expires_at_label: 'Ordu honetan iraungitzen da',
            granting_exception_for: 'Salbuespen bat ematea araurako: {name}',
            severity: 'Larritasuna',
            severity_warning: 'Abisua',
            severity_blocking: 'Blokeatzea',
            violation_count: '{count} urraketa(k)',
            exception_count: '{count} salbuespena(k)',
            rule_description: 'Deskribapena',
            confirm_delete: 'Ziur arau hau ezabatu nahi duzula?',
            deleted: 'Araua ezabatu da.',
            framework_code: 'Arau-esparrua',
            name_label: 'Izena*',
            severity_label: 'Larritasuna *',
            conflict_pairs_legend: 'Gatazka Baimen Bikoteak',
            conflict_pairs_hint: 'Bikote bakoitzak erabiltzaile berak eduki behar ez dituen bi baimen definitzen ditu.',
            placeholder_permission_a: '-- A baimena --',
            placeholder_permission_b: '-- B baimena --',
            permission_a_for_pair: 'A baimena {index} bikotearentzat',
            permission_b_for_pair: 'B baimena {index} bikotearentzat',
            conflict_pair_label: 'Gatazka bikotea {index}',
            remove_conflict_pair: 'Kendu gatazka bikotea {index}',
            add_pair: '+ Gehitu bikotea',
            placeholder_name: 'adib. Ordainketa-onarpena bereiztea',
            create_rule: 'Sortu araua',
            loading_rule: 'SoD araua kargatzen...',
            failed_load_rule: 'Ezin izan da kargatu araua.',
            failed_create_rule: 'Ezin izan da SoD araua sortu.',
            failed_update_rule: 'Ezin izan da eguneratu SoD araua.',
            validation_name_required: 'Izena behar da.',
            validation_pair_required: 'Gutxienez gatazka-baimen pare bat behar da.',
            validation_pair_both_required: '{index} bikotea: bi baimenak hautatu behar dira.',
            validation_pair_must_differ: '{index} bikotea: A eta B baimenak desberdinak izan behar dute.',
            added_conflict_pair: 'Gatazka bikotea gehitu da {index}',
            removed_conflict_pair: '{index} gatazka-parea kendu da',
            announce_loaded_violations: '{count} urraketa kargatu dira',
            announce_loaded_exceptions: '{count} salbuespen kargatu dira',
            announce_loaded_rules: '{count} SoD arauak kargatu dira',
            announce_loading_exceptions: 'Salbuespenak kargatzen…',
            regulatory_info: {
                sod_context: 'SoD arauek PDPLk eskatzen duen erregulazioko rolen bereizketa ezartzen dute. framework_code batekin lotutako urraketak markoko froga-paketeetan sar daitezke (UC-VN-09).',
                violation_export_context: 'SoD urraketa-esportazioek framework_code, gatazka-rolak, larritasuna, sortzailea eta detekzio-data dituzte froga-paketetarako (P-VN-10).',
                exception_context: 'SoD salbuespenek ebidentziak sortzen dituzte: onartzailea, justifikazioa, konpentsazio-kontrolak eta iraungitze-data. Iraungitako salbuespenak ikusgai geratzen dira (UC-VN-09 5. urratsa).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP integrazioa',
            status: 'Konexio-egoera',
            endpoint: 'CMP amaiera-puntua',
            last_sync: 'Azken sinkronizazioa',
            configure: 'Konfiguratu',
        },
        purpose_mapping: {
            title: 'Xede-esleipena',
            cmp_purpose: 'CMP xedea',
            grc_purpose: 'GRC xedea',
            mapping_status: 'Esleipena egoera',
            auto_map: 'Automatikoki esleitu',
            new_mapping: 'Esleipen berria',
        },
        webhook: {
            title: 'Webhook konfigurazioa',
            webhook_url: 'Webhook URLa',
            secret: 'Webhook sekretua',
            events: 'Gertaera motak',
            test_webhook: 'Probatu webhook-a',
        },
        bulk_sync: {
            title: 'Sinkronizazio masiboa',
            sync_now: 'Sinkronizatu orain',
            last_run: 'Azken exekuzioa',
            records_synced: 'Sinkronizatutako erregistroak',
            sync_history: 'Sinkronizazio-historia',
            syncing: 'Sinkronizatzen...',
        },
        sync_settings: {
            sync_failures: 'Sinkronizazio-hutsegiteak',
            sync_secret_warning: 'Sinkronizazio-sekretua (gorde orain — ez da berriro erakutsiko):',
            push_description: 'Konfiguratu the GRC Suite to push case egoera updates to AesirX CMP.',
            technical_description: 'Konfigurazio honek kontrolatzen du GRC Suite-k kasuen egoera eguneratzeak nola bidaltzen dituen AesirX CMP-ra.',
            direction_label: 'Norabidea',
            direction_value: 'GRC Suite → AesirX CMP (irteerako)',
            auth_label: 'Autentifikazioa',
            auth_value: 'HMAC-SHA256 sinadura X-GRC-Signature goiburuan',
            retry_label: 'Retry Politika',
            retry_value: '3 saiakera atzerapen esponentzialarekin (30s, 2min, 10min)',
            inbound_label: 'Sarrerako konfigurazioa',
            inbound_value: 'Sarrerako webhook-a (CMP → GRC) konfiguratuta dago hemen',
            cmp_webhook_settings: 'CMP Webhook Ezarpenak',
            page_title: 'CMP Sinkronizazio Ezarpenak',
            outbound_sync_configured: 'Irteerako sinkronizazioa konfiguratuta.',
            configure_sync_failed: 'Sinkronizazioa konfiguratzeak huts egin du',
            configuration_updated: 'Konfigurazioa eguneratuta.',
            update_configuration_failed: 'Konfigurazioa eguneratzeak huts egin du',
            connection_test_failed: 'Konexio-probak huts egin du',
            test_result_message: 'Proba {endpoint} helbidera bidalia. Egoera: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Berria → Under Berrikusketa (assigned)',
                under_review_to_identity_verification: 'Under Berrikusketa → Identity Verification',
                under_review_to_in_progress: 'Berrikusketan → Martxan',
                in_progress_to_completed: 'In Progress → Osatuta',
                in_progress_to_denied: 'Martxan → Ukatua',
                any_to_closed: 'Edozein → Itxita',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhook-ak',
            new: 'Webhook berria',
            url: 'URLa',
            url_placeholder: 'https://example.com/webhook',
            events: 'Gertaerak',
            secret: 'Sekretua',
            active: 'Aktibo',
            test: 'Probatu',
            create_failed: 'Huts egin du: create webhook. Saiatu berriro.',
            create_success: 'Webhook arrakastaz sortu da.',
            deactivate_failed: 'Huts egin du: deactivate webhook. Saiatu berriro.',
            deactivate_success: 'Webhook desaktibatuta dago.',
            fetch_failed: 'Huts egin du: fetch webhooks. Saiatu berriro.',
            send_test_failed: 'Huts egin du: send test. Saiatu berriro.',
            test_failed: 'Webhook proban huts egin du.',
            test_success: 'Webhook proba arrakastatsua izan da.',
            edit_title: 'Editatu webhook-a',
            announce_loaded: 'Webhook-ak kargatu dira',
            update_success: 'Webhook-a arrakastaz eguneratu da.',
            update_failed: 'Huts egin du webhook-a eguneratzean.',
            activate_success: 'Webhook-a aktibatu da.',
            activate_failed: 'Huts egin du webhook-a aktibatzean.',
        },
        domains: {
            title: 'Domeinuak',
            new: 'Domeinu berria',
            domain_name: 'Domeinuaren izena',
            verified: 'Egiaztatua',
            verify: 'Egiaztatu',
            add_failed: 'Huts egin du: add domain. Saiatu berriro.',
            add_success: 'Domeinu added successfully.',
            domain_placeholder: 'adib. grc.example.com',
            fetch_failed: 'Huts egin du: fetch domains. Saiatu berriro.',
            remove_failed: 'Huts egin du: remove domain. Saiatu berriro.',
            remove_success: 'Domeinu removed successfully.',
            txt_record: 'TXT Erregistro',
            verify_dns_failed: 'DNS egiaztapenak huts egin du.',
            verify_failed: 'Huts egin du: verify domain. Saiatu berriro.',
            verify_success: 'Domeinu verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Esportazio-programazioak',
            new: 'Programazio berria',
            schedule_name: 'Izena',
            frequency: 'Maiztasuna',
            format: 'Formatua',
            last_run: 'Azken exekuzioa',
            next_run: 'Hurrengo exekuzioa',
            create_failed: 'Huts egin du: create schedule. Saiatu berriro.',
            create_success: 'Ordutegi created successfully.',
            day_of_month: 'Hilabeteko eguna',
            day_of_week: 'Asteko eguna',
            deactivate_failed: 'Huts egin du: deactivate schedule. Saiatu berriro.',
            deactivate_success: 'Ordutegi deactivated.',
            fetch_failed: 'Huts egin du: fetch schedules. Saiatu berriro.',
            freq_daily: 'Egunero',
            freq_monthly: 'Hilero',
            freq_weekly: 'Astero',
            recipients: 'Hartzaileak',
            time_utc: 'Ordua (UTC)',
            trigger_failed: 'Huts egin du: trigger export. Saiatu berriro.',
            trigger_success: 'Esportatu triggered successfully.',
            type_audit_trail: 'Ikuskaritza Trail',
            type_compliance_report: 'Betetze Txosten',
            type_custom: 'Pertsonalizatua',
            type_incident_report: 'Gertaera Txosten',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Posta elektroniko-erregistroa',
            recipient: 'Hartzailea',
            subject: 'Gaia',
            sent_at: 'Bidalitze-data',
            status: 'Egoera',
            resend: 'Berbidali',
            fetch_failed: 'Huts egin du: fetch email logs. Saiatu berriro.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Erakundeak',
        create: 'Sortu Erakunde',
        edit: 'Editatu Erakunde',
        name: 'Izena',
        slug: 'Identifikatzailea',
        timezone: 'Ordu-eremua',
        locale: 'Lehenetsia Locale',
        parent: 'Parent Erakunde',
        group: 'Talde',
        org_group: 'Erakunde Taldea',
        no_group: '(Bat ere ez - ez dago talde batean)',
        created: 'Sortuta',
        no_parent: '(Bat ere ez - autonomoa)',
        no_orgs: 'Ez da organizations aurkitu',
        load_error: 'Huts egin du: load organizations. Saiatu berriro.',
        save_error: 'Huts egin du: save organization. Saiatu berriro.',
        created_msg: 'Erakunde created',
        updated: 'Erakunde updated',
        deactivate: 'Desaktibatu',
        deactivated: 'Erakunde deactivated',
        confirm_deactivate: 'Ziur zaude deactivate this organization?',
        loaded_results: 'Organizations kargatuta, {count} emaitza',
        form_loaded: 'Organization inprimakia kargatua',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Ingelesa (eu-AEB)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Esparru guztiak',
        assigned_at: 'Esleitze data',
    },
    intake_configs: {
        title: 'Sarrera-konfigurazio publikoak',
        create: 'Sortu Config',
        edit: 'Editatu Intake Config',
        label_col: 'Etiketa',
        key: 'Intake Gakoa',
        key_hint: 'URL publikoa izango da: /rights-request?key=',
        mapped_org: 'Helburu Erakunde',
        status: 'Egoera',
        enabled: 'Gaituta',
        disabled: 'Desgaituta',
        request_types: 'Gaituta Eskaera Motak',
        language: 'Lehenetsia Hizkuntza',
        public_url: 'URL publikoa',
        no_configs: 'Ez da intake configs found. sortu one to enable public rights request forms. aurkitu',
        load_error: 'Huts egin du: load intake configs. Saiatu berriro.',
        save_error: 'Huts egin du: save. Saiatu berriro.',
        delete_error: 'Huts egin du: delete. Saiatu berriro.',
        created_msg: 'Konfigurazioa sortuta',
        updated: 'Konfigurazioa eguneratuta',
        deleted: 'Sarrera-konfigurazioa ezabatuta',
        confirm_delete: 'Ezabatu this intake config?',
        select_types: 'Hautatu at least one request type',
        select_org: 'Hautatu a target organization',
        routing_mode: 'Bideratze Modua',
        single_org: 'Erakunde Bakarra',
        org_group_mode: 'Erakunde Taldea',
        org_group: 'Erakunde Taldea',
        select_group: 'Hautatu erakunde-talde bat...',
        group_root_hint: 'Automatikoki taldeko erro-erakundera ezartzen da taldea hautatzen denean.',
        loaded_results: 'Intake configurations kargatuta, {count} emaitza',
        form_loaded: 'Intake configuration inprimakia kargatua',
    },
    frequency: {
        weekly: 'Astero',
        monthly: 'Hilero',
        quarterly: 'Hiruhileko',
        semi_annually: 'Seihileko',
        annually: 'Urtero',
        custom: 'Pertsonalizatua',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Egutegia',
            all_tasks: 'Zeregin guztiak',
            my_tasks: 'Nire zereginak',
            overdue: 'Atzeratuak',
            board: 'Taula',
            workflows: 'Lan-fluxuak',
        },
        list: {
            title: 'Zeregin guztiak',
            my_title: 'Nire zereginak',
            overdue_title: 'Zeregin atzeratuak',
            new_task: 'Zeregina sortu',
            no_tasks: 'Ez da zereginik aurkitu.',
            empty_state: 'Ez da atalik aurkitu. Atazak moduluen lan-flujetatik sortzen dira, hala nola ebaluazioak, gorabeherak eta berrikusketa-kanpainak.',
            total: 'Zeregin guztira',
            search_placeholder: 'Zereginak bilatu...',
            bulk_actions: 'Ekintza masiboak',
            change_status: 'Egoera aldatu',
            change_priority: 'Lehentasuna aldatu',
            assign: 'Esleitu',
            sr_my_tasks_count: '{count} zeregin esleituta zuri',
            sr_days_overdue: '{count} egun atzeratuta',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Zereginaren xehetasunak',
            description: 'Deskripzioa',
            workflow_progress: 'Lan-fluxuaren aurrerapena',
            checklist: 'Egiaztapen-zerrenda',
            dependencies: 'Mendekotasunak',
            comments: 'Iruzkinak',
            activity_timeline: 'Jarduera-kronologia',
            assignees: 'Esleitutakoak',
            add_assignee: 'Esleitutakoa gehitu',
            remove_assignee: 'Esleitutakoa kendu',
            due_date: 'Epemuga',
            start_date: 'Hasiera-data',
            sla_status: 'SLA egoera',
            sla_on_track: 'Garaiz',
            sla_at_risk: 'Arriskuan',
            sla_breached: 'Urratua',
            tags: 'Etiketak',
            source_entity: 'Jatorri-entitatea',
            view_source: 'Jatorria ikusi',
            add_dependency: 'Mendekotasuna gehitu',
            remove_dependency: 'Mendekotasuna kendu',
            write_comment: 'Iruzkin bat idatzi...',
            post_comment: 'Argitaratu',
            edit_comment: 'Editatu',
            delete_comment: 'Ezabatu',
            add_checklist_item: 'Elementua gehitu',
            no_dependencies: 'Mendekotasunik gabe',
            no_comments: 'Oraindik iruzkinik ez',
            blocks: 'Blokeatzen du',
            blocked_by: 'Honek blokeatua',
            related: 'Erlazionatua',
            working_days_remain: 'lan-egun geratzen dira',
            created_by: 'Sortzailea',
            completed_at: 'Osatuta',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Esleitutakorik gabe',
            add_assignee_placeholder: 'Sartu erabiltzaile-IDa...',
            edited: 'editatua',
            timeline: 'Jarduera-kronologia',
            no_timeline: 'Oraindik jarduerarik ez',
            timeline_reason: 'Reason',
            no_workflow: 'Lan-fluxurik esleituta ez',
            add_dependency_placeholder: 'Sartu zeregina IDa...',
            sr_status_updated: 'Egoera eguneratu da: {status}',
            sr_priority_updated: 'Lehentasuna {priority}-ra eguneratu da',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Exekutatu lan-fluxu gisa',
            convert_modal_title: 'Bihurtu zeregina lan-fluxuaren exekuziora',
            convert_workflow_label: 'Lan-fluxua',
            convert_workflow_placeholder: 'Aukeratu lan-fluxu bat...',
            convert_attachments_label: 'Eranskinak',
            convert_attachments_empty: 'Zeregin honetan ez dago eranskinik.',
            convert_persona_label: 'Pertsonaren gainidaztea (aukerakoa)',
            convert_submit: 'Hasi lan-fluxua exekutatzen',
            convert_starting: 'Lan-fluxua exekutatzen hasten…',
            convert_started: 'Lan-fluxua martxan hasi da — exekuzioa irekitzen...',
            convert_disabled_in_flight: 'Zeregin honetatik exekutatutako lan-fluxu bat abian da dagoeneko.',
            linked_workflow_chip: '{workflow_title}-tik',
            linked_chat_chip: 'AI Txatetik',
            linked_more: '+{count} gehiago',
        },
        calendar: {
            title: 'Zereginen egutegia',
            page_title: 'Egutegia',
            create_task: 'Zeregina sortu',
            month: 'Hilabetea',
            week: 'Astea',
            day: 'Eguna',
            today: 'Gaur',
            more_tasks: '+{count} gehiago',
            no_tasks_day: 'Ez dago zereginik egun honetan',
            all_day: 'Egun osoa',
            quick_create_tooltip: 'Egun honetarako zeregina sortu',
            view_changed: 'Egutegi-ikuspegia {view} aldatu da',
            tasks_in_view: '{count} zeregin ikuspegiean',
            task_in_view: '{count} ataza bistan',
            filter_my_tasks: 'Nire zereginak',
            filter_overdue: 'Atzeratuak',
            filter_high_priority: 'Lehentasun handia',
            filter_all: 'Denak',
        },
        filters: {
            assignee: 'Esleitutakoa',
            me: 'Ni',
            role: 'Rola',
            status: 'Egoera',
            priority: 'Lehentasuna',
            task_type: 'Zeregin-mota',
            source_module: 'Jatorri-modulua',
            organization: 'Erakundea',
            sla_breached_only: 'SLA urratuak soilik erakutsi',
            clear_filters: 'Iragazkiak garbitu',
            apply_filters: 'Aplikatu',
            source_label: 'Iturria',
            source_manual: 'Eskuliburua',
            source_module_kind: 'Moduluek sortutakoa',
            source_chat: 'AI Txatetik',
            source_workflow_run: 'AI Workflow Run-etik',
        },
        status: {
            pending: 'Zain',
            in_progress: 'Abian',
            blocked: 'Blokeatuta',
            completed: 'Osatua',
            overdue: 'Atzeratuak',
            escalated: 'Eskalatua',
            cancelled: 'Bertan behera utzia',
        },
        priority: {
            critical: 'Kritikoa',
            high: 'Altua',
            medium: 'Ertaina',
            low: 'Baxua',
        },
        task_types: {
            manual_task: 'Eskuzko zeregina',
            dsr_case: 'DSR kasua',
            dsr_review: 'DSR berrikusketa',
            dsr_escalation: 'DSR eskalada',
            dpia_review: 'DBEB berrikusketa',
            dpia_approval: 'DBEB onarpena',
            transfer_review: 'Transferentzia-berrikusketa',
            incident_task: 'Gertaera-zeregina',
            incident_notification: 'Gertaera-jakinarazpena',
            playbook_step: 'Eskuliburuaren urratsa',
            regulator_deadline: 'Arautzaile-epemuga',
            monitoring_task: 'Monitorizazio-zeregina',
            kpi_alert: 'KPI alerta',
            vendor_review: 'Hornitzaile-berrikusketa',
            vendor_questionnaire: 'Hornitzaile-galdetegia',
            governance_review: 'Gobernantza-berrikusketa',
            drift_alert: 'Desbideratze-alerta',
            governance_approval: 'Gobernantza-onarpena',
            deletion_task: 'Ezabaketa-zeregina',
            deletion_verification: 'Ezabaketa-egiaztapena',
            access_review: 'Sarbide-berrikusketa',
            sod_alert: 'SoD alerta',
            consent_workflow: 'Adostasun-fluxua',
            consent_alert: 'Adostasun-alerta',
            property_review: 'Propietate-berrikusketa',
            inconsistency_alert: 'Inkoherentzia-alerta',
            form_review: 'Formulario-berrikusketa',
            dossier_deadline: 'Dosier-epemuga',
            submission_task: 'Bidalketa-zeregina',
            translation_review: 'Itzulpen-berrikusketa',
            governance_task: 'Gobernantza-zeregina',
            reporting_deadline: 'Txosten-epemuga',
            scanner_alert: 'Eskaner-alerta',
            classification_review: 'Sailkapen-berrikusketa',
            lifecycle_task: 'Bizi-ziklo zeregina',
            discovery_review: 'Aurkikuntza-berrikusketa',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Prestakuntza-esleipena',
        task_type_groups: {
            training: 'Prestakuntza',
            manual: 'Eskuzkoa',
            dsr: 'DSR / Eskubide-eskaerak',
            assessments: 'Ebaluazioak',
            incidents: 'Gertaerak',
            incident_ops: 'Gertaeren eragiketak',
            monitoring: 'Monitorizazioa',
            vendor_risk: 'Hornitzaileen arriskua',
            governance: 'Gobernantza',
            deletion: 'Ezabaketa',
            access_review: 'Sarbide-berrikusketa',
            consent: 'Adostasuna',
            consent_properties: 'Adostasunaren propietateak',
            forms: 'Formularioak',
            localization: 'Lokalizazioa',
            program_governance: 'Programaren gobernantza',
            privacy_scanner: 'Pribatutasun-eskánerra',
            classification: 'Sailkapena',
            discovery: 'Aurkikuntza',
            forseti: 'Forseti',
        },
        create: {
            title: 'Zeregina sortu',
            task_title: 'Izenburua',
            task_description: 'Deskripzioa',
            task_type: 'Zeregin-mota',
            task_type_label: 'Zeregin-mota',
            task_type_placeholder: 'Zeregin-mota hautatu',
            priority: 'Lehentasuna',
            assignees: 'Esleitutakoak',
            assignee_search_placeholder: 'Erabiltzaileak bilatu...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Kendu {name}',
            due_date: 'Epemuga',
            start_date: 'Hasiera-data',
            checklist: 'Egiaztapen-zerrenda',
            tags: 'Etiketak',
            add_checklist_item: 'Egiaztapen-zerrenda elementua gehitu',
            validation: {
                title_required: 'Izenburua beharrezkoa da',
                title_max_length: 'Izenburuak 500 karaktere edo gutxiago izan behar du',
                description_max_length: 'Deskripzioak 5000 karaktere edo gutxiago izan behar du',
                due_date_before_start: 'Epemuga hasiera-dataren ondoren izan behar da',
                max_reminders: 'Gehienez 5 oroigarri zereginkoa',
            },
            reminders: {
                title: 'Oroigarriak',
                add: 'Oroigarria gehitu',
                at_due_date: 'Epemugan',
                minutes_before: '{count} minutu lehenago',
                hours_before: '{count} ordu lehenago',
                days_before: '{count} egun lehenago',
                weeks_before: '{count} aste lehenago',
                custom: 'Pertsonalizatua',
                channel_platform: 'Plataforma',
                channel_email: 'Posta elektronikoa',
                channel_both: 'Biak',
                offset_label: '{n}. oroigarria: unea',
                channel_label: '{n}. oroigarria: kanala',
                remove_label: 'Kendu {n}. oroigarria',
            },
            workflow: {
                label: 'Lan-fluxua',
                none: 'Bat ere ez (eskuzko egoeraren kudeaketa)',
                steps_count: '{count} urrats',
                preview_label: 'Lan-fluxuaren urratsak',
            },
        },
        errors: {
            fetch_failed: 'Ezin izan dira zereginak kargatu.',
            save_failed: 'Ezin izan da zeregina gorde.',
            delete_failed: 'Ezin izan da zeregina ezabatu.',
            status_change_failed: 'Ezin izan da zereginaren egoera aldatu.',
            assign_failed: 'Ezin izan da zeregina esleitu.',
            comment_failed: 'Ezin izan da iruzkina gorde.',
            checklist_failed: 'Egiaztapen-zerrenda eguneratzeak huts egin du.',
            dependency_failed: 'Mendekotasuna eguneratzeak huts egin du.',
            workflow_failed: 'Ezin izan da lan-fluxua gorde.',
            workflow_activate_failed: 'Ezin izan da lan-fluxua aktibatu.',
            workflow_delete_failed: 'Ezin izan da lan-fluxua ezabatu.',
            template_install_failed: 'Ezin izan da txantiloia instalatu.',
            search_failed: 'Ezin izan dira zereginak bilatu.',
            convert_failed: 'Ezin izan da lan-fluxua exekutatzen hasi.',
            convert_quota_exhausted: 'Token ontzi txikia. Kargatu berriro exekutatu aurretik.',
        },
        dependency_types: {
            blocks: 'Blokeatzen du',
            blocked_by: 'Honek blokeatua',
            related: 'Erlazionatua',
        },
        workflow: {
            current_step: 'Uneko urratsa',
            transition: 'Trantsizioa',
            history: 'Urratsen historia',
            no_workflow: 'Lan-fluxurik esleituta ez',
            detail_title: 'Lan-fluxuaren xehetasunak',
            list_title: 'Lan-fluxuen definizioak',
            create_workflow: 'Lan-fluxua sortu',
            browse_templates: 'Txantiloiak arakatu',
            template_catalog_title: 'Lan-fluxuen txantiloi-katalogoa',
            name: 'Izena',
            description: 'Deskripzioa',
            task_types: 'Zeregin-motak',
            steps: 'Urratsak',
            version: 'Bertsioa',
            created: 'Sortua',
            actions: 'Ekintzak',
            status_draft: 'Zirriborroa',
            status_active: 'Aktiboa',
            status_archived: 'Artxibatua',
            activate: 'Aktibatu',
            archive: 'Artxibatu',
            save: 'Gorde',
            edit: 'Editatu',
            delete: 'Ezabatu',
            clone: 'Klonatu',
            install: 'Instalatu',
            installed: 'Instalatua',
            system: 'Sistema',
            general_settings: 'Ezarpen orokorrak',
            default_assignment: 'Esleipen lehenetsia',
            default_sla: 'SLA lehenetsia',
            workflow_steps: 'Lan-fluxuaren urratsak',
            add_step: 'Urratsa gehitu',
            step_name: 'Urratsaren izena',
            step_status: 'Egoera-balioa',
            assignment_strategy: 'Esleipen-estrategia',
            assignment_role: 'Esleipen-rola',
            sla_hours: 'SLA orduak',
            sla_calendar_type: 'Egutegi-mota',
            working_days: 'Lan-egunak',
            calendar_days: 'Egutegi-egunak',
            is_terminal: 'Terminal urratsa',
            required_fields: 'Beharrezko eremuak',
            checklist_template: 'Egiaztapen-zerrenda txantiloia',
            transitions: 'Trantsizioak',
            add_transition: 'Trantsizioa gehitu',
            from_step: 'Nondik',
            to_step: 'Nora',
            permission: 'Baimena',
            sla_action: 'SLA ekintza',
            notify: 'Jakinarazi',
            escalation_rules: 'Eskalada-arauak',
            add_rule: 'Araua gehitu',
            trigger: 'Abiarazlea',
            threshold: 'Atalasea',
            action: 'Ekintza',
            target_role: 'Helburu-rola',
            trigger_sla_threshold: 'SLA atalasea',
            trigger_sla_breach: 'SLA urraketa',
            trigger_blocked_duration: 'Blokeatze-iraupena',
            trigger_manual: 'Eskuzkoa',
            action_notify: 'Jakinarazi',
            action_escalate: 'Eskalatu maila',
            action_reassign: 'Berriro esleitu',
            sla_start: 'Hasi',
            sla_pause: 'Pausatu',
            sla_resume: 'Jarraitu',
            sla_stop: 'Gelditu',
            strategy_manual: 'Eskuzkoa',
            strategy_role_based: 'Roletan oinarritua',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Gutxien kargatua',
            strategy_creator: 'Sortzailea',
            confirm_delete: 'Ziur zaude lan-fluxu hau ezabatu nahi duzula?',
            confirm_archive: 'Ziur zaude lan-fluxu hau artxibatu nahi duzula?',
            cannot_delete_active: 'Lan-fluxu aktiboak ezin dira ezabatu. Artxibatu lehenik.',
            validation_errors: 'Lan-fluxuaren balidazio-erroreak',
            step_count: '{count} urrats',
            no_workflows: 'Ez da lan-fluxurik aurkitu.',
            no_templates: 'Ez dago txantiloirik erabilgarri.',
            sr_templates_count: '{count} txantiloi eskuragarri',
            template_installed: 'Txantiloia ondo instalatua.',
            activate_success: 'Lan-fluxua ondo aktibatua.',
            archive_success: 'Lan-fluxua ondo artxibatua.',
            save_success: 'Lan-fluxua ondo gordeta.',
            delete_success: 'Lan-fluxua ondo ezabatua.',
            applicable_task_types: 'Aplikagarri diren zeregin motak',
            steps_column: 'Urratsak',
            status_value_hint: 'Trantsizioek erabiltzen duten lan-fluxuaren egoera-gakoa. Letra xeheak, zenbakiak eta azpimarrak.',
            from_status: 'Hasierako egoera',
            to_status: 'Helburuko egoera',
            required_permissions: 'Beharrezko baimenak',
            transition_required_fields: 'Beharrezko eremuak (trantsizioa)',
            auto_assign: 'Esleitu automatikoki',
            auto_assign_role: 'Esleipen automatikoaren rola',
            threshold_percent: 'Atalasea (%)',
            blocked_hours: 'Blokeatutako orduak',
            notify_roles: 'Jakinarazi rolei',
            escalate_to_role: 'Eskalatu rolera',
            notification_template: 'Jakinarazpen-txantiloia',
            action_escalate_tier: 'Eskalatu maila',
            create_modal_title: 'Sortu lan-fluxua',
            create_modal_description: 'Definitu urratsak, trantsizioak eta eskalatze-arauak. Lan-fluxua zirriborro gisa sortzen da.',
            create_modal_footer_note: 'Zirriborro gisa sortua. Aktibatu grafoak balioztatzen duenean.',
            validation_name_required: 'Lan-fluxuaren izena beharrezkoa da.',
            validation_task_types_required: 'Hautatu aplikagarri den zeregin mota bat gutxienez.',
            validation_min_one_step: 'Gehitu urrats bat gutxienez.',
            validation_min_one_transition: 'Gehitu trantsizio bat gutxienez.',
            validation_step_name_required: 'Urratsaren izena beharrezkoa da.',
            validation_status_value_required: 'Egoera-balioa beharrezkoa da.',
            validation_status_value_duplicate: 'Egoera-balioak bakarra izan behar du urratsen artean.',
            validation_transition_endpoints_required: 'Hautatu hasierako egoera bat eta helburuko egoera bat.',
            validation_transition_unknown_status: 'Trantsizio honek inongo urratsek definitzen ez duen egoera bati egiten dio erreferentzia.',
            validation_sla_positive: 'SLA orduak zero baino handiagoak izan behar dute. Utzi hutsik SLArik ez izateko.',
            validation_threshold_percent_range: 'Atalaseak 0 eta 100 artean egon behar du.',
            validation_blocked_hours_positive: 'Blokeatutako orduak zero baino handiagoak izan behar dute.',
            warning_no_terminal_step: 'Amaierako urratsik ez. Aktibazioak huts egingo du urrats bat amaierako gisa markatu arte.',
            warning_step_no_outgoing: 'Irteerako trantsiziorik gabeko amaierakoak ez diren urratsak: {statuses}',
            warning_unreachable_steps: 'Lehen urratsetik iritsi ezin diren urratsak: {statuses}',
            orphaned_transitions_warning: '{count} trantsiziok ezabatutako urrats bati egiten diote erreferentzia.',
            remove_orphaned_transitions: 'Kendu trantsizio umezurtzak',
            transitions_renamed_announcement: '{count} trantsizio egoera-balio berrira eguneratu dira.',
            add_checklist_item: 'Gehitu egiaztapen-zerrendako elementua',
            checklist_item_label: 'Egiaztapen-zerrendako elementua',
        },
        columns: {
            priority: 'Lehentasuna',
            title: 'Izenburua',
            status: 'Egoera',
            type: 'Mota',
            assignees: 'Esleitutako(ak)',
            due_date: 'Epemuga',
            source: 'Jatorria',
            actions: 'Ekintzak',
        },
        groups: {
            overdue: 'Atzeratuak',
            due_today: 'Gaur epemugatzen da',
            due_this_week: 'Aste honetan epemugatzen da',
            due_later: 'Geroago epemugatzen da',
            no_due_date: 'Epemugarik gabe',
            closed: 'Osatuak edo bertan behera utziak',
        },
        board: {
            title: 'Zereginen taula',
            drag_hint: 'Arrastatu zereginak zutabeen artean egoera aldatzeko',
            empty_column: 'Zereginik gabe',
            keyboard_hint: 'Teklatua: gezi-teklak nabigatzeko, zuriunea ataza hartzeko, ezker/eskuin geziak zutabea hautatzeko, Enter jaregiteko, Escape bertan behera uzteko.',
            sr_moving: '{title} mugitzen. {column} gainean. Sakatu Enter jaregiteko, Escape bertan behera uzteko.',
            sr_tasks_shown: '{count} zeregin erakutsita',
            sr_picked_up: '{title} hartua',
            sr_returned: '{title} {column}-ra itzuli da',
            sr_dropped: '{title} {column} zutabean jaregina, {position}/{total} posizioa',
            sr_move_cancelled: 'Mugimendua bertan behera utzia',
            sr_over_column: '{column} gainean',
            sr_moved_to: '{title} mugituta {column} zutabera',
            invalid_transition: 'Ezin da ataza {from} egoeratik {to} egoerara mugitu.',
            terminal_status: 'Ataza hau amaierako egoeran dago eta ezin da mugitu.',
            invalid_drop_target: 'Helmuga baliogabea',
        },
        team: {
            title: 'Taldearen lan-karga',
            assignee: 'Esleitutakoa',
            active: 'Aktiboa',
            overdue: 'Atzeratuak',
            blocked: 'Blokeatuta',
            completed_7d: 'Amaitu (7d)',
            load: 'Karga',
            load_high: 'Altua',
            load_medium: 'Ertaina',
            load_normal: 'Normala',
        },
        graph: {
            title: 'Mendekotasun grafikoa',
            list_view: 'Zerrenda Ikuspegia',
            graph_view: 'Ikuspegi grafikoa',
            zoom_in: 'Handitu',
            zoom_out: 'Txikiagotu',
            reset_zoom: 'Berrezarri',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Ez dago menpekotasunik ikusteko.',
        },
        add_dependency_modal: {
            title: 'Mendekotasuna gehitu',
            search_placeholder: 'Bilatu zereginak izenburuaren edo IDaren arabera...',
            no_results: 'Ez da zereginik aurkitu.',
            this_blocks: 'Zeregin honek aukeratutako blokeak blokeatzen ditu',
            this_blocked_by: 'Zeregin hau hautatutakoek blokeatuta dago',
            related: 'Erlazionatua',
            max_dependencies: 'Gehienez 10 menpekotasun zeregin bakoitzeko.',
            circular_error: 'Ezin da gehitu: mendekotasun zirkular bat sortuko luke.',
        },
        conflict_detection: {
            title: 'Gatazka-detekzioa',
            unresolved: 'ebatzi gabe',
            no_conflicts: 'Ez da gatazkik antzeman',
            section: 'Atala',
            field: 'Eremua',
            dept_a: 'A Departamentua',
            value_a: 'A Balioa',
            dept_b: 'B Departamentua',
            value_b: 'B Balioa',
            severity: 'Larritasuna',
            status: 'Egoera',
            resolved: 'Ebatzita',
            open: 'Irekia',
            resolve: 'Ebatzi',
            load_failed: 'load conflicts huts egin du',
            resolve_success: 'Gatazka ondo ebatzia',
            resolve_failed: 'resolve conflict huts egin du',
            severity_critical: 'Kritikoa',
            severity_warning: 'Abisua',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Finkatze-aurrerapena',
            add_child: 'Gehitu azpiataza',
            child_task_id: 'Azpiataza-IDa',
            department_id: 'Departamentuaren IDa',
            link: 'Estekatu',
            overall_completion: 'Osatze orokorra',
            departments_complete: 'Departamentu osatuak',
            overdue: 'Epemuga gainditua',
            ready: 'Prest',
            not_ready: 'Ez dago prest',
            master_status: 'Egoera nagusia',
            department_breakdown: 'Departamentuen banaketa',
            department: 'Departamentua',
            status: 'Egoera',
            completion: 'Osatzea',
            due_date: 'Epemuga-data',
            overdue_flag: 'Epemuga gainditua',
            child_tasks: 'Azpiatazak',
            task: 'Ataza',
            load_failed: 'load consolidation data huts egin du',
            child_linked: 'Azpiataza ondo lotua',
            link_failed: 'link child task huts egin du',
            confirm_unlink: 'Ziur zaude azpiataza honen lotura kendu nahi duzula?',
            child_unlinked: 'Azpiatazaren lotura kendu da',
            unlink_failed: 'Azpiatazaren lotura kentzeak huts egin du',
            unlink: 'Lotura kendu',
            status_completed: 'Osatua',
            status_in_progress: 'Abian',
            status_pending: 'Zain',
            select_child_task: 'Hautatu azpiataza',
            select_department: 'Hautatu saila',
        },
        section_assignments: {
            title: 'Atal-esleipenak',
            assign_section: 'Esleitu atala',
            section_ref: 'Atal-erreferentzia',
            section_label: 'Atal-etiketa',
            department_id: 'Departamentuaren IDa',
            department: 'Saila',
            assignee_id: 'Esleitutakoaren IDa',
            assignee: 'Esleipenduna',
            mandatory: 'Derrigorrezkoa',
            assign: 'Esleitu',
            no_assignments: 'Ez dago atal-esleipenik',
            status: 'Egoera',
            completion: 'Osatzea',
            mark_complete: 'Osatu',
            load_failed: 'load section assignments huts egin du',
            assign_success: 'Atala ondo esleitua',
            assign_failed: 'assign section huts egin du',
            status_updated: 'Egoera eguneratua',
            status_update_failed: 'Egoera eguneratzeak huts egin du',
            section_ref_placeholder: 'adib. 3.2',
            status_completed: 'Osatua',
            status_in_progress: 'Eraikitzen',
            status_pending: 'Zain',
            select_department: 'Aukeratu saila',
            select_assignee: 'Hautatu esleipenduna',
        },
        overdue_columns: {
            days_overdue: 'Egun atzeratuta',
        },
        regulatory_info: {
            deadline_context: 'DPIA zereginak sortzean, epe-datek PDPL aurkezte-epemugekin bat etorri behar dute: sail-sarrera = 10 lanegun. Zibersegurtasun eta telekomunikazio-epemugak ere aplikatzen dira.',
            overdue_deadline_context: 'Araudi-epemugak pakete-datuetatik: PDPL sail-sarrera = 10 lanegun, erantzun osagarria = 15 lanegun, berbidaltzea = 10 lanegun. SLA hautsitako zereginak bisualki bereizten dira.',
            section_assignment_context: 'DPIA sail anitzeko lan-fluxua (PDPL-P1): DPOak dosier-atalak sailei esleitzen dizkie. Sail-sarrera epemuga: 10 lanegun.',
            template_catalog_context: 'Sistema-txantiloiek esparru-prozesu espezifikoak estaltzen dituzte: PDPL DPIA, Datu Legea egiaztagiria, zibersegurtasun-erantzuna, telekomunikazio-agintaritza eskaerak eta AI sistema erregistroa. Sistema-txantiloiak ez dira editagarriak.',
            workflow_instantiation_context: 'Instalatutako txantiloiak zuzeneko lan-fluxuetan instantzia daitezke. Zereginak automatikoki sortzen dira kalkulatutako epe-datekin.',
        },
        overdue: {
            days_overdue: 'egun atzeratuta',
        },
    },
    notifications: {
        bell: {
            title: 'Jakinarazpenak',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Jakinarazpenak',
            mark_all_read: 'Markatu dena irakurrita bezala',
            no_notifications: 'Ez dago jakinarazpenik',
            filter: {
                all: 'All',
                unread: 'Irakurri gabe',
                tasks: 'Zereginak',
                approvals: 'Onarpenak',
                alerts: 'Alertak',
            },
        },
        preferences: {
            title: 'Jakinarazpenen hobespenak',
            description: 'Konfiguratu jakinarazpenak nola jasotzen dituzun',
            category: {
                label: 'Kategoria',
                task_assigned: 'Esleitutako zeregina',
                task_sla_approaching: 'SLA Hurbiltzen',
                task_overdue: 'Ataza atzeratuta',
                task_completed: 'Ataza amaituta',
                task_escalated: 'Egiteko igoera',
                workflow_transition: 'Lan-fluxuaren trantsizioa',
                approval_requested: 'Onarpena eskatu da',
                comment_added: 'Iruzkina gehitu da',
                weekly_digest: 'Asteko laburpena',
                sla_breach_report: 'SLA Urratze-txostena',
                system_alert: 'Sistemaren alerta',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plataforma',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Ordu lasaiak',
                start: 'Hasi',
                end: 'End',
                timezone: 'Ordu-eremua',
            },
            digest: {
                title: 'Digestio-maiztasuna',
                daily: 'Egunero',
                weekly: 'Asterokoa',
                none: 'Bat ere ez',
            },
            watched: {
                title: 'Ikusitako Entitateak',
                empty: 'Ez dago ikusitako entitaterik',
            },
            save: 'Gorde Hobespenak',
            saving: 'Gordetzen...',
            saved_success: 'Hobespenak behar bezala gorde dira',
            load_error: 'Ezin izan dira kargatu gordetako hobespenak. Lehenetsiak erakusten: aldaketak gordeko dira Gorde sakatzen duzunean.',
            enabled: 'Gaituta',
            disabled: 'Ezindua',
            unwatch: 'Desikusi',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Jakinarazpenen administrazio-ilara',
            description: 'Posta elektronikoen bidalketa-egoera kontrolatu eta huts egindako mezuak berriro saiatu',
            load_error: 'Ezin izan dira posta-ilarako datuak kargatu',
            retry_error: 'Ezin izan da posta elektronikoa berriro saiatu',
            filter_status: 'Egoeraren arabera iragazi',
            all_statuses: 'Denak',
            refresh: 'Freskatu',
            status_queued: 'Ilaran',
            status_sent: 'Bidalita',
            status_failed: 'Huts eginda',
            status_bounced: 'Erreboteatuta',
            status_sending: 'Sending',
            col_recipient: 'Hartzailea',
            col_category: 'Kategoria',
            col_template: 'Txantiloia',
            col_status: 'Egoera',
            col_retries: 'Berrsaiakerak',
            col_created: 'Sortua',
            col_sent: 'Bidalitako data',
            col_actions: 'Ekintzak',
            no_items: 'Ez da posta-ilarako elementurik aurkitu',
            retry: 'Berriro saiatu',
            retrying: 'Berriro saiatzen...',
            prev: 'Aurrekoa',
            next: 'Hurrengoa',
            access_denied_title: 'Administratzaile-sarbidea beharrezkoa',
            access_denied: 'Orri hau administratzaileentzat soilik dago eskuragarri. Jarri harremanetan zure administratzailearekin sarbidea behar baduzu.',
            announce_queue_loaded: 'Posta elektronikoaren ilara kargatu da, {count} elementu',
            announce_processed: '{count} mezu elektroniko prozesatu dira',
            process_now: 'Prozesatu orain',
            processing_ellipsis: 'Prozesatzen…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI Laguntzailea',
            activate: 'Aktibatu',
            deactivate: 'Desaktibatu',
            connected: 'Konektatuta',
            disconnected: 'Deskonektatuta',
            disconnected_hint: 'Deskonektatuta — exekutatu `npm run relay` Claude CLIra konektatzeko',
            open_panel: 'Ireki UI Assist panela',
            history: 'Historia',
            settings: 'Ezarpenak',
        },
        prompt: {
            placeholder: 'Deskribatu egin nahi duzun aldaketa...',
            send: 'Bidali',
            cancel: 'Utzi',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Prozesatzen…',
            complete: 'Osatu',
            error: 'Errorea',
            abort: 'Abortatu',
            abort_confirm: 'Ziur abortatu nahi duzula?',
            aborted_hint: 'Saioa eten egin zen — hemendik aurrera jarraitu dezakezu.',
            undo: 'Desegin',
            redo: 'Berregin',
            retry: 'Saiatu berriro',
            dismiss: 'Baztertu',
        },
        label: {
            component: 'Osagaia',
            element: 'Elementua',
            file: 'Fitxategia',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Oraindik ez dago mezurik. Hautatu elementuak eta bidali gonbita hasteko.',
        },
        panel: {
            tab_conversation: 'Txateatu',
            tab_tasks: 'Zereginak',
            tab_sprint: 'Sprinta',
            tab_sessions: 'Saioak',
            tab_lobby: 'Atondoa',
            follow_up: 'Jarraipena',
            followup_placeholder: 'Egin ondorengo galdera...',
            interrupt_placeholder: 'Idatzi eteteko eta birbideratzeko…',
            send_interrupt: 'Bidali eta Eten',
            will_interrupt: 'Uneko erantzuna bertan behera utziko du eta hau bidaliko du horren ordez',
            expand: 'Zabaldu pantaila osora',
            collapse: 'Tolestu alboko panelera',
            no_sessions: 'Ez dago aurreko saiorik. Hautatu elementuak eta bidali mezua hasteko.',
            session_active: 'Aktiboa',
            session_ended: 'Amaitua',
            session_messages: '{count} mezu',
            switch_session: 'Aldatu saio honetara',
            load_session: 'Kargatu saioaren historia',
            session_restored: 'Historiatik berreskuratutako saioa',
            archive_session: 'Artxibo saioa',
            archive_confirm: 'Saio hau artxibatu nahi duzu? Saioaren fitxategia ezabatu egingo da.',
            new_session: 'Saio Berria',
            new_session_desc: 'Erabili Aktibatu UI elementu bat hautatzeko eta saio berri bat hasteko',
            start_new_session: 'Hasi saio berria',
            no_active_session_chat: 'Hasi saio berri bat edo erabili Aktibatu hasteko orrialdeko UI elementu bat hautatzeko.',
            no_active_session_tasks: 'Hasi saio berri bat edo hautatu lehendik dagoen saio bat zereginak sortzeko.',
            no_active_session_sprint: 'Hasi saio berri bat edo hautatu lehendik dagoen saio bat esprint bat antolatzeko.',
            previous_sessions: 'Aurreko saioak',
        },
        tasks: {
            none_found: 'Ez da zereginik aurkitu erantzunean. Eskatu Clauderi zereginak proposatzeko.',
            selected: 'selected',
            create_selected: 'Sortu Zereginak',
            execute_tasks: 'Atazak gauzatu',
            creating: 'Sortzen…',
            created: 'sortutako zereginak',
            create_error: 'Ezin izan da sortu zeregina',
            plan_sprint: 'Plan Sprint',
            priority_critical_desc: 'Blokeatzailea — berehala konpondu',
            priority_high_desc: 'Garrantzitsua — sprint honetan konpondu',
            priority_medium_desc: 'Normala — hurrengo sprinterako planifikatu',
            priority_low_desc: 'Komenigarria — backlog hautagaia',
            add_custom: 'Gehitu ataza',
            add_title_placeholder: 'Atazaren izenburua…',
            add_desc_placeholder: 'Deskribapena (aukerazkoa)',
            add_save: 'Gehitu',
            add_cancel: 'Utzi',
        },
        sprint: {
            title: 'Sprint Planifikatzailea',
            assigned: 'assigned',
            new_placeholder: 'Sprint izena (adibidez, Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Atzerapena',
            move_to: 'Mugitu hona...',
            empty: 'Sortu esprint talde bat eta esleitu hari zereginak.',
            create_all: 'Sortu Zeregin guztiak',
            remove: 'Kendu',
            drop_here: 'Esleitu zereginak beheko atzerapenetik',
            add_task: 'Gehitu zeregina atzerapenetik…',
        },
        feature_disabled: 'UI Assist ez dago gaituta',
        permission_denied: 'Ez duzu baimenik UI Assist erabiltzeko',
    },
    license: {
        title: 'Lizentzia eta paketea',
        current_license: 'Egungo Lizentzia',
        license_key: 'Lizentzia-gakoa',
        activate: 'Aktibatu Lizentzia',
        deactivate: 'Desaktibatu Lizentzia',
        validate_now: 'Baliozkotu orain',
        status: 'Lizentziaren egoera',
        active: 'Aktiboa',
        expired: 'Iraungi egin da',
        suspended: 'Etenda',
        invalid: 'Baliogabea',
        pending: 'Zain',
        no_license: 'Lizentziarik ez',
        expires_at: 'Iraungitzen da',
        last_validated: 'Azken balioztatua',
        domain: 'Lizentziadun Domeinua',
        activation_success: 'Lizentzia behar bezala aktibatu da.',
        activation_failed: 'Ezin izan da aktibatu lizentzia.',
        domain_mismatch: 'Lizentzia-domeinua ez dator bat uneko domeinuarekin.',
        deactivation_success: 'Lizentzia desaktibatuta dago.',
        validation_success: 'Lizentzia behar bezala balioztatu da.',
        validation_failed: 'Lizentzia baliozkotzeak huts egin du.',
        enter_key: 'Sartu zure lizentzia-gakoa',
        confirm_deactivate: 'Ziur lizentzia hau desaktibatu nahi duzula? Moduluaren murrizketak maila lehenetsiaren arabera aplikatuko dira.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Paketea',
        module_column: 'Modulua',
        current_tier: 'Egungo paketea',
        light: 'Argia',
        standard: 'Estandarra',
        enterprise_unlimited: 'Enpresa mugagabea',
        change_package: 'Aldatu paketea',
        upgrade: 'Berritu',
        downgrade: 'Jaitsi',
        target_tier: 'Helburu paketea',
        new_license_key: 'Helburu paketearen lizentzia-gakoa',
        confirm_change: 'Berretsi pakete aldaketa',
        change_success: 'Paketea behar bezala aldatu da.',
        change_failed: 'Pakete aldaketak huts egin du.',
        modules_included: 'Moduluak barne',
        modules_restricted: 'Ez dago eskuragarri oraingo paketean',
        upgrade_to_enable: 'Eguneratu modulu hau gaitzeko.',
        module_availability: 'Moduluaren erabilgarritasuna',
        comparison: 'Paketeen Konparazioa',
        power_users: 'Erabiltzaile boteretsuak',
        unlimited: 'Mugagabea',
        impact_warning: 'Bertsioak behera egiteak modulu hauek desgaituko ditu:',
        invalid_license: 'Emandako lizentzia-gakoak ez du balio pakete honetarako.',
        tier_mismatch: 'Lizentzia-gakoa pakete-maila ezberdin baterako da.',
    },
    board_report: {
        title: 'Betetze-txostena Administrazio Kontseilurako',
        subtitle: 'Betetzearen laburpen exekutiboa modulu guztietan',
        date_from: 'Hasiera data',
        date_to: 'Amaiera data',
        language: 'Txostenaren hizkuntza',
        generate: 'Txostena sortu',
        generating: 'Txostena sortzen...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'PDF deskargatu',
        close: 'Txostena itxi',
        open_board_report: 'Kontseiluko txostena sortu',
        close_board_report: 'Kontseiluko txostena itxi',
        report_period: 'Txosten aldia',
        generated_at: 'Sortua',
        generated_by: 'Sortzailea',
        organization: 'Erakundea',
        section_rights_requests: 'Eskubide-eskaerak',
        section_vendors: 'Hornitzaileak',
        section_dpias: 'DBEB-ak',
        section_consent: 'Baimena politikak',
        section_data_mapping: 'Datu-mapaketa',
        section_incidents: 'Gertaerak',
        section_classification: 'Datuen sailkapena',
        section_governance: 'Gobernantza',
        open: 'Irekia',
        closed: 'Itxia',
        sla_compliance: 'SLA-betetze',
        total: 'Guztira',
        expired_dpa: 'Iraungitako DPA',
        high_risk: 'Arrisku handia',
        completed: 'Osatua',
        overdue: 'Atzeratua',
        policies_active: 'Politika aktiboak',
        coverage: 'Estaldura',
        systems: 'Sistemak',
        completeness: 'Osotasuna',
        avg_resolution_days: 'Batez besteko ebazpen-denbora (egunak)',
        labeled: 'Etiketatua',
        unlabeled_systems: 'Etiketatu gabeko sistemak',
        compliance_score: 'Betetze-puntuazioa',
        drift_events_open: 'Desbideratze irekiak',
        no_data: 'Ez dago daturik aukeratutako aldirako.',
        error_date_range: 'Mesedez, aukeratu hasiera- eta amaiera-data.',
        table_category: 'Kategoria',
        table_metric: 'Metrika',
        table_value: 'Balioa',
        showing_chart_view: 'Grafiko-ikuspegia erakusten',
        showing_table_view: 'Datu-taularen ikuspegia erakusten',
        error_generate: 'Kontseiluko txostena ezin izan da sortu.',
        playbook_evidence_title: 'Playbook-en exekuzio ebidentzia',
        playbook_evidence_description: 'Playbook bakoitzeko exekuzio-jarduera eta erantsitako ebidentzia-fitxategiak txostenaren aldirako.',
        playbook_name: 'Jolas liburua',
        playbook_execution_count: 'Exekuzioak',
        playbook_steps_total: 'Urratsak',
        playbook_steps_required: 'Ebidentzia beharrezkoa',
        playbook_steps_attached: 'Ebidentzia erantsita',
        playbook_coverage_pct: 'Estaldura',
        playbook_evidence_files: 'Ebidentzia-fitxategiak',
        playbook_no_evidence: 'Aldi honetan ez dago playbook exekuziorik.',
        playbook_download_file: 'Deskargatu fitxategia',
    },
    framework_module_links: {
        title: 'Arau-esparruaren Lotura',
        modules: 'Lotutako Moduluak',
        add_module: 'Modulu bat Gehitu',
        module_code: 'Moduluaren Kodea',
        required: 'Nahitaezkoa',
        notes: 'Oharrak',
        link: 'Lotu',
        no_modules: 'Ez dago modulurik lotuta',
        personas: 'Lotutako Pertsonak',
        add_persona: 'Pertsona bat Gehitu',
        persona_id: 'Pertsonaren IDa',
        role_description: 'Rolaren Deskribapena',
        no_personas: 'Ez dago pertsonarik lotuta',
    },
    framework_channels: {
        title: 'Aurkezpen Kanalak & Epeak',
        channels: 'Aurkezpen Kanalak',
        add_channel: 'Kanala Gehitu',
        channel_type: 'Mota',
        name: 'Izena',
        instructions: 'Jarraibideak',
        active: 'Aktibo',
        no_channels: 'Ez dago aurkezpen kanalik konfiguratuta',
        portal: 'Ataria',
        in_person: 'Aurrez Aurre',
        postal: 'Posta',
        email: 'Posta elektronikoa',
        add: 'Gehitu',
        deadlines: 'Araudiko Epeak',
        add_deadline: 'Epea Gehitu',
        label: 'Etiketa',
        days: 'Egunak',
        day_type: 'Egun Mota',
        calendar_days: 'Egutegi-egunak',
        working_days: 'Lan-egunak',
        description: 'Deskribapena',
        no_deadlines: 'Ez dago eperik konfiguratuta',
    },
    regulatory_updates: {
        title: 'Araudi Eguneratzeak & Arau-esparruaren Aginte-panela',
        coverage_dashboard: 'Arau-esparruaren Estaldura-panela',
        no_frameworks: 'Ez da arau-esparrurik aurkitu',
        framework: 'Arau-esparrua',
        status: 'Egoera',
        modules: 'Moduluak',
        templates: 'Txantiloiak',
        forms: 'Inprimakiak',
        channels: 'Kanalak',
        deadlines: 'Epeak',
        pending: 'Eguneratze Iraunkorrak',
        updates: 'Araudi Eguneratzeak',
        create_update: 'Eguneratze bat Sortu',
        framework_id: 'Arau-esparruaren IDa',
        update_title: 'Izenburua',
        description: 'Deskribapena',
        effective_date: 'Indarrean Jartzeko Data',
        create: 'Sortu',
        no_updates: 'Ez dago araudi eguneratzerik',
        impact: 'Eragina',
        assess: 'Ebaluatu',
    },
    module_tips: euModuleTips,
    framework_rights: {
        be_informed: 'Informatua izateko eskubidea',
        consent: 'Baimena emateko eskubidea',
        refuse_consent: 'Baimena ukatzeko eskubidea',
        withdraw_consent: 'Baimena kentzeko eskubidea',
        access: 'Sarbide eskubidea',
        rectification: 'Zuzentzeko eskubidea',
        data_provision: 'Datuak emateko eskubidea',
        deletion: 'Ezabatzeko eskubidea',
        restriction_of_processing: 'Tratamendua mugatzeko eskubidea',
        object_to_processing: 'Tratamenduari aurka egiteko eskubidea',
        complaint: 'Erreklamazioa jartzeko eskubidea',
        denunciation: 'Salaketa egiteko eskubidea',
        lawsuit: 'Akzio legala jartzeko eskubidea',
        claim_damages: 'Kalte-ordainak eskatzeko eskubidea',
        request_protection_measures: 'Babes-neurriak eskatzeko eskubidea',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: '2002/58/EE Zuzentaraua (ePrivacy Zuzentaraua)',
            description: 'Pribatutasun elektronikoaren transposizio nazionalen zuzentarau gurasoa. Cookieak, komunikazio elektronikoen pribatutasuna eta marketin zuzeneko gaiak hartzen ditu bere baitan. Estatu kide bakoitzak bere lege nazionalean txertatu behar du.',
        },
        EU_GDPR: {
            display_name: 'Datuen Babeserako Erregelamendu Orokorra (EB) 2016/679',
            description: 'Datuen Babeserako Erregelamendu Orokorra (DBEO/RGPD) datuen babesari eta pribatutasunari buruzko Europar Batasuneko erregelamendua da. Zuzenean aplikatzen da EBko/EEEko estatu kide guztietan.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: '(EB) 2022/2555 Zuzentaraua (NIS2)',
            description: 'NIS2 transposizio nazionalen zuzentarau gurasoa. Zibersegurtasunaren arrisku-kudeaketa, gertakarien jakinarazpena eta hornidura-katearen segurtasuna hartzen ditu bere baitan, entitate funtsezkoentzat eta entitate garrantzitsuentzat.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnamgo AA Legea (Luật Trí tuệ nhân tạo 134/2025/QH15) — 2026/03/01ean indarrean',
            description: 'Vietnamgo Adimen Artifizialari buruzko Legea (Luật Trí tuệ nhân tạo 134/2025/QH15), adimen artifizialeko sistemen garapena, hedapena eta erabilera arautzen dituena. Arrisku-sailkapena, gardentasun- eta etiketatze-betebeharrak, gertakariak jakinarazteko eskakizunak, eraginaren ebaluazio-betebeharrak eta Vietnameko AA jardueretarako probaketa-mekanismo kontrolatua (sandbox) ezartzen ditu.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnamgo Zibersegurtasun Legea (24/2018 garaikoa)',
            description: 'Vietnamgo Zibersegurtasun Legea (Luật An ninh mạng 24/2018/QH14), zibersegurtasun-eskakizunak, datuen lokalizazioa eta gertakarien jakinarazpena ezartzen dituena. Indarrean dago Luật 116/2025/QH15ek (2026/07/01ean indarrean) ordeztu arte.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: '53. dekretua — Zibersegurtasun Legearen garapena',
            description: '53/2022/NĐ-CP Dekretua, Zibersegurtasun Legea garatzen duena: aplikazio-eremuaren zehaztapena, datuak lokalizatzeko eragingailuak, bertako presentziaren betebeharrak eta agintaritzarekin lankidetzan jarduteko prozedurak biltzen ditu.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Zibersegurtasun Legea (Luật 116/2025/QH15)',
            description: 'Vietnamgo Zibersegurtasun Lege bateratua (Luật 116/2025/QH15), Luật 24/2018/QH14 ordezten duena, 2026ko uztailaren 1ean indarrean. Decree 53/2022/NĐ-CParen aplikazio-eremua biltzen du (ikus VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Vietnamgo Datuen Legea',
            description: 'Vietnamgo Datuen Legea (Luật 60/2024/QH15), datuen gobernantza-eskakizun integralak ezartzen dituena: datuen inbentarioa, sailkapena, partekatzearen gobernantza eta mugaz haraindiko datu-transferentzien gobernantza. VN_PDPL_LAW_2025 (datu pertsonalei buruzkoa) eta VN_CYBERSEC_LAW_2025 (datuen lokalizazioa/sailkapena) legearekin gainjartzen da.',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: '165/2025/NĐ-CP Dekretua (Datuen Legearen garapena)',
            description: '165/2025/NĐ-CP Dekretua, Vietnamgo Datuen Legea (Luật 60/2024/QH15) garatzen duena. Mugaz haraindiko datu-transferentziaren eraginaren ebaluaziorako, urteko arrisku-ebaluazioa jakinarazteko eta datu-base nazionaletara sartzeko inprimaki ofizialak eskaintzen ditu. 11 inprimaki ofizial (Mẫu số) zehazten ditu.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Datu-trukeei buruzko Dekretu Zirriborroa 2026',
            description: 'Datu-truke plataformen funtzionamenduari buruzko dekretu zirriborroa (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Plataformen sailkapena, parte-hartzaileen hautagarritasuna, datu-produktu eta zerbitzuen zerrendatze-kontrolak, sandbox probak, transakzio-fluxua, kontratazio elektronikoa, datuen jabetza-eskubideen ziurtagiriak eta erreklamazio/auzi-kudeaketa arautzen ditu. Une honetan kontsulta publikoan dago; oraindik ez da promulgatu. Aipatzen ditu: Datuen Legea 60/2024/QH15, Datu Pertsonalen Babeserako Legea 91/2025/QH15, Zibersegurtasun Legea 116/2025/QH15 eta Transakzio Elektronikoen Legea 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Merkataritza Elektronikoari buruzko Legea (Luật 122/2025/QH15)',
            description: 'Vietnamgo Merkataritza Elektronikoari buruzko Legea (Luật Thương mại điện tử 122/2025/QH15), merkataritza elektronikorako araubide nazional bateratua ezartzen duena: barneko eta mugaz haraindiko plataformen funtzionamendua, plataformen gobernantza eta erantzukizuna, kontsumitzailearen babesa, saltzaileen eta afiliatuen kontrolak, erreklamazioen kudeaketa, edukien moderazioa eta kentze-betearazpena, eta merkataritza elektronikoa kudeatzeko sistema nazionalarekiko prestakuntza administratiboa.',
        },
        VN_PDPD_DECREE_13: {
            display_name: '13/2023/NĐ-CP Dekretua',
            description: 'Nghị định 13/2023/NĐ-CP, PDPLren betebeharretarako tresna operazionala: dosierraren aurkezpena, gobernantza-objektuak, mugaz haraindiko mekanismoak eta MoPSren prozedura administratiboak.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Datu Pertsonalen Babeserako Legea (Luật 91/2025/QH15)',
            description: 'Vietnamgo Datu Pertsonalen Babeserako Legea (Luật 91/2025/QH15), 2026ko urtarrilaren 1ean indarrean datu pertsonalen babeserako esparru integrala ezartzen duena. A–J betetze-gaiak biltzen ditu: tratamendurako oinarri legalak, datu sentikorretarako baimena, datu-subjektuaren eskubideak (sarbidea/zuzenketa/ezabatzea/murrizpena/aurkaratzea/eramangarritasuna), agintaritza eta datu-subjektuei urraketa-jakinarazpena, arrisku handiko tratamendurako DPIA, mugaz haraindiko transferentzien gobernantza, DPOren izendapena, tratamendu-jardueren erregistroak eta tratamendu-arduradunen kontratu-betebeharrak. Agintaritzarekiko aurkezpen-betebeharrak MoPSen bidez (VN_PDPL_ADMIN_PROCEDURES_778_2026). Erreferentzia gurutzatuak: VN_PDPL_DECREE_356_2025 (garapen-dekretua), VN_CYBERSEC_LAW_2025 (zibersegurtasun-gainjartzeak), VN_DATA_LAW (datuen gobernantza).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: '356/2025/NĐ-CP Dekretua (PDPLren garapena)',
            description: '356/2025/NĐ-CP Dekretua, Datu Pertsonalen Babeserako Legea (91/2025/QH15 Legea) garatzen duena. Dosierraren aurkezpena, gobernantza-objektuak, mugaz haraindiko mekanismoak eta zerbitzu-emaileen ziurtatzea biltzen ditu. Eranskin-inprimakien inbentarioa: Phụ lục I–VIII (eraginaren ebaluazioa, mugaz haraindiko transferentzia, ziurtatzea). MoPSren prozedura administratiboak VN_PDPL_ADMIN_PROCEDURES_778_2026 bidez (Erabakia 778/QĐ-BCA-A05). Ziurtagiri-fluxua: eskaera → emisioa → aldaketa-eskaera → berremisioa → ezeztapena. Erreferentzia gurutzatuak: VN_PDPL_LAW_2025 (lege gurasoa), VN_CYBERSEC_LAW_2025 (datuen lokalizazioa), VN_DATA_LAW (datuen gobernantza-gainjartzeak).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Prozedura Administratiboak (Erabakia 778/QĐ-BCA-A05)',
            description: '778/QĐ-BCA-A05 Erabakia (2026ko otsaila) — A05ren (MPS) menpeko datu pertsonalen babeserako prozedura administratiboak. Dosierraren aurkezpen-, izapidetze- eta emaitza-emisio prozedurak zehazten ditu, DLCN-HC inprimakiak eta Mẫu số inprimaki estatutarioak barne.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Betearazpen Dekretu Zirriborroa 2026',
            description: 'Datu Pertsonalen Babeserako Legearen (Luật 91/2025/QH15) urraketetarako betearazpen- eta zigor-dekretu zirriborroa. Zigor-tarteak, larrigarriak/aringarriak eta betearazpen-prozedurak zehazten ditu. Une honetan zirriborro-egoeran dago, promulgazioaren zain.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnamgo Telekomunikazio Legea 2023',
            description: 'Vietnamgo Telekomunikazio Legea 2023 (Luật Viễn thông), telekomunikazio operadore, ISP eta plataformen sektoreko gainjartzea: konfidentzialtasuna, ezagutarazte-kontrola eta agintaritzaren eskaerak izapidetzeko betebeharrak. Erreferentzia gurutzatuak: VN_CYBERSEC_LAW_2025 (informazio-sistemen segurtasuna), VN_PDPL_LAW_2025 (erabiltzaileen datuen tratamendua), VN_DATA_LAW (datuen gobernantza).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: '163/2024/NĐ-CP Dekretua (Telekomunikazio Legearen garapena)',
            description: '163/2024/NĐ-CP Dekretua, Telekomunikazio Legea (Luật 24/2023/QH15) garatzen duena. Telekomunikazio-zerbitzuen jakinarazpenerako, lizentziatzeko, erregistratzeko eta datu-zentroen garapen-planetarako inprimaki ofizialak eskaintzen ditu. 7 inprimaki ofizial (Mẫu số) zehazten ditu.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Erreklamazioak, auziak eta arrisku-kudeaketa',
            description: 'Plataforma-operadoreek erreklamazioen jasotze-bidea, auziak izapidetzeko fluxua frogen kontserbazioarekin, arriskuak identifikatzeko eta abisu goiztiarrerako kontrolak, eta pribatutasun-, zibersegurtasun- eta sarbide-kudeaketa-arriskuen aldizkako berrikuspena eskaini behar dituzte.',
        },
        vn_dp_draft_e: {
            title: 'Transakzio-fluxua eta datuen jabetza-eskubideen ziurtagiria',
            description: 'Transakzioek kontuaren erregistroa, zerrendaren onarpena, kontratu elektronikoa, ordainketaren berrespena, entrega-erregistroa eta, beharrezkoa denean, datuen jabetza-eskubideen ziurtagiria igortzea eskatzen dute. Transakzio-historia eta trazabilitatea transakzio-erregistroan kontserbatu behar dira.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox eta probaketa-ingurunearen kontrolak',
            description: 'Probaketa-inguruneak isolatuak, sarbide-mugatuak, segurtasun-monitorizatuak eta irteera atera aurretik berrikusiak izan behar dira. Probaketa-ingurunetik ezin da iturri-daturik atera, eta probaketa-saioen itxiera/garbiketa erregistratu behar da.',
        },
        vn_dp_draft_c: {
            title: 'Datu-produktu eta -zerbitzuen zerrendatze-kontrolak',
            description: 'Zerrendatzeek jatorri legalaren eta jabetza/erabilera-eskubideen frogak, balidazio teknikoaren erregistroak, kalitate-aitorpenak, eta plataformaren aldetik zerrendatzea berrikusi eta onartzeko erregistroak izan behar dituzte. Debekatutako datuak iragazi behar dira zerrendatu aurretik.',
        },
        vn_dp_draft_b: {
            title: 'Parte-hartzaileen hautagarritasuna eta harrera',
            description: 'Plataforma-operadoreek lege-egoera, jarduteko gaitasuna eta baldintzapeko negozio-eskakizunak egiaztatu behar dituzte saltzaile eta erosleentzat, transakzio- eta ordainketa-kontuak erregistratu, eta parte-hartzaileen erregistroa mantendu.',
        },
        vn_dp_draft_a: {
            title: 'Datu-plataformen sailkapena eta funtzionamendu-eredua',
            description: 'Plataforma-operadoreek plataforma-motaren sailkapena ezarri behar dute (datu-plataforma nazionala vs beste datu-plataformak), operadorearen hautagarritasuna eta erantzukizunak definitu, eta prezioak, kuotak, funtzionamendu-arauak eta gobernantza teknikoa dokumentatu.',
        },
        d165_r5: {
            title: 'Datu-base nazionalaren informaziorako sarbidea',
            description: 'Datu digitalen datu-base nazionaletik informazioa atera/erabiltzeko eskaerek agindutako inprimakiak erabiltzen dituzte (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Urteko arrisku-ebaluazioaren jakinarazpena',
            description: 'Urteko arrisku-ebaluazioaren txostenak agindutako inprimakiekin aurkeztu behar dira (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Mugaz haraindiko transferentzia uzteari buruzko jakinarazpena',
            description: 'Mugaz haraindiko transferentzia uzten denean, agindutako inprimakiekin jakinarazi behar da (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Ebaluazio-emaitzaren jakinarazpena',
            description: 'Agintaritzak ebaluazio-emaitzaren jakinarazpenak igortzen ditu agindutako inprimakia erabiliz (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Mugaz haraindiko datu-transferentziaren eraginaren ebaluazioaren aurkezpena',
            description: 'Erakundeek mugaz haraindiko datu-transferentzia/tratamenduaren eraginaren ebaluazio-dosierrak aurkeztu behar dituzte agindutako inprimakiekin (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Cookien eta antzeko teknologien baimena',
            description: 'Terminal-ekipamendu batean informazioa biltegiratzea edo bertara sartzea aldez aurretiko baimen informatuarekin bakarrik dago baimenduta, teknikoki beharrezkoa den biltegiratzea izan ezik.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Komunikazioen konfidentzialtasuna',
            description: 'Estatu kideek komunikazioen eta horiei lotutako trafiko-datuen konfidentzialtasuna bermatuko dute, komunikazio-sare publiko baten eta jendearen eskura dauden komunikazio elektronikoko zerbitzuen bidez.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Marketin zuzenerako baimena (opt-in)',
            description: 'Komunikazio elektronikoko sistemak marketin zuzenerako erabiltzeko, harpidedunaren aldez aurretiko baimena behar da.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft opt-in lehendik dauden bezeroentzat',
            description: 'Lehendik dauden bezeroei produktu/zerbitzu antzekoetarako marketin zuzena egitea baimenduta dago baimenik gabe, opt-out aukera eskaintzen bada.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Telekomunikazio-hornitzaileen urraketa-jakinarazpena',
            description: 'Jendearen eskura dauden komunikazio elektronikoko zerbitzuen hornitzaileek datu pertsonalen segurtasun-urraketak jakinaraziko dizkiote agintaritza nazional eskudunari.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Marketin zuzenari aurka egiteko eskubidea',
            description: 'Harpidedunei aukera eman behar zaie marketin zuzeneko mezuak jasotzeari edozein unetan eta doan aurka egiteko.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Trafiko-datuen kontserbazioa eta ezabaketa',
            description: 'Harpidedun eta erabiltzaileei lotutako trafiko-datuak ezabatu edo anonimo bihurtu behar dira transmisiorako beharrezko ez direnean, fakturazio- edo interkonexio-ordainketetarako izan ezik.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Kokapen-datuen tratamendurako baldintzak',
            description: 'Trafiko-datuetatik bestelako kokapen-datuak anonimo eginda edo erabiltzailearen baimenarekin soilik trata daitezke, beharrezko neurrian eta iraupenean.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Tratamenduaren zilegitasuna — baimena',
            description: 'Tratamendua zilegi izango da soilik datu-subjektuak bere datu pertsonalak helburu zehatz baterako edo gehiagotarako tratatzeari baimena eman dionean.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Tratamenduaren zilegitasuna — kontratua',
            description: 'Tratamendua beharrezkoa da datu-subjektua alderdia den kontratu bat betetzeko.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Tratamenduaren zilegitasuna — lege-betebeharra',
            description: 'Tratamendua beharrezkoa da tratamenduaren arduradunari aplikatzen zaion lege-betebehar bat betetzeko.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Tratamenduaren zilegitasuna — funtsezko interesak',
            description: 'Tratamendua beharrezkoa da datu-subjektuaren edo beste pertsona fisiko baten funtsezko interesak babesteko.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Tratamenduaren zilegitasuna — interes publikoa',
            description: 'Tratamendua beharrezkoa da interes publikoaren mesedetan edo agintaritza publikoaren ariketan egiten den eginkizun bat betetzeko.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Tratamenduaren zilegitasuna — interes legitimoak',
            description: 'Tratamendua beharrezkoa da tratamenduaren arduradunaren edo hirugarren baten interes legitimoetarako.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Kategoria berezien tratamendua (datu sentikorrak)',
            description: 'Datu pertsonalen kategoria berezien tratamendua (jatorri arrazikoa, iritzi politikoak, osasuna eta abar) debekatuta dago, salbu eta 9.2 art.ko baldintza zehatzak betetzen direnean.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Baimenaren baldintzak',
            description: 'Tratamenduaren arduradunak baimena eman zela frogatu behar du. Baimena modu askean, zehatzean, informatuan eta zalantzarik gabe eman behar da. Baimena kentzea baimena ematea bezain erraza izan behar da.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Adingabearen baimena — zerbitzu digitalak',
            description: '16 urtetik beherako adingabearen (edo estatu kideak ezarritako adin txikiagokoaren, 13 urtetik behera ez) datu pertsonalen tratamenduak gurasoen baimena behar du informazioaren gizarteko zerbitzuetarako.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Informazio eta komunikazio gardena',
            description: 'Tratamenduaren arduradunak tratamenduari buruzko informazioa modu trinko, garden, ulergarri eta erraz eskuragarrian eman behar du.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Bilketa zuzenerako informazioa',
            description: 'Datu pertsonalak datu-subjektuarengandik zuzenean biltzen direnean, tratamenduaren arduradunak zehaztutako informazioa eman behar du bilketa-unean.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Zeharkako bilketarako informazioa',
            description: 'Datu pertsonalak datu-subjektuarengandik lortu ez direnean, tratamenduaren arduradunak zehaztutako informazioa eman behar du epe arrazoizkoan.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Sarbide-eskubidea',
            description: 'Datu-subjektuak tratamenduaren berrespena lortzeko eta datu pertsonaletara eta informazio osagarrira sartzeko eskubidea izango du.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Zuzentzeko eskubidea',
            description: 'Datu-subjektuak datu pertsonal okerrak bidegabeko atzerapenik gabe zuzentzeko eskubidea izango du.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Ezabatzeko eskubidea (ahaztua izateko eskubidea)',
            description: 'Datu-subjektuak datu pertsonalak bidegabeko atzerapenik gabe ezabatzeko eskubidea izango du, zehaztutako arrazoiak betetzen direnean.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Tratamendua mugatzeko eskubidea',
            description: 'Datu-subjektuak tratamendua mugatzeko eskubidea izango du, zehaztutako baldintzak betetzen direnean.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Jakinarazpen-betebeharra — zuzenketa/ezabaketa/mugaketa',
            description: 'Tratamenduaren arduradunak edozein zuzenketa, ezabaketa edo mugaketa jakinaraziko die datuak komunikatu zaizkion hartzaile guztiei.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Datuen eramangarritasunerako eskubidea',
            description: 'Datu-subjektuak bere datu pertsonalak modu egituratuan, ohiko erabilerakoan eta makinaz irakurgarrian jasotzeko eskubidea izango du.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Aurka egiteko eskubidea',
            description: 'Datu-subjektuak interes publikoan edo interes legitimoetan oinarritutako tratamenduaren aurka egiteko eskubidea izango du, profilatzea barne.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Banakako erabaki automatizatuak, profilatzea barne',
            description: 'Datu-subjektuak ondorio juridiko edo antzeko esanguratsuak sortzen dituen tratamendu automatizatuan soilik oinarritutako erabaki baten menpe ez egoteko eskubidea izango du.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Ikuskaritza-agintaritzari segurtasun-urraketaren jakinarazpena (72 ordu)',
            description: 'Datu pertsonalen segurtasun-urraketa kasuan, tratamenduaren arduradunak 72 orduko epean jakinaraziko dio ikuskaritza-agintaritzari, jakitun izan ondoren, salbu eta arriskurik sortzeko probabilitatea txikia denean.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Datu-subjektuei segurtasun-urraketaren jakinarazpena (arrisku handia)',
            description: 'Urraketak eskubide eta askatasunentzako arrisku handia eragiteko probabilitatea duenean, tratamenduaren arduradunak datu-subjektuari komunikatuko dio bidegabeko atzerapenik gabe.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Datu-subjektuen eskaerei erantzuna (30 egun)',
            description: 'Tratamenduaren arduradunak bidegabeko atzerapenik gabe eta jasotzen denetik hilabeteko epean erantzungo die datu-subjektuen eskaerei; epe hori beste bi hilabetez luzatu daiteke eskaera konplexuen kasuan.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Datuen babesaren ondorioei buruzko ebaluazioa',
            description: 'Tratamenduak eskubide eta askatasunentzako arrisku handia eragiteko probabilitatea duenean, tratamenduaren arduradunak DPIA bat egingo du tratamendua hasi aurretik.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Ikuskaritza-agintaritzarekin aurretiazko kontsulta',
            description: 'Tratamenduaren arduradunak ikuskaritza-agintaritzari kontsultatuko dio DPIA batek erakusten duenean tratamenduak arrisku handia sortuko lukeela arintze-neurririk gabe.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA derrigorrezkoa duten tratamenduen zerrenda',
            description: 'DPIA derrigorrezkoa da ebaluazio sistematikoa/profilatzea, kategoria berezien tratamendu masiboa eta esparru publikoen monitorizazio sistematikoa egiten denean.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Tratamendu-jardueren erregistroa (arduraduna)',
            description: 'Tratamenduaren arduradun bakoitzak bere ardurapeko tratamendu-jardueren erregistroa mantenduko du, helburuak, kategoriak, hartzaileak, transferentziak eta gordetze-epeak barne.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Tratamendu-jardueren erregistroa (eragilea)',
            description: 'Tratamendu-eragile bakoitzak tratamenduaren arduradun baten izenean egindako tratamendu-jardueren kategoria guztien erregistroa mantenduko du.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Tratamenduaren segurtasuna',
            description: 'Tratamenduaren arduradunak eta eragileak neurri tekniko eta antolakuntzakoak hartuko dituzte arriskuari egokitutako segurtasuna bermatzeko.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Datuen babesa diseinuz eta lehenetsita',
            description: 'Tratamenduaren arduradunak neurri egokiak ezarriko ditu helburu zehatz bakoitzerako beharrezkoak diren datu pertsonalak bakarrik tratatzen direla bermatzeko (datuen minimizazioa).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Datuen babeserako ordezkariaren izendapena',
            description: 'Tratamenduaren arduradunak eta eragileak DBO bat izendatuko dute, tratamendua agintaritza publiko batek egiten duenean, monitorizazio erregular eta sistematikoa dakarrenean edo kategoria berezien tratamendu masiboa egiten denean.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Transferentzien printzipio orokorra',
            description: 'Datu pertsonalak hirugarren herrialde batera transferitzea soilik baimenduta dago V. kapituluko baldintzak betetzen direnean.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Egokitasun-erabakian oinarritutako transferentziak',
            description: 'Transferentzia bat egin daiteke Batzordeak hirugarren herrialdeak babes-maila egokia bermatzen duela erabaki duenean.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Berme egokien menpeko transferentziak',
            description: 'Egokitasun-erabakirik ez dagoenean, transferentziak egin daitezke berme egokiak ematen direnean, SCC edo BCR barne.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Kontratu-klausula tipoak',
            description: 'Batzordeak edo ikuskaritza-agintaritza batek onartutako kontratu-klausula tipoek nazioarteko transferentzietarako berme egokiak eskaintzen dituzte.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Korporazio-arau lotesleak',
            description: 'Ikuskaritza-agintaritza eskudun batek onartutako korporazio-arau lotesleek talde barneko nazioarteko transferentzietarako berme egokiak eskaintzen dituzte.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Egoera zehatzetarako salbuespenak',
            description: 'Egokitasunik edo bermerik ez dagoenean, transferentziak egin daitezke baimen espliziturikoan, kontratuaren beharrean, interes publikoan, lege-erreklamazioetan, funtsezko interesetan edo erregistro publikoan oinarrituta.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Zuzendaritza-organoaren erantzukizuna eta prestakuntza',
            description: 'Entitate funtsezkoen eta entitate garrantzitsuen zuzendaritza-organoek zibersegurtasuneko arrisku-kudeaketako neurriak onartu eta aldizkako prestakuntza jaso behar dute.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Zibersegurtasuneko arrisku-kudeaketako neurriak',
            description: 'Entitate funtsezkoek eta entitate garrantzitsuek neurri tekniko, operatibo eta antolakuntzakoak hartuko dituzte, egokiak eta proportzionalak, zibersegurtasuneko arriskuak kudeatzeko.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Gertakariak kudeatzeko politikak eta prozedurak',
            description: 'Entitateek gertakariak kudeatzeko politikak ezarriko dituzte, detekzio-, analisi-, mugatze- eta erantzun-prozedurak barne.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Negozio-jarraitutasuna eta krisi-kudeaketa',
            description: 'Entitateek negozio-jarraitutasuneko neurriak ezarriko dituzte, segurtasun-kopiaren kudeaketa, hondamendietatik berreskuratzea eta krisi-kudeaketa barne.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Hornidura-katearen segurtasuna',
            description: 'Entitateek hornitzaile zuzenekin eta zerbitzu-emaileekin dituzten harremanen segurtasun-alderdiak landu behar dituzte, hornidura-katearen arrisku-ebaluazioak barne.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Sare- eta informazio-sistemen segurtasuna',
            description: 'Entitateek sare- eta informazio-sistemen erosketan, garapenean eta mantentze-lanetan segurtasuna ezarriko dute, ahuleziak kudeatzea barne.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Ahuleziak kudeatzea eta jakinaraztea',
            description: 'Entitateek ahuleziak modu koordinatuan jakinarazteko eta kudeatzeko politikak eta prozedurak ezarriko dituzte.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Zibersegurtasuneko arrisku-ebaluazioaren eraginkortasuna',
            description: 'Entitateek zibersegurtasuneko arrisku-kudeaketako neurrien eraginkortasuna ebaluatzeko politikak eta prozedurak ezarriko dituzte.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Kriptografia eta zifratze-politikak',
            description: 'Entitateek kriptografia eta, egoki denean, zifratzea erabiltzeko politikak eta prozedurak ezarriko dituzte.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'Giza baliabideen segurtasuna, sarbide-kontrola eta aktiboen kudeaketa',
            description: 'Entitateek giza baliabideen segurtasuna, sarbide-kontroleko politikak eta aktiboen kudeaketa ezarriko dituzte.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Faktore anitzeko autentifikazioa',
            description: 'Entitateek faktore anitzeko autentifikazioa edo etengabeko autentifikazio-soluzioak, ahots/bideo/testu komunikazio seguruak eta larrialdiko komunikazio-sistema seguruak erabiliko dituzte.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'CSIRTri lehen abisua (24 ordu)',
            description: 'Entitateek CSIRT eskudunari lehen abisua bidaliko diote 24 orduko epean, gertakari esanguratsu baten berri izan ondoren.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'CSIRTri gertakariaren jakinarazpena (72 ordu)',
            description: 'Entitateek CSIRT eskudunari gertakariaren jakinarazpena bidaliko diote 72 orduko epean, gertakari esanguratsu baten berri izan ondoren.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'CSIRTri azken txostena (1 hilabete)',
            description: 'Entitateek CSIRT eskudunari azken txostena aurkeztuko diote, gertakariaren jakinarazpenetik hilabeteko epean gehienez.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'AA jardueretarako aplikagarritasunaren zehaztapena',
            description: 'Erakundeak Vietnamgo AA Legearen menpeko AA jarduerak gauzatzen dituen zehaztu eta aplikagarritasun-erabakia dokumentatu behar du.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AA sistemaren arrisku-sailkapena arrisku-mailaren arabera',
            description: 'AA sistema bakoitza arrisku-mailaren arabera sailkatu behar da (gutxienekoa, mugatua, handia, onartezina), gidatutako sailkapen-metodologia erabiliz.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AA Sistemen Erregistroaren sarrerak mantendu',
            description: 'AA sistemen erregistroa mantendu behar da, sistemaren izena, jabea, helburua, hedapen-testuingurua, arrisku-maila eta hornitzailearen partaidetza biltzen dituena.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AA Sistemaren Dosierra, objektuen zerrenda eta frogekin',
            description: 'Sistemaren dosierra eraiki eta mantendu behar da, beharrezko objektuen zerrenda, froga-liburutegia eta bertsio-historiarekin batera.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'AA sistemen operadoreen gardentasun-betebeharrak',
            description: 'AA sistemetarako gardentasun-neurriak ezarri behar dira: erabiltzailearen jakinarazpena, AAren parte-hartzearen aitorpena eta sistemaren dokumentazioa.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AAren irteeren etiketatzea, marka-jartzea eta metadatuen sinadura',
            description: 'AA bidez sortutako edukirako etiketatzea eta marka-jartzea ezarri behar dira, eta aplikagarria denean metadatuen sinadura ere bai.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'AA bidez sortutako edukiaren aitortze-eskakizunak',
            description: 'Erabiltzaileei jakinarazi behar zaie edukia AA bidez sortua dela, eta erabiltzaileek AAren parte-hartzea identifikatzeko mekanismoak eskaini.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Azalgarritasuna eta erabiltzaileari begirako gardentasun-betebeharrak',
            description: 'Pertsonei eragiten dieten AAren erabakientzat azaldu daitezkeen irteerak eskaini behar dira, eta erabiltzaileari begirako gardentasun-dokumentazioa mantendu.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Arrisku handiko AA sistemetarako giza ikuskaritza-betebeharrak',
            description: 'Arrisku handiko AA sistemetarako giza ikuskaritza-mekanismoak ezarrita egon behar dira, eskalatze- eta indargabetze-gaitasunekin batera.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AA gertakarien monitorizazioa eta detekzioa',
            description: 'AA sistemen gertakariak, akatsak eta ondorio kaltegarriak detektatzeko monitorizazio-gaitasunak ezarri behar dira.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'AAren gertakari larrien jakinarazpena agintaritzari',
            description: 'AAren gertakari larriak agintaritza eskudunari jakinarazi behar zaizkio agindutako epearen barruan (72 ordu).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AAren gertakarien jakinarazpen-epeak betetzea',
            description: 'AAren gertakarien jakinarazpen-epeak bete behar dira: hasierako jakinarazpena, eguneratzeak eta itxiera-txostenak barne.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'Arrisku handiko sistementzako AAren eraginaren ebaluazioa',
            description: 'Arrisku handiko AA sistementzat eraginaren ebaluazioa burutu behar da, arriskuak, arintzeak, giza ikuskaritza eta monitorizazio-planak hartzen dituena.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Probaketa kontrolatua / sandbox-aren hautagarritasuna zehaztea',
            description: 'Probaketa kontrolatuan (sandbox) parte hartzeko hautagarritasuna zehaztu eta ebaluazioa dokumentatu behar da.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox eskaeraren aurkezpena eta onarpenaren jarraipena',
            description: 'Sandbox eskaerak agintaritza eskudunari aurkeztu eta onarpen-egoeraren jarraipena egin behar da aurkezpenaren bizi-zikloan zehar.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandbox-eko monitorizazio-egiaztapenak eta amaitzea/irtetea',
            description: 'Sandbox-eko parte-hartzean zehar aldizkako monitorizazio-egiaztapenak burutu eta amaitze/irteera-txostenak prestatu behar dira.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Etiketatze-salbuespen/kanporatzeen dokumentazioa arrazoiekin',
            description: 'Etiketatze-salbuespen edo -kanporatze guztiak arrazoiekin eta froga babeslearekin dokumentatu behar dira.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Datuen lokalizazioa Vietnameko erabiltzaileentzat',
            description: 'Vietnamen emandako zerbitzuetan Vietnameko erabiltzaileen datuak Vietnameko lurraldean gorde behar dira.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Zibersegurtasun-gertakarien jakinarazpena',
            description: 'Zibersegurtasun-gertakariak agintaritza eskudunei garaiz jakinarazi behar zaizkie.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Informazio-sistemen sailkapena',
            description: 'Informazio-sistemak garrantzi-mailaren arabera sailkatu eta segurtasun-neurri egokiak aplikatu behar dira.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Zibersegurtasun-arriskuen ebaluazioa',
            description: 'Informazio-sistementzat zibersegurtasun-arriskuen aldizkako ebaluazioak burutu behar dira.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Aplikagarritasunaren zehaztapena (erabaki-zuhaitza)',
            description: 'Erakundeek 53. dekretuaren aplikazio-eremuan dauden zehaztu behar dute agindutako erabaki-zuhaitzaren bidez.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Datuen lokalizazioaren ebaluazioa eta prestakuntza',
            description: 'Erakundeek datuen lokalizazio-eskakizunak ebaluatu eta prestakuntza erakutsi behar dute.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Biltegiratze- eta atxikipen-prestakuntza-zerrenda',
            description: 'Datuen lokalizazioa betetzeko biltegiratze- eta atxikipen-prestakuntza-zerrenda osatu behar da.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Bertako presentzia-eskakizunaren ebaluazioa',
            description: 'Erakundeek bertako presentzia-eskakizunak aplikagarriak diren ebaluatu eta horren araberako plana egin behar dute.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Agintaritzaren eskaeren jasotzea eta egiaztapena',
            description: 'Agintaritzaren eskaerak agindutako prozeduren arabera jaso, egiaztatu eta izapidetu behar dira.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Agintaritzaren eskaerei erantzutea epearen barruan',
            description: 'Agintaritzaren eskaeretarako erantzunak agindutako epearen barruan eman behar dira.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Agintaritzaren eskaeren kudeaketa-erregistroa',
            description: 'Agintaritzaren eskaeren kudeaketa-jarduera guztien erregistroa mantendu behar da.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Aplikagarritasuna eta aplikazio-eremuaren zehaztapen bateratua',
            description: 'Erakundeek zibersegurtasun-lege bateratuaren aplikazio-eremuan dauden zehaztu behar dute, lege zaharreko 24/2018 eta Decree 53/2022en aplikazio-eremua biltzen duena.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Informazio-sistemen sailkapena garrantzi-mailaren arabera',
            description: 'Informazio-sistemak garrantzi-mailaren arabera sailkatu behar dira (orokorra, garrantzitsua, kritikoa), dagozkien segurtasun-betebeharrekin.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Zibersegurtasun-gobernantza-egitura eta langile arduradunak',
            description: 'Erakundeek zibersegurtasun-gobernantzako egiturak ezarri eta langile arduradunak izendatu behar dituzte.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Aldizkako zibersegurtasun-arriskuen ebaluazioa',
            description: 'Sailkatutako informazio-sistema guztietan zibersegurtasun-arriskuen aldizkako ebaluazioak egin behar dira.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Segurtasun-neurriak informazio-sistemaren sailkapenaren arabera',
            description: 'Segurtasun-neurri teknikoak informazio-sistemaren sailkapen-maila bakoitzari egokituak aplikatu behar dira.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Sarbide-kontrola eta identitatearen kudeaketa',
            description: 'Informazio-sistementzat sarbide-kontroleko eta identitatea kudeatzeko neurriak ezarri behar dira.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Erregistroa, monitorizazioa eta auditoretza-arrastoa',
            description: 'Informazio-sistemek erregistratze-, monitorizazio- eta auditoretza-arrastoaren gaitasunak mantendu behar dituzte.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Ahultasunen kudeaketa eta adabakitzea',
            description: 'Ahultasunak kudeatzeko eta adabakitzeko prozesuak ezarri eta mantendu behar dira.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Vietnameko erabiltzaileen datuen lokalizazioa',
            description: 'Vietnamen emandako zerbitzuetan Vietnameko erabiltzaileen datuak Vietnameko lurraldean gorde behar dira.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Atzerriko zerbitzu-emaileentzako bertako presentzia-eskakizunak',
            description: 'Vietnamen jarduten duten atzerriko zerbitzu-emaileek agindutakoaren araberako bertako presentzia mantendu behar dute.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Bertako biltegiratze-arkitektura eta mugaz haraindiko fluxuen dokumentazioa',
            description: 'Bertako biltegiratze-arkitektura dokumentatu behar da eta mugaz haraindiko datu-fluxuak mapatu eta kontrolatu.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Zibersegurtasun-gertakarien detekzioa eta erantzuna',
            description: 'Erakundeek zibersegurtasun-gertakariak detektatzeko eta horiei erantzuteko gaitasunak izan behar dituzte.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Agintaritza eskudunari gertakariak nahitaez jakinaraztea',
            description: 'Bete beharreko zibersegurtasun-gertakariak MPS/MICri jakinarazi behar zaizkie agindutako epeen barruan.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Gertakarien jakinarazpen-epeak betetzea',
            description: 'Gertakarien jakinarazpenek agindutako epe-eskakizunak bete behar dituzte (hasierako txostena 24 ordutan, txosten osoa 72 ordutan).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Agintaritzaren eskaeren kudeaketa eta lankidetza',
            description: 'Agintaritzaren eskaerak berehala izapidetu behar dira eta lankidetza eskaini behar da agindutako prozeduren arabera.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Agintaritzaren eskaeren kasuen erregistroa eta ezagutarazte-erregistroa',
            description: 'Agintaritzaren eskaera eta ezagutarazte guztiak kasu-erregistroan erregistratu behar dira auditoretza-arrasto osoarekin.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Hornitzaileen eta hirugarrenen segurtasun-ebaluazioa',
            description: 'Informazio-sistemetara sarbidea duten hirugarren hornitzaileek segurtasun-ebaluazioa egin behar dute.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Hornitzaileen datu-sarbide eta gertakarien jakinarazpen-kontrolak',
            description: 'Hornitzaileen datu-sarbidea dokumentatu behar da eta kontratuzko gertakarien jakinarazpen-betebeharrak ezarri.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Segurtasunari buruzko sentsibilizazio-prestakuntza eta ariketak',
            description: 'Zibersegurtasun-sentsibilizazio prestakuntza eta mahai-ariketa aldizkakoak burutu behar dira.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Aldizkako sarbide-proba eta segurtasun-auditoretza',
            description: 'Sailkatutako informazio-sistementzat aldizkako sarbide-probak (pen test) eta segurtasun-auditoretzak burutu behar dira.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Datu digitalen inbentarioa mantendu',
            description: 'Erakundeek datu-aktibo guztiak katalogatzen dituen datu digitalen inbentario osoa mantendu behar dute.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Datuen sailkapena sentikortasunaren eta garrantziaren arabera',
            description: 'Datuak sentikortasun- eta garrantzi-mailen arabera sailkatu behar dira, Datuen Legeak definitzen duenez.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Tratamendu-arauak sailkapen-mailaren arabera definitu',
            description: 'Datuen sailkapen-maila bakoitzerako tratamendu-arauak ezarri behar dira.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Datuen partekatzea eta ezagutaraztea jarraitu, onarpenekin',
            description: 'Datuen partekatze- eta ezagutarazte-jarduera guztiak onarpen egokiekin jarraitu behar dira.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Mugaz haraindiko datu-transferentzien gobernantza',
            description: 'Mugaz haraindiko datu-transferentziek Datuen Legearen gobernantza-eskakizunak bete behar dituzte.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Gobernantza-politikak eta erantzukizunak mantendu',
            description: 'Erakundeek datuen gobernantza-politikak mantendu eta gobernantza-erantzukizunak esleitu behar dituzte.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Urteko gobernantza-egiaztagiria',
            description: 'Urteko gobernantza-egiaztagiria osatu behar da, betetze jarraitua berresteko.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Datuen arrisku-ebaluazioa',
            description: 'Datuen arrisku-ebaluazioa burutu behar da, datuen gobernantza-arriskuak identifikatu eta arintzeko.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Plataforma-operadorearen erantzukizun- eta gobernantza-betebeharrak',
            description: 'Plataforma-operadorearen erantzukizun-egiturak ezarri behar dira: gobernantza-politikak, monitorizazio-erantzukizunak eta betearazpen-betebeharrak.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Plataformaren arauak argitaratzea eta informazioaren gardentasuna',
            description: 'Plataformaren arauak, merkataritza-baldintzak eta funtzionamendu-aitorpenak modu garden eta eskuragarrian argitaratu behar dira.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Merkataritza elektronikoko salmenta-webgune/aplikazioaren jakinarazpena MOITi',
            description: 'Merkataritza elektronikoko salmenta-webgune eta aplikazioetarako jakinarazpena MOITi aurkeztu behar zaio Online.gov.vn-en bidez edo izendatutako bideen bidez.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'Merkataritza elektronikoko zerbitzu-plataforma/merkatuaren erregistroa MOITean',
            description: 'Merkataritza elektronikoko zerbitzu-plataformak eta merkatuak MOITean erregistratu behar dira, dosierraren aurkezpena eta onarpenaren jarraipena barne.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Saltzaileen erregistroaren mantentzea (barnekoak eta atzerritarrak)',
            description: 'Plataformako saltzaile guztien erregistroa mantendu behar da, saltzaile barneko eta atzerritarrak barne, harrera, egiaztapena eta bizi-zikloaren jarraipena egiteaz.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream saltzaileen erregistroa eta betetze-kontrolak',
            description: 'Livestream saltzaileen erregistroa mantendu behar da, identitatearen egiaztapena, jardueraren jarraipena eta betetze-kontrolak biltzen dituena.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Afiliatu eta marketin-bazkideen erregistroa',
            description: 'Afiliatu-marketinaren eta gomendio-bazkideen erregistroa mantendu behar da, kontratu-egoera, edukien jarraibideak eta betetze-onarpenak biltzen dituena.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Produktuen zerrendaketa eta debekatutako/baldintzapeko salgaien betearazpena',
            description: 'Produktuen zerrendaketa-kontrolak ezarri behar dira: debekatutako eta baldintzapeko salgaien iragazketa, monitorizazioa eta betearazpen-ekintzak.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Kontsumitzaileen erreklamazioen erregistroa, jasotzeko, ekintzekin eta auditoretza-arrastoarekin',
            description: 'Erreklamazioen erregistroa mantendu behar da, jasotze-erregistroak, ikerketa-ekintzak, ebazpenaren emaitzak eta auditoretza-arrasto osoa biltzen dituena.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Erreklamazioak izapidetzeko prozedura eta ebazpen-epeak',
            description: 'Erreklamazioak izapidetzeko prozedurak ezarri behar dira, jakinarazpen-, ikerketa- eta ebazpen-epe definituekin.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Edukien moderazioa, monitorizazioa eta kentze-betearazpena',
            description: 'Edukien moderazioa, monitorizazioa eta kentze-betearazpena ezarri behar dira eduki ilegal, debekatu edo arau-hausleentzat.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Kentze- eta betearazpen-ekintzen erregistroa, frogen kontserbazioarekin',
            description: 'Kentze- eta betearazpen-ekintzen erregistroa mantendu behar da, erabaki-erregistroekin, frogen kontserbazioarekin eta saltzaileari egindako jakinarazpenekin batera.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Plataformaren politiken eta aitorpenen erregistroa (bertsioak, argitaratze-datak, onarpena)',
            description: 'Plataformaren politiken eta aitorpenen erregistroa mantendu behar da, bertsio-historia, argitaratze-datak eta onarpenaren frogarekin batera.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Kontsumitzailearen informazio-betebeharrak — produktuari buruzko informazio zehatza eta engainuaren aurkakoa',
            description: 'Produktu eta zerbitzuei buruzko informazio zehatza eta argia bermatu behar da, eta plataforman engainu-praktikak ekiditeko neurriak ezarri.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Atzerriko plataformen funtzionamendu-baldintzak eta erregistratzea/jakinaraztea',
            description: 'Vietnameko erabiltzaileei zerbitzu egiten dieten atzerriko plataformen funtzionamendu-baldintzak bete behar dira, erregistratze- edo jakinarazte-eskakizunak barne.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Bertako ordezkaritza edo baimena atzerriko plataformetarako',
            description: 'Atzerriko plataformetarako Vietnamen bertako ordezkari bat edo erakunde baimendua izendatu behar da, legeak eskatzen duenez.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Mugaz haraindiko saltzaileen eta erreklamazioen kudeaketa-koordinazioa',
            description: 'Mugaz haraindiko saltzaileen kudeaketa eta erreklamazioen ebazpena koordinatu behar dira, eskalatzea eta froga-kudeaketa barne.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Estatuko merkataritza elektroniko kudeaketa-sistemarekiko interakzioa eta jakinarazpen-prestakuntza',
            description: 'Estatuko merkataritza elektroniko kudeaketa-sistemarekiko interakzioa prestatu behar da, datu-eremuen mapaketa eta jakinarazpen-gaitasunak barne.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Onarpen ondoko aldaketa esanguratsuen jakinarazpena',
            description: 'Hasierako onarpenaren ondoren plataformaren xehetasun, politika edo funtzionamenduan aldaketa esanguratsuak izanez gero, jakinarazpenak aurkeztu behar dira.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Amaiera- eta desaktibazio-jakinarazpena',
            description: 'Merkataritza elektronikoko jarduerak uztean amaiera- edo desaktibazio-jakinarazpenak aurkeztu behar dira, ohar publikoa eta kontsumitzailearen babesa kudeatzea barne.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Trustmark eta sinesgarritasun-balorazio programen hautagarritasuna eta mantentzea',
            description: 'Trustmark eta sinesgarritasun-balorazio programetarako hautagarritasuna ebaluatu eta etengabeko parte-hartzerako betetze-frogak mantendu behar dira.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Datu pertsonalen tratamendurako baimena',
            description: 'Datu pertsonalen tratamenduak datu-subjektuaren baimena eskatzen du, salbuespenik aplikatzen ez bada.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Sarbide-eskubidea',
            description: 'Datu-subjektuek datuen tratamendu-arduradunak duen euren datu pertsonaletara sartzeko eskubidea dute.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Zuzentze-eskubidea',
            description: 'Datu-subjektuek datu pertsonal okerrak zuzentzea eskatzeko eskubidea dute.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Ezabatze-eskubidea',
            description: 'Datu-subjektuek euren datu pertsonalak ezabatzea eskatzeko eskubidea dute.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Tratamendua mugatzeko eskubidea',
            description: 'Datu-subjektuek euren datu pertsonalen tratamendua mugatzea eskatzeko eskubidea dute.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Aurkaratze-eskubidea',
            description: 'Datu-subjektuek euren datu pertsonalen tratamenduaren aurka egiteko eskubidea dute.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Baimena baliogabetzeko eskubidea',
            description: 'Datu-subjektuek aurretik emandako baimena edozein unetan baliogabetzeko eskubidea dute.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Urraketaren jakinarazpena agintaritzari (72 ordu)',
            description: 'Datuen tratamendu-arduradunak Bộ Công an (Segurtasun Publikoaren Ministerioa) jakinarazi behar du datu pertsonalen urraketa bat aurkitzen denetik 72 orduren barruan.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Urraketaren jakinarazpena datu-subjektuei',
            description: 'Datuen tratamendu-arduradunak datu pertsonalen urraketa baten berri eman behar die kaltetutako datu-subjektuei luzapen bidegabea gabe.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA arrisku handiko tratamendurako',
            description: 'Arrisku handia eragin dezaketen tratamendu-jarduerentzako Datuen Babeseko Eraginaren Ebaluazioa (DPIA) burutu behar da.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Mugaz haraindiko transferentziaren ebaluazioa',
            description: 'Datu pertsonalak Vietnametik kanpora transferitu aurretik eraginaren ebaluazioa burutu behar da.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Tratamendu-jardueren erregistroa',
            description: 'Datuen tratamendu-arduradunek tratamendu-jardueren erregistroa (RoPA) mantendu behar dute, tratamendu-jarduera guztiak dokumentatzen dituena.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Datuen tratamendu-akordioen erregistroak',
            description: 'Tratamendu-arduradunekin eta azpi-tratamendu-arduradunekin egindako datu-tratamendu-akordioen erregistroak mantendu behar dira.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Mugaz haraindiko transferentzien babesneurriak',
            description: 'Datu pertsonalen Vietnametik kanporako transferentziek babesneurri egokiak eta dokumentatutako ebaluazioak eskatzen dituzte.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Aitortzea 2 lan-eguneko epean',
            description: 'Datu-subjektuen eskaerak jaso eta 2 lan-eguneko epean aitortu behar dira.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Amaiera 15 lan-eguneko epean',
            description: 'Datu-subjektuen eskaerak 15 lan-eguneko epean amaitu behar dira, 5 lan-eguneko luzapenarekin posible.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPSeko eraginaren ebaluazio-dosierraren aurkezpena',
            description: 'Arrisku handiko tratamendua egiten duten erakundeek eraginaren ebaluazio-dosierra aurkeztu behar diote Bộ Công an (Segurtasun Publikoaren Ministerioa) agindutako prozedura administratiboa erabiliz.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPSeko mugaz haraindiko transferentzia-dosierraren aurkezpena',
            description: 'Datu pertsonalak Vietnametik kanpora transferitzen dituzten erakundeek mugaz haraindiko transferentziaren eraginaren ebaluazio-dosierra aurkeztu behar diote Bộ Công aneri.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Tratamendu-zerbitzu emailearen ziurtatzea',
            description: 'Datu pertsonalen tratamendua zerbitzu gisa eskaintzen duten erakundeek zerbitzu-tratamenduaren ziurtagiria lortu behar dute Bộ Công anetik.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Tratamenduaren eraginaren ebaluazio-dosierra eragingailuak betetzen direnean',
            description: 'Tratamenduaren eraginaren ebaluazio-dosierra aurkeztu behar da agindutako eragingailuak betetzen direnean.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Mugaz haraindiko transferentziaren eraginaren dosierra',
            description: 'Mugaz haraindiko transferentziaren eraginaren dosierra prestatu eta aurkeztu behar da nazioarteko datu-transferentzietarako.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Datuen babes-arduraduna izendatzea',
            description: '13. dekretuak eskatzen duen moduan, datuen babes-arduraduna izendatu behar da.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Gobernantza-objektuak (politikak, rolak, prozedurak)',
            description: 'Gobernantza-objektuak mantendu behar dira: politikak, rolak eta prozedurak.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Froga-paketearen osotasuna eta auditoretza-arrasto aldaezina',
            description: 'Froga-paketeak osatuak izan behar dira eta auditoretza-arrasto aldaezina mantendu behar da.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Aurkezpen-kasuen lan-fluxuaren euskarria',
            description: 'Aurkezpen-kasuen lan-fluxuek eraginaren ebaluazio- eta mugaz haraindiko dosierretarako euskarria izan behar dute.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Tratamendu-arduradunaren kontratu-eskakizunak',
            description: 'Datuen tratamendu-arduradunentzako kontratu-eskakizunak ezarri eta monitorizatu behar dira.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Urteko betetze-berrikuspena',
            description: 'Urteko betetze-berrikuspena egin behar da, etengabeko atxikipena egiaztatzeko.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Agintaritzari aurkeztutako dosierren erregistroak',
            description: 'Agintaritzari aurkeztutako dosier guztien erregistroak mantendu behar dira.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Datu pertsonalen tratamendurako oinarri legalak',
            description: 'Datu pertsonalen tratamendua PDPLk definitutako oinarri legaletan oinarritu behar da.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Datu-subjektuaren eskubideak (sarbidea, zuzentzea, ezabatzea, mugatzea, aurkaratzea, eramangarritasuna)',
            description: 'Datu-subjektuek eskubide osoak dituzte: sarbidea, zuzentzea, ezabatzea, tratamendua mugatzea, aurkaratzea eta eramangarritasuna.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Urraketaren jakinarazpena agintaritzari',
            description: 'Datuen tratamendu-arduradunek datu pertsonalen urraketak agintaritza eskudunari jakinarazi behar dizkiote agindutako epearen barruan.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Urraketaren jakinarazpena datu-subjektuei',
            description: 'Datuen tratamendu-arduradunek kaltetutako datu-subjektuei datu pertsonalen urraketak jakinarazi behar dizkiete.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Datuen babeseko eraginaren ebaluazioa',
            description: 'Arrisku handiko tratamendu-jardueretarako datuen babeseko eraginaren ebaluazioa burutu behar da.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Mugaz haraindiko transferentzien gobernantza',
            description: 'Datu pertsonalen mugaz haraindiko transferentziek gobernantza-eskakizunak bete behar dituzte.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Datuen babes-arduraduna izendatzea',
            description: 'Erakundeek datuen babes-arduraduna izendatu behar dute, eskatzen denez.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Tratamendu-jardueren erregistroak',
            description: 'Erakundeek tratamendu-jarduera guztien erregistroak mantendu behar dituzte.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Tratamendu-arduradunaren kontratu-betebeharrak',
            description: 'Datuen tratamendu-arduradunekin kontratu-betebeharrak ezarri behar dira.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Eraginaren ebaluazio-dosierraren aurkezpena',
            description: 'Eraginaren ebaluazio-dosierrak agintaritza eskudunari aurkeztu behar zaizkio agindutakoaren arabera.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Mugaz haraindiko transferentziaren eraginaren dosierra',
            description: 'Mugaz haraindiko transferentziaren eraginaren dosierra prestatu eta aurkeztu behar da nazioarteko datu-transferentzietarako.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPOren izendapena eta jakinarazpena',
            description: 'Datuen babes-arduraduna izendatu eta agintaritzari jakinarazi behar zaio.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Gobernantza-politikak eta prozedurak',
            description: 'Gobernantza-politikak eta prozedurak ezarri eta mantendu behar dira.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Froga-paketea eta auditoretza-arrasto aldaezina',
            description: 'Froga-paketeak osatuak izan behar dira eta auditoretza-arrasto aldaezina mantendu behar da.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Aurkezpen-kasuaren bizi-zikloa',
            description: 'Aurkezpen-kasuaren bizi-zikloa agindutako etapen bidez kudeatu behar da.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Tratamendu-arduradunaren kontratu-eskakizunak',
            description: 'Datuen tratamendu-arduradunentzako kontratu-eskakizunak ezarri eta monitorizatu behar dira.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Zerbitzu-tratamendu emailearen ziurtatzea',
            description: 'Datuen tratamendu-zerbitzuak ematen dituzten erakundeek ziurtagiria lortu behar dute.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Agintaritzari aurkeztutako dosierren erregistroak',
            description: 'Agintaritzari aurkeztutako dosier guztien erregistroak mantendu behar dira.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Urteko betetze-berrikuspena',
            description: 'Urteko betetze-berrikuspena egin behar da, etengabeko atxikipena egiaztatzeko.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Datuen segurtasunerako neurri teknikoak',
            description: 'Datuen segurtasunerako neurri teknikoak ezarri eta mantendu behar dira.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: '356. Dekretuaren Mẫu số inprimaki ofizialen eskakizunak',
            description: '356/2025/NĐ-CP Dekretuak agindutako 13 prozedura administratiboko Mẫu số inprimaki ofizialak (Mẫu số 01a–10) erabili behar dira Bộ Công an-i (Segurtasun Publikoaren Ministerioa) dagozkien aurkezpenetarako. Phụ lục I–VIII elementuak datu-egituren barruko mapaketak dira, ez dira inprimaki erregulatzaile ofizialak.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA dosierraren aurkezpena A05ri',
            description: 'Erakundeek DPIA dosierrak A05/MPSri aurkeztu behar dizkiote, datu pertsonal sentikorrak edo eskala handiko tratamendua dakarten tratamendu-jardueretarako.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Mugaz haraindiko transferentzia-dosierraren aurkezpena',
            description: 'Erakundeek mugaz haraindiko datu-transferentziaren dosierrak A05/MPSren berrikuspen eta onarpenetarako aurkeztu behar dituzte.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Tratamendu-zerbitzuaren ziurtagiri-eskaera',
            description: 'Datuen tratamendu-zerbitzuen emaileek A05/MPSren ziurtagiriak eskatu behar dituzte.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Datu pertsonalen tratamenduaren jakinarazpena',
            description: 'Tratamendu-arduradunek datu pertsonalen tratamendu-jarduerak A05/MPSri jakinarazi behar dizkiete agindutako inprimakiak erabiliz (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Urteko egoera-jakinarazpena',
            description: 'Tratamendu-arduradunek datu pertsonalen tratamenduari buruzko urteko egoera-txostenak aurkeztu behar dituzte (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Erabiltzaileen informazioaren konfidentzialtasuna lehenetsiz',
            description: 'Erabiltzaileen informazioa lehenetsiz konfidentzial gisa tratatu behar da.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Babes-kontrol antolakuntza- eta tekniko gutxienekoak',
            description: 'Antolakuntza- eta babes-kontrol tekniko gutxienekoak ezarri behar dira.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Ezagutaraztea oinarri egokiarekin soilik',
            description: 'Erabiltzaileen informazioa ezagutarazi ahal izango da soilik oinarri legal egoki batekin.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Ezagutaraztea nahitaez erregistratzea eta auditagarritasuna',
            description: 'Ezagutarazte guztiak erregistratu eta auditagarriak izan behar dira.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Ezagutarazteetarako barne-onarpen-fluxua',
            description: 'Ezagutarazte guztietarako barne-onarpen-fluxua ezarri behar da.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Agintaritzaren eskaerei erantzuteko kudeaketa',
            description: 'Agintaritzaren eskaerak agindutako prozeduren arabera kudeatu eta erantzun behar dira.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Datu-subjektuak datu pertsonalen tratamendu-jarduerei buruz informatua izateko eskubidea du.',
        consent: 'Datu-subjektuak bere datu pertsonalen tratamendurako baimena emateko eskubidea du.',
        refuse_consent: 'Datu-subjektuak bere datu pertsonalen tratamendurako baimena ez emateko eskubidea du.',
        withdraw_consent: 'Datu-subjektuak aurretik emandako baimena baliogabetzea eskatzeko eskubidea du.',
        access: 'Datu-subjektuak tratamendu-arduradunak duen bere datu pertsonalak ikusi eta horietara sartzeko eskubidea du.',
        rectification: 'Datu-subjektuak datu pertsonal okerrak zuzendu edo zuzentzea eskatzeko eskubidea du.',
        data_provision: 'Datu-subjektuak bere datu pertsonalak emateko eskaera egiteko eskubidea du (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Datu-subjektuak datu pertsonalak ezabatu edo suntsitzea eskatzeko eskubidea du, legearen salbuespenen menpe.',
        restriction_of_processing: 'Datu-subjektuak bere datu pertsonalen tratamendua mugatzea eskatzeko eskubidea du.',
        object_to_processing: 'Datu-subjektuak bere datu pertsonalen tratamenduaren aurkako eskaera bidaltzeko eskubidea du.',
        complaint: 'Datu-subjektuak datu pertsonalen tratamenduari buruzko erreklamazioa (khiếu nại) aurkezteko eskubidea du.',
        denunciation: 'Datu-subjektuak datu pertsonalen babesaren urraketei buruzko salaketa (tố cáo) egiteko eskubidea du.',
        lawsuit: 'Datu-subjektuak bere datu-babes eskubideen urraketengatik auzia (khởi kiện) jartzeko eskubidea du.',
        claim_damages: 'Datu-subjektuak datu pertsonalen babesaren urraketek eragindako kalteen kalte-ordaina eskatzeko eskubidea du.',
        request_protection_measures: 'Datu-subjektuak agintaritza eskudunei edo erakunde/pertsona egokiei bere datu pertsonalak babesteko neurriak ezartzeko eskaria egiteko eskubidea du.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Arrisku-kudeaketa eta monitorizazioa (zirriborroa)',
            steps: {
                '1': 'Arrisku-kategoriak identifikatzea',
                '2': 'Abisu goiztiarreko kontrolak mantentzea',
                '3': 'Bilketa-, biltegiratze-, tratamendu-, partekatze- eta transakzio-arriskuak berrikustea',
                '4': 'Pribatutasun-, zibersegurtasun- eta sarbide-kudeaketa-arriskuak erregistratzea',
                '5': 'Zuzenketa-ekintzak abiaraztea',
                '6': 'Berrikuspen-historia kontserbatzea',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Erreklamazioak eta auziak (zirriborroa)',
            steps: {
                '1': 'Erreklamazioa / auzia jasotzea',
                '2': 'Beharrezko informazioaren osotasuna egiaztatzea; osatu gabe egonez gero, 24 orduren barruan osagarria eskatzea',
                '3': 'Eskaera osoa jaso eta 3 lan-egunen barruan, alderdiei kudeaketa-bidea eta itxaropeneko epea jakinaraztea',
                '4': 'Erantzunak eta frogagiriak eskatzea',
                '5': 'Auziaren egitateak eta plataformaren erantzukizuna ebaluatzea',
                '6': 'Ebazpenaren emaitza erregistratzea',
                '7': 'Frogak kontserbatu eta kasua ixtea',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Entrega eta datuen jabetza-eskubideen ziurtagiria (zirriborroa)',
            steps: {
                '1': 'Produktua/zerbitzua kontratu-metodoaren arabera entregatzea',
                '2': 'Entrega-gertaera eta ordua erregistratzea',
                '3': 'Datuen jabetza-eskubideen ziurtagiria sortzea',
                '4': 'Transakzioaren amaiera erregistratzea',
                '5': 'Beharrezkoa denean ondorengo bilaketa / egiaztapena ahalbidetzea',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transakzioaren hasiera eta kontratazioa (zirriborroa)',
            steps: {
                '1': 'Zerrenda eta ebaluazio-emaitza berrikustea',
                '2': 'Erosketa-, negoziazio- edo enkante-fluxua hastea',
                '3': 'Kontratu elektronikoa sortzea',
                '4': 'Bi alderdien onarpena ziurtatzea',
                '5': 'Ordainketaren amaiera erregistratzea',
                '6': 'Entrega baimentzea',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Sandbox / probaketa-ingurunearen sarbidea (zirriborroa)',
            steps: {
                '1': 'Sandbox eskaera aurkeztea',
                '2': 'Irismena, baimendutako datuak, denbora-tartea eta tratamendu-baimenak definitzea',
                '3': 'Ingurune tekniko isolatua hornitzea',
                '4': 'Probaketan zehar segurtasuna eta sarbidea monitorizatzea',
                '5': 'Irteera berrikustea atera aurretik',
                '6': 'Ateratzea baimentzea soilik baimenduta dagoenean',
                '7': 'Probaketa-saioa itxi eta ingurunea garbitzea',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Datu-produktua / zerbitzua zerrendatzea (zirriborroa)',
            steps: {
                '1': 'Zerrendatze-eskaera sortzea',
                '2': 'Jatorri eta jabetza / erabilera-eskubideen frogak biltzea',
                '3': 'Ezaugarri teknikoak, formatua, APIaren xehetasunak eta murrizketak erregistratzea',
                '4': 'Kalitate-aitorpenak erregistratzea',
                '5': 'Zerrendatze-dosierra plataformaren berrikuspenera bidaltzea',
                '6': 'Plataformak osotasuna, legaltasuna eta egiaztapen teknikoa egitea',
                '7': 'Zerrendatzea onartzea edo errefusatzea',
                '8': 'Onartuz gero, zerrendatzea ebaluazio-emaitza erantsita argitaratzea',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Parte-hartzaileen harrera (zirriborroa)',
            steps: {
                '1': 'Parte-hartzaile mota zehaztea: saltzailea ala erosleak',
                '2': 'Lege-egoera eta jarduteko gaitasuna egiaztatzea',
                '3': 'Baldintzapeko negozio-jarduerak aplikatzen badira, baldintza gehigarriak egiaztatzea',
                '4': 'Transakzio-kontua erregistratzea',
                '5': 'Ordainketa-kontua lotzea',
                '6': 'Plataformako parte-hartzea onartzea',
                '7': 'Parte-hartzailearen erregistroa eta auditoretza-arrastoa gordetzea',
            },
        },
        AI_A1: {
            name: 'AA Sistemaren Erregistroa / Jakinarazpena',
            steps: {
                '1': 'Aplikagarritasun-egiaztapena eta AA sistemaren sailkapena',
                '2': 'Erregistro-/jakinarazpen-dosierra eraikitzea',
                '3': 'Barne-onarpena',
                '4': 'Bidearen bidez aurkeztea',
                '5': 'Aldaketa-zikloa — itzuliz gero, eguneratu eta berraurkeztu',
                '6': 'Itxi eta berrespena gorde',
            },
        },
        AI_A2: {
            name: 'AAren arrisku-sailkapenaren lan-fluxua',
            steps: {
                '1': 'AA Sistemen Erregistroan sarrera sortzea',
                '2': 'Sailkapen-laguntzailea exekutatzea',
                '3': 'Frogak eranstea',
                '4': 'Sailkapena onartzea',
                '5': 'Irteerak sistemaren dosierrarekin lotzea',
            },
        },
        AI_A3: {
            name: 'Etiketatzeari eta aitortzeari buruzko betetzea',
            steps: {
                '1': 'Sistema eta erabilera-kasuaren araberako etiketatze-betebeharrak identifikatzea',
                '2': 'Etiketatze-jakinarazpenak ezartzea',
                '3': 'Frogak biltzea — pantailaren irudiak, UI testua, metadatu teknikoak',
                '4': 'Salbuespenak kudeatu eta arrazoia erregistratzea',
                '5': 'Onartu eta bertsioa esleitzea',
            },
        },
        AI_A4: {
            name: 'Gertakari larrien jakinarazpena',
            steps: {
                '1': 'AAren Gertakari Erregistroa sortzea',
                '2': 'Larritasuna sailkatzea',
                '3': 'Edukitze-ekintzak eta froga-bilketa exekutatzea',
                '4': 'Gertakari Larrien Txosten txantiloiaren esportazioa sortzea',
                '5': 'Bidearen bidez agintaritzari aurkeztea',
                '6': 'Agintaritzaren erantzunen eta itxieraren jarraipena egitea',
            },
        },
        AI_A5: {
            name: 'AAren eraginaren ebaluazioa',
            steps: {
                '1': 'Ebaluazio-erregistroa sortzea',
                '2': 'Beharrezko atalak betetzea',
                '3': 'Frogak eranstea — datu-fluxuak, ereduaren dokumentazioa, kontrolak',
                '4': 'Barne-onarpena',
                '5': 'Txostena esportatu eta dosierran gordetzea',
            },
        },
        AI_A6: {
            name: 'Probaketa kontrolatua / sandbox-eko parte-hartzea',
            steps: {
                '1': 'Hautagarritasun- eta prestakuntza-erregistroa osatzea',
                '2': 'Eskaera prestatu eta esportatzea',
                '3': 'Bidearen bidez aurkeztea',
                '4': 'Onarpenaren jarraipena egitea',
                '5': 'Monitorizazio-egiaztapenak eta erregistroak exekutatzea',
                '6': 'Amaiera-txostena eta itxiera-paketea osatzea',
            },
        },
        CYBERSEC_C1: {
            name: 'Informazio-sistema kritikoaren segurtasun-ebaluazioa',
            steps: {
                '1': 'Informazio-sistemak nazio-zibersegurtasun eskakizunen arabera sailkatzea',
                '2': 'Sailkatutako sistementzat segurtasun-ebaluazioa eta arriskuen ebaluazioa burutzea',
                '3': 'Beharrezko segurtasun-neurriak eta oinarrizko kontrolak ezartzea',
                '4': 'Betetze-frogak dokumentatu eta aldizkako berrikuspenak antolatzea',
            },
        },
        CYBERSEC_C2: {
            name: 'Zibersegurtasun-gertakariaren jakinarazpena',
            steps: {
                '1': 'Zibersegurtasun-gertakaria detektatu, sailkatu eta ebaluatzea',
                '2': 'Berehalako erantzun-ekintzak eta frogen kontserbazioa exekutatzea',
                '3': 'Agintaritzari jakinarazi eta gertakari-erantzunaren koordinazio nazionalean parte hartzea',
                '4': 'Gertakari ondoko berrikuspena egin eta ikasitako lezioak dokumentatzea',
            },
        },
        CYBERSEC_C3: {
            name: 'Datuen lokalizazioaren betetzearen egiaztapena',
            steps: {
                '1': 'Datuen lokalizazio-eragingailuak aplikatzen diren ebaluatzea zerbitzu-mota eta datu-kategorien arabera',
                '2': 'Bertako biltegiratze-arkitektura eta mugaz haraindiko fluxu-kontrolak ezarrita daudela egiaztatzea',
                '3': 'Bertako presentzia-eskakizunak (sukurtsala / ordezkaritza) ebaluatzea aplikagarriak diren tokian',
                '4': 'Betetze-frogak dokumentatu eta agintaritzaren berrikusketarako mantentzea',
            },
        },
        CYBERSEC_C4: {
            name: 'Agintaritzaren lankidetza-eskaerei erantzutea',
            steps: {
                '1': 'Agintaritzaren eskaeraren legitimitatea eta irismena jaso eta egiaztatzea',
                '2': 'Eskatutako informazioa bildu eta prestatzea, edo beharrezko ekintzak exekutatzea',
                '3': 'Agintaritzari beharrezko epearen barruan erantzuna aurkeztea',
                '4': 'Ezagutaraztea erregistratu eta kasua ixtea',
            },
        },
        CYBERSEC_C5: {
            name: 'Zibersegurtasun-langileen kualifikazioaren egiaztapena',
            steps: {
                '1': 'Hornitzaileen segurtasun-egoera ebaluatzea zibersegurtasun-ebaluazio esparrua erabiliz',
                '2': 'Hornitzaileen datu-sarbidearen irismena egiaztatu eta hornitzaileen datu-sarbide erregistroan erregistratzea',
                '3': 'Hornitzaileen gertakari-jakinarazte klausula eta kontratuzko zibersegurtasun-eskakizunak berrikustea',
                '4': 'Hornitzaileen segurtasun-berrikuspen aldizkakoak antolatu eta betetzea monitorizatzea',
            },
        },
        DATA_D1: {
            name: 'Datu-inbentarioaren eta sailkapenaren konfigurazioa',
            steps: {
                '1': 'Datu-aktibo guztiak motekin eta iturriekin katalogatzea datu-inbentarioaren erregistroan',
                '2': 'Aktibo bakoitza sentikortasun-mailaren arabera sailkatzea',
                '3': 'Sailkapen-mailaren araberako tratamendu-arauak definitzea',
                '4': 'Frogak biltzea — pantailaren irudiak, arkitektura-diagramak, sarbide-kontrolak',
                '5': 'Aldizkako berrikuspenak antolatzea',
            },
        },
        DATA_D2: {
            name: 'Datuen partekatzearen gobernantza',
            steps: {
                '1': 'Partekatze-akordio berriak datuen partekatze-arriskuen ebaluazioaren bidez ebaluatzea',
                '2': 'Barne-onarpenaren lan-fluxua amaitzea',
                '3': 'Partekatze-gertaera datuen partekatze/ezagutarazte erregistroan erregistratzea',
                '4': 'Frogak biltzea — onarpen-erregistroak, kontratu-dokumentuak',
                '5': 'Partekatze-akordioen aldizkako berrikuspena',
            },
        },
        DATA_D3: {
            name: 'Mugaz haraindiko datu-transferentziaren ebaluazioa',
            steps: {
                '1': 'Mugaz haraindiko transferentziak datu-inbentarioan identifikatzea',
                '2': 'Transferentzia-arriskuen ebaluazioa egitea',
                '3': 'Babesneurriak eta gobernantza-neurriak aplikatzea',
                '4': 'Gobernantza-egiaztagiriaren mugaz haraindiko atalean dokumentatzea',
                '5': 'Auditoretzarako ebaluazio-paketea esportatzea',
            },
        },
        DATA_D4: {
            name: 'Urteko gobernantza-egiaztagiria',
            steps: {
                '1': 'Gobernantzaren RACI matrizea berrikustea',
                '2': 'Politiken egoera egiaztatzea (gobernantza, sailkapena, partekatzea, atxikipena)',
                '3': 'Egiaztagiri-paketea osatzea',
                '4': 'Barne-onarpena lortzea',
                '5': 'Egiaztagiria esportatu eta artxibatzea',
            },
        },
        DATA_D5: {
            name: 'Datu-gertakarien kudeaketa',
            steps: {
                '1': 'Erabaki-erregistroa erabiliz gertakaria sailkatzea',
                '2': 'Liburuxkaren araberako gertakari-erantzuna exekutatzea',
                '3': 'Frogak biltzea — erregistroak, pantailaren irudiak, komunikazioak',
                '4': 'Gertakari ondoko berrikuspena egitea',
                '5': 'Agintaritzari jakinaraztea behar bada: aurkezpen-kasua sortu eta MICri aurkeztea',
                '6': 'Itxi eta artxibatzea',
            },
        },
        ECOM_P1: {
            name: 'Jakinarazpena (Salmenta-webgunea/aplikazioa)',
            steps: {
                '1': 'Sailkapena zehaztea: salmenta-gunea/aplikazioa ala merkatua/zerbitzu-plataforma',
                '2': 'Barne-dosierraren erregistroa sortzea, enpresaren identitatea, webgune/aplikazio informazioa eta beharrezko aitorpenekin',
                '3': 'Froga-paketea sortzea — aitorpenen pantailaren irudiak, politiken URLak, bertsio-historia',
                '4': 'Online.gov.vn-en bidez aurkeztea — dokumentuak igo, ordu-zigilua eta berrespena erregistratzea',
                '5': 'Izapidetzeari jarraipena egitea — aldatzeko itzultzen badute, arrazoiak bildu eta berraurkeztea',
                '6': 'Itxi — berrespena gordetzea, frogak eta auditoretza-arrastoa lotzea',
            },
        },
        ECOM_P2: {
            name: 'Erregistroa (Zerbitzu-plataformak / Merkatuak)',
            steps: {
                '1': 'Plataforma-eredua eta betebeharrak zehaztea — merkatua vs nahasia vs gizarte-merkataritza',
                '2': 'Erregistro-dosierra eraikitzea — eskaera-inprimakia, zerbitzua emateko eskema, erabilera-baldintzak, plataformaren arauak',
                '3': 'Barne-onarpenak — lege/betetze-arduraduna esleitu, aurkezpenerako onartu',
                '4': 'Online.gov.vn-en bidez aurkeztea — dosierra igo, ordu-zigilua erregistratu',
                '5': 'Izapidetze-zikloa — aldaketarako itzultzen badute, eguneratu eta berraurkeztu',
                '6': 'Onarpena eta operazionalizazioa — berrespena gorde, etengabeko betetze-erregistroak mantendu',
            },
        },
        ECOM_P3: {
            name: 'Eguneratze/Aldaketa-jakinarazpena',
            steps: {
                '1': 'Aldaketaren eragingailua identifikatzea — dosierreko elementuetan aldaketa esanguratsua',
                '2': 'Aldaketa-paketea sortzea — aldaketa-laburpena, eguneratutako dokumentuak, frogen eguneraketak',
                '3': 'Eguneraketa aurkeztu eta aurkezpenaren egoera erregistratzea',
                '4': 'Izapidetzeari jarraipena egin eta ixtea',
            },
        },
        ECOM_P4: {
            name: 'Amaiera / Desaktibazioa',
            steps: {
                '1': 'Amaiera-paketea prestatzea — uzte-data, arrazoia, kontsumitzailearen babesa kudeatzeko plana, ohar publikoaren froga',
                '2': 'Amaiera aurkeztea — ordu-zigilua eta berrespena erregistratzea',
                '3': 'Itxi — azken egoera eta frogak gordetzea',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Sinesgarritasun-balorazio Programa',
            steps: {
                '1': 'Hautagarritasun- eta prestakuntza-berrikuspena',
                '2': 'Inprimaki ofiziala erabiliz dosierra prestatzea',
                '3': 'Aurkeztu eta jarraitu',
                '4': 'Etengabeko betetze-frogak mantentzea',
            },
        },
        PDPL_P1: {
            name: 'DPIA dosierraren aurkezpena MPSri',
            steps: {
                '1': 'DPIA osatzea 356. dekretuaren inprimaki-bidea erabiliz (Mẫu số 02a/02b)',
                '2': 'Estaldura-dokumentuak eta froga babesleak biltzea',
                '5': 'Auditoretza-arrastorako dosierra eta aurkezpenaren froga gordetzea',
            },
        },
        PDPL_P2: {
            name: 'Mugaz haraindiko transferentzia-eskaera',
            steps: {
                '1': 'Mugaz haraindiko transferentzia-dosierra osatzea 356. dekretuaren inprimakiekin (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Datuen tratamendu-zerbitzuaren ziurtagiri-eskaera',
            steps: {
                '1': '356. dekretuaren eskakizunen araberako ziurtatze-eskaeraren dosierra prestatzea',
                '2': 'Eskaera MPSri aurkeztea beharrezko dokumentu babesleekin',
                '3': 'MPSren berrikusketa-iruzkinei eta osagarri-eskaerei erantzutea',
            },
        },
        PDPL_P4: {
            name: 'Datu-subjektuaren eskubide-eskaeren izapidetzea',
            steps: {
                '1': 'Eskaera jaso eta erregistratzea; datu-subjektuaren identitatea 2 lan-eguneko epean egiaztatzea',
                '2': 'Eskaera-mota ebaluatu eta aplikagarri den epea zehaztea (motaren arabera 10/15/20 egun)',
                '3': 'Betearazte-ekintzak sistema eta sail egokietan zehar exekutatzea',
                '4': 'Datu-subjektuari emaitzarekin eta arrazoiarekin erantzuna prestatu eta bidaltzea',
                '5': 'Kasua itxi eta auditoretzarako frogak gordetzea; ukatze-arrazoiak dokumentatzea',
            },
        },
        PDPL_P5: {
            name: 'Urraketaren jakinarazpena MPSri',
            steps: {
                '1': 'Urraketa detektatu eta ebaluatzea; jakinarazpen-betebeharra aplikagarria den zehaztea eta aplikatu beharreko jakinarazpen-epea instalatutako epe-erregistroetatik ebaztea',
                '2': 'PDPLren eskakizunen araberako jakinarazpen-paketea prestatzea',
                '3': 'Jakinarazpena MPSri aurkeztea instalatutako epe-erregistroetatik ebatzitako jakinarazpen-epearen barruan, aurkikuntzatik zenbatuta',
                '4': 'MPSren jarraipen-eskaerei erantzutea; osagarriak eta koordinazio-frogak aurkeztea',
            },
        },
        TELECOM_T1: {
            name: 'Agintaritzaren eskaeren kudeaketa',
            steps: {
                '1': 'Agintaritzaren eskaera jaso eta erregistratzea jasotze-erregistroaren bidez',
                '2': 'Agintaritzaren identitatea, oinarri legala eta eskaeraren irismena egiaztatzea',
                '3': 'Barne-lege berrikusketa egin eta zuzendaritzaren onarpena lortzea',
                '4': 'Bildutako frogekin erantzun-paketea prestatzea',
                '5': 'Erantzun-gutuna idatzi eta onartzea',
                '6': 'Aurkezpen-kasua sortu eta erantzuna bidearen bidez aurkeztea',
                '7': 'Ezagutaraztea erregistratu eta agintaritzaren aitortzeari jarraipena egitea',
                '8': 'Kasua itxi eta auditoretza-arrastorako frogak gordetzea',
            },
        },
        TELECOM_T2: {
            name: 'Ezagutarazte-kudeaketa',
            steps: {
                '1': 'Ezagutarazte-eskaera jasotzea (erabiltzailearen baimena, lege-betebeharra edo agintaritzaren eskaera)',
                '2': 'Ezagutarazte-onarpenaren lan-fluxua amaitzea',
                '3': 'Ezagutaraztea exekutatu eta erregistro-erregistroan erregistratzea',
                '4': 'Frogak biltzea — onarpen-erregistroak, komunikazio-erregistroak',
                '5': 'Ezagutarazte-erregistroaren aldizkako berrikuspena',
            },
        },
        TELECOM_T3: {
            name: 'Konfidentzialtasun-politikaren eta sarbide-kontrolaren konfigurazioa',
            steps: {
                '1': 'Konfidentzialtasun-politika idatzi eta onartzea',
                '2': 'Sarbide-kontrolaren eta erregistratze-politika idatzi eta onartzea',
                '3': 'Tratamendu-jarduerak erabiltzaileen informazioa tratatzeko erregistroan dokumentatzea',
                '4': 'Frogak biltzea — politika-dokumentuak, ezarpen-frogak',
                '5': 'Aldizkako berrikuspenak antolatzea',
            },
        },
        TELECOM_T4: {
            name: 'Telekomunikazio-gertakarien ebaluazioa',
            steps: {
                '1': 'Gertakaria sailkatu eta ebaluatzea',
                '2': 'Jakinarazpen-eskakizunak zehaztea',
                '3': 'Agintaritzari jakinaraztea behar bada: aurkezpen-kasua sortzea',
                '4': 'Frogak bildu eta kontserbatzea',
                '5': 'Ebazpena eta itxiera',
            },
        },
        TELECOM_T5: {
            name: 'Atxikipenaren eta ezabatzearen kudeaketa',
            steps: {
                '1': 'Datu-kategoriaren araberako atxikipen- eta ezabatze-egutegia definitzea',
                '2': 'Ezabatze-prozedurak ezartzea',
                '3': 'Frogak biltzea — ezabatze-erregistroak',
                '4': 'Egutegien aldizkako berrikuspena',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Erregistroa / Jakinarazpena / Prestakuntza administratiboa',
            'ECOM-FINE-B': 'Erreklamazioak eta kontsumitzailearen babesa',
            'ECOM-FINE-C': 'Monitorizazioa eta kentze-betearazpena',
            'ECOM-FINE-D': 'Mugaz haraindiko operazioak',
            'ECOM-FINE-E': 'Plataformaren arauak eta gardentasuna',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Agintaritzaren eskaeren kudeaketa-hutsegiteak',
            'TELECOM-FINE-B': 'Konfidentzialtasuna eta baimendu gabeko ezagutaraztea',
            'TELECOM-FINE-C': 'Sarbide-kontrolaren eta erregistratzearen hutsegiteak',
            'TELECOM-FINE-D': 'Atxikipen- eta ezabatze-urraketak',
            'TELECOM-FINE-E': 'Telekomunikazio-gertakarien ebaluazioaren hutsegiteak',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: '100 milioi VND arte',
                fine_org: '200 milioi VND arte',
                deadlines: 'Berehala detektatzean',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: '100 milioi VND arte',
                fine_org: '200 milioi VND arte',
                deadlines: 'Etengabeko betebeharra',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: '100 milioi VND arte',
                fine_org: '200 milioi VND arte',
                deadlines: 'Etengabeko betebeharra',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: '100 milioi VND arte',
                fine_org: '200 milioi VND arte',
                deadlines: 'Etengabeko betebeharra',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: '100 milioi VND arte',
                fine_org: '200 milioi VND arte',
                deadlines: 'Agintaritzaren eskaeran zehaztutakoaren arabera',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: '80 milioi VND arte',
                fine_org: '160 milioi VND arte',
                deadlines: 'Etengabeko betebeharra',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: '80 milioi VND arte',
                fine_org: '160 milioi VND arte',
                deadlines: 'Etengabeko betebeharra',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: '80 milioi VND arte',
                fine_org: '160 milioi VND arte',
                deadlines: 'Etengabeko betebeharra',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: '80 milioi VND arte',
                fine_org: '160 milioi VND arte',
                deadlines: 'Erreklamazioak izapidetzeko betebeharretan zehaztutakoaren arabera',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: '80 milioi VND arte',
                fine_org: '160 milioi VND arte',
                deadlines: 'Merkataritza elektronikoko operazioak hasi aurretik',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Plataformaren funtzionamendu jarraitua',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plataforma-operadorea / Arriskua',
                '2': 'Plataforma-operadorea / Segurtasuna',
                '3': 'Plataforma-operadorea / Arriskua',
                '4': 'Plataforma-operadorea / Segurtasuna',
                '5': 'Plataforma-operadorea',
                '6': 'Betetzea',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Parte-hartzaile batek erreklamazioa edo auzi-eskaera aurkezten du',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plataforma-operadorea',
                '2': 'Plataforma-operadorea',
                '3': 'Plataforma-operadorea',
                '4': 'Plataforma-operadorea',
                '5': 'Plataforma-operadorea / Legala',
                '6': 'Plataforma-operadorea',
                '7': 'Betetzea',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Entrega kontratu- eta ordainketa-baldintzak bete ondoren amaitu da',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Saltzailea / Plataforma-operadorea',
                '2': 'Plataforma-operadorea',
                '3': 'Plataforma-operadorea',
                '4': 'Plataforma-operadorea',
                '5': 'Plataforma-operadorea',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Erosle batek datu, produktu edo zerbitzu baterako transakzioa hasten du',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Erosleak',
                '2': 'Erosleak / Plataforma-operadorea',
                '3': 'Plataforma-operadorea',
                '4': 'Erosleak / Saltzailea',
                '5': 'Plataforma-operadorea / Ordainketak',
                '6': 'Plataforma-operadorea',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Parte-hartzaile batek datu-produktuak/zerbitzuak ebaluatu nahi ditu ingurune isolatuan',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Parte-hartzailea',
                '2': 'Plataforma-operadorea / Parte-hartzailea',
                '3': 'Plataforma-operadorea / IT',
                '4': 'Plataforma-operadorea / Segurtasuna',
                '5': 'Plataforma-operadorea',
                '6': 'Plataforma-operadorea',
                '7': 'Plataforma-operadorea / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Saltzaile batek datu, produktu edo datu-zerbitzu bat zerrendatu nahi du salerosketarako',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Saltzailea',
                '2': 'Saltzailea / Legala',
                '3': 'Saltzailea / IT',
                '4': 'Saltzailea',
                '5': 'Saltzailea',
                '6': 'Plataforma-operadorea',
                '7': 'Plataforma-operadorea',
                '8': 'Plataforma-operadorea',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Saltzaile edo erosle batek datu-plataformara batzen da',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plataforma-operadorea',
                '2': 'Plataforma-operadorea / Legala',
                '3': 'Plataforma-operadorea / Legala',
                '4': 'Plataforma-operadorea',
                '5': 'Plataforma-operadorea',
                '6': 'Plataforma-operadorea',
                '7': 'Betetzea',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Eraginaren ebaluazioa eskatzen duen datu pertsonalen tratamenduaren hasiera',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Pribatutasun-taldea',
                '2': 'Pribatutasun-taldea',
                '3': 'Legala / Betetzea',
                '4': 'DPO / Pribatutasun-taldea',
                '5': 'Betetze-taldea',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Datu pertsonalak Vietnametik kanpora transferitzeko asmoa',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Pribatutasun-taldea',
                '2': 'Legala / Betetzea',
                '3': 'DPO / Pribatutasun-taldea',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Ziurtatzea eskatzen duten datuen tratamendu-zerbitzuak ematen dituen erakundea',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Legala / Betetzea',
                '2': 'Legala / Betetzea',
                '3': 'DPO / Pribatutasun-taldea',
                '4': 'Betetze-taldea',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Datu-subjektuaren eskubide-eskaeraren jasotzea',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Pribatutasun-taldea / Bezeroarentzako Arreta',
                '2': 'DPO / Pribatutasun-taldea',
                '3': 'Saileko arduradunak / IT',
                '4': 'DPO / Pribatutasun-taldea',
                '5': 'Betetze-taldea',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Datu pertsonalen babesaren urraketa edo urraketaren aurkikuntza',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Gertakarien Erantzun Taldea / DPO',
                '2': 'DPO / Pribatutasun-taldea',
                '3': 'Legala / Betetzea',
                '4': 'DPO / Gertakarien Erantzun Taldea',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL 9. art.',
        'PDPL Art. 17': 'PDPL 17. art.',
        'PDPL Art. 23': 'PDPL 23. art.',
        'Decree 13 Art. 24': '13. dekretua, 24. art.',
        'MoPS Admin Procedures': 'MoPSren Prozedura Administratiboak',
        'Annual cycle': 'Urteko zikloa',
        'Decree 13/2023/NĐ-CP Art. 24': '13/2023/NĐ-CP Dekretua, 24. art.',
        'Decree 356/2025/NĐ-CP Art. 25(4)': '356/2025/NĐ-CP Dekretua, 25(4). art.',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': '356/2025/NĐ-CP Dekretua, 5. art. 2. atala',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': '356/2025/NĐ-CP Dekretua, 5. art. 2.–5. atalak',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': '356/2025/NĐ-CP Dekretua, 5. art. 3. atala',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': '356/2025/NĐ-CP Dekretua, 5. art. 4. atala',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': '356/2025/NĐ-CP Dekretua, 5. art. 5. atala',
        'Decree 356/2025/NĐ-CP Art. 8(3)': '356/2025/NĐ-CP Dekretua, 8(3). art.',
        'Draft Decree on Data Platforms 2026': 'Datu-plataformei buruzko Dekretu Zirriborroa 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Luật 122/2025/QH15, III. kap.; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Luật 122/2025/QH15, IV. kap. (Kontsumitzailearen Babesa)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Luật 122/2025/QH15 — Online.gov.vn-en prozedura operatiboa',
        'Law 134/2025/QH15 Article 12': 'Luật 134/2025/QH15, 12. art.',
        'Law 134/2025/QH15 Article 21': 'Luật 134/2025/QH15, 21. art.',
        'Law 134/2025/QH15 Article 27': 'Luật 134/2025/QH15, 27. art.',
        'Law 134/2025/QH15 Articles 9–10': 'Luật 134/2025/QH15, 9.–10. art.',
        'Law 24/2023/QH15 — article mapping pending': 'Luật 24/2023/QH15 — artikulu-mapaketa zain',
        'Law 60/2024/QH15 (internal control target)': 'Luật 60/2024/QH15 (barne-kontroleko helburua)',
        'Law 91/2025/QH15 Art. 21': 'Luật 91/2025/QH15, 21. art.',
        'Law 91/2025/QH15 Art. 23': 'Luật 91/2025/QH15, 23. art.',
        'Policy-defined': 'Politikan definitua',
        Quarterly: 'Hiruhilekoa',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 prozedura administratiboa',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'barne-kontroleko helburua (Luật 116/2025/QH15, 24(2)(b), 41(5) art. — egutegi zehatza garapen-tresnaren zain)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'barne-kontroleko helburua (Luật 116/2025/QH15, 41(3) art. — egutegi zehatza garapen-tresnaren zain)',
        'source verification pending': 'iturriaren egiaztapena zain',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren ad hoc jakinarazpena SBVri (5. geruza)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Urteko ICAAP Txostena (Phụ lục III) (5. geruza)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Urteko Phụ lục I (5. geruza)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Urteko Phụ lục IV (5. geruza)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Urteko Arrisku-Kudeaketa Txostena (Phụ lục II) (5. geruza)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Aktiboen kudeaketaren erantzukizuna (2. geruza, C gaia)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Kontseilu / Bazkide-Kontseiluaren ikuskapena (2. geruza, A gaia)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Kontseiluko Batzordeak (2. geruza, A gaia)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Kontseiluaren erabakiak eta aktak (2. geruza, A gaia)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Sukurtsalaren kontrol-prozedurak (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Barne-arauen betetzearen ebaluazioa (2. geruza, D gaia)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Betetze-funtzioa (2. geruza, D gaia)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Betetze-konponbidearen monitorizazioa (2. geruza, D gaia)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kontzentrazio-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Interes-gatazka (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Kontrol-kultura (2. geruza, A/B gaiak)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kreditu-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Eskuordetutako agintaritza (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Aurreratutako ezarpenaren jakinarazpena (5. geruza)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Zuzendari Nagusiaren erantzukizuna (2. geruza, A gaia)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Banku-liburuko interes-tasen arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren gutuna (2. geruza, H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzako lanak (H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren aurkikuntzen komunikazioa (2. geruza, H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren aurkikuntzak eta zuzentze-ekintzak (H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren jarraipena (H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren gobernantza',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren independentzia (2. geruza, H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren plana (2. geruza/H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Barne-auditoretzaren jakinarazpena SBVri (2. geruza, H gaia)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Barne-kontrol sistemaren gobernantza (2. geruza)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Barne-jakinarazte prozedura (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Barne-txostenen erregistroa (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Lege-aldaketen monitorizazioa (2. geruza, D gaia)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Lege- eta ospe-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Mugen urraketaren eskalatzea (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Likidezia-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS urteko berrikuspena (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS babes-kopia eta jarraipena (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS eskalatze-mekanismoak (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS fidagarritasuna eta konfidentzialtasuna (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS arrisku-datuen iturriak (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Kudeaketa-Informazio Sistema (4. geruza)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Merkatu-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Arrisku materialen erregistroa (3. geruza)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Eredu-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Produktu berrien berrikuspena (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Eragiketa-arriskua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Kanporatzearen Negozio Jarraipen Plana (2. geruza, G gaia)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Kanporatze-kontratuak (2. geruza, G gaia)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Kanporatze-kontrolak',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Kanporatzearen due diligence (2. geruza, G gaia)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Kanporatzearen ordezko hornitzailea (2. geruza, G gaia)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Kanporatzearen materialtasuna (2. geruza, G gaia)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Kanporatzearen monitorizazioa (2. geruza, G gaia)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Erreferentzia teknikoak (5. geruza)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Politiken erregistroa (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Etika profesionala (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Arrisku-gosea (3. geruza)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Arrisku-batzordearen koordinazioa (2. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Arrisku-datuen gobernantza (3. geruza, E/F gaiak) — 2028-01-01era arte etapaka',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Arrisku-mugen berrikuspena (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Arrisku-mugen esparrua (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Arrisku-kudeaketa funtzioa (2. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Arrisku-metodologia eta ereduak (2. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Arriskuen jakinarazpena (2. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Arrisku-jakinarazpenaren erritmoa (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBVri konponbide-aurkezpena (5. geruza)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV aurkezpen-bideak (5. geruza)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sektorearen aplikagarritasuna (1. geruza)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Eginkizunen banaketa (2. geruza, C gaia)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Goi-zuzendaritzaren ikuskapena (2. geruza, A gaia)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Arrisku estrategikoa (3. geruza, E gaia)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Estres-probak (3. geruza, E gaia) — 2028-01-01era arte etapaka',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Ikuskaritza-batzordea (2. geruza, A gaia)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Ikuskaritzaren aurkikuntzen erregistroa (4. geruza, F gaia)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Hiru lerroen rolak eta eskalatzea (2. geruza, B gaia)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Defentsako hiru lerroak (2. geruza)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Urraketen eskalatzea (2. geruza, D gaia)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Batzar Nazionala)',
        'Ministry of Public Security / A05 Department': 'Bộ Công an / A05 Saila (Segurtasun Publikoaren Ministerioa)',
        'Ministry of Public Security': 'Bộ Công an (Segurtasun Publikoaren Ministerioa)',
        'Ministry of Information and Communications': 'Bộ Thông tin và Truyền thông (Informazio eta Komunikazioen Ministerioa)',
        'Government of Vietnam': 'Vietnamgo Gobernua',
        'European Data Protection Board (EDPB)': 'Datu Babeserako Europako Kontseilua (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / CSIRT Nazionalak',
        'State Bank of Vietnam': 'Vietnamgo Estatuko Bankua',
    },
    tour_titles: {
        access_accountability: 'Sar zaitez Kontu Kontu-ibilbidera',
        assessments: 'Ebaluazioen Tourra',
        audit_trail: 'Ikuskaritza Trail Tour',
        classification: 'Sailkapen Bira',
        compliance_forms: 'Betetze inprimakien ibilbidea',
        connectors: 'Konektoreen Tourra',
        consent_properties: 'Adostasun Propietateen Tourra',
        consent: 'Adostasun Tourra',
        dashboard: 'Dashboard Tour',
        data_mapping: 'Datuen Mapping Tour',
        data_subjects: 'Datu-titularren bisita gidatua',
        deletion_orchestration: 'Ezabaketa Orkestrazioaren Tourra',
        discovery: 'Aurkikuntza Tourra',
        governance: 'Gobernantza Tourra',
        help_center: 'Laguntza zentroaren bisita',
        incident_ops: 'Gorabeheren Operazioen Tourra',
        incidents: 'Gorabeheren Ibilaldia',
        localization: 'Lokalizazio Tourra',
        monitoring: 'Jarraipen Tourra',
        onboarding: 'Onboarding Tour',
        organizations: 'Erakundeen bira',
        partner_workspace: 'Bazkideen lan-eremuaren bisita',
        platform_settings: 'Plataformaren ezarpenen ibilbidea',
        privacy_scanner: 'Pribatutasun-eskaneraren ibilbidea',
        program_governance: 'Programaren Gobernantza Tourra',
        rights_requests: 'Eskubideen Eskaeren Tourra',
        task_management: 'Zereginak Kudeatzeko Tourra',
        vendor_risk: 'Saltzaileen Arriskuaren Tourra',
    },
    tour_descriptions: {
        access_accountability: 'Barne Auditoretzako arduradun gisa (P-VN-10), berrikusi sarbide-frogak, ikuskaritza-baimenaren erabilera eta eredu anormalak ikertu GDPR 5. artikuluko 1.f osotasuna eta konfidentzialtasuna erakusteko.',
        assessments: 'DPO (P-VN-01) Enpresa gisa, 356 Mau so 02a Dekretuari jarraituz DPIAk egin, betetze hutsuneak identifikatu eta mugaz gaindiko transferentzia arriskuak PDPL kontuan hartuta.',
        audit_trail: 'Barne Auditoretzako arduradun gisa (P-VN-10), kontsultatu moduluen arteko frogak, muntatu auditoretza-paketeak eta esportatu faltsutzeen erregistroak arauzko bidalketetarako.',
        classification: 'Datuen arduradun gisa (P-VN-05), zehaztu datuen sailkapen taxonomiak, esleitu sentsibilitate-etiketak, atxikipen-politikak betearazi eta PDPL betetzeko behar diren estaldura-hutsuneak konpondu.',
        compliance_forms: 'Enterprise DPO (P-VN-01) gisa, arakatu, osatu eta bidali Vietnamgo erregulazio-inprimaki ofizialak Phu luc I, Phu luc II eta Mau so txantiloiak barne, PDP Batzordeak aurkezteko.',
        connectors: 'IT Sistemen arduradun gisa (P-VN-05), hartu integrazioetarako negozio-eskakizunak, mapatu konektoreak betetze-datuen fluxuekin eta egiaztatu POC prest dagoela martxan jarri aurretik.',
        consent_properties: 'Enpresa DPO (P-VN-01) gisa, konfiguratu CMP hedapena: jabetza digitalaren erregistroa, PDPL adostasun pankarta koherenteetarako urrezko txantiloiak eta propietateak prozesatzeko jarduerekin lotzen dituzten matrize lotesleak.',
        consent: 'Enpresa DPO (P-VN-01) gisa, kudeatu baimenaren gidalerroen bizi-zikloa: zehaztu bilketa helburuak, bete PDPL kentzeko eskubideak eta integratu zure baimena kudeatzeko plataformarekin.',
        dashboard: 'Enterprise DPO (P-VN-01) gisa, orienta zaitez PDPL epeen, SLA-ak urratu eta auditoretzarako prest dauden KPI-ak azaleratzen dituen betetze-jarreraren aginte zentroaren inguruan.',
        data_mapping: 'Datuen arduradun gisa (P-VN-05), sortu zure ROPA inbentarioa, mapa mugaz gaindiko datu-fluxuak eta mantendu PDPL-k eskatzen duen prozesatzeko jardueren erregistroa.',
        data_subjects: 'Enpresa-mailako datu-babeserako ordezkari edo Hezkuntza betetze-arduradun gisa, kudeatu datu-titularren profil bateratuak, plataforma osoan baimen-erregistroak, eskubide-eskaerak eta denbora-lerroko jarduera lotzen dituztenak.',
        deletion_orchestration: 'DPO (P-VN-01) gisa, egiaztatu amaierako datuen ezabaketa barne-sistemetan eta hirugarrenen saltzaileen artean, GDPR 17. artikulua ezabatzeko eskubidea betetzen duen froga kriptografikoen kateekin.',
        discovery: 'Datuen arduradun gisa (P-VN-05), exekutatu azterketa automatikoak konektatutako sistemetan datu pertsonalak aurkitzeko, etapako aurkikuntzak berrikusteko eta emaitzak PDPL etiketatzeko Sailkapen moduluan sartzeko.',
        governance: 'Saltzaileen Arriskuen Kudeatzaile gisa (P-VN-08), saltzaileen erregistroa mantendu, azpiprozesadore-kateen jarraipena egin, mugaz gaindiko transferentzia-arriskuak ebaluatu eta PDPLren gobernantza liburuak betearazi.',
        help_center: 'Pertsona guztientzat (DPO, auditore, IT arduradunak eta betetze-kudeatzaileentzat) aurkitu erantzunak, nola egin beharreko gidak eta moduluetako dokumentazio espezifikoa zure eguneroko betetze-lan-fluxuei laguntzeko.',
        incident_ops: 'Gorabeheraren komandante gisa (P-VN-09), aktibatu erantzunen liburuak, egin SLA 72 orduko PDPL jakinarazpen-leihoaren aurrean, kudeatu igoerak eta koordinatu saltzaileen erantzuna.',
        incidents: 'Gorabeheren komandante gisa (P-VN-09), detektatu eta salatu datu-hausteak 72 orduko PDPL PDP Batzordeari jakinarazpen-betebeharrekin, larritasunaren arabera sailkatu eta auditoria-mailako gertaeren espedienteak sortu.',
        localization: 'Enpresa DPO (P-VN-01) gisa, kudeatu arau-esparruaren bizi-zikloa: instalatu vietnamdar merkatu-paketeak, aktibatu PDPL eta Dekretua 13 esparruak, mapatu eskakizunak plataforma-moduluetara eta kudeatu itzulpenak.',
        monitoring: 'Enpresa DPO (P-VN-01) gisa, etengabeko adostasuna kontrolatzeko programak ezarri, arau-aldaketen jarraipena egin, KPIak neurtu eta PDPL araudiko elkarrekintza kasuak kudeatu.',
        onboarding: 'Betetze-prestatzaile gisa (P-VN-02), eman prestakuntza bisita gidatuen bidez, kargatu ariketa praktikoetarako lagin-datu errealistak eta exekutatu konfigurazio morroia azkar hasteko eszenatokietarako.',
        organizations: 'DPO (P-VN-01) gisa, konfiguratu erakunde anitzeko betetze-egiturak, ezarri sailen hierarkiak eta bideratu datu-gaien eskaerak zure talde korporatiboan zehar.',
        partner_workspace: 'Bazkide-kontu-kudeatzaile gisa, bazkide-erakundeak txertatu, zerbitzu-mailak konfiguratu, betetze-bihurrien jarraipena egin eta bazkideen merkatua kudeatu.',
        platform_settings: 'Plataformaren administratzaile gisa, kudeatu konfigurazio operatibo guztiak: zure betetze-programaren oinarrian dauden identitatea, segurtasuna, integrazioak eta moduluen sarbidea.',
        privacy_scanner: 'Pribatutasun-analista gisa (P-VN-06), egin jarraipena etengabeko azterketak cookieen baimenaren urraketak, baimenik gabeko jarraitzaileak eta gidalerroen hutsuneak detektatzeko, eta pribatutasun elektronikoaren eta GDPR betetzen diren frogak sortuz.',
        program_governance: 'Nabigatu Heldutasun Ereduen, Ebaluazioen, Ebidentzien Hutsuneen, Laburpen Exekutiboen eta abarren artean. Azpimodulu bakoitzak zure barneko programaren osasunaren ikuspegi integrala lortzen laguntzen du.',
        rights_requests: 'Enpresa DPO (P-VN-01) gisa, kudeatu PDPL datu-hartzaileen eskubideen eskaerak 15 eguneko lege-epeekin, identitate-egiaztapenarekin eta ikuskaritza-mailako ebazpen-bideekin.',
        task_management: 'Bideratu betetze-zereginak pertsonen artean, egin SLA epeen jarraipena eta automatizatu zure taldea arduratsu mantentzen duten lan-fluxuak.',
        vendor_risk: 'Saltzaileen Arriskuen Kudeatzaile gisa (P-VN-08), baloratu hirugarrenen arriskua, ezarri atearen berrikuspenak eta abiarazi berrazterketak -- zure barne-heldutasunaren jarraipena egiten duen Programaren Gobernantzatik bereizita.',
    },
    consolidation: {
        title: 'Kontsolidazio kasuak',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Sortu bateratze kasua',
        case_count: 'case',
        created: 'Bateratze kasua sortu da',
        create: 'Sortu kasua',
        creating: 'Sortzen...',
        cancel: 'Utzi',
        no_cases: 'Ez da bateratze-kasurik aurkitu.',
        all_statuses: 'Egoera guztiak',
        all_purposes: 'Helburu guztiak',
        filter_status: 'Iragazi egoeraren arabera',
        filter_purpose: 'Iragazi helburuaren arabera',
        col_title: 'Izenburua',
        col_purpose: 'Helburua',
        col_status: 'Egoera',
        col_progress: 'Aurrerapena',
        col_deadline: 'Epea',
        col_department: 'Saila',
        col_version: 'Bertsioa',
        col_evidence: 'Frogak',
        sections_accepted: 'accepted',
        prev: 'Aurrekoa',
        next: 'Hurrengoa',
        page: 'Orria',
        prev_page: 'Aurreko orrialdea',
        next_page: 'Hurrengo orrialdea',
        field_title: 'Izenburua',
        field_purpose: 'Helburua',
        field_framework: 'Esparru Kodea',
        field_deadline: 'Epea',
        field_reviewer: 'Ebaluatzailearen erabiltzailearen IDa',
        title_placeholder: 'Sartu kasuaren izenburua',
        framework_placeholder: 'adibidez, PDPD-2023',
        reviewer_placeholder: 'Sartu berrikuslearen erabiltzaile IDa',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Artxibatzea',
            cross_border_filing: 'Mugaz Gaindiko Fitxategia',
            annual_governance_attestation: 'Urteko Gobernantza Agiria',
            breach_package: 'Urratze paketea',
            audit_inspection_pack: 'Ikuskaritza Ikuskapen Pack',
        },
        statuses: {
            draft: 'Zirriborroa',
            collecting: 'Biltzea',
            consolidating: 'Finkatzen',
            review: 'Berrikuspena',
            corrections: 'Zuzenketak',
            pending_approval: 'Onarpenaren zain',
            approved: 'Onartua',
            completed: 'Osatua',
            closed: 'Itxita',
            rejected: 'Baztertua',
        },
        section_statuses: {
            pending: 'Zain',
            in_progress: 'Eraikitzen',
            submitted: 'Aurkeztua',
            accepted: 'Onartua',
            needs_correction: 'Zuzenketa behar du',
            corrected: 'Zuzendua',
        },
        flag_types: {
            missing_section: 'Atala falta da',
            conflicting_entry: 'Sarrera gatazkatsua',
            stale_entry: 'Sarrera zaharkitua',
            missing_evidence: 'Froga falta',
            incomplete_data: 'Datu osatugabeak',
            duplicate_entry: 'Sarrera bikoiztua',
        },
        flag_severities: {
            critical: 'Kritikoa',
            warning: 'Abisua',
            info: 'Info',
        },
        sections_title: 'Atalak',
        no_sections: 'Oraindik ez dago atal esleitu.',
        flags_title: 'Banderak',
        no_flags: 'Ez da detektatu banderarik.',
        open: 'open',
        approval_chain: 'Onarpen Katea',
        pending: 'Zain',
        audit_trail: 'Ikuskaritza Pista',
        no_audit: 'Ez dago auditoria-gertaerarik.',
        auto_assign: 'Atal automatikoki esleitu',
        start_collection: 'Hasi bilduma',
        review_case: 'Berrikusi kasua',
        generate_export: 'Sortu esportazioa',
        review_title: 'Finkatzeko Berrikuspena',
        advance_approval: 'Onarpenerako aurrerapena',
        back_to_case: 'Itzuli Casera',
        accept: 'Onartu',
        return_section: 'Itzuli',
        return_comment: 'Itzuli iruzkina...',
        resolve: 'Ebatzi',
        dismiss: 'Baztertu',
        resolution_notes: 'Ebazpen-oharrak...',
        notes: 'Oharrak',
        section_accepted: 'Atala onartua',
        section_returned: 'Atala zuzentzeko itzuli da',
        advanced_to_approval: 'Onarpenaren zain dagoen kasua aurreratu da',
        section_data: 'Atala Datuak',
        save_progress: 'Gorde aurrerapena',
        submit_section: 'Bidali Atala',
        submitting: 'Bidaltzen...',
        resubmit: 'Bidali berriro',
        progress_saved: 'Aurrerapena gorde da',
        section_submitted: 'Atala aurkeztua',
        correction_required: 'Zuzenketa Beharrezkoa',
        evidence_title: 'Frogak',
        no_evidence: 'Ez da frogarik erantsi.',
        add_evidence: 'Gehitu Froga',
        evidence_added: 'Froga gehitu',
        evidence_files: 'froga-fitxategiak',
        uploaded_by: 'by kargatu',
        comments_title: 'Iruzkinak',
        no_comments: 'Oraindik ez dago iruzkinik.',
        comment_placeholder: 'Gehitu iruzkin bat...',
        add_comment: 'Gehitu iruzkina',
        comment_added: 'Iruzkina gehitu da',
        view_data: 'Ikusi bidalitako datuak',
        export_title: 'Espedientea esportatzea',
        export_generated: 'Esportatu paketea sortu da',
        regenerate: 'Birsortu',
        regenerating: 'Birsortzen...',
        generated_at: 'urtean sortua',
        artifacts_title: 'Esportatu artefaktuak',
        artifact_type: 'Artefaktua',
        file_name: 'Fitxategiaren izena',
        file_size: 'Tamaina',
        generated: 'Sortutakoa',
        download: 'Deskargatu',
        artifacts: {
            master_report: 'Txosten Nagusia',
            evidence_bundle: 'Froga sorta (ZIP)',
            contributor_lineage: 'Laguntzailea Leinua',
            conflict_resolution: 'Gatazkaren Konponbidea',
            approval_history: 'Onarpen-historia',
        },
        official_forms: 'Inprimaki Ofizialak',
        forms_description: 'Datu bateratuetatik hurrengo inprimaki ofizialak sor daitezke. Inprimaki ofizialak barne laneko paperetatik bereizita daude.',
        announce_loaded_cases: '{count} bateratze-kasu kargatu dira',
        announce_case_detail_loaded: 'Kargatu da kasuaren xehetasuna',
        announce_case_transitioned: 'Maiuskulak eta kasuak {status}-ra pasatu dira',
        announce_flag_resolved: 'Bandera konpondu da',
        announce_flag_dismissed: 'Bandera baztertu da',
        case_detail_loaded: 'Kargatu da kasuaren xehetasuna',
        sections_auto_assigned: 'Saileko hierarkiatik automatikoki esleitutako atalak',
        errors: {
            load_failed: 'Ezin izan dira datuak kargatu. Mesedez, saiatu berriro.',
            create_failed: 'Ezin izan da sortu kasuak. Mesedez, saiatu berriro.',
            required_fields: 'Mesedez, bete beharrezko eremu guztiak.',
            not_found: 'Ez da aurkitu.',
            comment_required: 'Iruzkina beharrezkoa da atal bat itzultzean.',
            notes_required: 'Oharrak behar dira bandera bat ebazteko edo baztertzeko.',
        },
    },
    rights_requests: {
        import: {
            title: 'Kasuak inportatu',
            nav_title: 'Inportazio masiboa',
            description: 'Igo CSV fitxategi bat eskubide-eskaera kasuak masiboki inportatzeko.',
            select_file: 'Aukeratu CSV fitxategia',
            file_help: 'CSV kasu-eremuei dagozkien goiburuak dituena.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Fitxategia handiegia da. Gehienezko tamaina 2MB da.',
            preview: 'Aurrebista',
            columns_detected: 'Detektatutako zutabeak: {count}',
            column_mapping: 'Zutabe-esleipena',
            import_button: 'Inportatu',
            import_complete: 'Inportazioa osatuta',
            import_error: 'Inportazioak huts egin du',
            result: 'Inportazio-emaitza',
            total: 'Guztira',
            imported: 'Inportatuta',
            skipped: 'Saltatuta',
            failed: 'Huts eginda',
            errors: 'Erroreak',
            error_row: 'Errenkada',
            error_field: 'Eremua',
            error_message: 'Mezua',
            history: 'Inportazio-historia',
            no_history: 'Ez dago inportazio-historiarik.',
            history_file: 'Fitxategi-izena',
            history_date: 'Data',
        },
        batch: {
            nav_title: 'Batch Prozesua',
            page_title: 'DSR Batch Prozesua',
            page_subtitle: 'Kargatu zain dauden DSR CSV bat. Forsetik eskaera bakoitzeko aitorpen-erantzuna zirriborroa egiten du eta errenkada bakoitzeko egiaztapen-zeregin bat jartzen du ilaran. DPO iritziak + onartzen ditu bakoitza bidali aurretik.',
            upload_heading: 'Kargatu',
            csv_hint: 'Beharrezko zutabeak: request_id, requester_email, request_type. Aukerakoa: epea (ISO-8601), oharrak.',
            rows_heading: '{count} errenkada analizatu dira',
            submit_label: 'Exekutatu batch',
            submitting: 'Zirriborroa egiten…',
            col_request_id: 'Eskaera id',
            col_email: 'Eskatzailearen posta elektronikoa',
            col_type: 'Mota',
            col_status: 'Egoera',
            col_actions: 'Ekintzak',
            status_queued: 'Ilaran jarrita',
            status_drafted: 'Zirriborroa',
            status_failed: 'Huts egin du',
            status_accepted: 'Onartua',
            status_rejected: 'Baztertua',
            status_parsing_error: 'Analisi-errorea',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Onartu',
            reject: 'Baztertu',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Ebaluazio-abiarazleak',
        subtitle: 'Konfiguratu ebaluazio automatizatuen sorkuntza eta DPO jakinarazpenak pribatutasun-fluxuetarako.',
        auto_create_dpia_label: 'Sortu DPIA automatikoki DPIA beharrezkoa den moduan markatutako jarduera prozesatzen duzunean',
        auto_notify_new_purpose_label: 'Jakinarazi automatikoki DPOri datu sentikorren kategoriekin helburu berria sortzen denean',
        auto_notify_cross_border_label: 'Jakinarazi automatikoki DPO prozesatzeko jarduerari mugaz gaindiko transferentzia gehitzen zaionean',
        default_dpo_label: 'DPO lehenetsia',
        default_dpo_placeholder: 'Hautatu DPO lehenetsia...',
        notification_channels_label: 'Jakinarazpen-kanalak',
        channel_in_app: 'Aplikazioan',
        channel_email: 'Email',
        save_success: 'Ebaluazio-abiarazlearen ezarpenak gorde dira.',
        save_error: 'Ezin izan dira gorde ebaluazio-abiarazlearen ezarpenak.',
        fetch_error: 'Ezin izan dira kargatu ebaluazio-abiarazlearen ezarpenak.',
        fetch_users_error: 'Ezin izan dira erabiltzaileak kargatu.',
        announce_loaded: 'Kargatu dira ebaluazioaren abiarazlearen ezarpenak',
        dpia_status: 'DPIA Egoera',
        dpia_linked: 'DPIA Estekatuta',
        dpia_required_no_dpia: 'DPIA Beharrezkoa — Ez da sortu',
        create_dpia: 'Sortu DPIA',
        create_dpia_success: 'DPIA sortu eta jarduerei lotuta dago.',
        create_dpia_error: 'Ezin izan da sortu DPIA jarduerarako.',
        view_dpia: 'Ikusi DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Datu-gaien profilak',
        nav_title: 'Datu-gaiak',
        nav: {
            profiles: 'Profilak',
        },
        create: 'Profila sortu',
        edit: 'Profila editatu',
        delete: 'Profila ezabatu',
        delete_confirm: 'Ziur datu-gaiaren profil hau ezabatu nahi duzula?',
        display_name: 'Bistaratzeko izena',
        email: 'Email',
        phone: 'Telefonoa',
        employee_id: 'Langilearen IDa',
        student_id: 'Ikaslearen NAN',
        notes: 'Oharrak',
        external_refs: 'Kanpoko Erreferentziak',
        cases_count: 'Kasuak',
        workflows_count: 'Adostasun Erregistroak',
        last_activity: 'Azken Jarduera',
        timeline: 'Denbora-lerroa',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Ez da aurkitu datu-gaien profilik.',
        search_placeholder: 'Bilatu izena, posta elektronikoa, telefonoa edo NANaren arabera...',
        create_success: 'Datu-gaiaren profila sortu da.',
        create_error: 'Ezin izan da sortu datu-gaiaren profila.',
        update_success: 'Datu-gaiaren profila eguneratu da.',
        update_error: 'Ezin izan da eguneratu datu-gaiaren profila.',
        delete_success: 'Datu-gaiaren profila ezabatu da.',
        delete_error: 'Ezin izan da ezabatu datu-gaiaren profila.',
        link_case_success: 'Kasua profilarekin lotuta.',
        link_case_error: 'Ezin izan da lotu kasua profilarekin.',
        link_workflow_success: 'Profilari lotuta dago lan-fluxua.',
        link_workflow_error: 'Ezin izan da lotu lan-fluxua profilarekin.',
        load_error: 'Ezin izan dira kargatu datu-gaiaren profilak.',
        detail_load_error: 'Ezin izan da kargatu datu-gaiaren profila.',
        timeline_load_error: 'Ezin izan da kargatu denbora-lerroa.',
        tab_cases: 'DSR kasuak',
        tab_consent: 'Adostasun Erregistroak',
        tab_timeline: 'Denbora-lerroa',
        tab_consent_workflows: 'Baimenaren lan-fluxuak',
        tab_web_consent: 'CMP Adostasuna',
        web_consent_empty: 'Ez da aurkitu gai honentzako CMP baimen-erregistrorik.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Ezin izan dira CMP baimen-erregistro kargatu.',
        col_policy_version: 'Politikaren bertsioa',
        col_versions: 'Bertsioak',
        col_consent_version: 'Adostasuna',
        col_cookie_declaration_version: 'Cookieen Adierazpena',
        col_purposes: 'Helburuak',
        purpose_granted: 'Emandakoa',
        purpose_denied: 'Ukatua',
        granted_domains: 'Emandakoa',
        denied_domains: 'Ukatua',
        col_action: 'Ekintza',
        col_timestamp: 'Denbora-zigilua',
        col_domain: 'Domeinua',
        no_cases: 'Ez dago lotuta kasurik.',
        no_workflows: 'Ez dago lotuta adostasun-fluxurik.',
        no_timeline: 'Ez dago denbora-lerroko gertaerarik.',
        case_id: 'Kasuaren IDa',
        case_status: 'Egoera',
        case_type: 'Eskaera Mota',
        case_created: 'Sortu',
        workflow_id: 'Lan-fluxuaren IDa',
        workflow_type: 'Mota',
        workflow_status: 'Egoera',
        workflow_created: 'Sortu',
        timeline_date: 'Data',
        timeline_event: 'Gertaera',
        timeline_details: 'Xehetasunak',
        timeline_time: 'Denbora',
        timeline_type: 'Mota',
        timeline_summary: 'Laburpena',
        type_case: 'DSR kasua',
        type_consent_workflow: 'Baimenaren lan-fluxua',
        profile_info: 'Profilaren informazioa',
        announce_loaded: 'Datu-gaiaren profilak kargatu dira',
        announce_detail_loaded: 'Datu-gaiaren profila kargatu da',
        select_case: '— Hautatu kasu bat —',
        select_workflow: '— Hautatu lan-fluxu bat —',
        no_cases_hint: 'Lotu kasuak hemen ikusteko.',
        no_workflows_hint: 'Lotu adostasun-lan-fluxuak hemen ikusteko.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} sistemak · {flows} fluxuak · {crossBorder} mugaz gaindi · {highRisk} arrisku handia/kritikoa',
            switch_to_list: 'Aldatu zerrenda ikuspegira',
            switch_to_list_aria: 'Aldatu taula ikuspegira',
            svg_aria: 'Datu-fluxuaren mapa: {systems} sistemak, {flows} fluxuak, {crossBorder} mugaz gaindi',
            selected_system: 'Hautatutako sistema',
            drill_hint: 'Ireki zerrendako fitxa sistema honen estekatutako fluxuak, DPIAak eta hornitzaileen esleipenak editatzeko.',
        },
    },
    packages: {
        light: {
            name: 'Argia',
            description: 'Pribatutasun-betetzearen oinarrizko tresnak talde txikientzat',
        },
        standard: {
            name: 'Estandarra',
            description: 'Hornituta dagoen betetze-plataforma erakunde haziarrentzat',
        },
        enterprise_unlimited: {
            name: 'Enpresa mugagabea',
            description: 'Enpresa-mailako betetzea, hainbat erakunderen euskarriarekin eta zerbitzu dedikatuekin',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Hornitzailearen ezarpenak',
            subtitle: 'Konfiguratu Claude hornitzailea Forseti AI Aholkulari Juridikorako.',
            healthHeading: 'Hornitzailearen osasuna',
            statusLabel: 'Egoera',
            statusOk: 'Ados',
            statusFailed: 'Ez dago erabilgarri',
            providerLabel: 'Hornitzailea',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI erreleboa',
            providerAesirManaged: 'Aesir-ek kudeatutako tokenak',
            lastCheckedLabel: 'Azken egiaztatua',
            reasonLabel: 'Arrazoia',
            refreshButton: 'Freskatu',
            apiKeyHeading: 'Claude API tekla',
            apiKeyHint: 'API tekla bat ezartzeak Claude API modua aktibatzen du. Utzi ezarri gabe CLI errele lokala erabiltzeko (garatzaile/auto-ostatatutako modua).',
            apiKeyLabel: 'API tekla',
            apiKeyMinLength: 'Gutxienez 32 karaktere. Enkriptatutako atsedenean; inoiz ez erakutsi.',
            apiKeyTooShort: 'API teklak 32 karaktere izan behar ditu gutxienez.',
            apiKeySaved: 'API gakoa gorde da. Osasuna freskatzen...',
            apiKeyCleared: 'API tekla garbitu da. Forsetik CLI errele lokala erabiltzen du orain.',
            setKeyButton: 'Gorde API tekla',
            clearKeyButton: 'Garbitu API tekla (erabili CLI errelea)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Erabilera',
            recentHeading: 'Azken jarduera',
            historyHeading: 'Azken 12 aldiak',
            noActivity: 'Oraindik ez dago jarduerarik aldi honetan.',
            featureLabel: 'Ezaugarri',
            whenLabel: 'Noiz',
            periodLabel: 'Aldia',
            byFeatureHeading: 'Hilabete honetan ezaugarrien arabera',
            byFeatureEmpty: 'Oraindik ez dago jarduerarik aldi honetan.',
            featureColumn: 'Ezaugarri',
            featureUnitsColumn: 'Unitateak',
            featureRunsColumn: 'Korrikak',
            byUserHeading: 'Hilabete honetan erabiltzailearen arabera (administratzailearen ikuspegia)',
            byUserEmpty: 'Oraindik ez dago erabiltzaile bakoitzeko jarduerarik aldi honetan.',
            userColumn: 'Erabiltzailea',
            reconciliationLinkLabel: 'Ikusi adiskidetze-erreportajearen txostena',
            reconciliationLinkHint: 'Aesir-en barneko administratzailea soilik.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Txateatu',
                workflow: 'Lan-fluxua martxan',
                tabular: 'Erauzketa tabularra',
                redline: 'Redline berrikuspena',
                title: 'Elkarrizketaren izenburua',
                unknown: 'Bestela',
            },
        },
        reconciliation: {
            title: 'Forseti Kuota Berradiskidetzea',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Itzuli Forseti Erabilera',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Orrialde hau Aesir-en barneko administratzaileentzat mugatuta dago. Sarbidea izan behar duzula uste baduzu, jarri harremanetan zure AesirX ordezkariarekin.',
        },
        projects: {
            title: 'Proiektuak',
            subtitle: 'Dokumentuak, elkarrizketak, lan-fluxuak eta berrikuspenetarako lan-eremuak.',
            modeLabel: 'Ikusi',
            tabMine: 'Nire proiektuak',
            tabShared: 'Nirekin partekatua',
            searchLabel: 'Bilatu proiektuak',
            searchPlaceholder: 'Bilatu…',
            newButton: 'Proiektu berria',
            colName: 'Izena',
            colVisibility: 'Ikusgarritasuna',
            colDocs: 'Dokumentuak',
            colMembers: 'Kideak',
            colUpdated: 'Eguneratua',
            visibilityOwn: 'Berezkoa bakarrik',
            visibilityShared: 'Partekatua',
            emptyMine: 'Oraindik ez dago proiekturik. Sortu bat hasteko.',
            emptyShared: 'Ez da proiekturik partekatu zurekin.',
            pageHint: '{shown}/{total} erakusten.',
            createTitle: 'Proiektu berria',
            createSubmit: 'Sortu proiektua',
            fieldName: 'Izena',
            fieldDescription: 'Deskribapena (aukerakoa)',
            fieldCmNumber: 'Kasu/gaiaren zenbakia (aukerakoa)',
            fieldVisibility: 'Ikusgarritasuna',
            errorNameRequired: 'Izena behar da.',
            errorSelectorRequired: 'Hautatu erabiltzaile edo erakunde talde bat gehitzeko.',
            renameButton: 'Aldatu izena',
            renamePrompt: 'Aldatu izena proiektua',
            deleteButton: 'Ezabatu',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Karpeta hau ezabatu nahi duzu? Barruko dokumentuak proiektuan geratuko dira, artxibatu gabe.',
            removeMemberConfirm: 'Kide hau kendu nahi duzu?',
            foldersHeading: 'Karpetak',
            foldersEmpty: 'Oraindik ez dago karpetarik.',
            addFolderButton: 'Karpeta berria',
            addFolderTitle: 'Karpeta berria',
            documentsHeading: 'Dokumentuak',
            documentsEmpty: 'Ez dago dokumentu erantsirik oraindik.',
            colFilename: 'Fitxategiaren izena',
            colAdded: 'Gehituta',
            unattachButton: 'Kendu',
            membersHeading: 'Kideak',
            membersEmpty: 'Jabea bakarrik.',
            addMemberButton: 'Gehitu kidea',
            addMemberTitle: 'Gehitu kidea',
            addMemberSubmit: 'Gehitu kidea',
            colMember: 'Kide',
            colRole: 'Rola',
            selectorTypeLabel: 'Gehitu by',
            selectorUser: 'Erabiltzaile IDa',
            selectorGroup: 'Taldearen IDa',
            fieldUserId: 'Erabiltzailea',
            fieldGroupId: 'Org taldea',
            fieldRole: 'Rola',
            roleAdmin: 'Admin',
            roleEditor: 'Editorea',
            roleViewer: 'Ikuslea',
            notFound: 'Proiektua ez da aurkitu.',
            selectUserPlaceholder: 'Hautatu erabiltzaile bat…',
            selectGroupPlaceholder: 'Hautatu erakunde-talde bat...',
            noUsersAvailable: 'Ez dago erabiltzailerik erabilgarri.',
            noGroupsAvailable: 'Ez dago talderik eskuragarri.',
            loadFailed: 'Ezin izan dira kargatu aukerak.',
            deleteProjectTitle: 'Proiektua ezabatu?',
            deleteFolderTitle: 'Karpeta ezabatu?',
            removeMemberTitle: 'Kide kendu?',
            removeMemberConfirmGeneric: 'Kide hau proiektutik kendu nahi duzu?',
            addDocumentButton: 'Gehitu dokumentua',
            addDocumentTitle: 'Gehitu dokumentua proiektura',
            fieldDocument: 'Hautatu dokumentua',
            selectDocPlaceholder: 'Bilatu dokumentu bat...',
            ownerLabel: 'Sistemaren jabea',
        },
        chat: {
            sidebarLabel: 'Txatak',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Zure AI Lege- eta betetze-aholkularia — zure maizterren datuetan eta instalatutako arau-paketeetan oinarritzen da.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Aukeratu pertsonaia',
            startChatButton: 'Hasi berriketa berri bat',
            startingChat: 'Txateatzen hasten…',
            recentHeading: 'Azken txatak',
            recentEmpty: 'Oraindik ez dago txatarik — hasi bat goian.',
            untitledChat: 'Txat berria',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Elkarrizketa',
            detailSubtitle: 'Pertsonaia: {persona}',
            composeHeading: 'Bidali mezu bat',
            composeLabel: 'Mezua Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Aurrez ezarritako pertsona',
            changePersona: 'Aldatu pertsona',
            hidePersonaOptions: 'Ezkutatu pertsona-aukerak',
            starterHeading: 'Hasi ekintza hauetako batekin',
            renameButton: 'Aldatu izena',
            renamePrompt: 'Berriendatu txata',
            deleteButton: 'Ezabatu',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Ez da txata aurkitu.',
            emptyChat: 'Oraindik ez dago mezurik — esan kaixo elkarrizketa bat hasteko.',
            roleUser: 'Zuk',
            roleAssistant: 'Forseti',
            roleSystem: 'Sistema',
            thinkingLabel: 'Pentsatzen',
            toolCallPlaceholder: 'Tresna-deia:',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Dokumentuaren aldaketak proposatu dira (V{version}).',
            disclaimer: 'Forsetik zure epaia onartzen du; ez du lizentziadun abokatua ordezkatzen.',
            sendButton: 'Bidali',
            sending: 'Bidaltzen…',
            composePlaceholder: 'Galdetu Forseti...',
            composingPlaceholder: 'Forseti erantzuten ari da...',
            streamingPlaceholder: 'Forseti idazten ari da...',
            promptInjectionTitle: 'Forsetik kontrol-token batzuk jaitsi zituen.',
            promptInjectionBody: 'Eredu kenduak: {patterns}. Forsetik zure mezuaren testua bakarrik ikusiko du.',
            rateLimitTitle: 'Mezuak azkarregi bidaltzen ari zara.',
            rateLimitRetryAfter: 'Saiatu berriro {seconds}s barru.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti ez dago ziur hemen.',
            counselReviewBody: 'Berretsi baimendutako aholkulariarekin erantzun honi buruz jardun aurretik.',
            legalConclusionRefused: 'Forsetik uko egin zion arrazoirik gabe ondorio juridiko bat emateari. Saiatu search_regulatory lehenik edo eskalatu aholkularira.',
            toolCallDone: 'Eginda.',
            toolCallArgsLabel: 'Argudioak',
            toolCallResultLabel: 'Emaitza',
            toolResultLogged: 'Erremintaren emaitza ikuskaritza erregistroan jaso da.',
            toolResultAria: 'tresnaren emaitza',
            documentEditAria: 'dokumentua editatzea',
            createTaskFromMessage: 'Sortu zeregin honetatik',
            createTaskHeading: 'Sortu zeregina mezu honetatik',
            createTaskTitleLabel: 'Izenburua',
            createTaskTitleRequired: 'Titulua beharrezkoa da.',
            createTaskDescriptionLabel: 'Deskribapena',
            createTaskAssigneeLabel: 'Esleipenduna',
            createTaskAssigneePlaceholder: 'Hautatu esleipenduna...',
            createTaskAssigneeNone: '— esleitu gabe —',
            createTaskDueDateLabel: 'Epemuga',
            createTaskWorkflowLabel: 'Lotutako lan-fluxua (aukerakoa)',
            createTaskWorkflowPlaceholder: 'Hautatu lan-fluxu bat...',
            createTaskWorkflowNone: '— bat ere ez —',
            renameModalSubmit: 'Aldatu izena',
            renameModalSaving: 'Gordetzen…',
            deleteConfirmTitle: 'Txata ezabatu?',
            createTaskSubmit: 'Sortu zeregina',
            createTaskSubmitting: 'Sortzen…',
            cancelButton: 'Utzi',
            autoRouteHintMessage: 'Galdera hau gehiago dago {persona}-ren gurpilean.',
            autoRouteSwitchButton: 'Aldatu {persona}-ra',
            autoRouteSwitching: 'Aldatzen…',
            handoffSuggestionMessage: 'Edo ireki berri bat {persona}-rekin galdera hau aurrera eramateko.',
            handoffSuggestionButton: 'Hasi berriketa berria {persona}-rekin',
            handoffSuggestionWorking: 'Irekitzen…',
            handoffSuggestionDismiss: 'Orain ez',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Betetze-espezialista',
                'forseti-legal': 'Lege-aholkularia',
                'forseti-dpo': 'DPO aholkularia',
                'forseti-risk': 'Arrisku-analista',
                'forseti-auditor': 'Auditorearen begirada',
            },
            exportButton: 'Esportatu PDF',
            exportRunning: 'Esportatzen…',
            exportSuccess: 'Esportatua: {filename} (ireki fitxategi-arakatzailea).',
            exportOpenFile: 'Ireki fitxategi-arakatzailean',
            exportDownload: 'Deskargatu PDFa',
            citation: {
                verifyBadge: '[EGIAZTU]',
                verifyAria: 'Ezin izan da aipamena egiaztatu — berrikusi fidatu aurretik',
                popoverAria: '{label}-ren iturria',
                openSource: 'Kode irekiko orria',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-ek sortutako gertaera Forseti auditoretza-bidetik.',
        },
        rowAction: {
            ask: 'Galdetu Forseti',
            tooltipOpen: 'Galdetu Forseti honi buruz {recordType}',
            tooltipLocked: '{recordType} hau blokeatuta dago — Forsetik galderak erantzun ditzake baina ezin du aldaketarik egin.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — disko honi buruz',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Laburpena',
            suggestedPersona: 'Iradokitako pertsonaia',
            askForseti: 'Galdetu Forseti',
            runWorkflow: 'Exekutatu lan-fluxu bat',
            viewLineage: 'Ikusi Ikuskaritza Leinua',
            createPdplEvidencePack: 'Sortu PDPL froga-paketea',
            prepareBreachDraft: 'Prestatu jakinarazpen-zirriborroa',
            primaryActions: {
                projectSummary: 'Laburtu proiektuaren dokumentua',
                supplementSummary: 'Laburtu eskaera osagarria',
                authoritySummary: 'Laburtu agintaritzaren eskaera',
                rightsRequestResponse: 'Zirriborratu DSR erantzuna',
                dpiaQuickReview: 'Exekutatu DPIA berrikuspen azkarra',
                taskControlWalkthrough: 'Aztertu kontrol-ebidentzia',
                vendorRiskNarrative: 'Sortu hornitzaile-arriskuaren narrazioa',
                incidentLetter: 'Zirriborratu PDPL urraketa-gutuna',
                ropaSufficiency: 'Exekutatu RoPA nahikotasun-egiaztapena',
                vendorDpaReview: 'Exekutatu hornitzailearen DPA berrikuspena',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forsetik lotutako Mẫu số 08 formulariorako zirriborro-edukia baino ez du prestatzen. Ez du erabakitzen jakinarazpena beharrezkoa den, eta ez du formularioa aurkezten edo bidaltzen.',
            breachDraftReady: 'Mẫu số 08 zirriborroa berrikusteko prest',
            breachDraftReviewBeforeSelecting: 'Berrikusi hautatu aurretik',
            workflowHeading: 'Lan-fluxu garrantzitsuak',
            workflowLoading: 'Lan-fluxu garrantzitsuak kargatzen…',
            workflowEmpty: 'Ez da lan-fluxu egokirik aurkitu. Sortu erregistro honetarako lan-fluxu gidatu bat.',
            workflowError: 'Ezin izan dira lan-fluxu garrantzitsuak kargatu.',
            selectWorkflow: 'Hautatu {title}',
            workflowSelected: 'Lan-fluxua hautatuta dago eta erregistro honekin exekutatzeko prest.',
            workflowRunSelected: 'Exekutatu hautatutako lan-fluxua',
            workflowStarting: 'Lan-fluxua abiarazten…',
            workflowRunError: 'Ezin izan da lan-fluxu hau abiarazi.',
            workflowCreateGuided: 'Sortu erregistro honetarako lan-fluxu gidatu bat',
            lineageHeading: 'Erregistro honen auditoria-jarraipena',
            lineageLoading: 'Auditoria-jarraipena kargatzen…',
            lineageEmpty: 'Ez da Forseti jarraipen-gertaerarik erregistratu erregistro honetarako.',
            lineageError: 'Ezin izan da auditoria-jarraipena kargatu.',
            retry: 'Saiatu berriro',
            chatContextLoading: 'Erregistroaren testuingurua kargatzen…',
            chatGuidance: {
                general: 'Forsetik erregistro hau laburbildu, falta den informazioa identifikatu eta berrikuspen-urratsak iradoki ditzake.',
                privacy: 'Forsetik pribatutasun-datuak berrikusi, instalatutako esparruen betebeharrak egiaztatu eta giza berrikuspenerako zirriborro bat prestatzen lagun dezake.',
                compliance: 'Forsetik betetze-hutsuneak identifikatu, eskakizunak instalatutako iturrietan oinarritu eta hurrengo urrats arduratsuak iradoki ditzake.',
                legal: 'Forsetik erregistroa laburbildu, oinarritutako iturri-materiala aurkitu eta aholkularitza juridikoak berrikusteko zirriborro bat prestatzen lagun dezake.',
                risk: 'Forsetik erregistratutako arriskuak ebaluatu, falta diren datuak adierazi eta berrikuspen-urrats proportzionatuak iradoki ditzake.',
                audit: 'Forsetik froga-hutsuneak aztertu, erregistroa instalatutako kontrolekin lotu eta auditoretzarako prest dagoen jarraipena iradoki dezake.',
            },
            starterPrompts: {
                identifyGaps: 'Zer informazio edo froga falta da erregistro honetan?',
                reviewFrameworks: 'Instalatutako gure esparruetako zer betebehar berrikusi behar ditut erregistro honetarako?',
                prepareDraft: 'Lagundu erregistro honetan eta oinarritutako instalatutako iturrietan soilik oinarritutako zirriborro bat prestatzen.',
                nextSteps: 'Zer berrikuspen-urrats egin behar ditut ondoren?',
                summarizeRecord: 'Laburbildu erregistro hau eta adierazi giza berrikuspena behar duten puntuak.',
                assessRisk: 'Ebaluatu erregistratutako arriskuak, identifikatu falta diren datuak eta iradoki berrikuspen-urratsak.',
            },
            disclaimer: 'Forsetik zure epaia onartzen du; ez du lizentziadun abokatua ordezkatzen.',
            recordType: {
                project: 'Proiektua',
                dpia: 'DPIA',
                dossier_filing: 'Espedientea aurkeztea',
                transfer_assessment: 'Mugaz gaindiko transferentzia',
                vendor: 'Saltzailea',
                vendor_contract: 'Saltzaileen Kontratua',
                vendor_acceptance: 'Saltzaileen Arriskuaren Onarpena',
                incident: 'Gorabehera',
                incident_op: 'Gorabeheren Eragiketa',
                breach_notification: 'Urratze jakinarazpena',
                authority_request: 'Agintaritzaren eskaera',
                supplement_request: 'Osagarri eskaera',
                rights_request: 'Eskubideen eskaera',
                contract: 'Kontratua',
                task: 'Zeregin',
                data_mapping_activity: 'RoPA jarduera',
                compliance_rule: 'Betetzeko Araua',
                program: 'Egitaraua',
                governance_document: 'Gobernantza Dokumentua',
                remediation_plan: 'Konponketa Plana',
                cross_border_filing: 'Mugaz Gaindiko Fitxategia',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Eskatu Forsetiri hau zirriborroa egiteko',
            drafting: 'Zirriborroa egiten…',
            hint_pick_activity: 'Aukeratu estekatutako prozesamendu-jarduera bat Forseti erredakzioa gaitzeko.',
            draft_origin_label: 'Forseti zirriborroa - eremu bakoitzeko berrikuspena',
            tokens_label: '≈ {count} token hileroko ontzitik',
            accept_selected: 'Onartu hautatutakoa',
            reject_all: 'Zirriborroa baztertu',
            field_checkbox_label: 'Aplikatu Forseti zirriborroa {field}-rako',
            field_labels: {
                description: 'Deskribapena',
                purpose: 'Helburua',
                data_subjects: 'Datuen subjektuak',
                data_types: 'Datu motak',
                necessity_justification: 'Beharra eta proportzionaltasuna',
                consultation_notes: 'Kontsulten oharrak',
                dpo_opinion: 'DPO iritzia',
                system_ids: 'Lotutako sistemak',
                vendor_ids: 'Lotutako saltzaileak',
            },
            error_generic: 'Forsetik ezin izan du DPIA hau zirriborroa egin. Saiatu berriro edo hobetu jarduera-erregistroa.',
            variant_legend: 'Esparru markoa',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Ez dago instalatutako esparrurik erabilgarri',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Adi: {count} marko arteko gatazka(k) aplikatzen zaio aldaera honi. Berrikusi onartu aurretik.',
        },
        formDraft: {
            button_label: 'Eskatu Forsetiri inprimaki hau zirriborratzeko',
            drafting: 'Zirriborratzen…',
            draft_origin_label: 'Forsetiren zirriborroa — berrikusi eremu bakoitza',
            tokens_label: '≈ {count} token zure hileko kupoetik',
            accept_selected: 'Onartu hautatutakoak',
            reject_all: 'Baztertu zirriborroa',
            field_checkbox_label: 'Aplikatu Forsetiren zirriborroa {field} eremurako',
            excluded_note: '{count} eremu ezin dira zirriborratu (erreferentziak, eranskinak edo irakurtzeko soilik).',
            error_generic: 'Forsetik ezin izan du inprimaki hau zirriborratu. Saiatu berriro.',
            empty_draft: 'Forsetik ez du eremu-zirriborrorik sortu inprimaki honetarako.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Eskatu Forsetiri TIA hau idazteko',
            drafting: 'TIA idazten…',
            hint_pick_destination: 'Ezarri helmugako herrialdea transferentzia erregistroan Forsetik zirriborroa egin aurretik.',
            draft_origin_label: 'Forseti TIA zirriborroa - eremu bakoitzeko berrikuspena',
            tokens_label: '≈ {count} token hileroko ontzitik',
            accept_selected: 'Onartu hautatutakoa',
            reject_all: 'Zirriborroa baztertu',
            field_checkbox_label: 'Aplikatu Forseti zirriborroa {field}-rako',
            error_generic: 'Forsetik ezin izan zuen TIA hau idatzi. Saiatu berriro edo hobetu transferentzia-erregistroa.',
            usage_details: 'IAren erabilera-xehetasunak',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Gomendatutako erregistro-eguneratzeak',
            mechanism_recommendation: 'Transferentzia-mekanismoa: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Ezarri berrikuspen-epe bat transferentzia sentikor hau eguneratu aurretik.',
            processing_activity_warning: 'Lotu gutxienez tratamendu-jarduera / ROPA erregistro bat TIA osatu aurretik.',
            go_to_field: 'Joan eremura',
            adequacy_label: 'Helmuga-erregimena',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Trantsiziozko / EEE lasterbidea',
            adequacy_status_unknown: 'Ezezaguna — ezarri helmuga',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Adi: {count} esparru arteko gatazka(k) aplikatzen zaizkio transferentzia-erregimen honi. Berrikusi onartu aurretik.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Falta diren nahitaezko datuak',
            missing_facts_hint: 'Forsetik erregistroan ez dauden datuak markatu ditu. Haien mende dauden eremuak ez dira aldez aurretik hautatzen: eman datuak edo onartu eremua nahita.',
            risk_label: 'Kalibratutako arriskua',
            risk_low: 'Baxua',
            risk_medium: 'Ertaina',
            risk_high: 'Altua',
            jurisdiction_label: 'Jurisdikzio-eragingailuak',
            jurisdiction_none_installed: 'Instalatutako esparruetako batek ere ez du ebaluazio hau estaltzen — ez da erregimen juridikorik ebaluatu. Instalatu dagokion araudi-paketea zirriborro honetako ondorio juridikoetan oinarritu aurretik.',
            jurisdiction_none_assessed: 'Forsetik ez du jurisdikzio-ebaluaziorik itzuli. Ez da ezer ebaluatu — hau ez da inolako erregimenik aplikatzen ez denaren ondorioa.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Ebaluatutako erregimenen izenak kargatzen…',
            trigger_applicable: 'Aplikagarria',
            trigger_not_applicable: 'Ez da aktibatu',
            trigger_unknown: 'Ezezaguna — datua falta da',
            recipient_role_label: 'Hartzailearen rola',
            recipient_role_controller: 'Tratamenduaren arduraduna',
            recipient_role_processor: 'Tratamenduaren eragilea',
            recipient_role_joint_controller: 'Arduradun bateratua',
            recipient_role_unknown: 'Ezezaguna — ez da adierazi',
            opt_in_hint: 'Ondorio juridikoa: hautatu soilik eskura dauden datuekin onartzen baduzu.',
        },
        mcp: {
            pageTitle: 'MCP konektore-gakoak',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Igorri gako berria',
            issueModalTitle: 'Igorri MCP API tekla',
            labelField: 'Etiketa',
            labelHint: 'Deskribapen labur bat (adibidez, "AI gaitasun-laborategia — arauzko ikerketa").',
            permittedToolsField: 'Baimendutako tresnak',
            expiresAtField: 'Iraungitzen da (aukerakoa)',
            expiresAtHint: 'Utzi hutsik inoiz iraungi ez dadin.',
            submit: 'Arazoaren gakoa',
            cancel: 'Utzi',
            keyDisplayedOnce: 'Kopiatu gako hau orain; ezin izango duzu berriro ikusi. Gorde ezazu zure kudeatzaile sekretuan (1Password, AWS Secrets Manager, Vault, etab.).',
            keyDisplayedOnceClose: 'Kopiatu nuen - itxi',
            colLabel: 'Etiketa',
            colPrefix: 'Aurrizkia',
            colStatus: 'Egoera',
            colTools: 'Tresnak',
            colCreatedAt: 'Sortu',
            colLastUsed: 'Azken erabilia',
            colCalls: 'Deiak',
            colActions: 'Ekintzak',
            statusActive: 'Aktiboa',
            statusRevoked: 'Ezeztatua',
            statusExpired: 'Iraungi egin da',
            revokeAction: 'Errebokatu',
            rotateAction: 'Biratu',
            confirmRevokeTitle: 'MCP gako hau ezeztatu nahi duzu?',
            confirmRevokeBody: 'Gako hau erabiltzen duten Claude kanpoko bezeroek berehala galduko dute sarbidea. Hau ezin da desegin.',
            confirmRevoke: 'Errebokatu giltza',
            rotatedTitle: 'Tekla biratuta',
            rotatedBody: 'Testu arruntaren gako berria behean dago. Gako zaharrak 5 minutuz funtzionatzen jarraitzen du, beraz, hegaldiko deiak amaitzen dira.',
            emptyState: 'Oraindik ez da MCP gakorik igorri.',
            errorIssue: 'Ezin izan da MCP gakoa igorri.',
            errorList: 'Ezin izan dira MCP gakoak kargatu.',
            errorRevoke: 'Ezin izan da giltza kendu.',
            errorRotate: 'Ezin izan da tekla biratu.',
            neverUsed: '—',
            neverExpires: 'Inoiz ez',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti memoria',
            pageSubtitle: 'Arakatu eta zaindu Forsetik elkarrizketetan eta erabakietan pilatu duen erakundearen berariazko testuingurua. Sarrera komisarioak gaueko atxikipenaren aurka blokeatzen dira eta pertsona guztien eskura geratzen dira.',
            filterCategory: 'Kategoria',
            filterAllCategories: 'Kategoria guztiak',
            totalCount: '{count} sarrera',
            colAnchor: 'Aingura',
            colCategory: 'Kategoria',
            colSource: 'Iturria',
            colContent: 'Edukia',
            colCreatedAt: 'Sortu',
            colActions: 'Ekintzak',
            editButton: 'Editatu',
            deleteButton: 'Ezabatu',
            promoteButton: 'Sustatu komisarioetara',
            emptyState: 'Oraindik ez dago oroitzapenik — Forsetik txateatzen duzun bitartean testuingurua pilatuko du.',
            categories: {
                framework_interpretation: 'Esparruaren interpretazioa',
                process_preference: 'Prozesuaren lehentasuna',
                escalation_threshold: 'Igoera atalasea',
                decision_context: 'Erabakiaren testuingurua',
                glossary_term: 'Glosarioko terminoa',
                auto_inferred: 'Automatikoki ondorioztatua',
            },
            sources: {
                admin_curated: 'Komisarioa',
                user_explicit: 'Esplizitua',
                auto: 'Inferitu',
            },
            deleteConfirmTitle: 'Memoria hau ezabatu nahi duzu?',
            deleteConfirmBody: 'Ezabatzeak aingura hau kenduko du etorkizuneko Forseti txat abio guztietatik. Aingura: {anchor}',
            deleteConfirmLabel: 'Ezabatu',
            deleteCancelLabel: 'Utzi',
            editModalTitle: 'Editatu memoria',
            editAnchorLabel: 'Aingura',
            editCategoryLabel: 'Kategoria',
            editContentLabel: 'Edukia',
            editContentCount: '{n}/{max} karaktere',
            editEmptyError: 'Edukia ezin da hutsik egon.',
            editTooLongError: 'Edukiak ≤1000 karaktere izan behar ditu.',
            editAutoPromoteHint: 'Automatikoki ondorioztatu den memoria hau gordez gero, erabiltzaileak berretsitako gisa markatuko du eta atxikipen-miaketatik kenduko du.',
            editCancelButton: 'Utzi',
            editSaveButton: 'Gorde',
            editSaving: 'Gordetzen…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Ikuskaritza leinua',
            subtitle: 'Egin erregistro bati erantsitako AI-ek sortutako artefaktu bakoitzari. Txatak, tresna-deiak, zereginak, froga-zirriborroak eta dokumentu-bertsioak.',
            filterForm: 'Iragazkiak',
            recordType: 'Erregistro mota',
            recordId: 'Erregistroaren IDa',
            dateStart: 'Bertatik',
            dateEnd: 'To',
            search: 'Bilatu',
            loading: 'Kargatzen…',
            exportPdf: 'Esportatu PDF',
            exportCsv: 'Esportatu CSV',
            nodesHeading: 'Leinu-nodoak',
            colType: 'Mota',
            colLabel: 'Etiketa',
            colOccurredAt: 'urtean gertatu zen',
            colActor: 'Aktorea',
            colId: 'ID',
            noResults: 'Ez dago aholkulari jarduerarik erregistro honetarako.',
            draftBadge: 'AI-k sortutako zirriborroa',
            prev: 'Aurrekoa',
            next: 'Hurrengoa',
            viewToggleTable: 'Ikusi taula gisa',
            viewToggleGraph: 'Ikusi grafiko gisa',
            graphAriaLabel: 'Leinu grafikoa (bisuala). Erabili taularen ikuspegia laguntza-teknologietarako.',
            graphLegendDraft: 'AI froga zirriborroa',
            graphEmpty: 'Grafikatzeko nodorik ez.',
            paginationAriaLabel: 'Paginazioa',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Ezin izan da esportatu: {message}',
            truncatedNotice: 'Ibilbide hau ez dago osorik. Plataformak iturri bakoitzetik errenkada kopuru finko bat irakurtzen du gehienez, eta muga hauetan lortu da: {sources}. Mugatik harago dauden errenkadak, balego, ez dira erakusten. Murriztu data-tartea haiek sartzeko.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Proiektua',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Saltzailea',
                incident: 'Gorabehera',
                incident_op: 'Gorabeheren eragiketa',
                rights_request: 'Eskubideen eskaera',
                contract: 'Kontratua',
                task: 'Zeregin',
                data_mapping_activity: 'Datuak mapatzeko jarduera',
                compliance_rule: 'Betetzeko araua',
                program: 'Egitaraua',
            },
            nodeTypes: {
                chat: 'Txateatu',
                tool_call: 'Tresna-deia',
                workflow_run: 'Lan-fluxua martxan',
                task: 'Zeregin',
                evidence: 'Frogak',
                document_version: 'Dokumentuaren bertsioa',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'Ados',
                drifted: 'Deribatua',
                hash_mismatch: 'Hash desegokia',
                merkle_mismatch: 'Merkle desegokia',
                source_missing: 'Iturria falta da',
                not_found: 'Ez da aurkitu',
                chain_unreachable: 'Katea iritsi ezina',
                service_unreachable: 'Zerbitzua eskuraezina',
                pending: 'Ainguratzeko zain',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-k sortu du ZRIRRITZA',
            pendingReviewBadge: 'Berrikuspenaren zain',
            approvedBadge: 'Onartua',
            rejectedBadge: 'Baztertua',
            moveToReviewButton: 'Markatu berrikusteko prest',
            approveButton: 'Onartu',
            rejectButton: 'Baztertu',
            rejectReasonLabel: 'Errefusaren arrazoia',
            humanSignoffBanner: 'Forsetik zure epaia onartzen du; ez du lizentziadun abokatua ordezkatzen. AI-ek sortutako frogak gizakiaren onespena behar du froga formal gisa zenbatu aurretik.',
            pageTitle: 'AI froga-zirriborro',
            pageSubtitle: 'Berrikusi, onartu edo baztertu AI-ek sortutako frogak. Zirriborroak gizaki batek onartu behar ditu froga formaltzat hartu aurretik.',
            filterAll: 'Guztiak egintza',
            filterDrafts: 'Zirriborroak soilik',
            filterPending: 'Berrikuspenaren zain',
            filterApproved: 'Duela gutxi onartua',
            filterRejected: 'Berriki baztertua',
            colId: 'Froga ID',
            colStatus: 'Egoera',
            colSource: 'Iturria',
            colCreatedAt: 'Sortu',
            colActions: 'Ekintzak',
            empty: 'Ez dago AI froga zirriborrorik egoera honetan.',
            confirmApprove: 'AI-k sortutako froga formaltzat onartu nahi duzu?',
            confirmReject: 'Baztertu froga zirriborro hau. Hau ezin da desegin.',
            reasonRequired: 'Errefusaren arrazoia behar da.',
            rejectModalTitle: 'Ezetsi froga zirriborroa',
            approveModalTitle: 'Froga zirriborroa onartzea',
            operationFailed: 'Eragiketak huts egin du. Mesedez, saiatu berriro.',
            moveToReviewSuccess: 'Berrikusteko prest dago markatuta.',
            approveSuccess: 'Onartua.',
            rejectSuccess: 'Baztertua.',
            sourceLabelWorkflowRunTitle: 'Lan-fluxua: {title}',
            sourceLabelWorkflowRunId: 'Lan-fluxua exekutatu {id}',
            sourceLabelChatTitle: 'Txata: {title}',
            sourceLabelChatId: 'Forseti txata {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Iragazkien egoera',
        },
        workflows: {
            pageTitle: 'AI lan-fluxuak',
            pageSubtitle: 'Gordetako AI prompt-txantiloiak, atazak eta froga-zirriborroak sortzen dituztenak.',
            newWorkflow: 'Lan-fluxu berria',
            newWorkflowHeading: 'AI lan-fluxu berria',
            tabAll: 'Denak',
            tabBuiltin: 'Pakete integratua',
            tabMine: 'Nire lan-fluxuak',
            tabDetail: 'Xehetasunak',
            tabLinkedTasks: 'Lotutako atazak',
            builtinBadge: 'Integratua',
            builtinSubtitle: 'Lan-fluxu integratua.',
            userSubtitle: 'Lan-fluxu pertsonalizatua.',
            emptyHint: 'Oraindik ez dago lan-fluxurik.',
            loadTimeoutError: 'Ezin izan dira lan-fluxuak kargatu 10 segundoren ostean. Saiatu berriro, edo egiaztatu nabigatzailearen Sare fitxa zintzilik dagoen eskaera baten bila.',
            loadError: 'Ezin izan dira lan-fluxuak kargatu.',
            hideError: 'Ezkutatzeak huts egin du.',
            unhideError: 'Erakusteak huts egin du.',
            retry: 'Saiatu berriro',
            producesTasks: 'Atazak sortzen ditu',
            producesEvidence: 'Froga-zirriborroak sortzen ditu',
            hideButton: 'Ezkutatu nire zerrendatik',
            unhideButton: 'Erakutsi',
            runButton: 'Exekutatu',
            editButton: 'Editatu',
            shareButton: 'Partekatu',
            backToList: 'Itzuli zerrendara',
            requireTitleAndPrompt: 'Izenburua eta prompt-a beharrezkoak dira.',
            fieldTitle: 'Izenburua',
            fieldDescription: 'Deskribapena',
            fieldType: 'Mota',
            fieldPersona: 'Persona-afinitatea',
            fieldPractice: 'Praktika',
            fieldPrompt: 'Galdera (Markdown)',
            fieldProducesTasks: 'Atazak sortzen ditu',
            fieldProducesEvidence: 'Froga-zirriborroak sortzen ditu',
            fieldProjectId: 'Proiektua (aukerakoa)',
            fieldAttachmentIds: 'Eranskinak',
            fieldParentRecord: 'Erregistro gurasoa (aukerakoa)',
            fieldPersonaOverride: 'Persona gainidaztea (aukerakoa)',
            typeAssistant: 'Laguntzailea',
            typeTabular: 'Tabularra',
            personaNone: '— bat ere ez —',
            parentNone: '— bat ere ez —',
            parentIdPlaceholder: 'Erregistro gurasoaren IDa',
            selectProjectPlaceholder: 'Hautatu proiektu bat…',
            selectProjectFirst: 'Hautatu proiektu bat eranskinak aukeratzeko.',
            selectAttachmentsPlaceholder: 'Hautatu dokumentuak…',
            noAttachmentsInProject: 'Proiektu honek ez du dokumentu erantsirik.',
            selectParentRecordPlaceholder: 'Hautatu erregistro bat…',
            selectParentRecordFirst: 'Hautatu lehenik erregistro mota bat.',
            noRecordsForType: 'Ez dago mota honetako erregistrorik eskuragarri.',
            shareUserSelectorLabel: 'Hartzaileak (erabiltzaileak)',
            shareGroupSelectorLabel: 'Hartzaileak (erakunde-taldeak)',
            selectUsersPlaceholder: 'Hautatu erabiltzaileak…',
            selectOrgGroupsPlaceholder: 'Hautatu erakunde-taldeak…',
            loadingOptions: 'Kargatzen…',
            loadFailed: 'Aukerak kargatzeak huts egin du.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Hornitzailea',
                incident: 'Gertakaria',
                rights_request: 'Eskubide-eskaera',
                contract: 'Kontratua',
                data_mapping_activity: 'RoPA jarduera',
                compliance_rule: 'Betetze-araua',
                program: 'Programa',
            },
            cancelButton: 'Utzi',
            closeButton: 'Itxi',
            createButton: 'Sortu',
            creating: 'Sortzen…',
            saveButton: 'Gorde',
            saving: 'Gordetzen…',
            shareWorkflowHeading: 'Partekatu lan-fluxua',
            shareIntro: 'Partekatzen: {title}',
            shareUserIds: 'Erabiltzaile IDak (zuriunez edo komaz bereiziak)',
            shareGroupIds: 'Erakunde-talde IDak (zuriunez edo komaz bereiziak)',
            shareAllowEdit: 'Baimendu hartzaileei editatzea',
            shareRequireRecipient: 'Eman gutxienez erabiltzaile id bat edo talde id bat.',
            shareSuccess: 'Partekatua.',
            sharing: 'Partekatzen…',
            runWorkflowHeading: 'Exekutatu: {title}',
            runStarting: 'Hasten…',
            runStatusLabel: 'Egoera: {status}',
            runProducedCounts: 'Atazak: {tasks} · Froga-zirriborroak: {evidence} · Eranskinak: {attachments}',
            runPanelHeading: 'Lan-fluxuaren exekuzioa',
            runPanelHeadingWithTitle: 'Lan-fluxuaren exekuzioa: {title}',
            runViewTasks: 'Ikusi sortutako atazak',
            runViewEvidence: 'Ikusi zirriborroko froga',
            runResultHeading: 'Aholkulariaren emaitza',
            runNoResult: 'Exekuzioa amaitu da, baina ez du berrikusgarria den emaitzarik itzuli. Ez da ataza, froga edo eranskinik sortu. Saiatu berriro beharrezko erregistroa edo dokumentuak hautatuta.',
            runCitationWarning: 'Aipamen batzuk ezin izan dira automatikoki egiaztatu. Berrikusi itzazu emaitza honetan oinarritu aurretik.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Ezin izan da lan-fluxuaren exekuzioa kargatu.',
            runStreamFailed: 'Lan-fluxuaren aurrerapen-konexioak huts egin du.',
            runStatusValues: {
                pending: 'Zain',
                running: 'Exekutatzen',
                completed: 'Osatuta',
                succeeded: 'Arrakastatsua',
                failed: 'Huts egin du',
                cancelled: 'Bertan behera utzia',
            },
            runProgressStatus: 'Egoera: {status}',
            runProgressTaskCreated: 'Ataza sortua.',
            runProgressEvidenceDrafted: 'Froga-zirriborroa sortua.',
            runProgressAttachmentGenerated: 'Eranskina sortu da.',
            guidedWorkflowTitle: '{recordType} motako lan-fluxu gidatua',
            guidedWorkflowDescription: '{recordType} motako erregistroetarako sortua. Berrikusi eta pertsonalizatu lan-fluxu hau gorde aurretik.',
            guidedWorkflowPrompt: 'Berrikusi emandako {recordType} motako erregistroa, identifikatu falta diren datuak eta proposatu giza berrikuspenerako oinarritutako hurrengo urratsak.',
            promptHeading: 'Galdera',
            howItWorksHeading: 'Zer gertatzen da lan-fluxu hau exekutatzen duzunean',
            builtinGuidance: 'Forsetik hautatzen dituzun erregistroak eta dokumentuak berrikusten ditu, operadoreak berrikusteko modukoa den emaitza itzultzen du eta ataza edo froga zirriborroak sortzen ditu emaitzak hala eskatzen duenean bakarrik. Ez da ezer automatikoki onartzen edo bidaltzen.',
            editWorkflowHeading: 'Editatu lan-fluxua',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Lan-fluxua ez da aurkitu.',
            schedules: {
                tabTitle: 'Programazioak',
                sectionHeading: 'Programatutako exekuzioak',
                empty: 'Oraindik ez dago programaziorik. Sortu bat lan-fluxu hau automatikoki exekutatzeko.',
                addButton: 'Gehitu programazioa',
                addHeading: 'Programatu lan-fluxua',
                editHeading: 'Editatu programazioa',
                fieldFrequency: 'Maiztasuna',
                fieldDayOfWeek: 'Asteko eguna',
                fieldDayOfMonth: 'Hilaren eguna',
                fieldTime: 'Ordua (UTC)',
                fieldTimezone: 'Ordu-eremua',
                fieldProject: 'Proiektua (aukerakoa)',
                fieldAttachments: 'Lehenetsitako eranskinak',
                fieldPersonaOverride: 'Persona gainidaztea (aukerakoa)',
                frequencyDaily: 'Egunero',
                frequencyWeekly: 'Astero',
                frequencyMonthly: 'Hilero',
                weekdaySun: 'Igandea',
                weekdayMon: 'Astelehena',
                weekdayTue: 'Asteartea',
                weekdayWed: 'Asteazkena',
                weekdayThu: 'Osteguna',
                weekdayFri: 'Ostirala',
                weekdaySat: 'Larunbata',
                statusActive: 'Aktiboa',
                statusPaused: 'Pausatua',
                nextRunLabel: 'Hurrengo exekuzioa',
                lastRunLabel: 'Azken exekuzioa',
                pauseButton: 'Pausatu',
                resumeButton: 'Berrekin',
                deleteButton: 'Ezabatu',
                triggerButton: 'Exekutatu orain',
                savingButton: 'Gordetzen…',
                saveButton: 'Gorde',
                triggered: 'Exekuzioa hasi da — egiaztatu exekuzio-historia.',
            },
            branching: {
                helpHeading: 'Baldintzapeko prompt-sintaxia',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Esparru arteko gatazkak',
            pageSubtitle: 'Instalatutako erregulazio paketeek modu gatazkatsuetan elkarreragiten duten tokien katalogo prestatua. Forseti gainazalak; gizakiak erabakitzen du.',
            noInstalledPacks: 'Ez dago erakunde honetarako arauzko paketerik instalatu; instalatu gutxienez bi pakete esparruen arteko gatazkak azaleratzeko.',
            noConflicts: 'Ez dago gatazkarik uneko iragazkiekin.',
            allAcknowledged: 'Ageriko gatazka oro onartu da. Lan polita.',
            columnSeverity: 'Larritasuna',
            columnTitle: 'Izenburua',
            columnFrameworks: 'Esparruak',
            columnTopic: 'Gaia',
            columnStatus: 'Egoera',
            filterFrameworkPlaceholder: 'Esparru guztiak',
            filterTopicPlaceholder: 'Gai guztiak',
            filterSeverityPlaceholder: 'Larritasun guztiak',
            filterStatusPlaceholder: 'Egoera guztiak',
            filterStatusAcknowledged: 'Aitortua',
            filterStatusUnacknowledged: 'Aitortu gabe',
            severityCritical: 'Kritikoa',
            severityHigh: 'Alta',
            severityMedium: 'Ertaina',
            severityLow: 'Baxua',
            statusAcknowledged: 'Aitortua',
            statusInReview: 'Berrikuspenean',
            statusResolved: 'Ebatzita',
            statusWontFix: 'Ez da konponduko',
            statusUnacknowledged: 'Aitortu gabe',
            drawerSummaryHeading: 'Laburpena',
            drawerSideAHeading: 'A aldea',
            drawerSideBHeading: 'B aldea',
            drawerRequirementsLabel: 'Aipatutako betekizunak',
            drawerProvisionsLabel: 'Aipatutako xedapenak',
            drawerSuggestedResolutionHeading: 'Iradokitako ebazpena',
            drawerLegalCounselBadge: 'Aholkularitza juridikoaren berrikuspena behar da',
            drawerAckHeading: 'Aitorpena',
            drawerAckStatusLabel: 'Egoera',
            drawerAckAssigneeLabel: 'Honi esleitua',
            drawerAckDueDateLabel: 'Epemuga',
            drawerAckNotesLabel: 'Oharrak',
            drawerAckButtonAcknowledge: 'Aitortu',
            drawerAckButtonUpdate: 'Eguneratu',
            drawerAckButtonUnacknowledge: 'Desaitortu',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Bilatu izenburua edo laburpena...',
            searchButton: 'Bilatu',
            searchButtonAriaLabel: 'Exekutatu gatazken bilaketa',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Ezin izan da esportatu: {message}',
            exportAriaLabel: 'Esportatu gatazkak',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Berrikuspen tabularrak',
            pageSubtitle: 'Konparatu N dokumentu M atributuetan zehar oinarritutako aipuekin.',
            newReview: 'Berrikuspen berria',
            newReviewHeading: 'Berrikuspen tabular berria',
            emptyHint: 'Oraindik ez dago berrikuspen tabularrik.',
            docsLabel: '{n} dokumentu',
            colsLabel: '{n} zutabe',
            detailSubtitle: '{docs} dokumentu · {cols} zutabe',
            addColumn: 'Gehitu zutabea',
            addColumnHeading: 'Gehitu zutabea',
            columnInfo: 'Ikusi gonbita eta formatua',
            columnInfoAria: 'Erakutsi {name} zutabearen gonbita eta formatua',
            columnInfoPrompt: 'Gonbita',
            columnInfoFormat: 'Itzulera-formatua',
            columnInfoPresets: 'Onartutako balioak',
            columnInfoCitations: 'Aipamenak',
            columnInfoCitationsRequired: 'Beharrezkoa',
            removeColumn: 'Kendu zutabea',
            removeColumnAria: 'Kendu {name} zutabea',
            removeColumnConfirm: '"{name}" zutabea kendu nahi duzu? Bere atera diren gelaxkak ezabatuko dira.',
            deleteReview: 'Ezabatu',
            deleteReviewAria: 'Ezabatu {title} berrikuspena',
            deleteReviewConfirm: '"{title}" berrikuspena ezabatu nahi duzu? Honek bere zutabeak, gelaxkak eta eztabaidak betiko ezabatzen ditu.',
            runAll: 'Exekutatu denak',
            running: 'Exekutatzen…',
            cancel: 'Utzi',
            exportXlsx: 'Esportatu Excel',
            exportCsv: 'Esportatu CSV',
            exporting: 'Esportatzen…',
            backToList: 'Itzuli zerrendara',
            notFound: 'Berrikuspen tabularra ez da aurkitu.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Erantsi dokumentuak berrikuspena hasteko.',
            noColumns: 'Gehitu zutabeak zer atera definitzeko.',
            tableAriaLabel: 'Erauzketa-sareta',
            threadsCountAria: '{n} eztabaida-mezu',
            startDiscussionAria: 'Hasi eztabaida',
            docColumn: 'Dokumentua',
            statusPending: 'Zain',
            statusRunning: 'Exekutatzen…',
            statusFailed: 'Huts egin du: {msg}',
            statusCancelled: 'Bertan behera utzia',
            citationsCount: '{n} aipu',
            citationsView: 'Ikusi iturburuko pasarteak',
            citationPage: '{n}. orria',
            titleRequired: 'Izenburua beharrezkoa da.',
            fieldTitle: 'Izenburua',
            fieldProjectId: 'Proiektua (aukerakoa)',
            fieldAttachmentIds: 'Eranskinak',
            fieldColumnName: 'Zutabe-izena',
            fieldColumnNameHelp: 'Etiketa labur eta deskribatzailea zutabe honentzat erauzketa-saretan.',
            columnNamePlaceholder: 'adib. Kontratuaren hasiera-data',
            fieldExtractionPrompt: 'Erauzketa-prompta',
            fieldExtractionPromptHelp: 'Deskribatu erauzi nahi duzun informazioa. Izan zaitez zehatza emaitza hobeak lortzeko.',
            columnPromptPlaceholder: 'Zeren bila ibili behar du Forsetik dokumentuetan?',
            fieldReturnFormat: 'Itzulera-formatua',
            fieldReturnFormatHelp: 'Kontrolatzen du AAk bere erantzuna nola formateatzen duen (adib. data edo zenbaki gisa).',
            fieldPresets: 'Onartutako balioak (bat lerroko)',
            fieldPresetsPlaceholder: 'bai\nez\npartziala',
            fieldCitationsRequired: 'Aipua beharrezkoa',
            formatShortText: 'Testu laburra',
            formatLongText: 'Testu luzea',
            formatEnum: 'Enum',
            formatNumber: 'Zenbakia',
            formatDate: 'Data',
            formatList: 'Zerrenda',
            columnRequireFields: 'Izena eta prompta beharrezkoak dira.',
            addColumnError: 'Ezin izan da zutabea gehitu.',
            cancelButton: 'Utzi',
            createButton: 'Sortu',
            creating: 'Sortzen…',
            addButton: 'Gehitu',
            adding: 'Gehitzen…',
            runConfirmHeading: 'Exekutatu erauzketa',
            estimateBody: '~{units} fakturazio-unitate estimatuta ({confidence} konfiantza).',
            starting: 'Hasten…',
            confirmRun: 'Baieztatu exekuzioa',
            charts: {
                title: 'Zutabe-grafikoak',
                tabsAria: 'Grafiko-zutabeak',
                enumAria: 'Enum balioen banaketa',
                numberAria: 'Zenbaki-banaketaren histograma',
                dateAria: 'Data-denbora-lerroa',
                enumEmpty: 'Ez dago osatutako gelaxkarik.',
                numberEmpty: 'Ez dago zenbakizko baliorik.',
                numberExcluded: '{n} gelaxka kanpoan utzita (hutsa / ez-zenbakizkoa / ez-osatua).',
                dateEmpty: 'Ez dago data-baliorik.',
                dateExcluded: '{n} gelaxka kanpoan utzita.',
                unsupportedFormat: 'Onartzen ez den zutabe-formatua.',
            },
            threads: {
                drawerTitle: 'Eztabaida',
                loading: 'Kargatzen…',
                empty: 'Oraindik ez dago mezurik. Hasi elkarrizketa.',
                composerLabel: 'Iruzkin berria',
                composerPlaceholder: 'Gehitu iruzkin bat…',
                post: 'Argitaratu',
                posting: 'Argitaratzen…',
                edit: 'Editatu',
                editLabel: 'Editatu mezua',
                delete: 'Ezabatu',
                save: 'Gorde',
                cancel: 'Utzi',
                close: 'Itxi',
                editedBadge: 'editatua',
                loadError: 'Ezin izan da haria kargatu',
                postError: 'Argitaratzeak huts egin du',
                editError: 'Editatzeak huts egin du',
                deleteError: 'Ezabatzeak huts egin du',
            },
            compare: {
                pageTitle: 'Konparatu berrikuspenak',
                leftReviewLabel: 'Ezkerreko berrikuspena',
                rightReviewLabel: 'Eskuineko berrikuspena',
                pickReview: 'Aukeratu berrikuspena konparatzeko',
                noSharedDocuments: 'Berrikuspen hauek ez dute dokumenturik partekatzen — konparazioak gutxienez eranskin komun bat eskatzen du.',
                sharedDocumentsHeading: 'Partekatutako dokumentuak',
                leftOnlyHeading: 'Ezkerreko berrikuspeneko zutabeak soilik',
                rightOnlyHeading: 'Eskuineko berrikuspeneko zutabeak soilik',
                differs: 'Desberdina',
                identical: 'Berdina',
                emptyValue: '—',
                compareButton: 'Konparatu honekin…',
                documentHeading: 'Dokumentua',
                errorMissingIds: 'Ezkerreko eta eskuineko berrikuspenen idak beharrezkoak dira.',
                errorComparisonFailed: 'Konparazioak huts egin du.',
                errorLoadFailed: 'Berrikuspenak kargatzeak huts egin du.',
                pickerLoading: 'Kargatzen…',
                pickerNoOtherReviews: 'Ez dago beste berrikuspenik konparatzeko.',
                pickerCancel: 'Utzi',
            },
        },
        documentReview: {
            pageTitle: 'Dokumentuen berrikuspena',
            pageSubtitle: 'Onartu edo baztertu AI-ek iradokitako aldaketak dokumentu honetan.',
            sidePanelLabel: 'AI marra gorriak',
            sidePanelHeading: 'AI marra gorriak',
            noPendingEdits: 'Ez dago aldaketarik zain.',
            pendingCount: '{count} zain',
            acceptAllButton: 'Onartu guztiak',
            rejectAllButton: 'Guztiak baztertu',
            acceptAllTitle: 'Zain dauden aldaketa guztiak onartu nahi dituzu?',
            acceptAllConfirm: 'Honek {count} zain dauden aldaketak onartuko ditu eta bertsio berri bat edo gehiago sortuko ditu. Jarraitu nahi duzu?',
            editsHeading: 'Aldaketak',
            editsEmpty: 'Ez dago berrikusteko aldaketarik.',
            versionsHeading: 'Bertsioen historia',
            versionsEmpty: 'Oraindik ez dago bertsiorik.',
            editLabel: 'Editatu',
            editLabelWithId: 'Editatu #{id}',
            acceptButton: 'Onartu',
            rejectButton: 'Baztertu',
            accepting: 'Onartzen…',
            rejecting: 'Baztertzen…',
            diffDeletedAria: 'Ezabatu da',
            diffInsertedAria: 'Txertatuta',
            statusPending: 'Zain',
            statusAccepted: 'Onartua',
            statusRejected: 'Baztertua',
            versionLabel: 'Bertsioa',
            versionAriaLabel: 'Bertsioa {n}',
            versionAriaLabelCurrent: 'Bertsioa {n} ({current})',
            versionCurrentTag: 'korronte',
            versionSourceLabel: 'Iturria',
            versionCreatedLabel: 'Sortu',
            versionModelLabel: 'Eredua',
            sourceUpload: 'Kargatu',
            sourceUserUpload: 'Erabiltzaileen karga',
            sourceAssistantEdit: 'Laguntzailea editatzeko',
            sourceUserAccept: 'Erabiltzaileak aldaketa onartu du',
            sourceUserReject: 'Erabiltzaileak baztertu du aldaketa',
            sourceGenerated: 'Sortutakoa',
            extractionExtracted: 'Testua erauzita',
            extractionFailed: 'Testu-erauzketak huts egin du',
            extractionUnsupported: 'Ezin izan da testua erauzi',
            extractionPages: '{n} orrialde',
            extractionWords: '{n} hitz',
            extractionReasonScanned: 'Hau eskaneatutako edo irudiz soilik osatutako dokumentua dirudi. Forsetik ezin du oraindik irakurri — igo testuan oinarritutako fitxategi bat edo itsatsi testua zuzenean.',
            extractionReasonEncrypted: 'Fitxategia pasahitzez babestuta dago. Kendu babesa eta igo berriro.',
            extractionReasonMalformed: 'Ezin izan da fitxategia analizatu eta hondatuta egon daiteke.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Fitxategia handiegia da prozesatzeko.',
            extractionReasonTimeout: 'Prozesatzeak luzeegi iraun du. Saiatu fitxategi txikiago edo sinpleago batekin.',
            extractionReasonUnknown: 'Ez dago xehetasun gehiagorik eskuragarri.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Dokumentuaren testua',
            textPaneTitle: 'Dokumentuaren testua',
            textPaneVersion: '{n} bertsioa',
            textPaneEmpty: 'Ez dago testurik eskuragarri bertsio honetarako. Eskaneatutako PDFek eta zenbait dokumentu motak ez dute erauz daitekeen testurik.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Dokumentu-berrikuspena',
            pageTitle: 'Dokumentu-berrikuspena',
            pageSubtitle: 'Zure erakundeko dokumentuak, AIk iradokitako edizioak berrikuspenaren zain dutenak edo Forsetik sortutako bertsioak dituztenak.',
            colDocument: 'Dokumentua',
            colLinkedRecord: 'Lotutako erregistroa',
            colPendingEdits: 'Edizio zain',
            colLastActivity: 'Azken jarduera',
            colOpen: 'Ireki',
            open: 'Ireki',
            empty: 'Oraindik ez dago Forseti berrikuspenpean dauden dokumenturik. Dokumentuak ilara honetara sartzen dira Forseti-k txat batean edizio bat iradokitzen duenean, AI lan-fluxu batek redline bat sortzen duenean, edo dokumentu bat berrikusteko bidaltzen duzunean goiko botoia erabiliz.',
            loading: 'Kargatzen…',
            loadError: 'Berrikuspenpean dauden dokumentuak kargatzeak huts egin du.',
            retryButton: 'Saiatu berriro',
            noLinkedRecord: '—',
            pendingZero: 'Bat ere ez',
            sendForReviewButton: 'Bidali dokumentu bat berrikusteko',
            sendForReviewTitle: 'Bidali dokumentu bat berrikusteko',
            sendForReviewSubtitle: 'Igo .txt, .md, .pdf edo .docx dokumentu bat (gehienez 50 MB). Berrikuspen-ilarara gehituko da eta Forsetik redline pasaldi bat egingo dio.',
            sendForReviewTabUpload: 'Igo berria',
            sendForReviewPickerLabel: 'Bilatu eranskinak',
            sendForReviewPickerPlaceholder: 'Idatzi bilatzeko…',
            sendForReviewPickerEmpty: 'Ez dago bat datorren eranskinik. Saiatu beste bilaketa batekin.',
            sendForReviewSubmit: 'Bidali berrikusteko',
            sendForReviewCancel: 'Utzi',
            sendForReviewSubmitting: 'Bidaltzen…',
            sendForReviewError: 'Ezin izan da dokumentua berrikusteko bidali. Saiatu berriro.',
            sendForReviewSuccess: 'Dokumentua berrikusteko bidali da.',
            sendForReviewAcceptedExt: 'Onartuak: {ext}.',
            sendForReviewMaxSize: 'Gehienezko tamaina {mb} MB.',
        },
        chats: {
            exportError: 'Esportazioak huts egin du.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL froga-paketeak',
        subtitle: 'Bezeroak kontrolatutako bidalketa MoPS A05-era',
        nav: { emailProviders: 'Posta-hornitzaileak' },
        providers: {
            pageTitle: 'Posta-hornitzaileak',
            pageSubtitle: 'Konfiguratu zure erakundearen irteerako posta-hornitzailea PDPL froga-paketeak MoPS A05-era bidaltzeko.',
            architectureRuleHeading: 'Arkitektura-araua (blokeatuta)',
            architectureRuleBody: 'AesirX ez da froga-paketeen posten igorlea. Bidalketa bakoitzak zure jabetzakoa eta zure kontrolpekoa den hornitzaile bat erabiltzen du. AesirX-ek ez du inoiz zure izenean bidaltzen.',
            empty: 'Oraindik ez dago hornitzailerik konfiguratuta.',
            createCta: 'Sortu hornitzailea',
            statusPendingConsent: 'Onarpenaren zain',
            statusPendingConnectionCheck: 'Konexio-egiaztapenaren zain',
            statusActive: 'Aktibo',
            statusConnectionCheckFailed: 'Konexio-egiaztapenak huts egin du',
            statusDisabled: 'Desgaituta',
            acceptConsentCta: 'Onartu onarpena',
            runConnectionCheckCta: 'Egin konexio-egiaztapena',
            disableCta: 'Desgaitu',
            disableConfirm: 'Hornitzaile hau desgaitzeak bidalketa zuzenak gelditzen ditu hornitzaile berri bat konfiguratu eta onarpena berriz baieztatu arte. Jarraitu?',
            columnDisplayName: 'Izena',
            columnFromAddress: 'Igorlearen helbidea',
            columnStatus: 'Egoera',
            columnLastCheck: 'Azken egiaztapena',
            columnActions: 'Ekintzak',
            createModalTitle: 'SMTP hornitzaile berria',
            createDisplayNameLabel: 'Bistaratze-izena',
            createFromAddressLabel: 'Igorlearen helbidea',
            createReplyToLabel: 'Erantzun-helbidea (aukerakoa)',
            createSmtpHostLabel: 'SMTP zerbitzaria',
            createSmtpPortLabel: 'SMTP ataka',
            createSmtpUsernameLabel: 'SMTP erabiltzaile-izena',
            createSmtpPasswordLabel: 'SMTP pasahitza',
            createSmtpEncryptionLabel: 'Enkriptatzea',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (inplizitua)',
            createSmtpEncryptionNone: 'Bat ere ez',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Laster',
            createOauthDisabledHint: 'OAuth hornitzaileak ez daude oraindik gaituta. Erabili SMTP oraingoz.',
            createSubmitCta: 'Sortu hornitzailea',
            createCancelCta: 'Utzi',
            createErrorInvalidFromAddress: 'Igorlearen helbideak posta-kutxa baliodun bat izan behar du.',
            createErrorDuplicateFromAddress: 'Beste hornitzaile bat dago jadanik konfiguratuta igorlearen helbide honetarako.',
            createErrorGeneric: 'Ezin izan da hornitzailea sortu. Berrikusi inprimakia eta saiatu berriro.',
            consentModalTitle: 'Baieztatu arkitektura-araua',
            consentLocaleToggleEn: 'Ingelesa',
            consentLocaleToggleVi: 'Vietnamera',
            consentAcknowledgeCta: 'Baieztatzen dut — onartu onarpena',
            consentCancelCta: 'Utzi',
            consentErrorMismatch: 'Onarpenaren testua aldatu egin da eta ez dator bat oharra kanonikoarekin. Ireki elkarrizketa berriro eta bidali berriro.',
            testModalTitle: 'Konexio-egiaztapena',
            testRunningCta: 'Konexioa probatzen…',
            testRunCta: 'Egin konexio-egiaztapena',
            testRetryCta: 'Saiatu konexio-egiaztapena berriro',
            testSuccessHeading: 'Konexioa zuzena',
            testSuccessBody: 'Autentifikatua {host}:{port} aurrean {latencyMs} ms-tan. Hornitzailea bidalketa zuzenetarako prest dago.',
            testFailureHeading: 'Konexioak huts egin du',
            testFailureBody: 'Ezin izan da proba osatu. Berrikusi azpiko arrazoia eta saiatu berriro zuzendu ondoren.',
            testFailureReasonLabel: 'Errorearen arrazoia',
            testCloseCta: 'Itxi',
            detailDrawerTitle: 'Hornitzailearen xehetasunak',
            detailDrawerHistoryHeading: 'Konexio-egiaztapenen historia',
            detailDrawerConsentHeading: 'Onarpenaren baieztapena',
            detailDrawerNoHistory: 'Oraindik ez da konexio-egiaztapenik erregistratu.',
            detailDrawerNoConsent: 'Onarpena oraindik ez da baieztatu.',
            outcomeSuccess: 'Arrakasta',
            outcomeFailure: 'Errorea',
        },
        builder: {
            pageTitle: 'Froga-paketea muntatu',
            pageSubtitle: 'Igo 6 zatiko fitxategiak, aukeratu pakete-mota eta muntatu bidalketa-rako 8 zatiko ZIP deterministikoa.',
            packTypeLabel: 'Pakete-mota',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: '01. Zatia — Aurkezpen-gutuna (PDF)',
            part02AuthorityForm: '02. Zatia — Agintaritzaren inprimakia (PDF)',
            part03Supporting: '03. Zatia — Babes-frogak (≥1)',
            part04Safeguards: '04. Zatia — Babes-neurriak',
            part04SafeguardsRequiredOtia: '(OTIA-rentzat beharrezkoa)',
            part05PriorCorrespondence: '05. Zatia — Aurreko korrespondentzia (aukerakoa)',
            part06Signature: '06. Zatia — Sinadura eta zigilu frogak (≥1)',
            uploadPickFile: 'Aukeratu fitxategia',
            uploadPickFiles: 'Aukeratu fitxategiak',
            uploadRemoveFile: 'Kendu',
            requiredMissing: 'Beharrezko zatia falta da',
            assembleCta: 'Muntatu paketea',
            assembling: 'Muntatzen…',
            assembleSuccessHeading: 'Paketea muntatu da',
            assembleSuccessSummary: '{fileCount} fitxategi · {bytes} byte · SHA-256 {shaPrefix}…',
            composeNextCta: 'Idatzi e-mail zirriborroa',
            warningsHeading: 'Balidazio-abisuak',
            assembleError: 'Ezin izan da paketea muntatu. Berrikusi sarrerak eta saiatu berriro.',
            forsetiAssistHeading: 'Forseti AI laguntza (aukerakoa)',
            forsetiAssistHint: 'Exekutatu PDPL Evidence Pack Assembly Advisor falta diren zatiak nabarmentzeko, gutuna prestatzeko eta babes hutsuneak markatzeko. AesirX ez da bidaltzailea — IAk aholkatzen du soilik; zuk artefaktu bakoitza onartzen duzu.',
            forsetiAssistCta: 'Ireki Forseti lan-fluxuen liburutegia',
            forsetiAiRunIdLabel: 'Lotutako Forseti AI exekuzio-id (itsatsi AI laguntza erregistratzeko)',
        },
        draft: {
            pageTitle: 'E-mail zirriborroa',
            pageSubtitle: 'Idatzi eta editatu e-mail zirriborroa. Zuzeneko bidalketa hurrengo bertsio batean iritsiko da.',
            composeCta: 'Idatzi zirriborroa',
            composing: 'Idazten…',
            noDraftYet: 'Oraindik ez dago zirriborrorik. Idatzi bat hasteko.',
            localeToggleLabel: 'Hizkuntza',
            localeEn: 'Ingelesa',
            localeVi: 'Vietnamera',
            subjectLabel: 'Gaia',
            bodyLabel: 'Edukia',
            saveCta: 'Gorde aldaketak',
            saving: 'Gordetzen…',
            saveError: 'Ezin izan da zirriborroa gorde. Saiatu berriro.',
            manifestSummaryLabel: 'Manifestaren laburpena',
            coverLetterLabel: 'Aurkezpen-gutuna (PDF)',
            coverLetterDownloadCta: 'Deskargatu PDFa',
            editHistoryHeading: 'Edizio-historia',
            editHistoryEmpty: 'Oraindik ez dago edizioik.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 kanal ez-ofizialaren oharra',
            uploadSentProofCta: 'Igo eskuzko bidalketa-froga',
            versionLabel: 'Bertsioa',
            statusDraft: 'Zirriborroa',
            statusEdited: 'Editatua',
            statusFinalized: 'Amaitua',
        },
        sentProof: {
            modalTitle: 'Igo eskuzko bidalketa-froga',
            architectureLockNote: 'AesirX ez da bidaltzailea. Froga honek zure e-mail bezeroaren bidez egindako eskuzko bidalketa jasotzen du.',
            fileLabel: 'Froga-fitxategia (.eml, .png, .jpg)',
            envelopeFromLabel: 'Nork',
            envelopeToLabel: 'Nori',
            envelopeSubjectLabel: 'Gaia (bidaltzean)',
            envelopeSentAtLabel: 'Bidalita',
            envelopeAttachmentShaLabel: 'Eranskinaren SHA-256 (bidaltzean)',
            uploadCta: 'Igo froga',
            uploading: 'Igotzen…',
            uploadSuccess: 'Bidalketa-froga igo da.',
            uploadErrorInvalidMime: 'Fitxategi-mota ez da onartzen. Erabili .eml, .png, .jpg edo .jpeg.',
            uploadErrorGeneric: 'Ezin izan da froga igo. Saiatu berriro.',
            existingProofsHeading: 'Erregistratutako bidalketa-frogak',
            existingProofsEmpty: 'Oraindik ez da bidalketa-frogarik erregistratu.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Froga-paketea',
            notFoundMessage: 'Paketea ez da aurkitu: ezabatu egin daiteke edo esteka ez da zuzena.',
            manifestHeading: 'Manifestua',
            dossierLabel: 'Espedientea',
            packShaLabel: 'Paketearen SHA-256',
            fileCountLabel: 'Fitxategiak',
            bytesLabel: 'Tamaina',
            composeDraftCta: 'Idatzi zirriborroa',
            sendComingSoonCta: 'Zuzeneko bidalketa: laster',
            sendComingSoonHint: 'Zuzeneko bidalketa fluxua garapenean dago. Erabili "Igo eskuzko bidalketa-froga" zirriborro orrian iristen den arte.',
            sendCta: 'Bidali konfiguratutako hornitzailearen bidez',
            sendDisabledNoProviderHint: 'Konfiguratu lehenik bezeroak kontrolatutako posta-hornitzaile bat. AesirX-ek inoiz ez du zure izenean bidaltzen.',
            timelineHeading: 'Bidalketen historia',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Bidali froga-paketea',
            providerPickerLabel: 'Konfiguratutako posta-hornitzailea',
            providerPickerHint: 'AesirX-ek inoiz ez du zure izenean bidaltzen. Aukeratu zure konfiguratutako hornitzaileetako bat behean.',
            providerPickerEmpty: 'Ez dago hornitzaile aktiborik konfiguratuta. Konfiguratu bat Posta-hornitzaileen orrian bidali aurretik.',
            providerPickerEmptyCta: 'Ireki Posta-hornitzaileak',
            draftPickerLabel: 'Posta-zirriborroa',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Pakete honetarako ez da oraindik zirriborrorik idatzi. Idatzi bat zirriborro-orrian lehenengo.',
            recipientToLabel: 'Nori',
            recipientToHint: 'Helbide elektroniko bat edo gehiago, komaz bereizita.',
            recipientCcLabel: 'Cc (komaz bereiztuta)',
            recipientCcHint: 'Aukerakoa. Komaz bereizitako helbide elektronikoen zerrenda.',
            recipientBccLabel: 'Bcc (komaz bereiztuta)',
            recipientBccHint: 'Aukerakoa. Komaz bereizitako helbide elektronikoen zerrenda.',
            consentReaffirmHeading: 'Berretsi hornitzailearen baimena',
            submitCta: 'Bidali orain',
            sending: 'Bidaltzen…',
            cancelCta: 'Utzi',
            sendSuccess: 'Bidalketa erregistratu da. Paketea zure konfiguratutako hornitzailearen bidez transmititu da.',
            sendErrorGeneric: 'Ezin izan da paketea bidali. Berrikusi beheko errorea eta saiatu berriro.',
            sendErrorAlreadyFinalized: 'Zirriborro hau jada arrakastaz bidali da. Idatzi zirriborro berri bat berriro bidali aurretik.',
            sendErrorInvalidRecipients: 'Hartzaile-helbide bat edo gehiago ez dira posta-kutxa baliodunak.',
            // Timeline
            timelineEmpty: 'Oraindik ez dago bidalketarik erregistratuta. Erabili goiko "Bidali konfiguratutako hornitzailearen bidez" zure bezeroak kontrolatutako SMTP / Microsoft 365 / Google Workspace hornitzailearen bidez bidaltzeko.',
            timelineProviderLabel: 'Zure konfiguratutako hornitzailearen bidez bidalita',
            timelineSentAtLabel: 'Bidalita',
            timelineRecipientLabel: 'Hartzailea',
            timelineOutcomeSent: 'Bidalita',
            timelineOutcomeFailed: 'Hutsegitea',
            timelineViewDetailsCta: 'Ikusi xehetasunak',
            // Authority-response chips
            authorityChipNone: 'Erantzunaren zain',
            authorityChipAcknowledgement: 'Jasotze-agiria',
            authorityChipAcceptance: 'Onarpena',
            authorityChipRejection: 'Atzera botatzea',
            authorityChipSupplementRequest: 'Osagarri-eskaera',
            authorityChipNoResponse: 'Erantzunik gabe',
            // Hard-copy chips
            hardCopyChipNone: 'Paperezko jarraipenik gabe',
            hardCopyChipPending: 'Bidalita — entrega zain',
            hardCopyChipDelivered: 'Entregatuta',
            // Drawer
            drawerTitle: 'Bidalketa-erregistroa',
            drawerEnvelopeHeading: 'Gutun-azala',
            drawerEnvelopeFromLabel: 'Nondik',
            drawerEnvelopeToLabel: 'Nori',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Gaia',
            drawerEnvelopeMessageIdLabel: 'Mezuaren IDa',
            drawerOutcomeHeading: 'Emaitza',
            drawerSnapshotHeading: 'Zaintza-katearen argazkia',
            drawerSnapshotPackShaLabel: 'Paketearen SHA-256 bidaltzean',
            drawerSnapshotDraftVersionLabel: 'Zirriborroaren bertsioa bidaltzean',
            drawerSnapshotAttachmentLabel: 'Transmititutako ZIP-a',
            drawerSnapshotAttachmentDownloadCta: 'Deskargatu ZIP-a',
            drawerAuthorityResponseHeading: 'Agintaritzaren erantzuna',
            drawerHardCopyHeading: 'Paperezko jarraipena',
            drawerSentByLabel: 'Nork bidalia',
            drawerSendIdLabel: 'Bidalketa-erregistroaren IDa',
            drawerSmtpResponseLabel: 'SMTP erantzuna',
            drawerFailureReasonLabel: 'Hutsegitearen arrazoia',
            drawerLatencyLabel: 'Latentzia',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Onartua',
            drawerRejectedLabel: 'Baztertua',
            drawerCloseCta: 'Itxi',
            // Authority-response panel
            authorityRecordCta: 'Erregistratu erantzuna',
            authorityReclassifyCta: 'Birsailkatu',
            authorityClassificationLabel: 'Sailkapena',
            authorityReceivedAtLabel: 'Jasotze-data',
            authorityFreeTextLabel: 'Oharrak (aukerakoa)',
            authorityEvidenceLabel: 'Froga-fitxategia (.eml / .pdf / .png / .jpg — aukerakoa)',
            authorityReasonLabel: 'Birsailkatzeko arrazoia',
            authorityHistoryHeading: 'Birsailkapenen historia',
            authorityHistoryEmpty: 'Ez dago birsailkapenik erregistratuta.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Gorde erantzuna',
            authoritySubmitting: 'Gordetzen…',
            authorityErrorInvalidMime: 'Froga-fitxategi mota ez da onartzen. Erabili .eml, .pdf, .png edo .jpg.',
            authorityErrorGeneric: 'Ezin izan da erantzuna gorde. Saiatu berriro.',
            authorityRecordedAtLabel: 'Erregistratze-data',
            authorityRecordedByLabel: 'Nork erregistratua',
            authorityEvidenceFilenameLabel: 'Froga',
            authorityEvidenceDownloadCta: 'Frogak deskargatu',
            // Hard-copy panel
            hardCopyRecordCta: 'Erregistratu paperezko jarraipena',
            hardCopyConfirmCta: 'Berretsi entrega',
            hardCopyEditNotesCta: 'Editatu entrega-oharrak',
            hardCopyPostedAtLabel: 'Bidaltze-data',
            hardCopyCourierLabel: 'Mezularia',
            hardCopyTrackingLabel: 'Jarraipen-zenbakia',
            hardCopyRecipientLabel: 'Etiketako hartzailea',
            hardCopyReceiptLabel: 'Hartu-agiriaren froga (.pdf / .png / .jpg — aukerakoa)',
            hardCopyReceiptDownloadCta: 'Egiaztagiria deskargatu',
            hardCopyDeliveredAtLabel: 'Entrega-data',
            hardCopyDeliveryNotesLabel: 'Entrega-oharrak (aukerakoa)',
            hardCopySubmitCta: 'Gorde jarraipena',
            hardCopySubmitting: 'Gordetzen…',
            hardCopyErrorInvalidMime: 'Hartu-agiriaren fitxategi mota ez da onartzen. Erabili .pdf, .png edo .jpg.',
            hardCopyErrorGeneric: 'Ezin izan da jarraipena gorde. Saiatu berriro.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Espedienteko txosten eta jakinarazpen osatuak',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=eu.js.map